import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useContext,
  createContext,
  Fragment,
} from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
// Marco del shell nuevo, el mismo que usan "Editar imagen" y "Editar video con IA".
import { MARCO_PANTALLA, BORDE_PANTALLA } from "../components/PantallaChrome";
import { supabase } from "../lib/supabase";
import { withTimeout } from "../lib/withTimeout";
import { fetchInChunks } from "../utils/supabaseChunkedIn";
import { detectAutocutRanges } from "../lib/autocut";
import DialogueAudioInput from "../components/DialogueAudioInput";
import CachedImg from "../components/CachedImg";
import ProductGridSkeleton from "../components/ProductGridSkeleton";
import VoiceCloneCreator from "../components/VoiceCloneCreator";
import ClipVoicePicker from "../components/ClipVoicePicker";
import {
  ClonedVoice,
  fetchClonedVoices,
} from "../lib/clonedVoicesApi";
import {
  GENERATE_AUTH_TOKEN,
  AGENT_CHAT_URL,
  SUPABASE_ANON_KEY,
} from "../config";
import { generateAudioCreator } from "../lib/audioGenerate";
import { generateVideoCreator } from "../lib/videoCreator";
import {
  creatorAttemptRef,
  creatorParamsKey,
  isN8nOnlyCreatorRefMode,
} from "../lib/creatorRetry";
import { changeClipVoiceVideo } from "../lib/changeClipVoiceVideo";
import { GenerateAdError, postGenerateAdAtlas } from "../lib/generateAdRequest";
import { USE_ATLAS_BACKEND } from "../config";
import { atlasBackendFetch, pollJobUntilSettled } from "../lib/atlasBackend";
import { awaitImageGeneration } from "./v2/imageGenerationRecovery";
import EraseBrushPanel from "../components/EraseBrushPanel";
import { EditarVideoIA } from "../components/EditarVideoIA";
import { UpscaleVideoPanel } from "../components/UpscaleVideoPanel";
import {
  creditsForBrushErase,
  marksToSamPoints,
  requestSam2Mask,
  type EraseMark,
} from "../lib/sam2Erase";
import {
  uploadProjectAsset,
  getAssetSignedUrl,
  fetchAssetsByIds,
  fetchProjectAssetsByType,
  getSignedUrlsForAssetIds,
  registerExistingAsset,
  describeUploadError,
  type ProjectAsset,
} from "../utils/projectAssets";
import {
  uploadBrandAsset,
  fetchBrandAssets,
  getBrandAssetSignedUrl,
  deleteBrandAsset,
  type BrandAsset,
  type BrandAssetTipo,
} from "../utils/brandAssets";
import { AGENT_TOOLS } from "../agent/tools";
import { TEXT_FONT_OPTIONS } from "../lib/fontOptions";
import {
  fetchUserProductsByBrand,
  fetchSceneImages,
  fetchHistorialImages,
} from "./v2/productsApi";
import type { MockProductV2 } from "./v2/mockData";
import {
  fetchUserModels,
  createModelFromPhoto,
  type ModelV2,
} from "./v2/modelsApi";
import { CreateView, UploadFlow } from "./v2/ModelPicker";
import { postTranslateStart, postTranslateStatus } from "../lib/videoTranslate";
import { postVideoEraseStart, postVideoEraseStatus } from "../lib/videoErase";
import { postImageEdit } from "../lib/imageEdit";
import { AddOptionCard } from "./v2/components/AddOptionCard";
import { startEditorTour, startContextualTour } from "../lib/editorTour";
import {
  loadOnboardingSeen,
  isSeen as isOnboardingSeen,
  markSeen as markOnboardingSeen,
} from "../lib/onboardingSeen";
import { VIDEO_AGENT_SYSTEM_PROMPT } from "../agent/systemPrompt";
import { generateVideoThumbnails } from "../utils/videoThumbnails";
import {
  findCoverClipAtZero,
  hashCoverClip,
  regenerateProjectThumbnail,
} from "../utils/projectThumbnail";
import { AnimationLayer } from "../animations/AnimationLayer";
import {
  computeClipMotion,
  splitTextForMotion,
} from "../animations/clipMotion";
import { MotionPicker } from "../components/MotionPicker";
import {
  SelectionFrame,
  type SelectionHandle,
} from "../components/SelectionFrame";
import { AnimationPreviewTile } from "../animations/AnimationPreviewTile";
import { LottiePicker } from "../animations/LottiePicker";
import {
  ANIMATION_TEMPLATES,
  getAnimationTemplate,
} from "../animations/registry";
import type { AnimationPropSpec, TextStylingLinks } from "../animations/types";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  Play,
  Pause,
  Upload,
  UserPlus,
  Sparkles,
  Sparkle,
  Music,
  Type,
  Plus,
  Trash2,
  Image as ImageIcon,
  Film,
  Loader2,
  Square,
  ZoomIn,
  ZoomOut,
  Copy,
  Download,
  Wand2,
  Monitor,
  Undo2,
  Redo2,
  Folder,
  FolderOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  Clock,
  Mic,
  Captions,
  Languages,
  Send,
  Pencil,
  AlertCircle,
  Gem,
  Heart,
  Video,
  MoreHorizontal,
  Menu,
  BookOpen,
  Search,
  BarChart3,
  Brain,
  Compass,
  ArrowLeftRight,
  Ratio,
  Hand,
  ArrowUp,
  ArrowRight,
  X,
  Check,
  Scissors,
  ScissorsLineDashed,
  RotateCcw,
  Volume2,
  VolumeX,
  PanelLeftOpen,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette,
  LifeBuoy,
  MessageSquare,
  Eraser,
  Settings,
  Package,
  Mountain,
  Images,
  Users,
} from "lucide-react";
import {
  exportProjectAsPremiereXmlZip,
  downloadBlob,
  type PremiereXmlExportClip,
  type PremiereXmlAspectRatio,
} from "../utils/premiereXmlExport";
import {
  serializeTimeline,
  preflightExport,
  startRender,
  renderMessageFor,
  RENDER_MESSAGE_ROTATE_MS,
  type RenderHandle,
} from "../lib/videoExport";
import {
  creditsForVideoGeneral,
  creditsForVideoDialog,
  creditsForVoice,
  creditsForSong,
  creditsForImage,
  creditsForMultiSceneVideo,
  creditsForAiClip,
  creditsForVideoTranslate,
  type VideoModel,
  type VideoResolution,
} from "../utils/credits";
import CreditBadge from "../components/CreditBadge";
import SubscriptionLimitModal from "../components/SubscriptionLimitModal";
import TransitionPreviewTile from "../components/TransitionPreviewTile";

// ─── Types ──────────────────────────────────────────────────────────────────

type ClipType =
  | "video"
  | "image"
  | "audio"
  | "text"
  | "ai-generated"
  | "animation";

type AiClipStatus = "draft" | "generating" | "ready" | "error";

type AspectRatio = "16:9" | "9:16" | "4:3";

interface TimelineClip {
  id: string;
  type: ClipType;
  name: string;
  startTime: number;
  duration: number;
  color: string;
  thumbnail?: string;
  volume?: number;
  src?: string;
  // For split clips: seconds into the source media where this clip starts playing (0 = from the beginning)
  mediaOffset?: number;
  // Total duration of the source media (for video/audio). Used to cap trim handles at original length.
  sourceDuration?: number;
  storagePath?: string;
  // asset_id pointing to video_project_assets for uploaded video/audio/image clips
  assetId?: string;
  aiPrompt?: string;
  // New: asset-based references (arrays of video_project_assets.id)
  aiRefAssetIds?: string[];
  aiFirstFrameAssetId?: string;
  aiLastFrameAssetId?: string;
  // Legacy: base64 data URLs (kept for backward compat with old projects)
  aiFirstFrame?: string;
  aiLastFrame?: string;
  aiRefImage?: string;
  aiRefMode?: "none" | "reference" | "frames";
  aiQuality?: "rapido" | "estandar" | "profesional";
  aiResolution?: "480p" | "720p" | "1080p";
  aiStatus?: AiClipStatus;
  // Mensaje del último fallo de generación. Se setea en el catch cuando
  // aiStatus pasa a "error" y se limpia al darle Reintentar (cuando vuelve
  // a "generating"). El botón Reintentar lo muestra como tooltip.
  aiError?: string;
  // ── Idempotencia de la generación IA (fix doble cobro por reintentos) ──
  // execution_id que viaja al backend. Mientras el último intento no haya
  // concluido (fallo TÉCNICO: timeout/red/202), se REUSA en el reintento con
  // los mismos parámetros: el backend dedupea por idempotency key y se
  // re-engancha al job en curso (o devuelve el video ya generado) en vez de
  // generar y cobrar otro. Se limpia en éxito y en fallo definitivo del
  // backend; si cambian los parámetros (paramsKey distinto) se descarta.
  // Ver ADR-2026-08-24-doble-cobro-video-creator-reintentos.
  aiExecutionId?: string;
  aiExecutionParamsKey?: string;
  // Warning para cuando el video/audio AI se generó correctamente y está
  // en Supabase Storage, pero el registro como asset del proyecto falló.
  // Caso problemático: el usuario ya pagó créditos, el archivo existe,
  // pero NO aparece en la biblioteca → si recarga la página lo pierde.
  // El badge en el clip de la timeline lo avisa.
  aiAssetWarning?: string;
  // Error al cargar la signed URL del archivo binario al abrir el proyecto.
  // Sin signed URL el clip no se puede reproducir — antes fallaba silencioso
  // y el clip aparecía "vacío" en la timeline sin explicación. Ahora ponemos
  // este flag para mostrar un badge con la opción de reintentar.
  loadError?: string;
  aiGenerationStartedAt?: number;
  // ── Traducción de video con lip-sync (HeyGen Video Translate v3) ───────
  // Flow async: el usuario clickea "Traducir al inglés" → frontend POST al
  // endpoint /start de n8n → n8n llama HeyGen y devuelve `jobId` → el
  // frontend polea /status cada 15s hasta que HeyGen termine. Cuando
  // termina, el clip se REEMPLAZA en la timeline con el video traducido
  // (mismo startTime + trackIndex, nuevo src/asset/duration).
  aiTranslating?: boolean;
  aiTranslateJobId?: string;
  aiTranslateError?: string;
  aiTranslateStartedAt?: number;
  // ── Borrado de objetos (Bria video-erase-object via n8n) ───────────────
  // Flow async idéntico al de traducción: el Director llama erase_object →
  // POST a bria_erase_start → jobId → poll a bria_erase_status → cuando
  // termina, el clip se REEMPLAZA in-place con el video borrado (mismo
  // startTime/trackIndex/mediaOffset, nuevo src/storagePath/assetId).
  // `eraseStatus` "processing" muestra un shimmer en la timeline.
  eraseStatus?: "processing" | "error";
  eraseJobId?: string;
  eraseError?: string;
  eraseStartedAt?: number;
  // storagePath del clip ANTES de borrar — para revertir al original (el
  // resultado borrado ya quedó como asset en Archivos, así que revertir es
  // solo visual y no cuesta créditos de nuevo).
  originalStoragePathBeforeErase?: string;
  originalSrcBeforeErase?: string;
  // ── Cambiar voz (S2S ElevenLabs) — clip de audio (Fase 1) ──────────────
  // Flow async idéntico a traducción/borrado: el usuario elige una voz en el
  // ClipVoicePicker → S2S sobre el audio del clip (preserva ritmo/entonación)
  // → el clip se REEMPLAZA in-place con el audio de la voz nueva.
  // `voiceChangeStatus` "processing" muestra un shimmer en la timeline.
  voiceChangeStatus?: "processing" | "error";
  voiceChangeError?: string;
  voiceChangeStartedAt?: number;
  aiSceneMode?: "general" | "personas";
  aiPersonType?: "static" | "motion";
  // Dialog-mode only: the spoken dialogue (while aiPrompt is the visual scene prompt).
  aiDialogue?: string;
  // Dialog-mode only: speech rate multiplier (0.5 .. 1.5). Default 1.1.
  aiSpeechSpeed?: number;
  // Dialog-mode only: how the user provided the spoken content.
  // `text` (default) means the spoken text is in aiPrompt/aiDialogue and the
  // workflow synthesizes it via ElevenLabs TTS. `audio` means the user
  // uploaded/recorded their own audio, which lives in aiDialogueAudio* fields
  // and gets sent through ElevenLabs speech-to-speech instead of TTS.
  aiDialogueInputMode?: "text" | "audio";
  aiDialogueAudioUrl?: string;
  aiDialogueAudioStoragePath?: string;
  aiDialogueAudioDurationSec?: number;
  aiDialogueAudioAssetId?: string;
  // Multi-scene mode (General + Reference only): when enabled, the clip is
  // generated from a list of scenes (each with its own prompt + duration).
  // Sum of durations is capped at 15s; each scene 4..10s.
  aiMultiEnabled?: boolean;
  aiMultiScenes?: Array<{ prompt: string; duration: number }>;
  // AI audio clip fields
  aiAudioType?: "voice" | "music";
  aiVoiceId?: string;
  aiVoiceName?: string;
  aiInstrumentalOnly?: boolean;
  // Row index within the same clip category (currently only used for audio to support multiple audio tracks). Defaults to 0.
  trackIndex?: number;
  // ── Text clip fields (only meaningful when type === 'text') ───────────────
  // The actual rendered string. Multi-line allowed via "\n".
  text?: string;
  // CSS font-family value (no quotes). Must match one of TEXT_FONT_OPTIONS or
  // any system font; rendering uses the value verbatim with a sensible
  // fallback stack appended in the renderer.
  fontFamily?: string;
  // Font size as a percentage of the canvas height (1..40). We store a
  // resolution-independent number so the same clip looks identical on 16:9,
  // 9:16, etc., regardless of preview pixel size.
  fontSizePct?: number;
  fontWeight?: "normal" | "bold";
  fontStyle?: "normal" | "italic";
  // Text fill color. Hex or any CSS color.
  textColor?: string;
  // Text alignment for multi-line strings.
  textAlign?: "left" | "center" | "right";
  // Position of the text's *center* as a percentage of canvas (0..100). The
  // renderer translates by -50%/-50% so the text is anchored from its center,
  // which makes drag math simple and lets us clamp 0..100 against the canvas.
  textXPct?: number;
  textYPct?: number;
  // Ancho de la caja del texto como porcentaje del ancho de la canvas
  // (0..100). Cuando está seteado, el wrapper del texto fija ese ancho
  // y el contenido hace word-wrap dentro — *sin* cambiar el font-size.
  // Esto desacopla "tamaño visual del texto" (font) de "ancho de la
  // caja" (resize manual desde el preview). Cuando es undefined, el
  // wrapper usa su ancho natural acotado por `maxWidth`.
  textWidthPct?: number;
  // ── Subtitle group fields (only meaningful when type === 'text') ─────────
  // When `isSubtitle` is true the clip behaves like a normal text clip but
  // is rendered on its own dedicated timeline row and shares its style
  // with every other subtitle clip in the same `subtitleGroupId`. Editing
  // one (font, size, color, alignment, position, animations) propagates
  // to siblings — except for clips with `subtitleStyleOverride: true`,
  // which opt out of the group sync (both as source and as target).
  isSubtitle?: boolean;
  subtitleGroupId?: string;
  subtitleStyleOverride?: boolean;
  // ── Linked-subtitle fields ───────────────────────────────────────────────
  // When a subtitle text clip is generated from a video/audio clip, we
  // remember the source and the word timings in *source-media* seconds
  // (i.e. absolute to the original file, not to the clip's trimmed view).
  // A useEffect below reconciles `startTime`/`duration` of every linked
  // subtitle whenever its source clip is moved, resized or trimmed, so the
  // captions follow the clip visually. Clips whose linked range falls
  // entirely outside the source's trimmed window are kept with a tiny
  // duration so the user can restore them by undoing the trim.
  linkedClipId?: string;
  linkedMediaStart?: number;
  linkedMediaEnd?: number;
  // ── Transcript (only meaningful for video/audio/ai-generated audio) ──────
  // Full word-level transcript of the *source media*, with timestamps in
  // source-media seconds (absolute to the original file, NOT to the trimmed
  // clip view). Stored once per source clip; trims/splits never mutate it —
  // consumers are expected to filter words by `[mediaOffset, mediaOffset +
  // duration]` to get the slice that's actually visible on the timeline.
  // Populated on demand by the `generate_transcript` agent tool, and as a
  // side-effect of running the "Generar subtítulos" flow on this clip.
  transcript?: TranscriptResult;
  // ── Animation clip fields (only meaningful when type === 'animation') ──
  // Id of the template in `ANIMATION_TEMPLATES`. The renderer resolves it
  // through `getAnimationTemplate()` so unknown templates render nothing
  // (forward-compat: opening a project saved with a newer template just
  // shows an empty clip instead of crashing).
  animationTemplate?: string;
  // Per-instance overrides on top of the template defaults. Shape depends
  // on the template; the agent will validate this against the template's
  // schema before writing it.
  animationProps?: Record<string, unknown>;
  // Position of the animation's *center* as percentage of canvas (0..100).
  // Mismo modelo que `textXPct/YPct`: anclado al centro, así el clamp es
  // simétrico y la math del drag es trivial. Si no está seteado, se asume
  // 50/50 (centro), que respeta el comportamiento previo.
  animXPct?: number;
  animYPct?: number;
  // Escala uniforme aplicada al wrapper de la animación (1 = ocupa toda
  // la canvas, igual que antes; 0.5 = la mitad). Sin esto no había forma
  // de mover una animación: a 100% no hay espacio para correrla.
  animScale?: number;
  // ── Motion (entrada / salida) ─────────────────────────────────────
  // Aplica solo a clips de tipo 'text' y 'animation'. El renderer compone
  // estos efectos sobre el transform base del wrapper, sin tocar el
  // contenido del template ni el texto en sí. Ver `clipMotion.ts` para
  // la lista de presets disponibles. Los presets de texto-only
  // (typewriter, char-cascade, word-cascade) se muestran únicamente en
  // los dropdowns de clips de texto.
  enterAnimation?: string;
  enterDurationSec?: number;
  exitAnimation?: string;
  exitDurationSec?: number;
  // ── Incoming transition (only meaningful for video / ai-generated clips) ─
  // The transition is owned by the SECOND clip in an adjacent pair. When
  // present, the renderer cross-fades from the previous clip on the same
  // track during the first `durationSec` seconds of this clip — the
  // overlap is created by ripple-shifting this clip back when the
  // transition is added (so visually the two clips occupy the same
  // window of timeline time during the fade).
  transitionIn?: {
    type: TransitionType;
    durationSec: number;
  };
}

type TransitionType =
  | "crossfade"
  | "fade-to-black"
  | "slide" // B enters from the right
  | "slide-left" // B enters from the left
  | "slide-up" // B enters from the bottom (moving up)
  | "slide-down" // B enters from the top (moving down)
  | "zoom" // B grows from a small scale at center, fading in
  | "reveal" // White swipe-line travels across the frame revealing B
  | "diapositivas" // Both clips morph into a rounded "slide" rectangle, then slide past each other
  | "stretch-horizontal" // A stretches wide, cut, B comes in stretched and settles
  | "stretch-vertical" // Same but on the vertical axis
  // Overlay-style transitions — all use the same A/B opacity cut logic
  // and only differ in the .mp4 they composite via screen-blend.
  | "light-leak"
  | "plastic"
  | "distorsion"
  | "vhs"
  | "vintage"
  | "glitch";

type ToolPanel =
  | "none"
  | "root"
  | "ai-generate"
  | "ai-scene"
  | "ai-audio"
  | "ai-image-gen"
  | "upload"
  | "audio"
  | "text"
  | "effects"
  | "files"
  | "agent-upload"
  | "animation"
  | "brand"
  | "transitions"
  | "erase-brush"
  | "edit-video-ia"
  | "upscale-video";
// Sub-tabs for the unified "Archivos" panel: each surface lists assets of
// one type. The 'image' tab merges uploaded image assets with AI-generated
// images so the user has a single place to grab any visual.
type FilesTab = "media" | "audio" | "productos" | "escenas" | "historial";

// ── Text clip catalogue ────────────────────────────────────────────────────
// Curated list of fonts loaded via Google Fonts in index.html. The `family`
// is the exact CSS font-family token; `category` decides the fallback stack
// applied at render time so unloaded fonts still look reasonable.
// TEXT_FONT_OPTIONS + FontCategory moved to src/lib/fontOptions.ts (shared with
// the brand typography editor in Configuración). Imported at the top.
// Build a CSS font-family value with a category-appropriate fallback so the
// preview never collapses to Times New Roman if the Google Font is still
// loading or the user is offline.
const fontStackFor = (family?: string): string => {
  const fam = family && family.trim() ? family.trim() : "Inter";
  const opt = TEXT_FONT_OPTIONS.find(
    (o) => o.family.toLowerCase() === fam.toLowerCase(),
  );
  const fallback = (() => {
    switch (opt?.category) {
      case "serif":
        return 'Georgia, "Times New Roman", serif';
      case "display":
        return '"Impact", "Arial Black", system-ui, sans-serif';
      case "handwriting":
        return '"Comic Sans MS", "Brush Script MT", cursive';
      case "mono":
        return "Menlo, Consolas, monospace";
      default:
        return 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
    }
  })();
  return `"${fam}", ${fallback}`;
};
// Brand colors can arrive in two shapes: the editor's own `string[]` of hex
// codes, or Estudio Virtual's richer `{ hex, name, role }[]` objects. The whole
// Marca → Colores UI (and the agent color tool) assume plain hex strings, so we
// normalize on read. Anything without a usable hex is dropped. This is read-only
// coercion; unifying the stored format lives in the brand-identity work item.
const normalizeBrandColors = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c): string | null => {
      if (typeof c === "string") return c.trim() || null;
      if (c && typeof c === "object") {
        const hex = (c as { hex?: unknown }).hex;
        if (typeof hex === "string") return hex.trim() || null;
      }
      return null;
    })
    .filter((c): c is string => c !== null);
};
// Toolbar-friendly numeric input that keeps a local string draft so the user
// can clear the field and retype without each keystroke being clamped /
// re-validated. We only commit (clamp + onCommit) on blur or Enter; Escape
// reverts to the canonical value. Externally-driven changes to `value` (e.g.
// the +/- stepper buttons) re-sync the draft when the field is not focused.
type NumberFieldProps = {
  value: number;
  min: number;
  max: number;
  step?: number;
  decimals?: number;
  onCommit: (n: number) => void;
  className?: string;
  ariaLabel?: string;
};
const NumberField: React.FC<NumberFieldProps> = ({
  value,
  min,
  max,
  step,
  decimals = 1,
  onCommit,
  className,
  ariaLabel,
}) => {
  const format = (n: number) => {
    const fixed = n.toFixed(decimals);
    // Trim trailing ".0" so integers stay clean ("16" not "16.0").
    return fixed.replace(/\.0+$/, "");
  };
  const [draft, setDraft] = React.useState<string>(() => format(value));
  const focusedRef = React.useRef(false);
  // Re-sync from prop only while the input isn't focused (so external
  // updates like +/- buttons reflect, but we never clobber what the user
  // is typing).
  React.useEffect(() => {
    if (!focusedRef.current) setDraft(format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const commit = () => {
    const trimmed = draft.trim().replace(",", ".");
    const parsed = parseFloat(trimmed);
    if (!isFinite(parsed)) {
      setDraft(format(value));
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    setDraft(format(clamped));
    if (clamped !== value) onCommit(clamped);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        focusedRef.current = true;
        e.currentTarget.select();
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          setDraft(format(value));
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      step={step}
      className={className}
      aria-label={ariaLabel}
    />
  );
};
// ─── FontPicker ─────────────────────────────────────────────────────────────
// Custom dropdown for picking a text-clip font family. We don't use the
// native <select> here because each option needs to be rendered in its own
// font family so the user can preview the face before committing to it
// (browsers do not consistently honor `font-family` on <option> elements).
type FontPickerProps = {
  value: string;
  onChange: (family: string) => void;
  triggerClassName: string;
  panelAlign?: "left" | "right" | "center";
  panelMinWidth?: number;
  ariaLabel?: string;
};
const FontPicker: React.FC<FontPickerProps> = ({
  value,
  onChange,
  triggerClassName,
  panelAlign = "left",
  panelMinWidth = 220,
  ariaLabel,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const currentLabel =
    TEXT_FONT_OPTIONS.find((o) => o.family === value)?.label || value;
  // Brand typography from context (provided once at the editor root with
  // `brandProfile?.fonts` + custom uploaded fonts). Dedupe by family so a
  // font assigned to multiple roles or also uploaded only shows up once.
  const brandFontsMap = useContext(BrandFontsContext);
  const customBrandFonts = useContext(BrandCustomFontsContext);
  const brandFamilies = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const consider = (fam: unknown) => {
      if (!fam || typeof fam !== "string") return;
      const trimmed = fam.trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(trimmed);
    };
    // brandFontsMap can be a role map ({ titulo, cuerpo, ... }) or the
    // onboarding shape ({ suggested: string[], style: string }). Pull family
    // names from both: arrays (suggested) contribute each element, and never
    // treat the `style` key (a prose description) as a font family.
    const fontsObj = (brandFontsMap || {}) as Record<string, unknown>;
    for (const [k, val] of Object.entries(fontsObj)) {
      if (k === "style") continue;
      if (Array.isArray(val)) {
        for (const fam of val) consider(fam);
      } else {
        consider(val);
      }
    }
    for (const fam of customBrandFonts) consider(fam);
    return out;
  }, [brandFontsMap, customBrandFonts]);
  React.useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const panelPos =
    panelAlign === "right"
      ? "right-0"
      : panelAlign === "center"
        ? "left-1/2 -translate-x-1/2"
        : "left-0";
  return (
    <div ref={wrapperRef} className="relative" data-clip-ui="true">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName}
        style={{ fontFamily: fontStackFor(value) }}
        title="Fuente"
        aria-label={ariaLabel || "Fuente"}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex-1 truncate text-left">{currentLabel}</span>
        <ChevronDown className="w-3 h-3 text-neutral-400 shrink-0" />
      </button>
      {open && (
        <div
          role="listbox"
          className={`absolute top-full mt-1.5 z-30 ${panelPos} bg-white border border-neutral-200 rounded-lg shadow-md py-1 max-h-64 overflow-y-auto`}
          style={{ minWidth: panelMinWidth }}
        >
          {brandFamilies.length > 0 && (
            <>
              <div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-medium text-neutral-400">
                Fuentes de la marca
              </div>
              {brandFamilies.map((fam) => {
                const isActive =
                  fam.toLowerCase() === (value || "").toLowerCase();
                const label =
                  TEXT_FONT_OPTIONS.find(
                    (o) => o.family.toLowerCase() === fam.toLowerCase(),
                  )?.label || fam;
                return (
                  <button
                    key={`brand-${fam}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      onChange(fam);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[14px] transition-colors ${
                      isActive
                        ? "bg-neutral-100 text-neutral-900"
                        : "text-neutral-800 hover:bg-neutral-50"
                    }`}
                    style={{ fontFamily: fontStackFor(fam) }}
                  >
                    <span className="truncate text-left">{label}</span>
                    {isActive && (
                      <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                    )}
                  </button>
                );
              })}
              <div className="my-1 mx-2 h-px bg-neutral-200" />
              <div className="px-3 pt-0.5 pb-1 text-[10px] uppercase tracking-wider font-medium text-neutral-400">
                Generales
              </div>
            </>
          )}
          {TEXT_FONT_OPTIONS.map((opt) => {
            const isActive = opt.family === value;
            return (
              <button
                key={opt.family}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(opt.family);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[14px] transition-colors ${
                  isActive
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-800 hover:bg-neutral-50"
                }`}
                style={{ fontFamily: fontStackFor(opt.family) }}
              >
                <span className="truncate text-left">
                  {opt.label || opt.family}
                </span>
                {isActive && (
                  <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
// Active brand palette surfaced to every color picker in the editor.
// Provided once at the VideoEditor level (with `brandProfile?.colors`) so
// nested components — including the ones rendered by sub-modules like the
// animation editor — can pull the palette from context instead of every
// caller having to thread the prop manually. An explicit `brandColors`
// prop on a picker still overrides the context, useful in a future world
// where a panel might want to scope the palette differently.
const BrandColorsContext = createContext<string[]>([]);

// Same idea as `BrandColorsContext` but for the brand typography. Stores a
// partial map of role → font family (matching `brandProfile?.fonts`). The
// FontPicker pulls this so it can render a "Fuentes de la marca" section at
// the top of its dropdown without callers having to thread a prop. Empty
// object when no brand profile exists.
const BrandFontsContext = createContext<Record<string, string | undefined>>({});

// Custom font families uploaded by the user (brand_assets with tipo='font').
// The values are the CSS family identifiers — already registered as
// @font-face at the editor root, so any consumer can render text in them
// just by setting `font-family`. Surfaced in the same "Fuentes de la marca"
// section of the FontPicker as the role-assigned fonts.
const BrandCustomFontsContext = createContext<string[]>([]);

// Quick palette for the color swatch grid in the text panel. The user can
// always type a custom hex.
const TEXT_COLOR_PRESETS: string[] = [
  "#FFFFFF",
  "#000000",
  "#1644EB",
  "#F59E0B",
  "#EF4444",
  "#10B981",
  "#A855F7",
  "#EC4899",
  "#0EA5E9",
  "#FACC15",
];

/**
 * Tiny visual used to represent a transparent color in pickers — a light
 * checkerboard pattern with a red diagonal slash on top, matching the
 * universal "no fill" / "PNG transparency" convention.
 */
const TransparentSwatch: React.FC<{
  size: number;
  rounded?: string;
  bordered?: boolean;
}> = ({ size, rounded = "rounded-sm", bordered = false }) => (
  <span
    className={`relative inline-block ${rounded} overflow-hidden shrink-0 ${
      bordered ? "ring-1 ring-black/10" : ""
    }`}
    style={{
      width: size,
      height: size,
      // Checkerboard via two linear gradients tiled at 50% — small tiles
      // (1/3 of the swatch) so the pattern reads even at 24px.
      backgroundColor: "#fff",
      backgroundImage:
        "linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4)," +
        "linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4)",
      backgroundSize: `${Math.max(6, Math.round(size / 2))}px ${Math.max(6, Math.round(size / 2))}px`,
      backgroundPosition: `0 0, ${Math.max(3, Math.round(size / 4))}px ${Math.max(3, Math.round(size / 4))}px`,
    }}
  >
    {/* Red diagonal slash. Anchored corner-to-corner so the visual is
        consistent at any swatch size. */}
    <span
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(to top right, transparent calc(50% - 1px), #DC2626 calc(50% - 1px), #DC2626 calc(50% + 1px), transparent calc(50% + 1px))",
      }}
    />
  </span>
);

// Standard swatch picker used wherever the editor needs a color field. The
// trigger is a small chip showing the current color (with a downward chevron
// so it reads as a control). On click it pops a 6-column preset grid plus a
// dashed '+' tile that opens the OS color picker for arbitrary hex values.
// Visually identical to the text-toolbar color popover so the experience
// stays consistent across the app.
const ColorSwatchPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  /** Where to anchor the popover relative to the trigger. */
  align?: "left" | "right";
  /** Optional aria-label for the trigger button. */
  ariaLabel?: string;
  /** When true, the picker exposes a "transparent" swatch in the popover. */
  allowTransparent?: boolean;
  /**
   * Optional brand palette. If non-empty, rendered as a labeled row above the
   * generic presets so the user can pick a brand color in one click. Hex
   * strings; case-insensitive comparisons. Caller decides what counts as a
   * brand color (typically `brandProfile?.colors`).
   */
  brandColors?: string[];
  /**
   * When provided, replaces the hex code in the trigger with this static
   * label (e.g. "Color"). Useful when the picker sits inside a compact
   * toolbar and the hex would feel verbose. The popover content is
   * unchanged.
   */
  triggerLabel?: string;
}> = ({
  value,
  onChange,
  align = "right",
  ariaLabel,
  allowTransparent,
  brandColors,
  triggerLabel,
}) => {
  // Pull the brand palette from context when the caller doesn't pass an
  // explicit override. Empty array if the project has no brand profile.
  const ctxBrand = useContext(BrandColorsContext);
  const effectiveBrand = brandColors ?? ctxBrand;
  const isTransparent = value === "transparent";
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Close on outside click. Uses pointerdown so it fires before the global
  // 'click outside clip' listener and doesn't fight with native pickers.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`h-8 ${triggerLabel ? "pl-2 pr-1" : "pl-1 pr-2"} flex items-center gap-2 rounded-md border bg-white transition-colors ${
          open
            ? "border-brand-primary/40 ring-2 ring-brand-primary/15"
            : "border-black/[0.08] hover:border-black/[0.16]"
        }`}
        aria-label={ariaLabel || "Elegir color"}
        title={isTransparent ? "Transparente" : value}
      >
        {triggerLabel && (
          <span className="text-[12px] text-neutral-700">{triggerLabel}</span>
        )}
        {isTransparent ? (
          <TransparentSwatch size={24} bordered />
        ) : (
          <span
            className="w-6 h-6 rounded-sm shrink-0"
            style={{
              backgroundColor: value || "#000000",
              boxShadow:
                (value || "").toLowerCase() === "#ffffff"
                  ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                  : undefined,
            }}
          />
        )}
        {!triggerLabel && (
          <span className="text-[12px] text-neutral-700 font-mono tabular-nums">
            {isTransparent ? "Sin fondo" : value || "—"}
          </span>
        )}
      </button>
      {open && (
        <div
          data-clip-ui="true"
          className={`absolute top-full mt-2 z-30 bg-white border border-neutral-200 rounded-lg shadow-md p-2 ${
            align === "right" ? "right-0" : "left-0"
          }`}
          style={{ width: 200 }}
        >
          {(() => {
            // Dedupe brand colors (case-insensitive) and filter out anything
            // that isn't a plausible hex so a malformed entry can't crash
            // the row. Only render the section when there's at least one.
            const seen = new Set<string>();
            const cleanBrand = (effectiveBrand || [])
              .map((c) => (typeof c === "string" ? c.trim() : ""))
              .filter((c) => /^#?[0-9a-fA-F]{3,8}$/.test(c))
              .map((c) => (c.startsWith("#") ? c : `#${c}`))
              .filter((c) => {
                const k = c.toLowerCase();
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
              });
            if (cleanBrand.length === 0) return null;
            return (
              <>
                <div className="text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5">
                  De tu marca
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {cleanBrand.map((color) => {
                    const isActive =
                      !isTransparent &&
                      color.toLowerCase() === (value || "").toLowerCase();
                    return (
                      <button
                        key={`brand-${color}`}
                        type="button"
                        onClick={() => {
                          onChange(color);
                          setOpen(false);
                        }}
                        className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                          isActive
                            ? "ring-2 ring-brand-primary ring-offset-1"
                            : ""
                        }`}
                        style={{
                          backgroundColor: color,
                          boxShadow:
                            color.toLowerCase() === "#ffffff"
                              ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                              : undefined,
                        }}
                        title={`${color} (marca)`}
                        aria-label={`Color de marca ${color}`}
                      />
                    );
                  })}
                </div>
                <div className="h-px bg-neutral-200 -mx-2 mb-2" />
                <div className="text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5">
                  Generales
                </div>
              </>
            );
          })()}
          <div className="grid grid-cols-6 gap-1.5">
            {TEXT_COLOR_PRESETS.map((color) => {
              const isActive =
                !isTransparent &&
                color.toLowerCase() === (value || "").toLowerCase();
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    onChange(color);
                    setOpen(false);
                  }}
                  className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                    isActive ? "ring-2 ring-brand-primary ring-offset-1" : ""
                  }`}
                  style={{
                    backgroundColor: color,
                    boxShadow:
                      color.toLowerCase() === "#ffffff"
                        ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                        : undefined,
                  }}
                  title={color}
                  aria-label={`Color ${color}`}
                />
              );
            })}
            {/* Transparent swatch — shown like any other preset so the
                affordance reads as a color choice, not a separate setting. */}
            {allowTransparent && (
              <button
                type="button"
                onClick={() => {
                  onChange("transparent");
                  setOpen(false);
                }}
                className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ring-1 ring-black/10 ${
                  isTransparent ? "ring-2 ring-brand-primary ring-offset-1" : ""
                }`}
                title="Sin fondo (transparente)"
                aria-label="Sin fondo (transparente)"
              >
                <TransparentSwatch size={28} rounded="rounded-md" />
              </button>
            )}
            <label
              className="w-7 h-7 rounded-md flex items-center justify-center border border-dashed border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 cursor-pointer relative transition-colors"
              title="Color personalizado"
            >
              <Plus className="w-3.5 h-3.5" />
              <input
                type="color"
                value={isTransparent ? "#000000" : value || "#000000"}
                onChange={(e) => onChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
/**
 * Image picker for animation templates. Trigger shows a thumbnail of the
 * current image (or a placeholder), click opens a popover with:
 *   - The project's image assets (via fetchProjectAssetsByType).
 *   - An "upload" tile that calls uploadProjectAsset and inserts the new
 *     asset at the head of the list.
 * The stored value is a signed URL string. NOTE: signed URLs may expire on
 * reload — users will need to re-pick if that happens. A future iteration
 * could store assetId + resolve at render time.
 */
const ImagePicker: React.FC<{
  value: string;
  onChange: (url: string) => void;
  projectId: string | null | undefined;
  companyId: string | null;
}> = ({ value, onChange, projectId, companyId }) => {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState<
    Array<{ id: string; url: string; name: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  // Load project images when the popover opens. We resolve signed URLs in
  // parallel so the grid renders thumbnails as soon as it's mounted.
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await fetchProjectAssetsByType(projectId, "image");
        if (cancelled) return;
        const withUrls = await Promise.all(
          list.map(async (a) => ({
            id: a.id,
            url: (await getAssetSignedUrl(a.storage_path)) || "",
            name: a.name || "Imagen",
          })),
        );
        if (!cancelled) setAssets(withUrls.filter((a) => a.url));
      } catch (e) {
        console.error("[ImagePicker] fetch assets failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const handleUpload = async (file: File) => {
    if (!projectId || !companyId) return;
    setUploading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      const url = await getAssetSignedUrl(asset.storage_path);
      if (url) {
        onChange(url);
        setAssets((prev) => [
          { id: asset.id, url, name: asset.name || file.name },
          ...prev,
        ]);
      }
    } catch (e) {
      console.error("[ImagePicker] upload failed", e);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-12 pl-1.5 pr-2 flex items-center gap-2 rounded-md border bg-white transition-colors ${
          open
            ? "border-brand-primary/40 ring-2 ring-brand-primary/15"
            : "border-black/[0.08] hover:border-black/[0.16]"
        }`}
        aria-label="Elegir imagen"
      >
        {value ? (
          <img
            src={value}
            alt=""
            className="w-9 h-9 rounded object-cover shrink-0 ring-1 ring-black/10"
          />
        ) : (
          <div className="w-9 h-9 rounded bg-neutral-100 flex items-center justify-center text-neutral-400 shrink-0">
            <ImageIcon className="w-4 h-4" />
          </div>
        )}
        <span className="text-[13px] text-neutral-700 truncate flex-1 text-left">
          {value ? "Imagen seleccionada" : "Elegir imagen…"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
      </button>
      {open && (
        <div
          data-clip-ui="true"
          className="absolute top-full right-0 mt-2 z-30 bg-white border border-neutral-200 rounded-lg shadow-md p-3 w-[320px]"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400">
              Imágenes del proyecto
            </p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !projectId || !companyId}
              className="h-7 px-2 inline-flex items-center gap-1 rounded-md bg-brand-primary text-white text-[12px] font-medium hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Upload className="w-3 h-3" />
              {uploading ? "Subiendo…" : "Subir"}
            </button>
          </div>
          {loading ? (
            <p className="text-[12px] text-neutral-500 py-6 text-center">
              Cargando…
            </p>
          ) : assets.length === 0 ? (
            <p className="text-[12px] text-neutral-500 py-6 text-center">
              No hay imágenes en el proyecto.
              <br />
              Subí una con el botón de arriba.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto">
              {assets.map((a) => {
                const isActive = a.url === value;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      onChange(a.url);
                      setOpen(false);
                    }}
                    className={`relative aspect-square rounded-md overflow-hidden ring-1 transition-all hover:ring-2 ${
                      isActive
                        ? "ring-2 ring-brand-primary"
                        : "ring-black/[0.06] hover:ring-brand-primary/40"
                    }`}
                    title={a.name}
                  >
                    <img
                      src={a.url}
                      alt={a.name}
                      className="w-full h-full object-cover"
                    />
                    {isActive && (
                      <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-primary text-white flex items-center justify-center">
                        <Check className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="mt-3 w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md text-[12px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Quitar imagen
            </button>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

/**
 * Per-text styling popover used inside the animation editor. The trigger is
 * the small "Aa" icon next to a text input; clicking it opens a card with
 * font / size / color / bold / italic / alignment controls scoped to that
 * specific piece of copy. The available aspects are declared by the
 * template via `TextStylingLinks`, which maps each aspect to a sibling prop
 * in the same schema. The component just reads/writes those props.
 */
const TextStyleButton: React.FC<{
  styling: TextStylingLinks;
  schema: Record<string, AnimationPropSpec | undefined>;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  /** Optional title shown at the top of the popover. */
  title?: string;
}> = ({ styling, schema, values, onChange, title }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const get = (key: string | undefined) => (key ? values[key] : undefined);
  const fontSpec = styling.font ? schema[styling.font] : undefined;
  const sizeSpec = styling.size ? schema[styling.size] : undefined;
  const alignSpec = styling.align ? schema[styling.align] : undefined;
  const boldVal = !!get(styling.bold);
  const italicVal = !!get(styling.italic);
  const colorVal = (get(styling.color) as string) || "#FFFFFF";
  const fontVal = (get(styling.font) as string) || "";
  const sizeVal = (get(styling.size) as string) || "";
  const alignVal = (get(styling.align) as string) || "center";

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={title || "Estilo del texto"}
        aria-label={title || "Estilo del texto"}
        className={`h-9 w-9 inline-flex items-center justify-center rounded-md border bg-white transition-colors ${
          open
            ? "border-brand-primary/40 ring-2 ring-brand-primary/15 text-brand-black"
            : "border-black/[0.08] text-neutral-500 hover:border-black/[0.16] hover:text-neutral-800"
        }`}
      >
        <Type className="w-4 h-4" />
      </button>
      {open && (
        <div
          data-clip-ui="true"
          className="absolute top-full right-0 mt-2 z-30 bg-white border border-neutral-200 rounded-lg shadow-md p-3 space-y-3"
          style={{ width: 280 }}
        >
          {title && (
            <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 -mb-1">
              {title}
            </p>
          )}

          {/* Tipografía — shared FontPicker so each option renders in its
              own typeface, identical to the text-clip toolbar. */}
          {fontSpec?.type === "font" && styling.font && (
            <div>
              <span className="block text-[12px] font-medium text-brand-black/70 mb-1">
                Tipografía
              </span>
              <FontPicker
                value={fontVal || "Inter"}
                onChange={(fam) => onChange(styling.font!, fam)}
                triggerClassName="h-8 w-full pl-2.5 pr-2 flex items-center justify-between gap-1 rounded-md border border-black/[0.08] bg-white text-[13px] text-brand-black/85 hover:border-black/[0.16] transition-colors"
                panelAlign="left"
                panelMinWidth={240}
              />
            </div>
          )}

          {/* Tamaño — −/+ pill with free numeric input. */}
          {sizeSpec?.type === "number" &&
            styling.size &&
            (() => {
              const cur = Number(get(styling.size) ?? 0);
              const step = sizeSpec.step ?? 1;
              const min = sizeSpec.min ?? 0;
              const max = sizeSpec.max ?? 9999;
              const dec = sizeSpec.decimals ?? 0;
              const clamp = (n: number) => Math.min(max, Math.max(min, n));
              return (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] font-medium text-brand-black/70">
                    {sizeSpec.label}
                  </span>
                  <div className="h-8 flex items-center rounded-md border border-black/[0.08] bg-white overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onChange(styling.size!, clamp(cur - step))}
                      className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                      title="Reducir"
                    >
                      −
                    </button>
                    <NumberField
                      value={cur}
                      min={min}
                      max={max}
                      step={step}
                      decimals={dec}
                      onCommit={(n) => onChange(styling.size!, n)}
                      className="w-12 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                      ariaLabel={sizeSpec.label}
                    />
                    <button
                      type="button"
                      onClick={() => onChange(styling.size!, clamp(cur + step))}
                      className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                      title="Aumentar"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })()}

          {/* Color */}
          {styling.color && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12px] font-medium text-brand-black/70">
                Color
              </span>
              <ColorSwatchPicker
                value={colorVal}
                onChange={(c) => onChange(styling.color!, c)}
                ariaLabel="Color del texto"
              />
            </div>
          )}

          {/* Bold / Italic / Alignment — compact toolbar row */}
          {(styling.bold || styling.italic || styling.align) && (
            <div className="flex items-center gap-2 pt-1 border-t border-black/[0.06]">
              {styling.bold && (
                <button
                  type="button"
                  onClick={() => onChange(styling.bold!, !boldVal)}
                  title="Negrita"
                  aria-pressed={boldVal}
                  className={`h-8 w-8 flex items-center justify-center rounded-md text-[14px] font-bold transition-colors ${
                    boldVal
                      ? "bg-neutral-900 text-white"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  B
                </button>
              )}
              {styling.italic && (
                <button
                  type="button"
                  onClick={() => onChange(styling.italic!, !italicVal)}
                  title="Cursiva"
                  aria-pressed={italicVal}
                  className={`h-8 w-8 flex items-center justify-center rounded-md text-[14px] italic font-serif transition-colors ${
                    italicVal
                      ? "bg-neutral-900 text-white"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  I
                </button>
              )}
              {alignSpec?.type === "select" && styling.align && (
                <div className="ml-auto inline-flex items-center bg-neutral-100 rounded-md p-0.5">
                  {alignSpec.options.map((opt) => {
                    const active = opt.value === alignVal;
                    const Icon =
                      opt.value === "left"
                        ? AlignLeft
                        : opt.value === "right"
                          ? AlignRight
                          : AlignCenter;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(styling.align!, opt.value)}
                        title={opt.label}
                        aria-label={opt.label}
                        aria-pressed={active}
                        className={`h-7 w-7 inline-flex items-center justify-center rounded transition-colors ${
                          active
                            ? "bg-white text-brand-black shadow-sm"
                            : "text-neutral-500 hover:text-neutral-700"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Subtitle generation ───────────────────────────────────────────────────
// Shape returned by the `/webhook/generar_transcript` endpoint. Each word
// comes with start/end timestamps (seconds, relative to the media start).
// `spacing` entries are whitespace runs and are ignored when chunking.
export interface TranscriptWord {
  text: string;
  start: number;
  end: number;
  type: "word" | "spacing" | string;
  logprob?: number;
}
export interface TranscriptResult {
  language_code?: string;
  language_probability?: number;
  text: string;
  words: TranscriptWord[];
  transcription_id?: string;
  audio_duration_secs?: number;
}

// URL of the n8n webhook that performs the transcription. Kept at module
// scope so tests can stub it, and so the call site stays readable.
const SUBTITLES_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/generar_transcript";

// Transporte del transcript (ex generar_transcript) → /v1/media/transcript con JWT
// cuando USE_ATLAS_BACKEND; fallback n8n. Devuelve un resultado discriminado para
// que cada call site conserve su manejo de error (throw / is_error / notice). Audio
// largo puede venir 202 (scribe tardó) → poleamos el job.
type TranscriptRequestResult =
  | { ok: true; transcript: TranscriptResult }
  | { ok: false; message: string };
async function requestTranscript(
  url: string,
  userId: string,
): Promise<TranscriptRequestResult> {
  const normalize = (raw: unknown): TranscriptResult | null => {
    const t = (
      Array.isArray(raw)
        ? raw[0]
        : raw && typeof raw === "object" && "data" in raw
          ? (raw as { data: TranscriptResult }).data
          : raw
    ) as TranscriptResult;
    return t && Array.isArray(t.words) ? t : null;
  };
  const badFormat = "La respuesta del servicio no tiene el formato esperado.";
  if (USE_ATLAS_BACKEND) {
    const res = await atlasBackendFetch("/v1/media/transcript", {
      user_id: userId,
      url,
    });
    let raw: unknown;
    if (res.status === 202) {
      const pending = (await res.json().catch(() => ({}))) as { job_id?: string };
      if (!pending.job_id) return { ok: false, message: "Transcript 202 sin job_id" };
      const settled = await pollJobUntilSettled(pending.job_id, {
        timeoutMs: 8 * 60 * 1000,
      });
      if (!settled)
        return { ok: false, message: "La transcripción tardó demasiado (timeout)." };
      if (settled.status === "failed")
        return { ok: false, message: settled.userMessage || "La transcripción falló." };
      raw = settled.output;
    } else {
      if (!res.ok) {
        if (res.status === 402) {
          return {
            ok: false,
            message:
              "No te alcanzan los créditos para transcribir este clip. Sumá créditos o actualizá tu plan.",
          };
        }
        const txt = await res.text().catch(() => "");
        return {
          ok: false,
          message: `El servicio de transcripción respondió ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`,
        };
      }
      raw = await res.json();
    }
    const t = normalize(raw);
    return t ? { ok: true, transcript: t } : { ok: false, message: badFormat };
  }
  const res = await fetch(SUBTITLES_WEBHOOK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({ user_id: userId, url }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    return {
      ok: false,
      message: `El servicio de transcripción respondió ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`,
    };
  }
  const t = normalize(await res.json());
  return t ? { ok: true, transcript: t } : { ok: false, message: badFormat };
}

// Maximum words per subtitle line and maximum duration (seconds) per
// subtitle clip. Tuned so chunks feel snappy on social-media vertical
// video — short enough to always fit on one line, long enough that the
// viewer can read them.
const SUBTITLE_MAX_WORDS = 6;
const SUBTITLE_MIN_WORDS = 2;
const SUBTITLE_MAX_DURATION_SEC = 3;
// Inter-word silence thresholds. A "large" pause (typical sentence beat)
// is treated as a strong break and splits as long as the current chunk
// has at least SUBTITLE_MIN_WORDS. A "medium" pause (short breath, hedge)
// only splits when the chunk is already substantial, so we don't slice
// natural phrases in half.
const SUBTITLE_LARGE_PAUSE_SEC = 0.45;
const SUBTITLE_MEDIUM_PAUSE_SEC = 0.25;

// ── Borrado de objetos: parámetros ─────────────────────────────────────────
// Bria procesa ~5s por llamada; n8n chunkea. Tope blando: 60s (si el usuario
// quiere más, el Director avisa que tarda y tiene más riesgo, y re-llama con
// confirmLongClip:true). El poll espera lo suyo porque chunkear + recoser tarda.
const ERASE_SOFT_CAP_SEC = 60;
const ERASE_POLL_MS = 8000; // cada 8s
const ERASE_MAX_POLLS = 225; // ~30 min de techo (clips largos chunkeados)
// Tarifa de créditos por segundo de video borrado. Base: Bria $0.14/s en
// Replicate. AJUSTAR con el jefe según el ratio crédito↔USD del producto.
const ERASE_CREDITS_PER_SEC = 1;

// Créditos que cuesta borrar un objeto en un clip de `durationSec` segundos.
function creditsForErase({ durationSec }: { durationSec: number }): number {
  return Math.ceil(Math.max(1, durationSec) * ERASE_CREDITS_PER_SEC);
}

// Break the ElevenLabs-style word stream into subtitle segments. We
// filter out `spacing` entries, then append real words into a running
// chunk until any of the following triggers a flush. The order matters:
// hard caps run before the word is pushed, soft breaks (punctuation /
// silence) run after.
//   Hard caps (pre-push):
//     1. Chunk already at SUBTITLE_MAX_WORDS
//     2. Adding this word would exceed SUBTITLE_MAX_DURATION_SEC
//   Soft breaks (post-push, in priority order):
//     3. Last word ended with sentence punctuation (`.`, `?`, `!`)
//     4. Last word ended with a comma AND chunk has ≥3 words
//     5. Silence to the next word ≥ SUBTITLE_LARGE_PAUSE_SEC AND chunk
//        has ≥ SUBTITLE_MIN_WORDS (natural beat between phrases)
//     6. Silence to the next word ≥ SUBTITLE_MEDIUM_PAUSE_SEC AND chunk
//        has ≥4 words (shorter beat, only used to avoid overstuffing)
// After the main loop, an orphan-tail pass merges a 1-word final chunk
// into the previous one whenever it fits — this stops the splitter from
// ending a video on a lonely "ya." or "sí." caption.
// Returns `[]` if no word-type entries exist.
export function chunkWordsIntoSubtitles(
  words: TranscriptWord[],
): Array<{ start: number; end: number; text: string }> {
  const out: Array<{ start: number; end: number; text: string }> = [];
  const real = (words || []).filter(
    (w) =>
      w &&
      w.type === "word" &&
      typeof w.start === "number" &&
      typeof w.end === "number",
  );
  if (real.length === 0) return out;
  let buf: TranscriptWord[] = [];
  const flush = () => {
    if (buf.length === 0) return;
    const start = buf[0].start;
    const end = buf[buf.length - 1].end;
    const text = buf
      .map((w) => w.text)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    if (text) out.push({ start, end, text });
    buf = [];
  };
  for (let i = 0; i < real.length; i++) {
    const w = real[i];
    const prospectiveDur = buf.length === 0 ? 0 : w.end - buf[0].start;
    if (
      buf.length >= SUBTITLE_MAX_WORDS ||
      prospectiveDur > SUBTITLE_MAX_DURATION_SEC
    ) {
      flush();
    }
    buf.push(w);
    const trimmed = w.text.replace(/\s+$/, "");
    const lastChar = trimmed.slice(-1);
    if (/[.!?]/.test(lastChar)) {
      flush();
      continue;
    }
    if (lastChar === "," && buf.length >= 3) {
      flush();
      continue;
    }
    // Use the silence between this word and the next as a soft break.
    // ElevenLabs reports word boundaries tightly enough that the gap is
    // a reliable signal of a natural phrase boundary.
    const next = real[i + 1];
    if (next) {
      const pause = next.start - w.end;
      if (
        pause >= SUBTITLE_LARGE_PAUSE_SEC &&
        buf.length >= SUBTITLE_MIN_WORDS
      ) {
        flush();
      } else if (pause >= SUBTITLE_MEDIUM_PAUSE_SEC && buf.length >= 4) {
        flush();
      }
    }
  }
  flush();
  // Orphan-tail merge. If the final segment is a single word and folding
  // it back into the previous segment keeps us under the word/duration
  // budgets (with a tiny duration fudge to absorb the just-closed gap),
  // do so. Pure UX polish: avoids closing a video on a lonely caption.
  if (out.length >= 2) {
    const last = out[out.length - 1];
    const prev = out[out.length - 2];
    const lastWordCount = last.text.split(/\s+/).filter(Boolean).length;
    const prevWordCount = prev.text.split(/\s+/).filter(Boolean).length;
    if (
      lastWordCount === 1 &&
      prevWordCount + 1 <= SUBTITLE_MAX_WORDS &&
      last.end - prev.start <= SUBTITLE_MAX_DURATION_SEC + 0.5
    ) {
      prev.text = `${prev.text} ${last.text}`.replace(/\s+/g, " ").trim();
      prev.end = last.end;
      out.pop();
    }
  }
  return out;
}

// Style fields that are kept in sync across every clip in the same
// subtitle group. We deliberately EXCLUDE per-clip data like `text`,
// `name`, `startTime`, `duration`, `id`, `isSubtitle`, `subtitleGroupId`
// and `subtitleStyleOverride` — those are always individual.
const SUBTITLE_STYLE_FIELDS = [
  "fontFamily",
  "fontSizePct",
  "fontWeight",
  "fontStyle",
  "textColor",
  "textAlign",
  "textXPct",
  "textYPct",
  "textWidthPct",
  "enterAnimation",
  "enterDurationSec",
  "exitAnimation",
  "exitDurationSec",
] as const;

// Apply a patch to a single clip and, if that clip is a non-overridden
// subtitle, also propagate the subset of style fields to every other
// non-overridden member of the same subtitle group. Pure / immutable —
// always returns a new array. Designed to drop in wherever we currently
// do `setClips(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c))`
// for a text clip.
function patchClipsWithSubtitleSync(
  prev: TimelineClip[],
  clipId: string,
  patch: Partial<TimelineClip>,
): TimelineClip[] {
  const target = prev.find((c) => c.id === clipId);
  if (!target) return prev;
  // Build the style-only patch up-front so we don't recompute it per row.
  let stylePatch: Partial<TimelineClip> | null = null;
  const shouldSync = !!(
    target.isSubtitle &&
    target.subtitleGroupId &&
    !target.subtitleStyleOverride
  );
  if (shouldSync) {
    const sp: Record<string, unknown> = {};
    let hasAny = false;
    for (const k of SUBTITLE_STYLE_FIELDS) {
      if (k in patch) {
        sp[k] = (patch as Record<string, unknown>)[k];
        hasAny = true;
      }
    }
    stylePatch = hasAny ? (sp as Partial<TimelineClip>) : null;
  }
  return prev.map((c) => {
    if (c.id === clipId) return { ...c, ...patch };
    if (
      stylePatch &&
      c.isSubtitle &&
      c.subtitleGroupId === target.subtitleGroupId &&
      !c.subtitleStyleOverride
    ) {
      return { ...c, ...stylePatch };
    }
    return c;
  });
}

// Default values applied when a brand-new text clip is created without
// explicit params (UI button or agent tool with missing fields).
const DEFAULT_TEXT_CLIP = {
  text: "Texto nuevo",
  fontFamily: "Inter",
  fontSizePct: 8,
  fontWeight: "normal" as const,
  fontStyle: "normal" as const,
  textColor: "#FFFFFF",
  textAlign: "center" as const,
  textXPct: 50,
  textYPct: 50,
  duration: 3,
};

// Target slot for generated image to replace when user hits "Utilizar"
type ImageGenTarget = { kind: "first" } | { kind: "last" } | { kind: "ref" };

// Generated image result (during or after generation)
interface GenImageResult {
  id: string; // imagenes_generadas.id
  fileName?: string;
  signedUrl?: string;
  loading: boolean;
  createdAt?: string; // imagenes_generadas.created_at — para ordenar en Multimedia
  session?: boolean; // true si se generó en ESTA sesión (transitorio, NO se persiste). El panel de generar solo muestra estas; el historial completo vive en Multimedia/Historial.
}

type TrackType = "video" | "audio" | "elements";

interface TrackConfig {
  type: TrackType;
  label: string;
  color: string;
  clipTypes: ClipType[];
}

const TRACK_CONFIG: TrackConfig[] = [
  {
    type: "elements",
    label: "Elementos",
    color: "#F59E0B",
    clipTypes: ["text", "image", "animation"],
  },
  {
    type: "video",
    label: "Video",
    color: "#1644EB",
    clipTypes: ["video", "ai-generated"],
  },
  { type: "audio", label: "Audio", color: "#10B981", clipTypes: ["audio"] },
];

// Build a transient DOM element styled like a timeline clip so the HTML5
// drag image matches the clip the user is about to create on drop, instead
// of a screenshot of the sidebar tile being dragged. The returned element
// is appended to <body> off-screen, used as the drag image (snapshot is
// taken synchronously by the browser), and then removed on the next tick.
function buildTimelineClipDragImage(
  type: "video" | "audio" | "image",
  name: string,
  thumbnailUrl?: string | null,
): HTMLElement {
  const COLORS = { video: "#1644EB", audio: "#10B981", image: "#F59E0B" };
  const color = COLORS[type];
  const root = document.createElement("div");
  root.style.position = "fixed";
  root.style.top = "-1000px";
  root.style.left = "-1000px";
  root.style.width = "200px";
  root.style.height = "40px";
  root.style.borderRadius = "6px";
  root.style.overflow = "hidden";
  root.style.boxShadow = "0 6px 16px rgba(0,0,0,0.18)";
  root.style.pointerEvents = "none";
  root.style.fontFamily = "inherit";
  root.style.fontSize = "11px";
  root.style.fontWeight = "500";
  root.style.display = "flex";
  root.style.alignItems = "center";
  if ((type === "video" || type === "image") && thumbnailUrl) {
    // Mimic the dark "frame strip" look used for video/image timeline clips.
    root.style.backgroundColor = "#0a0a0a";
    const img = document.createElement("div");
    img.style.flex = "1";
    img.style.height = "100%";
    img.style.backgroundImage = `url("${thumbnailUrl}")`;
    img.style.backgroundSize = "cover";
    img.style.backgroundPosition = "center";
    root.appendChild(img);
    const label = document.createElement("div");
    label.textContent = name;
    label.style.position = "absolute";
    label.style.left = "8px";
    label.style.right = "8px";
    label.style.bottom = "4px";
    label.style.color = "#fff";
    label.style.textShadow = "0 1px 2px rgba(0,0,0,0.6)";
    label.style.whiteSpace = "nowrap";
    label.style.overflow = "hidden";
    label.style.textOverflow = "ellipsis";
    root.style.position = "fixed"; // keep fixed for offscreen
    root.appendChild(label);
  } else {
    // Audio (and any thumbnail-less fallback): tinted block + colored
    // left border + colored truncated label, matching the clip layout.
    root.style.backgroundColor = color + "20";
    root.style.borderLeft = `3px solid ${color}`;
    root.style.padding = "0 10px";
    root.style.color = color;
    root.style.whiteSpace = "nowrap";
    const span = document.createElement("span");
    span.textContent = name;
    span.style.overflow = "hidden";
    span.style.textOverflow = "ellipsis";
    span.style.flex = "1";
    root.appendChild(span);
  }
  document.body.appendChild(root);
  setTimeout(() => {
    root.remove();
  }, 0);
  return root;
}

// Fetch an image URL and return a resized base64-encoded JPEG (no `data:`
// prefix). Used to embed generated images into Anthropic tool_result blocks
// so Claude can observe them directly.
async function urlToBase64Jpeg(
  url: string,
  maxWidth: number = 768,
): Promise<string> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = document.createElement("img");
      el.crossOrigin = "anonymous";
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("No se pudo decodificar la imagen"));
      el.src = objectUrl;
    });
    const ratio = img.naturalWidth > maxWidth ? maxWidth / img.naturalWidth : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No se pudo crear contexto canvas");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
    return dataUrl.split(",")[1] || "";
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Extract `count` frames evenly distributed along the video at `videoUrl`.
// Returns base64-encoded JPEGs (without the data: prefix) plus the timestamp
// in seconds where each frame was sampled. Used by the analyze_video_quick
// agent tool to surface visual context to Claude as image content blocks.
async function extractVideoFrames(
  videoUrl: string,
  count: number,
  maxWidth: number = 768,
): Promise<{ tSec: number; base64: string }[]> {
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.preload = "auto";
  video.muted = true;
  (video as any).playsInline = true;
  video.src = videoUrl;
  await new Promise<void>((resolve, reject) => {
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("No se pudo cargar el video"));
    };
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
    };
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
  });
  const duration = video.duration || 0;
  if (!isFinite(duration) || duration <= 0)
    throw new Error("Duración de video inválida");
  const ratio = video.videoWidth > maxWidth ? maxWidth / video.videoWidth : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(video.videoWidth * ratio));
  canvas.height = Math.max(1, Math.round(video.videoHeight * ratio));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo crear contexto canvas");
  const frames: { tSec: number; base64: string }[] = [];
  for (let i = 0; i < count; i++) {
    // Sample at evenly spaced internal points (avoids t=0 / t=duration which often render blank).
    const t = ((i + 0.5) / count) * duration;
    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("Error al buscar frame"));
      };
      const cleanup = () => {
        video.removeEventListener("seeked", onSeeked);
        video.removeEventListener("error", onError);
      };
      video.addEventListener("seeked", onSeeked, { once: true });
      video.addEventListener("error", onError, { once: true });
      try {
        video.currentTime = t;
      } catch (err) {
        cleanup();
        reject(err as Error);
      }
    });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    const base64 = dataUrl.split(",")[1] || "";
    frames.push({ tSec: t, base64 });
  }
  return frames;
}

// Rough estimate of speech duration (seconds) based on natural Spanish reading
// pace. Used to size placeholder AI clips (voice-over audio, dialogue scenes)
// in the timeline BEFORE generation finishes. The real duration is probed
// afterwards and updates the clip.
//   ~15 chars/s at speed=1.0 (Spanish natural), scaled by the speed multiplier.
//   +0.5s tail for natural pauses/breaths. Returns 0 for empty text.
const estimateSpeechDuration = (text: string, speed: number = 1.0): number => {
  const t = (text || "").trim();
  if (!t) return 0;
  const charsPerSecondAtSpeed1 = 15;
  const s = Math.max(0.1, speed);
  return t.length / charsPerSecondAtSpeed1 / s + 0.5;
};

const getTrackForClipType = (clipType: ClipType): TrackConfig =>
  TRACK_CONFIG.find((t) => t.clipTypes.includes(clipType)) || TRACK_CONFIG[0];

const getClipTrackIndex = (c: TimelineClip): number => c.trackIndex ?? 0;

// Two video clips count as "adjacent" (and therefore a transition target)
// when they're on the same row of the same track and either touching or
// already overlapping by at most ADJACENCY_OVERLAP_LIMIT seconds. A larger
// gap is treated as intentional silence/jump-cut.
const ADJACENCY_GAP_LIMIT = 0.15;
const ADJACENCY_OVERLAP_LIMIT = 5;
const isVideoClip = (c: TimelineClip): boolean =>
  c.type === "video" || c.type === "ai-generated";

// Returns ordered pairs (left, right) of adjacent video clips. Pairs are
// derived once per `clips` change so the timeline can render the `+`
// indicators without recomputing on every frame.
const findAdjacentVideoPairs = (
  clips: TimelineClip[],
): Array<{ left: TimelineClip; right: TimelineClip }> => {
  const videos = clips.filter(isVideoClip);
  // Group by track index — only same-row clips are candidates.
  const byTrack = new Map<number, TimelineClip[]>();
  for (const c of videos) {
    const idx = getClipTrackIndex(c);
    if (!byTrack.has(idx)) byTrack.set(idx, []);
    byTrack.get(idx)!.push(c);
  }
  const pairs: Array<{ left: TimelineClip; right: TimelineClip }> = [];
  for (const list of byTrack.values()) {
    list.sort((a, b) => a.startTime - b.startTime);
    for (let i = 0; i < list.length - 1; i++) {
      const left = list[i];
      const right = list[i + 1];
      const leftEnd = left.startTime + left.duration;
      const gap = right.startTime - leftEnd;
      if (gap <= ADJACENCY_GAP_LIMIT && gap >= -ADJACENCY_OVERLAP_LIMIT) {
        pairs.push({ left, right });
      }
    }
  }
  return pairs;
};

// Where the fade sits relative to the boundary between A and B.
// 0.5 = perfectly centered: half the fade happens BEFORE B.startTime
// (while A is still in normal playback) and half AFTER (while A is in
// extension). Smaller values bias the fade toward B's side; larger
// values bias toward A's side. Centered is the sweet spot — both clips
// contribute real motion to the transition instead of one being a still.
const TRANSITION_EARLY_FRACTION = 0.5;

// Returns the [start, end] timeline range over which clip B's transition
// plays out, or null if clip has no transitionIn. The window is centered
// over the boundary between A and B per `TRANSITION_EARLY_FRACTION`, so
// it actually starts a bit BEFORE clip.startTime.
const getTransitionWindow = (
  clip: TimelineClip,
): { start: number; end: number; duration: number } | null => {
  if (!clip.transitionIn || clip.transitionIn.durationSec <= 0) return null;
  const D = clip.transitionIn.durationSec;
  const start = clip.startTime - D * TRANSITION_EARLY_FRACTION;
  const end = clip.startTime + D * (1 - TRANSITION_EARLY_FRACTION);
  return { start: Math.max(0, start), end, duration: D };
};

// Look up the clip that immediately succeeds `clip` on the same video
// track and carries a `transitionIn`. "Immediately" = touching (gap ≈ 0)
// or with a tiny gap up to ADJACENCY_GAP_LIMIT. Also tolerates the
// legacy overlapping arrangement (when transitions used to ripple-shift)
// so existing projects keep working.
const findTransitionSuccessor = (
  clip: TimelineClip,
  clips: TimelineClip[],
): TimelineClip | null => {
  if (!isVideoClip(clip)) return null;
  const myTrack = getClipTrackIndex(clip);
  const myEnd = clip.startTime + clip.duration;
  const candidate = clips
    .filter(
      (c) =>
        c.id !== clip.id &&
        isVideoClip(c) &&
        getClipTrackIndex(c) === myTrack &&
        c.transitionIn &&
        c.transitionIn.durationSec > 0,
    )
    .filter((c) => {
      const gap = c.startTime - myEnd;
      return gap <= ADJACENCY_GAP_LIMIT && gap >= -ADJACENCY_OVERLAP_LIMIT;
    })
    .sort((a, b) => a.startTime - b.startTime)[0];
  return candidate || null;
};

// Whether a clip should be treated as "playing" at a given timeline time.
// Normally that's just `[startTime, startTime + duration)`, but the
// active window stretches in two situations to support transitions
// without moving any clip on the timeline:
//
//   1. The clip itself has `transitionIn` and the fade has started
//      EARLIER than `clip.startTime` (because the window is centered
//      over the boundary). The clip is "pre-active" for that lead-in
//      portion — its <video> stays mounted and we freeze on its first
//      frame so the opacity fade has something to draw.
//
//   2. The clip is the immediate adjacent predecessor of one with a
//      `transitionIn` and the fade is still in progress past this
//      clip's end. The clip is "extended" for the trailing portion —
//      its <video> freezes on the last frame for the same reason.
const isClipActiveAtTime = (
  clip: TimelineClip,
  time: number,
  clips: TimelineClip[],
): boolean => {
  // Normal range first — the common case on every frame.
  if (time >= clip.startTime && time < clip.startTime + clip.duration)
    return true;
  if (!isVideoClip(clip)) return false;
  // Pre-active: this clip's own transitionIn has a window that starts
  // before `clip.startTime`.
  if (clip.transitionIn) {
    const win = getTransitionWindow(clip);
    if (win && time >= win.start && time < clip.startTime) return true;
  }
  // Extended: a successor's fade is in progress past this clip's end.
  const successor = findTransitionSuccessor(clip, clips);
  if (successor) {
    const win = getTransitionWindow(successor);
    if (win && time >= clip.startTime && time < win.end) return true;
  }
  return false;
};

// Per-frame render state for a clip. Most transitions only need to
// modulate `opacity`; the slide family also drives `translateXPct` /
// `translateYPct` (percent of clip width / height), and the zoom family
// uses `scale`. Adding a new transition type usually means returning a
// new combination here without touching the timeline / playback code.
interface ClipRenderState {
  opacity: number;
  translateXPct?: number;
  translateYPct?: number;
  scale?: number;
  blurPx?: number;
  // Direction of motion blur. 'x' = horizontal streak (for horizontal
  // slides), 'y' = vertical streak. Implemented via an SVG <feGaussianBlur>
  // with one of the stdDeviation axes pinned to 0 — CSS `filter: blur()`
  // is Gaussian/omnidirectional and gives a "lens defocus" look instead.
  blurDirection?: "x" | "y";
  // Horizontal wipe — used by the `reveal` transition. The clip is
  // visible only in the [0, wipeShowLeftPct%] horizontal range. The
  // outgoing side uses `wipeShowRightPct` to mask the LEFT side
  // instead, so the two clips fit together exactly along the wipe edge.
  wipeShowLeftPct?: number;
  wipeShowRightPct?: number;
  // Inset clip mask used by the `diapositivas` transition. The clip is
  // shown only inside a centered rectangle whose edges are insetPct
  // away from the container, with rounded corners of `slideRoundPx`.
  // Combined with translateXPct, the rectangle can also be slid in/out.
  slideInsetPct?: number;
  slideRoundPx?: number;
  // Per-axis scale used by the stretch transitions. When set, they
  // override the uniform `scale` field for that axis. Setting only one
  // (e.g. just stretchX) deforms the clip along that axis while the
  // other dimension stays at 1.
  stretchX?: number;
  stretchY?: number;
}

// Builds the CSS `transform` string for a render state. Centralised so
// the playback render and the preview tile compose transforms the same
// way. Returns `undefined` when there's no transform (vast majority of
// frames) so React skips the style entirely.
//
// Scale handling: a transition can either set a uniform `scale` (zoom,
// zoom-shake) OR per-axis `stretchX`/`stretchY` (stretch transitions).
// Per-axis takes priority when present so we can stretch one axis
// without touching the other.
const buildClipTransform = (s: ClipRenderState): string | undefined => {
  const parts: string[] = [];
  if (s.translateXPct != null && s.translateXPct !== 0)
    parts.push(`translateX(${s.translateXPct}%)`);
  if (s.translateYPct != null && s.translateYPct !== 0)
    parts.push(`translateY(${s.translateYPct}%)`);
  const sx = s.stretchX ?? s.scale;
  const sy = s.stretchY ?? s.scale;
  if (sx != null || sy != null) {
    const x = sx ?? 1;
    const y = sy ?? 1;
    if (x === y) parts.push(`scale(${x})`);
    else parts.push(`scale(${x}, ${y})`);
  }
  return parts.length > 0 ? parts.join(" ") : undefined;
};

// Builds the CSS `clip-path` for any clip-shape effect. Returns
// undefined when no shape is set (vast majority of frames).
//
//   wipeShowLeftPct / wipeShowRightPct: horizontal wipe used by the
//     `reveal` transition.
//   slideInsetPct / slideRoundPx: centred inset rectangle with rounded
//     corners, used by `diapositivas` to mask each clip into a slide.
const buildClipClipPath = (s: ClipRenderState): string | undefined => {
  if (s.wipeShowLeftPct != null) {
    const visible = Math.max(0, Math.min(100, s.wipeShowLeftPct));
    return `inset(0 ${100 - visible}% 0 0)`;
  }
  if (s.wipeShowRightPct != null) {
    const visible = Math.max(0, Math.min(100, s.wipeShowRightPct));
    return `inset(0 0 0 ${100 - visible}%)`;
  }
  if (s.slideInsetPct != null && s.slideInsetPct > 0) {
    const inset = Math.max(0, Math.min(45, s.slideInsetPct));
    const round = Math.max(0, s.slideRoundPx ?? 0);
    return `inset(${inset}% round ${round}px)`;
  }
  return undefined;
};

// Two-stage piecewise ease used by the `reveal` swipe-line. The first
// third of the time advances LINEARLY but at a slow pace (covers only
// `SLOW_PHASE_PROGRESS` of the distance), then the remaining two-thirds
// snap LINEARLY at a much faster pace to finish the run. Two distinct
// constant speeds with a kink in the middle reads as "build then whip"
// far more clearly than a smooth cubic/exponential curve.
const REVEAL_SLOW_PHASE_T = 0.6;
const REVEAL_SLOW_PHASE_PROGRESS = 0.32;
const revealLineEase = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  if (c <= REVEAL_SLOW_PHASE_T) {
    return (c / REVEAL_SLOW_PHASE_T) * REVEAL_SLOW_PHASE_PROGRESS;
  }
  const t2 = (c - REVEAL_SLOW_PHASE_T) / (1 - REVEAL_SLOW_PHASE_T);
  return REVEAL_SLOW_PHASE_PROGRESS + t2 * (1 - REVEAL_SLOW_PHASE_PROGRESS);
};

// Same idea for `filter`. Returns a reference to the directional motion
// blur SVG filter (`#atlas-motion-blur`) when the clip is mid-slide.
// The SVG element itself is rendered inside the preview container and
// its `stdDeviation` is updated each frame (see `motionBlurState`).
// When there's no blur on this clip we return undefined so React drops
// the style entirely — no filter computation cost on the GPU.
const MOTION_BLUR_FILTER_ID = "atlas-motion-blur";
const buildClipFilter = (s: ClipRenderState): string | undefined => {
  if (s.blurPx == null || s.blurPx < 0.1) return undefined;
  if (s.blurDirection === "x" || s.blurDirection === "y") {
    return `url(#${MOTION_BLUR_FILTER_ID})`;
  }
  // Fallback: omnidirectional CSS blur (kept for future non-slide
  // transitions that might want a Gaussian defocus).
  return `blur(${s.blurPx}px)`;
};

// Blur curve used by transitions that "punctuate" their middle: peaks at
// t=0.5 and lands sharp at both ends. Sine instead of a triangle gives
// a softer, more organic ramp. Used by transitions where motion is
// roughly symmetric in time (zoom punch). Each transition picks its own
// peak magnitude so the blur intensity matches the visual strength.
const blurPeakAt = (t: number, peakPx: number): number =>
  peakPx * Math.sin(Math.PI * Math.max(0, Math.min(1, t)));

// Punch ease-out cubic: front-loads the motion so 70% of the distance is
// covered in the first 30% of the time. This is what gives slides their
// "whip" feel (the visual signature of viral edits) instead of the
// "smooth premium" feel of linear interpolation. Matches CSS's
// `cubic-bezier(0.2, 0.9, 0.3, 1)` ballpark.
const punchEaseOut = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
};

// Back ease-out — motion overshoots the target slightly then settles
// back to it. Used by the slide variants so the incoming clip "lands
// with a small bounce" instead of stopping flat. `SLIDE_BOUNCE_OVERSHOOT`
// is intentionally small (~1.0) for a minimum/subtle bounce — higher
// values (1.7+) read as a cartoon-y spring which doesn't fit the viral
// edit aesthetic.
const SLIDE_BOUNCE_OVERSHOOT = 1.0;
const easeOutBack = (t: number): number => {
  const c = Math.max(0, Math.min(1, t)) - 1;
  return (
    c * c * ((SLIDE_BOUNCE_OVERSHOOT + 1) * c + SLIDE_BOUNCE_OVERSHOOT) + 1
  );
};

// Normalised velocity of the punch ease-out at time `t`. Used to drive
// motion blur magnitude — when the position is moving fastest (start of
// the curve) the blur is heaviest, when motion has settled (end) blur
// goes back to 0. This matches how camera shutter integration works in
// real life and is what makes whip-style blur read as "fast" instead of
// "out of focus".
const punchEaseOutVelocity = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  return Math.pow(1 - c, 2);
};

// Blur peak constants. Higher values give a more aggressive viral whip;
// lower values feel "premium / cinematic". Tuneable from one place.
const SLIDE_BLUR_PEAK_PX = 28;
const ZOOM_BLUR_PEAK_PX = 32;

// Per-frame shake offsets used by the `zoom-shake` transition. Tuning
// notes from earlier iterations:
//
//   * Amplitude has to be big enough to register visually — at 2% on a
//     1080-wide frame each "kick" is ~22px, which is the floor for the
//     eye to read it as a separate displacement instead of just blur.
//     Going to 6% (~65px) lands clearly in the "violent action camera"
//     territory.
//   * Frequencies must sit around 8–18 Hz. Anything higher than ~25 Hz
//     blurs into continuous vibration (you can't see individual jolts);
//     anything lower than ~5 Hz reads as a slow swing instead of a
//     shake. We layer one main low-freq oscillation with a smaller
//     high-freq twitch on top so it doesn't look like a single sine.
//   * Phase offsets keep X and Y from syncing into a diagonal line.
//   * Trapezoidal envelope: full amplitude across most of the
//     transition, ramping in/out only at the very edges so the shake
//     starts fast and stops fast (no slow buildup before the punch).

// Incoming-side animation. `t` ∈ [0..1] is the normalized progress through
// the transition window (0 = clip B just appearing, 1 = transition done).
const computeIncomingTransitionState = (
  type: TransitionType,
  t: number,
): ClipRenderState => {
  switch (type) {
    case "crossfade":
      return { opacity: t };
    case "fade-to-black":
      // B is invisible for the first half (while A fades to black), then
      // ramps in during the second half.
      return { opacity: Math.max(0, t * 2 - 1) };
    case "slide": {
      // B whip-pans in from the right with a small bounce on landing.
      // `easeOutBack` overshoots ~3% past centre then settles, while
      // the velocity-tracked directional blur fades to 0 right at the
      // overshoot peak so the bounce reads as a clean visible wobble
      // rather than a blur smear.
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateXPct: (1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "x",
      };
    }
    case "slide-left": {
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateXPct: -(1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "x",
      };
    }
    case "slide-up": {
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateYPct: (1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "y",
      };
    }
    case "slide-down": {
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateYPct: -(1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "y",
      };
    }
    case "reveal": {
      // White swipe-line travels left → right across the frame.
      // Uses an exponential ease-in (slow build → fast finish) so it
      // feels like the line gathers momentum, instead of the mechanical
      // pace a linear wipe would have. B is masked to the area behind
      // the line that has already passed.
      const eased = revealLineEase(t);
      return {
        opacity: 1,
        wipeShowLeftPct: eased * 100, // shows left N% — grows with the line
      };
    }
    case "zoom": {
      // "Zoom punch" — A keeps zooming in fast; at the cut moment
      // (t≈0.5) B reveals SHARPLY at ~3.5×, then de-zooms linearly to
      // 1× over the entire second half so the user can clearly see the
      // settle motion. Two key choices that make this read as "viral"
      // instead of "smooth crossfade":
      //
      //   1) Sharp opacity cut around t=0.5 (10% of duration window)
      //      → B reaches full opacity well before the de-zoom finishes,
      //        so the entire de-zoom motion plays with B fully visible.
      //   2) LINEAR settle for the scale (not eased) — the de-zoom
      //      takes the whole second half to play out, giving the eye
      //      time to register the motion. Easing it would compress
      //      the motion into the first 30% of the visible window
      //      and the user would barely notice it.
      const opacity = t < 0.45 ? 0 : Math.min(1, (t - 0.45) / 0.1);
      const visT = Math.max(0, t * 2 - 1); // 0 at t=0.5, 1 at t=1
      return {
        opacity,
        scale: 1 + (1 - visT) * 2.5, // 3.5× → 1× LINEAR over the full visible window
        blurPx: blurPeakAt(t, ZOOM_BLUR_PEAK_PX),
      };
    }
    case "light-leak":
    case "plastic":
    case "distorsion":
    case "vhs":
    case "vintage":
    case "glitch": {
      // Sharp cut at the midpoint — A and B don't animate themselves
      // (no scale/translate/blur), the overlay video composited on top
      // does the visual work. B becomes visible right at t=0.5 with a
      // tiny 5% ramp so the cut isn't perfectly binary.
      return {
        opacity: t < 0.5 ? 0 : Math.min(1, (t - 0.5) / 0.05),
      };
    }
    case "stretch-horizontal":
    case "stretch-vertical": {
      // B comes in HEAVILY stretched along the transition's axis and
      // de-stretches linearly back to 1× over the second half — same
      // shape as the `zoom` settle but per-axis. The other axis stays
      // at 1 throughout, so the clip looks elongated along the chosen
      // direction. Sharp opacity ramp around t=0.5 so the eye sees a
      // clear "snap" rather than a slow crossfade.
      const opacity = t < 0.45 ? 0 : Math.min(1, (t - 0.45) / 0.1);
      const visT = Math.max(0, t * 2 - 1);
      const stretch = 1 + (1 - visT) * 3; // 4× → 1× linear
      const isHoriz = type === "stretch-horizontal";
      return {
        opacity,
        stretchX: isHoriz ? stretch : 1,
        stretchY: isHoriz ? 1 : stretch,
        // Directional motion blur along the stretch axis amplifies the
        // "smear" feel — same SVG filter the slides use.
        blurPx: blurPeakAt(t, SLIDE_BLUR_PEAK_PX),
        blurDirection: isHoriz ? "x" : "y",
      };
    }
    case "diapositivas": {
      // Slideshow-style transition with three temporal phases:
      //
      //   t ∈ [0.00, 0.25]  CROP: A morphs into a centred rounded
      //                      "slide" (B isn't visible yet)
      //   t ∈ [0.25, 0.75]  SLIDE: A slides off-screen left, B slides
      //                      in from off-screen right — both stay
      //                      framed as the rounded slide
      //   t ∈ [0.75, 1.00]  UNCROP: B's slide expands back to fill
      //                      the whole canvas
      //
      // B's perspective:
      //   • Phase 1: invisible (off-screen + alpha 0).
      //   • Phase 2: enters from translateX +150% with the slide mask.
      //   • Phase 3: stays at translate 0, mask animates back to full.
      if (t < 0.25) return { opacity: 0 };
      if (t < 0.75) {
        // Front-loaded slide motion: B snaps in from the right and
        // settles smoothly. Linear interpolation reads as "mechanical"
        // here — the punch ease-out is what gives the viral whip feel.
        const slideT = punchEaseOut((t - 0.25) / 0.5);
        return {
          opacity: 1,
          translateXPct: (1 - slideT) * 150,
          slideInsetPct: DIAPOS_INSET_PCT,
          slideRoundPx: DIAPOS_ROUND_PX,
        };
      }
      // Uncrop also uses ease-out so the expansion feels snappy at first
      // then lands gently at full-frame.
      const uncropT = punchEaseOut((t - 0.75) / 0.25);
      return {
        opacity: 1,
        translateXPct: 0,
        slideInsetPct: DIAPOS_INSET_PCT * (1 - uncropT),
        slideRoundPx: DIAPOS_ROUND_PX * (1 - uncropT),
      };
    }
  }
};

// Catalogue of overlay-video transitions. Each entry maps a transition
// type to the URL of an MP4 with a black background — the video is
// composited on top of the A/B cut via `mix-blend-mode: screen`, which
// treats black as transparent so only the bright effect (light leak,
// glitch, flash, etc.) is visible. The actual A→B cut happens at the
// midpoint of the transition window where the overlay is at its peak
// brightness, so the cut is visually masked by the effect.
//
// To add more overlays: drop the .mp4 in `project/public/images/transitions/`
// and add a new entry here + a new TransitionType variant.
const OVERLAY_TRANSITIONS: Partial<Record<TransitionType, string>> = {
  "light-leak": "/images/transitions/Light Leaks 09.mp4",
  plastic: "/images/transitions/Plastic 01.mp4",
  distorsion: "/images/transitions/Scratches 05.mp4",
  vhs: "/images/transitions/VHS 07.mp4",
  vintage: "/images/transitions/Vintage Film 08.mp4",
  glitch: "/images/transitions/Glitch 04.mp4",
};

// Tuneables for the `diapositivas` transition. Higher inset = smaller
// "polaroid"-style slide with more black around it. Bigger round = more
// pronounced corner radius. Tuned so the slide reads as a distinct card
// inside the frame, not just a slightly-cropped video.
const DIAPOS_INSET_PCT = 18;
const DIAPOS_ROUND_PX = 30;

// Outgoing-side animation for the predecessor clip during the same window.
const computeOutgoingTransitionState = (
  type: TransitionType,
  t: number,
): ClipRenderState => {
  switch (type) {
    case "crossfade":
      return { opacity: 1 - t };
    case "fade-to-black":
      // A fades to black over the first half, then stays at 0.
      return { opacity: Math.max(0, 1 - t * 2) };
    case "slide":
    case "slide-left": {
      // A holds steady but inherits the same velocity-tracked horizontal
      // blur as B — sells the "whole frame is whipping" feel of viral
      // edits instead of looking like B is sliding through static A.
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "x",
      };
    }
    case "slide-up":
    case "slide-down": {
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "y",
      };
    }
    case "reveal": {
      // A is masked to the area the line HASN'T passed yet — i.e. the
      // right side. As the line moves left → right, A's visible region
      // shrinks from the right edge inward, perfectly meeting B at the
      // line's position.
      const eased = revealLineEase(t);
      return {
        opacity: 1,
        wipeShowRightPct: (1 - eased) * 100,
      };
    }
    case "zoom": {
      // A explodes outward fast via punch-ease-out so by t≈0.3 it's
      // already at ~3×, fully blurred, ready to hand off. Stays fully
      // visible during the zoom-in, then SHARPLY cuts out around the
      // same t=0.45–0.55 window where B reveals. Mirroring B's sharp
      // opacity ramp here is what makes the cut feel like a punch
      // instead of a slow crossfade — together with the eased scale
      // and peak blur, the eye reads it as a single explosive moment.
      const eased = punchEaseOut(t);
      const opacity = t < 0.45 ? 1 : Math.max(0, 1 - (t - 0.45) / 0.1);
      return {
        opacity,
        scale: 1 + eased * 3.0, // 1× → 4× front-loaded
        blurPx: blurPeakAt(t, ZOOM_BLUR_PEAK_PX),
      };
    }
    case "light-leak":
    case "plastic":
    case "distorsion":
    case "vhs":
    case "vintage":
    case "glitch": {
      // Mirror of the incoming side — A stays fully visible until t=0.5
      // then cuts out sharply.
      return {
        opacity: t < 0.5 ? 1 : Math.max(0, 1 - (t - 0.5) / 0.05),
      };
    }
    case "stretch-horizontal":
    case "stretch-vertical": {
      // A explodes outward along the stretch axis with punch ease-out
      // (front-loaded, fast snap) reaching ~4× by the cut. The OTHER
      // axis stays at 1 so the clip elongates rather than zooming.
      // Same opacity / blur logic as the zoom outgoing.
      const eased = punchEaseOut(t);
      const opacity = t < 0.45 ? 1 : Math.max(0, 1 - (t - 0.45) / 0.1);
      const stretch = 1 + eased * 3; // 1× → 4× front-loaded
      const isHoriz = type === "stretch-horizontal";
      return {
        opacity,
        stretchX: isHoriz ? stretch : 1,
        stretchY: isHoriz ? 1 : stretch,
        blurPx: blurPeakAt(t, SLIDE_BLUR_PEAK_PX),
        blurDirection: isHoriz ? "x" : "y",
      };
    }
    case "diapositivas": {
      // A's perspective in the slideshow transition:
      //   • Phase 1 (CROP): morph from full-frame into the rounded
      //     slide rectangle (mask animates in, no translate yet).
      //   • Phase 2 (SLIDE): slide off-screen to the LEFT while
      //     keeping the slide mask, finishing fully out of view.
      //   • Phase 3 (UNCROP): A is gone — opacity 0.
      if (t < 0.25) {
        // Crop morphs A from full-frame into the slide. Ease-out so
        // the inset/round shoot in fast and land smoothly — the
        // polaroid "appears" with a snap rather than a slow grow.
        const cropT = punchEaseOut(t / 0.25);
        return {
          opacity: 1,
          translateXPct: 0,
          slideInsetPct: DIAPOS_INSET_PCT * cropT,
          slideRoundPx: DIAPOS_ROUND_PX * cropT,
        };
      }
      if (t < 0.75) {
        const slideT = punchEaseOut((t - 0.25) / 0.5);
        return {
          opacity: 1,
          translateXPct: -slideT * 150,
          slideInsetPct: DIAPOS_INSET_PCT,
          slideRoundPx: DIAPOS_ROUND_PX,
        };
      }
      return { opacity: 0 };
    }
  }
};

// Returns the per-frame render state (opacity + optional transform) for
// `clip` at the given timeline `time`. Returns `{ opacity: 1 }` for clips
// that don't intersect any transition window — the common case — so the
// caller can short-circuit cheaply.
const computeClipRenderStateAtTime = (
  clip: TimelineClip,
  time: number,
  clips: TimelineClip[],
): ClipRenderState => {
  // Incoming side: clip has its own transitionIn. The fade window is
  // centered over the boundary so it actually starts a bit before
  // `clip.startTime` (see TRANSITION_EARLY_FRACTION).
  if (clip.transitionIn) {
    const win = getTransitionWindow(clip);
    if (win && time >= win.start && time <= win.end) {
      const t = Math.max(0, Math.min(1, (time - win.start) / win.duration));
      return computeIncomingTransitionState(clip.transitionIn.type, t);
    }
  }
  // Outgoing side: this clip is the adjacent predecessor of a clip that
  // has `transitionIn` and the (centered) fade is currently in progress.
  if (isVideoClip(clip)) {
    const successor = findTransitionSuccessor(clip, clips);
    if (successor && successor.transitionIn) {
      const win = getTransitionWindow(successor);
      if (win && time >= win.start && time <= win.end) {
        const t = Math.max(0, Math.min(1, (time - win.start) / win.duration));
        return computeOutgoingTransitionState(successor.transitionIn.type, t);
      }
    }
  }
  return { opacity: 1 };
};

// Distinct colors for each audio track. Kept in the same vivid-yet-soft family
// as the video (#1644EB) and elements (#F59E0B) tracks so everything feels
// visually consistent. Cycles for trackIndex beyond the list length.
const AUDIO_TRACK_COLORS = [
  "#10B981", // emerald (default, matches historic audio color)
  "#06B6D4", // sky / cyan
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#F97316", // orange
  "#EAB308", // yellow
  "#14B8A6", // teal
];
const getAudioTrackColor = (trackIndex: number): string =>
  AUDIO_TRACK_COLORS[
    ((trackIndex % AUDIO_TRACK_COLORS.length) + AUDIO_TRACK_COLORS.length) %
      AUDIO_TRACK_COLORS.length
  ];

const getNextStartTime = (
  clips: TimelineClip[],
  trackConfig: TrackConfig,
  trackIndex = 0,
): number => {
  const trackClips = clips.filter(
    (c) =>
      trackConfig.clipTypes.includes(c.type) &&
      getClipTrackIndex(c) === trackIndex,
  );
  if (trackClips.length === 0) return 0;
  return Math.max(...trackClips.map((c) => c.startTime + c.duration));
};

// ── Lightweight markdown renderer (chat bubbles) ─────────────────────────────
// Supports: **bold**, *italic*, _italic_, `code`, [label](url), ordered/unordered lists,
// headings (#..######), and preserves line breaks. No external dependencies.
const renderInlineMarkdown = (s: string): (string | React.ReactElement)[] => {
  const out: (string | React.ReactElement)[] = [];
  const re =
    /(\*\*[^*\n]+\*\*)|(`[^`\n]+`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1]) out.push(<strong key={key++}>{m[1].slice(2, -2)}</strong>);
    else if (m[2])
      out.push(
        <code
          key={key++}
          className="px-1 py-0.5 rounded bg-black/10 text-[0.9em] font-mono"
        >
          {m[2].slice(1, -1)}
        </code>,
      );
    else if (m[3])
      out.push(
        <a
          key={key++}
          href={m[5]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
        >
          {m[4]}
        </a>,
      );
    else if (m[6]) out.push(<em key={key++}>{m[6].slice(1, -1)}</em>);
    else if (m[7]) out.push(<em key={key++}>{m[7].slice(1, -1)}</em>);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
};

const renderMarkdown = (text: string): React.ReactNode => {
  const lines = text.split("\n");
  const blocks: React.ReactElement[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trimStart();
    // Unordered list
    if (/^(-|\*)\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^(-|\*)\s+/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^(-|\*)\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={k++} className="list-disc pl-5 space-y-0.5 my-1">
          {items.map((it, idx) => (
            <li key={idx}>{renderInlineMarkdown(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={k++} className="list-decimal pl-5 space-y-0.5 my-1">
          {items.map((it, idx) => (
            <li key={idx}>{renderInlineMarkdown(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }
    // GFM table: a header row of pipes followed by a separator row like
    // | --- | :---: | ---: |. Body rows continue until a non-pipe line.
    const isPipeRow = (s: string) => /^\s*\|.*\|\s*$/.test(s);
    const isSeparator = (s: string) =>
      /^\s*\|?\s*:?-{3,}:?(\s*\|\s*:?-{3,}:?)+\s*\|?\s*$/.test(s);
    if (isPipeRow(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const splitRow = (s: string) =>
        s
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => c.trim());
      const headers = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map((spec) => {
        const l = spec.startsWith(":");
        const r = spec.endsWith(":");
        if (l && r) return "center";
        if (r) return "right";
        return "left";
      });
      i += 2;
      const rows: string[][] = [];
      while (
        i < lines.length &&
        isPipeRow(lines[i]) &&
        !isSeparator(lines[i])
      ) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push(
        <div
          key={k++}
          className="my-2 overflow-x-auto rounded-md border border-black/[0.08]"
        >
          <table className="w-full text-[13px] border-collapse">
            <thead className="bg-black/[0.03]">
              <tr>
                {headers.map((h, hi) => (
                  <th
                    key={hi}
                    className="px-3 py-2 font-semibold text-brand-black/80 border-b border-black/[0.08]"
                    style={{
                      textAlign: aligns[hi] as "left" | "center" | "right",
                    }}
                  >
                    {renderInlineMarkdown(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr
                  key={ri}
                  className={
                    ri < rows.length - 1 ? "border-b border-black/[0.06]" : ""
                  }
                >
                  {r.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-2 align-top"
                      style={{
                        textAlign: aligns[ci] as "left" | "center" | "right",
                      }}
                    >
                      {renderInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    // Heading
    const hMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (hMatch) {
      const level = Math.min(hMatch[1].length + 2, 6);
      const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
      blocks.push(
        React.createElement(
          Tag,
          { key: k++, className: "font-semibold my-1" },
          renderInlineMarkdown(hMatch[2]),
        ),
      );
      i++;
      continue;
    }
    // Blank line → small spacer (acts as paragraph break)
    if (line.trim() === "") {
      blocks.push(<div key={k++} className="h-2" />);
      i++;
      continue;
    }
    // Paragraph
    blocks.push(
      <p key={k++} className="my-0 whitespace-pre-wrap break-words">
        {renderInlineMarkdown(line)}
      </p>,
    );
    i++;
  }
  return <>{blocks}</>;
};

// Sorted list of distinct audio track indices currently in use. Always contains at least [0] if there are any audio clips.
const getAudioTrackIndices = (clips: TimelineClip[]): number[] => {
  const set = new Set<number>();
  clips.forEach((c) => {
    if (c.type === "audio") set.add(getClipTrackIndex(c));
  });
  return [...set].sort((a, b) => a - b);
};

// Distinct video track indices (for video and ai-generated clips). Always includes 0 so there is a default video row.
const getVideoTrackIndices = (clips: TimelineClip[]): number[] => {
  const set = new Set<number>();
  set.add(0);
  clips.forEach((c) => {
    if (c.type === "video" || c.type === "ai-generated")
      set.add(getClipTrackIndex(c));
  });
  return [...set].sort((a, b) => a - b);
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const generateId = () => Math.random().toString(36).slice(2, 10);

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Small badge that ticks every second, displays mm:ss elapsed since startedAt.
// Reconexión automática del stream del Director ante cortes TRANSITORIOS
// (red / 5xx / 429 / timeout / idle). Reintentar el round es SEGURO: las tools
// se ejecutan DESPUÉS de consumir el stream, así que re-streamear solo regenera
// la respuesta del asistente — no re-ejecuta acciones ni cobra créditos.
// CLAVE (bug reportado): si NO hay red, NO hay que quemar los intentos al toque y
// rendirse en 2s. Hay que quedar en STANDBY esperando que la conexión vuelva y
// reintentar ni bien aparece (navigator.onLine + evento `online`). Igual damos una
// ventana larga con backoff creciente por si el corte es del servidor (5xx).
const MAX_STREAM_ATTEMPTS = 10; // ~2-3 min de ventana con el backoff de abajo
const STREAM_BACKOFF_MS = [1500, 3000, 6000, 12000, 15000]; // luego se queda en 15s
const OFFLINE_STANDBY_MS = 90_000; // tope esperando que vuelva la red

/** ¿Hay conexión? En el browser navigator.onLine es confiable. */
function isOnline(): boolean {
  try {
    return typeof navigator !== "undefined" && "onLine" in navigator
      ? navigator.onLine !== false
      : true;
  } catch {
    return true;
  }
}

// Espera antes de reintentar. Si estamos SIN red, queda en standby y corta apenas
// vuelve la conexión (evento `online` + poll); si hay red (5xx/idle), usa el
// backoff. Siempre corta si `isCancelled()` (Detener / turno superado).
function waitBeforeRetry(
  backoffMs: number,
  isCancelled: () => boolean,
): Promise<void> {
  return new Promise((resolve) => {
    if (isCancelled()) return resolve();
    const offline = !isOnline();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearInterval(poll);
      window.removeEventListener("online", finish);
      resolve();
    };
    // Poll: corta al volver la red (si offline), al vencer el tiempo, o al cancelar.
    const started = { t: 0 };
    const cap = offline ? OFFLINE_STANDBY_MS : backoffMs;
    const step = 300;
    const poll = window.setInterval(() => {
      started.t += step;
      if (isCancelled() || started.t >= cap || (offline && isOnline())) finish();
    }, step);
    if (offline) window.addEventListener("online", finish);
  });
}

// Clasifica si un corte del stream del agente es transitorio → conviene
// reintentar. Un `.retryable` explícito manda; si no, inferimos por tipo.
function isRetryableStreamError(err: unknown): boolean {
  const e = err as { retryable?: boolean; name?: string; message?: string } | null;
  if (typeof e?.retryable === "boolean") return e.retryable;
  if (e?.name === "AbortError") return true; // watchdog de inactividad (no Detener: ya se filtró por genToken)
  if (e?.name === "TimeoutError" || /^timeout:/.test(e?.message || "")) return true; // withTimeout(getSession)
  if (e?.message === "No session") return true; // token ausente → reintentar puede refrescar
  if (e?.name === "TypeError") return true; // fetch de red falló (Failed to fetch / NetworkError)
  return false;
}

const ElapsedBadge: React.FC<{ startedAt: number; color: string }> = ({
  startedAt,
  color,
}) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  const label = mm > 0 ? `${mm}:${ss.toString().padStart(2, "0")}` : `${ss}s`;
  return (
    <span
      className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums"
      style={{ backgroundColor: color + "24", color }}
    >
      {label}
    </span>
  );
};

// Button that opens the "Director de estudio" agent chat. The small video
// inside stays paused on the first frame at rest, and loops while the pointer
// is over the button. When the chat is already visible, the button collapses
// to "Ocultar agente" with a chevron.
const DirectorButton: React.FC<{
  showAgentChat: boolean;
  onClick: () => void;
  busy?: boolean;
}> = ({ showAgentChat, onClick, busy }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => {
        const v = videoRef.current;
        if (v) v.play().catch(() => {});
      }}
      onMouseLeave={() => {
        const v = videoRef.current;
        if (v) {
          v.pause();
          try {
            v.currentTime = 0;
          } catch {}
        }
      }}
      className="flex-1 py-2.5 px-5 rounded-md text-[13px] font-medium transition-colors flex items-center justify-center gap-2 border border-neutral-200 text-brand-black/80 hover:bg-neutral-50"
    >
      {showAgentChat ? (
        <>
          <ChevronsRight className="w-4 h-4" />
          Ocultar agente
        </>
      ) : (
        <>
          <span className="relative inline-flex w-5 h-5 shrink-0">
            <video
              ref={videoRef}
              src="/images/animacion_ia_carga.mp4"
              muted
              loop
              playsInline
              preload="auto"
              className="w-5 h-5 rounded-full object-cover"
            />
            {busy && (
              <span className="absolute -top-0.5 -right-0.5 inline-flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-brand-primary opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-brand-primary" />
              </span>
            )}
          </span>
          Director de estudio
        </>
      )}
    </button>
  );
};

// ─── AnimationCanvasItem ────────────────────────────────────────────────────
// Wrapper de un único clip de animación dentro del preview. Encapsula:
//   • el div arrastrable (posición + escala),
//   • la capa interna que renderiza el AnimationLayer de Remotion,
//   • un outline calculado en runtime midiendo el bounding box real del
//     contenido pintado, en lugar de envolver toda la canvas.
//
// El outline vive como hermano del wrapper (no dentro), posicionado en
// coordenadas de la canvas. Así el grosor de la línea es siempre 1px sin
// depender del scale, y la caja queda pegada al contenido aunque el
// template ocupe solo una franja del frame (ej. statsGrid).
type AnimationCanvasItemProps = {
  clip: TimelineClip;
  isSelected: boolean;
  xPct: number;
  yPct: number;
  scale: number;
  compW: number;
  compH: number;
  localTime: number;
  propsForTemplate: Record<string, unknown>;
  previewRef: React.RefObject<HTMLDivElement>;
  onPointerDown: (
    e: React.PointerEvent<HTMLDivElement>,
    clip: TimelineClip,
  ) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  // Callback de resize emitido por las manijas del SelectionFrame.
  // El padre traduce los deltas en pixels a un nuevo `animScale`.
  onResize?: (
    handle: SelectionHandle,
    dx: number,
    dy: number,
    phase: "start" | "move" | "end",
  ) => void;
};

const AnimationCanvasItem: React.FC<AnimationCanvasItemProps> = ({
  clip,
  isSelected,
  xPct,
  yPct,
  scale,
  compW,
  compH,
  localTime,
  propsForTemplate,
  previewRef,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onResize,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Bounding box del contenido pintado, en % de la canvas (preview).
  // null cuando no se pudo medir (ej. todavía no montó el Player).
  const [contentBox, setContentBox] = useState<{
    leftPct: number;
    topPct: number;
    widthPct: number;
    heightPct: number;
  } | null>(null);

  // Mientras el clip está seleccionado medimos el contenido en cada
  // frame (vía rAF). Hace falta porque muchos templates animan posición
  // y opacidad, y un único snapshot quedaría desfasado en cuanto el
  // contenido se mueve. El costo es despreciable: solo corre cuando hay
  // selección activa, y un único elemento a la vez.
  useEffect(() => {
    if (!isSelected) {
      setContentBox(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      const wrapper = wrapperRef.current;
      const canvas = previewRef.current;
      if (wrapper && canvas) {
        const canvasRect = canvas.getBoundingClientRect();
        // Recorremos solo "hojas" — elementos sin hijos — para evitar
        // contar contenedores grandes que envuelven contenido pequeño.
        // Filtramos los que tengan width/height 0 (typical en
        // <AbsoluteFill> vacíos durante la animación inicial).
        const all = wrapper.querySelectorAll<HTMLElement>("*");
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        all.forEach((el) => {
          if (el.children.length > 0) return;
          let r = el.getBoundingClientRect();
          if (r.width <= 0 || r.height <= 0) return;
          // Si es una <img> con object-fit: contain, el elemento es más
          // grande que la imagen visible (letterbox). Medimos el rect real
          // de la imagen (según su aspecto natural) para que el marco abrace
          // lo visible (ej. un logo cuadrado), no la caja del elemento.
          if (
            el.tagName === "IMG" &&
            getComputedStyle(el).objectFit === "contain"
          ) {
            const img = el as HTMLImageElement;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              const elAR = r.width / r.height;
              const imgAR = img.naturalWidth / img.naturalHeight;
              let vw = r.width;
              let vh = r.height;
              if (imgAR > elAR) vh = r.width / imgAR;
              else vw = r.height * imgAR;
              r = new DOMRect(
                r.left + (r.width - vw) / 2,
                r.top + (r.height - vh) / 2,
                vw,
                vh,
              );
            }
          }
          // Algunos elementos son completamente transparentes durante
          // las primeras frames; los incluimos igual porque ocupan
          // espacio "lógico" (sino el outline saltaría al aparecer).
          if (r.left < minX) minX = r.left;
          if (r.top < minY) minY = r.top;
          if (r.right > maxX) maxX = r.right;
          if (r.bottom > maxY) maxY = r.bottom;
        });
        if (
          minX !== Infinity &&
          canvasRect.width > 0 &&
          canvasRect.height > 0
        ) {
          const next = {
            leftPct: ((minX - canvasRect.left) / canvasRect.width) * 100,
            topPct: ((minY - canvasRect.top) / canvasRect.height) * 100,
            widthPct: ((maxX - minX) / canvasRect.width) * 100,
            heightPct: ((maxY - minY) / canvasRect.height) * 100,
          };
          // Evitamos setState si el cambio es despreciable (<0.25 px
          // a typical canvas size) — corta re-renders durante easing
          // suaves donde el bbox apenas se mueve.
          setContentBox((prev) => {
            if (
              prev &&
              Math.abs(prev.leftPct - next.leftPct) < 0.05 &&
              Math.abs(prev.topPct - next.topPct) < 0.05 &&
              Math.abs(prev.widthPct - next.widthPct) < 0.05 &&
              Math.abs(prev.heightPct - next.heightPct) < 0.05
            )
              return prev;
            return next;
          });
        }
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [isSelected, previewRef]);

  // Calculamos el motion (enter/exit) para este instante. Se aplica en
  // un div *interno* — separado del wrapper de drag — para no contaminar
  // el transform base de posicionamiento, que ya combina translate(-50%)
  // con scale(). Si los componés en un solo div el orden importa y los
  // efectos terminan multiplicándose con la escala manual.
  const motion = computeClipMotion({
    localTime,
    duration: clip.duration,
    enterId: clip.enterAnimation,
    exitId: clip.exitAnimation,
    enterDur: clip.enterDurationSec,
    exitDur: clip.exitDurationSec,
  });

  return (
    <Fragment>
      <div
        ref={wrapperRef}
        onPointerDown={(e) => onPointerDown(e, clip)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`absolute touch-none select-none ${
          isSelected ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{
          left: `${xPct}%`,
          top: `${yPct}%`,
          width: "100%",
          height: "100%",
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "center center",
        }}
      >
        {/* Capa interna que aplica enter/exit. Sus transforms se aplican
            *dentro* del scaled wrapper, así un slide de 40px se ve como
            40px en pantalla independientemente del scale del clip. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={motion.wrapperStyle}
        >
          <AnimationLayer
            templateId={clip.animationTemplate as string}
            props={propsForTemplate}
            localTime={localTime}
            durationSec={clip.duration}
            width={compW}
            height={compH}
          />
        </div>
      </div>

      {/* Marco de selección con manijas. Vive en coordenadas de la
          canvas (no dentro del wrapper escalado) para que el grosor de
          la línea sea 1.5px sin depender del scale, y para que las
          manijas tengan tamaño fijo en pantalla aunque el elemento
          esté muy reducido. */}
      {isSelected && contentBox && (
        <div
          className="absolute"
          style={{
            left: `${contentBox.leftPct}%`,
            top: `${contentBox.topPct}%`,
            width: `${contentBox.widthPct}%`,
            height: `${contentBox.heightPct}%`,
          }}
        >
          <SelectionFrame onResize={onResize} />
        </div>
      )}
    </Fragment>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

export const VideoEditor: React.FC = () => {
  const { sidebarWidth } = useSidebar();
  // El editor colapsaba la sidebar al entrar y la restauraba al salir, para
  // ganar ancho. Se sacó con ADR-2026-08-05-el-estudio-de-video-entra-al-shell:
  // el shell nuevo colapsa con su PROPIO estado y sólo re-provee la geometría,
  // así que ese toggle ya no movía la sidebar que se ve — le daba vuelta la
  // preferencia guardada del chrome viejo, calladamente y desde otra pantalla.
  // La sidebar queda como el usuario la dejó, igual que en los otros editores.
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // ── Project persistence ────────────────────────────────────────────────────
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectName, setProjectName] = useState("Proyecto sin título");
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Clip que se está editando con IA (Gemini Omni). null = panel cerrado. Solo
  // clips de video ≤10s. Feature nueva, independiente del editor Sora. ADR-500.
  const [omniEditClipId, setOmniEditClipId] = useState<string | null>(null);
  // Clip que se está mejorando con upscale (Topaz). null = panel cerrado. ADR-596.
  const [upscaleClipId, setUpscaleClipId] = useState<string | null>(null);
  // Menú "Más ▾" de la barra del clip: agrupa las acciones menos usadas
  // (Extender · Separar audio · Restaurar · Duplicar · Descargar) para
  // descongestionar la toolbar (antes eran ~13 botones sueltos).
  const [clipMoreMenuOpen, setClipMoreMenuOpen] = useState(false);
  // Menú "IA ▾" de la barra del clip: agrupa las acciones de IA sobre el clip
  // (Editar con IA · Borrar objeto · Traducir · Subtítulos) para descongestionar
  // la toolbar.
  const [clipIaMenuOpen, setClipIaMenuOpen] = useState(false);
  // Brand profile attached to this project — drives the "Memoria" panel and
  // the update_brand_context agent tool.
  // Fonts are stored as role -> font family, so the agent and the UI can
  // talk about "fuente del título" / "fuente del cuerpo" instead of a flat
  // list. Each role is optional; missing means "no pref, usar fallback".
  type BrandFontRole =
    | "antetitulo"
    | "titulo"
    | "subtitulo"
    | "cuerpo"
    | "detalles";
  type BrandFontRoles = Partial<Record<BrandFontRole, string>>;
  type BrandProfile = {
    id: string;
    name: string;
    context: string;
    colors: string[];
    fonts: BrandFontRoles;
    logoUrl: string | null;
  };
  const [brandProfile, setBrandProfile] = useState<BrandProfile | null>(null);
  // Whether the read-only "Memoria de la IA" card in the Marca panel is
  // expanded (mirrors the brand view in Configuración → Mis Marcas).
  const [brandMemoriaExpanded, setBrandMemoriaExpanded] = useState(false);
  // Load the brand's suggested fonts so the read-only typography preview in the
  // Marca panel renders in the real families (same as Config → Mis Marcas).
  useEffect(() => {
    const f = (brandProfile?.fonts || {}) as Record<string, unknown>;
    const fams = (
      Array.isArray((f as any).suggested)
        ? ((f as any).suggested as unknown[])
        : Object.values(f)
    ).filter((v): v is string => typeof v === "string");
    if (fams.length === 0) return;
    const id = `brand-marca-fonts-${brandProfile?.id ?? ""}`;
    if (document.getElementById(id)) return;
    const families = fams
      .map((fam) => `family=${encodeURIComponent(fam)}:wght@400;500;700`)
      .join("&");
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }, [brandProfile?.id, brandProfile?.fonts]);
  // Active tab inside the Marca panel. The panel used to stack Memoria,
  // Fuentes and Colores vertically; now they share space behind a horizontal
  // segmented selector so the panel stays compact regardless of how long
  // the memoria text is or how many colors / fonts the brand has.
  const [brandTab, setBrandTab] = useState<
    "memoria" | "fuentes" | "colores" | "imagenes"
  >("memoria");
  // Brand image library: cached per brand profile. Two `tipo` buckets
  // ('logo' / 'element') rendered as two stacked grids inside the
  // Imágenes tab. URLs are resolved lazily and stored alongside the row.
  const [brandAssets, setBrandAssets] = useState<
    Array<BrandAsset & { url?: string }>
  >([]);
  const [brandAssetsLoading, setBrandAssetsLoading] = useState(false);
  const [brandAssetsUploading, setBrandAssetsUploading] =
    useState<BrandAssetTipo | null>(null);
  // Hidden file inputs that the "Subir" tile of each section triggers.
  // Kept as separate refs so each section opens its own picker.
  const brandLogoInputRef = useRef<HTMLInputElement>(null);
  // Brand switcher dropdown (in the panel header). Lists every brand_profile
  // belonging to the current company so the user can re-attach the project
  // to a different brand without leaving the editor, plus a "Nueva marca"
  // CTA that creates and assigns a fresh one in a single step.
  const [brandSwitcherOpen, setBrandSwitcherOpen] = useState(false);
  // Same dropdown but anchored on the badge that sits next to the project
  // title in the top toolbar; kept in its own state so it doesn't open in
  // tandem with the panel header switcher.
  const [brandBadgeOpen, setBrandBadgeOpen] = useState(false);
  const [companyBrands, setCompanyBrands] = useState<
    Array<{ id: string; name: string }>
  >([]);
  // Auto-close the switcher when the user navigates away from the
  // Marca panel so it doesn't reopen lingering on next visit.
  // Debounced persistence helper for the brand_profile editable fields
  // (context / colors / fonts). Keeps the same write surface used by the
  // agent's update_brand_* tools but driven from the UI: optimistic local
  // update first, debounced supabase upsert after 400ms of inactivity so
  // typing in the memoria textarea doesn't fire a request per keystroke.
  const brandSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistBrandProfile = useCallback(
    (patch: Partial<Omit<BrandProfile, "id">>) => {
      setBrandProfile((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        // Keep the switcher list in sync with inline renames so the
        // dropdown immediately reflects the new label.
        if (patch.name !== undefined) {
          setCompanyBrands((list) =>
            list.map((b) => (b.id === next.id ? { ...b, name: next.name } : b)),
          );
        }
        if (brandSaveTimerRef.current) clearTimeout(brandSaveTimerRef.current);
        brandSaveTimerRef.current = setTimeout(async () => {
          const update: Record<string, any> = {
            updated_at: new Date().toISOString(),
          };
          if (patch.name !== undefined) update.name = next.name;
          if (patch.context !== undefined) update.context = next.context;
          if (patch.colors !== undefined) update.colors = next.colors;
          if (patch.fonts !== undefined) update.fonts = next.fonts;
          const { error } = await supabase
            .from("brand_profiles")
            .update(update)
            .eq("id", next.id);
          if (error)
            console.error("[VideoEditor] brand_profiles save failed", error);
        }, 400);
        return next;
      });
    },
    [],
  );

  // Load the brand-asset library whenever the active brand changes.
  // We load eagerly (instead of waiting for the Imágenes tab) because
  // custom font assets need to be registered as @font-face rules so any
  // FontPicker in the editor can render them — not just the Marca panel.
  // Signed URLs are resolved in parallel and stored alongside each row.
  useEffect(() => {
    const brandId = brandProfile?.id;
    if (!brandId) {
      setBrandAssets([]);
      return;
    }
    let cancelled = false;
    setBrandAssetsLoading(true);
    (async () => {
      try {
        const list = await fetchBrandAssets(brandId);
        const withUrls = await Promise.all(
          list.map(async (a) => ({
            ...a,
            url: (await getBrandAssetSignedUrl(a.storage_path)) || undefined,
          })),
        );
        if (!cancelled) setBrandAssets(withUrls);
      } catch (e) {
        console.error("[VideoEditor] brand-assets fetch failed", e);
      } finally {
        if (!cancelled) setBrandAssetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandProfile?.id]);

  // ── Custom brand fonts ────────────────────────────────────────────────
  // Strip the extension off a font asset's `name` so the family identifier
  // is friendlier (e.g. "Helvetica-Bold" instead of "Helvetica-Bold.woff2").
  // Falls back to the asset id if no name was stored.
  const fontFamilyFromAsset = useCallback((asset: BrandAsset): string => {
    const raw = (asset.name || asset.id).trim();
    return raw.replace(/\.(woff2?|ttf|otf)$/i, "") || asset.id;
  }, []);

  // List of custom font family names — derived from the loaded brand
  // assets. Memoized so the @font-face effect below has a stable dep.
  const customFontEntries = useMemo(() => {
    return brandAssets
      .filter((a) => a.tipo === "font" && a.url)
      .map((a) => ({
        id: a.id,
        family: fontFamilyFromAsset(a),
        url: a.url as string,
      }));
  }, [brandAssets, fontFamilyFromAsset]);
  const customFontFamilies = useMemo(
    () => customFontEntries.map((e) => e.family),
    [customFontEntries],
  );

  // Register a `FontFace` for every uploaded brand font so the browser can
  // actually render text styled with `font-family: <family>`. Cleans up on
  // change so removing a font also removes its rule. The map keeps a
  // reference per asset id; we re-load whenever the URL flips (e.g. signed
  // URL refresh) but keep stable identity otherwise.
  const registeredFontFacesRef = useRef<
    Map<string, { family: string; url: string; face: FontFace }>
  >(new Map());
  useEffect(() => {
    if (typeof document === "undefined" || !(document as any).fonts) return;
    const reg = registeredFontFacesRef.current;
    const seenIds = new Set<string>();
    for (const entry of customFontEntries) {
      seenIds.add(entry.id);
      const existing = reg.get(entry.id);
      if (
        existing &&
        existing.family === entry.family &&
        existing.url === entry.url
      )
        continue;
      // Family or URL changed — drop the previous face before adding the new one.
      if (existing) {
        try {
          (document as any).fonts.delete(existing.face);
        } catch {
          /* noop */
        }
      }
      try {
        const face = new FontFace(entry.family, `url("${entry.url}")`);
        face
          .load()
          .then((loaded) => {
            (document as any).fonts.add(loaded);
          })
          .catch((err) => {
            console.warn(
              "[VideoEditor] custom font load failed",
              entry.family,
              err,
            );
          });
        reg.set(entry.id, { family: entry.family, url: entry.url, face });
      } catch (err) {
        console.warn(
          "[VideoEditor] FontFace construction failed",
          entry.family,
          err,
        );
      }
    }
    // Drop any registered face whose asset is no longer present.
    for (const [id, info] of reg) {
      if (seenIds.has(id)) continue;
      try {
        (document as any).fonts.delete(info.face);
      } catch {
        /* noop */
      }
      reg.delete(id);
    }
  }, [customFontEntries]);

  // Upload a brand image (logo or element). Optimistically prepends the
  // new row to the grid once the request resolves.
  const handleBrandAssetUpload = useCallback(
    async (file: File, tipo: BrandAssetTipo) => {
      const brandId = brandProfile?.id;
      if (!brandId || !companyId) return;
      setBrandAssetsUploading(tipo);
      try {
        const asset = await uploadBrandAsset({
          file,
          brandProfileId: brandId,
          companyId,
          tipo,
        });
        const url =
          (await getBrandAssetSignedUrl(asset.storage_path)) || undefined;
        setBrandAssets((prev) => [{ ...asset, url }, ...prev]);
      } catch (e) {
        console.error("[VideoEditor] brand-asset upload failed", e);
      } finally {
        setBrandAssetsUploading(null);
      }
    },
    [brandProfile?.id, companyId],
  );

  // Remove a brand asset (storage + row). State is updated first so the
  // tile disappears instantly; the network call follows.
  const handleBrandAssetDelete = useCallback(async (asset: BrandAsset) => {
    setBrandAssets((prev) => prev.filter((a) => a.id !== asset.id));
    try {
      await deleteBrandAsset(asset);
    } catch (e) {
      console.error("[VideoEditor] brand-asset delete failed", e);
    }
  }, []);

  // Load every brand profile belonging to the current company so the
  // header dropdown can offer them. Re-fetches whenever the company
  // changes (e.g. after the project finishes loading).
  const refreshCompanyBrands = useCallback(
    async (cId?: string | null) => {
      const id = cId ?? companyId;
      if (!id) return;
      const { data, error } = await supabase
        .from("brand_profiles")
        .select("id, name, is_default, created_at")
        .eq("id_company", id)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      if (error) {
        console.error("[VideoEditor] brand_profiles list failed", error);
        return;
      }
      setCompanyBrands(
        (data || []).map((b: any) => ({ id: b.id, name: b.name || "Marca" })),
      );
    },
    [companyId],
  );
  // Switch the current project to a different brand_profile: persists
  // the FK on video_projects and pulls the full brand record so the
  // panel updates immediately. No-op when re-selecting the active one.
  const switchBrand = useCallback(
    async (brandId: string) => {
      setBrandSwitcherOpen(false);
      if (!projectId || !brandId || brandId === brandProfile?.id) return;
      const { error: linkErr } = await supabase
        .from("video_projects")
        .update({ brand_profile_id: brandId })
        .eq("id", projectId);
      if (linkErr) {
        console.error("[VideoEditor] switchBrand link failed", linkErr);
        return;
      }
      const { data: bp, error } = await supabase
        .from("brand_profiles")
        .select("id, name, context, colors, fonts, logo_url")
        .eq("id", brandId)
        .maybeSingle();
      if (error || !bp) {
        console.error("[VideoEditor] switchBrand fetch failed", error);
        return;
      }
      setBrandProfile({
        id: bp.id,
        name: bp.name || "Marca",
        context: bp.context || "",
        colors: normalizeBrandColors(bp.colors),
        logoUrl: (bp as any).logo_url ?? null,
        fonts:
          (bp as any).fonts &&
          !Array.isArray((bp as any).fonts) &&
          typeof (bp as any).fonts === "object"
            ? ((bp as any).fonts as BrandFontRoles)
            : {},
      });
    },
    [projectId, brandProfile?.id],
  );
  // Create a brand_profile for the current company and assign it to the
  // active project in a single flow. The new brand starts empty so the
  // user can rename it inline right after.
  const createBrandAndAssign = useCallback(async () => {
    setBrandSwitcherOpen(false);
    if (!companyId || !projectId) return;
    const { data: created, error } = await supabase
      .from("brand_profiles")
      .insert({
        id_company: companyId,
        name: "Nueva marca",
        context: "",
        colors: [],
        fonts: {},
        is_default: false,
      })
      .select("id, name, context, colors, fonts")
      .single();
    if (error || !created) {
      console.error("[VideoEditor] create brand failed", error);
      return;
    }
    const { error: linkErr } = await supabase
      .from("video_projects")
      .update({ brand_profile_id: created.id })
      .eq("id", projectId);
    if (linkErr) {
      console.error("[VideoEditor] new brand link failed", linkErr);
      return;
    }
    setBrandProfile({
      id: created.id,
      name: created.name || "Nueva marca",
      context: created.context || "",
      colors: normalizeBrandColors(created.colors),
      logoUrl: (created as any).logo_url ?? null,
      fonts:
        (created as any).fonts &&
        !Array.isArray((created as any).fonts) &&
        typeof (created as any).fonts === "object"
          ? ((created as any).fonts as BrandFontRoles)
          : {},
    });
    setCompanyBrands((prev) => [
      ...prev,
      { id: created.id, name: created.name || "Nueva marca" },
    ]);
  }, [companyId, projectId]);
  // Initial load + refresh on company change.
  useEffect(() => {
    void refreshCompanyBrands();
  }, [refreshCompanyBrands]);
  useEffect(() => {
    setBrandSwitcherOpen(false);
    setBrandBadgeOpen(false);
  }, [brandProfile?.id]);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadedRef = useRef(false);

  // ── State ─────────────────────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30);
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("16:9");
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<ToolPanel>("none");
  // ── Transitions: the id of the SECOND clip in the pair currently being
  // edited. The transitions panel reads this to know which clip's
  // `transitionIn` it's mutating. Cleared when the panel closes.
  const [transitionTargetClipId, setTransitionTargetClipId] = useState<
    string | null
  >(null);
  const [volumePopoverOpen, setVolumePopoverOpen] = useState(false);
  useEffect(() => {
    setVolumePopoverOpen(false);
    // Cerrar los dropdowns de la toolbar al cambiar de clip (si no, quedan
    // flotando sobre la barra del clip nuevo hasta el próximo click afuera).
    setClipIaMenuOpen(false);
    setClipMoreMenuOpen(false);
  }, [selectedClipId]);
  // ── Cortar silencios ──────────────────────────────────────────────────
  // Mini-menú colgado del botón "Cortar silencios" del clip toolbar:
  // muestra un input para el umbral (en segundos) y un botón que dispara
  // la acción. `silenceCutBusy` tiene el id del clip mientras se está
  // ejecutando para mostrar spinner sin bloquear el resto de la UI.
  // `silenceCutNotice` levanta un mensaje breve cuando no hubo nada que
  // cortar (ej: "no se detectó habla en este clip"), que se auto-limpia.
  const [silencePopoverOpen, setSilencePopoverOpen] = useState(false);
  const [silenceThresholdSec, setSilenceThresholdSec] = useState(0.5);
  // Padding (head/tail) que se deja a cada lado de cada tramo de habla
  // para que los cortes no suenen bruscos — cortar exacto donde termina
  // una palabra suele comer la última consonante o el resonar de la voz.
  // Default 0.12s = sweet spot entre respirado y aire excesivo.
  const [silencePaddingSec, setSilencePaddingSec] = useState(0.12);
  const [silenceCutBusy, setSilenceCutBusy] = useState<string | null>(null);
  const [silenceCutNotice, setSilenceCutNotice] = useState<string | null>(null);
  useEffect(() => {
    setSilencePopoverOpen(false);
    setSilenceCutNotice(null);
  }, [selectedClipId]);
  useEffect(() => {
    if (!silenceCutNotice) return;
    const t = window.setTimeout(() => setSilenceCutNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [silenceCutNotice]);
  // Popovers for the text clip toolbar (color swatches, duration editor).
  // Only one can be open at a time; closing happens via click-outside or
  // selecting a different clip.
  const [textToolbarPopover, setTextToolbarPopover] = useState<
    "color" | "duration" | "motion" | null
  >(null);
  useEffect(() => {
    setTextToolbarPopover(null);
  }, [selectedClipId]);

  // ── Subtítulos ─────────────────────────────────────────────────────────
  // State for the "Generar subtítulos" modal. `subtitlesLoading` covers
  // the webhook request, `subtitlesError` any failure (network / missing
  // signed URL / etc), and `subtitlesResult` holds both the raw
  // transcript (so the user can see the full text) and the computed
  // chunk list ready to be inserted as text clips. `subtitlesSourceClipId`
  // remembers which clip was the source so we know where to anchor the
  // subtitle clips on the timeline even if the selection changes while
  // the modal is open.
  const [subtitlesModalOpen, setSubtitlesModalOpen] = useState(false);
  const [subtitlesLoading, setSubtitlesLoading] = useState(false);
  const [subtitlesError, setSubtitlesError] = useState<string | null>(null);
  const [subtitlesResult, setSubtitlesResult] = useState<{
    transcript: TranscriptResult;
    segments: Array<{ start: number; end: number; text: string }>;
  } | null>(null);
  const [subtitlesSourceClipId, setSubtitlesSourceClipId] = useState<
    string | null
  >(null);
  // Controls the split dropdown shown on the toolbar's delete button when
  // the selected clip belongs to a subtitle group. Closed by default.
  const [subtitleDeleteMenuOpen, setSubtitleDeleteMenuOpen] = useState(false);

  // ── Edición de animaciones ───────────────────────────────────────────────
  // Draft con los cambios pendientes. Mientras el usuario edita una
  // animación, los cambios viven acá hasta que apriete "Guardar cambios".
  // Cuando es null, no hay edición en curso. La previsualización del
  // player consulta este draft (si su clipId coincide con el clip que está
  // renderizando) para mostrar los cambios sin haberlos persistido.
  const [animationEditDraft, setAnimationEditDraft] = useState<{
    clipId: string;
    props: Record<string, unknown>;
  } | null>(null);
  // ID del clip en modo "reproducir en loop". Cuando está seteado, la línea
  // de tiempo no se detiene al final del clip: hace snap al inicio y sigue
  // reproduciendo. Se limpia al apretar pausa (en el panel o en cualquier
  // otro botón que pause el video global).
  const [animationPreviewLoopClipId, setAnimationPreviewLoopClipId] = useState<
    string | null
  >(null);
  // Sub-pantalla activa dentro del panel "Elementos". `null` muestra la
  // pantalla raíz con las cards de categoría; los demás valores muestran
  // el contenido de cada categoría. Se resetea automáticamente cuando se
  // cierra el panel.
  const [elementsCategory, setElementsCategory] = useState<
    "text" | "image" | "lottie" | null
  >(null);
  useEffect(() => {
    if (activePanel !== "animation") setElementsCategory(null);
  }, [activePanel]);
  // Inicializa / limpia el draft cuando cambia el clip seleccionado. Solo
  // dependemos de selectedClipId a propósito: cuando guardamos cambios y
  // el array `clips` se actualiza, no queremos que este efecto re-pise el
  // draft con una versión vieja.
  useEffect(() => {
    const sel = clips.find((c) => c.id === selectedClipId);
    if (!sel || sel.type !== "animation") {
      setAnimationEditDraft(null);
      setAnimationPreviewLoopClipId(null);
      return;
    }
    setAnimationEditDraft({
      clipId: sel.id,
      props: { ...(sel.animationProps || {}) },
    });
    setAnimationPreviewLoopClipId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId]);
  // Refs for the responsive clip toolbar live here so they can be assigned
  // by JSX further down. The actual measurement effects are installed AFTER
  // `showAgentChat` is declared (it's referenced by the deps).
  const clipToolbarRef = useRef<HTMLDivElement>(null);
  const directorBtnRef = useRef<HTMLDivElement>(null);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(0);
  const [toolbarTick, setToolbarTick] = useState(0);
  // Total number of collapsible buttons currently rendered in the toolbar.
  // Populated by the layout effect via a DOM count and used during render
  // to map button render-index → "distance from the right edge", so that
  // the rightmost (least-important) buttons drop their labels first.
  const [toolbarBtnCount, setToolbarBtnCount] = useState(0);
  useEffect(() => {
    setToolbarCollapsed(0);
  }, [selectedClipId]);
  // ── Text composer state ─────────────────────────────────────────────────
  // Working draft for the "Añadir texto" panel. Used to build a NEW text clip
  // before it's pushed onto the timeline. When the user selects an existing
  // text clip in the preview, the panel switches to editing mode and writes
  // through to the clip directly via setClips (the textDraft state stays
  // around as the "next new clip" template).
  const [textDraft, setTextDraft] = useState({
    text: "",
    fontFamily: DEFAULT_TEXT_CLIP.fontFamily,
    fontSizePct: DEFAULT_TEXT_CLIP.fontSizePct,
    fontWeight: DEFAULT_TEXT_CLIP.fontWeight as "normal" | "bold",
    fontStyle: DEFAULT_TEXT_CLIP.fontStyle as "normal" | "italic",
    textColor: DEFAULT_TEXT_CLIP.textColor,
    textAlign: DEFAULT_TEXT_CLIP.textAlign as "left" | "center" | "right",
    enterAnimation: "none" as string,
    enterDurationSec: 0.5,
    exitAnimation: "none" as string,
    exitDurationSec: 0.5,
  });
  // Whether the tracks portion of the timeline is collapsed (ruler + transport remain visible).
  const [timelineMinimized, setTimelineMinimized] = useState(false);
  // Dropdown above the "Generar con IA" button (Video / Audio options).
  const [aiGenerateMenuOpen, setAiGenerateMenuOpen] = useState(false);
  const aiGenerateBtnRef = useRef<HTMLButtonElement | null>(null);
  // Dropdown next to the left rail's "Generar con IA" tile (Video / Imagen).
  const [railAiMenuOpen, setRailAiMenuOpen] = useState(false);
  const railAiBtnRef = useRef<HTMLButtonElement | null>(null);
  // ── "Archivos del proyecto" panel state ────────────────────────────────
  // The Files panel unifies what used to be three separate sub-panels
  // (video-library, audio-library, and the implicit image gallery inside
  // the AI image-gen panel). It exposes one place to:
  //   1) upload any media file (video/audio/image),
  //   2) trigger any IA generation (Video, Audio, Imagen),
  //   3) browse existing project assets filtered by media type, with the
  //      `image` tab merging uploaded images + AI-generated ones.
  const [filesTab, setFilesTab] = useState<FilesTab>("media");
  // Pill "Productos" del panel Archivos: catálogo de la marca del proyecto.
  const [filesProductItems, setFilesProductItems] = useState<MockProductV2[]>(
    [],
  );
  const [filesProductsLoading, setFilesProductsLoading] = useState(false);
  const [filesImportingProductIds, setFilesImportingProductIds] = useState<
    Set<string>
  >(new Set());
  // Evita el flash del spinner al cambiar de pill: una vez que el panel
  // Archivos cargó la primera vez, los cambios de tab no muestran spinner.
  const filesLoadedOnceRef = useRef(false);
  // Pill "Escenas" del panel Archivos: fondos guardados del cliente + defaults
  // de ATLAS (filtrados por rubro). Mismo shape que productos.
  const [filesSceneItems, setFilesSceneItems] = useState<MockProductV2[]>([]);
  const [filesScenesLoading, setFilesScenesLoading] = useState(false);
  const [filesImportingSceneIds, setFilesImportingSceneIds] = useState<
    Set<string>
  >(new Set());
  // Pill "Historial" del panel Archivos: imágenes generadas del cliente
  // (imagenes_generadas), filtradas por la marca del proyecto.
  const [filesHistorialItems, setFilesHistorialItems] = useState<
    MockProductV2[]
  >([]);
  const [filesHistorialLoading, setFilesHistorialLoading] = useState(false);
  const [filesImportingHistorialIds, setFilesImportingHistorialIds] = useState<
    Set<string>
  >(new Set());
  // Same UX pattern as railAiMenuOpen: anchored dropdown with the three
  // generation entry points. We keep state local to the panel so it
  // doesn't fight with the rail-level menu.
  // Hidden file input used by the panel's "Subir archivo" CTA. Accepts
  // video + audio + image; the change handler sniffs the MIME and routes
  // to either the timeline-aware video/audio uploader (auto-adds a clip)
  // or a plain image upload (asset-only).
  const filesUploadInputRef = useRef<HTMLInputElement | null>(null);
  // Image assets shown in the `image` tab. Loaded lazily when the tab is
  // opened, refreshed after a manual upload. Kept separate from
  // `libraryAssets` (which holds video OR audio per active sub-panel) so
  // switching tabs doesn't lose the other type's data.
  const [imageLibraryAssets, setImageLibraryAssets] = useState<ProjectAsset[]>(
    [],
  );
  const [imageLibraryLoading, setImageLibraryLoading] = useState(false);
  // Anchored dropdown shown when the user clicks an image tile in the
  // Archivos→Imágenes tab. We track the asset id + the tile's bounding
  // rect so the floating menu can position itself relative to it (same
  // pattern as `imageSlotMenu`/`imageSlotMenuRect`). Restricted to
  // project-asset images; AI-generated thumbs keep their existing
  // single-click "open detail" behavior because turning them into a
  // ref/frame requires first importing as a project asset.
  const [filesImageMenu, setFilesImageMenu] = useState<{
    id?: string;
    product?: MockProductV2;
    generatedId?: string; // imagen generada con IA (imagenes_generadas.id)
    rect: DOMRect;
  } | null>(null);
  // Mapa product/scene id → asset id de los importados desde Productos/Escenas.
  // Sirve para deduplicar (no re-importar el mismo) y para ocultarlos de la
  // grilla Imágenes (viven solo en su pill). Persiste en el estado del proyecto.
  const [productSceneAssetMap, setProductSceneAssetMap] = useState<
    Record<string, string>
  >({});

  // ── Modelos (Modo Diálogo) ──────────────────────────────────────────────
  // Picker de modelos para el "Frame inicial" de una escena de diálogo: el
  // modelo elegido (su foto de presentación) pasa a ser la persona que habla.
  // Globales de ATLAS + los de la empresa (sin filtro por marca, como la web).
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  // A qué destino va el modelo elegido: "first" = Modo Diálogo (lleva a Generar
  // con IA apuntando al Frame inicial); "ref" = referencia visual en Modo
  // General; "attach" = adjuntar a la generación de imagen IA en curso.
  const [modelPickKind, setModelPickKind] = useState<
    "first" | "ref" | "attach"
  >("first");
  // True cuando Generar con IA se abrió desde el flujo "Usar modelo" de Modo
  // Diálogo (ya hay un modelo elegido). En ese caso ocultamos el botón "Usar
  // modelo" del panel (sería redundante). En el resto, se muestra por default.
  const [imageGenFromModelFlow, setImageGenFromModelFlow] = useState(false);
  const [modelItems, setModelItems] = useState<ModelV2[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsImportingIds, setModelsImportingIds] = useState<Set<number>>(
    new Set(),
  );
  // Dedup modelo id → asset id importado (no re-subir la misma foto).
  const [modelAssetMap, setModelAssetMap] = useState<Record<number, string>>(
    {},
  );
  // Alta de personaje desde el picker — misma UX que el catálogo de la V2:
  // card "Agregar" con opciones "Creá tu personaje" (características →
  // CreateView) o "Cargá tu propio personaje" (foto cruda → UploadFlow).
  const [personajeAddView, setPersonajeAddView] = useState<
    "create" | "upload" | null
  >(null);
  // Al cerrar el picker (Cancelar o personaje elegido) el alta vuelve a la
  // grilla — que la próxima apertura no arranque en un form a medio llenar.
  useEffect(() => {
    if (!modelPickerOpen) setPersonajeAddView(null);
  }, [modelPickerOpen]);

  // Clip bounce + click-sound when an AI generation finishes
  const [bounceClipIds, setBounceClipIds] = useState<Set<string>>(new Set());
  const prevAiStatusRef = useRef<Record<string, AiClipStatus | undefined>>({});

  // Panel-field bounce — fires when the agent prefills a sidebar field
  // (prompt textarea, image refs, frames, etc.) so the user sees a small
  // pop where the agent just wrote. Keyed by stable strings (see usages).
  const [bouncePanelKeys, setBouncePanelKeys] = useState<Set<string>>(
    new Set(),
  );
  const triggerPanelBounce = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setBouncePanelKeys((curr) => {
      const next = new Set(curr);
      keys.forEach((k) => next.add(k));
      return next;
    });
    window.setTimeout(() => {
      setBouncePanelKeys((curr) => {
        const next = new Set(curr);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }, 700);
  }, []);
  const panelBounceClass = useCallback(
    (key: string) => (bouncePanelKeys.has(key) ? " clip-bounce" : ""),
    [bouncePanelKeys],
  );

  // Stronger 3x bounce — used by the agent to nudge the user toward an
  // image upload slot (e.g. "necesito que subas el primer frame").
  const [nudgePanelKeys, setNudgePanelKeys] = useState<Set<string>>(new Set());
  const triggerPanelNudge = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setNudgePanelKeys((curr) => {
      const next = new Set(curr);
      keys.forEach((k) => next.add(k));
      return next;
    });
    // 0.65s × 3 iterations ≈ 1950ms; clear shortly after to allow re-trigger.
    window.setTimeout(() => {
      setNudgePanelKeys((curr) => {
        const next = new Set(curr);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }, 2100);
  }, []);
  const panelNudgeClass = useCallback(
    (key: string) => (nudgePanelKeys.has(key) ? " clip-bounce-thrice" : ""),
    [nudgePanelKeys],
  );

  const finishSoundRef = useRef<HTMLAudioElement | null>(null);
  if (typeof window !== "undefined" && !finishSoundRef.current) {
    try {
      finishSoundRef.current = new Audio("/images/clicksound.m4a");
      finishSoundRef.current.preload = "auto";
      finishSoundRef.current.volume = 0.7;
    } catch {}
  }

  // Pincel inteligente (SAM2): frame capturado + clip objetivo, modal de
  // confirmación (tiempo+créditos) y toast de éxito/error del job async.
  const [eraseBrushClipId, setEraseBrushClipId] = useState<string | null>(null);
  const [eraseBrushFrame, setEraseBrushFrame] = useState<
    { url: string; w: number; h: number } | null
  >(null);
  const [eraseConfirm, setEraseConfirm] = useState<
    { clipId: string; marks: EraseMark[] } | null
  >(null);

  // AI generation (old panel)
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDuration, setAiDuration] = useState<"3" | "5" | "10">("5");

  // AI scene (new panel)
  const [editingClipId, setEditingClipId] = useState<string | null>(null);
  const [scenePrompt, setScenePrompt] = useState("");
  const [sceneDialogue, setSceneDialogue] = useState("");
  const [sceneDialogueInputMode, setSceneDialogueInputMode] = useState<
    "text" | "audio"
  >("text");
  const [sceneDialogueAudio, setSceneDialogueAudio] = useState<{
    url: string;
    storagePath: string;
    durationSec: number;
    assetId: string;
  } | null>(null);
  const [sceneRefMode, setSceneRefModeState] = useState<
    "none" | "reference" | "frames"
  >("none");
  // Los `prefill_*` del Director abren el panel ANTES de terminar de resolver
  // las imágenes de referencia (ver
  // ADR-2026-08-11-el-panel-del-prefill-abre-antes-de-resolver-referencias).
  // Eso deja al usuario editando un panel mientras hay escrituras en vuelo, así
  // que las tardías tienen que poder detectar que ya no corresponden:
  //   · el token lo pisa un prefill más nuevo,
  //   · el espejo del modo detecta que el usuario lo cambió a mano.
  const scenePrefillTokenRef = useRef(0);
  const sceneRefModeRef = useRef(sceneRefMode);
  // El espejo se actualiza EN EL MISMO TICK que el estado, no en un efecto:
  // entre el click del usuario y el commit de React hay una ventana en la que
  // un efecto todavía no corrió, y si la resolución cae justo ahí el guard lee
  // el valor viejo, cree que nada cambió y pisa al usuario — justo lo que el
  // guard existe para evitar. Envolver el setter mantiene los call sites
  // intactos y saca la sincronización del terreno de "cuándo corre el efecto".
  const setSceneRefMode = useCallback(
    (mode: "none" | "reference" | "frames") => {
      sceneRefModeRef.current = mode;
      setSceneRefModeState(mode);
    },
    [],
  );
  const [sceneMode, setSceneMode] = useState<"general" | "personas">("general");
  const [scenePersonType, setScenePersonType] = useState<"static" | "motion">(
    "static",
  );
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const modeDropdownRef = useRef<HTMLDivElement>(null);

  // AI audio (new panel) — generate voice-over or music with AI
  const [audioAiType, setAudioAiType] = useState<"voice" | "music">("voice");
  const [audioAiPrompt, setAudioAiPrompt] = useState("");
  // When `voice` mode, the user can either type text (default) or record/upload
  // their own audio. Audio mode goes through ElevenLabs speech-to-speech in the
  // workflow instead of TTS.
  const [audioAiDialogueInputMode, setAudioAiDialogueInputMode] = useState<
    "text" | "audio"
  >("text");
  const [audioAiDialogueAudio, setAudioAiDialogueAudio] = useState<{
    url: string;
    storagePath: string;
    durationSec: number;
    assetId: string;
  } | null>(null);
  const [audioAiDuration, setAudioAiDuration] = useState<number>(8);
  const [audioAiVoiceId, setAudioAiVoiceId] = useState<string | null>(null);
  const [audioAiVoiceName, setAudioAiVoiceName] = useState<string>("");
  const [audioAiInstrumentalOnly, setAudioAiInstrumentalOnly] =
    useState<boolean>(false);
  const [audioAiSpeechSpeed, setAudioAiSpeechSpeed] = useState<number>(1.1);
  const [showAudioAiVoiceSelector, setShowAudioAiVoiceSelector] =
    useState(false);
  const [audioAiVoices, setAudioAiVoices] = useState<
    Array<{
      id: string;
      nombre: string;
      sexo: string;
      descripcion: string;
      preview_url: string;
      idioma: string;
      acento: string;
    }>
  >([]);
  const [audioAiVoicesLoading, setAudioAiVoicesLoading] = useState(false);
  const [audioAiVoicesFetched, setAudioAiVoicesFetched] = useState(false);
  const [audioAiVoicePlayingId, setAudioAiVoicePlayingId] = useState<
    string | null
  >(null);
  const [audioAiVoiceSexFilter, setAudioAiVoiceSexFilter] =
    useState<string>("Todas");
  const [audioAiVoiceLanguageFilter, setAudioAiVoiceLanguageFilter] =
    useState<string>("Todas");
  const [voiceSexMenuOpen, setVoiceSexMenuOpen] = useState(false);
  const [voiceLangMenuOpen, setVoiceLangMenuOpen] = useState(false);
  const audioAiVoicePreviewRef = useRef<HTMLAudioElement | null>(null);

  // ── Voice cloning state ──────────────────────────────────────────────
  // El voice picker tiene 2 tabs: 'mis-voces' (voces clonadas de la
  // empresa) y 'voces-atlas' (catálogo global preset). El 'view'
  // permite alternar entre la lista y la sub-vista de creación.
  const [audioAiVoiceView, setAudioAiVoiceView] = useState<
    "list" | "create"
  >("list");
  const [audioAiVoiceTab, setAudioAiVoiceTab] = useState<
    "mis-voces" | "voces-atlas"
  >("voces-atlas");
  const [clonedVoices, setClonedVoices] = useState<ClonedVoice[]>([]);
  const [clonedVoicesLoading, setClonedVoicesLoading] = useState(false);
  const [clonedVoicesFetched, setClonedVoicesFetched] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  // Ref espejo: los callbacks de los tours (onDone diferido) leen el userId
  // FRESCO al momento de marcar visto, no el capturado al crear el effect.
  const currentUserIdRef = useRef<string | null>(null);
  currentUserIdRef.current = currentUserId;

  // Fetch user id once on mount — needed para pasarle a VoiceCloneCreator.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      setCurrentUserId(user?.id ?? null);
      // Cargar el estado "ya vi este onboarding" desde la DB (cross-device),
      // así los gates de los tours leen la verdad antes de decidir si muestran.
      if (user?.id) void loadOnboardingSeen(user.id);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch cloned voices lazily la primera vez que abren el voice picker.
  useEffect(() => {
    if (!showAudioAiVoiceSelector || clonedVoicesFetched) return;
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      setClonedVoicesLoading(true);
      const voices = await fetchClonedVoices(companyId);
      if (cancelled) return;
      setClonedVoices(voices);
      setClonedVoicesFetched(true);
      setClonedVoicesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [showAudioAiVoiceSelector, clonedVoicesFetched, companyId]);

  // Fetch voices lazily the first time the voice selector opens
  useEffect(() => {
    if (!showAudioAiVoiceSelector || audioAiVoicesFetched) return;
    let cancelled = false;
    (async () => {
      setAudioAiVoicesLoading(true);
      const { data, error } = await supabase
        .from("voces")
        .select("id, nombre, sexo, descripcion, preview_url, idioma, acento")
        .order("nombre");
      if (cancelled) return;
      if (!error && data) setAudioAiVoices(data as any);
      setAudioAiVoicesFetched(true);
      setAudioAiVoicesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [showAudioAiVoiceSelector, audioAiVoicesFetched]);

  // Stop voice preview audio on unmount
  useEffect(
    () => () => {
      if (audioAiVoicePreviewRef.current) {
        audioAiVoicePreviewRef.current.pause();
        audioAiVoicePreviewRef.current = null;
      }
    },
    [],
  );

  // ── AI Image Generation panel state ──
  // Dropdown visibility per image slot (which slot's "Subir / Generar IA" popover is open)
  const [imageSlotMenu, setImageSlotMenu] = useState<ImageGenTarget | null>(
    null,
  );
  const [imageSlotMenuRect, setImageSlotMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  // When user selects "Generar con IA" from a slot, we remember where to write the result
  const [imageGenTarget, setImageGenTarget] = useState<ImageGenTarget | null>(
    null,
  );
  const [imageGenPrompt, setImageGenPrompt] = useState("");
  const [imageGenResults, setImageGenResults] = useState<GenImageResult[]>([]);
  const [imageGenBusy, setImageGenBusy] = useState(false);
  const [imageGenPendingCount, setImageGenPendingCount] = useState(0);
  const [imageGenError, setImageGenError] = useState<string | null>(null);
  const [imageGenDetailId, setImageGenDetailId] = useState<string | null>(null);
  // True cuando el detalle/edición de una imagen generada se abrió DESDE
  // Multimedia (menú contextual "Editar"). La flecha de atrás vuelve a
  // Archivos → Multimedia en vez de a la grilla de generar / la escena.
  const [imageGenDetailFromFiles, setImageGenDetailFromFiles] = useState(false);
  const [imageGenHistorial, setImageGenHistorial] = useState<string[]>([]);
  const [showImageGenHistorial, setShowImageGenHistorial] = useState(false);
  const [imageGenCompanyId, setImageGenCompanyId] = useState<string | null>(
    null,
  );
  const [imageGenEditing, setImageGenEditing] = useState(false);
  const [imageGenEditPrompt, setImageGenEditPrompt] = useState("");
  // When the agent calls prefill_edit_generated_image, we need to open the
  // detail view AND enter edit mode with a specific prompt. The reset effect
  // on imageGenDetailId would normally clobber that, so we stash the
  // intended prompt here and the reset effect applies it instead of clearing.
  const pendingGenEditPromptRef = useRef<string | null>(null);
  const [imageGenEditSubmitting, setImageGenEditSubmitting] = useState(false);
  const [imageGenEditError, setImageGenEditError] = useState<string | null>(
    null,
  );
  // "Añadir" flow: attach reference images from device or previously-uploaded project gallery
  const [imageGenGalleryAssetIds, setImageGenGalleryAssetIds] = useState<
    string[]
  >([]);
  // Metadata (nombre + tipo) de los assets de la galería del modal "Añadir".
  // Se usa para filtrar: NO mostrar videos/no-imágenes (tiles rotos) ni las
  // copias-asset de imágenes generadas (duplicados, matcheo por nombre).
  const [galleryAssetMeta, setGalleryAssetMeta] = useState<
    Record<string, { name: string | null; type: string }>
  >({});
  const [imageGenAttachedAssetIds, setImageGenAttachedAssetIdsState] = useState<
    string[]
  >([]);
  // Mismo problema que en la escena: `prefill_ai_image` abre el panel antes de
  // resolver los adjuntos, así que el usuario puede tocarlos mientras tanto.
  const imagePrefillTokenRef = useRef(0);
  const imageGenAttachedAssetIdsRef = useRef(imageGenAttachedAssetIds);
  // Mismo criterio que `setSceneRefMode`: el espejo se sincroniza en el tick
  // del cambio, no en un efecto. Acá el setter recibe tanto un valor como un
  // updater —los call sites usan las dos formas— así que el ref se escribe
  // adentro del updater, donde ya está resuelto el valor final. Es idempotente
  // (mismo `prev` ⇒ mismo resultado), así que el doble render de StrictMode no
  // molesta.
  const setImageGenAttachedAssetIds = useCallback(
    (next: string[] | ((prev: string[]) => string[])) => {
      setImageGenAttachedAssetIdsState((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        imageGenAttachedAssetIdsRef.current = value;
        return value;
      });
    },
    [],
  );
  const [imageGenGalleryOpen, setImageGenGalleryOpen] = useState(false);
  const [imageGenGalleryMenuId, setImageGenGalleryMenuId] = useState<
    string | null
  >(null);
  // Pill filter for the "Imágenes del proyecto" overlay: 'uploaded' shows
  // user-uploaded assets, 'generated' shows AI-generated images. Clicking
  // a generated tile imports it as a project asset (async) and attaches it.
  const [imageGenGalleryFilter, setImageGenGalleryFilter] = useState<
    "uploaded" | "generated" | "productos" | "escenas" | "historial"
  >("uploaded");
  const [imageGenImportingGenIds, setImageGenImportingGenIds] = useState<
    Set<string>
  >(new Set());
  // Tracks generated-image ids that have already been imported in this
  // session, mapped to their resulting asset id, so re-clicking toggles
  // attachment instead of importing twice.
  const [imageGenImportedMap, setImageGenImportedMap] = useState<
    Record<string, string>
  >({});
  // Productos pill en la modal "Imágenes del proyecto": catálogo de la marca
  // del proyecto. Al tocar uno se importa como asset y se adjunta como
  // referencia (cacheado por product id dentro de la sesión).
  const [imageGenProductItems, setImageGenProductItems] = useState<
    MockProductV2[]
  >([]);
  const [imageGenProductsLoading, setImageGenProductsLoading] = useState(false);
  // Pill "Escenas" en la modal (mismo patrón que Productos; reusa el import y
  // los maps de productos porque los ids no colisionan).
  const [imageGenSceneItems, setImageGenSceneItems] = useState<MockProductV2[]>(
    [],
  );
  const [imageGenScenesLoading, setImageGenScenesLoading] = useState(false);
  // Pill "Historial" en la modal: imágenes generadas del cliente, por marca.
  const [imageGenHistorialItems, setImageGenHistorialItems] = useState<
    MockProductV2[]
  >([]);
  const [imageGenHistorialLoading, setImageGenHistorialLoading] =
    useState(false);
  const [imageGenProductImportedMap, setImageGenProductImportedMap] = useState<
    Record<string, string>
  >({});
  const [imageGenImportingProductIds, setImageGenImportingProductIds] =
    useState<Set<string>>(new Set());
  // Tracks the live height of the prompt-card section so the inline
  // "Imágenes del proyecto" overlay can be positioned exactly above it
  // (filling the panel from header bottom down to the card top).
  const imageGenPromptSectionRef = useRef<HTMLDivElement>(null);
  const [imageGenPromptSectionH, setImageGenPromptSectionH] = useState(0);
  useEffect(() => {
    const el = imageGenPromptSectionRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setImageGenPromptSectionH(el.offsetHeight),
    );
    ro.observe(el);
    setImageGenPromptSectionH(el.offsetHeight);
    return () => ro.disconnect();
  }, [activePanel]);
  const [imageGenAttachedPreviewId, setImageGenAttachedPreviewId] = useState<
    string | null
  >(null);
  const [imageGenUploading, setImageGenUploading] = useState(false);
  // "Seleccionar de video" flow: pick first/last frame from a timeline clip as the target slot's frame
  const [frameFromVideoTarget, setFrameFromVideoTarget] =
    useState<ImageGenTarget | null>(null);
  // Slot-preview flow: when the user picks an asset for a scene slot (e.g. a
  // frame extracted from a clip), instead of assigning it directly we route
  // them through a preview screen — same UX as the generated-image detail —
  // with two actions: Utilizar (commit to the slot) or Editar (open the
  // generation flow with this image attached as a reference + a prompt
  // describing the change).
  const [slotPreview, setSlotPreview] = useState<{
    assetId: string;
    target: ImageGenTarget;
  } | null>(null);
  const [slotPreviewEditing, setSlotPreviewEditing] = useState(false);
  const [slotPreviewEditPrompt, setSlotPreviewEditPrompt] = useState("");
  const [frameFromVideoClipMenu, setFrameFromVideoClipMenu] = useState<{
    clipId: string;
    top: number;
    left: number;
  } | null>(null);
  const [pickerClipRects, setPickerClipRects] = useState<
    Array<{
      clipId: string;
      top: number;
      left: number;
      width: number;
      height: number;
    }>
  >([]);
  const imageGenFileInputRef = useRef<HTMLInputElement>(null);
  const imageGenDetailScrollRef = useRef<HTMLDivElement>(null);
  // Reference images: array of asset IDs from video_project_assets
  const [sceneRefAssetIds, setSceneRefAssetIds] = useState<string[]>([]);
  // Local preview URLs by asset id (blob URL just uploaded, or signed URL after load)
  const [assetPreviewUrls, setAssetPreviewUrls] = useState<
    Record<string, string>
  >({});
  // Cached video thumbnail strips by clip id
  const [clipThumbnails, setClipThumbnails] = useState<
    Record<string, string[]>
  >({});
  const thumbGenRef = useRef<Set<string>>(new Set());
  // Cached waveform peaks by clip id (downsampled amplitude values 0..1)
  const [audioPeaks, setAudioPeaks] = useState<Record<string, number[]>>({});
  const peaksGenRef = useRef<Set<string>>(new Set());
  // Same shape but keyed by ASSET id (independent of timeline clips). Used
  // by the Archivos→Audio tab so each card can render its waveform inline
  // even before the asset has been added to the timeline.
  const [audioAssetPeaks, setAudioAssetPeaks] = useState<
    Record<string, number[]>
  >({});
  const audioAssetPeaksGenRef = useRef<Set<string>>(new Set());
  // Single-track audio preview for the Audio tab: which asset is playing,
  // its current playback progress (0..1) for the moving waveform overlay,
  // and the underlying <audio> element + RAF id so we can clean up cleanly.
  const [playingAudioAssetId, setPlayingAudioAssetId] = useState<string | null>(
    null,
  );
  const [audioPlaybackProgress, setAudioPlaybackProgress] = useState(0);
  const audioPreviewElRef = useRef<HTMLAudioElement | null>(null);
  const audioPreviewRafRef = useRef<number | null>(null);
  // Single-track video preview for the Videos tab. We keep a registry of
  // each tile's <video> element via a ref-callback Map so the toggle helper
  // can pause the previously-playing tile when starting a new one.
  const [playingVideoAssetId, setPlayingVideoAssetId] = useState<string | null>(
    null,
  );
  const videoPreviewElsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const sceneRefImageRef = useRef<HTMLInputElement>(null);
  const [sceneFirstFrameAssetId, setSceneFirstFrameAssetId] = useState<
    string | null
  >(null);
  const [sceneLastFrameAssetId, setSceneLastFrameAssetId] = useState<
    string | null
  >(null);
  const [sceneDuration, setSceneDuration] = useState(5);
  const [sceneSpeechSpeed, setSceneSpeechSpeed] = useState(1.1);
  // ── Multiescena (General + Reference only) ───────────────────────────────
  // When the toggle is on, the user composes multiple scenes (each with its
  // own prompt + duration). Topes BASE (calidad Estándar / Seedance 2):
  // por escena 4..10s, suma ≤15s. En Profesional (Seedance 2.5 multi_frame)
  // el API solo limita la SUMA (4-30s), así que por escena se puede llegar a
  // 30 — ver sceneMultiMaxTotal / sceneMultiMaxPerScene más abajo.
  const SCENE_MULTI_MIN = 4;
  const SCENE_MULTI_MAX = 10;
  const SCENE_MULTI_MAX_TOTAL = 15;
  const [sceneMultiEnabled, setSceneMultiEnabled] = useState(false);
  const [sceneMultiList, setSceneMultiList] = useState<
    Array<{ id: string; prompt: string; duration: number }>
  >(() => [{ id: generateId(), prompt: "", duration: 5 }]);
  const sceneMultiTotal = sceneMultiList.reduce((s, x) => s + x.duration, 0);
  // Visual feedback when the user tries to drag past the per-scene/global cap.
  // multiHitTick is incremented to retrigger the shake animation on the total
  // counter; multiMaxHitId marks which scene's value badge should show "Max".
  const [multiHitTick, setMultiHitTick] = useState(0);
  const [multiMaxHitId, setMultiMaxHitId] = useState<string | null>(null);
  const multiMaxHitTimerRef = useRef<number | null>(null);
  const triggerMultiMaxHit = useCallback((sceneId: string) => {
    setMultiHitTick((c) => c + 1);
    setMultiMaxHitId(sceneId);
    if (multiMaxHitTimerRef.current)
      window.clearTimeout(multiMaxHitTimerRef.current);
    multiMaxHitTimerRef.current = window.setTimeout(() => {
      setMultiMaxHitId(null);
      multiMaxHitTimerRef.current = null;
    }, 700);
  }, []);
  // When entering dialog + motion, snap duration to the closest UGC-nuevo option.
  useEffect(() => {
    if (!(sceneMode === "personas" && scenePersonType === "motion")) return;
    const OPTS = [4, 6, 8, 12, 14, 16, 20, 22, 24, 28, 30, 32];
    if (OPTS.includes(sceneDuration)) return;
    const snapped = OPTS.reduce((p, c) =>
      Math.abs(c - sceneDuration) < Math.abs(p - sceneDuration) ? c : p,
    );
    setSceneDuration(snapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneMode, scenePersonType]);
  const sceneFirstFrameRef = useRef<HTMLInputElement>(null);
  const sceneLastFrameRef = useRef<HTMLInputElement>(null);
  // "rapido" se quitó del Estudio el 2026-08-30 (clips viejos pueden seguir
  // teniendo aiQuality "rapido" persistido — al editarlos caen a Estándar).
  const [sceneQuality, setSceneQuality] = useState<"estandar" | "profesional">(
    "estandar",
  );
  const [sceneResolution, setSceneResolution] = useState<
    "480p" | "720p" | "1080p"
  >("720p");
  const [qualityMenuOpen, setQualityMenuOpen] = useState(false);
  const [resolutionMenuOpen, setResolutionMenuOpen] = useState(false);

  // Dialog mode doesn't support 480p — bump to 720p automatically.
  useEffect(() => {
    if (sceneMode === "personas" && sceneResolution === "480p") {
      setSceneResolution("720p");
    }
  }, [sceneMode, sceneResolution]);

  // El modo personas genera con HeyGen (rama dialogo): Profesional (Seedance
  // 2.5) no aplica ahí — si quedó elegido de otro modo, baja a Estándar para
  // no cobrar la tarifa pro por un video que no la usa.
  useEffect(() => {
    if (sceneMode === "personas" && sceneQuality === "profesional") {
      setSceneQuality("estandar");
    }
  }, [sceneMode, sceneQuality]);

  // Profesional permite hasta 30s; al bajar a Estándar el tope vuelve a
  // 15s — clamp para que el payload nunca salga fuera de rango.
  useEffect(() => {
    if (sceneQuality !== "profesional" && sceneDuration > 15) {
      setSceneDuration(15);
    }
  }, [sceneQuality, sceneDuration]);

  // Topes de la multiescena según calidad. Profesional (Seedance 2.5): el API
  // solo limita la SUMA (multi_frame_prompts, 4-30s) — sin tope por toma, así
  // que una escena puede llegar a 30s. Estándar (Seedance 2): suma ≤15s y
  // 4-10s por toma (tope histórico). Los leen el contador, los sliders por
  // escena, "Añadir escena" y el guard del botón Generar.
  const sceneMultiMaxTotal =
    sceneQuality === "profesional" ? 30 : SCENE_MULTI_MAX_TOTAL;
  const sceneMultiMaxPerScene =
    sceneQuality === "profesional" ? 30 : SCENE_MULTI_MAX;

  // Al bajar de Profesional a Estándar, la multiescena se adapta sola a los
  // topes de Estándar: primero cada toma baja a ≤10s (Profesional permite
  // hasta 30 por toma), y si la suma sigue pasada de 15s se recortan
  // duraciones desde la última escena (piso 4s c/u) — los prompts no se
  // tocan. Si ni así entra (más de 3 escenas al piso), no se borra nada: el
  // contador queda en rojo y Generar deshabilitado con el motivo, así el
  // backend nunca devuelve el 400 de "hasta 30/15 segundos".
  useEffect(() => {
    if (sceneQuality === "profesional") return;
    setSceneMultiList((prev) => {
      const clamped = prev.map((s) => ({
        ...s,
        duration: Math.min(SCENE_MULTI_MAX, s.duration),
      }));
      const total = clamped.reduce((s, x) => s + x.duration, 0);
      if (
        total <= SCENE_MULTI_MAX_TOTAL &&
        clamped.every((s, i) => s.duration === prev[i].duration)
      )
        return prev;
      let excess = total - SCENE_MULTI_MAX_TOTAL;
      for (let i = clamped.length - 1; i >= 0 && excess > 0; i--) {
        const cut = Math.min(excess, clamped[i].duration - SCENE_MULTI_MIN);
        if (cut > 0) {
          clamped[i].duration -= cut;
          excess -= cut;
        }
      }
      return clamped;
    });
    // Solo al cambiar la calidad — no re-recortar en cada edición manual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneQuality]);

  // Modelo de tarifa (utils/credits) según la calidad elegida — mismo mapeo
  // que hace el backend con `quality` (estandar→standard · profesional→pro /
  // Seedance 2.5).
  const sceneCreditModel: VideoModel =
    sceneQuality === "profesional" ? "pro" : "standard";

  // Live credit-cost projection for the audio panel's "Generar" button.
  // Music = flat song price; voice = per-character TTS price unless the user
  // uploaded their own audio (s2s), in which case we charge per real-second.
  // Read by the badge AND by addAiAudioClip's pre-flight guard.
  const audioPanelCost: number = (() => {
    if (audioAiType === "music") return creditsForSong();
    if (
      audioAiDialogueInputMode === "audio" &&
      audioAiDialogueAudio
    ) {
      // ElevenLabs s2s costs the same as TTS per output second.
      // Pricing reference (credits.ts): VOICE_CR_PER_CHAR × CHARS_PER_SEC_AT_1X
      // ≈ 0.047 cr/s. Round up, minimum 1.
      return creditsForVoice(
        Math.ceil(audioAiDialogueAudio.durationSec * 12.5),
      );
    }
    return creditsForVoice(audioAiPrompt.trim().length);
  })();

  // Live credit-cost projection for the scene panel's "Generar" button.
  // The badge reads this; the assertCredits() guard reads it too — so the
  // displayed cost and the enforcement match exactly. Recomputed every
  // render (cheap: one switch + a few arithmetic ops).
  const scenePanelCost: number = (() => {
    if (sceneMode === "personas") {
      // Audio input mode: the user provided their own audio, so we know its
      // exact duration. The workflow charges per-second using the same
      // resolution/quality table as general clips, so we mirror that to
      // keep the badge consistent with the actual deduction.
      if (
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
      ) {
        return creditsForVideoGeneral({
          durationSec: sceneDialogueAudio.durationSec,
          model: sceneCreditModel,
          resolution: sceneResolution as VideoResolution,
        });
      }
      // Static personas: the lone textarea labeled "Diálogo de la escena"
      // writes to `scenePrompt` (a quirk of the current panel JSX). Motion
      // mode is the only one with a dedicated `sceneDialogue` textarea
      // separate from the visual prompt. Picking the wrong source is what
      // makes the badge get stuck at 1 in static mode.
      const dialogText =
        scenePersonType === "motion" ? sceneDialogue : scenePrompt;
      return creditsForVideoDialog({
        chars: dialogText.length,
        speed: sceneSpeechSpeed,
      });
    }
    const isMulti =
      sceneMode === "general" &&
      sceneRefMode === "reference" &&
      sceneMultiEnabled;
    if (isMulti) {
      return creditsForMultiSceneVideo({
        scenes: sceneMultiList.map((s) => ({ duration: s.duration })),
        model: sceneCreditModel,
        resolution: sceneResolution as VideoResolution,
      });
    }
    return creditsForVideoGeneral({
      durationSec: sceneDuration,
      model: sceneCreditModel,
      resolution: sceneResolution as VideoResolution,
    });
  })();

  // Agent chatbot
  const [showAgentChat, setShowAgentChat] = useState(false);
  // Responsive clip toolbar — when there is not enough horizontal room for
  // the floating clip toolbar (e.g. both side panels open, narrow viewport,
  // Director button taking space), we progressively collapse text-bearing
  // buttons to icon-only starting from the LEFT (least-important actions).
  useEffect(() => {
    const el = clipToolbarRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(() => setToolbarTick((t) => t + 1));
    ro.observe(parent);
    ro.observe(el);
    if (directorBtnRef.current) ro.observe(directorBtnRef.current);
    return () => ro.disconnect();
  }, [selectedClipId, showAgentChat]);
  // Last action this effect took, to break the collapse↔restore oscillation
  // that happens when the available room sits between "fits with N labels"
  // and "fits with N+1 labels". Without this guard the effect would flip
  // forever and React throws "Maximum update depth exceeded".
  const lastToolbarActionRef = useRef<"collapse" | "restore" | null>(null);
  // Reset the guard when an *external* trigger changes — if the user
  // selected a different clip or toggled the chat, the toolbar's ideal
  // collapsed value can legitimately move and we shouldn't carry over the
  // previous chain's decision.
  useEffect(() => {
    lastToolbarActionRef.current = null;
  }, [selectedClipId, showAgentChat, toolbarTick, toolbarBtnCount]);
  useLayoutEffect(() => {
    const el = clipToolbarRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    // The toolbar is horizontally centered (left-1/2 -translate-x-1/2),
    // so anything that floats on the right edge (the Director de estudio
    // button) effectively cuts the available width by twice its visible
    // footprint: once on the right where it lives, and once on the left
    // (mirrored) so the toolbar can stay centered. We add 12px for the
    // right-3 offset plus 8px of breathing room on each side.
    const directorEl = directorBtnRef.current;
    const directorReserve = directorEl ? directorEl.offsetWidth + 12 + 8 : 0;
    const available = parent.clientWidth - 24 - 2 * directorReserve;
    // Sync the rendered-button count so the next render can map indices
    // to right-edge distances.
    const totalLabels = el.querySelectorAll("[data-clip-tb-label]").length;
    if (totalLabels !== toolbarBtnCount) setToolbarBtnCount(totalLabels);
    const tooWide = el.scrollWidth > available;
    if (tooWide) {
      // Only keep collapsing while there are still labels visible.
      const visibleLabels = el.querySelectorAll(
        "[data-clip-tb-label]:not(.hidden)",
      ).length;
      if (visibleLabels > 0) {
        setToolbarCollapsed((c) => c + 1);
        lastToolbarActionRef.current = "collapse";
      }
    } else if (toolbarCollapsed > 0 && el.scrollWidth + 80 < available) {
      // Plenty of headroom — try restoring one collapsed label, *unless*
      // we just collapsed in the previous run. That would mean we're
      // sitting in the gap between "fits with N labels" and "doesn't fit
      // with N+1", and toggling would loop forever.
      if (lastToolbarActionRef.current === "collapse") return;
      setToolbarCollapsed((c) => Math.max(0, c - 1));
      lastToolbarActionRef.current = "restore";
    }
  }, [
    toolbarCollapsed,
    toolbarTick,
    toolbarBtnCount,
    selectedClipId,
    showAgentChat,
  ]);
  // Width preset when the agent chat is visible. 'sidebar' docks it as a
  // narrow right rail; 'full' expands it to take over the full editor area
  // (to the right of any open AI panel). Default to 'sidebar' — the more
  // passive state that doesn't hide the preview/timeline.
  const [agentChatMode, setAgentChatMode] = useState<"sidebar" | "full">(
    "sidebar",
  );
  useEffect(() => {
    if (activePanel !== "ai-image-gen") setImageGenGalleryOpen(false);
  }, [activePanel]);
  useEffect(() => {
    setFilesImageMenu(null);
  }, [activePanel, filesTab]);
  // Si salís del panel Archivos, cerramos el modo "elegir modelo" para que no
  // quede pegado y reaparezca la próxima vez que abras Archivos.
  useEffect(() => {
    if (activePanel !== "files") setModelPickerOpen(false);
  }, [activePanel]);
  // Onboarding del editor: una vez que el proyecto cargó, si el usuario no lo
  // vio antes, lanzamos el TOUR guiado (spotlight). El "ya lo vi" se persiste en
  // la DB (profiles.onboarding_seen) vía el helper, con localStorage de fallback.
  useEffect(() => {
    if (projectLoading || !currentUserId) return;
    let cancelled = false;
    let t: ReturnType<typeof setTimeout> | null = null;
    // Esperamos el estado de la DB (cross-device) antes de decidir, así no se
    // re-muestra en otra PC si el usuario ya lo vio. loadOnboardingSeen está
    // cacheado, así que esto es instantáneo si ya cargó en el mount.
    (async () => {
      await loadOnboardingSeen(currentUserId);
      if (cancelled || isOnboardingSeen("tour")) return;
      // Esperamos un toque a que el layout del editor esté pintado: el tour mide
      // los elementos (rail, Director, timeline) para el spotlight.
      t = setTimeout(() => {
        if (cancelled) return;
        startEditorTour(() =>
          markOnboardingSeen("tour", currentUserIdRef.current),
        );
      }, 500);
    })();
    return () => {
      cancelled = true;
      if (t) clearTimeout(t);
    };
  }, [projectLoading, currentUserId]);
  // ── Hydrate the "Añadir" gallery with all project image assets ──────────
  // `imageGenGalleryAssetIds` only held hand-curated IDs from inline
  // uploads / agent attachments / persisted state, so images uploaded via
  // other flows (the Archivos panel, logos, reference pickers, …) were
  // invisible here. When the AI image-gen panel opens we fetch every image
  // asset in the project and merge them in, deduped and with existing
  // ordering preserved (existing IDs stay first so recent attachments
  // don't jump around when the DB list lands).
  useEffect(() => {
    if (activePanel !== "ai-image-gen") return;
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const assets = await fetchProjectAssetsByType(projectId, "image");
        if (cancelled) return;
        setGalleryAssetMeta((prev) => {
          const next = { ...prev };
          for (const a of assets)
            next[a.id] = { name: a.name, type: a.type };
          return next;
        });
        setImageGenGalleryAssetIds((prev) => {
          const seen = new Set(prev);
          const merged = [...prev];
          for (const a of assets) {
            if (!seen.has(a.id)) {
              merged.push(a.id);
              seen.add(a.id);
            }
          }
          return merged;
        });
        // Kick off signed-URL resolution for any asset we don't have a
        // preview for yet; the gallery's effect also does this but only
        // runs when the id-set changes, so we preempt it here to avoid a
        // visible empty-placeholder flash.
        const missing = assets.filter((a) => !assetPreviewUrls[a.id]);
        if (missing.length > 0) {
          const resolved = await Promise.all(
            missing.map(async (a) => {
              try {
                const url = await getAssetSignedUrl(a.storage_path);
                return url ? ([a.id, url] as const) : null;
              } catch {
                return null;
              }
            }),
          );
          if (cancelled) return;
          const updates: Record<string, string> = {};
          for (const r of resolved) if (r) updates[r[0]] = r[1];
          if (Object.keys(updates).length > 0) {
            setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
          }
        }
      } catch (err) {
        console.warn("[VideoEditor] failed to hydrate image-gen gallery", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activePanel, projectId]);
  // While the chat is open, opening any AI generation panel auto-expands the
  // chat to 'full' mode (sidebar + 510px panel would crowd the editor).
  // We remember the mode that was active just before auto-expanding so we can
  // restore it when the AI panel closes.
  const agentChatModeBeforeAiPanelRef = useRef<"sidebar" | "full" | null>(null);
  useEffect(() => {
    const aiPanels = ["ai-scene", "ai-audio", "ai-image-gen"];
    const inAiPanel = aiPanels.includes(activePanel);
    if (showAgentChat && inAiPanel) {
      if (agentChatMode !== "full") {
        if (agentChatModeBeforeAiPanelRef.current === null) {
          agentChatModeBeforeAiPanelRef.current = agentChatMode;
        }
        setAgentChatMode("full");
      }
    } else if (!inAiPanel && agentChatModeBeforeAiPanelRef.current !== null) {
      // AI panel closed — restore the chat mode we had before auto-expanding.
      const restored = agentChatModeBeforeAiPanelRef.current;
      agentChatModeBeforeAiPanelRef.current = null;
      setAgentChatMode(restored);
    }
  }, [activePanel, showAgentChat, agentChatMode]);
  type AgentAskQuestion = {
    id: string;
    question: string;
    type: "single" | "multi" | "text";
    options?: string[];
    allowCustom?: boolean;
  };
  // Registry of lucide icon components used in tool-message rows. Stored
  // by stable string key so we can persist tool messages to the DB and
  // restore them on load (we can't serialize React components directly).
  // If you add a new icon to a `toolMessage(...)` call, add it here too.
  const TOOL_MSG_ICON_BY_KEY: Record<
    string,
    React.ComponentType<{ className?: string }>
  > = {
    ArrowLeftRight,
    BarChart3,
    BookOpen,
    Brain,
    Compass,
    Eraser,
    Film,
    FolderOpen,
    Hand,
    Image: ImageIcon,
    Mic,
    Music,
    Pencil,
    Plus,
    Ratio,
    Scissors,
    Search,
    Sparkles,
    Trash2,
    Upload,
    Video,
  };
  const keyForToolIcon = (
    Icon: React.ComponentType<{ className?: string }> | undefined,
  ): string | undefined => {
    if (!Icon) return undefined;
    for (const [k, v] of Object.entries(TOOL_MSG_ICON_BY_KEY)) {
      if (v === Icon) return k;
    }
    return undefined;
  };
  type AgentMessage = {
    role: "user" | "assistant" | "tool" | "ask" | "thinking";
    text?: string;
    // Lucide icon component used as a leading glyph for tool messages
    // (rendered in the same color as the label).
    icon?: React.ComponentType<{ className?: string }>;
    // Images the user attached with this turn (rendered in the bubble and
    // forwarded to Claude as multimodal content blocks).
    attachments?: { assetId?: string; url: string }[];
    questions?: AgentAskQuestion[];
    answers?: Record<string, string | string[]>;
    answered?: boolean;
    // Only for role === 'thinking': Anthropic's extended-thinking signature.
    // Must be passed back verbatim on subsequent tool-use rounds, or the API
    // rejects the call. `redacted` marks a server-side redacted thinking
    // block (no plaintext available).
    thinkingSignature?: string;
    thinkingRedactedData?: string;
    // ISO timestamp — used to scope a server-side delete when the user
    // rewinds the conversation back to one of their messages.
    createdAt?: string;
    // Solo para role === 'tool'. `startedAt` (Date.now) alimenta el cronómetro
    // vivo que se muestra mientras la tool corre → el usuario ve que NO está
    // colgado. `hint` es el "puede tardar ~1 min" de las tools largas. Ambos son
    // efímeros (no se persisten): al recargar, una tool histórica no está viva.
    startedAt?: number;
    hint?: string;
    // Solo para role === 'assistant': marca una burbuja de ERROR del turno (no
    // una respuesta real). La UI le cuelga un botón "Reintentar" cuando es el
    // último mensaje; el rebuild-desde-texto la EXCLUYE (no debe ir a Claude).
    error?: boolean;
  };
  const AGENT_INITIAL_GREETING =
    "Hola! Soy tu director de estudio. Puedo ayudarte a crear escenas, sugerir prompts, y planificar tu video. ¿En qué puedo ayudarte?";
  const [agentMessages, setAgentMessages] = useState<AgentMessage[]>([
    { role: "assistant", text: AGENT_INITIAL_GREETING },
  ]);
  // Conversación ESTRUCTURADA que sobrevive entre turnos (incluye los
  // tool_use / tool_result, no solo el texto de las burbujas). Es la memoria
  // real del Director: sin esto, al mandar un mensaje nuevo se reconstruía el
  // historial solo desde el texto y el agente perdía qué tools ya corrió y qué
  // obtuvo (ej: qué producto elegiste) → re-preguntaba todo. Vacío = se
  // reconstruye desde el texto (primer turno, post-rewind o post-restore).
  const conversationRef = useRef<
    Array<{ role: "user" | "assistant"; content: any }>
  >([]);
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  // The dropdown has two views: the action list ('menu') and an inline
  // "Consumo" detail panel. Both render in the same popover position,
  // anchored to the three-dot button — no centered modal, no scrim.
  const [agentMenuView, setAgentMenuView] = useState<"menu" | "consumo">(
    "menu",
  );
  const [consumoLoading, setConsumoLoading] = useState(false);
  const [consumoCredits, setConsumoCredits] = useState<number | null>(null);
  const [consumoError, setConsumoError] = useState<string | null>(null);
  // ── Interactive request_user_upload state ──────────────────────────────
  // When the agent calls request_user_upload, it opens a dedicated panel
  // with a custom message + button label so the user can drop files and
  // confirm. The tool call resolves with the asset IDs the user uploaded.
  const uploadResolverRef = useRef<
    ((result: { content: string | any[] }) => void) | null
  >(null);
  const [agentUploadRequest, setAgentUploadRequest] = useState<{
    message: string;
    buttonLabel: string;
    accept: string; // e.g. 'image/*' or 'image/*,video/*'
    // Cuando true, las imágenes subidas se procesan a fondo blanco (personaje)
    // antes de devolverlas al agente (en vez de usarse crudas).
    procesarComoPersonaje?: boolean;
  } | null>(null);
  // Project assets the user has uploaded during the current request session.
  // Reset every time a new request_user_upload is opened.
  const [agentUploadedAssets, setAgentUploadedAssets] = useState<
    Array<{ id: string; name: string; url?: string }>
  >([]);
  const [agentUploadInProgress, setAgentUploadInProgress] = useState(false);
  // Upload a batch of files to the project, tracking them as session uploads
  // so the confirm button can pass their IDs back to the agent tool call.
  const handleAgentUploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files || []);
      if (list.length === 0) return;
      if (!projectId || !companyId) return;
      const procesarModelo = !!agentUploadRequest?.procesarComoPersonaje;
      const cid = companyId ?? imageGenCompanyId;
      const rechazados: string[] = [];
      setAgentUploadInProgress(true);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        for (const file of list) {
          try {
            if (
              procesarModelo &&
              cid &&
              user?.id &&
              file.type.startsWith("image/")
            ) {
              // Procesar la foto como PERSONAJE (fondo blanco) vía n8n
              // (createModelFromPhoto → procesar_modelo_creado), guardarlo como
              // modelo, e importar la imagen PROCESADA como asset del proyecto
              // para devolvérsela al agente (en vez de la foto cruda).
              const nuevo = await createModelFromPhoto({
                companyId: cid,
                userId: user.id,
                name: "",
                file,
              });
              const resp = await fetch(nuevo.thumbnailUrl);
              const blob = await resp.blob();
              const procFile = new File(
                [blob],
                `${(nuevo.nombre || "personaje").slice(0, 40)}.png`,
                { type: blob.type || "image/png" },
              );
              const asset = await uploadProjectAsset({
                file: procFile,
                projectId,
                companyId,
                userId: user.id,
              });
              const blobUrl = URL.createObjectURL(blob);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              setAgentUploadedAssets((prev) => [
                ...prev,
                { id: asset.id, name: nuevo.nombre || file.name, url: blobUrl },
              ]);
            } else {
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              const blobUrl = URL.createObjectURL(file);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              setAgentUploadedAssets((prev) => [
                ...prev,
                { id: asset.id, name: asset.name || file.name, url: blobUrl },
              ]);
            }
          } catch (err) {
            console.error(
              "[VideoEditor] agent upload file failed",
              file.name,
              err,
            );
            rechazados.push(describeUploadError(err, file.name));
          }
        }
      } finally {
        setAgentUploadInProgress(false);
        // Una sola modal para toda la tanda: con `multiple` el usuario puede
        // elegir 5 archivos y que fallen 3, y encadenar tres modales sería
        // peor que no avisar. Los que sí entraron ya están en la lista.
        if (rechazados.length > 0) setUploadErrorMsg(rechazados.join("\n\n"));
      }
    },
    [projectId, companyId, imageGenCompanyId, agentUploadRequest],
  );
  const removeAgentUploadedAsset = useCallback((assetId: string) => {
    setAgentUploadedAssets((prev) => prev.filter((a) => a.id !== assetId));
  }, []);
  const confirmAgentUpload = useCallback(async () => {
    const resolver = uploadResolverRef.current;
    uploadResolverRef.current = null;
    const sessionAssets = agentUploadedAssets.slice();
    setAgentUploadRequest(null);
    setAgentUploadedAssets([]);
    setActivePanel("none");
    if (!resolver) return;
    if (sessionAssets.length === 0) {
      resolver({ content: JSON.stringify({ cancelled: true }) });
      return;
    }
    // Build a content block array: a leading text summary with the assetIds
    // (so the agent can reference them in subsequent tools), then each
    // uploaded image inline as an image block so Claude can actually SEE it.
    // Videos are listed by name only (Claude can't view video frames here;
    // for that the agent should call analyze_video_quick with the assetId).
    const isVideo = (name: string) =>
      /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(name);
    const isImage = (name: string) =>
      /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif)$/i.test(name);
    const summary = {
      assetIds: sessionAssets.map((a) => a.id),
      assets: sessionAssets.map((a) => ({
        id: a.id,
        name: a.name,
        kind: isVideo(a.name) ? "video" : isImage(a.name) ? "image" : "other",
      })),
    };
    const blocks: any[] = [
      {
        type: "text",
        text:
          `El usuario subió ${sessionAssets.length} archivo(s). ` +
          `Detalle (id | nombre | tipo): ` +
          summary.assets
            .map((a) => `${a.id} | ${a.name} | ${a.kind}`)
            .join("  ;  ") +
          `. A continuación las imágenes inline para que las observes directamente. ` +
          `Para los videos, usá analyze_video_quick con su assetId si necesitás verlos.`,
      },
    ];
    for (const a of sessionAssets) {
      if (!a.url) continue;
      if (isImage(a.name)) {
        try {
          const base64 = await urlToBase64Jpeg(a.url);
          blocks.push({ type: "text", text: `asset_id=${a.id} (${a.name})` });
          blocks.push({
            type: "image",
            source: { type: "base64", media_type: "image/jpeg", data: base64 },
          });
        } catch (err: any) {
          blocks.push({
            type: "text",
            text: `asset_id=${a.id} (${a.name}): no se pudo cargar como imagen (${err?.message || err})`,
          });
        }
      } else if (isVideo(a.name)) {
        blocks.push({
          type: "text",
          text: `asset_id=${a.id} (${a.name}): video — usá analyze_video_quick para ver frames.`,
        });
      } else {
        blocks.push({
          type: "text",
          text: `asset_id=${a.id} (${a.name}): tipo no visualizable directamente.`,
        });
      }
    }
    resolver({ content: blocks });
  }, [agentUploadedAssets]);
  // If the panel gets closed by any means other than confirm (e.g. the
  // user clicked the X in the header, or the agent triggered another
  // prefill_* that changed activePanel) while the tool call is still
  // awaiting, resolve it as cancelled so the agent doesn't hang forever.
  useEffect(() => {
    if (activePanel !== "agent-upload" && uploadResolverRef.current) {
      const resolver = uploadResolverRef.current;
      uploadResolverRef.current = null;
      setAgentUploadRequest(null);
      setAgentUploadedAssets([]);
      resolver({ content: JSON.stringify({ cancelled: true }) });
    }
  }, [activePanel]);
  // ── Interactive request_product_selection state ─────────────────────────
  // Cuando el Director llama request_product_selection, abrimos la pill
  // "Productos" del panel Archivos en modo "selección para el Director".
  // El resolver devuelve los productos elegidos (o null si canceló); el
  // handler de la tool se encarga de importarlos como assets + mandarlos
  // inline al agente. En modo simple, tocar un producto resuelve al toque;
  // en modo múltiple se marcan varios y se confirma con el botón.
  const productPickResolverRef = useRef<
    ((products: MockProductV2[] | null) => void) | null
  >(null);
  const [agentProductRequest, setAgentProductRequest] = useState<{
    message: string;
    buttonLabel: string;
    multiple: boolean;
  } | null>(null);
  const [agentProductSelection, setAgentProductSelection] = useState<
    MockProductV2[]
  >([]);
  const confirmAgentProductPick = useCallback((products: MockProductV2[]) => {
    const resolver = productPickResolverRef.current;
    productPickResolverRef.current = null;
    setAgentProductRequest(null);
    setAgentProductSelection([]);
    setActivePanel("none");
    if (resolver) resolver(products);
  }, []);
  const cancelAgentProductPick = useCallback(() => {
    const resolver = productPickResolverRef.current;
    productPickResolverRef.current = null;
    setAgentProductRequest(null);
    setAgentProductSelection([]);
    if (resolver) resolver(null);
  }, []);
  // Si el usuario sale de la pill Productos mientras hay una selección
  // pendiente, resolvemos como cancelado para que el agente no quede colgado.
  useEffect(() => {
    if (
      productPickResolverRef.current &&
      !(activePanel === "files" && filesTab === "productos")
    ) {
      const resolver = productPickResolverRef.current;
      productPickResolverRef.current = null;
      setAgentProductRequest(null);
      setAgentProductSelection([]);
      resolver(null);
    }
  }, [activePanel, filesTab]);
  // Interactive ask_user state: a single active question session.
  const askResolverRef = useRef<((json: string) => void) | null>(null);
  // Cinturones anti-cuelgue del Director (patrón espejo de useV2Chat):
  // AbortController del turno (botón Detener + watchdog), token de generación
  // para descartar turnos viejos, flag del watchdog y timer de inactividad.
  const turnAbortRef = useRef<AbortController | null>(null);
  const agentGenerationRef = useRef(0);
  const watchdogFiredRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Snapshot del historial (apiMessages) del último turno que FALLÓ, para que el
  // botón "Reintentar" reanude el stream sin re-enviar el texto (no duplica el
  // user message ni toca la DB). Ver el catch de sendAgentMessage.
  const lastFailedTurnRef = useRef<{
    apiMessages: Array<{ role: "user" | "assistant"; content: any }>;
  } | null>(null);
  // Hydration latch for the localStorage persistence below: stays false
  // until the restore effect has run for the current projectId. Without
  // this, the persist effect runs first on initial render with
  // activeAsk=null and wipes the saved key before restore can read it.
  const askHydratedRef = useRef(false);
  const [activeAsk, setActiveAsk] = useState<{
    questions: AgentAskQuestion[];
    currentIndex: number;
  } | null>(null);
  const [askFormState, setAskFormState] = useState<
    Record<string, string | string[]>
  >({});
  const [askCustomState, setAskCustomState] = useState<Record<string, string>>(
    {},
  );
  const [agentInput, setAgentInput] = useState("");
  const [agentStreaming, setAgentStreaming] = useState(false);
  const [agentAttachments, setAgentAttachments] = useState<
    { id: string; assetId?: string; url: string; uploading?: boolean }[]
  >([]);
  // URL of an agent-chat image to show in an enlarged lightbox overlay.
  const [agentPreviewUrl, setAgentPreviewUrl] = useState<string | null>(null);
  const agentFileInputRef = useRef<HTMLInputElement>(null);
  const agentMessagesEndRef = useRef<HTMLDivElement>(null);
  // Scroll container for the agent chat messages; used to track whether the
  // user is near the bottom (to show/hide the scroll-down floating arrow).
  const agentMessagesScrollRef = useRef<HTMLDivElement>(null);
  // DOM node of the latest user message — set via callback ref on render so
  // we can scroll it to the top of the chat after sending.
  const latestUserMsgRef = useRef<HTMLDivElement | null>(null);
  // While true, the chat is "pinned" to the latest user message at the top:
  // streaming-driven auto-scroll is suppressed so the assistant reply grows
  // beneath the user message without yanking the viewport. Cleared when the
  // user manually scrolls to the bottom (opt-in to follow streaming) or
  // when the streaming turn finishes.
  const pinUserToTopRef = useRef(false);
  // Tracks whether a recent scroll event was likely user-driven (wheel,
  // touchmove, keyboard). Used so the onScroll handler only clears the
  // pin on real user intent — not on our own programmatic re-anchoring
  // or layout-shift-induced scroll.
  const userScrollIntentRef = useRef(0);
  const markUserScrollIntent = useCallback(() => {
    userScrollIntentRef.current = Date.now();
    // As soon as the user genuinely tries to scroll, release the "pin user
    // msg to top" lock so the rAF re-anchor loop stops fighting them. This
    // lets the user freely scroll up/down during streaming to re-read
    // earlier messages or jump to the bottom to follow the response.
    pinUserToTopRef.current = false;
  }, []);
  const [agentChatAtBottom, setAgentChatAtBottom] = useState(true);
  const scrollAgentChatToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const el = agentMessagesScrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
    },
    [],
  );
  // Scroll the scroll container so the most recent user message sits near the
  // top, with a little breathing room. Called right after a user message is
  // appended to the chat.
  const scrollLatestUserMsgToTop = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const container = agentMessagesScrollRef.current;
      const target = latestUserMsgRef.current;
      if (!container || !target) return;
      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const offset = tRect.top - cRect.top + container.scrollTop - 16;
      container.scrollTo({ top: Math.max(0, offset), behavior });
    },
    [],
  );
  // When opening the chat, snap instantly to the bottom so the latest
  // message is visible without any visible scroll animation.
  useEffect(() => {
    if (!showAgentChat) return;
    const t = setTimeout(() => {
      scrollAgentChatToBottom("auto");
      setAgentChatAtBottom(true);
    }, 50);
    return () => clearTimeout(t);
  }, [showAgentChat, scrollAgentChatToBottom]);
  // ── Per-message hover actions (rewind & copy) ──────────────────────────
  // Rewind: confirms with the user, then truncates the conversation back to
  // (but excluding) the picked user message and refills the input with its
  // text. Also deletes the matching server-side rows.
  // Modal that lets the user choose an export format (MP4 standard /
  // FCPXML pro). MP4 is grayed-out for now since the render pipeline isn't
  // wired up yet.
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Support modal — shows a QR for desktop users and a "Open WhatsApp Web"
  // shortcut. Same pattern used by Paywall + SubscriptionLimitModal so the
  // contact UX stays consistent across the app.
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  // FCPXML export progress + error state. `exporting` is the (loaded, total)
  // pair of asset downloads so the modal can render a determinate bar; null
  // means we're not currently exporting.
  const [exporting, setExporting] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  // ── MP4 (Remotion) render export state ───────────────────────────────────
  // Discriminated union covering the 4 phases of the MP4 export modal:
  // idle (pick a format), rendering (progress + cancel), done (download),
  // error (retry). The `handle` lives both in state (for rendering UI) and
  // a ref (so the modal-close cleanup effect can cancel without reading
  // a stale closure of `exportState`).
  type ExportState =
    | { phase: "idle" }
    | {
        phase: "rendering";
        progress: number;
        label: string;
        handle: RenderHandle;
      }
    | { phase: "done"; outputUrl: string }
    | { phase: "error"; message: string };
  const [exportState, setExportState] = useState<ExportState>({
    phase: "idle",
  });
  const exportHandleRef = useRef<RenderHandle | null>(null);
  // Contador que rota la frase mostrada durante el render. El backend se
  // consulta cada 2.5s y puede devolver el mismo progreso varias veces
  // seguidas; sin este tick el texto quedaría congelado y parecería colgado.
  const [renderMessageTick, setRenderMessageTick] = useState(0);
  useEffect(() => {
    if (exportState.phase !== "rendering") {
      setRenderMessageTick(0);
      return;
    }
    const id = setInterval(
      () => setRenderMessageTick((t) => t + 1),
      RENDER_MESSAGE_ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [exportState.phase]);
  // Cancel any in-flight MP4 render when the modal closes — avoids ghost
  // progress callbacks firing after the dialog is gone — and reset the
  // phase so reopening the modal starts on the format picker.
  useEffect(() => {
    if (exportMenuOpen) return;
    const h = exportHandleRef.current;
    if (h) {
      h.cancel();
      exportHandleRef.current = null;
    }
    if (exportState.phase !== "idle") setExportState({ phase: "idle" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportMenuOpen]);
  // ── Credit balance + insufficient-credits modal ──────────────────────────
  // `companyCredits` mirrors `company_profiles.imagenes_restantes` (1 credit
  // = 1 image) and is refetched on mount + on the global
  // `atlas:credits-updated` event, so the editor stays in sync with other
  // pages that decrement the balance.
  const [companyCredits, setCompanyCredits] = useState<number | null>(null);
  const [companyPlan, setCompanyPlan] = useState<string | null>(null);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] =
    useState(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<
    "free-trial-ended" | "plan-limit-reached"
  >("plan-limit-reached");
  // ID del clip pendiente de confirmar traducción. Cuando el usuario
  // clickea "Traducir EN" del toolbar, en vez de disparar el flow directo,
  // seteamos este estado y mostramos un modal con el costo. Si confirma,
  // arrancamos translateClipToEnglish; si cancela, cerramos el modal.
  const [translateConfirmClipId, setTranslateConfirmClipId] = useState<
    string | null
  >(null);
  // "Cambiar voz" (barra IA del clip): clip target + visibilidad del picker.
  const [changeVoiceClipId, setChangeVoiceClipId] = useState<string | null>(
    null,
  );
  const [showClipVoicePicker, setShowClipVoicePicker] = useState(false);
  // Modal para elegir una imagen ya generada / subida en este proyecto y
  // usarla en alguno de los slots del panel AI Scene (frame inicial, frame
  // final, o como imagen de referencia). El kind indica a qué slot
  // aplicar la imagen seleccionada. null = modal cerrado.
  // El panel Archivos entra en modo selección para un slot de escena IA
  // (frame inicial / final / referencia). null = entrada normal a Archivos.
  const [filesPickTarget, setFilesPickTarget] = useState<
    { kind: "first" | "last" | "ref" } | null
  >(null);
  // Token para descartar importaciones en vuelo que ya no corresponden.
  // Elegir un producto / escena / historial / generada en modo selección sube
  // la imagen antes de poder asignarla, y eso tarda: si mientras tanto el
  // usuario cancela o toca OTRA imagen, la subida vieja termina igual y
  // asignaba el slot sin preguntar (pisando la elección nueva, o llenando un
  // slot de una selección que ya se había cancelado).
  // Se incrementa en dos lados: en cada intento de elección (así la última
  // gana) y ante cualquier cambio de `filesPickTarget`, que cubre el cancelar
  // y el cambio de slot sin tener que tocar cada call site.
  const filesPickTokenRef = useRef(0);
  useEffect(() => {
    filesPickTokenRef.current += 1;
  }, [filesPickTarget]);
  // Abre Archivos en modo selección para un slot. Reemplaza la vieja grilla
  // inline: ahora se elige desde la vista completa de Archivos (solo imágenes,
  // sin "Subir archivo" ni "Generar con IA").
  const openLibraryPicker = useCallback(
    (kind: "first" | "last" | "ref") => {
      setFilesPickTarget({ kind });
      setActivePanel("files");
      setFilesTab("media");
    },
    [],
  );
  // ID del clip cuyo registro de asset se está reintentando ahora mismo.
  // Cuando el usuario clickea el badge ⚠ ámbar de "no se guardó en
  // biblioteca", llamamos `registerExistingAsset` de nuevo y mostramos un
  // spinner en el badge mientras corre. null = no hay reintento en curso.
  const [retryingAssetForClipId, setRetryingAssetForClipId] = useState<
    string | null
  >(null);
  // ID del clip cuya signed URL se está reintentando cargar. El badge
  // rojo de "loadError" en el clip dispara esto al hacer click.
  const [retryingLoadForClipId, setRetryingLoadForClipId] = useState<
    string | null
  >(null);

  // Fetch the current company's credit balance and plan once on mount, and
  // refetch whenever any other surface dispatches `atlas:credits-updated`
  // (image generation, AI video success, etc) so the badges and guards
  // reflect reality without a full reload.
  useEffect(() => {
    let cancelled = false;
    const refetch = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!profile?.id_company) return;
        const { data } = await supabase
          .from("company_profiles")
          .select("plan, imagenes_restantes")
          .eq("id_company", profile.id_company)
          .single();
        if (cancelled || !data) return;
        const remaining = (data as { imagenes_restantes?: number | null })
          .imagenes_restantes;
        setCompanyCredits(typeof remaining === "number" ? remaining : null);
        setCompanyPlan((data as { plan?: string | null }).plan ?? null);
      } catch (err) {
        // Silent — without a balance we let actions through (server is the
        // ultimate authority on enforcement); we just can't pre-block.
        console.warn("[VideoEditor] credit balance fetch failed", err);
      }
    };
    void refetch();
    const handler = () => {
      void refetch();
    };
    window.addEventListener("atlas:credits-updated", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("atlas:credits-updated", handler);
    };
  }, []);

  // Guard: returns true when the user can afford `cost`, otherwise pops the
  // shared SubscriptionLimitModal (same UX as the rest of the app) and
  // returns false. Caller is expected to early-return on `false`.
  // When the balance is unknown (fetch failed) we let the action through —
  // the server still enforces and the user just won't see the pre-check.
  const assertCredits = useCallback(
    (cost: number): boolean => {
      if (companyCredits == null) return true;
      if (companyCredits >= cost) return true;
      setSubscriptionLimitType(
        companyPlan === "gratis" ? "free-trial-ended" : "plan-limit-reached",
      );
      setShowSubscriptionLimitModal(true);
      return false;
    },
    [companyCredits, companyPlan],
  );

  // Switch the agent dropdown into its "Consumo" view and refresh the
  // running total. The Edge Function aggregates token / cost / credit
  // usage onto the project row directly, so we just read one column off
  // `video_projects` instead of summing per-message rows.
  const openConsumoView = useCallback(async () => {
    setAgentMenuView("consumo");
    setConsumoError(null);
    if (!projectId) {
      setConsumoCredits(0);
      return;
    }
    setConsumoLoading(true);
    try {
      const { data, error } = await supabase
        .from("video_projects")
        .select("total_credits_used")
        .eq("id", projectId)
        .single();
      if (error) throw error;
      const total = (data as { total_credits_used: number | null } | null)
        ?.total_credits_used;
      setConsumoCredits(typeof total === "number" ? total : 0);
    } catch (err) {
      console.error("[VideoEditor] consumo fetch failed", err);
      setConsumoError(
        err instanceof Error ? err.message : "No pudimos calcular el consumo.",
      );
    } finally {
      setConsumoLoading(false);
    }
  }, [projectId]);

  const [chatRewindConfirm, setChatRewindConfirm] = useState<{
    messageIndex: number;
    text: string;
    deleteCount: number;
  } | null>(null);
  // Index of the user message that just had its text copied — drives the
  // brief "Copiado" tick state on the copy button.
  const [chatCopiedIndex, setChatCopiedIndex] = useState<number | null>(null);
  // Indices of thinking messages that the user has expanded. Storing as a
  // set keyed by message index is fine because we only append new messages
  // (indices of existing rows don't shift).
  const [expandedThinkingIndices, setExpandedThinkingIndices] = useState<
    Set<number>
  >(new Set());
  const toggleThinkingExpanded = useCallback((index: number) => {
    setExpandedThinkingIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);
  const requestChatRewind = useCallback((messageIndex: number) => {
    setAgentMessages((prev) => {
      const target = prev[messageIndex];
      if (!target || target.role !== "user") return prev;
      const deleteCount = prev.length - messageIndex;
      setChatRewindConfirm({
        messageIndex,
        text: target.text || "",
        deleteCount,
      });
      return prev;
    });
  }, []);
  const confirmChatRewind = useCallback(async () => {
    if (!chatRewindConfirm) return;
    const { messageIndex, text } = chatRewindConfirm;
    // Snapshot the cutoff timestamp before we mutate state.
    const cutoffTs = agentMessages[messageIndex]?.createdAt;
    setAgentMessages((prev) => prev.slice(0, messageIndex));
    // El historial estructurado ya no aplica desde este punto: lo reseteamos
    // para que el próximo turno se reconstruya desde el texto recortado.
    conversationRef.current = [];
    setAgentInput(text);
    setChatRewindConfirm(null);
    // Refocus + autogrow the input so the user can edit and resend.
    setTimeout(() => {
      const el = agentInputRef.current;
      if (!el) return;
      autoGrowTextarea(el);
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }, 0);
    // Delete server-side rows from the cutoff onward. We only know which
    // rows to delete if we have a cutoff timestamp; if not, fall back to a
    // no-op on the server (the local state is already truncated).
    if (cutoffTs && projectId) {
      const { error } = await supabase
        .from("agent_messages")
        .delete()
        .eq("project_id", projectId)
        .gte("created_at", cutoffTs);
      if (error) console.error("rewind delete agent_messages failed", error);
    }
  }, [chatRewindConfirm, agentMessages, projectId]);
  const copyChatMessage = useCallback(
    async (messageIndex: number, text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        console.error("copy message failed", err);
        return;
      }
      setChatCopiedIndex(messageIndex);
      window.setTimeout(() => {
        setChatCopiedIndex((prev) => (prev === messageIndex ? null : prev));
      }, 1500);
    },
    [],
  );

  // Recompute the "at bottom" flag whenever the messages change. The onScroll
  // handler alone misses cases where content grows/shrinks without the user
  // scrolling (e.g. short conversations where the container doesn't even
  // overflow). In that case scrollHeight === clientHeight and the distance is
  // 0 → button hidden, as expected.
  useEffect(() => {
    const el = agentMessagesScrollRef.current;
    if (!el) return;
    // Defer to next frame so the DOM has laid out the newly added content.
    const raf = requestAnimationFrame(() => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      setAgentChatAtBottom(distance < 80);
    });
    return () => cancelAnimationFrame(raf);
  }, [agentMessages, showAgentChat, agentStreaming]);
  // While the chat is "pinned" to the latest user message at the top, keep
  // re-anchoring on every animation frame. Streaming text deltas, tool
  // messages, panel layout shifts, video element loads, focus changes and
  // other side-effects can all nudge the scroll position; this loop is the
  // simplest way to robustly defeat all of them. The loop stops as soon as
  // the pin is cleared (turn finished, or user manually scrolled to the
  // bottom — see onScroll handler).
  useEffect(() => {
    if (!agentStreaming) return;
    let rafId = 0;
    const tick = () => {
      if (!pinUserToTopRef.current) return; // pin cleared → stop snapping back
      const container = agentMessagesScrollRef.current;
      const target = latestUserMsgRef.current;
      if (container && target) {
        const cRect = container.getBoundingClientRect();
        const tRect = target.getBoundingClientRect();
        const offset = tRect.top - cRect.top + container.scrollTop - 16;
        const clamped = Math.max(0, offset);
        // Only write if it actually drifted, to avoid unnecessary work and
        // potential interference with the scroll event loop.
        if (Math.abs(container.scrollTop - clamped) > 1) {
          container.scrollTop = clamped;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [agentStreaming]);
  // Refs for textareas that grow with their content up to a max height.
  const agentInputRef = useRef<HTMLTextAreaElement>(null);
  const imageGenPromptRef = useRef<HTMLTextAreaElement>(null);
  // Auto-grow helper: resets to auto, then clamps to scrollHeight up to `max`.
  const autoGrowTextarea = (el: HTMLTextAreaElement | null, max = 200) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  };
  useEffect(() => {
    autoGrowTextarea(agentInputRef.current);
  }, [agentInput]);
  useEffect(() => {
    autoGrowTextarea(imageGenPromptRef.current);
  }, [imageGenPrompt]);
  // Small looping animation shown next to the agent title. Plays while the
  // agent is generating text; paused at the first frame otherwise.
  const iaLoadingVideoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const v = iaLoadingVideoRef.current;
    if (!v) return;
    if (agentStreaming) {
      v.play().catch(() => {});
    } else {
      v.pause();
      try {
        v.currentTime = 0;
      } catch {}
    }
  }, [agentStreaming]);
  // sendAgentMessage is defined further down, after the editor helpers
  // (addAssetToTimeline, addTextClip) that the tool executor uses.

  // Load chat history from DB when project is resolved.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("agent_messages")
          .select("role, content, created_at")
          .eq("project_id", projectId)
          .order("created_at", { ascending: true });
        if (error) {
          console.error("load agent_messages failed", error);
          return;
        }
        if (cancelled || !data || data.length === 0) return;

        const mapped: AgentMessage[] = [];
        const assetIdsToResolve = new Set<string>();
        for (const row of data) {
          // Only restore roles we actually render. Anthropic-only tool_use /
          // tool_result rows (if any get persisted in the future) are ignored.
          if (
            row.role !== "user" &&
            row.role !== "assistant" &&
            row.role !== "tool" &&
            row.role !== "thinking"
          )
            continue;
          const c: any = row.content;
          if (row.role === "tool") {
            const text =
              typeof c === "string"
                ? c
                : c && typeof c === "object" && typeof c.text === "string"
                  ? c.text
                  : "";
            const iconKey =
              c && typeof c === "object" && typeof c.iconKey === "string"
                ? c.iconKey
                : undefined;
            const Icon = iconKey ? TOOL_MSG_ICON_BY_KEY[iconKey] : undefined;
            mapped.push({
              role: "tool",
              text,
              icon: Icon,
              createdAt: row.created_at,
            });
            continue;
          }
          if (row.role === "thinking") {
            if (c && typeof c === "object") {
              if (c.type === "redacted_thinking") {
                mapped.push({
                  role: "thinking",
                  text: "",
                  thinkingRedactedData:
                    typeof c.data === "string" ? c.data : "",
                  createdAt: row.created_at,
                });
              } else {
                mapped.push({
                  role: "thinking",
                  text: typeof c.text === "string" ? c.text : "",
                  thinkingSignature:
                    typeof c.signature === "string" ? c.signature : "",
                  createdAt: row.created_at,
                });
              }
            }
            continue;
          }
          let text = "";
          let attachmentAssetIds: string[] = [];
          if (typeof c === "string") text = c;
          else if (c && typeof c === "object") {
            if (typeof c.text === "string") text = c.text;
            else text = JSON.stringify(c);
            if (Array.isArray(c.attachmentAssetIds)) {
              attachmentAssetIds = c.attachmentAssetIds.filter(
                (x: any) => typeof x === "string",
              );
              attachmentAssetIds.forEach((id) => assetIdsToResolve.add(id));
            }
          }
          const msg: AgentMessage = {
            role: row.role as "user" | "assistant",
            text,
            createdAt: row.created_at,
          };
          if (attachmentAssetIds.length) {
            msg.attachments = attachmentAssetIds.map((assetId) => ({
              assetId,
              url: "",
            }));
          }
          mapped.push(msg);
        }
        // Resolve signed URLs for every referenced asset (single batch).
        if (assetIdsToResolve.size > 0) {
          const assets = await fetchAssetsByIds([...assetIdsToResolve]);
          const urlByAssetId = new Map<string, string>();
          await Promise.all(
            assets.map(async (a) => {
              const u = await getAssetSignedUrl(a.storage_path).catch(
                () => null,
              );
              if (u) urlByAssetId.set(a.id, u);
            }),
          );
          for (const m of mapped) {
            if (m.attachments) {
              m.attachments = m.attachments
                .map((a) => ({
                  ...a,
                  url: (a.assetId && urlByAssetId.get(a.assetId)) || a.url,
                }))
                .filter((a) => a.url);
            }
          }
        }
        if (mapped.length > 0) setAgentMessages(mapped);
        // Historial restaurado desde DB = solo texto/thinking (no se persisten
        // tool_use/tool_result). El próximo turno se reconstruye desde el texto.
        conversationRef.current = [];
      } catch (e) {
        console.error("load agent_messages exception", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Restore pending ask on mount (once projectId is known). Declared BEFORE
  // the persist effect below so on the initial render we read the saved key
  // before persist gets a chance to overwrite it. We also flip
  // askHydratedRef.current = true so the persist effect can start working.
  useEffect(() => {
    if (!projectId) return;
    askHydratedRef.current = false;
    try {
      const raw = localStorage.getItem(`atlas-agent-ask-${projectId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        const qs = parsed?.activeAsk?.questions;
        if (Array.isArray(qs) && qs.length > 0) {
          setActiveAsk({
            questions: qs,
            currentIndex: Math.max(
              0,
              Math.min(
                qs.length - 1,
                Number(parsed.activeAsk.currentIndex) || 0,
              ),
            ),
          });
          if (parsed.askFormState && typeof parsed.askFormState === "object") {
            setAskFormState(parsed.askFormState);
          }
          if (
            parsed.askCustomState &&
            typeof parsed.askCustomState === "object"
          ) {
            setAskCustomState(parsed.askCustomState);
          }
        }
      }
    } catch {}
    askHydratedRef.current = true;
  }, [projectId]);

  // ── Persist pending ask session to localStorage so reloading the page while
  // a question is open restores the UI (the agent's streaming resolver is
  // lost on reload — on submit we fall back to sending answers as a new user
  // message, see finishSession below).
  useEffect(() => {
    if (!projectId) return;
    // Skip until the restore effect has run for this projectId, otherwise
    // the first call (activeAsk=null) would clobber the stored value.
    if (!askHydratedRef.current) return;
    const key = `atlas-agent-ask-${projectId}`;
    if (activeAsk) {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ activeAsk, askFormState, askCustomState }),
        );
      } catch {}
    } else {
      try {
        localStorage.removeItem(key);
      } catch {}
    }
  }, [projectId, activeAsk, askFormState, askCustomState]);

  // Clips
  const [clips, setClips] = useState<TimelineClip[]>([]);
  // Onboarding contextual — Transiciones: la 1ª vez que aparece el botón ＋
  // entre dos clips de video pegados, lo resaltamos (es chico y difícil de
  // descubrir). El existence-check dentro de startContextualTour evita que
  // arranque antes de que el botón esté en el DOM. (Va DESPUÉS de declarar
  // `clips` para no caer en TDZ al referenciarlo en las deps.)
  useEffect(() => {
    if (projectLoading) return;
    if (isOnboardingSeen("ctx_transitions")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () =>
          markOnboardingSeen("ctx_transitions", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="clip-transition"]',
            popover: {
              title: "Transiciones",
              description:
                "Cuando dos clips quedan pegados, aparece este botón entre ellos. Tocalo para elegir una transición (fundido, deslizar, glitch…).",
              side: "top",
              align: "center",
            },
          },
        ],
      });
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clips, projectLoading]);
  // Onboarding contextual — Modo Diálogo: la 1ª vez que el usuario abre el panel
  // "Nueva escena con IA" en Modo Diálogo (personas), explicamos sus 3 partes:
  // Frame inicial (la persona/modelo), Diálogo (lo que dice) y Voz (cómo suena).
  // Las anclas dialog-text/dialog-voice existen tanto en sub-modo static como
  // motion; solo una está montada, así que el selector encuentra la correcta.
  useEffect(() => {
    if (activePanel !== "ai-scene" || sceneMode !== "personas") return;
    if (isOnboardingSeen("ctx_dialog")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () =>
          markOnboardingSeen("ctx_dialog", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="dialog-frame"]',
            popover: {
              title: "Frame inicial",
              description:
                "Acá va el personaje que va a hablar en la escena. Subí una imagen o elegí un personaje de tu biblioteca.",
              side: "left",
              align: "start",
            },
          },
          {
            element: '[data-tour="dialog-text"]',
            popover: {
              title: "Diálogo",
              description:
                "Escribí lo que la persona va a decir. También podés grabar tu propia voz en lugar de escribir.",
              side: "left",
              align: "center",
            },
          },
          {
            element: '[data-tour="dialog-voice"]',
            popover: {
              title: "Voz",
              description:
                "Elegí la voz con la que se sincronizan los labios. Podés probar varias hasta encontrar la que mejor queda.",
              side: "left",
              align: "center",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel, sceneMode]);
  // Onboarding contextual — Generar video (Modo General): la 1ª vez que se abre
  // el panel "Nueva escena con IA" en Modo General, explicamos el selector de
  // modo (General vs Diálogo), las imágenes de referencia y el prompt.
  useEffect(() => {
    if (activePanel !== "ai-scene" || sceneMode !== "general") return;
    if (isOnboardingSeen("ctx_video")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () => markOnboardingSeen("ctx_video", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="scene-mode"]',
            popover: {
              title: "Modo de la escena",
              description:
                "Modo General para cualquier escena creativa; Modo Diálogo para personas hablando con voz sincronizada. Empezá eligiendo el que necesites.",
              side: "bottom",
              align: "start",
            },
          },
          {
            element: '[data-tour="scene-reference"]',
            popover: {
              title: "Imágenes de referencia",
              description:
                "Opcional: subí una imagen para guiar el estilo de la escena, o definí frame de inicio y fin. Si no, la IA la crea solo con tu texto.",
              side: "left",
              align: "center",
            },
          },
          {
            element: '[data-tour="scene-prompt"]',
            popover: {
              title: "Prompt de la escena",
              description:
                "Describí qué querés ver. Cuanto más claro seas (qué pasa, dónde, con qué estilo), mejor sale el video.",
              side: "left",
              align: "center",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel, sceneMode]);
  // Onboarding contextual — Generar audio: la 1ª vez que se abre el panel
  // "Nuevo audio con IA", explicamos el tipo (Voz en off vs Música) y el texto.
  useEffect(() => {
    if (activePanel !== "ai-audio") return;
    if (isOnboardingSeen("ctx_audio")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () => markOnboardingSeen("ctx_audio", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="audio-type"]',
            popover: {
              title: "Tipo de audio",
              description:
                "Elegí “Voz en off” para una narración hablada, o “Música” para generar una pista de fondo.",
              side: "bottom",
              align: "start",
            },
          },
          {
            element: '[data-tour="audio-text"]',
            popover: {
              title: "¿Qué querés generar?",
              description:
                "En Voz en off, escribí (o grabá) lo que se va a decir. En Música, describí el estilo y el clima que buscás.",
              side: "left",
              align: "center",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel]);
  // Onboarding contextual — Archivos: la 1ª vez que se abre el panel Archivos
  // (no en modo selección de un frame), señalamos las pills menos obvias:
  // Productos (catálogo de la marca), Escenas (fondos) e Historial (lo generado).
  useEffect(() => {
    if (activePanel !== "files" || filesPickTarget) return;
    if (isOnboardingSeen("ctx_files")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () => markOnboardingSeen("ctx_files", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="files-pills"]',
            popover: {
              title: "Tus archivos",
              description:
                "Además de Multimedia y Audio (tus subidas), tenés Productos (el catálogo de tu marca), Escenas (fondos listos) e Historial (todo lo que generaste con IA). Usalos sin volver a subir nada.",
              side: "bottom",
              align: "start",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel, filesPickTarget]);
  // Ref siempre sincronizado con el último `clips`. Los executors del agente
  // viven en un closure de larga vida (durante toda la corrida del agente) y
  // capturan un `clips` viejo; leer `clipsRef.current` da el estado FRESCO
  // (ej. autocut necesita ver el transcript que generate_transcript acaba de
  // agregar con setClips, aunque el closure todavía no se haya recreado).
  const clipsRef = useRef<TimelineClip[]>([]);
  clipsRef.current = clips;
  const [snapTarget, setSnapTarget] = useState<{
    clipId: string;
    startTime: number;
  } | null>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);

  // ── Undo / redo history of the `clips` array ─────────────────────────────
  // Implementation notes:
  //   * Snapshots are coalesced via a ~250ms debounce, so a continuous
  //     drag-to-resize ends up as ONE history entry instead of dozens.
  //   * `isApplyingHistoryRef` suppresses snapshotting while an undo or
  //     redo is in flight — without it, restoring an old state would
  //     immediately push the just-restored value back onto the undo
  //     stack, breaking redo.
  //   * `historyInitializedRef` skips the very first non-empty load
  //     (clips populated from Supabase on mount). That isn't a user
  //     action, so we don't want it on the undo stack.
  //   * Stacks are length-capped (`HISTORY_LIMIT`) so memory stays bounded
  //     across long sessions.
  const HISTORY_LIMIT = 50;
  const SNAPSHOT_DEBOUNCE_MS = 250;
  const [undoStack, setUndoStack] = useState<TimelineClip[][]>([]);
  const [redoStack, setRedoStack] = useState<TimelineClip[][]>([]);
  const isApplyingHistoryRef = useRef(false);
  const historyInitializedRef = useRef(false);
  const lastSnapshotRef = useRef<TimelineClip[]>([]);
  const snapshotTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Skip while replaying history.
    if (isApplyingHistoryRef.current) {
      isApplyingHistoryRef.current = false;
      lastSnapshotRef.current = clips;
      return;
    }
    // Treat the first non-empty load as the initial baseline, not as
    // a user-driven change worth pushing.
    if (!historyInitializedRef.current) {
      if (clips.length > 0) {
        historyInitializedRef.current = true;
        lastSnapshotRef.current = clips;
      }
      return;
    }
    // Coalesce rapid sequential changes into one history entry.
    if (snapshotTimerRef.current) window.clearTimeout(snapshotTimerRef.current);
    const previous = lastSnapshotRef.current;
    snapshotTimerRef.current = window.setTimeout(() => {
      // Only push if something actually changed at the snapshot level.
      if (previous === lastSnapshotRef.current) {
        setUndoStack((prev) => {
          const next = [...prev, previous];
          if (next.length > HISTORY_LIMIT) next.shift();
          return next;
        });
        setRedoStack([]);
        lastSnapshotRef.current = clips;
      }
    }, SNAPSHOT_DEBOUNCE_MS);
    return () => {
      if (snapshotTimerRef.current) {
        window.clearTimeout(snapshotTimerRef.current);
      }
    };
  }, [clips]);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const target = prev[prev.length - 1];
      // Push the current state onto the redo stack so we can come back.
      setRedoStack((r) => {
        const next = [...r, lastSnapshotRef.current];
        if (next.length > HISTORY_LIMIT) next.shift();
        return next;
      });
      isApplyingHistoryRef.current = true;
      setClips(target);
      return prev.slice(0, -1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const target = prev[prev.length - 1];
      setUndoStack((u) => {
        const next = [...u, lastSnapshotRef.current];
        if (next.length > HISTORY_LIMIT) next.shift();
        return next;
      });
      isApplyingHistoryRef.current = true;
      setClips(target);
      return prev.slice(0, -1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  // Keyboard shortcuts. Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z = redo
  // (also accept Cmd/Ctrl+Y on Windows for redo). Suppressed while the
  // user is typing in an input/textarea/contenteditable to avoid eating
  // their native browser undo for text fields.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable)
        return;
      const key = e.key.toLowerCase();
      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  // ── Project media library (videos + audios) — populated when the user opens the Video/Audio subpanels ──
  const [libraryAssets, setLibraryAssets] = useState<ProjectAsset[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  // Measure media clip rects while the frame-from-video picker is active so we can render click hitboxes above the blur overlay
  useEffect(() => {
    if (!frameFromVideoTarget) {
      setPickerClipRects([]);
      return;
    }
    const measure = () => {
      const nodes = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-clip-id][data-is-media="1"]',
        ),
      );
      const rects = nodes
        .map((el) => {
          const r = el.getBoundingClientRect();
          return {
            clipId: el.getAttribute("data-clip-id") || "",
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
          };
        })
        .filter((x) => x.clipId && x.width > 0 && x.height > 0);
      setPickerClipRects(rects);
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [frameFromVideoTarget, clips]);

  // ── Load project from DB ───────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) {
      navigate("/editor-video", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Fetch companyId for storage paths
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const cId = (profile as any)?.id_company as string | undefined;
        if (cId && !cancelled) setCompanyId(cId);

        const { data, error } = await supabase
          .from("video_projects")
          .select("name, state, duration, brand_profile_id")
          .eq("id", projectId)
          .single();
        if (error || !data) {
          if (!cancelled) navigate("/editor-video", { replace: true });
          return;
        }
        if (cancelled) return;
        setProjectName(data.name || "Proyecto sin título");
        setDuration(data.duration || 30);
        // Load the brand profile linked to this project (used by the "Memoria"
        // panel and the update_brand_context agent tool).
        const brandId = (data as any).brand_profile_id as string | null;
        if (brandId) {
          supabase
            .from("brand_profiles")
            .select("id, name, context, colors, fonts, logo_url")
            .eq("id", brandId)
            .maybeSingle()
            .then(({ data: bp }) => {
              if (cancelled || !bp) return;
              setBrandProfile({
                id: bp.id,
                name: bp.name || "Marca",
                context: bp.context || "",
                colors: normalizeBrandColors(bp.colors),
                logoUrl: (bp as any).logo_url ?? null,
                // Coerce legacy shape (array of family names) to an empty
                // role map; new writes from the UI / agent always store an
                // object, so old array data simply gets superseded.
                fonts:
                  (bp as any).fonts &&
                  !Array.isArray((bp as any).fonts) &&
                  typeof (bp as any).fonts === "object"
                    ? ((bp as any).fonts as BrandFontRoles)
                    : {},
              });
            });
        }
        const s = data.state as any;
        if (s && typeof s === "object") {
          // Resolve storage paths to signed URLs for clips with media
          let loadedClips: TimelineClip[] = Array.isArray(s.clips)
            ? s.clips
            : [];
          if (loadedClips.length > 0) {
            const resolved = await Promise.all(
              loadedClips.map(async (clip: TimelineClip) => {
                if (clip.storagePath && !clip.src) {
                  try {
                    const { data: urlData } = await supabase.storage
                      .from("video-project-assets")
                      .createSignedUrl(clip.storagePath, 3600);
                    if (urlData?.signedUrl) {
                      return { ...clip, src: urlData.signedUrl };
                    }
                    // El SDK no tiró pero tampoco devolvió URL → archivo
                    // probablemente eliminado del bucket. Marcamos el clip.
                    return {
                      ...clip,
                      loadError:
                        "El archivo de este clip no se encontró en el servidor.",
                    };
                  } catch (e) {
                    const msg =
                      e instanceof Error ? e.message : "Error desconocido";
                    return {
                      ...clip,
                      loadError: `No pudimos cargar el archivo: ${msg}`,
                    };
                  }
                }
                return clip;
              }),
            );
            loadedClips = resolved;
          }
          if (!cancelled) setClips(loadedClips);

          // Backfill: register AI-generated ready clips as project assets if missing.
          // This makes them appear in the Video library and persist across clip deletion.
          if (cId && projectId) {
            const backfillTargets = loadedClips.filter(
              (c) =>
                c.type === "ai-generated" &&
                c.aiStatus === "ready" &&
                c.storagePath &&
                !c.assetId,
            );
            if (backfillTargets.length > 0) {
              (async () => {
                const pairs = await Promise.all(
                  backfillTargets.map(async (c) => {
                    try {
                      const asset = await registerExistingAsset({
                        projectId: projectId!,
                        companyId: cId,
                        userId: user.id,
                        storagePath: c.storagePath!,
                        type: "video",
                        name: c.aiPrompt?.slice(0, 80) || c.name || "Video IA",
                        mimeType: "video/mp4",
                        durationSeconds: c.duration,
                      });
                      return asset ? ([c.id, asset.id] as const) : null;
                    } catch {
                      return null;
                    }
                  }),
                );
                if (cancelled) return;
                const byClipId = new Map(
                  pairs.filter((p): p is readonly [string, string] => !!p),
                );
                if (byClipId.size > 0) {
                  setClips((prev) =>
                    prev.map((c) =>
                      byClipId.has(c.id)
                        ? { ...c, assetId: byClipId.get(c.id) }
                        : c,
                    ),
                  );
                }
              })();
            }
          }

          if (typeof s.zoom === "number") setZoom(s.zoom);
          if (typeof s.scenePrompt === "string") setScenePrompt(s.scenePrompt);
          if (s.sceneRefMode) setSceneRefMode(s.sceneRefMode);
          // Redondeamos al restaurar: los proyectos guardados antes de este
          // fix tienen el decimal del clip persistido en `sceneDuration`.
          if (s.sceneDuration)
            setSceneDuration(Math.max(1, Math.round(s.sceneDuration)));
          if (s.aspectRatio) setAspectRatio(s.aspectRatio);
          if (Array.isArray(s.imageGenHistorial))
            setImageGenHistorial(s.imageGenHistorial);
          if (typeof s.imageGenCompanyId === "string")
            setImageGenCompanyId(s.imageGenCompanyId);
          if (Array.isArray(s.imageGenGalleryAssetIds))
            setImageGenGalleryAssetIds(s.imageGenGalleryAssetIds);
          if (
            s.productSceneAssetMap &&
            typeof s.productSceneAssetMap === "object"
          )
            setProductSceneAssetMap(s.productSceneAssetMap);
          if (s.modelAssetMap && typeof s.modelAssetMap === "object")
            setModelAssetMap(s.modelAssetMap);
          if (
            Array.isArray(s.imageGenResults) &&
            s.imageGenResults.length > 0 &&
            typeof s.imageGenCompanyId === "string"
          ) {
            const savedResults: GenImageResult[] = s.imageGenResults;
            const compId = s.imageGenCompanyId;
            // Set placeholders first (clear loading since we have fileName)
            setImageGenResults(
              savedResults.map((r) => ({
                id: r.id,
                fileName: r.fileName,
                loading: !r.fileName,
                createdAt: r.createdAt,
              })),
            );
            // Resolve signed URLs in background
            (async () => {
              // Proyectos viejos no guardaron created_at: lo traemos en un solo
              // batch para poder ordenar Multimedia por fecha real.
              const missingTs = savedResults
                .filter((r) => !r.createdAt)
                .map((r) => r.id);
              const tsById = new Map<string, string>();
              if (missingTs.length > 0) {
                try {
                  // Fetch en lotes para no exceder el largo de URL del gateway
                  // REST con muchos resultados (HTTP 400). Ver utils/supabaseChunkedIn.
                  const rows = await fetchInChunks<any>(missingTs, (chunk) =>
                    supabase
                      .from("imagenes_generadas")
                      .select("id, created_at")
                      .in("id", chunk),
                  );
                  for (const row of ((rows || []) as any[]))
                    if (row?.created_at)
                      tsById.set(String(row.id), row.created_at);
                } catch {}
              }
              const resolved = await Promise.all(
                savedResults.map(async (r) => {
                  const createdAt = r.createdAt || tsById.get(r.id);
                  if (!r.fileName) return { ...r, createdAt };
                  try {
                    const { data: signed } = await supabase.storage
                      .from("generadas")
                      .createSignedUrl(`${compId}/full/${r.fileName}`, 86400);
                    return {
                      ...r,
                      createdAt,
                      signedUrl: signed?.signedUrl,
                      loading: false,
                    };
                  } catch {
                    return { ...r, createdAt, loading: false };
                  }
                }),
              );
              if (!cancelled) setImageGenResults(resolved);
            })();
          }
        }
        isLoadedRef.current = true;
      } catch {
        if (!cancelled) navigate("/editor-video", { replace: true });
      } finally {
        if (!cancelled) setProjectLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, navigate]);

  // Hash del clip "de portada" (top-most en t=0) en el último thumbnail subido.
  // Si cambia entre saves, regeneramos el thumbnail en background.
  const lastThumbnailHashRef = useRef<string>("");
  const thumbnailRegenInFlightRef = useRef<boolean>(false);

  // ── Auto-save to DB (debounced 3s) ─────────────────────────────────────────
  const saveProject = useCallback(async () => {
    if (!projectId || !isLoadedRef.current) return;
    // Strip ephemeral blob URLs and reset 'generating' to 'draft' before saving
    const persistableClips = clips.map(({ src, ...rest }) => ({
      ...rest,
      aiStatus:
        rest.aiStatus === "generating"
          ? ("draft" as AiClipStatus)
          : rest.aiStatus,
      // un borrado "processing" no debe quedar pegado si se recarga a mitad
      eraseStatus: rest.eraseStatus === "processing" ? undefined : rest.eraseStatus,
      eraseJobId: rest.eraseStatus === "processing" ? undefined : rest.eraseJobId,
    }));
    const state = {
      clips: persistableClips,
      zoom,
      scenePrompt,
      sceneRefMode,
      sceneDuration,
      aspectRatio,
      imageGenResults: imageGenResults
        .filter((r) => r.fileName) // skip failed/still-pending-without-filename entries
        .map((r) => ({
          id: r.id,
          fileName: r.fileName,
          loading: false,
          createdAt: r.createdAt,
        })),
      imageGenHistorial,
      imageGenCompanyId,
      imageGenGalleryAssetIds,
      productSceneAssetMap,
      modelAssetMap,
    };
    // Duración REAL del proyecto = fin del último clip (no el placeholder 30
    // que se setea al crear). Sin esto, la galería de proyectos muestra 0:30
    // fijo para todos. Mismo cálculo que el memo `contentDuration` (que se
    // declara más abajo, por eso se computa inline acá).
    const contentDur = clips.reduce(
      (acc, c) => Math.max(acc, c.startTime + c.duration),
      0,
    );
    try {
      await supabase
        .from("video_projects")
        .update({
          state,
          duration: contentDur > 0 ? contentDur : duration,
          name: projectName,
          updated_at: new Date().toISOString(),
        })
        .eq("id", projectId);
    } catch {
      // silent
    }

    // Fire-and-forget thumbnail regen if the cover clip at t=0 changed.
    if (companyId && !thumbnailRegenInFlightRef.current) {
      const cover = findCoverClipAtZero(clips);
      const nextHash = hashCoverClip(cover);
      if (nextHash !== lastThumbnailHashRef.current) {
        thumbnailRegenInFlightRef.current = true;
        regenerateProjectThumbnail({ projectId, companyId, clip: cover })
          .then(() => {
            lastThumbnailHashRef.current = nextHash;
          })
          .catch(() => {})
          .finally(() => {
            thumbnailRegenInFlightRef.current = false;
          });
      }
    }
  }, [
    projectId,
    companyId,
    clips,
    zoom,
    scenePrompt,
    sceneRefMode,
    sceneDuration,
    aspectRatio,
    duration,
    projectName,
    imageGenResults,
    imageGenHistorial,
    imageGenCompanyId,
    imageGenGalleryAssetIds,
    productSceneAssetMap,
    modelAssetMap,
  ]);

  useEffect(() => {
    if (!isLoadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveProject();
    }, 3000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [saveProject]);

  // Save immediately on unmount
  useEffect(() => {
    return () => {
      if (isLoadedRef.current) saveProject();
    };
  }, [saveProject]);

  // Refs
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineAreaRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const draggingClipRef = useRef<{ id: string; offsetSec: number } | null>(
    null,
  );
  // Pista de origen "pinneada" durante un drag vertical. Cuando el usuario
  // arrastra un clip audio/video a otra pista, el clip cambia su `trackIndex`
  // en vivo — si la pista origen queda vacía, `displayRows` la destruiría
  // inmediatamente, haciendo saltar al resto de las pistas y rompiendo la
  // referencia visual del usuario. Mientras haya un pin activo, inyectamos
  // esa fila en `displayRows` aunque no tenga clips, y la limpiamos al
  // soltar el mouse (`handleTimelinePointerUp`).
  const [pinnedOriginRow, setPinnedOriginRow] = useState<{
    trackType: "audio" | "video";
    index: number;
  } | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);
  const mediaRefs = useRef<Map<string, HTMLVideoElement | HTMLAudioElement>>(
    new Map(),
  );
  // WebAudio graph for clip-level amplification > 100%
  const audioCtxRef = useRef<AudioContext | null>(null);
  const mediaAudioNodesRef = useRef<
    Map<string, { source: MediaElementAudioSourceNode; gain: GainNode }>
  >(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const anyFileInputRef = useRef<HTMLInputElement>(null);
  const agentUploadInputRef = useRef<HTMLInputElement>(null);

  const selectedClip = clips.find((c) => c.id === selectedClipId);

  // ── Timeline geometry (pixel-based) ──────────────────────────────────────
  // Base pixels-per-second at zoom=1. Zoom scales this linearly.
  const BASE_PPS = 40;
  const MIN_TIMELINE_SECONDS = 30;
  const TIMELINE_TAIL_SECONDS = 120; // generous empty space after the last clip's end so the user can always scroll past content
  const pps = BASE_PPS * zoom;
  // True end time of all content (for export + transport display)
  const contentDuration = useMemo(() => {
    if (clips.length === 0) return 0;
    return clips.reduce((acc, c) => Math.max(acc, c.startTime + c.duration), 0);
  }, [clips]);

  // ── Handler that fires the MP4 render. Lives here (not next to the
  // ── export state above) because it depends on `contentDuration`, which
  // ── is declared in this scope further down — TDZ ordering forces the
  // ── handler to come after.
  const handleExportMp4 = useCallback(async () => {
    const timelineClips = clips;
    const timelineDurationSecs =
      contentDuration > 0 ? contentDuration : duration;

    const pre = preflightExport({
      clips: timelineClips,
      durationInSeconds: timelineDurationSecs,
    });
    if (pre.blockers.length > 0) {
      setExportState({ phase: "error", message: pre.blockers.join(" ") });
      return;
    }

    try {
      const { input } = await serializeTimeline({
        clips: timelineClips,
        aspectRatio,
        durationInSeconds: timelineDurationSecs,
        fps: 30,
      });
      if (input.clips.length === 0) {
        setExportState({
          phase: "error",
          message:
            "Ningún clip pudo resolverse para el render. Revisá que los medios estén disponibles.",
        });
        return;
      }

      // `projectId` is only required for the real (non-mock) render path.
      const handle = startRender(input, { projectId: projectId || undefined });
      exportHandleRef.current = handle;
      setExportState({
        phase: "rendering",
        progress: 0,
        label: "Iniciando…",
        handle,
      });

      handle.onProgress((p) => {
        setExportState((prev) =>
          prev.phase === "rendering"
            ? { ...prev, progress: p.progress, label: p.label }
            : prev,
        );
      });

      const result = await handle.result;
      exportHandleRef.current = null;
      setExportState({ phase: "done", outputUrl: result.outputUrl });
    } catch (err: any) {
      exportHandleRef.current = null;
      setExportState((prev) =>
        prev.phase === "idle"
          ? prev
          : {
              phase: "error",
              message: err?.message || "Error inesperado durante el render.",
            },
      );
    }
  }, [clips, contentDuration, duration, aspectRatio, projectId]);

  // Track visible (container) width + scroll width to make sure the ruler always fills the viewport and to drive the custom scrollbar thumb
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(0);
  const [timelineScrollWidth, setTimelineScrollWidth] = useState(0);
  const [timelineScrollLeft, setTimelineScrollLeft] = useState(0);
  useEffect(() => {
    const el = timelineAreaRef.current;
    if (!el) return;
    const update = () => {
      setTimelineViewportWidth(el.clientWidth);
      setTimelineScrollWidth(el.scrollWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also observe the inner content wrapper to catch width changes from zoom
    const inner = el.firstElementChild as HTMLElement | null;
    if (inner) ro.observe(inner);
    return () => ro.disconnect();
  }, []);
  // Keep live refs of zoom so the native wheel handler (registered once) can read the current value.
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  // Pending zoom pivot: after zoom state changes, useLayoutEffect applies the scrollLeft needed to keep
  // `timeUnderCursor` under `cursorX`, synchronously before paint so width + scroll update in one frame.
  const zoomPivotRef = useRef<{
    timeUnderCursor: number;
    cursorX: number;
  } | null>(null);
  useLayoutEffect(() => {
    const pivot = zoomPivotRef.current;
    if (!pivot) return;
    const el = timelineAreaRef.current;
    if (!el) return;
    const newPps = BASE_PPS * zoom;
    const target = Math.max(0, pivot.timeUnderCursor * newPps - pivot.cursorX);
    el.scrollLeft = target;
    zoomPivotRef.current = null;
  }, [zoom]);
  // Native wheel listener (non-passive):
  //  - ctrlKey (trackpad pinch on macOS) or metaKey/ctrlKey + wheel → zoom centered on cursor
  //  - otherwise → horizontal scroll (vertical wheel delta translated to horizontal)
  useEffect(() => {
    const el = timelineAreaRef.current;
    if (!el) return;
    const MIN_ZOOM = 0.25;
    const MAX_ZOOM = 6;
    // Batch rapid pinch events into a single update per animation frame to avoid jitter.
    let accumulatedDeltaY = 0;
    let pendingCursorX = 0;
    let rafId: number | null = null;
    const flushZoom = () => {
      rafId = null;
      const el2 = timelineAreaRef.current;
      if (!el2 || accumulatedDeltaY === 0) {
        accumulatedDeltaY = 0;
        return;
      }
      const currentZoom = zoomRef.current;
      const currentPps = BASE_PPS * currentZoom;
      const timeUnderCursor = (el2.scrollLeft + pendingCursorX) / currentPps;
      const factor = Math.exp(-accumulatedDeltaY * 0.01);
      const newZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, currentZoom * factor),
      );
      accumulatedDeltaY = 0;
      if (newZoom === currentZoom) return;
      zoomPivotRef.current = { timeUnderCursor, cursorX: pendingCursorX };
      setZoom(newZoom);
    };
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        pendingCursorX = e.clientX - rect.left;
        accumulatedDeltaY += e.deltaY;
        if (rafId === null) rafId = requestAnimationFrame(flushZoom);
        return;
      }
      if (el.scrollWidth <= el.clientWidth) return;
      const primary =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (primary === 0) return;
      e.preventDefault();
      el.scrollLeft += primary;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);
  // Visible timeline span — ruler always extends to cover at least the visible viewport, with tail beyond the last clip
  const minSecondsForViewport =
    timelineViewportWidth > 0 ? timelineViewportWidth / pps : 0;
  const timelineDuration = Math.max(
    contentDuration + TIMELINE_TAIL_SECONDS,
    minSecondsForViewport,
    MIN_TIMELINE_SECONDS,
  );
  const timelineWidth = timelineDuration * pps;

  // ── Deselect clip when clicking anywhere outside a clip ───────────────────
  useEffect(() => {
    if (!selectedClipId) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.closest("[data-clip-id]") ||
          target.closest("[data-clip-ui]") ||
          // La regla y el cursor mueven el tiempo, no cambian la selección.
          // Sin esta excepción este listener deshace lo que decide
          // `handleTimelinePointerDown` y el clip se pierde igual.
          target.closest("[data-timeline-scrub]"))
      )
        return;
      setSelectedClipId(null);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [selectedClipId]);

  // ── Generate thumbnails for video/ready AI clips ──────────────────────────
  useEffect(() => {
    clips.forEach((clip) => {
      const isMedia =
        clip.type === "video" ||
        (clip.type === "ai-generated" && clip.aiStatus === "ready");
      if (!isMedia || !clip.src) return;
      const key = `${clip.id}::${clip.src}`;
      if (thumbGenRef.current.has(key)) return;
      if (clipThumbnails[clip.id]) return;
      thumbGenRef.current.add(key);
      (async () => {
        try {
          const thumbs = await generateVideoThumbnails(clip.src!, 12, 96);
          if (thumbs.length > 0) {
            setClipThumbnails((prev) => ({ ...prev, [clip.id]: thumbs }));
          }
        } catch (err) {
          console.warn("[VideoEditor] Thumbnail generation failed:", err);
        }
      })();
    });
  }, [clips, clipThumbnails]);

  // ── Generate waveform peaks for audio clips ──────────────────────────────
  useEffect(() => {
    clips.forEach((clip) => {
      if (clip.type !== "audio" || !clip.src) return;
      const key = `${clip.id}::${clip.src}`;
      if (peaksGenRef.current.has(key)) return;
      if (audioPeaks[clip.id]) return;
      peaksGenRef.current.add(key);
      (async () => {
        try {
          const res = await fetch(clip.src!);
          const buf = await res.arrayBuffer();
          const Ctx = (window.AudioContext ||
            (window as any).webkitAudioContext) as typeof AudioContext;
          const ctx = new Ctx();
          const decoded = await ctx.decodeAudioData(buf);
          const channel = decoded.getChannelData(0);
          const BUCKETS = 200;
          const samplesPerBucket = Math.max(
            1,
            Math.floor(channel.length / BUCKETS),
          );
          const peaks: number[] = new Array(BUCKETS);
          let globalMax = 0;
          for (let i = 0; i < BUCKETS; i++) {
            let max = 0;
            const start = i * samplesPerBucket;
            const end = Math.min(start + samplesPerBucket, channel.length);
            for (let j = start; j < end; j++) {
              const v = Math.abs(channel[j]);
              if (v > max) max = v;
            }
            peaks[i] = max;
            if (max > globalMax) globalMax = max;
          }
          // Normalize to 0..1 so quiet audio still renders visibly
          const normFactor = globalMax > 0 ? 1 / globalMax : 1;
          for (let i = 0; i < peaks.length; i++)
            peaks[i] = peaks[i] * normFactor;
          ctx.close();
          setAudioPeaks((prev) => ({ ...prev, [clip.id]: peaks }));
        } catch (err) {
          console.warn("[VideoEditor] Waveform generation failed:", err);
        }
      })();
    });
  }, [clips, audioPeaks]);

  // ── Generate per-ASSET waveform peaks for the Archivos→Audio tab ─────────
  // Mirrors the per-clip peak generation above but keyed by asset id so the
  // tile waveforms persist across the panel lifecycle (independent of any
  // timeline clip referencing the asset).
  useEffect(() => {
    if (filesTab !== "audio") return;
    libraryAssets.forEach((asset) => {
      if (asset.type !== "audio") return;
      const url = assetPreviewUrls[asset.id];
      if (!url) return;
      if (audioAssetPeaks[asset.id]) return;
      if (audioAssetPeaksGenRef.current.has(asset.id)) return;
      audioAssetPeaksGenRef.current.add(asset.id);
      (async () => {
        try {
          const res = await fetch(url);
          const buf = await res.arrayBuffer();
          const Ctx = (window.AudioContext ||
            (window as any).webkitAudioContext) as typeof AudioContext;
          const ctx = new Ctx();
          const decoded = await ctx.decodeAudioData(buf);
          const channel = decoded.getChannelData(0);
          const BUCKETS = 56;
          const samplesPerBucket = Math.max(
            1,
            Math.floor(channel.length / BUCKETS),
          );
          const peaks: number[] = new Array(BUCKETS);
          let globalMax = 0;
          for (let i = 0; i < BUCKETS; i++) {
            let max = 0;
            const start = i * samplesPerBucket;
            const end = Math.min(start + samplesPerBucket, channel.length);
            for (let j = start; j < end; j++) {
              const v = Math.abs(channel[j]);
              if (v > max) max = v;
            }
            peaks[i] = max;
            if (max > globalMax) globalMax = max;
          }
          const norm = globalMax > 0 ? 1 / globalMax : 1;
          for (let i = 0; i < peaks.length; i++) peaks[i] = peaks[i] * norm;
          ctx.close();
          setAudioAssetPeaks((prev) => ({ ...prev, [asset.id]: peaks }));
        } catch (err) {
          console.warn("[VideoEditor] Asset waveform generation failed:", err);
        }
      })();
    });
  }, [filesTab, libraryAssets, assetPreviewUrls, audioAssetPeaks]);

  // ── Single-track audio preview controls (Archivos→Audio tab) ─────────────
  const stopAudioPreview = useCallback(() => {
    if (audioPreviewRafRef.current)
      cancelAnimationFrame(audioPreviewRafRef.current);
    audioPreviewRafRef.current = null;
    const el = audioPreviewElRef.current;
    if (el) {
      try {
        el.pause();
      } catch {}
      el.src = "";
      audioPreviewElRef.current = null;
    }
    setPlayingAudioAssetId(null);
    setAudioPlaybackProgress(0);
  }, []);
  const playAudioPreview = useCallback(
    (assetId: string, url: string) => {
      if (playingAudioAssetId === assetId) {
        stopAudioPreview();
        return;
      }
      // Stop any previous track first.
      if (audioPreviewRafRef.current)
        cancelAnimationFrame(audioPreviewRafRef.current);
      audioPreviewRafRef.current = null;
      const prev = audioPreviewElRef.current;
      if (prev) {
        try {
          prev.pause();
        } catch {}
        prev.src = "";
      }
      const audio = new Audio(url);
      audioPreviewElRef.current = audio;
      setPlayingAudioAssetId(assetId);
      setAudioPlaybackProgress(0);
      const onEnd = () => stopAudioPreview();
      audio.addEventListener("ended", onEnd);
      audio.play().catch((err) => {
        console.warn("[VideoEditor] audio preview play failed:", err);
        stopAudioPreview();
      });
      const tick = () => {
        const el = audioPreviewElRef.current;
        if (!el) return;
        const d = el.duration || 0;
        setAudioPlaybackProgress(d > 0 ? Math.min(1, el.currentTime / d) : 0);
        audioPreviewRafRef.current = requestAnimationFrame(tick);
      };
      audioPreviewRafRef.current = requestAnimationFrame(tick);
    },
    [playingAudioAssetId, stopAudioPreview],
  );
  // Stop preview if user leaves the audio tab / files panel.
  useEffect(() => {
    if (activePanel !== "files" || filesTab !== "audio") stopAudioPreview();
  }, [activePanel, filesTab, stopAudioPreview]);
  // Cleanup on unmount.
  useEffect(() => () => stopAudioPreview(), [stopAudioPreview]);

  // ── Single-track video preview controls (Archivos→Videos tab) ────────────
  const stopVideoPreview = useCallback(() => {
    const id = playingVideoAssetId;
    if (id) {
      const el = videoPreviewElsRef.current.get(id);
      if (el) {
        try {
          el.pause();
        } catch {}
        try {
          el.currentTime = 0;
        } catch {}
      }
    }
    setPlayingVideoAssetId(null);
  }, [playingVideoAssetId]);
  const toggleVideoPreview = useCallback(
    (assetId: string) => {
      if (playingVideoAssetId === assetId) {
        const el = videoPreviewElsRef.current.get(assetId);
        if (el) {
          try {
            el.pause();
          } catch {}
          try {
            el.currentTime = 0;
          } catch {}
        }
        setPlayingVideoAssetId(null);
        return;
      }
      // Pause previously playing one (if any).
      if (playingVideoAssetId) {
        const prev = videoPreviewElsRef.current.get(playingVideoAssetId);
        if (prev) {
          try {
            prev.pause();
          } catch {}
          try {
            prev.currentTime = 0;
          } catch {}
        }
      }
      const el = videoPreviewElsRef.current.get(assetId);
      if (!el) return;
      el.muted = false;
      el.currentTime = 0;
      el.play()
        .then(() => setPlayingVideoAssetId(assetId))
        .catch((err) => {
          console.warn("[VideoEditor] video preview play failed:", err);
          setPlayingVideoAssetId(null);
        });
    },
    [playingVideoAssetId],
  );
  // Stop preview if user leaves the videos tab / files panel.
  useEffect(() => {
    if (activePanel !== "files" || filesTab !== "media") stopVideoPreview();
  }, [activePanel, filesTab, stopVideoPreview]);

  // ── Detect AI generation finished → play sound + bounce ──────────────────
  useEffect(() => {
    const prev = prevAiStatusRef.current;
    const nextMap: Record<string, AiClipStatus | undefined> = {};
    const justFinished: string[] = [];
    clips.forEach((c) => {
      nextMap[c.id] = c.aiStatus;
      if (prev[c.id] === "generating" && c.aiStatus === "ready") {
        justFinished.push(c.id);
      }
    });
    prevAiStatusRef.current = nextMap;
    if (justFinished.length > 0) {
      try {
        const el = finishSoundRef.current;
        if (el) {
          el.currentTime = 0;
          el.play().catch(() => {});
        }
      } catch {}
      setBounceClipIds((curr) => {
        const next = new Set(curr);
        justFinished.forEach((id) => next.add(id));
        return next;
      });
      // Clear bounce class after the animation completes (matches index.css duration)
      window.setTimeout(() => {
        setBounceClipIds((curr) => {
          const next = new Set(curr);
          justFinished.forEach((id) => next.delete(id));
          return next;
        });
      }, 700);
    }
  }, [clips]);

  // ── Derived: clips with media that are active at currentTime ─────────────
  const activeVideoClip = useMemo(() => {
    const candidates = clips.filter(
      (c) =>
        (c.type === "video" || c.type === "ai-generated") &&
        c.src &&
        currentTime >= c.startTime &&
        currentTime < c.startTime + c.duration,
    );
    if (candidates.length === 0) return null;
    // Higher trackIndex wins (drawn on top of lower tracks).
    return candidates.reduce((a, b) =>
      getClipTrackIndex(b) > getClipTrackIndex(a) ? b : a,
    );
  }, [clips, currentTime]);

  // Active motion-blur state — derives the current blur magnitude and
  // direction from whichever clip is mid-slide-transition. All clips in
  // a slide transition share the same `blurPeakAt(t, SLIDE_BLUR_PEAK_PX)` value so a single
  // SVG <feGaussianBlur> instance can drive both A and B simultaneously.
  // Returns null when no slide is in progress so the SVG element drops
  // out of the DOM entirely (no GPU compositing cost on idle frames).
  // Position of the white reveal-wipe line, in % of preview width. null
  // when no `reveal` transition is active. The line is rendered as an
  // overlay div on top of the videos so it stays sharp and white
  // regardless of what's behind it.
  const revealLineState = useMemo<{ leftPct: number } | null>(() => {
    for (const c of clips) {
      if (c.type !== "video" && c.type !== "ai-generated") continue;
      if (!c.transitionIn || c.transitionIn.type !== "reveal") continue;
      const win = getTransitionWindow(c);
      if (!win) continue;
      if (currentTime < win.start || currentTime > win.end) continue;
      const t = (currentTime - win.start) / win.duration;
      return { leftPct: revealLineEase(t) * 100 };
    }
    return null;
  }, [clips, currentTime]);

  const motionBlurState = useMemo<{
    px: number;
    direction: "x" | "y";
  } | null>(() => {
    for (const c of clips) {
      if (c.type !== "video" && c.type !== "ai-generated") continue;
      if (!isClipActiveAtTime(c, currentTime, clips)) continue;
      const state = computeClipRenderStateAtTime(c, currentTime, clips);
      if (
        state.blurPx &&
        state.blurPx > 0.1 &&
        (state.blurDirection === "x" || state.blurDirection === "y")
      ) {
        return { px: state.blurPx, direction: state.blurDirection };
      }
    }
    return null;
  }, [clips, currentTime]);

  // When an overlay-style transition is in progress, returns the URL of
  // the overlay video to composite on top + the normalised progress
  // through the transition window (0..1). The progress is what the
  // overlay's currentTime gets synced to, so the overlay's own
  // animation lines up with the transition timing regardless of the
  // user's chosen duration.
  const overlayState = useMemo<{ url: string; progress: number } | null>(() => {
    for (const c of clips) {
      if (c.type !== "video" && c.type !== "ai-generated") continue;
      if (!c.transitionIn) continue;
      const url = OVERLAY_TRANSITIONS[c.transitionIn.type];
      if (!url) continue;
      const win = getTransitionWindow(c);
      if (!win) continue;
      if (currentTime < win.start || currentTime > win.end) continue;
      const progress = (currentTime - win.start) / win.duration;
      return { url, progress };
    }
    return null;
  }, [clips, currentTime]);

  const mediaClips = useMemo(
    () =>
      clips.filter(
        (c) =>
          c.src &&
          (c.type === "video" ||
            c.type === "audio" ||
            c.type === "ai-generated"),
      ),
    [clips],
  );

  // Text clips that are visible at the current playhead. Render order = clip
  // array order, so newly-added clips draw on top. The renderer uses these
  // to overlay <div>s on the preview canvas.
  const activeTextClips = useMemo(
    () =>
      clips.filter(
        (c) =>
          c.type === "text" &&
          currentTime >= c.startTime &&
          currentTime < c.startTime + c.duration,
      ),
    [clips, currentTime],
  );

  // Animation clips visible at the playhead. Rendered in array order so the
  // newest clip ends up on top, matching the text clip behaviour.
  const activeAnimationClips = useMemo(
    () =>
      clips.filter(
        (c) =>
          c.type === "animation" &&
          !!c.animationTemplate &&
          currentTime >= c.startTime &&
          currentTime < c.startTime + c.duration,
      ),
    [clips, currentTime],
  );

  // ── Preview canvas size tracking ─────────────────────────────────────────
  // The preview container is responsive (its height depends on viewport +
  // aspect ratio). We need the live pixel height to compute text font sizes
  // from the resolution-independent `fontSizePct` we store per clip, and the
  // pixel rect to translate pointer events into 0..100 % coordinates while
  // dragging text.
  const previewRef = useRef<HTMLDivElement | null>(null);
  // Ref to the overlay-transition <video> element (only mounted while
  // an overlay-style transition is in progress). The effect below
  // seeks/plays it in lockstep with the timeline playhead so the
  // overlay's animation always matches the transition's progress
  // regardless of the user's chosen duration.
  const overlayVideoRef = useRef<HTMLVideoElement | null>(null);
  // Cache of per-overlay-URL durations so we can normalise the seek
  // (overlay video's currentTime = progress × duration). Loaded lazily
  // via a tiny probe video on first sight of each URL.
  const [overlayDurations, setOverlayDurations] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    const el = overlayVideoRef.current;
    if (!el || !overlayState) return;
    const dur = overlayDurations[overlayState.url];
    if (!dur) {
      const handler = () => {
        const d = el.duration;
        if (isFinite(d) && d > 0) {
          setOverlayDurations((prev) =>
            prev[overlayState.url] === d
              ? prev
              : { ...prev, [overlayState.url]: d },
          );
        }
      };
      el.addEventListener("loadedmetadata", handler);
      return () => el.removeEventListener("loadedmetadata", handler);
    }
    // We need the overlay to PLAY (not just seek) so the browser decodes
    // every frame — pure currentTime assignments only land on keyframes
    // for typical MP4s, which is why the user saw "only first/last frame"
    // before. The clip's transitionIn.durationSec is what we scale to.
    const targetClip = clips.find(
      (c) =>
        c.transitionIn &&
        OVERLAY_TRANSITIONS[c.transitionIn.type] === overlayState.url,
    );
    const transitionDur = targetClip?.transitionIn?.durationSec ?? dur;
    const desiredRate = dur / transitionDur;
    if (Math.abs(el.playbackRate - desiredRate) > 0.01)
      el.playbackRate = desiredRate;
    const expected = overlayState.progress * dur;
    if (Math.abs(el.currentTime - expected) > 0.3) {
      try {
        el.currentTime = expected;
      } catch {}
    }
    if (isPlaying) {
      if (el.paused) el.play().catch(() => {});
    } else if (!el.paused) {
      el.pause();
    }
  }, [overlayState, overlayDurations, isPlaying, clips]);
  const [previewSize, setPreviewSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setPreviewSize((prev) =>
        prev.w === r.width && prev.h === r.height
          ? prev
          : { w: r.width, h: r.height },
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Drag state for moving a text clip across the preview. Stored in a ref so
  // the rAF playback loop doesn't re-render on every pointermove.
  const textDragRef = useRef<{
    id: string;
    pointerId: number;
    rect: DOMRect;
    startX: number;
    startY: number;
    baseXPct: number;
    baseYPct: number;
    moved: boolean;
  } | null>(null);

  // Which text clip (if any) is currently in inline-content-edit mode
  // (entered via double-click on the overlay). While set, the overlay is
  // contenteditable and dragging is disabled so the user can select text.
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  // Exit content-edit mode whenever the selection changes to something else.
  useEffect(() => {
    if (editingTextId && selectedClipId !== editingTextId)
      setEditingTextId(null);
  }, [selectedClipId, editingTextId]);
  // Auto-focus + select-all when entering content-edit mode so the user can
  // start typing or replacing immediately without an extra click.
  useEffect(() => {
    if (!editingTextId) return;
    // Defer one frame so the just-rendered span has contentEditable=true.
    const raf = requestAnimationFrame(() => {
      const span = previewRef.current?.querySelector<HTMLSpanElement>(
        `span[contenteditable="true"]`,
      );
      if (!span) return;
      span.focus();
      const range = document.createRange();
      range.selectNodeContents(span);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [editingTextId]);

  const handleTextPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    clip: TimelineClip,
  ) => {
    if (!previewRef.current) return;
    // While the user is editing the content inline, let the browser handle
    // pointer events for caret placement / text selection.
    if (editingTextId === clip.id) return;
    e.stopPropagation();
    setSelectedClipId(clip.id);
    // Editing happens in the top toolbar now; don't hijack the side panel.
    const rect = previewRef.current.getBoundingClientRect();
    textDragRef.current = {
      id: clip.id,
      pointerId: e.pointerId,
      rect,
      startX: e.clientX,
      startY: e.clientY,
      baseXPct: clip.textXPct ?? 50,
      baseYPct: clip.textYPct ?? 50,
      moved: false,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handleTextPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = textDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = ((e.clientX - drag.startX) / drag.rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / drag.rect.height) * 100;
    if (!drag.moved && Math.hypot(dx, dy) < 0.5) return;
    drag.moved = true;
    const nextX = Math.max(0, Math.min(100, drag.baseXPct + dx));
    const nextY = Math.max(0, Math.min(100, drag.baseYPct + dy));
    setClips((prev) =>
      patchClipsWithSubtitleSync(prev, drag.id, {
        textXPct: nextX,
        textYPct: nextY,
      }),
    );
  };

  const handleTextPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = textDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    textDragRef.current = null;
  };

  // ── Text resize via SelectionFrame ───────────────────────────────────────
  // Cambiar el ancho de la caja del texto reacomoda el wrap (más / menos
  // líneas) sin alterar el font-size: separamos "tamaño visual" (font)
  // de "área de layout" (caja). Solo handles horizontales y de esquina
  // aplican — N/S serían no-ops porque el alto del texto sigue al contenido.
  const textResizeRef = useRef<{ id: string; baseWidthPct: number } | null>(
    null,
  );
  const handleTextResize = useCallback(
    (clip: TimelineClip) =>
      (
        handle: SelectionHandle,
        dx: number,
        _dy: number,
        phase: "start" | "move" | "end",
      ) => {
        if (handle === "n" || handle === "s") return;
        const canvasW = previewSize.w;
        if (!canvasW) return;

        if (phase === "start") {
          // Medimos el ancho actual del wrapper en pixels y lo pasamos
          // a % de canvas. Si el clip nunca tuvo `textWidthPct` seteado,
          // este es el primer commit explícito desde su ancho natural.
          let baseW = clip.textWidthPct;
          if (typeof baseW !== "number") {
            const el = previewRef.current?.querySelector<HTMLElement>(
              `[data-text-clip-id="${clip.id}"]`,
            );
            if (el) {
              const r = el.getBoundingClientRect();
              baseW = (r.width / canvasW) * 100;
            } else {
              baseW = 50;
            }
          }
          textResizeRef.current = { id: clip.id, baseWidthPct: baseW };
          return;
        }

        const ref = textResizeRef.current;
        if (!ref || ref.id !== clip.id) return;

        const dxPct = (dx / canvasW) * 100;
        // Como el texto está anclado al centro (translate -50%), mover
        // *un* lado debe ensanchar la caja en 2*dx para que el centro
        // quede fijo. Signo según qué handle se está arrastrando.
        const horizSign =
          handle === "e" || handle === "ne" || handle === "se"
            ? 1
            : handle === "w" || handle === "nw" || handle === "sw"
              ? -1
              : 0;
        const next = Math.max(
          5,
          Math.min(300, ref.baseWidthPct + 2 * horizSign * dxPct),
        );

        setClips((prev) =>
          prev.map((c) =>
            c.id === clip.id ? { ...c, textWidthPct: next } : c,
          ),
        );

        if (phase === "end") {
          textResizeRef.current = null;
        }
      },
    [previewSize.w],
  );

  // ── Animation drag (mismo patrón que el texto) ──────────────────────────
  // Conceptualmente idéntico al drag de texto, pero con un clamp distinto:
  // como el wrapper se escala con `animScale`, hay que mantener el contenido
  // dentro del canvas teniendo en cuenta cuánto ocupa después de escalar.
  const animDragRef = useRef<{
    id: string;
    pointerId: number;
    rect: DOMRect;
    startX: number;
    startY: number;
    baseXPct: number;
    baseYPct: number;
    scale: number;
    moved: boolean;
  } | null>(null);

  const handleAnimPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    clip: TimelineClip,
  ) => {
    if (!previewRef.current) return;
    e.stopPropagation();
    setSelectedClipId(clip.id);
    const rect = previewRef.current.getBoundingClientRect();
    animDragRef.current = {
      id: clip.id,
      pointerId: e.pointerId,
      rect,
      startX: e.clientX,
      startY: e.clientY,
      baseXPct: clip.animXPct ?? 50,
      baseYPct: clip.animYPct ?? 50,
      scale: clip.animScale ?? 1,
      moved: false,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handleAnimPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = animDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = ((e.clientX - drag.startX) / drag.rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / drag.rect.height) * 100;
    if (!drag.moved && Math.hypot(dx, dy) < 0.5) return;
    drag.moved = true;
    // Mismo clamp que el texto: solo el *centro* tiene que quedar dentro
    // del canvas. Esto permite arrastrar el elemento parcialmente afuera
    // (medio fuera de pantalla a propósito), que es algo que el usuario
    // pidió explícitamente — útil para entradas/salidas y composiciones
    // tipo "viene desde el costado".
    const nextX = Math.max(0, Math.min(100, drag.baseXPct + dx));
    const nextY = Math.max(0, Math.min(100, drag.baseYPct + dy));
    setClips((prev) =>
      prev.map((c) =>
        c.id === drag.id ? { ...c, animXPct: nextX, animYPct: nextY } : c,
      ),
    );
  };

  const handleAnimPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = animDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    animDragRef.current = null;
  };

  // ── Animation resize via SelectionFrame ──────────────────────────────────
  // Cualquier handle escala uniformemente con respecto al centro.
  // Convertimos el drag-outward (componente del delta en la dirección del
  // handle) a un factor multiplicativo sobre `animScale`. El denominador
  // (alto del canvas en px) hace que la sensibilidad sea proporcional al
  // tamaño visible — un drag del 50% del canvas duplica/halve la escala.
  const animResizeRef = useRef<{ id: string; baseScale: number } | null>(null);
  const handleAnimResize = useCallback(
    (clip: TimelineClip) =>
      (
        handle: SelectionHandle,
        dx: number,
        dy: number,
        phase: "start" | "move" | "end",
      ) => {
        const refH = previewSize.h;
        if (!refH) return;
        if (phase === "start") {
          animResizeRef.current = {
            id: clip.id,
            baseScale: clip.animScale ?? 1,
          };
          return;
        }
        const ref = animResizeRef.current;
        if (!ref || ref.id !== clip.id) return;

        // Vector "outward" según qué lado/esquina se arrastra. N/S no
        // aportan en X y E/W no aportan en Y; las esquinas suman ambas.
        const dirX =
          handle === "e" || handle === "ne" || handle === "se"
            ? 1
            : handle === "w" || handle === "nw" || handle === "sw"
              ? -1
              : 0;
        const dirY =
          handle === "s" || handle === "se" || handle === "sw"
            ? 1
            : handle === "n" || handle === "ne" || handle === "nw"
              ? -1
              : 0;
        const dragOut = dx * dirX + dy * dirY;
        // Factor 2 porque la manija está a la mitad del bbox: un drag
        // de "d" mueve 2d el ancho total (centro fijo).
        const factor = 1 + (2 * dragOut) / refH;
        const next = Math.max(0.1, Math.min(3, ref.baseScale * factor));
        setClips((prev) =>
          prev.map((c) => (c.id === clip.id ? { ...c, animScale: next } : c)),
        );
        if (phase === "end") animResizeRef.current = null;
      },
    [previewSize.h],
  );

  // ── Display rows (one per visible track; audio expands into N rows) ──────
  type DisplayRow = {
    key: string;
    trackType: TrackType;
    label: string;
    color: string;
    matchClip: (c: TimelineClip) => boolean;
    audioIndex?: number;
    videoIndex?: number;
  };
  const displayRows = useMemo<DisplayRow[]>(() => {
    const rows: DisplayRow[] = [];
    // Si hay un pin de pista origen activo (drag en curso), aseguramos que
    // esa fila siga existiendo aunque su clip único ya se haya movido a
    // otra pista durante el drag.
    const pinnedAudioIdx =
      pinnedOriginRow?.trackType === "audio" ? pinnedOriginRow.index : null;
    const pinnedVideoIdx =
      pinnedOriginRow?.trackType === "video" ? pinnedOriginRow.index : null;
    for (const tc of TRACK_CONFIG) {
      if (tc.type === "audio") {
        const baseIdxs = getAudioTrackIndices(clips);
        const idxs =
          pinnedAudioIdx !== null && !baseIdxs.includes(pinnedAudioIdx)
            ? [...baseIdxs, pinnedAudioIdx].sort((a, b) => a - b)
            : baseIdxs;
        idxs.forEach((idx, orderPos) => {
          rows.push({
            key: `audio-${idx}`,
            trackType: "audio",
            label: idxs.length > 1 ? `Audio ${orderPos + 1}` : "Audio",
            color: getAudioTrackColor(idx),
            matchClip: (c) =>
              c.type === "audio" && getClipTrackIndex(c) === idx,
            audioIndex: idx,
          });
        });
      } else if (tc.type === "video") {
        // Render video tracks in descending trackIndex so that higher tracks
        // appear higher in the timeline (and are drawn on top in the preview).
        const baseVideoIdxs = getVideoTrackIndices(clips);
        const mergedVideoIdxs =
          pinnedVideoIdx !== null && !baseVideoIdxs.includes(pinnedVideoIdx)
            ? [...baseVideoIdxs, pinnedVideoIdx]
            : baseVideoIdxs;
        const idxs = [...mergedVideoIdxs].sort((a, b) => b - a);
        idxs.forEach((idx) => {
          rows.push({
            key: `video-${idx}`,
            trackType: "video",
            label: idxs.length > 1 ? `Video ${idx + 1}` : "Video",
            color: tc.color,
            matchClip: (c) =>
              tc.clipTypes.includes(c.type) && getClipTrackIndex(c) === idx,
            videoIndex: idx,
          });
        });
      } else {
        // Elements track: optionally split into a dedicated "Subtítulos" row
        // (when at least one subtitle clip exists) plus the regular elements
        // row. Subtitles always render *above* other elements so the user can
        // visually parse which row hosts the auto-generated captions.
        const hasSubtitles = clips.some(
          (c) => c.type === "text" && c.isSubtitle,
        );
        const hasOtherElements = clips.some(
          (c) =>
            tc.clipTypes.includes(c.type) &&
            !(c.type === "text" && c.isSubtitle),
        );
        if (hasSubtitles) {
          rows.push({
            key: "elements-subtitles",
            trackType: tc.type,
            label: "Subtítulos",
            color: "#8B5CF6",
            matchClip: (c) => c.type === "text" && !!c.isSubtitle,
          });
        }
        if (
          hasOtherElements ||
          (!hasSubtitles && clips.some((c) => tc.clipTypes.includes(c.type)))
        ) {
          rows.push({
            key: tc.type,
            trackType: tc.type,
            label: tc.label,
            color: tc.color,
            matchClip: (c) =>
              tc.clipTypes.includes(c.type) &&
              !(c.type === "text" && c.isSubtitle),
          });
        }
      }
    }
    return rows;
  }, [clips, pinnedOriginRow]);

  // Layout constants shared between render and pointer handlers
  const TIMELINE_RULER_H = 28;
  const TIMELINE_ROW_H = 48;
  const TIMELINE_ROW_GAP = 6;

  // ── Sync a single media element to the timeline ────────────────────────
  const syncMediaElement = useCallback(
    (
      clip: TimelineClip,
      el: HTMLVideoElement | HTMLAudioElement,
      time: number,
      playing: boolean,
      allClips: TimelineClip[],
    ) => {
      // Video clips can stay "active" past their visual end while a successor
      // clip is mid cross-fade — see `isClipActiveAtTime`. Audio clips never
      // extend (no transitions for audio yet) so their behaviour is unchanged.
      const isActive =
        clip.type === "audio"
          ? time >= clip.startTime && time < clip.startTime + clip.duration
          : isClipActiveAtTime(clip, time, allClips);

      // Always keep the element volume in sync with the clip's volume.
      // HTMLMediaElement.volume only supports 0..1, so for values above 100% we
      // route the element through a GainNode that can go up to 2 (200%).
      //
      // Importante: una vez que se llamó a createMediaElementSource(el) para
      // un elemento, ese elemento queda PERMANENTEMENTE desconectado del
      // output nativo del navegador — todo el audio pasa por el AudioContext.
      // Por eso, si alguna vez existió un gain node para este clip, el
      // control real del volumen está en el gain, NO en el.volume. Setear
      // el.volume cuando el clip ya tiene gain node es no-op.
      const rawVol = Math.max(0, Math.min(2, clip.volume ?? 1));
      if (rawVol > 1) {
        // Lazy-init shared AudioContext + per-clip source & gain
        if (!audioCtxRef.current) {
          try {
            const Ctx = (window.AudioContext ||
              (window as any).webkitAudioContext) as typeof AudioContext;
            audioCtxRef.current = new Ctx();
          } catch {}
        }
        const ctx = audioCtxRef.current;
        if (ctx) {
          if (ctx.state === "suspended") {
            ctx.resume().catch(() => {});
          }
          let node = mediaAudioNodesRef.current.get(clip.id);
          if (!node) {
            try {
              const source = ctx.createMediaElementSource(el);
              const gain = ctx.createGain();
              source.connect(gain);
              gain.connect(ctx.destination);
              node = { source, gain };
              mediaAudioNodesRef.current.set(clip.id, node);
            } catch {
              // If the element is already attached to a different context, fall back silently
            }
          }
          if (node) node.gain.gain.value = rawVol;
        }
        if (Math.abs(el.volume - 1) > 0.001) el.volume = 1;
      } else {
        // 0..100%: si el clip alguna vez pasó por > 100% ya existe un gain
        // node y el audio rutea por el AudioContext. En ese caso seguimos
        // controlando todo desde el gain, no desde el.volume.
        const node = mediaAudioNodesRef.current.get(clip.id);
        if (node) {
          // El ctx se suspende cuando la página pierde foco / hay
          // inactividad. Si está suspended, no se escucha NADA aunque el
          // gain esté en su valor correcto. Lo resumimos.
          const ctx = audioCtxRef.current;
          if (ctx && ctx.state === "suspended") {
            ctx.resume().catch(() => {});
          }
          node.gain.gain.value = rawVol;
          if (Math.abs(el.volume - 1) > 0.001) el.volume = 1;
        } else {
          if (Math.abs(el.volume - rawVol) > 0.001) el.volume = rawVol;
        }
      }
      // Red de seguridad: si algún path raro dejó el elemento con
      // muted=true (autoplay policies del browser, otros componentes, etc),
      // lo destrabamos. El control de "audible o no" lo hace el rawVol
      // arriba (via el.volume o gain).
      if (el.muted) el.muted = false;

      if (!isActive) {
        if (!el.paused) el.pause();
        return;
      }

      // ── Pre-active freeze ────────────────────────────────────────────────
      // The clip's own transitionIn window starts BEFORE `clip.startTime`
      // (we center the fade over the boundary). During that lead-in the
      // <video> is mounted but we don't want to dip into pre-roll source
      // — hold on the first visible frame instead.
      const inPreActive =
        clip.type !== "audio" && time < clip.startTime && !!clip.transitionIn;
      if (inPreActive) {
        const firstFrame = clip.mediaOffset || 0;
        if (Math.abs(el.currentTime - firstFrame) > 0.05) {
          try {
            el.currentTime = firstFrame;
          } catch {}
        }
        if (!el.paused) el.pause();
        return;
      }

      // ── Transition extension freeze ──────────────────────────────────────
      // When this video clip is past its normal end but a successor's
      // transition keeps it "active", we DON'T let playback advance into
      // the source's trimmed-away tail (that would reveal content the user
      // already cut). Instead we hold on the clip's last visible frame
      // while the opacity fade does the visual work.
      const normalEnd = clip.startTime + clip.duration;
      const inExtensionFreeze = clip.type !== "audio" && time >= normalEnd;
      if (inExtensionFreeze) {
        const trimEnd = (clip.mediaOffset || 0) + clip.duration;
        // Land on the frame just BEFORE the cut (~16ms back, ~1 frame at
        // 60fps) so we're guaranteed to be inside the user's intended range.
        const freezeFrame = Math.max(0, trimEnd - 1 / 60);
        if (Math.abs(el.currentTime - freezeFrame) > 0.05) {
          try {
            el.currentTime = freezeFrame;
          } catch {}
        }
        if (!el.paused) el.pause();
        return;
      }

      const localTime = time - clip.startTime + (clip.mediaOffset || 0);
      // Only seek if difference is significant (avoid constant micro-seeks during playback)
      if (Math.abs(el.currentTime - localTime) > 0.15) {
        el.currentTime = localTime;
      }

      if (playing && el.paused) {
        el.play().catch(() => {});
      } else if (!playing && !el.paused) {
        el.pause();
      }
    },
    [],
  );

  // ── Sync all media to a given time ─────────────────────────────────────
  const syncAllMedia = useCallback(
    (time: number, playing: boolean) => {
      mediaClips.forEach((clip) => {
        const el = mediaRefs.current.get(clip.id);
        if (el) syncMediaElement(clip, el, time, playing, clips);
      });
    },
    [mediaClips, syncMediaElement, clips],
  );

  // ── Playback loop (rAF-based) ──────────────────────────────────────────
  useEffect(() => {
    if (isPlaying) {
      lastFrameRef.current = performance.now();
      const tick = (now: number) => {
        const delta = (now - lastFrameRef.current) / 1000;
        lastFrameRef.current = now;
        setCurrentTime((prev) => {
          const next = prev + delta;
          // Modo loop de previsualización: si hay un clip seleccionado para
          // reproducir en loop y el playhead llega al final del mismo,
          // hacemos snap a su inicio en lugar de frenar.
          if (animationPreviewLoopClipId) {
            const loopClip = clips.find(
              (c) => c.id === animationPreviewLoopClipId,
            );
            if (loopClip) {
              const end = loopClip.startTime + loopClip.duration;
              if (next >= end) return loopClip.startTime;
            }
          }
          const stopAt = contentDuration > 0 ? contentDuration : duration;
          if (next >= stopAt) {
            setIsPlaying(false);
            return stopAt;
          }
          return next;
        });
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, duration, contentDuration, animationPreviewLoopClipId, clips]);

  // Cuando el playback se detiene por cualquier vía (botón global de
  // pausa, llegar al fin del proyecto sin loop, etc.), limpiamos el modo
  // loop. Así el botón del panel queda sincronizado con el estado real.
  useEffect(() => {
    if (!isPlaying && animationPreviewLoopClipId) {
      setAnimationPreviewLoopClipId(null);
    }
  }, [isPlaying, animationPreviewLoopClipId]);

  // ── Keep media in sync with currentTime ────────────────────────────────
  useEffect(() => {
    syncAllMedia(currentTime, isPlaying);
  }, [currentTime, isPlaying, syncAllMedia]);

  // ── Clean up stale media refs when clips change ────────────────────────
  useEffect(() => {
    const clipIds = new Set(clips.map((c) => c.id));
    mediaRefs.current.forEach((_, id) => {
      if (!clipIds.has(id)) mediaRefs.current.delete(id);
    });
    mediaAudioNodesRef.current.forEach((node, id) => {
      if (!clipIds.has(id)) {
        try {
          node.source.disconnect();
          node.gain.disconnect();
        } catch {}
        mediaAudioNodesRef.current.delete(id);
      }
    });
  }, [clips]);

  // ── Timeline helpers ─────────────────────────────────────────────────────────
  const clientXToSeconds = useCallback(
    (clientX: number): number => {
      if (!timelineAreaRef.current) return 0;
      const el = timelineAreaRef.current;
      const rect = el.getBoundingClientRect();
      const px = Math.max(0, clientX - rect.left + el.scrollLeft);
      return px / pps;
    },
    [pps],
  );

  // ── Timeline seek (drag-aware) ─────────────────────────────────────────────
  const handleTimelinePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      isDraggingRef.current = true;
      draggingClipRef.current = null;
      // Mover el cursor por la regla (o agarrándolo) NO deselecciona: el
      // usuario necesita tener el clip elegido MIENTRAS busca el punto de
      // corte, o el botón Dividir desaparece justo cuando lo va a usar.
      // Click en un espacio vacío de las pistas sí deselecciona, que es la
      // forma natural de soltar el clip.
      const scrubbing = !!(e.target as HTMLElement | null)?.closest?.(
        "[data-timeline-scrub]",
      );
      if (!scrubbing) setSelectedClipId(null);
      setIsDraggingPlayhead(true);
      setCurrentTime(clientXToSeconds(e.clientX));
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    },
    [clientXToSeconds],
  );

  const SNAP_THRESHOLD = 0.5; // seconds proximity to trigger snap

  const handleTimelinePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (draggingClipRef.current) {
        const sec = clientXToSeconds(e.clientX);
        const { id, offsetSec } = draggingClipRef.current;
        const rawStart = Math.max(0, sec - offsetSec);
        const draggedClip = clips.find((c) => c.id === id);
        if (!draggedClip) return;

        const clampedStart = rawStart;
        const draggedEnd = clampedStart + draggedClip.duration;

        // Vertical drag: let audio and video clips move between tracks of their
        // own kind (and create a new one beyond the existing rows).
        //
        // Caso especial — clip "solo" en su pista: si el usuario jitterea el
        // mouse cerca del borde, podría oscilar entre la pista actual y una
        // pista *nueva* recién creada → la pista vieja queda vacía, se destruye,
        // `displayRows` se reconstruye y los índices se corren, produciendo un
        // flicker de colores y otra ronda de oscilación. Para evitarlo, cuando
        // `soloOnTrack` está activo, sólo permitimos mover a **pistas que ya
        // existen**; nunca generamos pistas nuevas. Mover a una pista existente
        // distinta es siempre válido — el destino ya está en `displayRows`, así
        // que no hay reshuffle: la pista origen desaparece una vez y listo.
        let targetTrackIndex: number | null = null;
        const isVerticallyDraggable =
          draggedClip.type === "audio" ||
          draggedClip.type === "video" ||
          draggedClip.type === "ai-generated";
        const draggedTrackCfg = getTrackForClipType(draggedClip.type);
        const draggedTrackIdx = getClipTrackIndex(draggedClip);
        const soloOnTrack = !clips.some(
          (c) =>
            c.id !== id &&
            draggedTrackCfg.clipTypes.includes(c.type) &&
            getClipTrackIndex(c) === draggedTrackIdx,
        );
        if (isVerticallyDraggable && timelineAreaRef.current) {
          const rect = timelineAreaRef.current.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const step = TIMELINE_ROW_H + TIMELINE_ROW_GAP;
          // Top-of-first-row = ruler + gap (each row starts with a 6px marginTop)
          const firstRowTop = TIMELINE_RULER_H + TIMELINE_ROW_GAP;
          const rawRowIdx = Math.floor((y - firstRowTop) / step);
          const rowIdx = Math.max(0, rawRowIdx);
          // Look up the row at the *raw* index so cursor positions above the
          // first row yield `undefined` instead of falling back to row 0.
          // Without this, when the topmost video row IS row 0 (no elements
          // rows present), dragging above it still maps to that video row
          // and the "create new track" elif never fires.
          const row = rawRowIdx >= 0 ? displayRows[rawRowIdx] : undefined;

          if (draggedClip.type === "audio") {
            const audioRows = displayRows.filter(
              (r) => r.trackType === "audio",
            );
            if (
              row &&
              row.trackType === "audio" &&
              typeof row.audioIndex === "number"
            ) {
              targetTrackIndex = row.audioIndex;
            } else if (
              !soloOnTrack &&
              rowIdx >= displayRows.length &&
              audioRows.length > 0
            ) {
              const lastAudioIdx = audioRows.reduce(
                (m, r) => Math.max(m, r.audioIndex ?? 0),
                0,
              );
              targetTrackIndex = lastAudioIdx + 1;
            }
          } else {
            // video / ai-generated. Mirrors audio's drag-to-new-row UX, but
            // because video tracks are sorted DESCENDING (higher index = higher
            // visual position), the "create new track" trigger is dragging
            // ABOVE the topmost video row rather than below the bottommost.
            //
            // IMPORTANT: we compare against `rawRowIdx` (not the clamped
            // `rowIdx`). `rowIdx` is clamped to ≥ 0, so when the cursor is
            // physically above the first row, rowIdx still reads 0 and we'd
            // never detect the "above top" condition when the topmost video
            // row IS row 0 (no elements rows above). `rawRowIdx` goes
            // negative in that case, which is exactly the signal we need.
            const videoRows = displayRows.filter(
              (r) => r.trackType === "video",
            );
            const firstVideoRowPos = displayRows.findIndex(
              (r) => r.trackType === "video",
            );
            // Stability guard: if the dragged clip is already alone on a
            // track strictly higher than any other video clip, DON'T keep
            // creating new tracks above it on every tick. Without this,
            // each pointer-move while the cursor sits above the topmost
            // row would push the clip up by one (1 → 2 → 3 → ...) because
            // the condition stays true after the first jump. The check
            // mirrors audio's `soloOnTrack` guard but is keyed on "am I
            // already at the top?" rather than "am I solo on this track?",
            // which lets the common single-clip case still work.
            const otherVideoMaxIdx = clips
              .filter(
                (c) =>
                  c.id !== id &&
                  (c.type === "video" || c.type === "ai-generated"),
              )
              .reduce((m, c) => Math.max(m, getClipTrackIndex(c)), 0);
            const isAtTopAlone = draggedTrackIdx > otherVideoMaxIdx;
            if (
              row &&
              row.trackType === "video" &&
              typeof row.videoIndex === "number"
            ) {
              targetTrackIndex = row.videoIndex;
            } else if (
              firstVideoRowPos >= 0 &&
              rawRowIdx < firstVideoRowPos &&
              !isAtTopAlone
            ) {
              const topVideoIdx = videoRows.reduce(
                (m, r) => Math.max(m, r.videoIndex ?? 0),
                0,
              );
              targetTrackIndex = topVideoIdx + 1;
            }
          }
        }

        // Find the track config for this clip
        const trackCfg = getTrackForClipType(draggedClip.type);
        const effectiveIdx = targetTrackIndex ?? getClipTrackIndex(draggedClip);
        const siblings = clips.filter(
          (c) =>
            c.id !== id &&
            trackCfg.clipTypes.includes(c.type) &&
            getClipTrackIndex(c) === effectiveIdx,
        );

        // Check snap edges: dragged clip's start snaps to sibling's end, or dragged clip's end snaps to sibling's start
        let bestSnap: number | null = null;
        let bestDist = SNAP_THRESHOLD;

        for (const sib of siblings) {
          const sibEnd = sib.startTime + sib.duration;
          // Snap dragged start → sibling end (place after)
          const distAfter = Math.abs(clampedStart - sibEnd);
          if (distAfter < bestDist) {
            bestDist = distAfter;
            bestSnap = sibEnd;
          }
          // Snap dragged end → sibling start (place before)
          const distBefore = Math.abs(draggedEnd - sib.startTime);
          if (distBefore < bestDist) {
            bestDist = distBefore;
            bestSnap = sib.startTime - draggedClip.duration;
          }
          // Snap dragged start → 0 (beginning of timeline)
          const distZero = Math.abs(clampedStart);
          if (distZero < bestDist) {
            bestDist = distZero;
            bestSnap = 0;
          }
        }

        if (bestSnap !== null && bestSnap >= 0) {
          setSnapTarget({ clipId: id, startTime: bestSnap });
        } else {
          setSnapTarget(null);
        }

        setClips((prev) =>
          prev.map((c) => {
            if (c.id !== id) return c;
            const next = { ...c, startTime: clampedStart };
            if (
              targetTrackIndex !== null &&
              targetTrackIndex !== getClipTrackIndex(c)
            ) {
              next.trackIndex = targetTrackIndex;
              if (c.type === "audio")
                next.color = getAudioTrackColor(targetTrackIndex);
            }
            return next;
          }),
        );
        return;
      }
      if (!isDraggingRef.current) return;
      setCurrentTime(clientXToSeconds(e.clientX));
    },
    [clientXToSeconds, duration, clips, displayRows],
  );

  const handleTimelinePointerUp = useCallback(() => {
    // Apply snap if active
    if (draggingClipRef.current && snapTarget) {
      const { clipId, startTime } = snapTarget;
      setClips((prev) =>
        prev.map((c) => (c.id === clipId ? { ...c, startTime } : c)),
      );
    }
    setSnapTarget(null);
    isDraggingRef.current = false;
    draggingClipRef.current = null;
    setIsDraggingPlayhead(false);
    // Liberamos el pin de la pista origen: si quedó vacía por el drag,
    // `displayRows` la colapsará ahora (ya sin flicker porque el drag
    // terminó).
    setPinnedOriginRow(null);
  }, [snapTarget]);

  // ── Edit AI clip ──────────────────────────────────────────────────────────
  const editAiClip = useCallback(
    (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || clip.type !== "ai-generated") return;
      setEditingClipId(clipId);
      setScenePrompt(clip.aiPrompt || "");
      setSceneRefMode(clip.aiRefMode || "none");
      setSceneMode(clip.aiSceneMode || "general");
      setScenePersonType("static");
      setSceneDialogue(clip.aiDialogue || "");
      setSceneDialogueInputMode(clip.aiDialogueInputMode || "text");
      setSceneDialogueAudio(
        clip.aiDialogueAudioUrl &&
          clip.aiDialogueAudioStoragePath &&
          clip.aiDialogueAudioAssetId
          ? {
              url: clip.aiDialogueAudioUrl,
              storagePath: clip.aiDialogueAudioStoragePath,
              durationSec: clip.aiDialogueAudioDurationSec ?? 0,
              assetId: clip.aiDialogueAudioAssetId,
            }
          : null,
      );
      setAudioAiVoiceId(clip.aiVoiceId || null);
      setAudioAiVoiceName(clip.aiVoiceName || "");
      setSceneRefAssetIds(clip.aiRefAssetIds || []);
      setSceneFirstFrameAssetId(clip.aiFirstFrameAssetId || null);
      setSceneLastFrameAssetId(clip.aiLastFrameAssetId || null);
      // `clip.duration` es la duración REAL medida al archivo generado
      // (`probe.duration`), así que trae decimales: 4.0000012. Eso está bien
      // para el timeline, pero el control de duración es de segundos enteros —
      // los sliders solo producen enteros y `DURATION_OPTIONS.indexOf()` no
      // matchea un float (devuelve -1 y la barra se dibuja en el extremo).
      // Redondeamos acá, en el único punto por donde entra un decimal.
      setSceneDuration(Math.max(1, Math.round(clip.duration)));
      setSceneSpeechSpeed(clip.aiSpeechSpeed ?? 1.1);
      // Legacy: clips generados cuando existía "rapido" caen a Estándar.
      setSceneQuality(
        clip.aiQuality === "profesional" ? "profesional" : "estandar",
      );
      setSceneResolution(clip.aiResolution || "720p");
      if (
        clip.aiMultiEnabled &&
        Array.isArray(clip.aiMultiScenes) &&
        clip.aiMultiScenes.length > 0
      ) {
        setSceneMultiEnabled(true);
        setSceneMultiList(
          clip.aiMultiScenes.map((s) => ({
            id: generateId(),
            prompt: s.prompt,
            duration: s.duration,
          })),
        );
      } else {
        setSceneMultiEnabled(false);
        setSceneMultiList([{ id: generateId(), prompt: "", duration: 5 }]);
      }
      setSelectedClipId(clipId);
      setActivePanel("ai-scene");

      // Resolve signed URLs for preview in background
      const idsToResolve: string[] = [];
      if (clip.aiRefAssetIds) idsToResolve.push(...clip.aiRefAssetIds);
      if (clip.aiFirstFrameAssetId) idsToResolve.push(clip.aiFirstFrameAssetId);
      if (clip.aiLastFrameAssetId) idsToResolve.push(clip.aiLastFrameAssetId);
      if (idsToResolve.length > 0) {
        (async () => {
          const assets = await fetchAssetsByIds(idsToResolve);
          const urls: Record<string, string> = {};
          await Promise.all(
            assets.map(async (a) => {
              const u = await getAssetSignedUrl(a.storage_path);
              if (u) urls[a.id] = u;
            }),
          );
          setAssetPreviewUrls((prev) => ({ ...prev, ...urls }));
        })();
      }
    },
    [clips],
  );

  // ── Edit AI audio clip ────────────────────────────────────────────────────
  const editAiAudioClip = useCallback(
    (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || clip.type !== "audio" || !clip.aiPrompt) return;
      setEditingClipId(clipId);
      setAudioAiType(clip.aiAudioType || "voice");
      setAudioAiPrompt(clip.aiPrompt || "");
      setAudioAiDuration(clip.duration);
      setAudioAiVoiceId(clip.aiVoiceId || null);
      setAudioAiVoiceName(clip.aiVoiceName || "");
      setAudioAiInstrumentalOnly(!!clip.aiInstrumentalOnly);
      setAudioAiSpeechSpeed(clip.aiSpeechSpeed ?? 1.1);
      setAudioAiDialogueInputMode(clip.aiDialogueInputMode || "text");
      setAudioAiDialogueAudio(
        clip.aiDialogueAudioUrl &&
          clip.aiDialogueAudioStoragePath &&
          clip.aiDialogueAudioAssetId
          ? {
              url: clip.aiDialogueAudioUrl,
              storagePath: clip.aiDialogueAudioStoragePath,
              durationSec: clip.aiDialogueAudioDurationSec ?? 0,
              assetId: clip.aiDialogueAudioAssetId,
            }
          : null,
      );
      setSelectedClipId(clipId);
      setActivePanel("ai-audio");
    },
    [clips],
  );

  // ── Generate an existing AI audio clip (draft or error) ───────────────────
  const generateAiAudioClip = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || clip.type !== "audio") return;
      // Guard explícita: si falta info crítica, marcamos el clip con un
      // error visible en vez de hacer return silencioso. Así el usuario ve
      // por qué su Reintentar "no hizo nada".
      if (!clip.aiPrompt || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "error" as AiClipStatus,
                  aiError:
                    "No se pudo iniciar la generación: falta información del proyecto. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }
      if (!assertCredits(creditsForAiClip(clip))) return;
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                aiStatus: "generating" as AiClipStatus,
                aiError: undefined,
                aiGenerationStartedAt: Date.now(),
              }
            : c,
        ),
      );
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        const body: Record<string, any> = {
          user_id: session.user.id,
          ref_mode: clip.aiAudioType === "music" ? "musica" : "voz",
          voz_id: clip.aiVoiceId || "",
          project_id: projectId,
          // Trackeo (ADR-585): audio generado desde el Estudio.
          modo_utilizado: "estudio_audio",
          text: clip.aiPrompt,
          instrumental_only:
            clip.aiAudioType === "music" ? !!clip.aiInstrumentalOnly : false,
          ...(clip.aiAudioType !== "music"
            ? { velocidad_voz: clip.aiSpeechSpeed ?? 1.1 }
            : {}),
          // Voice mode audio input: user uploaded/recorded their own audio
          // and the workflow runs ElevenLabs speech-to-speech instead of TTS.
          ...(clip.aiAudioType !== "music" &&
          clip.aiDialogueInputMode === "audio" &&
          clip.aiDialogueAudioUrl
            ? {
                dialogo_input_mode: "audio",
                audio_url: clip.aiDialogueAudioUrl,
                audio_duration_sec: clip.aiDialogueAudioDurationSec ?? null,
              }
            : clip.aiAudioType !== "music"
              ? { dialogo_input_mode: "texto" }
              : {}),
        };
        // Helper con flag atlas/n8n (ADR-583) — timeout de 5 min adentro
        // para que el clip no se quede en "generating" eterno; sin eso el
        // botón Reintentar nunca aparece porque el catch nunca corre.
        const { path: audioPath } = await generateAudioCreator(body);

        // Credits were just deducted by the workflow — notify the sidebar.
        window.dispatchEvent(new Event("atlas:credits-updated"));

        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(audioPath, 3600);

        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "ready" as AiClipStatus,
                  storagePath: audioPath,
                  src: urlData?.signedUrl || undefined,
                }
              : c,
          ),
        );

        if (urlData?.signedUrl) {
          const probe = new Audio();
          probe.preload = "metadata";
          probe.src = urlData.signedUrl;
          probe.addEventListener("loadedmetadata", () => {
            const dur = probe.duration;
            if (isFinite(dur) && dur > 0) {
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? { ...c, duration: dur, sourceDuration: dur }
                    : c,
                ),
              );
            }
          });
        }

        if (companyId) {
          try {
            const asset = await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: audioPath,
              type: "audio",
              name: (
                clip.aiPrompt ||
                (clip.aiAudioType === "music" ? "Música IA" : "Voz IA")
              ).slice(0, 80),
              mimeType: "audio/mpeg",
            });
            if (asset) {
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId ? { ...c, assetId: asset.id } : c,
                ),
              );
            }
          } catch (e) {
            console.warn("[VideoEditor] Failed to register AI audio asset:", e);
            setClips((prev) =>
              prev.map((c) =>
                c.id === clipId
                  ? {
                      ...c,
                      aiAssetWarning:
                        "El audio se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                    }
                  : c,
              ),
            );
          }
        }
      } catch (err) {
        console.error("[VideoEditor] AI audio regeneration failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La generación tardó demasiado (más de 5 min). Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al generar el audio.";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, aiStatus: "error" as AiClipStatus, aiError: msg }
              : c,
          ),
        );
      }
    },
    [clips, projectId, companyId, assertCredits],
  );

  // ── Clip drag start ────────────────────────────────────────────────────────
  const lastClickRef = useRef<{ clipId: string; time: number } | null>(null);

  const handleClipPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, clip: TimelineClip) => {
      e.stopPropagation();
      setSelectedClipId(clip.id);
      setCurrentTime(clientXToSeconds(e.clientX));

      // Double-click detection (pointer capture blocks native dblclick)
      const now = Date.now();
      if (
        lastClickRef.current &&
        lastClickRef.current.clipId === clip.id &&
        now - lastClickRef.current.time < 400
      ) {
        lastClickRef.current = null;
        if (clip.type === "ai-generated") {
          editAiClip(clip.id);
          return;
        }
        if (clip.type === "audio" && clip.aiPrompt) {
          editAiAudioClip(clip.id);
          return;
        }
      }
      lastClickRef.current = { clipId: clip.id, time: now };

      const sec = clientXToSeconds(e.clientX);
      draggingClipRef.current = {
        id: clip.id,
        offsetSec: sec - clip.startTime,
      };
      isDraggingRef.current = false; // not a playhead drag

      // Pin de la pista de origen: sólo aplica a clips verticalmente
      // draggables (audio / video / ai-generated). Si durante el drag la
      // pista origen queda vacía, este pin evita que `displayRows` la
      // colapse hasta que el usuario suelte el mouse.
      if (
        clip.type === "audio" ||
        clip.type === "video" ||
        clip.type === "ai-generated"
      ) {
        const originIdx = getClipTrackIndex(clip);
        setPinnedOriginRow({
          trackType: clip.type === "audio" ? "audio" : "video",
          index: originIdx,
        });
      }
      // Capture on the timeline area so we get move/up even outside the clip
      if (timelineAreaRef.current) {
        timelineAreaRef.current.setPointerCapture(e.pointerId);
      }
    },
    [clientXToSeconds, editAiClip, editAiAudioClip],
  );

  // ── Load project library assets when Files panel opens / tab changes ───
  // The unified Files panel hosts three tabs (video, audio, image). For
  // video and audio we reuse `libraryAssets` (single bucket) and refetch
  // when the user switches; for image we keep a separate `imageLibraryAssets`
  // because the image tab also folds in AI-generated images on top.
  useEffect(() => {
    if (activePanel !== "files") return;
    if (!projectId) return;
    if (filesTab !== "media" && filesTab !== "audio") return;
    let cancelled = false;

    // Resuelve signed URLs (en background) para los assets sin preview.
    const resolvePreviews = async (assets: ProjectAsset[]) => {
      const missing = assets.filter((a) => !assetPreviewUrls[a.id]);
      if (missing.length === 0) return;
      const resolved = await Promise.all(
        missing.map(async (a) => {
          try {
            const url = await getAssetSignedUrl(a.storage_path);
            return url ? ([a.id, url] as const) : null;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const updates: Record<string, string> = {};
      for (const r of resolved) if (r) updates[r[0]] = r[1];
      if (Object.keys(updates).length > 0)
        setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
    };

    if (filesTab === "media") {
      // Multimedia: imágenes (bucket propio) + videos (libraryAssets), juntos.
      setImageLibraryLoading(true);
      setLibraryLoading(true);
      (async () => {
        try {
          const [imgs, vids] = await Promise.all([
            fetchProjectAssetsByType(projectId, "image"),
            fetchProjectAssetsByType(projectId, "video"),
          ]);
          if (cancelled) return;
          setImageLibraryAssets(imgs);
          setLibraryAssets(vids);
          await resolvePreviews([...imgs, ...vids]);
        } finally {
          if (!cancelled) {
            setImageLibraryLoading(false);
            setLibraryLoading(false);
          }
          filesLoadedOnceRef.current = true;
        }
      })();
    } else {
      // Audio (aparte).
      setLibraryLoading(true);
      (async () => {
        try {
          const assets = await fetchProjectAssetsByType(projectId, "audio");
          if (cancelled) return;
          setLibraryAssets(assets);
          await resolvePreviews(assets);
        } finally {
          if (!cancelled) setLibraryLoading(false);
          filesLoadedOnceRef.current = true;
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [activePanel, projectId, filesTab]);

  // ── Add an existing project asset (video or audio) to the timeline ──────
  const addAssetToTimeline = useCallback(
    async (asset: ProjectAsset, opts?: { startTime?: number }) => {
      if (asset.type !== "video" && asset.type !== "audio") return;
      const clipType: ClipType = asset.type;
      const track = getTrackForClipType(clipType);
      const clipId = generateId();
      // Resolve signed URL (use cached one if available)
      let src = assetPreviewUrls[asset.id];
      if (!src) {
        const url = await getAssetSignedUrl(asset.storage_path);
        if (url) {
          src = url;
          setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: url }));
        }
      }
      const baseName = asset.name || `Asset ${asset.id.slice(0, 6)}`;
      const startTime =
        opts?.startTime !== undefined
          ? Math.max(0, opts.startTime)
          : getNextStartTime(clips, track);
      const newClip: TimelineClip = {
        id: clipId,
        type: clipType,
        name: baseName.length > 20 ? baseName.slice(0, 17) + "..." : baseName,
        startTime,
        duration: 5,
        color: track.color,
        src,
        volume: 1,
        assetId: asset.id,
        storagePath: asset.storage_path,
      };
      setClips((prev) => [...prev, newClip]);
      setSelectedClipId(newClip.id);
      // Probe real duration so the clip length matches the source
      if (src) {
        const el = document.createElement(
          clipType === "video" ? "video" : "audio",
        );
        el.preload = "metadata";
        el.onloadedmetadata = () => {
          const d = el.duration;
          if (isFinite(d) && d > 0) {
            setClips((prev) =>
              prev.map((c) =>
                c.id === clipId ? { ...c, duration: d, sourceDuration: d } : c,
              ),
            );
          }
        };
        (el as HTMLMediaElement).src = src;
      }
    },
    [clips, assetPreviewUrls],
  );

  // Insert a logo (or any image URL) as a centered, movable overlay on the
  // Elements track — same mechanism the Elements panel uses for templates,
  // via the `image-reveal-v1` template but with text/background stripped so
  // only the logo shows. Safe only for permanent URLs (e.g. the brand's public
  // logo_url); private signed URLs would expire and aren't re-signed on reload.
  const insertLogoOverlay = useCallback(
    (imageUrl: string, name: string) => {
      const tpl = Object.values(ANIMATION_TEMPLATES).find(
        (t) => t.id === "image-reveal-v1",
      );
      if (!tpl) return;
      const elementsTrack = TRACK_CONFIG.find((t) => t.type === "elements")!;
      const clipId = generateId();
      const newClip: TimelineClip = {
        id: clipId,
        type: "animation",
        name,
        startTime: getNextStartTime(clips, elementsTrack),
        duration: tpl.defaultDurationSec,
        color: elementsTrack.color,
        animationTemplate: tpl.id,
        animationProps: {
          ...(tpl.defaultProps as Record<string, unknown>),
          imageUrl,
          title: "",
          subtitle: "",
          background: "transparent",
          kenBurns: false,
          reveal: "none",
          fit: "contain",
        },
        animXPct: 50,
        animYPct: 50,
        animScale: 0.6,
      };
      setClips((prev) => [...prev, newClip]);
      setSelectedClipId(clipId);
    },
    [clips],
  );

  // ── File upload ───────────────────────────────────────────────────────────
  // Modal de error de subida (ej. archivo demasiado grande). Mensaje o null.
  const [uploadErrorMsg, setUploadErrorMsg] = useState<string | null>(null);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: "video" | "audio") => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !companyId || !projectId) return;

      // Tope de 100MB: hoy Supabase Storage tiene el Global file size limit en
      // 100MB; un archivo más grande falla en el gateway (502) sin feedback al
      // usuario. Hasta subir ese límite, lo bloqueamos acá y avisamos.
      if (file.size > 100 * 1024 * 1024) {
        setUploadErrorMsg(
          `El archivo "${file.name}" pesa ${(file.size / (1024 * 1024)).toFixed(0)} MB. ` +
            `Por ahora no se aceptan videos de más de 100 MB. Probá con uno más liviano.`,
        );
        return;
      }

      const blobUrl = URL.createObjectURL(file);
      const isVideo = type === "video";
      const clipType: ClipType = isVideo ? "video" : "audio";
      const track = getTrackForClipType(clipType);
      const clipId = generateId();

      const newClip: TimelineClip = {
        id: clipId,
        type: clipType,
        name:
          file.name.length > 20 ? file.name.slice(0, 17) + "..." : file.name,
        startTime: getNextStartTime(clips, track),
        duration: 5,
        color: track.color,
        src: blobUrl,
        volume: 1,
      };

      if (isVideo) {
        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.onloadedmetadata = () => {
          setClips((prev) =>
            prev.map((c) =>
              c.id === newClip.id ? { ...c, duration: vid.duration } : c,
            ),
          );
        };
        vid.src = blobUrl;
      } else {
        const aud = document.createElement("audio");
        aud.preload = "metadata";
        aud.onloadedmetadata = () => {
          setClips((prev) =>
            prev.map((c) =>
              c.id === newClip.id ? { ...c, duration: aud.duration } : c,
            ),
          );
        };
        aud.src = blobUrl;
      }

      setClips((prev) => [...prev, newClip]);
      setSelectedClipId(newClip.id);

      // Upload to Storage + insert into video_project_assets in background
      (async () => {
        const _t0 = performance.now();
        console.log(
          `[Atlas][upload] START "${file.name}" ${(file.size / 1e6).toFixed(1)}MB (${file.type || "?"})`,
        );
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          const asset = await uploadProjectAsset({
            file,
            projectId,
            companyId,
            userId: user?.id,
          });
          console.log(
            `[Atlas][upload] DONE en ${((performance.now() - _t0) / 1000).toFixed(1)}s → storagePath=${asset.storage_path}`,
          );
          // Attach the asset id and storage path back to the clip
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? { ...c, assetId: asset.id, storagePath: asset.storage_path }
                : c,
            ),
          );
          // Mostrarlo al toque en el panel Archivos (Multimedia/Audio) sin
          // tener que salir y volver a entrar a la pill. OJO: usamos un blob URL
          // PROPIO (no el del clip), porque al borrar el clip de la timeline se
          // revoca su `src` — si lo compartiéramos, la miniatura de Multimedia
          // quedaría rota (ERR_FILE_NOT_FOUND).
          setAssetPreviewUrls((prev) => ({
            ...prev,
            [asset.id]: URL.createObjectURL(file),
          }));
          setLibraryAssets((prev) =>
            prev.some((a) => a.id === asset.id) ? prev : [asset, ...prev],
          );
        } catch (err) {
          console.error(
            `[Atlas][upload] FALLÓ tras ${((performance.now() - _t0) / 1000).toFixed(1)}s:`,
            err,
          );
          // OJO: el clip ya está en la línea de tiempo (se agrega antes de
          // subir, para que se vea al toque). Si la subida falla, queda ahí
          // apuntando a un blob que NO sobrevive un reload. Avisamos, pero no
          // lo sacamos solo: el usuario puede haberlo movido o recortado ya.
          setUploadErrorMsg(describeUploadError(err, file.name));
        }
      })();
    },
    [clips, projectId, companyId],
  );

  // ── AI generate (placeholder) ─────────────────────────────────────────────
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    await new Promise((res) => setTimeout(res, 3000));

    const aiTrack = getTrackForClipType("ai-generated");
    const newClip: TimelineClip = {
      id: generateId(),
      type: "ai-generated",
      name: `IA: ${aiPrompt.slice(0, 15)}...`,
      startTime: getNextStartTime(clips, aiTrack),
      duration: parseInt(aiDuration),
      color: aiTrack.color,
      volume: 1,
    };

    setClips((prev) => [...prev, newClip]);
    setSelectedClipId(newClip.id);
    setAiGenerating(false);
    setAiPrompt("");
  };

  // ── Add text clip ─────────────────────────────────────────────────────────
  // Creates a new text clip on the elements track at the current playhead
  // (or end-of-track if the playhead overlaps existing clips). Returns the
  // new clip id so callers (agent tool) can reference it.
  const addTextClip = useCallback(
    (
      opts?: Partial<{
        text: string;
        fontFamily: string;
        fontSizePct: number;
        fontWeight: "normal" | "bold";
        fontStyle: "normal" | "italic";
        textColor: string;
        textAlign: "left" | "center" | "right";
        textXPct: number;
        textYPct: number;
        // Optional % of canvas width the text box should occupy. When set, the
        // text wraps inside a fixed-width box instead of auto-sizing to the
        // content. Used by the subtitle generator to make captions span most
        // of the canvas.
        textWidthPct: number;
        startTime: number;
        duration: number;
        enterAnimation: string;
        enterDurationSec: number;
        exitAnimation: string;
        exitDurationSec: number;
        // Subtitle group fields. When `isSubtitle` is true the caller should also
        // pass a stable `subtitleGroupId` so that all clips created in the same
        // batch can be style-synced together by the toolbar later on.
        isSubtitle: boolean;
        subtitleGroupId: string;
        // Linked-subtitle authoring: when set, the created text clip will track
        // its source media/video clip on every move/resize/trim (see the
        // reconciler useEffect). `linkedMediaStart/End` are in *source-media*
        // seconds (original file timeline), NOT timeline seconds.
        linkedClipId: string;
        linkedMediaStart: number;
        linkedMediaEnd: number;
        // When true, the newly created clip will not be auto-selected. Useful
        // when adding many clips in a loop (subtitle batch) to avoid hijacking
        // the user's current selection.
        skipSelect: boolean;
      }>,
    ): string => {
      const textTrack = getTrackForClipType("text");
      const text = (opts?.text ?? DEFAULT_TEXT_CLIP.text).slice(0, 500);
      const startTime =
        typeof opts?.startTime === "number"
          ? Math.max(0, opts.startTime)
          : getNextStartTime(clips, textTrack);
      const duration =
        typeof opts?.duration === "number"
          ? Math.max(0.5, Math.min(60, opts.duration))
          : DEFAULT_TEXT_CLIP.duration;
      const id = generateId();
      const newClip: TimelineClip = {
        id,
        type: "text",
        name: text.length > 20 ? text.slice(0, 17) + "…" : text || "Texto",
        startTime,
        duration,
        color: textTrack.color,
        text,
        fontFamily: opts?.fontFamily ?? DEFAULT_TEXT_CLIP.fontFamily,
        fontSizePct: Math.max(
          2,
          Math.min(40, opts?.fontSizePct ?? DEFAULT_TEXT_CLIP.fontSizePct),
        ),
        fontWeight: opts?.fontWeight ?? DEFAULT_TEXT_CLIP.fontWeight,
        fontStyle: opts?.fontStyle ?? DEFAULT_TEXT_CLIP.fontStyle,
        textColor: opts?.textColor ?? DEFAULT_TEXT_CLIP.textColor,
        textAlign: opts?.textAlign ?? DEFAULT_TEXT_CLIP.textAlign,
        textXPct: Math.max(
          0,
          Math.min(100, opts?.textXPct ?? DEFAULT_TEXT_CLIP.textXPct),
        ),
        textYPct: Math.max(
          0,
          Math.min(100, opts?.textYPct ?? DEFAULT_TEXT_CLIP.textYPct),
        ),
        enterAnimation: opts?.enterAnimation ?? "none",
        enterDurationSec:
          typeof opts?.enterDurationSec === "number"
            ? Math.max(0, Math.min(10, opts.enterDurationSec))
            : 0.5,
        exitAnimation: opts?.exitAnimation ?? "none",
        exitDurationSec:
          typeof opts?.exitDurationSec === "number"
            ? Math.max(0, Math.min(10, opts.exitDurationSec))
            : 0.5,
        ...(typeof opts?.textWidthPct === "number"
          ? { textWidthPct: Math.max(5, Math.min(100, opts.textWidthPct)) }
          : {}),
        ...(opts?.isSubtitle ? { isSubtitle: true } : {}),
        ...(opts?.subtitleGroupId
          ? { subtitleGroupId: opts.subtitleGroupId }
          : {}),
        ...(opts?.linkedClipId &&
        typeof opts.linkedMediaStart === "number" &&
        typeof opts.linkedMediaEnd === "number"
          ? {
              linkedClipId: opts.linkedClipId,
              linkedMediaStart: Math.max(0, opts.linkedMediaStart),
              linkedMediaEnd: Math.max(
                opts.linkedMediaStart,
                opts.linkedMediaEnd,
              ),
            }
          : {}),
      };
      setClips((prev) => [...prev, newClip]);
      if (!opts?.skipSelect) setSelectedClipId(id);
      return id;
    },
    [clips],
  );

  // ── Subtítulos ───────────────────────────────────────────────────────────
  // Kick off the transcription flow for a given clip: resolve the clip's
  // signed URL, POST it (plus the current user id) to the n8n webhook,
  // chunk the returned word stream and open the modal so the user can
  // review the full transcript and confirm adding subtitle clips.
  //
  // The caller is expected to have already verified that the clip is
  // transcribable (video / audio / ai-generated with a reachable source).
  // We still double-check here so the UI can surface a helpful error
  // instead of silently calling the webhook with an empty URL.
  const openSubtitlesModalForClip = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      // We intentionally do NOT open the modal here anymore. The generation
      // UI lives directly on top of the source clip in the timeline (shimmer
      // + "Generando subtítulos" overlay). The modal is only surfaced below
      // in the error branch, so the user can still see details + retry.
      setSubtitlesSourceClipId(clipId);
      setSubtitlesLoading(true);
      setSubtitlesError(null);
      setSubtitlesResult(null);
      try {
        // 1) Resolve a fresh signed URL. Prefer the asset's storage path
        //    (long-lived row in the DB → always re-signable) and fall back
        //    to the in-memory `src` only if the clip was never persisted.
        let url: string | null = null;
        if (clip.storagePath) {
          url = await getAssetSignedUrl(clip.storagePath);
        }
        if (
          !url &&
          typeof clip.src === "string" &&
          /^https?:\/\//.test(clip.src)
        ) {
          url = clip.src;
        }
        if (!url)
          throw new Error(
            "No pudimos obtener la URL del clip. Probá volver a subir el archivo.",
          );

        // 2) Resolve the current user id so the webhook can attribute the
        //    transcription. Fail loudly if there's no session — the user
        //    should always be authenticated inside the editor.
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user?.id) throw new Error("No hay una sesión activa.");

        // 3) Fire the POST. We don't retry automatically because the
        //    webhook is slow (ElevenLabs transcription) and users benefit
        //    from seeing the error immediately and deciding what to do.
        const tr = await requestTranscript(url, user.id);
        if (!tr.ok) throw new Error(tr.message);
        const transcript = tr.transcript;
        const segments = chunkWordsIntoSubtitles(transcript.words);
        setSubtitlesResult({ transcript, segments });
        // Cache the full transcript on the source clip itself. This makes it
        // immediately available to the agent (`get_clip_transcript`) without a
        // second webhook call, and survives splits/trims because consumers
        // filter words by the clip's visible window at read-time.
        setClips((prev) =>
          prev.map((c) => (c.id === clipId ? { ...c, transcript } : c)),
        );
      } catch (err) {
        console.error("[VideoEditor] subtitle generation failed", err);
        setSubtitlesError(
          err instanceof Error
            ? err.message
            : "No pudimos generar los subtítulos.",
        );
        // Pop the modal only on failure so the user can read the message
        // and trigger the retry flow.
        setSubtitlesModalOpen(true);
      } finally {
        setSubtitlesLoading(false);
      }
    },
    [clips],
  );

  // Apply the currently computed subtitle segments to the timeline as
  // individual text clips anchored to `subtitlesSourceClipId`'s
  // startTime. We default them to a centered, bold, white style placed
  // near the bottom of the frame — the user can restyle afterwards
  // using the regular text-clip toolbar.
  const applySubtitlesToTimeline = useCallback(() => {
    if (!subtitlesResult || subtitlesResult.segments.length === 0) return;
    const baseClip = subtitlesSourceClipId
      ? clips.find((c) => c.id === subtitlesSourceClipId)
      : null;
    const base = baseClip?.startTime ?? 0;
    // Segment timestamps come from the transcription service in
    // *source-media* seconds (absolute to the original file). To map them
    // onto the timeline we subtract the source clip's mediaOffset (0 for
    // non-split clips). This also lets us later re-derive the timeline
    // position whenever the source clip is trimmed/moved, using the stored
    // linkedMediaStart/End values.
    const srcMediaOffset = baseClip?.mediaOffset ?? 0;
    const srcDuration = baseClip?.duration ?? Infinity;
    // One groupId per "generate subtitles" run so style edits to this batch
    // never leak into a previous batch the user may have already styled.
    const groupId = generateId();
    // Segments come from the transcription service anchored to the spoken
    // word boundaries, so two consecutive captions leave a silence gap
    // between them (breaths, commas, word-final consonants, hedges). We
    // close that gap by extending each clip up to the next segment's
    // start, capped so actual long silences between sentences/paragraphs
    // don't get absorbed into the previous caption. The cap is generous
    // on purpose — short pauses should feel seamless; only genuinely
    // long pauses (>1.2s) remain as visible silence.
    const MAX_HOLD_EXTEND_SEC = 1.2;
    const segs = subtitlesResult.segments;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i];
      const nextStart = i + 1 < segs.length ? segs[i + 1].start : seg.end;
      const heldEnd = Math.min(nextStart, seg.end + MAX_HOLD_EXTEND_SEC);
      // Convert source-media seconds to the source clip's *local* seconds
      // (i.e. seconds from the source clip's visible start) by subtracting
      // mediaOffset, then clamp to the visible window. Captions whose
      // entire range falls outside the trimmed window are skipped.
      const localStart = Math.max(
        0,
        Math.min(srcDuration, seg.start - srcMediaOffset),
      );
      const localEnd = Math.max(
        0,
        Math.min(srcDuration, heldEnd - srcMediaOffset),
      );
      const segDur = Math.max(0.3, localEnd - localStart);
      if (localEnd <= 0 || localStart >= srcDuration) continue;
      addTextClip({
        text: seg.text,
        startTime: base + localStart,
        duration: segDur,
        linkedClipId: subtitlesSourceClipId ?? undefined,
        linkedMediaStart: seg.start,
        linkedMediaEnd: heldEnd,
        textAlign: "center",
        textXPct: 50,
        textYPct: 85,
        fontWeight: "bold",
        textColor: "#FFFFFF",
        fontSizePct: 4,
        // Take up almost the full canvas width so long lines wrap nicely
        // instead of overflowing or getting auto-shrunk. Leaves a small
        // safe-area margin on each side.
        textWidthPct: 90,
        isSubtitle: true,
        subtitleGroupId: groupId,
        skipSelect: true,
      });
    }
    setSubtitlesModalOpen(false);
    setSubtitlesResult(null);
    setSubtitlesSourceClipId(null);
  }, [subtitlesResult, subtitlesSourceClipId, clips, addTextClip]);

  // Delta-based linked-subtitle reconciler. Each render we snapshot every
  // source clip's "anchor" (startTime + mediaOffset). On the *next* render,
  // if a source clip's anchor has shifted we translate every caption
  // linked to it by the same timeline delta — which keeps subtitles glued
  // to the source when the source is moved or left-trimmed, *without*
  // overwriting the user's own edits to the subtitle (move/trim/resize on
  // its own track). Left-trims propagate naturally because trimming from
  // the left increments both startTime and mediaOffset by the same amount;
  // the delta we apply is `Δ startTime`, so subtitles follow. Right-trims
  // only change `duration`, not the anchor, so subtitles stay put — the
  // user can manually re-time them if desired.
  // IMPORTANT: we deliberately compute the anchor diff *outside* of the
  // `setClips` updater and only invoke `setClips` when we actually need to
  // shift subtitles. Mutating `sourceAnchorsRef.current` inside the updater
  // is unsafe under React 18 StrictMode, which invokes state updaters twice
  // to surface side effects — on the second pass the ref would already be
  // updated, `delta` would collapse to 0, and the shifted-subtitles result
  // would be thrown away (the regression the user hit: captions refusing to
  // follow their source clip on drag).
  const sourceAnchorsRef = useRef<
    Map<string, { startTime: number; mediaOffset: number }>
  >(new Map());
  useEffect(() => {
    const byId = new Map(clips.map((c) => [c.id, c] as const));
    const prevAnchors = sourceAnchorsRef.current;
    const nextAnchors = new Map<
      string,
      { startTime: number; mediaOffset: number }
    >();
    // Only track anchors for source clips that actually have at least one
    // linked caption; avoids growing the map for every clip on the board.
    const linkedSourceIds = new Set<string>();
    for (const c of clips)
      if (c.linkedClipId) linkedSourceIds.add(c.linkedClipId);
    for (const id of linkedSourceIds) {
      const src = byId.get(id);
      if (src)
        nextAnchors.set(id, {
          startTime: src.startTime,
          mediaOffset: src.mediaOffset ?? 0,
        });
    }
    // Collect per-source deltas ahead of time so the updater below stays
    // pure (reads only from `prev` + the captured `deltas` map).
    const deltas = new Map<string, number>();
    for (const [id, nextA] of nextAnchors) {
      const prevA = prevAnchors.get(id);
      if (!prevA) continue; // first time we see this source — nothing to sync.
      const d = nextA.startTime - prevA.startTime;
      if (Math.abs(d) >= 1e-6) deltas.set(id, d);
    }
    // Update the ref unconditionally so the next render's diff is correct
    // — whether or not we actually schedule a subtitle shift this tick.
    sourceAnchorsRef.current = nextAnchors;
    if (deltas.size === 0) return;
    setClips((prev) =>
      prev.map((c) => {
        if (!c.linkedClipId) return c;
        const d = deltas.get(c.linkedClipId);
        if (d === undefined) return c;
        return { ...c, startTime: Math.max(0, c.startTime + d) };
      }),
    );
  }, [clips]);

  // Drop linked subtitles when their source clip is removed so we don't
  // leave orphan captions floating on the timeline.
  useEffect(() => {
    setClips((prev) => {
      const ids = new Set(prev.map((c) => c.id));
      const filtered = prev.filter(
        (c) => !c.linkedClipId || ids.has(c.linkedClipId),
      );
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [clips]);

  // Auto-apply transcription as soon as the webhook returns a usable result
  // so the user skips the intermediate "review the analysis" screen. Errors
  // and the loading spinner still render inside the modal because they're
  // the only states that actually need user attention.
  useEffect(() => {
    if (!subtitlesResult) return;
    if (subtitlesLoading) return;
    if (subtitlesError) return;
    if (subtitlesResult.segments.length === 0) return;
    applySubtitlesToTimeline();
  }, [
    subtitlesResult,
    subtitlesLoading,
    subtitlesError,
    applySubtitlesToTimeline,
  ]);

  // ── Agent tool executor ────────────────────────────────────────────────
  // Receives a tool name + parsed input, returns a {content, is_error?} shape.
  // Content is normally a string (JSON or plain text). For tools that need to
  // surface images to Claude (e.g. analyze_video_quick) it can also be an
  // array of content blocks (text + image) compatible with Anthropic's
  // tool_result.content format.
  const executeAgentTool = useCallback(
    async (
      name: string,
      input: any,
    ): Promise<{ content: string | any[]; is_error?: boolean }> => {
      const toolMessage = (
        label: string,
        Icon?: React.ComponentType<{ className?: string }>,
        hint?: string,
      ) => {
        const ts = new Date().toISOString();
        setAgentMessages((prev) => [
          ...prev,
          {
            role: "tool",
            text: label,
            icon: Icon,
            createdAt: ts,
            startedAt: Date.now(),
            hint,
          },
        ]);
        // Persist to DB so the tool-use timeline survives reloads. Fire and
        // forget — failures only affect history reconstruction, not the
        // current session.
        if (projectId && companyId) {
          const iconKey = keyForToolIcon(Icon);
          supabase
            .from("agent_messages")
            .insert({
              project_id: projectId,
              id_company: companyId,
              role: "tool",
              created_at: ts,
              content: {
                type: "tool",
                text: label,
                ...(iconKey ? { iconKey } : {}),
              },
            })
            .then(({ error }) => {
              if (error) console.error("insert tool msg failed", error);
            });
        }
      };
      // If the agent passes a generated-image id where an assetId is expected
      // (ref / firstFrame / lastFrame), import it as a project asset on the fly
      // so the panel preview works and the generation webhook receives a valid
      // assetId. Returns null if the id can't be resolved (e.g. the generated
      // image is still loading). A normal assetId is returned unchanged.
      const resolveRefIdToAssetId = async (
        rawId: string,
      ): Promise<string | null> => {
        const id = String(rawId || "").trim();
        if (!id) return null;
        const gen = imageGenResults.find((r) => r.id === id);
        if (!gen) return id; // not a generated image → assume it's already an assetId
        if (!gen.signedUrl) {
          console.warn(
            "[agent] skipping generated image ref — not ready yet:",
            id,
          );
          return null;
        }
        if (!projectId || !companyId) return null;
        try {
          const resp = await fetch(gen.signedUrl);
          const blob = await resp.blob();
          const ext = (gen.fileName?.split(".").pop() || "jpg").toLowerCase();
          const file = new File(
            [blob],
            gen.fileName || `ai-${Date.now()}.${ext}`,
            { type: blob.type || "image/jpeg" },
          );
          const {
            data: { user },
          } = await supabase.auth.getUser();
          const asset = await uploadProjectAsset({
            file,
            projectId,
            companyId,
            userId: user?.id,
          });
          const blobUrl = URL.createObjectURL(blob);
          setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
          return asset.id;
        } catch (err) {
          console.error(
            "[agent] failed to import generated image as asset",
            id,
            err,
          );
          return null;
        }
      };
      // En paralelo, no en fila: cada resolución puede descargar y volver a
      // subir una imagen entera, así que con 3 referencias la versión
      // secuencial sumaba las tres esperas. `Promise.all` preserva el orden
      // del input, igual que el `for` que reemplaza.
      const resolveRefIdList = async (raw: unknown): Promise<string[]> => {
        if (!Array.isArray(raw)) return [];
        const resolved = await Promise.all(
          raw.map((v) => resolveRefIdToAssetId(String(v))),
        );
        return resolved.filter((id): id is string => !!id);
      };
      try {
        switch (name) {
          case "ask_user": {
            const qsIn = Array.isArray(input?.questions) ? input.questions : [];
            const questions: AgentAskQuestion[] = qsIn
              .filter(
                (q: any) =>
                  q &&
                  typeof q.id === "string" &&
                  typeof q.question === "string",
              )
              .map((q: any) => ({
                id: String(q.id),
                question: String(q.question),
                type:
                  q.type === "multi" || q.type === "text" || q.type === "single"
                    ? q.type
                    : "single",
                options: Array.isArray(q.options)
                  ? q.options.map(String)
                  : undefined,
                allowCustom:
                  typeof q.allowCustom === "boolean" ? q.allowCustom : true,
              }));
            if (questions.length === 0)
              return { content: "Sin preguntas válidas", is_error: true };

            // Seed form defaults (empty) for this session.
            const seededForm: Record<string, string | string[]> = {};
            const seededCustom: Record<string, string> = {};
            for (const q of questions) {
              seededForm[q.id] = q.type === "multi" ? [] : "";
              seededCustom[q.id] = "";
            }
            setAskFormState(seededForm);
            setAskCustomState(seededCustom);

            // Activate the floating panel above the input field (not inline).
            setActiveAsk({ questions, currentIndex: 0 });

            // Return a Promise that resolves when the user submits or cancels.
            return await new Promise<{ content: string }>((resolve) => {
              askResolverRef.current = (json: string) =>
                resolve({ content: json });
            });
          }
          case "request_user_upload": {
            const message =
              typeof input?.message === "string" && input.message.trim()
                ? input.message.trim()
                : "Subí uno o más archivos para continuar.";
            const buttonLabel =
              typeof input?.buttonLabel === "string" && input.buttonLabel.trim()
                ? input.buttonLabel.trim()
                : "Listo";
            const acceptKey: string =
              typeof input?.accept === "string" ? input.accept : "image";
            const accept =
              acceptKey === "video"
                ? "video/*"
                : acceptKey === "image+video"
                  ? "image/*,video/*"
                  : "image/*";
            const procesarComoPersonaje = input?.procesar_como_personaje === true;
            toolMessage("Pidiendo archivos al usuario", Upload);
            setAgentUploadedAssets([]);
            setAgentUploadInProgress(false);
            setAgentUploadRequest({
              message,
              buttonLabel,
              accept,
              procesarComoPersonaje,
            });
            setActivePanel("agent-upload");
            // Block until the user confirms or cancels. The panel UI calls
            // uploadResolverRef.current(json) to resolve.
            return await new Promise<{ content: string | any[] }>((resolve) => {
              uploadResolverRef.current = (result) => resolve(result);
            });
          }
          case "request_product_selection": {
            const message =
              typeof input?.message === "string" && input.message.trim()
                ? input.message.trim()
                : "Elegí un producto de tu catálogo.";
            const buttonLabel =
              typeof input?.buttonLabel === "string" && input.buttonLabel.trim()
                ? input.buttonLabel.trim()
                : "Usar selección";
            const multiple = input?.multiple === true;
            if (!companyId && !imageGenCompanyId)
              return { content: "No hay empresa cargada.", is_error: true };
            if (!brandProfile?.id)
              return {
                content: "No hay marca cargada en el proyecto.",
                is_error: true,
              };
            toolMessage(
              "Pidiéndole al usuario que elija un producto",
              Package,
            );
            setAgentProductSelection([]);
            setAgentProductRequest({ message, buttonLabel, multiple });
            setActivePanel("files");
            setFilesTab("productos");
            // Bloquea hasta que el usuario elige (resolver recibe los
            // productos) o cancela (null). El resolver lo dispara la grilla.
            const chosen = await new Promise<MockProductV2[] | null>(
              (resolve) => {
                productPickResolverRef.current = (products) =>
                  resolve(products);
              },
            );
            if (!chosen || chosen.length === 0)
              return { content: JSON.stringify({ cancelled: true }) };
            // Importar cada producto elegido como asset + mostrarlo inline.
            const imported: Array<{
              assetId: string;
              name: string;
              url: string;
            }> = [];
            for (const p of chosen) {
              const assetId = await importProductToImageAsset(p);
              if (assetId)
                imported.push({
                  assetId,
                  name: p.name,
                  url: p.imageUrl,
                });
            }
            if (imported.length === 0)
              return {
                content: "No se pudo importar ningún producto elegido.",
                is_error: true,
              };
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `El usuario eligió ${imported.length} producto(s) de su catálogo (ya importados como assets del proyecto). ` +
                  `Detalle (assetId | nombre): ` +
                  imported
                    .map((i) => `${i.assetId} | ${i.name}`)
                    .join("  ;  ") +
                  `. Usá estos assetIds como firstFrameAssetId / lastFrameAssetId / refAssetIds en prefill_ai_scene o prefill_ai_image. ` +
                  `A continuación las imágenes inline para que las observes.`,
              },
            ];
            for (const i of imported) {
              try {
                const base64 = await urlToBase64Jpeg(i.url);
                blocks.push({
                  type: "text",
                  text: `asset_id=${i.assetId} (${i.name})`,
                });
                blocks.push({
                  type: "image",
                  source: {
                    type: "base64",
                    media_type: "image/jpeg",
                    data: base64,
                  },
                });
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `asset_id=${i.assetId} (${i.name}): no se pudo cargar imagen inline (${err?.message || err}).`,
                });
              }
            }
            return { content: blocks };
          }
          case "get_project_context": {
            const counts: Record<string, number> = {};
            for (const c of clips) counts[c.type] = (counts[c.type] || 0) + 1;
            toolMessage("Leyendo contexto del proyecto", BarChart3);
            return {
              content: JSON.stringify({
                name: projectName,
                duration,
                aspectRatio,
                totalClips: clips.length,
                clipCounts: counts,
              }),
            };
          }
          case "list_timeline_clips": {
            toolMessage("Leyendo clips de la timeline", Video);
            return {
              content: JSON.stringify(
                clips.map((c) => ({
                  id: c.id,
                  type: c.type,
                  name: c.name,
                  startTime: c.startTime,
                  duration: c.duration,
                  trackIndex: c.trackIndex ?? 0,
                  aiPrompt: c.aiPrompt,
                  aiStatus: c.aiStatus,
                })),
              ),
            };
          }
          case "list_project_assets": {
            if (!projectId)
              return { content: "Project not loaded", is_error: true };
            toolMessage("Buscando assets del proyecto", FolderOpen);
            const typeIn = input?.type as
              | "video"
              | "audio"
              | "image"
              | undefined;
            const list = await fetchProjectAssetsByType(projectId, typeIn);
            return {
              content: JSON.stringify(
                list.map((a) => ({
                  id: a.id,
                  name: a.name,
                  type: a.type,
                  duration: (a as any).duration,
                })),
              ),
            };
          }
          case "analyze_video_quick": {
            const assetId: string | undefined =
              typeof input?.assetId === "string" ? input.assetId : undefined;
            const frameCount = Math.max(
              3,
              Math.min(12, Number(input?.frameCount) || 6),
            );
            if (!assetId) return { content: "Falta assetId", is_error: true };
            toolMessage("Análisis rápido del video", Film);
            const assets = await fetchAssetsByIds([assetId]);
            const asset = assets[0];
            if (!asset)
              return { content: "Asset no encontrado", is_error: true };
            if (asset.type !== "video")
              return { content: "El asset no es un video", is_error: true };
            const url = await getAssetSignedUrl(asset.storage_path);
            if (!url)
              return {
                content: "No se pudo firmar URL del video",
                is_error: true,
              };
            let frames: { tSec: number; base64: string }[] = [];
            try {
              // Reloj: la extracción usa un <video> oculto + seek; si un
              // evento (loadedmetadata/seeked) nunca dispara (video no
              // seekable, CORS, compu dormida), colgaría el turno para siempre.
              frames = await withTimeout(
                extractVideoFrames(url, frameCount),
                60_000,
                "extracción de frames del video",
              );
            } catch (err: any) {
              return {
                content: `No se pudieron extraer frames: ${err?.message || err}`,
                is_error: true,
              };
            }
            if (frames.length === 0)
              return { content: "No se obtuvieron frames", is_error: true };
            // Build a tool_result content array with a header text + image blocks
            // (one per frame, with its timestamp inlined as a label).
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `Frames extraídos del video "${asset.name || assetId}" (duración aprox. ` +
                  `${asset.duration_seconds ? asset.duration_seconds.toFixed(1) : "?"}s). ` +
                  `Se muestran ${frames.length} frames distribuidos uniformemente. ` +
                  `Timestamps (s): ${frames.map((f) => f.tSec.toFixed(2)).join(", ")}.`,
              },
            ];
            for (const f of frames) {
              blocks.push({ type: "text", text: `t=${f.tSec.toFixed(2)}s` });
              blocks.push({
                type: "image",
                source: {
                  type: "base64",
                  media_type: "image/jpeg",
                  data: f.base64,
                },
              });
            }
            return { content: blocks };
          }
          case "analyze_media_deep": {
            const ids: string[] = Array.isArray(input?.assetIds)
              ? input.assetIds.filter((x: any) => typeof x === "string")
              : [];
            const prompt: string =
              typeof input?.prompt === "string" ? input.prompt.trim() : "";
            if (ids.length === 0)
              return { content: "Falta assetIds", is_error: true };
            if (!prompt) return { content: "Falta prompt", is_error: true };
            toolMessage(
              "Analizando en profundidad",
              Search,
              "análisis con IA — puede tardar",
            );
            const urls = await getSignedUrlsForAssetIds(ids);
            if (urls.length === 0)
              return {
                content: "No se pudieron resolver URLs firmadas.",
                is_error: true,
              };
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (!user) return { content: "Sin sesión activa", is_error: true };
            try {
              // Atlas backend (ex analizar_video_o_audio) → /v1/media/analyze con
              // JWT; fallback n8n. Video largo puede venir 202 → poleamos el job.
              if (USE_ATLAS_BACKEND) {
                const bkRes = await atlasBackendFetch("/v1/media/analyze", {
                  user_id: user.id,
                  asset_ids: ids,
                  urls,
                  prompt,
                });
                const pick = (out: unknown): string =>
                  out &&
                  typeof out === "object" &&
                  typeof (out as { response?: unknown }).response === "string"
                    ? (out as { response: string }).response
                    : out
                      ? JSON.stringify(out)
                      : "";
                if (bkRes.status === 202) {
                  const pending = (await bkRes.json().catch(() => ({}))) as {
                    job_id?: string;
                  };
                  if (!pending.job_id)
                    return { content: "Analyze devolvió 202 sin job_id", is_error: true };
                  const settled = await pollJobUntilSettled(pending.job_id, {
                    timeoutMs: 5 * 60 * 1000,
                  });
                  if (!settled)
                    return { content: "El análisis tardó demasiado (timeout).", is_error: true };
                  if (settled.status === "failed")
                    return {
                      content: settled.userMessage || "El análisis falló.",
                      is_error: true,
                    };
                  return { content: pick(settled.output) };
                }
                if (!bkRes.ok) {
                  if (bkRes.status === 402) {
                    return {
                      content:
                        "No te alcanzan los créditos para analizar este medio. Sumá créditos o actualizá tu plan.",
                      is_error: true,
                    };
                  }
                  const txt = await bkRes.text().catch(() => "");
                  return {
                    content: `Analyze HTTP ${bkRes.status}: ${txt.slice(0, 500)}`,
                    is_error: true,
                  };
                }
                return { content: pick(await bkRes.json().catch(() => null)) };
              }
              const res = await fetch(
                "https://ai-agency-dt7d.onrender.com/webhook/analizar_video_o_audio",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: GENERATE_AUTH_TOKEN,
                  },
                  body: JSON.stringify({
                    user_id: user.id,
                    asset_ids: ids,
                    urls,
                    prompt,
                  }),
                },
              );
              if (!res.ok) {
                const txt = await res.text().catch(() => "");
                return {
                  content: `Webhook HTTP ${res.status}: ${txt.slice(0, 500)}`,
                  is_error: true,
                };
              }
              const txt = await res.text();
              // The webhook returns { response: "<gemini answer>" }. Fall back to
              // the raw payload if the shape is different so we never lose info.
              try {
                const json = JSON.parse(txt);
                if (
                  json &&
                  typeof json === "object" &&
                  typeof (json as any).response === "string"
                ) {
                  return { content: (json as any).response };
                }
                return {
                  content:
                    typeof json === "string" ? json : JSON.stringify(json),
                };
              } catch {
                return { content: txt };
              }
            } catch (err: any) {
              return {
                content: `Error contactando webhook: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "generate_transcript": {
            const clipId: string | undefined =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            const force = input?.force === true;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            const clip = clips.find((c) => c.id === clipId);
            if (!clip)
              return {
                content: `Clip ${clipId} no encontrado en la timeline`,
                is_error: true,
              };
            const isTranscribable =
              clip.type === "video" ||
              clip.type === "audio" ||
              (clip.type === "ai-generated" && clip.aiStatus === "ready");
            if (!isTranscribable) {
              return {
                content: `El clip "${clip.name}" (tipo=${clip.type}, status=${clip.aiStatus ?? "n/a"}) no se puede transcribir. Soporta video, audio, o ai-generated con audio listo.`,
                is_error: true,
              };
            }
            // Cache hit: skip the network round-trip entirely.
            if (
              !force &&
              clip.transcript &&
              Array.isArray(clip.transcript.words) &&
              clip.transcript.words.length > 0
            ) {
              toolMessage("Transcript ya cacheado", Captions);
              const wordCount = clip.transcript.words.filter(
                (w) => w.type === "word",
              ).length;
              return {
                content: JSON.stringify({
                  clipId,
                  cached: true,
                  language: clip.transcript.language_code,
                  wordCount,
                  durationSecs:
                    clip.transcript.audio_duration_secs ??
                    clip.sourceDuration ??
                    clip.duration,
                  preview: clip.transcript.text?.slice(0, 240) ?? "",
                  hint: "Usá get_clip_transcript para leer SÓLO la ventana visible (post-trim) o get_timeline_transcript para todo el proyecto.",
                }),
              };
            }
            toolMessage(
              "Transcribiendo clip",
              Captions,
              "transcribiendo — puede tardar ~1 min",
            );
            // Resolve a fresh signed URL the same way the subtitle generator
            // does — prefer the persisted storagePath, fall back to in-memory src.
            let url: string | null = null;
            if (clip.storagePath) {
              try {
                url = await getAssetSignedUrl(clip.storagePath);
              } catch {}
            }
            if (
              !url &&
              typeof clip.src === "string" &&
              /^https?:\/\//.test(clip.src)
            ) {
              url = clip.src;
            }
            if (!url)
              return {
                content: "No pudimos obtener la URL del clip para transcribir.",
                is_error: true,
              };
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (!user?.id)
              return { content: "No hay una sesión activa.", is_error: true };
            try {
              const tr = await requestTranscript(url, user.id);
              if (!tr.ok) return { content: tr.message, is_error: true };
              const transcript = tr.transcript;
              // Persist on the source clip so future calls (and split halves)
              // can read it without re-transcribing.
              setClips((prev) =>
                prev.map((c) => (c.id === clipId ? { ...c, transcript } : c)),
              );
              // Sync el ref AHORA para que un autocut inmediato (mismo turno del
              // agente) vea el transcript sin esperar el re-render de React.
              clipsRef.current = clipsRef.current.map((c) =>
                c.id === clipId ? { ...c, transcript } : c,
              );
              const wordCount = transcript.words.filter(
                (w) => w.type === "word",
              ).length;
              return {
                content: JSON.stringify({
                  clipId,
                  cached: false,
                  language: transcript.language_code,
                  wordCount,
                  durationSecs:
                    transcript.audio_duration_secs ??
                    clip.sourceDuration ??
                    clip.duration,
                  preview: transcript.text?.slice(0, 240) ?? "",
                  hint: "Para leer la ventana visible llamá get_clip_transcript; para todo el proyecto, get_timeline_transcript.",
                }),
              };
            } catch (err: any) {
              return {
                content: `Error contactando webhook de transcripción: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "get_clip_transcript": {
            const clipId: string | undefined =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            const clip = clips.find((c) => c.id === clipId);
            if (!clip)
              return {
                content: `Clip ${clipId} no encontrado en la timeline`,
                is_error: true,
              };
            const t = clip.transcript;
            if (!t || !Array.isArray(t.words)) {
              return {
                content: `El clip "${clip.name}" todavía no tiene transcript guardado. Llamá primero a generate_transcript con clipId="${clipId}".`,
                is_error: true,
              };
            }
            toolMessage("Leyendo transcript del clip", Captions);
            const offset = clip.mediaOffset ?? 0;
            const winStart = offset;
            const winEnd = offset + clip.duration;
            // Pure word entries inside the visible window. We use overlap
            // (`end > winStart && start < winEnd`) so a word straddling the
            // edge still appears — the agent generally cares "did the user
            // say X within this trim", not strict containment.
            const visible = t.words.filter(
              (w) =>
                w &&
                w.type === "word" &&
                typeof w.start === "number" &&
                typeof w.end === "number" &&
                w.end > winStart &&
                w.start < winEnd,
            );
            // Remap to timeline-relative seconds so the agent can quote
            // timestamps that match what list_timeline_clips returns.
            const timelineWords = visible.map((w) => ({
              word: w.text,
              start: Math.max(
                clip.startTime,
                clip.startTime + (w.start - offset),
              ),
              end: Math.min(
                clip.startTime + clip.duration,
                clip.startTime + (w.end - offset),
              ),
            }));
            const text = timelineWords
              .map((w) => w.word)
              .join(" ")
              .replace(/\s+/g, " ")
              .trim();
            return {
              content: JSON.stringify({
                clipId,
                name: clip.name,
                startTime: clip.startTime,
                endTime: clip.startTime + clip.duration,
                durationVisible: clip.duration,
                language: t.language_code,
                text,
                words: timelineWords,
              }),
            };
          }
          case "get_timeline_transcript": {
            const includeMuted = input?.includeMuted === true;
            toolMessage("Leyendo transcript de la timeline", Captions);
            // Only audible-source clips contribute. Subtitles are derivative
            // (text clips) and would double-count, so we skip them.
            const candidates = clips
              .filter(
                (c) =>
                  c.type === "video" ||
                  c.type === "audio" ||
                  (c.type === "ai-generated" && c.aiStatus === "ready"),
              )
              .filter((c) => includeMuted || (c.volume ?? 1) > 0)
              .sort((a, b) => a.startTime - b.startTime);
            const segments: any[] = [];
            const missing: string[] = [];
            for (const clip of candidates) {
              const t = clip.transcript;
              if (!t || !Array.isArray(t.words)) {
                missing.push(clip.id);
                continue;
              }
              const offset = clip.mediaOffset ?? 0;
              const winStart = offset;
              const winEnd = offset + clip.duration;
              const visible = t.words.filter(
                (w) =>
                  w &&
                  w.type === "word" &&
                  typeof w.start === "number" &&
                  typeof w.end === "number" &&
                  w.end > winStart &&
                  w.start < winEnd,
              );
              if (visible.length === 0) continue;
              const timelineWords = visible.map((w) => ({
                word: w.text,
                start: Math.max(
                  clip.startTime,
                  clip.startTime + (w.start - offset),
                ),
                end: Math.min(
                  clip.startTime + clip.duration,
                  clip.startTime + (w.end - offset),
                ),
              }));
              const text = timelineWords
                .map((w) => w.word)
                .join(" ")
                .replace(/\s+/g, " ")
                .trim();
              segments.push({
                clipId: clip.id,
                clipName: clip.name,
                startTime: clip.startTime,
                endTime: clip.startTime + clip.duration,
                text,
                words: timelineWords,
              });
            }
            const fullText = segments
              .map((s) => s.text)
              .join(" ")
              .replace(/\s+/g, " ")
              .trim();
            return {
              content: JSON.stringify({
                segments,
                fullText,
                missingTranscriptClipIds: missing,
                hint:
                  missing.length > 0
                    ? `Hay ${missing.length} clip(s) audibles sin transcript todavía. Llamá generate_transcript en cada uno si querés cobertura completa.`
                    : undefined,
              }),
            };
          }
          case "list_generated_images": {
            const limit = Math.max(1, Math.min(20, Number(input?.limit) || 12));
            // Take newest first; skip still-loading entries without a URL.
            const ready = imageGenResults
              .filter((r) => !!r.signedUrl)
              .slice(0, limit);
            if (ready.length === 0) {
              return {
                content:
                  "El proyecto todavía no tiene imágenes generadas (o ninguna terminó de cargar).",
              };
            }
            toolMessage(
              `Leyendo ${ready.length} imagen(es) generada(s)`,
              ImageIcon,
            );
            // Fetch prompt + created_at for these IDs so the agent can pick by
            // semantic meaning / recency without having to analyze every image
            // visually. One query, tolerant to missing rows.
            const metaById = new Map<
              string,
              { prompt?: string; createdAt?: string }
            >();
            try {
              // Fetch en lotes para no exceder el largo de URL del gateway REST
              // con muchos resultados (HTTP 400). Ver utils/supabaseChunkedIn.
              const metaRows = await fetchInChunks<any>(
                ready.map((r) => r.id),
                (chunk) =>
                  supabase
                    .from("imagenes_generadas")
                    .select("id, prompt, created_at")
                    .in("id", chunk),
              );
              if (Array.isArray(metaRows)) {
                for (const row of metaRows as Array<{
                  id: string;
                  prompt: string | null;
                  created_at: string | null;
                }>) {
                  metaById.set(row.id, {
                    prompt: row.prompt || undefined,
                    createdAt: row.created_at || undefined,
                  });
                }
              }
            } catch (err) {
              console.warn(
                "[list_generated_images] failed to fetch metadata",
                err,
              );
            }
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `Imágenes generadas con IA guardadas en el proyecto (${ready.length} de ${imageGenResults.length}). ` +
                  `IDs en orden (más nuevas primero): ${ready.map((r) => r.id).join(", ")}.`,
              },
            ];
            for (const r of ready) {
              let base64 = "";
              try {
                base64 = await urlToBase64Jpeg(r.signedUrl!);
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `imagen ${r.id}: no se pudo cargar (${err?.message || err})`,
                });
                continue;
              }
              const meta = metaById.get(r.id);
              const header = [`imagen_id=${r.id}`];
              if (meta?.createdAt) header.push(`generada=${meta.createdAt}`);
              if (meta?.prompt)
                header.push(`prompt=${JSON.stringify(meta.prompt)}`);
              blocks.push({ type: "text", text: header.join(" | ") });
              blocks.push({
                type: "image",
                source: {
                  type: "base64",
                  media_type: "image/jpeg",
                  data: base64,
                },
              });
            }
            return { content: blocks };
          }
          case "import_generated_image_as_asset": {
            const genId: string | undefined =
              typeof input?.generatedImageId === "string"
                ? input.generatedImageId
                : undefined;
            if (!genId)
              return { content: "Falta generatedImageId", is_error: true };
            if (!projectId || !companyId)
              return { content: "Project not loaded", is_error: true };
            const result = imageGenResults.find((r) => r.id === genId);
            if (!result)
              return {
                content: `No hay imagen generada con id ${genId} en este proyecto.`,
                is_error: true,
              };
            if (!result.signedUrl)
              return {
                content: "La imagen todavía no terminó de generarse.",
                is_error: true,
              };
            toolMessage("Importando imagen generada como asset", ImageIcon);
            try {
              const resp = await fetch(result.signedUrl);
              const blob = await resp.blob();
              const ext = (
                result.fileName?.split(".").pop() || "jpg"
              ).toLowerCase();
              const file = new File(
                [blob],
                result.fileName || `ai-${Date.now()}.${ext}`,
                { type: blob.type || "image/jpeg" },
              );
              const {
                data: { user },
              } = await supabase.auth.getUser();
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              const blobUrl = URL.createObjectURL(blob);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              return {
                content: JSON.stringify({
                  assetId: asset.id,
                  name: asset.name,
                  type: "image",
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo importar la imagen: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "list_products": {
            const query =
              typeof input?.query === "string"
                ? input.query.trim().toLowerCase()
                : "";
            const cid = companyId ?? imageGenCompanyId;
            if (!cid || !brandProfile?.id)
              return {
                content: "No hay marca/empresa cargada en el proyecto.",
                is_error: true,
              };
            toolMessage("Buscando productos del catálogo", Package);
            try {
              const catalog = await fetchUserProductsByBrand(
                cid,
                brandProfile.id,
              );
              const withImg = catalog.filter((p) => p.imageUrl);
              const filtered = query
                ? withImg.filter((p) =>
                    (p.name || "").toLowerCase().includes(query),
                  )
                : withImg;
              if (filtered.length === 0)
                return {
                  content: query
                    ? `No encontré productos que coincidan con "${query}" (${withImg.length} producto(s) en el catálogo de la marca). Probá request_product_selection para que el usuario lo elija a mano.`
                    : "La marca no tiene productos cargados en el catálogo.",
                };
              return {
                content: JSON.stringify({
                  total: withImg.length,
                  matched: filtered.length,
                  products: filtered.map((p) => ({
                    productId: p.id,
                    name: p.name,
                  })),
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo leer el catálogo: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "use_product": {
            const productId: string | undefined =
              typeof input?.productId === "string"
                ? input.productId
                : undefined;
            if (!productId)
              return { content: "Falta productId", is_error: true };
            const cid = companyId ?? imageGenCompanyId;
            if (!cid || !brandProfile?.id)
              return {
                content: "No hay marca/empresa cargada en el proyecto.",
                is_error: true,
              };
            toolMessage("Importando producto del catálogo", Package);
            try {
              const catalog = await fetchUserProductsByBrand(
                cid,
                brandProfile.id,
              );
              const product = catalog.find((p) => p.id === productId);
              if (!product)
                return {
                  content: `No encontré un producto con id ${productId} en el catálogo de la marca. Usá list_products para ver los ids válidos.`,
                  is_error: true,
                };
              const assetId = await importProductToImageAsset(product);
              if (!assetId)
                return {
                  content: "No se pudo importar el producto como asset.",
                  is_error: true,
                };
              const blocks: any[] = [
                {
                  type: "text",
                  text:
                    `Producto importado como asset del proyecto. ` +
                    `assetId=${assetId} | nombre=${product.name}. ` +
                    `Pasá este assetId como firstFrameAssetId / lastFrameAssetId / refAssetIds en prefill_ai_scene o prefill_ai_image. ` +
                    `A continuación la imagen inline para que confirmes que es el producto correcto.`,
                },
              ];
              try {
                const base64 = await urlToBase64Jpeg(product.imageUrl);
                blocks.push({
                  type: "image",
                  source: {
                    type: "base64",
                    media_type: "image/jpeg",
                    data: base64,
                  },
                });
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `(no se pudo cargar la imagen inline: ${err?.message || err})`,
                });
              }
              return { content: blocks };
            } catch (err: any) {
              return {
                content: `No se pudo usar el producto: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "extract_clip_frame_as_asset": {
            const clipId: string | undefined =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            const which: "first" | "last" =
              input?.which === "last" ? "last" : "first";
            if (!clipId) return { content: "Falta clipId", is_error: true };
            if (!projectId || !companyId)
              return { content: "Project not loaded", is_error: true };
            const clip = clips.find((c) => c.id === clipId);
            if (!clip)
              return {
                content: `No hay clip con id ${clipId}`,
                is_error: true,
              };
            if (clip.type !== "video" && clip.type !== "ai-generated") {
              return { content: "El clip no es de video", is_error: true };
            }
            toolMessage(
              `Extrayendo ${which === "first" ? "primer" : "último"} frame del clip`,
              Film,
            );
            try {
              // Resolve at native resolution. For AI-generated clips we already
              // have full-res first/last frames cached. For regular video clips
              // we deliberately SKIP the clipThumbnails strip (low-res, meant
              // for the timeline) and always capture from the video source so
              // the extracted frame is sharp enough to reuse as an asset.
              let frameDataUrl: string | null = null;
              if (clip.type === "ai-generated") {
                frameDataUrl =
                  which === "first"
                    ? clip.aiFirstFrame || null
                    : clip.aiLastFrame || null;
              }
              if (!frameDataUrl && clip.src) {
                frameDataUrl = await new Promise<string | null>((resolve) => {
                  const video = document.createElement("video");
                  video.crossOrigin = "anonymous";
                  video.src = clip.src!;
                  video.muted = true;
                  video.preload = "auto";
                  const onLoaded = () => {
                    const seekTo =
                      which === "first"
                        ? 0.05
                        : Math.max(0, (video.duration || 0) - 0.05);
                    video.currentTime = seekTo;
                  };
                  const onSeeked = () => {
                    try {
                      const canvas = document.createElement("canvas");
                      canvas.width = video.videoWidth || 720;
                      canvas.height = video.videoHeight || 1280;
                      const ctx = canvas.getContext("2d");
                      if (!ctx) return resolve(null);
                      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                      resolve(canvas.toDataURL("image/jpeg", 0.92));
                    } catch {
                      resolve(null);
                    }
                  };
                  video.addEventListener("loadedmetadata", onLoaded, {
                    once: true,
                  });
                  video.addEventListener("seeked", onSeeked, { once: true });
                  video.addEventListener("error", () => resolve(null), {
                    once: true,
                  });
                  // Reloj: si loadedmetadata/seeked nunca disparan (video no
                  // seekable, CORS, compu dormida) degradamos a null en vez de
                  // colgar el turno del agente para siempre. resolve() es
                  // idempotente, así que un evento tardío no rompe nada.
                  setTimeout(() => {
                    console.warn(
                      "[video-agent][timeout] ⏱ extracción de frame local venció (20s) → null",
                    );
                    resolve(null);
                  }, 20_000);
                });
              }
              if (!frameDataUrl)
                return {
                  content: "No se pudo extraer el frame",
                  is_error: true,
                };
              const res = await fetch(frameDataUrl);
              const blob = await res.blob();
              const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
              const file = new File(
                [blob],
                `frame-${clip.id}-${which}-${Date.now()}.${ext}`,
                { type: blob.type || "image/jpeg" },
              );
              const blobUrl = URL.createObjectURL(blob);
              const {
                data: { user },
              } = await supabase.auth.getUser();
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              return {
                content: JSON.stringify({
                  assetId: asset.id,
                  name: asset.name,
                  type: "image",
                  clipId,
                  which,
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo extraer el frame: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "list_available_voices": {
            toolMessage("Consultando voces IA disponibles", Mic);
            const { data, error } = await supabase
              .from("voces")
              .select("id, nombre, sexo, descripcion, idioma, acento")
              .order("nombre");
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            let list = (data || []) as any[];
            // Filtros TOLERANTES a propósito: el agente manda 'es'/'español' o
            // 'femenino'/'femenina' según cómo lo razonó, pero la tabla `voces`
            // guarda 'Español'/'Inglés' y 'Femenina'/'Masculina'. Con igualdad
            // estricta el filtro devolvía [] y el agente concluía "no hay voces
            // en español". Normalizamos acento+caso y mapeamos sinónimos.
            const normText = (s: unknown) =>
              String(s ?? "")
                .toLowerCase()
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "")
                .trim();
            if (input?.language) {
              const LANG_ALIASES: Record<string, string> = {
                es: "espanol",
                esp: "espanol",
                "es-ar": "espanol",
                spanish: "espanol",
                castellano: "espanol",
                en: "ingles",
                eng: "ingles",
                english: "ingles",
              };
              const q = normText(input.language);
              const target = LANG_ALIASES[q] ?? q;
              list = list.filter((v) => {
                const lang = normText(v.idioma);
                return (
                  lang !== "" &&
                  (lang === target ||
                    lang.startsWith(target) ||
                    target.startsWith(lang))
                );
              });
            }
            if (input?.sex) {
              // 'femenino'/'femenina' → 'fem'; 'masculino'/'masculina' → 'mas'.
              const sexRoot = (s: unknown) => {
                const n = normText(s);
                if (n.startsWith("fem") || n === "f" || n === "mujer")
                  return "fem";
                if (
                  n.startsWith("mas") ||
                  n === "m" ||
                  n === "hombre" ||
                  n === "varon"
                )
                  return "mas";
                return n;
              };
              const target = sexRoot(input.sex);
              list = list.filter((v) => sexRoot(v.sexo) === target);
            }
            return { content: JSON.stringify(list) };
          }
          case "get_brand_context": {
            toolMessage("Leyendo contexto de marca", BookOpen);
            // Only return the brand_profile attached to this project — the
            // free-form memoria the agent edits via update_brand_context. We do
            // NOT include the company-level config (media_agent_*) because that
            // serves a different purpose.
            if (!brandProfile) {
              return { content: JSON.stringify({ brand_profile: null }) };
            }
            return {
              content: JSON.stringify({
                brand_profile: {
                  id: brandProfile.id,
                  name: brandProfile.name,
                  context: brandProfile.context,
                  colors: brandProfile.colors,
                  fonts: brandProfile.fonts,
                },
              }),
            };
          }
          case "update_brand_context": {
            const text =
              typeof input?.context === "string" ? input.context.trim() : "";
            const mode: "append" | "replace" =
              input?.mode === "replace" ? "replace" : "append";
            if (!text)
              return { content: "Falta el texto del contexto", is_error: true };
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const existing = brandProfile.context || "";
            const nextContext =
              mode === "replace"
                ? text
                : existing
                  ? `${existing.trimEnd()}\n\n${text}`
                  : text;
            toolMessage(
              `${mode === "replace" ? "Reemplazando" : "Guardando en"} memoria de ${brandProfile.name}`,
              Brain,
            );
            const { error } = await supabase
              .from("brand_profiles")
              .update({
                context: nextContext,
                updated_at: new Date().toISOString(),
              })
              .eq("id", brandProfile.id);
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            setBrandProfile({ ...brandProfile, context: nextContext });
            return {
              content: JSON.stringify({
                ok: true,
                mode,
                brand_profile_id: brandProfile.id,
                context: nextContext,
              }),
            };
          }
          case "update_brand_colors": {
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const incoming = Array.isArray(input?.colors)
              ? (input.colors as unknown[])
                  .filter(
                    (c): c is string =>
                      typeof c === "string" && c.trim().length > 0,
                  )
                  .map((c) => c.trim().toLowerCase())
              : [];
            if (incoming.length === 0)
              return { content: "Falta el array `colors`.", is_error: true };
            const mode: "add" | "remove" | "replace" =
              input?.mode === "replace"
                ? "replace"
                : input?.mode === "remove"
                  ? "remove"
                  : "add";
            const current = brandProfile.colors.map((c) => c.toLowerCase());
            let nextColors: string[];
            if (mode === "replace") {
              // Dedupe while preserving order.
              const seen = new Set<string>();
              nextColors = incoming.filter((c) =>
                seen.has(c) ? false : (seen.add(c), true),
              );
            } else if (mode === "remove") {
              const drop = new Set(incoming);
              nextColors = current.filter((c) => !drop.has(c));
            } else {
              // add: merge, dedupe, keep existing first.
              const seen = new Set(current);
              nextColors = [...current];
              for (const c of incoming)
                if (!seen.has(c)) {
                  seen.add(c);
                  nextColors.push(c);
                }
            }
            toolMessage(
              `${mode === "replace" ? "Reemplazando" : mode === "remove" ? "Quitando" : "Agregando"} colores en ${brandProfile.name}`,
              Palette,
            );
            const { error } = await supabase
              .from("brand_profiles")
              .update({
                colors: nextColors,
                updated_at: new Date().toISOString(),
              })
              .eq("id", brandProfile.id);
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            setBrandProfile({ ...brandProfile, colors: nextColors });
            return {
              content: JSON.stringify({
                ok: true,
                mode,
                brand_profile_id: brandProfile.id,
                colors: nextColors,
              }),
            };
          }
          case "update_brand_fonts": {
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const VALID_ROLES: BrandFontRole[] = [
              "antetitulo",
              "titulo",
              "subtitulo",
              "cuerpo",
              "detalles",
            ];
            const assignments = (
              input && typeof input === "object" ? input.assignments : null
            ) as Record<string, unknown> | null;
            if (!assignments || typeof assignments !== "object") {
              return {
                content:
                  "Falta el objeto `assignments` con la asignación rol → familia.",
                is_error: true,
              };
            }
            // Map every input role; null/empty clears the role, a string sets
            // it to the canonical family name. Unknown roles or unknown
            // families are reported back without halting the rest.
            const known = new Map(
              TEXT_FONT_OPTIONS.map((o) => [o.family.toLowerCase(), o.family]),
            );
            const sets: Partial<Record<BrandFontRole, string>> = {};
            const clears: BrandFontRole[] = [];
            const ignoredFonts: Array<{ role: string; value: string }> = [];
            const ignoredRoles: string[] = [];
            for (const [rawRole, rawValue] of Object.entries(assignments)) {
              if (!VALID_ROLES.includes(rawRole as BrandFontRole)) {
                ignoredRoles.push(rawRole);
                continue;
              }
              const role = rawRole as BrandFontRole;
              if (
                rawValue === null ||
                rawValue === undefined ||
                rawValue === ""
              ) {
                clears.push(role);
                continue;
              }
              if (typeof rawValue !== "string") {
                ignoredFonts.push({ role, value: String(rawValue) });
                continue;
              }
              const canonical = known.get(rawValue.trim().toLowerCase());
              if (!canonical) {
                ignoredFonts.push({ role, value: rawValue });
                continue;
              }
              sets[role] = canonical;
            }
            if (Object.keys(sets).length === 0 && clears.length === 0) {
              return {
                content: `No se aplicó ninguna asignación válida. Roles válidos: ${VALID_ROLES.join(", ")}. Familias disponibles: ${TEXT_FONT_OPTIONS.map((o) => o.family).join(", ")}`,
                is_error: true,
              };
            }
            const mode: "merge" | "replace" =
              input?.mode === "replace" ? "replace" : "merge";
            let nextFonts: BrandFontRoles;
            if (mode === "replace") {
              // Replace wipes everything not present in `sets`.
              nextFonts = { ...sets };
            } else {
              nextFonts = { ...brandProfile.fonts, ...sets };
              for (const role of clears) delete nextFonts[role];
            }
            toolMessage(
              `${mode === "replace" ? "Redefiniendo" : "Actualizando"} fuentes de ${brandProfile.name}`,
              Type,
            );
            const { error } = await supabase
              .from("brand_profiles")
              .update({
                fonts: nextFonts,
                updated_at: new Date().toISOString(),
              })
              .eq("id", brandProfile.id);
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            setBrandProfile({ ...brandProfile, fonts: nextFonts });
            return {
              content: JSON.stringify({
                ok: true,
                mode,
                brand_profile_id: brandProfile.id,
                fonts: nextFonts,
                ignored_fonts: ignoredFonts,
                ignored_roles: ignoredRoles,
              }),
            };
          }
          case "get_brand_assets": {
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const tipoFilter: BrandAssetTipo | undefined =
              input?.tipo === "logo" || input?.tipo === "element"
                ? input.tipo
                : undefined;
            toolMessage(
              tipoFilter
                ? `Leyendo ${tipoFilter === "logo" ? "logos" : "elementos"} de la marca`
                : "Leyendo imágenes de la marca",
              ImageIcon,
            );
            const list = await fetchBrandAssets(brandProfile.id, tipoFilter);
            if (list.length === 0) {
              return {
                content:
                  tipoFilter === "logo"
                    ? "La marca todavía no tiene logos cargados. Si los necesitás, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba."
                    : tipoFilter === "element"
                      ? "La marca todavía no tiene elementos visuales cargados. Si los necesitás, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba."
                      : "La marca todavía no tiene logos ni elementos cargados. Si los necesitás, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba.",
              };
            }
            // Inline each image as base64 so Claude sees them visually,
            // mirroring list_generated_images. Headers carry the metadata
            // the agent needs to pick the right one (id + tipo + name + dims).
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `Imágenes guardadas en la marca "${brandProfile.name}" (${list.length} item${list.length === 1 ? "" : "s"}). ` +
                  `IDs en orden: ${list.map((a) => a.id).join(", ")}. ` +
                  "Para usar una de estas imágenes como referencia / primer / último frame de una escena IA o como adjunto de imagen IA, primero llamá a import_brand_asset_to_project con su id para obtener un assetId del proyecto.",
              },
            ];
            for (const a of list) {
              const url = await getBrandAssetSignedUrl(a.storage_path);
              if (!url) {
                blocks.push({
                  type: "text",
                  text: `brand_asset id=${a.id} tipo=${a.tipo} name=${JSON.stringify(a.name)}: no se pudo firmar URL`,
                });
                continue;
              }
              let base64 = "";
              try {
                base64 = await urlToBase64Jpeg(url);
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `brand_asset id=${a.id}: no se pudo cargar (${err?.message || err})`,
                });
                continue;
              }
              const header = [
                `brand_asset_id=${a.id}`,
                `tipo=${a.tipo}`,
                `name=${JSON.stringify(a.name || "")}`,
              ];
              if (a.width && a.height)
                header.push(`dim=${a.width}x${a.height}`);
              blocks.push({ type: "text", text: header.join(" | ") });
              blocks.push({
                type: "image",
                source: {
                  type: "base64",
                  media_type: "image/jpeg",
                  data: base64,
                },
              });
            }
            return { content: blocks };
          }
          case "import_brand_asset_to_project": {
            const brandAssetId: string | undefined =
              typeof input?.brandAssetId === "string"
                ? input.brandAssetId.trim()
                : undefined;
            if (!brandAssetId)
              return { content: "Falta brandAssetId", is_error: true };
            if (!projectId || !companyId)
              return { content: "Project not loaded", is_error: true };
            // Fetch the brand-assets row directly (avoids depending on the
            // local brandAssets cache, which only loads when the Imágenes
            // tab is opened).
            const { data: row, error: fetchErr } = await supabase
              .from("brand_assets")
              .select("*")
              .eq("id", brandAssetId)
              .maybeSingle();
            if (fetchErr || !row) {
              return {
                content: `No hay brand_asset con id ${brandAssetId}.`,
                is_error: true,
              };
            }
            const ba = row as BrandAsset;
            const url = await getBrandAssetSignedUrl(ba.storage_path);
            if (!url)
              return {
                content: "No se pudo firmar URL del brand asset.",
                is_error: true,
              };
            toolMessage("Importando imagen de marca como asset", ImageIcon);
            try {
              const resp = await fetch(url);
              const blob = await resp.blob();
              const ext = (
                ba.name?.split(".").pop() ||
                blob.type.split("/")[1] ||
                "jpg"
              ).toLowerCase();
              const file = new File(
                [blob],
                ba.name || `brand-${ba.tipo}-${Date.now()}.${ext}`,
                { type: blob.type || ba.mime_type || "image/jpeg" },
              );
              const {
                data: { user },
              } = await supabase.auth.getUser();
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              const blobUrl = URL.createObjectURL(blob);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              return {
                content: JSON.stringify({
                  assetId: asset.id,
                  name: asset.name,
                  type: "image",
                  from_brand_asset_id: ba.id,
                  tipo: ba.tipo,
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo importar el brand asset: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "open_brand_panel": {
            const tab = input?.tab;
            if (
              tab !== "memoria" &&
              tab !== "fuentes" &&
              tab !== "colores" &&
              tab !== "imagenes"
            ) {
              return {
                content:
                  "tab debe ser 'memoria' | 'fuentes' | 'colores' | 'imagenes'.",
                is_error: true,
              };
            }
            setActivePanel("brand");
            setBrandTab(tab);
            const labels: Record<typeof tab, string> = {
              memoria: "Memoria",
              fuentes: "Fuentes",
              colores: "Colores",
              imagenes: "Imágenes",
            };
            toolMessage(`Abriendo panel de Marca · ${labels[tab]}`, Palette);
            return {
              content: `Panel de Marca abierto en la pestaña "${labels[tab]}". Avisale al usuario qué tiene que hacer ahí.`,
            };
          }
          case "get_ui_context": {
            toolMessage("Leyendo estado de la UI", Compass);
            const selectedClip = selectedClipId
              ? clips.find((c) => c.id === selectedClipId)
              : null;
            let panelFields: Record<string, any> | null = null;
            if (activePanel === "ai-scene") {
              panelFields = {
                mode: sceneMode,
                prompt: scenePrompt,
                duration: sceneDuration,
                quality: sceneQuality,
                resolution: sceneResolution,
                dialogue: sceneMode === "personas" ? sceneDialogue : undefined,
                speechSpeed:
                  sceneMode === "personas" ? sceneSpeechSpeed : undefined,
                voiceId: sceneMode === "personas" ? audioAiVoiceId : undefined,
                voiceName:
                  sceneMode === "personas" ? audioAiVoiceName : undefined,
                refMode: sceneRefMode,
                firstFrameAssetId: sceneFirstFrameAssetId || null,
                lastFrameAssetId: sceneLastFrameAssetId || null,
                refAssetIds: sceneRefAssetIds,
                editingClipId: editingClipId || null,
              };
            } else if (activePanel === "ai-audio") {
              panelFields = {
                type: audioAiType,
                prompt: audioAiPrompt,
                duration: audioAiDuration,
                voiceId: audioAiType === "voice" ? audioAiVoiceId : undefined,
                voiceName:
                  audioAiType === "voice" ? audioAiVoiceName : undefined,
                speechSpeed:
                  audioAiType === "voice" ? audioAiSpeechSpeed : undefined,
                instrumentalOnly:
                  audioAiType === "music" ? audioAiInstrumentalOnly : undefined,
                editingClipId: editingClipId || null,
              };
            } else if (activePanel === "ai-image-gen") {
              panelFields = {
                prompt: imageGenPrompt,
                attachedAssetIds: imageGenAttachedAssetIds,
                aspectRatio,
              };
            }
            return {
              content: JSON.stringify({
                activePanel,
                aspectRatio,
                selectedClipId: selectedClipId || null,
                selectedClip: selectedClip
                  ? {
                      id: selectedClip.id,
                      type: selectedClip.type,
                      name: selectedClip.name,
                      startTime: selectedClip.startTime,
                      duration: selectedClip.duration,
                      trackIndex: selectedClip.trackIndex ?? 0,
                      aiPrompt: selectedClip.aiPrompt,
                      aiStatus: selectedClip.aiStatus,
                    }
                  : null,
                panelFields,
              }),
            };
          }
          case "prefill_ai_scene": {
            const sm: "general" | "personas" =
              input?.sceneMode === "personas" ? "personas" : "general";
            // En modo personas la UI tiene dos sub-modos con bindings
            // distintos:
            //   · static (default, UGC testimonial): UN textarea labeled
            //     "Diálogo de la escena" que escribe sobre `scenePrompt`.
            //     `sceneDialogue` queda oculto y NO se envía como `dialogo`
            //     a la API — el texto spoken viaja en `prompt`.
            //   · motion (más cinematográfico): DOS textareas — visual
            //     `scenePrompt` + spoken `sceneDialogue`.
            // El agente puede pasar `personType` explícito; si no, lo
            // inferimos: si vino sólo `dialogue` (típico de static) o
            // sólo `prompt` lo tratamos como static. Si vinieron AMBOS,
            // motion. Esto evita el bug clásico donde el agente llena los
            // dos pensando en motion pero la UI estaba en static y mostraba
            // la descripción visual donde el usuario espera el diálogo.
            let pt: "static" | "motion" = "static";
            if (sm === "personas") {
              if (
                input?.personType === "motion" ||
                input?.personType === "static"
              ) {
                pt = input.personType;
              } else {
                const hasPrompt =
                  typeof input?.prompt === "string" &&
                  input.prompt.trim().length > 0;
                const hasDialogue =
                  typeof input?.dialogue === "string" &&
                  input.dialogue.trim().length > 0;
                pt = hasPrompt && hasDialogue ? "motion" : "static";
              }
            }
            // Multi-scene mode: only valid with sceneMode='general' and a non-empty scenes array.
            const rawScenes = Array.isArray(input?.scenes)
              ? input.scenes
              : null;
            const multiScenes =
              rawScenes && sm === "general"
                ? rawScenes
                    .filter(
                      (s: any) =>
                        s &&
                        typeof s.prompt === "string" &&
                        typeof s.duration === "number",
                    )
                    .slice(0, 3)
                    .map((s: any) => ({
                      id: generateId(),
                      prompt: String(s.prompt),
                      duration: Math.max(
                        4,
                        Math.min(10, Math.round(s.duration)),
                      ),
                    }))
                : null;
            const isMulti = !!(multiScenes && multiScenes.length >= 2);
            // Si hay un ai-generated clip seleccionado y todavía no se
            // disparó la generación (draft o error), tratamos esta llamada
            // como edición — así el agente puede "fijar" un prefill mal
            // armado sin crear un clip nuevo paralelo. Si está generando o
            // ya está ready, no editamos en sitio (sería destructivo).
            const selectedAi = selectedClipId
              ? clips.find(
                  (c) => c.id === selectedClipId && c.type === "ai-generated",
                )
              : null;
            const editable =
              selectedAi &&
              (selectedAi.aiStatus === "draft" ||
                selectedAi.aiStatus === "error" ||
                !selectedAi.aiStatus);
            setEditingClipId(editable ? selectedAi!.id : null);
            setSceneMode(sm);
            if (sm === "personas") setScenePersonType(pt);
            if (isMulti) {
              // In multi mode the top-level prompt/duration are ignored; the
              // per-scene list drives the generation payload.
              setSceneMultiEnabled(true);
              setSceneMultiList(multiScenes!);
              setScenePrompt("");
            } else if (sm === "personas" && pt === "static") {
              // Static dialog: el textarea único está atado a `scenePrompt`
              // y la UI lo etiqueta como "Diálogo". Routeamos lo que el
              // agente haya mandado (prefiriendo `dialogue`) hacia ese
              // campo, y limpiamos `sceneDialogue` para que no quede
              // residuo de un personType anterior.
              setSceneMultiEnabled(false);
              const spoken =
                typeof input?.dialogue === "string" && input.dialogue
                  ? input.dialogue
                  : input?.prompt || "";
              setScenePrompt(String(spoken));
              setSceneDialogue("");
              if (typeof input?.duration === "number")
                setSceneDuration(input.duration);
            } else {
              setSceneMultiEnabled(false);
              setScenePrompt(String(input?.prompt || ""));
              if (typeof input?.duration === "number")
                setSceneDuration(input.duration);
            }
            if (input?.aspectRatio) setAspectRatio(input.aspectRatio);
            // "rapido" ya no existe en el Estudio — si el agente lo manda
            // (prompt viejo), cae a Estándar.
            if (input?.quality === "estandar" || input?.quality === "profesional")
              setSceneQuality(input.quality);
            else if (input?.quality === "rapido") setSceneQuality("estandar");
            if (
              input?.resolution === "480p" ||
              input?.resolution === "720p" ||
              input?.resolution === "1080p"
            )
              setSceneResolution(input.resolution);
            if (sm === "personas") {
              // Sólo motion usa el campo dedicado de diálogo. En static ya
              // routeamos el spoken text a `scenePrompt` arriba.
              if (pt === "motion" && input?.dialogue)
                setSceneDialogue(String(input.dialogue));
              if (typeof input?.speechSpeed === "number")
                setSceneSpeechSpeed(input.speechSpeed);
              if (input?.voiceId) {
                const vid = String(input.voiceId);
                setAudioAiVoiceId(vid);
                const cached = audioAiVoices.find((x) => x.id === vid);
                if (cached) {
                  setAudioAiVoiceName(cached.nombre);
                } else {
                  supabase
                    .from("voces")
                    .select("nombre")
                    .eq("id", vid)
                    .maybeSingle()
                    .then(({ data }) => {
                      if (data?.nombre)
                        setAudioAiVoiceName(String(data.nombre));
                    });
                }
              }
            }
            // El modo de referencia se decide con el input CRUDO, no con los
            // ids ya resueltos: se sabe al instante y deja que el panel abra
            // con los slots correctos a la vista, en vez de arrancar en "sin
            // referencia" y saltar cuando las imágenes terminan de resolverse.
            const wantsFrames = !!(
              input?.firstFrameAssetId || input?.lastFrameAssetId
            );
            const wantsRefs = !!(
              Array.isArray(input?.refAssetIds) && input.refAssetIds.length > 0
            );
            const optimisticRefMode: "none" | "reference" | "frames" = isMulti
              ? // Multi-scene always uses a shared reference image.
                "reference"
              : wantsFrames
                ? "frames"
                : wantsRefs
                  ? "reference"
                  : "none";
            // `setSceneRefMode` sincroniza `sceneRefModeRef` en el mismo tick,
            // así que las escrituras tardías de más abajo pueden compararlo
            // sin depender de que haya corrido ningún efecto.
            setSceneRefMode(optimisticRefMode);
            // Token de ESTE prefill: si el Director dispara otro mientras
            // resolvemos, el viejo no debe escribir nada.
            const prefillToken = ++scenePrefillTokenRef.current;
            // Abrimos el panel ACÁ, antes de resolver las referencias.
            // Resolver una referencia no es una consulta: descarga la imagen
            // generada y la vuelve a subir como asset del proyecto, y pueden
            // ser varias. Con el `setActivePanel` al final, el prompt quedaba
            // escrito en el estado desde el primer instante pero el panel no
            // aparecía hasta que terminaban todas las subidas — el usuario
            // leía "te cargué el panel" y no veía nada durante segundos.
            // Los slots se llenan solos cuando cada referencia aterriza.
            setActivePanel("ai-scene");
            {
              const keys: string[] = [];
              if (input?.prompt) keys.push("scene-prompt");
              if (sm === "personas" && input?.dialogue)
                keys.push("scene-dialogue");
              if (input?.firstFrameAssetId) keys.push("scene-firstframe");
              if (input?.lastFrameAssetId) keys.push("scene-lastframe");
              if (wantsRefs) keys.push("scene-refs");
              triggerPanelBounce(keys);
            }
            // Las tres resoluciones son independientes entre sí: en paralelo.
            const [resolvedFirstFrame, resolvedLastFrame, resolvedSceneRefs] =
              await Promise.all([
                input?.firstFrameAssetId
                  ? resolveRefIdToAssetId(String(input.firstFrameAssetId))
                  : Promise.resolve(null),
                input?.lastFrameAssetId
                  ? resolveRefIdToAssetId(String(input.lastFrameAssetId))
                  : Promise.resolve(null),
                resolveRefIdList(input?.refAssetIds),
              ]);
            // El panel estuvo abierto y editable durante toda la resolución,
            // así que estas escrituras pueden llegar tarde a una escena que ya
            // no es la que estaban armando. Dos formas de quedar obsoleto:
            //   · otro prefill del Director pisó a éste,
            //   · el usuario cambió el modo de referencia a mano.
            // En cualquiera de los dos casos mandan ellos: descartamos y se lo
            // decimos al Director, que si no seguiría el plan creyendo que las
            // referencias quedaron cargadas.
            const supersededByPrefill =
              scenePrefillTokenRef.current !== prefillToken;
            const userChangedRefMode =
              sceneRefModeRef.current !== optimisticRefMode;
            const stale = supersededByPrefill || userChangedRefMode;
            if (!stale) {
              if (resolvedFirstFrame)
                setSceneFirstFrameAssetId(resolvedFirstFrame);
              if (resolvedLastFrame)
                setSceneLastFrameAssetId(resolvedLastFrame);
              if (Array.isArray(input?.refAssetIds))
                setSceneRefAssetIds(resolvedSceneRefs);
              // Si NINGUNA referencia pudo resolverse, el modo que fijamos
              // arriba quedó prometiendo slots que nunca se van a llenar.
              // Corregimos al valor real (salvo multi, que siempre es
              // 'reference' por definición).
              if (!isMulti) {
                let corrected: "none" | "reference" | "frames" | null = null;
                if (wantsFrames && !resolvedFirstFrame && !resolvedLastFrame) {
                  corrected =
                    resolvedSceneRefs.length > 0 ? "reference" : "none";
                } else if (
                  !wantsFrames &&
                  wantsRefs &&
                  resolvedSceneRefs.length === 0
                ) {
                  corrected = "none";
                }
                if (corrected) setSceneRefMode(corrected);
              }
            }
            if (stale) {
              const motivo = supersededByPrefill
                ? "se disparó otro prefill mientras se cargaban"
                : "el usuario cambió el modo de referencia a mano mientras se cargaban";
              toolMessage(
                "Escena pre-llenada (referencias descartadas)",
                Sparkles,
              );
              return {
                content: `Panel de escena IA abierto y pre-llenado, PERO las imágenes de referencia no se aplicaron: ${motivo}. No asumas que están cargadas — verificá con get_ui_context antes de seguir.`,
              };
            }
            if (isMulti) {
              const totalDur = multiScenes!.reduce((s, x) => s + x.duration, 0);
              toolMessage(
                `Multi-escena (${multiScenes!.length} escenas, ${totalDur}s) pre-llenada`,
                Sparkles,
              );
              const warnNoRef =
                resolvedSceneRefs.length === 0
                  ? " ATENCIÓN: no hay refAssetIds cargados; el modo multi-escena requiere al menos una imagen de referencia compartida."
                  : "";
              return {
                content: `Panel de escena IA abierto en modo multi-escena (${multiScenes!.length} escenas, total ${totalDur}s). El usuario debe revisar y apretar Generar.${warnNoRef}`,
              };
            }
            toolMessage("Panel de escena IA pre-llenado", Sparkles);
            return {
              content:
                "Panel de escena IA abierto y pre-llenado. El usuario debe revisar y apretar Generar.",
            };
          }
          case "prefill_ai_audio": {
            const t: "voice" | "music" =
              input?.type === "music" ? "music" : "voice";
            setEditingClipId(null);
            setAudioAiType(t);
            setAudioAiPrompt(String(input?.prompt || ""));
            if (typeof input?.duration === "number")
              setAudioAiDuration(input.duration);
            if (t === "voice") {
              if (input?.voiceId) {
                const vid = String(input.voiceId);
                setAudioAiVoiceId(vid);
                const cached = audioAiVoices.find((x) => x.id === vid);
                if (cached) {
                  setAudioAiVoiceName(cached.nombre);
                } else {
                  // Voice cache may not be loaded yet (it's lazy). Fetch the
                  // single voice name so the user sees the label immediately.
                  supabase
                    .from("voces")
                    .select("nombre")
                    .eq("id", vid)
                    .maybeSingle()
                    .then(({ data }) => {
                      if (data?.nombre)
                        setAudioAiVoiceName(String(data.nombre));
                    });
                }
              }
              if (typeof input?.speechSpeed === "number")
                setAudioAiSpeechSpeed(input.speechSpeed);
            } else {
              setAudioAiInstrumentalOnly(!!input?.instrumentalOnly);
            }
            setActivePanel("ai-audio");
            if (input?.prompt) triggerPanelBounce(["audio-prompt"]);
            toolMessage("Panel de audio IA pre-llenado", Music);
            return {
              content:
                "Panel de audio IA abierto y pre-llenado. El usuario debe revisar y apretar Generar.",
            };
          }
          case "prefill_ai_image": {
            setImageGenPrompt(String(input?.prompt || ""));
            if (input?.aspectRatio) setAspectRatio(input.aspectRatio);
            // Mismo criterio que `prefill_ai_scene`: el panel se abre ANTES de
            // resolver las referencias (que descargan y vuelven a subir cada
            // imagen). Los adjuntos se agregan cuando terminan.
            setActivePanel("ai-image-gen");
            {
              const keys: string[] = [];
              if (input?.prompt) keys.push("image-prompt");
              if (
                Array.isArray(input?.refAssetIds) &&
                input.refAssetIds.length > 0
              )
                keys.push("image-attach");
              triggerPanelBounce(keys);
            }
            const imgToken = ++imagePrefillTokenRef.current;
            // Foto de los adjuntos al arrancar: si al terminar difieren, el
            // usuario los tocó con el panel ya abierto y manda él.
            const attachedBefore = imageGenAttachedAssetIdsRef.current;
            const resolvedRefs = await resolveRefIdList(input?.refAssetIds);
            const imgSuperseded = imagePrefillTokenRef.current !== imgToken;
            const current = imageGenAttachedAssetIdsRef.current;
            const userChangedAttachments =
              current.length !== attachedBefore.length ||
              current.some((id, i) => id !== attachedBefore[i]);
            const imgStale = imgSuperseded || userChangedAttachments;
            if (!imgStale && Array.isArray(input?.refAssetIds))
              setImageGenAttachedAssetIds(resolvedRefs);
            toolMessage("Panel de imagen IA pre-llenado", ImageIcon);
            if (imgStale) {
              const motivo = imgSuperseded
                ? "se disparó otro prefill mientras se cargaban"
                : "el usuario cambió los adjuntos a mano mientras se cargaban";
              return {
                content: `Panel de imagen IA abierto y pre-llenado, PERO las referencias no se adjuntaron: ${motivo}. No asumas que están cargadas — verificá con get_ui_context antes de seguir.`,
              };
            }
            return { content: "Panel de imagen IA abierto y pre-llenado." };
          }
          case "prefill_edit_generated_image": {
            const id =
              typeof input?.generatedImageId === "string"
                ? input.generatedImageId.trim()
                : "";
            const editPrompt =
              typeof input?.editPrompt === "string"
                ? input.editPrompt.trim()
                : "";
            if (!id)
              return { content: "Falta generatedImageId", is_error: true };
            if (!editPrompt)
              return { content: "Falta editPrompt", is_error: true };
            const exists = imageGenResults.some((r) => r.id === id);
            if (!exists)
              return {
                content: `Imagen generada ${id} no encontrada en este proyecto.`,
                is_error: true,
              };
            setActivePanel("ai-image-gen");
            setSlotPreview(null);
            setSlotPreviewEditing(false);
            // Stash the prompt so the reset effect on imageGenDetailId applies
            // it instead of clearing edit mode.
            pendingGenEditPromptRef.current = editPrompt;
            if (imageGenDetailId === id) {
              // The id didn't change, so the reset effect won't fire — apply
              // the edit state directly here.
              pendingGenEditPromptRef.current = null;
              setImageGenEditing(true);
              setImageGenEditPrompt(editPrompt);
            } else {
              setImageGenDetailId(id);
            }
            triggerPanelBounce(["gen-edit-prompt"]);
            toolMessage("Imagen generada abierta para editar", Pencil);
            return {
              content:
                "Panel de edición de imagen generada abierto y pre-llenado.",
            };
          }
          case "prefill_edit_scene_frame": {
            const which: "first" | "last" =
              input?.which === "last" ? "last" : "first";
            const editPrompt =
              typeof input?.editPrompt === "string"
                ? input.editPrompt.trim()
                : "";
            if (!editPrompt)
              return { content: "Falta editPrompt", is_error: true };
            const assetId =
              which === "first"
                ? sceneFirstFrameAssetId
                : sceneLastFrameAssetId;
            if (!assetId) {
              return {
                content: `No hay ${which === "first" ? "primer" : "último"} frame cargado. Cargá uno primero (prefill_ai_scene con firstFrameAssetId/lastFrameAssetId).`,
                is_error: true,
              };
            }
            setSlotPreview({ assetId, target: { kind: which } });
            setSlotPreviewEditing(true);
            setSlotPreviewEditPrompt(editPrompt);
            setActivePanel("ai-image-gen");
            triggerPanelBounce(["slot-edit-prompt"]);
            toolMessage(
              `${which === "first" ? "Primer" : "Último"} frame abierto para editar`,
              Pencil,
            );
            return {
              content:
                "Frame abierto en modo preview con prompt de edición pre-llenado.",
            };
          }
          case "nudge_image_slot": {
            const allowed = new Set([
              "scene-firstframe",
              "scene-lastframe",
              "scene-ref",
              "image-attach",
            ]);
            const slots = Array.isArray(input?.slots)
              ? input.slots.map(String).filter((s: string) => allowed.has(s))
              : [];
            if (slots.length === 0)
              return { content: "No valid slots provided.", is_error: true };
            triggerPanelNudge(slots);
            toolMessage("Resaltando slot de imagen", Hand);
            return { content: `Saltito disparado en: ${slots.join(", ")}.` };
          }
          case "add_text_clip": {
            const textVal = String(input?.text || DEFAULT_TEXT_CLIP.text);
            // Map the legacy `style` enum to concrete typography defaults so
            // older agent calls keep working. New calls can also pass any of
            // the explicit fields (fontFamily, fontSizePct, fontWeight,
            // fontStyle, textColor, textAlign, textXPct, textYPct).
            const styleDefaults: Partial<{
              fontFamily: string;
              fontSizePct: number;
              fontWeight: "normal" | "bold";
              fontStyle: "normal" | "italic";
              textAlign: "left" | "center" | "right";
            }> = (() => {
              switch (input?.style) {
                case "titulo":
                  return {
                    fontSizePct: 12,
                    fontWeight: "bold",
                    textAlign: "center",
                  };
                case "subtitulo":
                  return {
                    fontSizePct: 8,
                    fontWeight: "bold",
                    textAlign: "center",
                  };
                case "cuerpo":
                  return {
                    fontSizePct: 5,
                    fontWeight: "normal",
                    textAlign: "center",
                  };
                case "animado":
                  return {
                    fontSizePct: 10,
                    fontWeight: "bold",
                    textAlign: "center",
                  };
                default:
                  return {};
              }
            })();
            const id = addTextClip({
              text: textVal,
              startTime:
                typeof input?.startTime === "number"
                  ? input.startTime
                  : undefined,
              duration:
                typeof input?.duration === "number"
                  ? input.duration
                  : undefined,
              fontFamily:
                typeof input?.fontFamily === "string"
                  ? input.fontFamily
                  : styleDefaults.fontFamily,
              fontSizePct:
                typeof input?.fontSizePct === "number"
                  ? input.fontSizePct
                  : styleDefaults.fontSizePct,
              fontWeight:
                input?.fontWeight === "bold"
                  ? "bold"
                  : (styleDefaults.fontWeight ?? undefined),
              fontStyle: input?.fontStyle === "italic" ? "italic" : undefined,
              textColor:
                typeof input?.textColor === "string"
                  ? input.textColor
                  : undefined,
              textAlign: ["left", "center", "right"].includes(input?.textAlign)
                ? input.textAlign
                : styleDefaults.textAlign,
              textXPct:
                typeof input?.textXPct === "number"
                  ? input.textXPct
                  : undefined,
              textYPct:
                typeof input?.textYPct === "number"
                  ? input.textYPct
                  : undefined,
            });
            toolMessage(`Texto agregado: "${textVal.slice(0, 40)}"`, Type);
            return { content: JSON.stringify({ clipId: id }) };
          }
          case "add_asset_to_timeline": {
            if (!input?.assetId)
              return { content: "assetId required", is_error: true };
            const [asset] = await fetchAssetsByIds([String(input.assetId)]);
            if (!asset || (asset.type !== "video" && asset.type !== "audio")) {
              return {
                content: "Asset no encontrado o no soportado",
                is_error: true,
              };
            }
            await addAssetToTimeline(asset, {
              startTime:
                typeof input?.startTime === "number"
                  ? input.startTime
                  : undefined,
            });
            toolMessage(`Asset agregado: ${asset.name}`, Plus);
            return { content: `Asset "${asset.name}" agregado.` };
          }
          case "move_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            setClips((prev) =>
              prev.map((c) =>
                c.id === input.clipId
                  ? {
                      ...c,
                      startTime:
                        typeof input.startTime === "number"
                          ? Math.max(0, input.startTime)
                          : c.startTime,
                      trackIndex:
                        typeof input.trackIndex === "number"
                          ? Math.max(0, Math.floor(input.trackIndex))
                          : c.trackIndex,
                    }
                  : c,
              ),
            );
            toolMessage("Clip movido", ArrowLeftRight);
            return { content: "Clip movido." };
          }
          case "trim_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            setClips((prev) =>
              prev.map((c) =>
                c.id === input.clipId
                  ? {
                      ...c,
                      duration: Math.max(
                        0.1,
                        Number(input.duration) || c.duration,
                      ),
                      mediaOffset:
                        typeof input.mediaOffset === "number"
                          ? Math.max(0, input.mediaOffset)
                          : c.mediaOffset,
                    }
                  : c,
              ),
            );
            toolMessage("Clip recortado", Scissors);
            return { content: "Clip ajustado." };
          }
          case "split_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            const splitAt = Number(input.splitAt);
            const MIN_GAP = 0.1;
            const newId = generateId();
            let ok = false;
            setClips((prev) => {
              const clip = prev.find((c) => c.id === input.clipId);
              if (
                !clip ||
                splitAt < MIN_GAP ||
                splitAt > clip.duration - MIN_GAP
              )
                return prev;
              ok = true;
              const next: TimelineClip[] = [];
              for (const c of prev) {
                if (c.id !== clip.id) {
                  next.push(c);
                  continue;
                }
                next.push({ ...c, duration: splitAt });
                next.push({
                  ...c,
                  id: newId,
                  startTime: c.startTime + splitAt,
                  duration: c.duration - splitAt,
                  mediaOffset: (c.mediaOffset || 0) + splitAt,
                });
              }
              return next;
            });
            if (ok) {
              toolMessage("Clip dividido", Scissors);
              return { content: JSON.stringify({ newClipId: newId }) };
            }
            return { content: "splitAt inválido", is_error: true };
          }
          case "delete_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            setClips((prev) => prev.filter((c) => c.id !== input.clipId));
            toolMessage("Clip eliminado", Trash2);
            return { content: "Clip eliminado." };
          }
          case "autocut_interview": {
            const clipId =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            try {
              const liveClips = clipsRef.current;
              const clip = liveClips.find((c) => c.id === clipId);
              console.log("[Atlas][autocut][DIAG] entrando", {
                clipId,
                totalClips: liveClips.length,
                clipFound: !!clip,
                clipType: clip?.type,
                hasTranscript: !!clip?.transcript,
                wordCount: clip?.transcript?.words?.length ?? 0,
                input,
              });
              if (!clip) {
                console.warn("[Atlas][autocut] clip NO encontrado:", clipId);
                return {
                  content: `Clip ${clipId} no encontrado en la timeline`,
                  is_error: true,
                };
              }
              const t = clip.transcript;
              if (!t || !Array.isArray(t.words)) {
                console.warn(
                  "[Atlas][autocut] el clip NO tiene transcript (¿estado viejo de React?):",
                  { clipId, hasTranscript: !!t, isArray: Array.isArray(t?.words) },
                );
                return {
                  content: `El clip "${clip.name}" no tiene transcript. Llamá primero a generate_transcript con clipId="${clipId}".`,
                  is_error: true,
                };
              }
              const removeFillers = input?.remove_filler_words !== false;
              const removeSilences = input?.remove_silences !== false;
              if (!removeFillers && !removeSilences) {
                console.warn("[Atlas][autocut] ambos removes en false");
                return {
                  content:
                    "Dejá al menos uno de remove_filler_words o remove_silences en true.",
                  is_error: true,
                };
              }

              const offset = clip.mediaOffset ?? 0;
              const winStart = offset;
              const winEnd = offset + clip.duration;
              const words = t.words
                .filter(
                  (w) =>
                    w &&
                    w.type === "word" &&
                    typeof w.start === "number" &&
                    typeof w.end === "number",
                )
                .map((w) => ({ text: w.text, start: w.start, end: w.end }));
              console.log("[Atlas][autocut][DIAG] palabras", {
                totalWords: t.words.length,
                wordsInClip: words.length,
                winStart,
                winEnd,
                removeFillers,
                removeSilences,
              });

              const result = detectAutocutRanges(words, {
                fillerWords: Array.isArray(input?.filler_words)
                  ? input.filler_words
                  : undefined,
                maxSilenceSeconds:
                  typeof input?.max_silence_seconds === "number"
                    ? input.max_silence_seconds
                    : undefined,
                paddingSeconds:
                  typeof input?.padding_seconds === "number"
                    ? input.padding_seconds
                    : undefined,
                removeFillerWords: removeFillers,
                removeSilences,
                windowStart: winStart,
                windowEnd: winEnd,
              });
              console.log("[Atlas][autocut][DIAG] detección", {
                cuts: result.cuts.length,
                keepSegments: result.keepSegments.length,
                totalRemoved: Number(result.totalRemoved.toFixed(2)),
              });

              if (result.cuts.length === 0) {
                return {
                  content: JSON.stringify({
                    cuts: 0,
                    totalSecondsRemoved: 0,
                    message:
                      "No encontré muletillas ni silencios para cortar en este clip.",
                  }),
                };
              }

              const fillerCuts = result.cuts.filter(
                (c) => c.reason === "filler",
              ).length;
              const silenceCuts = result.cuts.filter(
                (c) => c.reason === "silence",
              ).length;
              const dryRun = !!input?.dry_run;

              if (dryRun || result.keepSegments.length === 0) {
                console.log("[Atlas][autocut][DIAG] preview/dry_run", {
                  dryRun,
                  keepSegments: result.keepSegments.length,
                });
                return {
                  content: JSON.stringify({
                    dryRun: true,
                    cuts: result.cuts.length,
                    fillerCuts,
                    silenceCuts,
                    totalSecondsRemoved: Number(
                      result.totalRemoved.toFixed(2),
                    ),
                    keepSegments: result.keepSegments.length,
                    warning:
                      result.keepSegments.length === 0
                        ? "Todo el clip quedaría cortado — NO aplico sin confirmación explícita del usuario."
                        : "Mostrale esto al usuario y pedí confirmación antes de aplicar (dry_run=false).",
                    ranges: result.cuts.map((c) => ({
                      start: Number(c.start.toFixed(2)),
                      end: Number(c.end.toFixed(2)),
                      reason: c.reason,
                      detail: c.detail,
                    })),
                  }),
                };
              }

              // Aplicar: reconstruir el clip como N sub-clips contiguos + ripple
              // de los clips siguientes en la MISMA pista.
              const subClips: TimelineClip[] = [];
              let acc = 0;
              for (const k of result.keepSegments) {
                subClips.push({
                  ...clip,
                  id: generateId(),
                  mediaOffset: k.start,
                  duration: k.length,
                  startTime: clip.startTime + acc,
                });
                acc += k.length;
              }
              const removed = result.totalRemoved;
              const clipTrack = clip.trackIndex ?? 0;
              const clipEnd = clip.startTime + clip.duration;
              setClips((prev) => {
                const out: TimelineClip[] = [];
                for (const c of prev) {
                  if (c.id === clip.id) {
                    out.push(...subClips);
                  } else if (
                    (c.trackIndex ?? 0) === clipTrack &&
                    c.startTime >= clipEnd
                  ) {
                    out.push({ ...c, startTime: c.startTime - removed });
                  } else {
                    out.push(c);
                  }
                }
                return out;
              });
              console.log("[Atlas][autocut][DIAG] APLICADO", {
                subClips: subClips.length,
                removed: Number(removed.toFixed(2)),
              });
              toolMessage(
                `Autocut: ${result.cuts.length} cortes (−${removed.toFixed(1)}s)`,
                Scissors,
              );
              return {
                content: JSON.stringify({
                  applied: true,
                  cuts: result.cuts.length,
                  fillerCuts,
                  silenceCuts,
                  totalSecondsRemoved: Number(removed.toFixed(2)),
                  resultingSubclips: subClips.length,
                }),
              };
            } catch (err: any) {
              console.error("[Atlas][autocut] ERROR inesperado:", err);
              return {
                content: `Error inesperado en autocut: ${err?.message || String(err)}`,
                is_error: true,
              };
            }
          }
          case "erase_object": {
            const clipId =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            const prompt =
              typeof input?.prompt === "string" ? input.prompt.trim() : "";
            if (!clipId) return { content: "Falta clipId", is_error: true };
            if (!prompt)
              return { content: "Falta describir qué objeto borrar (prompt).", is_error: true };
            const clip = clipsRef.current.find((c) => c.id === clipId);
            if (!clip)
              return { content: `Clip ${clipId} no encontrado.`, is_error: true };
            if (clip.type !== "video" && clip.type !== "ai-generated")
              return { content: "Solo se puede borrar objetos en clips de video.", is_error: true };
            if (!clip.storagePath)
              return {
                content: `El clip "${clip.name}" todavía no terminó de subirse a la nube. Esperá unos segundos y reintentá.`,
                is_error: true,
              };
            const confirmLong = input?.confirm_long_clip === true;
            if (clip.duration > ERASE_SOFT_CAP_SEC && !confirmLong) {
              return {
                content: JSON.stringify({
                  needsConfirmation: true,
                  reason: "long_clip",
                  durationSec: Math.round(clip.duration),
                  message: `El clip dura ${Math.round(clip.duration)}s (más de ${ERASE_SOFT_CAP_SEC}s). Va a tardar más y tiene más riesgo de costuras visibles. Confirmale al usuario y, si acepta, volvé a llamar con confirm_long_clip:true.`,
                }),
              };
            }
            const cost = creditsForErase({ durationSec: clip.duration });
            // Fire-and-forget: la función maneja su propio polling y estado.
            void eraseObjectInClip(clipId, prompt, confirmLong);
            return {
              content: JSON.stringify({
                started: true,
                estimatedCredits: cost,
                message: `Borrando "${prompt}" del clip. Tarda unos minutos; el clip muestra un shimmer y al terminar se reemplaza solo (y queda en Archivos).`,
              }),
            };
          }
          case "set_clip_volume": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            const v = Math.max(0, Math.min(2, Number(input.volume)));
            setClips((prev) =>
              prev.map((c) =>
                c.id === input.clipId ? { ...c, volume: v } : c,
              ),
            );
            toolMessage(
              v === 0 ? "Clip silenciado" : `Volumen = ${v}`,
              v === 0 ? VolumeX : Volume2,
            );
            return { content: `Volumen = ${v}` };
          }
          case "translate_clip_to_english": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            const targetClip = clips.find((c) => c.id === input.clipId);
            if (!targetClip) {
              return {
                content: `No existe ningún clip con id "${input.clipId}".`,
                is_error: true,
              };
            }
            if (
              targetClip.type !== "video" &&
              targetClip.type !== "ai-generated"
            ) {
              return {
                content: `El clip "${input.clipId}" es de tipo "${targetClip.type}", no se puede traducir. Solo aplica a video y ai-generated.`,
                is_error: true,
              };
            }
            if (
              targetClip.type === "ai-generated" &&
              targetClip.aiStatus !== "ready"
            ) {
              return {
                content: `El clip AI "${input.clipId}" todavía no está listo (aiStatus="${targetClip.aiStatus}"). Esperá a que termine la generación antes de traducir.`,
                is_error: true,
              };
            }
            if (targetClip.aiTranslating) {
              return {
                content: `El clip "${input.clipId}" ya tiene una traducción en curso.`,
                is_error: true,
              };
            }
            if (!targetClip.src) {
              return {
                content: `El clip "${input.clipId}" no tiene un archivo de video cargado.`,
                is_error: true,
              };
            }
            const cost = creditsForVideoTranslate({
              durationSec: targetClip.duration,
            });
            toolMessage(
              `Traduciendo "${targetClip.name}" al inglés (${cost} cr)`,
              Languages,
            );
            // Fire-and-forget: la función maneja su propio polling y
            // actualiza el clip cuando termina (5-15 min). Devolvemos
            // inmediato al agente para que pueda seguir conversando.
            void translateClipToEnglish(targetClip.id);
            return {
              content: `Traducción iniciada para el clip "${targetClip.name}" (id ${targetClip.id}). Costo: ${cost} créditos. Tarda entre 5 y 15 minutos. El clip se va a reemplazar in-place cuando termine.`,
            };
          }
          case "set_project_name": {
            if (!input?.name)
              return { content: "name required", is_error: true };
            const n = String(input.name).slice(0, 120);
            setProjectName(n);
            toolMessage(`Proyecto renombrado a "${n}"`, Pencil);
            return { content: "Nombre actualizado." };
          }
          case "set_project_aspect_ratio": {
            if (!input?.aspectRatio)
              return { content: "aspectRatio required", is_error: true };
            setAspectRatio(input.aspectRatio);
            toolMessage(`Aspect ratio → ${input.aspectRatio}`, Ratio);
            return { content: "Aspect ratio actualizado." };
          }
          default:
            return { content: `Unknown tool: ${name}`, is_error: true };
        }
      } catch (e: any) {
        console.error(`executeAgentTool(${name}) failed:`, e);
        return { content: `Error: ${e?.message || String(e)}`, is_error: true };
      }
    },
    [
      clips,
      projectId,
      companyId,
      projectName,
      duration,
      aspectRatio,
      audioAiVoices,
      addAssetToTimeline,
      addTextClip,
      activePanel,
      selectedClipId,
      editingClipId,
      brandProfile,
      sceneMode,
      scenePrompt,
      sceneDuration,
      sceneQuality,
      sceneResolution,
      sceneDialogue,
      sceneSpeechSpeed,
      sceneRefMode,
      sceneFirstFrameAssetId,
      sceneLastFrameAssetId,
      sceneRefAssetIds,
      audioAiType,
      audioAiPrompt,
      audioAiDuration,
      audioAiVoiceId,
      audioAiVoiceName,
      audioAiSpeechSpeed,
      audioAiInstrumentalOnly,
      imageGenPrompt,
      imageGenAttachedAssetIds,
      imageGenResults,
      clipThumbnails,
    ],
  );

  // ── Send agent message (with tool_use loop) ────────────────────────────
  const sendAgentMessage = useCallback(
    async (
      text: string,
      opts?: {
        // Modo REINTENTO (botón "Reintentar"): reanuda el stream con este
        // historial ya armado en vez de re-enviar el texto → no duplica el user
        // message ni re-inserta en DB. Ver lastFailedTurnRef.
        resumeApiMessages?: Array<{ role: "user" | "assistant"; content: any }>;
      },
    ) => {
      const resume = opts?.resumeApiMessages;
      const trimmed = text.trim();
      if (agentStreaming) return;
      if (!resume && !trimmed) return;
      if (!projectId || !companyId) {
        console.warn("sendAgentMessage: missing projectId or companyId");
        return;
      }
      // The director's spend is variable (depends on tools / tokens), so we
      // can't pre-quote the exact cost. Gate on the minimum: if the user
      // has at least 1 credit they can send; otherwise the same shared
      // SubscriptionLimitModal that other actions use pops up.
      if (!assertCredits(1)) return;

      // API messages = conversación estructurada para Anthropic. Si ya hay un
      // historial estructurado de turnos previos (conversationRef), continuamos
      // desde ahí — así el agente conserva los tool_use/tool_result (contexto
      // de marca, assetId del producto elegido, respuestas de ask_user, etc.) y
      // no re-pregunta. Si está vacío (primer turno, post-rewind/restore),
      // reconstruimos desde el texto de las burbujas como fallback.
      const apiMessages: Array<{ role: "user" | "assistant"; content: any }> =
        resume
          ? resume.map((m) => ({ ...m }))
          : conversationRef.current.length > 0
            ? conversationRef.current.map((m) => ({ ...m }))
            : agentMessages
                // Excluir burbujas de ERROR del rebuild-desde-texto: nunca deben
                // ir a Claude (contaminarían el contexto con "No pude conectarme…").
                .filter(
                  (m) =>
                    (m.role === "user" || m.role === "assistant") && !m.error,
                )
                .map((m) => ({
                  role: m.role as "user" | "assistant",
                  content: m.text || "",
                }));
      // Sanitiza la conversación para guardarla como historial del próximo
      // turno: (1) saca los bloques `thinking`/`redacted_thinking` (efímeros,
      // no deben cruzar turnos), y (2) "compacta" las imágenes (base64) ya
      // vistas a texto, para no re-mandar los píxeles cada turno (carísimo en
      // tokens). El texto de los tool_result —con los assetIds— se conserva.
      const sanitizeForHistory = (
        msgs: Array<{ role: "user" | "assistant"; content: any }>,
      ): Array<{ role: "user" | "assistant"; content: any }> =>
        msgs.map((m) => {
          if (!Array.isArray(m.content)) return { ...m };
          const content = m.content
            .map((block: any) => {
              if (
                block?.type === "thinking" ||
                block?.type === "redacted_thinking"
              )
                return null;
              if (block?.type === "image")
                return {
                  type: "text",
                  text: "[imagen ya vista en un turno anterior]",
                };
              if (block?.type === "tool_result") {
                let inner = block.content;
                if (Array.isArray(inner)) {
                  inner = inner.map((ib: any) =>
                    ib?.type === "image"
                      ? { type: "text", text: "[imagen ya vista]" }
                      : ib,
                  );
                }
                return { ...block, content: inner };
              }
              return block;
            })
            .filter(Boolean);
          return { ...m, content };
        });

      let attachmentAssetIds: string[] = [];
      let userTurnTs = "";
      if (resume) {
        // Reintento: el user message y el historial ya existen (snapshot); solo
        // sacamos la burbuja de error trailing. NO re-insertamos en DB ni UI.
        setAgentMessages((prev) =>
          prev.length &&
          prev[prev.length - 1].role === "assistant" &&
          prev[prev.length - 1].error
            ? prev.slice(0, -1)
            : prev,
        );
      } else {
        // Snapshot attachments for this turn (skip any still uploading; they'll
        // be dropped quietly rather than silently sent without images).
        const turnAttachments = agentAttachments.filter(
          (a) => a.assetId && !a.uploading,
        );
        attachmentAssetIds = turnAttachments.map((a) => a.assetId!) as string[];

      // Resolve signed URLs so Claude can fetch the images over HTTPS.
      let currentContent: any = trimmed;
      if (attachmentAssetIds.length > 0) {
        const assets = await fetchAssetsByIds(attachmentAssetIds);
        const pathById = new Map(assets.map((a) => [a.id, a.storage_path]));
        const urls: string[] = [];
        for (const id of attachmentAssetIds) {
          const path = pathById.get(id);
          if (!path) continue;
          const u = await getAssetSignedUrl(path).catch(() => null);
          if (u) urls.push(u);
        }
        if (urls.length > 0) {
          currentContent = [
            ...urls.map((url) => ({
              type: "image",
              source: { type: "url", url },
            })),
            { type: "text", text: trimmed },
          ];
        }
      }
      apiMessages.push({ role: "user", content: currentContent });

      // Optimistic UI: user message (with attachment previews).
      const uiAttachments = turnAttachments.map((a) => ({
        assetId: a.assetId,
        url: a.url,
      }));
      userTurnTs = new Date().toISOString();
      setAgentMessages((prev) => [
        ...prev,
        {
          role: "user",
          text: trimmed,
          attachments: uiAttachments.length ? uiAttachments : undefined,
          createdAt: userTurnTs,
        },
      ]);
      setAgentInput("");
      setAgentAttachments([]);
      } // fin del envío normal (en reintento saltamos todo lo de arriba)
      setAgentStreaming(true);
      // Pin the chat to the just-sent user message at the top. The pin
      // suppresses the streaming auto-scroll so the assistant response grows
      // beneath the user message without yanking the viewport. We use an
      // instant ('auto') scroll instead of smooth so we land at the final
      // position before the first streaming delta arrives — otherwise the
      // smooth animation races the streaming auto-scroll.
      pinUserToTopRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollLatestUserMsgToTop("auto"));
      });

      // Persist user turn (keep only asset IDs; URLs are re-resolved on load).
      // We pin `created_at` to the same timestamp we stored locally so the two
      // stay in sync — this lets the rewind button delete server-side rows
      // using the local message's createdAt as a cutoff. En REINTENTO no
      // re-insertamos (ya está persistido del envío original).
      if (!resume) {
        supabase
          .from("agent_messages")
          .insert({
            project_id: projectId,
            id_company: companyId,
            role: "user",
            created_at: userTurnTs,
            content: {
              type: "text",
              text: trimmed,
              ...(attachmentAssetIds.length ? { attachmentAssetIds } : {}),
            },
          })
          .then(({ error }) => {
            if (error) console.error("insert user msg failed", error);
          });
      }

      // Token de este turno: si el usuario aprieta Detener (o arranca otro
      // turno), bumpeamos el ref y este turno se descarta silenciosamente.
      const genToken = (agentGenerationRef.current += 1);
      watchdogFiredRef.current = false;
      const MAX_TOOL_ROUNDS = 10;
      try {
        for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
          // Si el turno fue cancelado/superado mientras esperábamos, cortamos.
          if (genToken !== agentGenerationRef.current) return;

          // Add an assistant placeholder bubble we'll stream into.
          setAgentMessages((prev) => [
            ...prev,
            { role: "assistant", text: "" },
          ]);

          // Acumuladores del round — declarados ACÁ (fuera del loop de
          // reconexión) para que el código de abajo los vea; se resetean por
          // intento.
          const blocks = new Map<number, any>(); // index -> {type, ...}
          let textSoFar = "";
          let stopReason: string | null = null;

          // Reconexión automática: reintentamos conectar+consumir el stream ante
          // cortes transitorios, sin mostrarle el error al usuario. Seguro: las
          // tools se ejecutan DESPUÉS de este loop.
          for (let attempt = 1; ; attempt++) {
            // Reset de este intento: acumuladores, watchdog, y el texto parcial /
            // "Reconectando…" del placeholder (siempre la última burbuja).
            blocks.clear();
            textSoFar = "";
            stopReason = null;
            watchdogFiredRef.current = false;
            if (attempt > 1) {
              setAgentMessages((prev) => {
                if (prev.length === 0) return prev;
                const next = [...prev];
                const last = next[next.length - 1];
                if (last.role === "assistant")
                  next[next.length - 1] = { ...last, text: "" };
                return next;
              });
            }
            try {
          // getSession con reloj: punto de cuelgue conocido de Supabase (si el
          // refresh del token se traba, no resuelve nunca) y pasa ANTES del
          // watchdog del fetch → sin esto, "Pensando" eterno.
          let {
            data: { session },
          } = await withTimeout(supabase.auth.getSession(), 15_000, "getSession");
          if (!session?.access_token) {
            // Token vencido por inactividad: refrescar UNA vez antes de fallar.
            try {
              const refreshed = await withTimeout(
                supabase.auth.refreshSession(),
                15_000,
                "refreshSession",
              );
              session = refreshed.data.session;
            } catch {
              /* si el refresh falla, cae al throw de abajo */
            }
          }
          if (!session?.access_token) {
            // Sesión realmente caída: NO reintentable (reintentar no recupera el
            // login) → el catch de afuera muestra el mensaje.
            const e = new Error("No session") as Error & { retryable?: boolean };
            e.retryable = false;
            throw e;
          }

          // Watchdog de inactividad + soporte de cancelación (botón Detener).
          // Si la conexión muere en silencio (compu dormida, red caída, edge
          // trabado), reader.read() no resuelve nunca → "Pensando" eterno.
          // Abortamos tras 90s sin actividad → cae al catch y se reintenta.
          // keepalive:true pide pings ": ping" cada 15s al edge, así el
          // silencio real es corto y el watchdog casi no se dispara solo.
          const ac = new AbortController();
          turnAbortRef.current = ac;
          const armIdleWatchdog = () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            idleTimerRef.current = setTimeout(() => {
              watchdogFiredRef.current = true;
              ac.abort();
            }, 90_000);
          };
          armIdleWatchdog();

          const res = await fetch(AGENT_CHAT_URL, {
            method: "POST",
            signal: ac.signal,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              apikey: SUPABASE_ANON_KEY,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              messages: apiMessages,
              project_id: projectId,
              id_company: companyId,
              tools: AGENT_TOOLS,
              system: VIDEO_AGENT_SYSTEM_PROMPT,
              keepalive: true,
            }),
          });
          if (!res.ok || !res.body) {
            if (res.status === 402) {
              // Límite de créditos: NO es error técnico ni se reintenta.
              const e = new Error(
                "No te alcanzan los créditos para esto. Recargá y volvé a intentar.",
              ) as Error & { retryable?: boolean };
              e.retryable = false;
              throw e;
            }
            // 5xx / 429 / 408 = server frío, sobrecarga o rate-limit →
            // transitorio. 4xx (payload/permiso) es definitivo.
            const e = new Error(
              `Chat agent error: ${res.status}`,
            ) as Error & { retryable?: boolean };
            e.retryable =
              res.status >= 500 || res.status === 429 || res.status === 408;
            throw e;
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          // (blocks / textSoFar / stopReason viven en el scope del round y ya se
          // resetearon arriba para este intento.)

          const appendText = (delta: string) => {
            if (!delta) return;
            textSoFar += delta;
            setAgentMessages((prev) => {
              if (prev.length === 0) return prev;
              const next = [...prev];
              const last = next[next.length - 1];
              if (last.role === "assistant")
                next[next.length - 1] = { ...last, text: last.text + delta };
              return next;
            });
            // While pinned to the latest user message at the top, never
            // auto-scroll — the user explicitly asked for the message to stay
            // anchored. The pin clears when the user manually scrolls to the
            // bottom (see onScroll handler) or when streaming finishes.
            if (pinUserToTopRef.current) return;
            // Otherwise only auto-follow if the user is already near the
            // bottom (so we don't yank them while reading).
            const sc = agentMessagesScrollRef.current;
            if (sc) {
              const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
              if (dist < 80)
                agentMessagesEndRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                });
            }
          };

          const processEvent = (block: string) => {
            const dataLines: string[] = [];
            for (const line of block.split("\n")) {
              if (line.startsWith("data:"))
                dataLines.push(line.slice(5).trimStart());
            }
            if (dataLines.length === 0) return;
            const raw = dataLines.join("\n");
            if (raw === "[DONE]") return;
            try {
              const evt = JSON.parse(raw);
              if (evt?.type === "content_block_start") {
                const idx = evt.index;
                const cb = evt.content_block;
                if (cb?.type === "tool_use") {
                  blocks.set(idx, {
                    type: "tool_use",
                    id: cb.id,
                    name: cb.name,
                    _json: "",
                  });
                } else if (cb?.type === "text") {
                  blocks.set(idx, { type: "text", text: "" });
                } else if (cb?.type === "thinking") {
                  // Track thinking block. We also append a new 'thinking' row
                  // to the UI so the user sees a "Pensando…" indicator that
                  // expands on click.
                  blocks.set(idx, {
                    type: "thinking",
                    text: "",
                    signature: "",
                  });
                  setAgentMessages((prev) => {
                    // Insert the thinking row BEFORE the current assistant
                    // placeholder (which was appended at the start of this
                    // round) so the order in the UI matches the order of the
                    // content blocks in the stream.
                    const next = [...prev];
                    const insertAt =
                      next.length > 0 &&
                      next[next.length - 1].role === "assistant" &&
                      !next[next.length - 1].text
                        ? next.length - 1
                        : next.length;
                    next.splice(insertAt, 0, { role: "thinking", text: "" });
                    return next;
                  });
                } else if (cb?.type === "redacted_thinking") {
                  // Opaque server-redacted thinking block. We can't show its
                  // contents but we still need to echo it back to the API on
                  // subsequent tool rounds, so preserve the `data` field.
                  blocks.set(idx, {
                    type: "redacted_thinking",
                    data: cb.data || "",
                  });
                  setAgentMessages((prev) => {
                    const next = [...prev];
                    const insertAt =
                      next.length > 0 &&
                      next[next.length - 1].role === "assistant" &&
                      !next[next.length - 1].text
                        ? next.length - 1
                        : next.length;
                    next.splice(insertAt, 0, {
                      role: "thinking",
                      text: "",
                      thinkingRedactedData: cb.data || "",
                    });
                    return next;
                  });
                }
              } else if (evt?.type === "content_block_delta") {
                const idx = evt.index;
                const existing = blocks.get(idx);
                if (evt.delta?.type === "text_delta") {
                  appendText(evt.delta.text || "");
                  if (existing?.type === "text")
                    existing.text += evt.delta.text || "";
                } else if (
                  evt.delta?.type === "input_json_delta" &&
                  existing?.type === "tool_use"
                ) {
                  existing._json += evt.delta.partial_json || "";
                } else if (
                  evt.delta?.type === "thinking_delta" &&
                  existing?.type === "thinking"
                ) {
                  const piece = evt.delta.thinking || "";
                  existing.text += piece;
                  // Patch the most recent thinking row (still streaming: it's
                  // the only one without a trailing 'assistant' bubble after
                  // it that has text, so we find it from the back).
                  setAgentMessages((prev) => {
                    const next = [...prev];
                    for (let k = next.length - 1; k >= 0; k--) {
                      if (
                        next[k].role === "thinking" &&
                        !next[k].thinkingRedactedData
                      ) {
                        next[k] = {
                          ...next[k],
                          text: (next[k].text || "") + piece,
                        };
                        break;
                      }
                    }
                    return next;
                  });
                } else if (
                  evt.delta?.type === "signature_delta" &&
                  existing?.type === "thinking"
                ) {
                  const piece = evt.delta.signature || "";
                  existing.signature = (existing.signature || "") + piece;
                }
              } else if (evt?.type === "content_block_stop") {
                const existing = blocks.get(evt.index);
                if (existing?.type === "tool_use") {
                  try {
                    existing.input = existing._json
                      ? JSON.parse(existing._json)
                      : {};
                  } catch {
                    existing.input = {};
                  }
                  delete existing._json;
                } else if (existing?.type === "thinking") {
                  // Attach the final signature to the matching UI row.
                  const sig = existing.signature || "";
                  if (sig) {
                    setAgentMessages((prev) => {
                      const next = [...prev];
                      for (let k = next.length - 1; k >= 0; k--) {
                        if (
                          next[k].role === "thinking" &&
                          !next[k].thinkingSignature &&
                          !next[k].thinkingRedactedData
                        ) {
                          next[k] = { ...next[k], thinkingSignature: sig };
                          break;
                        }
                      }
                      return next;
                    });
                  }
                }
              } else if (evt?.type === "message_delta") {
                if (evt.delta?.stop_reason) stopReason = evt.delta.stop_reason;
              }
            } catch {
              // ignore malformed
            }
          };

          while (true) {
            const { done, value } = await reader.read();
            // Cualquier actividad (datos o ping) re-arma el watchdog — solo el
            // silencio total dispara el abort.
            armIdleWatchdog();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            buffer = buffer.replace(/\r\n/g, "\n");
            let boundary = buffer.indexOf("\n\n");
            while (boundary !== -1) {
              const eventBlock = buffer.slice(0, boundary);
              buffer = buffer.slice(boundary + 2);
              if (eventBlock.trim()) processEvent(eventBlock);
              boundary = buffer.indexOf("\n\n");
            }
          }
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
          if (buffer.trim()) processEvent(buffer);
              break; // stream consumido OK → salimos del loop de reconexión
            } catch (streamErr) {
              // Cancelado/superado por el usuario (Detener) → salir en silencio.
              if (genToken !== agentGenerationRef.current) return;
              // Transitorio y quedan intentos → avisar, esperar y reintentar.
              if (
                isRetryableStreamError(streamErr) &&
                attempt < MAX_STREAM_ATTEMPTS
              ) {
                const online = isOnline();
                const backoff =
                  STREAM_BACKOFF_MS[
                    Math.min(attempt - 1, STREAM_BACKOFF_MS.length - 1)
                  ];
                console.warn(
                  `[video-agent] intento ${attempt} falló (online=${online}) → ${online ? "reintento" : "esperando que vuelva la red"}`,
                  streamErr,
                );
                setAgentMessages((prev) => {
                  if (prev.length === 0) return prev;
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last.role === "assistant")
                    next[next.length - 1] = {
                      ...last,
                      text: online
                        ? "⟳ _Reconectando…_"
                        : "⏸ _Sin conexión — reintento apenas vuelva…_",
                    };
                  return next;
                });
                if (idleTimerRef.current) {
                  clearTimeout(idleTimerRef.current);
                  idleTimerRef.current = null;
                }
                // Si no hay red, queda en standby hasta que vuelva (o Detener).
                await waitBeforeRetry(
                  backoff,
                  () => genToken !== agentGenerationRef.current,
                );
                if (genToken !== agentGenerationRef.current) return;
                continue;
              }
              // Definitivo o sin intentos: dejar el placeholder vacío para que el
              // catch de afuera muestre el mensaje de error estándar, y propagar.
              setAgentMessages((prev) => {
                if (prev.length === 0) return prev;
                const next = [...prev];
                const last = next[next.length - 1];
                if (last.role === "assistant")
                  next[next.length - 1] = { ...last, text: "" };
                return next;
              });
              throw streamErr;
            }
          }

          // Build assistant content array for the API history. Thinking blocks
          // must be preserved (and in order) when we're going to continue with
          // tool_results on the next round — the Anthropic API rejects the
          // request otherwise when extended thinking is active.
          const orderedBlocks = [...blocks.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
          const assistantContent: any[] = [];
          for (const b of orderedBlocks) {
            if (b.type === "thinking" && b.text) {
              assistantContent.push({
                type: "thinking",
                thinking: b.text,
                signature: b.signature || "",
              });
            } else if (b.type === "redacted_thinking") {
              assistantContent.push({
                type: "redacted_thinking",
                data: b.data || "",
              });
            } else if (b.type === "text" && b.text) {
              assistantContent.push({ type: "text", text: b.text });
            } else if (b.type === "tool_use") {
              assistantContent.push({
                type: "tool_use",
                id: b.id,
                name: b.name,
                input: b.input || {},
              });
            }
          }

          // Persist thinking blocks for this round. Fire and forget — failures
          // only affect history reconstruction, not the current session.
          for (const b of orderedBlocks) {
            if (
              projectId &&
              companyId &&
              (b.type === "thinking" || b.type === "redacted_thinking")
            ) {
              const ts = new Date().toISOString();
              supabase
                .from("agent_messages")
                .insert({
                  project_id: projectId,
                  id_company: companyId,
                  role: "thinking",
                  created_at: ts,
                  content:
                    b.type === "thinking"
                      ? {
                          type: "thinking",
                          text: b.text || "",
                          signature: b.signature || "",
                        }
                      : { type: "redacted_thinking", data: b.data || "" },
                })
                .then(({ error }) => {
                  if (error) console.error("insert thinking msg failed", error);
                });
            }
          }

          // Persist assistant text (if any). Tool_use rows skipped in this iteration.
          if (textSoFar.trim()) {
            const assistantTs = new Date().toISOString();
            // Patch the in-memory assistant bubble with the same timestamp so
            // local state matches the row we're inserting.
            setAgentMessages((prev) => {
              const next = [...prev];
              for (let k = next.length - 1; k >= 0; k--) {
                if (
                  next[k].role === "assistant" &&
                  !next[k].createdAt &&
                  next[k].text === textSoFar
                ) {
                  next[k] = { ...next[k], createdAt: assistantTs };
                  break;
                }
              }
              return next;
            });
            const { error } = await supabase.from("agent_messages").insert({
              project_id: projectId,
              id_company: companyId,
              role: "assistant",
              created_at: assistantTs,
              content: { type: "text", text: textSoFar },
            });
            if (error) console.error("insert assistant msg failed", error);
          }

          // If empty assistant placeholder (no text), remove it from UI.
          if (!textSoFar.trim()) {
            setAgentMessages((prev) => {
              if (
                prev.length === 0 ||
                prev[prev.length - 1].role !== "assistant" ||
                prev[prev.length - 1].text !== ""
              )
                return prev;
              return prev.slice(0, -1);
            });
          }

          // If no tool calls, we're done.
          const toolUses = orderedBlocks.filter((b) => b.type === "tool_use");
          if (stopReason !== "tool_use" || toolUses.length === 0) {
            // Turno final: registrar la respuesta del asistente (solo texto, sin
            // thinking) en el historial estructurado para que el próximo turno
            // tenga contexto de lo que se dijo (ej: el plan que el usuario
            // aprueba con "ok").
            const finalText = assistantContent.filter(
              (b) => b.type === "text",
            );
            if (finalText.length > 0)
              apiMessages.push({ role: "assistant", content: finalText });
            break;
          }

          // Otherwise: push assistant turn into apiMessages and execute tools.
          apiMessages.push({ role: "assistant", content: assistantContent });

          const toolResults: any[] = [];
          for (const tu of toolUses) {
            console.log(`[video-agent][tool] ▶ ${tu.name}`, tu.input);
            const _toolT0 = Date.now();
            const result = await executeAgentTool(tu.name, tu.input);
            console.log(
              `[video-agent][tool] ${result.is_error ? "✗" : "✓"} ${tu.name} (${((Date.now() - _toolT0) / 1000).toFixed(1)}s)`,
            );
            toolResults.push({
              type: "tool_result",
              tool_use_id: tu.id,
              content: result.content,
              ...(result.is_error ? { is_error: true } : {}),
            });
          }
          apiMessages.push({ role: "user", content: toolResults });
          // Guardar el contexto de este round COMPLETO de forma incremental: si
          // un round POSTERIOR falla (o el usuario Detiene), un mensaje nuevo
          // —sin apretar Reintentar— igual arranca con los rounds ya ejecutados
          // en contexto, en vez de contradecir/repetir lo que ya se hizo (el
          // output ya está visible y persistido). Ver Greptile Issue 2.
          // GUARD (Greptile "Cancelled rounds corrupt context"): SOLO si el turno
          // sigue vigente. Un turno viejo —Detenido o superado por un mensaje
          // nuevo, ej. Detener durante un request_user_upload que desbloquea el
          // ask con {cancelled}— NO debe pisar el conversationRef del turno nuevo.
          if (genToken === agentGenerationRef.current) {
            conversationRef.current = sanitizeForHistory(apiMessages);
          }
          // Loop continues → next round asks Claude to continue with tool results.
        }
        // Guardar la conversación estructurada (con tool_use/tool_result,
        // imágenes compactadas y sin thinking) para que el PRÓXIMO turno
        // continúe desde acá en vez de reconstruir solo desde el texto. Guard por
        // genToken (igual que la incremental): un turno superado no pisa el nuevo.
        if (genToken === agentGenerationRef.current) {
          conversationRef.current = sanitizeForHistory(apiMessages);
        }
      } catch (err) {
        // Turno cancelado por el usuario (Detener) o superado por otro turno →
        // no es un error, lo saltamos en silencio.
        if (genToken !== agentGenerationRef.current) {
          console.log("sendAgentMessage: turno cancelado/superado");
        } else {
          const msg = watchdogFiredRef.current
            ? "El asistente dejó de responder."
            : "No pude conectarme con el agente.";
          // Snapshot del historial ya armado (user 1×, imágenes resueltas, rounds
          // completos) para que "Reintentar" reanude el stream sin re-enviar.
          lastFailedTurnRef.current = { apiMessages: [...apiMessages] };
          setAgentMessages((prev) => {
            if (prev.length === 0) return prev;
            const next = [...prev];
            const last = next[next.length - 1];
            if (last.role === "assistant" && last.text === "") {
              next[next.length - 1] = { ...last, text: msg, error: true };
            }
            return next;
          });
          console.error("sendAgentMessage error", err);
        }
      } finally {
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
          idleTimerRef.current = null;
        }
        turnAbortRef.current = null;
        setAgentStreaming(false);
        // Release the "pin user msg to top" lock so future scrolls behave
        // normally. (If the user manually scrolled to the bottom mid-stream
        // it was already cleared.)
        pinUserToTopRef.current = false;
      }
    },
    [
      agentMessages,
      agentStreaming,
      agentAttachments,
      projectId,
      companyId,
      executeAgentTool,
      assertCredits,
    ],
  );

  // Botón "Detener": corta el turno en curso sin borrar el chat. Bumpea la
  // generación (el catch del turno viejo lo saltea) y aborta el stream.
  const stopAgentTurn = useCallback(() => {
    console.log("[video-agent] Detener: cancelando el turno en curso");
    agentGenerationRef.current += 1;
    try {
      turnAbortRef.current?.abort();
    } catch {
      /* noop */
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Desbloquear TODAS las interacciones pendientes: ask_user,
    // request_user_upload y request_product_selection usan resolvers SEPARADOS.
    // Sin resolver los tres, el tool loop viejo queda colgado para siempre (y una
    // interacción posterior le pisa el resolver compartido) → leak (Greptile).
    const askResolver = askResolverRef.current;
    askResolverRef.current = null;
    setActiveAsk(null);
    if (askResolver) askResolver(JSON.stringify({ cancelled: true }));
    const uploadResolver = uploadResolverRef.current;
    uploadResolverRef.current = null;
    setAgentUploadRequest(null);
    if (uploadResolver)
      uploadResolver({ content: JSON.stringify({ cancelled: true }) });
    const productResolver = productPickResolverRef.current;
    productPickResolverRef.current = null;
    setAgentProductRequest(null);
    if (productResolver) productResolver(null);
    // Feedback visible (reusa el placeholder vacío del turno si quedó uno).
    setAgentMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === "assistant" && last.text === "") {
        next[next.length - 1] = { ...last, text: "⏹ Detenido." };
      } else {
        next.push({ role: "assistant", text: "⏹ Detenido." });
      }
      return next;
    });
    setAgentStreaming(false);
  }, []);

  // ── AI scene creation ───────────────────────────────────────────────────
  const handleFrameUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    target: "first" | "last",
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId || !companyId) return;
    // Show blob URL preview immediately
    const blobUrl = URL.createObjectURL(file);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
      if (target === "first") setSceneFirstFrameAssetId(asset.id);
      else setSceneLastFrameAssetId(asset.id);
    } catch (err) {
      console.error("[VideoEditor] Frame upload failed:", err);
      URL.revokeObjectURL(blobUrl);
      setUploadErrorMsg(describeUploadError(err, file.name));
    }
  };

  // Import a generated image into the project as a regular image asset.
  // Used by the "Imágenes del proyecto" overlay so the user can attach a
  // previously-generated image as a reference for a new generation. Returns
  // the resulting asset id, or null if it can't be imported (e.g. signed
  // URL still pending). Idempotent within a session: the result is cached
  // in `imageGenImportedMap` so re-clicking the same generation reuses the
  // existing asset id instead of uploading a new copy.
  const importGeneratedImageAsAsset = useCallback(
    async (genId: string): Promise<string | null> => {
      const cached = imageGenImportedMap[genId];
      if (cached) return cached;
      const gen = imageGenResults.find((r) => r.id === genId);
      if (!gen?.signedUrl || !projectId || !imageGenCompanyId) return null;
      try {
        const resp = await fetch(gen.signedUrl);
        const blob = await resp.blob();
        const ext = (gen.fileName?.split(".").pop() || "jpg").toLowerCase();
        const file = new File(
          [blob],
          gen.fileName || `ai-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: imageGenCompanyId,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        setImageGenImportedMap((prev) => ({ ...prev, [genId]: asset.id }));
        return asset.id;
      } catch (err) {
        console.error(
          "[VideoEditor] failed to import generated image as asset",
          genId,
          err,
        );
        return null;
      }
    },
    [imageGenImportedMap, imageGenResults, projectId, imageGenCompanyId],
  );

  // Productos pill: importa la imagen de un producto del catálogo como asset
  // del proyecto para poder adjuntarla como referencia. Cacheado por product
  // id dentro de la sesión.
  const importProductImageForGen = useCallback(
    async (product: MockProductV2): Promise<string | null> => {
      const cached = imageGenProductImportedMap[product.id];
      if (cached) {
        if (product.imageUrl) {
          setAssetPreviewUrls((prev) =>
            prev[cached] ? prev : { ...prev, [cached]: product.imageUrl },
          );
        }
        return cached;
      }
      const cid = companyId ?? imageGenCompanyId;
      if (!product.imageUrl || !projectId || !cid) return null;
      try {
        const resp = await fetch(product.imageUrl);
        const blob = await resp.blob();
        const ext = (blob.type.split("/")[1] || "jpg").toLowerCase();
        const file = new File(
          [blob],
          `${(product.name || "producto").slice(0, 40)}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: cid,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        setImageGenProductImportedMap((prev) => ({
          ...prev,
          [product.id]: asset.id,
        }));
        // Registrar para que tampoco aparezca en la grilla Imágenes de Archivos.
        setProductSceneAssetMap((prev) => ({ ...prev, [product.id]: asset.id }));
        // También queda disponible en "Subidas".
        setImageGenGalleryAssetIds((prev) =>
          prev.includes(asset.id) ? prev : [...prev, asset.id],
        );
        return asset.id;
      } catch (err) {
        console.error(
          "[VideoEditor] failed to import product image for gen",
          product.id,
          err,
        );
        return null;
      }
    },
    [imageGenProductImportedMap, projectId, companyId, imageGenCompanyId],
  );

  // Cargar los productos de la marca del proyecto cuando se activa el pill
  // "Productos" en la modal del image-gen.
  useEffect(() => {
    if (activePanel !== "ai-image-gen") return;
    if (imageGenGalleryFilter !== "productos") return;
    if (imageGenProductItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid || !brandProfile?.id) {
      console.warn("[productos-pill] sin companyId o brandId:", {
        companyId: cid,
        brandId: brandProfile?.id,
      });
      return;
    }
    let cancelled = false;
    (async () => {
      setImageGenProductsLoading(true);
      console.log("[productos-pill] fetch →", {
        companyId: cid,
        brandId: brandProfile?.id,
      });
      try {
        const items = await fetchUserProductsByBrand(cid, brandProfile.id);
        console.log("[productos-pill] recibidos:", items.length, items);
        if (!cancelled)
          setImageGenProductItems(items.filter((p) => p.imageUrl));
      } catch (e) {
        console.error("[productos-pill] fetch falló:", e);
      } finally {
        // Siempre apagamos el loading (aunque el efecto se re-dispare), si no
        // el spinner queda colgado para siempre.
        setImageGenProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    imageGenGalleryFilter,
    companyId,
    imageGenCompanyId,
    brandProfile?.id,
    imageGenProductItems.length,
  ]);

  // Cargar las escenas cuando se activa el pill "Escenas" en la modal.
  useEffect(() => {
    if (activePanel !== "ai-image-gen") return;
    if (imageGenGalleryFilter !== "escenas") return;
    if (imageGenSceneItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid) return;
    let cancelled = false;
    (async () => {
      setImageGenScenesLoading(true);
      try {
        const items = await fetchSceneImages(cid);
        if (!cancelled) setImageGenSceneItems(items.filter((s) => s.imageUrl));
      } catch (e) {
        console.error("[escenas-pill] fetch falló:", e);
      } finally {
        setImageGenScenesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    imageGenGalleryFilter,
    companyId,
    imageGenCompanyId,
    imageGenSceneItems.length,
  ]);

  // Cargar el historial (imágenes generadas del cliente, por marca) cuando se
  // activa el pill "Historial" en la modal del image-gen.
  useEffect(() => {
    if (activePanel !== "ai-image-gen") return;
    if (imageGenGalleryFilter !== "historial") return;
    if (imageGenHistorialItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid || !brandProfile?.id) return;
    let cancelled = false;
    (async () => {
      setImageGenHistorialLoading(true);
      try {
        const items = await fetchHistorialImages(cid, brandProfile.id);
        if (!cancelled)
          setImageGenHistorialItems(items.filter((h) => h.imageUrl));
      } catch (e) {
        console.error("[historial-pill] fetch falló:", e);
      } finally {
        setImageGenHistorialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    imageGenGalleryFilter,
    companyId,
    imageGenCompanyId,
    brandProfile?.id,
    imageGenHistorialItems.length,
  ]);

  // [Archivos] Importa un producto del catálogo como imagen del proyecto, para
  // que se comporte igual que cualquier imagen (aparece en Imágenes, mismo menú).
  const importProductToImageAsset = useCallback(
    async (product: MockProductV2): Promise<string | null> => {
      // Dedup: si ya lo importamos, reusar el mismo asset (no subir otra copia).
      const cachedId = productSceneAssetMap[product.id];
      if (cachedId) {
        // El mapping producto→asset se PERSISTE en el proyecto, pero la preview
        // (blob URL) NO sobrevive a una recarga. Si volvemos de otra sesión, el
        // asset existe pero no tiene preview → el slot de referencia/frame sale
        // vacío ("..."). Aseguramos la preview con la URL firmada del producto.
        if (product.imageUrl) {
          setAssetPreviewUrls((prev) =>
            prev[cachedId] ? prev : { ...prev, [cachedId]: product.imageUrl },
          );
        }
        return cachedId;
      }
      const cid = companyId ?? imageGenCompanyId;
      if (!product.imageUrl || !projectId || !cid) return null;
      try {
        const resp = await fetch(product.imageUrl);
        const blob = await resp.blob();
        const ext = (blob.type.split("/")[1] || "jpg").toLowerCase();
        const file = new File(
          [blob],
          `${(product.name || "producto").slice(0, 40)}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: cid,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        // NO se agrega a imageLibraryAssets: el producto/escena vive en su pill,
        // no en Imágenes. Se registra para dedup + filtrado de Imágenes.
        setProductSceneAssetMap((prev) => ({
          ...prev,
          [product.id]: asset.id,
        }));
        return asset.id;
      } catch (err) {
        console.error(
          "[VideoEditor] import product to image asset failed",
          err,
        );
        return null;
      }
    },
    [productSceneAssetMap, companyId, imageGenCompanyId, projectId],
  );

  // Importa la foto de un modelo como asset del proyecto (para usarla como la
  // persona del "Frame inicial" en Modo Diálogo). Dedup vía `modelAssetMap`.
  const importModelToImageAsset = useCallback(
    async (model: ModelV2): Promise<string | null> => {
      const cachedId = modelAssetMap[model.id];
      if (cachedId) {
        if (model.thumbnailUrl)
          setAssetPreviewUrls((prev) =>
            prev[cachedId]
              ? prev
              : { ...prev, [cachedId]: model.thumbnailUrl },
          );
        return cachedId;
      }
      const cid = companyId ?? imageGenCompanyId;
      if (!model.thumbnailUrl || !projectId || !cid) return null;
      try {
        const resp = await fetch(model.thumbnailUrl);
        const blob = await resp.blob();
        const ext = (blob.type.split("/")[1] || "jpg").toLowerCase();
        const file = new File(
          [blob],
          `${(model.nombre || "modelo").slice(0, 40)}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: cid,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        setModelAssetMap((prev) => ({ ...prev, [model.id]: asset.id }));
        return asset.id;
      } catch (err) {
        console.error("[VideoEditor] import model to image asset failed", err);
        return null;
      }
    },
    [modelAssetMap, companyId, imageGenCompanyId, projectId],
  );

  // Carga los modelos (globales + de la empresa) cuando se abre el picker O
  // cuando se abre el panel Archivos (los necesitamos para ocultar de
  // Multimedia, por nombre, los assets que son fotos de modelos).
  useEffect(() => {
    if (!modelPickerOpen && activePanel !== "files") return;
    if (modelItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid) return;
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try {
        const items = await fetchUserModels(cid);
        if (!cancelled) setModelItems(items.filter((m) => m.thumbnailUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch models failed", e);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    modelPickerOpen,
    activePanel,
    modelItems.length,
    companyId,
    imageGenCompanyId,
  ]);

  // [Archivos] Cargar los productos de la marca cuando se activa el pill
  // "Productos" del panel Archivos.
  useEffect(() => {
    if (activePanel !== "files") return;
    if (filesTab !== "productos") return;
    if (filesProductItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid || !brandProfile?.id) return;
    let cancelled = false;
    (async () => {
      setFilesProductsLoading(true);
      try {
        const items = await fetchUserProductsByBrand(cid, brandProfile.id);
        if (!cancelled) setFilesProductItems(items.filter((p) => p.imageUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch products for files failed", e);
      } finally {
        setFilesProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    filesTab,
    companyId,
    imageGenCompanyId,
    brandProfile?.id,
    filesProductItems.length,
  ]);

  // [Archivos] Cargar las escenas (fondos guardados del cliente + defaults de
  // ATLAS) cuando se activa el pill "Escenas".
  useEffect(() => {
    if (activePanel !== "files") return;
    if (filesTab !== "escenas") return;
    if (filesSceneItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid) return;
    let cancelled = false;
    (async () => {
      setFilesScenesLoading(true);
      try {
        const items = await fetchSceneImages(cid);
        if (!cancelled) setFilesSceneItems(items.filter((s) => s.imageUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch scenes failed", e);
      } finally {
        setFilesScenesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    filesTab,
    companyId,
    imageGenCompanyId,
    filesSceneItems.length,
  ]);

  // [Archivos] Cargar el historial (imágenes generadas del cliente) filtrado
  // por la marca del proyecto, cuando se activa el pill "Historial".
  useEffect(() => {
    if (activePanel !== "files") return;
    if (filesTab !== "historial") return;
    if (filesHistorialItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid || !brandProfile?.id) return;
    let cancelled = false;
    (async () => {
      setFilesHistorialLoading(true);
      try {
        const items = await fetchHistorialImages(cid, brandProfile.id);
        if (!cancelled)
          setFilesHistorialItems(items.filter((h) => h.imageUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch historial failed", e);
      } finally {
        setFilesHistorialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    filesTab,
    companyId,
    imageGenCompanyId,
    brandProfile?.id,
    filesHistorialItems.length,
  ]);

  // Re-sign an expired signed URL for a generated image (uses project companyId)
  const refreshGenImageSignedUrl = useCallback(
    async (id: string) => {
      const target = imageGenResults.find((r) => r.id === id);
      if (!target?.fileName || !imageGenCompanyId) return;
      try {
        const { data: signed } = await supabase.storage
          .from("generadas")
          .createSignedUrl(
            `${imageGenCompanyId}/full/${target.fileName}`,
            86400,
          );
        if (signed?.signedUrl) {
          setImageGenResults((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, signedUrl: signed.signedUrl } : r,
            ),
          );
        }
      } catch {
        // silent
      }
    },
    [imageGenResults, imageGenCompanyId],
  );

  // Reset edit mode whenever the selected detail image changes or closes.
  // Exception: if a pending edit prompt was queued (e.g. by the agent
  // calling prefill_edit_generated_image), enter edit mode with it.
  useEffect(() => {
    setImageGenEditError(null);
    const pending = pendingGenEditPromptRef.current;
    if (pending && imageGenDetailId) {
      pendingGenEditPromptRef.current = null;
      setImageGenEditing(true);
      setImageGenEditPrompt(pending);
    } else {
      setImageGenEditing(false);
      setImageGenEditPrompt("");
    }
  }, [imageGenDetailId]);

  // Scroll detail carousel to the clicked image's index when detail opens
  useEffect(() => {
    if (!imageGenDetailId) return;
    const idx = imageGenResults.findIndex((r) => r.id === imageGenDetailId);
    if (idx < 0) return;
    // Wait for DOM to render the detail view, then scroll
    const t = setTimeout(() => {
      const el = imageGenDetailScrollRef.current;
      if (!el) return;
      el.scrollTo({ left: idx * el.clientWidth, behavior: "auto" });
    }, 0);
    return () => clearTimeout(t);
  }, [imageGenDetailId, imageGenResults]);

  // ── AI Image Generation helpers ───────────────────────────────────────────
  const openImageGenPanel = useCallback((target: ImageGenTarget) => {
    setImageGenTarget(target);
    setImageGenPrompt("");
    // Do NOT clear imageGenResults — keep existing project-generated images visible
    setImageGenError(null);
    setImageGenDetailId(null);
    setImageGenDetailFromFiles(false); // venimos de una escena, no de Multimedia
    setImageGenFromModelFlow(false); // apertura normal: mostrar "Usar modelo"
    setShowImageGenHistorial(false);
    setActivePanel("ai-image-gen");
    setImageSlotMenu(null);
  }, []);

  const backToSceneFromImageGen = useCallback(() => {
    // Slot-preview takes precedence: cancel the preview (and any inline edit
    // mode) without committing.
    if (slotPreview) {
      setSlotPreview(null);
      setSlotPreviewEditing(false);
      setSlotPreviewEditPrompt("");
      setActivePanel("ai-scene");
      return;
    }
    if (imageGenDetailId) {
      // Si el detalle/edición se abrió desde Multimedia, volvemos ahí (no a la
      // grilla de generar ni a la escena).
      if (imageGenDetailFromFiles) {
        setImageGenDetailId(null);
        setImageGenEditing(false);
        setImageGenEditPrompt("");
        setImageGenDetailFromFiles(false);
        setActivePanel("files");
        setFilesTab("media");
        return;
      }
      setImageGenDetailId(null);
      return;
    }
    setActivePanel("ai-scene");
  }, [imageGenDetailId, imageGenDetailFromFiles, slotPreview]);

  // Poll imagenes_generadas rows until file_name is available, then resolve signed URL
  const pollGeneratedImage = useCallback(
    async (imageId: number | string, compId: string) => {
      const maxAttempts = 20;
      const delayMs = 2000;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const { data, error } = await supabase
            .from("imagenes_generadas")
            .select("file_name, created_at")
            .eq("id", imageId)
            .eq("id_company", compId)
            .single();
          if (!error && data?.file_name) {
            const filePath = `${compId}/full/${data.file_name}`;
            const { data: signed } = await supabase.storage
              .from("generadas")
              .createSignedUrl(filePath, 86400);
            if (signed?.signedUrl) {
              setImageGenResults((prev) =>
                prev.map((r) =>
                  r.id === String(imageId)
                    ? {
                        ...r,
                        fileName: data.file_name,
                        signedUrl: signed.signedUrl,
                        loading: false,
                        createdAt:
                          (data as any).created_at || r.createdAt,
                      }
                    : r,
                ),
              );
              return;
            }
          }
        } catch {}
        await new Promise((res) => setTimeout(res, delayMs));
      }
      // Mark failed after timeout
      setImageGenResults((prev) =>
        prev.map((r) =>
          r.id === String(imageId) ? { ...r, loading: false } : r,
        ),
      );
    },
    [],
  );

  const startImageGeneration = useCallback(
    async (override?: { prompt?: string; attachedAssetIds?: string[] }) => {
      const prompt = (override?.prompt ?? imageGenPrompt).trim();
      if (!prompt) return;
      if (!assertCredits(creditsForImage())) return;
      const attachedIds =
        override?.attachedAssetIds ?? imageGenAttachedAssetIds;
      setImageGenBusy(true);
      setImageGenError(null);
      setImageGenPendingCount((c) => c + 1);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user session");

        const body: Record<string, any> = {
          user_id: user.id,
          variante_id: [],
          modo: "creativo_plus",
          // Trackeo (ADR-585): imagen generada desde el Estudio.
          modo_utilizado: "estudio_imagen",
          cantidad_imagenes: 1,
          Format: aspectRatio,
          new_ui: true,
          prompt,
        };

        // Attach any selected reference images as signed URLs
        if (attachedIds.length > 0) {
          const assets = await fetchAssetsByIds(attachedIds);
          const urls = (
            await Promise.all(
              assets.map((a) =>
                getAssetSignedUrl(a.storage_path).catch(() => null),
              ),
            )
          ).filter((u): u is string => !!u);
          if (urls.length > 0) body.reference_images = urls;
        }

        // Ruteo atlas/n8n (ADR-587): este flujo es freeform PURO
        // (variante_id: [] siempre) — mismo contrato que el freeform del
        // asistente (ADR-583). El backend exige execution_id; con atlas
        // corremos la carrera respuesta-vs-poll para que un 202 (>150s) no
        // pierda la imagen. El fallback n8n es el webhook V2 byte-idéntico.
        const executionId = crypto.randomUUID();
        body.execution_id = executionId;
        let ids: (number | string)[];
        let compId: string;
        if (USE_ATLAS_BACKEND) {
          if (companyId) {
            const result = await awaitImageGeneration({
              fetchPromise: postGenerateAdAtlas(body),
              executionId,
              companyId,
              expectedCount: 1,
            });
            ids = result.imageIds;
            compId = result.companyId || companyId;
          } else {
            // Sin companyId no hay poll de respaldo — camino directo.
            const data = await postGenerateAdAtlas(body);
            ids = Array.isArray(data?.imagenes_generadas_ids)
              ? data.imagenes_generadas_ids
              : [];
            compId = (data?.id_company as string) || "";
          }
        } else {
          const res = await fetch(
            "https://ai-agency-dt7d.onrender.com/webhook/generar_anuncio_v2",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: GENERATE_AUTH_TOKEN,
              },
              body: JSON.stringify(body),
            },
          );
          if (!res.ok) throw new Error(`API ${res.status}`);
          const data = await res.json();
          if (
            data?.response !== "success" ||
            !Array.isArray(data?.imagenes_generadas_ids)
          ) {
            throw new Error("No se recibieron imágenes generadas.");
          }
          ids = data.imagenes_generadas_ids;
          compId = data.id_company || companyId || "";
        }
        if (ids.length === 0) {
          throw new Error("No se recibieron imágenes generadas.");
        }
        setImageGenCompanyId(compId);
        // Prepend new placeholders so newest appear first in grid
        setImageGenResults((prev) => [
          ...ids.map((id) => ({
            id: String(id),
            loading: true,
            session: true,
          })),
          ...prev,
        ]);
        setImageGenHistorial((prev) =>
          [prompt, ...prev.filter((p) => p !== prompt)].slice(0, 30),
        );
        setImageGenPrompt("");
        setImageGenAttachedAssetIds([]);
        // Start polling in parallel
        ids.forEach((id) => {
          pollGeneratedImage(id, compId);
        });
      } catch (err: any) {
        console.error("[VideoEditor] image generation failed:", err);
        setImageGenError(err?.message || "Error al generar imágenes");
      } finally {
        setImageGenBusy(false);
        setImageGenPendingCount((c) => Math.max(0, c - 1));
      }
    },
    [
      imageGenPrompt,
      companyId,
      pollGeneratedImage,
      aspectRatio,
      imageGenAttachedAssetIds,
      assertCredits,
    ],
  );

  // Remove a generated image from the project's gallery (local state only — persisted on next save)
  const deleteGeneratedImage = useCallback(
    (id: string) => {
      setImageGenResults((prev) => {
        const next = prev.filter((r) => r.id !== id);
        // If the detail view was showing the removed image, navigate to a sibling or close
        if (imageGenDetailId === id) {
          if (next.length > 0) {
            setImageGenDetailId(next[0].id);
          } else {
            setImageGenDetailId(null);
          }
        }
        return next;
      });
    },
    [imageGenDetailId],
  );

  // Edit a generated image via editor_ia webhook; replaces the current detail image with the edited result
  const handleEditImage = useCallback(async () => {
    const detailId = imageGenDetailId;
    const instruction = imageGenEditPrompt.trim();
    if (!detailId || !instruction || !imageGenCompanyId) return;
    if (!assertCredits(creditsForImage())) return;
    setImageGenEditSubmitting(true);
    setImageGenEditError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user session");

      // Atlas o n8n según flag (ADR-601).
      const data = await postImageEdit({
        user_id: user.id,
        imagen_id: detailId,
        instruction,
        mode: "edit_general",
        modo_utilizado: "estudio_edicion",
      });

      if (data?.response === "error_moderation") {
        throw new Error(
          "La instrucción fue rechazada por moderación. Probá con otra.",
        );
      }

      const newImageIdRaw: string | number | undefined =
        data?.edited_image_id ?? data?.imagen_id ?? data?.id;
      if (!newImageIdRaw)
        throw new Error("No se recibió el ID de la imagen editada");

      const newIdStr = String(newImageIdRaw);
      // Resolve DB row (id UUID or file_name)
      let newRow: {
        id: string;
        file_name: string;
        created_at?: string;
      } | null = null;
      const { data: idMatch } = await supabase
        .from("imagenes_generadas")
        .select("id, file_name, created_at")
        .eq("id", newIdStr)
        .eq("id_company", imageGenCompanyId)
        .maybeSingle();
      if (idMatch) newRow = idMatch as any;
      if (!newRow) {
        const { data: exact } = await supabase
          .from("imagenes_generadas")
          .select("id, file_name, created_at")
          .eq("file_name", newIdStr)
          .eq("id_company", imageGenCompanyId)
          .maybeSingle();
        if (exact) newRow = exact as any;
      }
      if (!newRow) {
        const { data: likeData } = await supabase
          .from("imagenes_generadas")
          .select("id, file_name, created_at")
          .like("file_name", `${newIdStr}.%`)
          .eq("id_company", imageGenCompanyId)
          .maybeSingle();
        if (likeData) newRow = likeData as any;
      }
      if (!newRow?.file_name)
        throw new Error("No se pudo cargar la imagen editada");

      const { data: signed } = await supabase.storage
        .from("generadas")
        .createSignedUrl(
          `${imageGenCompanyId}/full/${newRow.file_name}`,
          86400,
        );
      if (!signed?.signedUrl)
        throw new Error("No se pudo obtener la URL firmada");

      const newResult: GenImageResult = {
        id: newRow.id,
        fileName: newRow.file_name,
        signedUrl: signed.signedUrl,
        loading: false,
        createdAt: newRow.created_at || undefined,
        // La grilla del panel de generar muestra SOLO las de esta sesión
        // (`sessionGenResults`). Sin esta marca la imagen editada se
        // generaba bien pero quedaba invisible junto a las otras.
        session: true,
      };

      // Prepend the edited image so it appears at the top of the grid; switch detail to it
      setImageGenResults((prev) => {
        // Avoid duplicates if already present
        const filtered = prev.filter((r) => r.id !== newResult.id);
        return [newResult, ...filtered];
      });
      setImageGenDetailId(newResult.id);
      setImageGenEditPrompt("");
      setImageGenEditing(false);
    } catch (err: any) {
      console.error("[VideoEditor] edit image failed:", err);
      setImageGenEditError(err?.message || "No se pudo editar la imagen");
    } finally {
      setImageGenEditSubmitting(false);
    }
  }, [imageGenDetailId, imageGenEditPrompt, imageGenCompanyId, assertCredits]);

  // "Utilizar" — upload the selected generated image as a project asset and,
  // if there's an explicit slot target (set when the panel was opened from the
  // AI scene flow), also assign it to that slot. When the panel was opened
  // without a target (e.g. from the agent or the root tools menu), we still
  // upload the image so it appears in the media library / list_project_assets.
  const useGeneratedImage = useCallback(
    async (result: GenImageResult) => {
      if (!result.signedUrl || !projectId || !companyId) return;
      try {
        const resp = await fetch(result.signedUrl);
        const blob = await resp.blob();
        const ext = (result.fileName?.split(".").pop() || "jpg").toLowerCase();
        const file = new File(
          [blob],
          result.fileName || `ai-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        if (imageGenTarget) {
          if (imageGenTarget.kind === "first")
            setSceneFirstFrameAssetId(asset.id);
          else if (imageGenTarget.kind === "last")
            setSceneLastFrameAssetId(asset.id);
          else setSceneRefAssetIds((prev) => [...prev, asset.id]);
          // Return to scene panel only when we came from there.
          setActivePanel("ai-scene");
        } else {
          // No slot context: just close the detail view and leave the panel
          // (the user explicitly opened image gen without a scene target).
          setActivePanel("none");
        }
        setImageGenDetailId(null);
      } catch (err) {
        console.error("[VideoEditor] useGeneratedImage failed:", err);
      }
    },
    [imageGenTarget, projectId, companyId],
  );

  // Asigna una imagen de la biblioteca al slot de la escena IA. first/last
  // reemplazan el valor; ref agrega si no estaba (mismo comportamiento que la
  // modal vieja). El toggle de ref (sacar una ya elegida) lo maneja el grid.
  const assignSceneImage = useCallback(
    (kind: "first" | "last" | "ref", assetId: string) => {
      if (kind === "first") setSceneFirstFrameAssetId(assetId);
      else if (kind === "last") setSceneLastFrameAssetId(assetId);
      else
        setSceneRefAssetIds((prev) =>
          prev.includes(assetId) ? prev : [...prev, assetId],
        );
    },
    [],
  );

  // ── Slot-preview helpers ──────────────────────────────────────────────────
  // Commit the previewed asset to its target scene slot and return to the
  // scene panel.
  const useSlotPreviewImage = useCallback(() => {
    if (!slotPreview) return;
    const { assetId, target } = slotPreview;
    if (target.kind === "first") setSceneFirstFrameAssetId(assetId);
    else if (target.kind === "last") setSceneLastFrameAssetId(assetId);
    else
      setSceneRefAssetIds((prev) =>
        prev.includes(assetId) ? prev : [...prev, assetId],
      );
    setSlotPreview(null);
    setSlotPreviewEditing(false);
    setSlotPreviewEditPrompt("");
    setActivePanel("ai-scene");
  }, [slotPreview]);


  // Submit the edit prompt: pivot into the image-gen flow with the previewed
  // asset attached as a reference, the prompt pre-filled, and the slot's
  // target preserved (so the next "Utilizar" sends the new image back).
  const submitSlotPreviewEdit = useCallback(() => {
    if (!slotPreview) return;
    const prompt = slotPreviewEditPrompt.trim();
    if (!prompt) return;
    const { assetId, target } = slotPreview;
    setImageGenTarget(target);
    setImageGenAttachedAssetIds([assetId]);
    setImageGenPrompt(prompt);
    setSlotPreview(null);
    setSlotPreviewEditing(false);
    setSlotPreviewEditPrompt("");
    setImageGenError(null);
    setImageGenDetailId(null);
    // Fire-and-forget: kick off the generation immediately so the user lands
    // in the grid view watching the result come in. We pass the prompt and
    // attached asset ids explicitly to avoid racing with React state.
    void startImageGeneration({ prompt, attachedAssetIds: [assetId] });
  }, [slotPreview, slotPreviewEditPrompt, startImageGeneration]);

  // Start "Seleccionar de video" flow — open the frame picker overlay to choose a frame from a timeline clip
  const startFrameFromVideoPicker = useCallback((target: ImageGenTarget) => {
    setImageSlotMenu(null);
    setImageSlotMenuRect(null);
    setFrameFromVideoTarget(target);
    setShowAgentChat(false);
  }, []);

  // Extract a frame (first or last) from a clip and assign it to the target slot
  const pickFrameFromClip = useCallback(
    async (clipId: string, which: "first" | "last") => {
      const target = frameFromVideoTarget;
      if (!target || !projectId || !companyId) return;
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      setFrameFromVideoClipMenu(null);

      try {
        // Resolve a frame data/URL from available sources. For AI-generated
        // clips we have a full-resolution first/last frame already cached. For
        // regular video clips we deliberately SKIP the clipThumbnails strip
        // (which is intentionally low-res for the timeline) and capture from
        // the original video source so the picked frame stays sharp.
        let frameDataUrl: string | null = null;
        if (clip.type === "ai-generated") {
          frameDataUrl =
            which === "first"
              ? clip.aiFirstFrame || null
              : clip.aiLastFrame || null;
        }
        // Capture from the video source using a canvas at native resolution.
        if (!frameDataUrl && clip.src) {
          frameDataUrl = await new Promise<string | null>((resolve) => {
            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.src = clip.src!;
            video.muted = true;
            video.preload = "auto";
            const onLoaded = () => {
              const seekTo =
                which === "first"
                  ? 0.05
                  : Math.max(0, (video.duration || 0) - 0.05);
              video.currentTime = seekTo;
            };
            const onSeeked = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth || 720;
                canvas.height = video.videoHeight || 1280;
                const ctx = canvas.getContext("2d");
                if (!ctx) return resolve(null);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.92));
              } catch {
                resolve(null);
              }
            };
            video.addEventListener("loadedmetadata", onLoaded, { once: true });
            video.addEventListener("seeked", onSeeked, { once: true });
            video.addEventListener("error", () => resolve(null), {
              once: true,
            });
          });
        }
        if (!frameDataUrl) {
          console.warn(
            "[VideoEditor] Could not extract frame for clip",
            clipId,
          );
          return;
        }

        // Convert to File and upload as project asset
        const res = await fetch(frameDataUrl);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
        const file = new File(
          [blob],
          `frame-${clip.id}-${which}-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const blobUrl = URL.createObjectURL(blob);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));

        // Route through the slot-preview screen instead of assigning directly,
        // so the user can confirm (Utilizar) or branch into editing the image
        // (Editar → image-gen flow with this frame attached as reference).
        setSlotPreview({ assetId: asset.id, target });
        setSlotPreviewEditing(false);
        setSlotPreviewEditPrompt("");
        setActivePanel("ai-image-gen");
      } catch (err) {
        console.error("[VideoEditor] pickFrameFromClip failed:", err);
      } finally {
        setFrameFromVideoTarget(null);
      }
    },
    [frameFromVideoTarget, projectId, companyId, clips],
  );

  // Extend an existing video: open AI scene panel (General + frames) with the clip's last frame
  // pre-loaded as the new scene's first frame.
  const extendVideoClip = useCallback(
    async (clipId: string) => {
      if (!projectId || !companyId) return;
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;

      try {
        // Resolve the last frame at native resolution. AI-generated clips
        // already have full-res aiLastFrame cached; for regular video clips we
        // skip the low-res timeline thumbnails and capture directly from the
        // video source.
        let frameDataUrl: string | null = null;
        if (clip.type === "ai-generated") {
          frameDataUrl = clip.aiLastFrame || null;
        }
        if (!frameDataUrl && clip.src) {
          frameDataUrl = await new Promise<string | null>((resolve) => {
            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.src = clip.src!;
            video.muted = true;
            video.preload = "auto";
            const onLoaded = () => {
              video.currentTime = Math.max(0, (video.duration || 0) - 0.05);
            };
            const onSeeked = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth || 720;
                canvas.height = video.videoHeight || 1280;
                const ctx = canvas.getContext("2d");
                if (!ctx) return resolve(null);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.92));
              } catch {
                resolve(null);
              }
            };
            video.addEventListener("loadedmetadata", onLoaded, { once: true });
            video.addEventListener("seeked", onSeeked, { once: true });
            video.addEventListener("error", () => resolve(null), {
              once: true,
            });
          });
        }
        if (!frameDataUrl) {
          console.warn(
            "[VideoEditor] extendVideoClip: could not extract last frame for",
            clipId,
          );
          return;
        }

        const res = await fetch(frameDataUrl);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
        const file = new File(
          [blob],
          `extend-${clip.id}-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const blobUrl = URL.createObjectURL(blob);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));

        // Configure the ai-scene panel: General + frames, with this frame as first frame.
        setEditingClipId(null);
        setSceneMode("general");
        setSceneRefMode("frames");
        setScenePrompt("");
        setSceneDialogue("");
        setSceneRefAssetIds([]);
        setSceneFirstFrameAssetId(asset.id);
        setSceneLastFrameAssetId(null);
        setActivePanel("ai-scene");
      } catch (err) {
        console.error("[VideoEditor] extendVideoClip failed:", err);
      }
    },
    [projectId, companyId, clips],
  );

  // Upload a file and attach it to the next image generation (and add to persistent gallery)
  const handleImageGenAttachUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId || !companyId) return;
    const blobUrl = URL.createObjectURL(file);
    setImageGenUploading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
      setImageGenGalleryAssetIds((prev) =>
        prev.includes(asset.id) ? prev : [...prev, asset.id],
      );
      setImageGenAttachedAssetIds((prev) =>
        prev.includes(asset.id) ? prev : [...prev, asset.id],
      );
      setImageGenGalleryOpen(false);
    } catch (err) {
      console.error("[VideoEditor] image-gen attach upload failed:", err);
      URL.revokeObjectURL(blobUrl);
      setUploadErrorMsg(describeUploadError(err, file.name));
    } finally {
      setImageGenUploading(false);
    }
  };

  // Resolve signed URLs + metadata (nombre/tipo) para los assets de la galería.
  // Fetcheamos los ids que no tengan META todavía (aunque tengan preview), así
  // un video persistido —que tiene blob pero no es imagen— queda con type
  // "video" y se filtra del modal.
  useEffect(() => {
    const needFetch = imageGenGalleryAssetIds.filter(
      (id) => !galleryAssetMeta[id] || !assetPreviewUrls[id],
    );
    if (needFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const assets = await fetchAssetsByIds(needFetch);
        if (cancelled) return;
        setGalleryAssetMeta((prev) => {
          const next = { ...prev };
          for (const a of assets)
            next[a.id] = { name: a.name, type: a.type };
          return next;
        });
        const results = await Promise.all(
          assets
            .filter((a) => !assetPreviewUrls[a.id])
            .map(async (a) => {
              try {
                const url = await getAssetSignedUrl(a.storage_path);
                return url ? ([a.id, url] as const) : null;
              } catch {
                return null;
              }
            }),
        );
        if (cancelled) return;
        const updates: Record<string, string> = {};
        for (const r of results) if (r) updates[r[0]] = r[1];
        if (Object.keys(updates).length > 0) {
          setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [imageGenGalleryAssetIds, assetPreviewUrls, galleryAssetMeta]);

  const handleRefImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId || !companyId) return;
    const blobUrl = URL.createObjectURL(file);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
      setSceneRefAssetIds((prev) => [...prev, asset.id]);
    } catch (err) {
      console.error("[VideoEditor] Reference image upload failed:", err);
      URL.revokeObjectURL(blobUrl);
      setUploadErrorMsg(describeUploadError(err, file.name));
    }
  };

  // Guard síncrona anti doble-click: clips con una generación de video IA en
  // vuelo. El estado de React (aiExecutionId) se publica async, así que dos
  // clicks en el mismo tick leerían ambos el clip "sin id" y generarían dos
  // jobs con UUIDs distintos → dos videos cobrados. El ref se toca sincrónico,
  // sin ventana de carrera (JS single-thread, no hay await entre check y add).
  const aiVideoInFlightRef = useRef<Set<string>>(new Set());

  const generateAiClip = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      // Ya hay una generación en vuelo para este clip (doble click / carrera
      // con el panel): el primer click ya la está haciendo — no-op.
      if (aiVideoInFlightRef.current.has(clipId)) return;
      // Guard explícita: si falta info crítica marcamos el clip con error
      // visible en vez de hacer return silencioso (sino el usuario clickea
      // Reintentar y no pasa nada).
      if (!clip.aiPrompt || !companyId || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "error" as AiClipStatus,
                  aiError:
                    "No se pudo iniciar la generación: falta información del proyecto. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }

      // Pre-flight credit check. Centralized here so retry/draft/agent paths
      // all hit the same guard — server still enforces the spend.
      if (!assertCredits(creditsForAiClip(clip))) return;

      aiVideoInFlightRef.current.add(clipId);

      // Mark as generating
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                aiStatus: "generating" as AiClipStatus,
                aiError: undefined,
                aiGenerationStartedAt: Date.now(),
                name: clip.aiPrompt!.slice(0, 30),
              }
            : c,
        ),
      );

      // Dialog mode overrides ref_mode with `dialogo` / `dialogo_movimiento`.
      // Fuera del try: el catch lo necesita para decidir si el execution_id
      // se conserva (solo tiene sentido si la request ruteó a atlas).
      const refModeForApi =
        clip.aiSceneMode === "personas"
          ? clip.aiPersonType === "motion"
            ? "dialogo_movimiento"
            : "dialogo"
          : clip.aiRefMode || "none";

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        // Reintento del MISMO pedido (parámetros iguales, intento previo sin
        // concluir) → mismo execution_id: el backend dedupea y no cobra un
        // segundo video. Pedido nuevo o parámetros cambiados → id nuevo.
        const attemptRef = creatorAttemptRef(
          clip.aiExecutionId && clip.aiExecutionParamsKey
            ? {
                executionId: clip.aiExecutionId,
                paramsKey: clip.aiExecutionParamsKey,
              }
            : null,
          creatorParamsKey({
            prompt: clip.aiPrompt,
            duration: Math.max(1, Math.round(clip.duration ?? 0)),
            refMode: refModeForApi,
            resolution: clip.aiResolution || "720p",
            quality: clip.aiQuality || "estandar",
            aspectRatio,
            speechSpeed:
              clip.aiSceneMode === "personas"
                ? (clip.aiSpeechSpeed ?? 1.1)
                : null,
            multiScenes:
              clip.aiMultiEnabled && clip.aiMultiScenes
                ? clip.aiMultiScenes.map((s) => ({
                    prompt: s.prompt,
                    duration: s.duration,
                  }))
                : null,
            refAssetIds: clip.aiRefAssetIds ?? null,
            firstFrameAssetId: clip.aiFirstFrameAssetId ?? null,
            lastFrameAssetId: clip.aiLastFrameAssetId ?? null,
            voiceId: clip.aiVoiceId ?? null,
            dialogue: clip.aiDialogue ?? null,
            dialogueInputMode: clip.aiDialogueInputMode ?? null,
            audioUrl: clip.aiDialogueAudioUrl ?? null,
          }),
        );
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiExecutionId: attemptRef.executionId,
                  aiExecutionParamsKey: attemptRef.paramsKey,
                }
              : c,
          ),
        );
        const body: Record<string, any> = {
          user_id: session.user.id,
          project_id: projectId,
          company_id: companyId,
          execution_id: attemptRef.executionId,
          prompt: clip.aiPrompt,
          // Redondeado como en el alta de escena ("ningún float sale por el
          // request") y para que coincida con la clave de idempotencia.
          duration: Math.max(1, Math.round(clip.duration ?? 0)),
          ref_mode: refModeForApi,
          // Trackeo (ADR-585): video generado desde el Estudio.
          modo_utilizado: "estudio_video",
          aspect_ratio: aspectRatio,
          quality: clip.aiQuality || "estandar",
          resolution: clip.aiResolution || "720p",
          ...(clip.aiSceneMode === "personas" && clip.aiVoiceId
            ? { voz_id: clip.aiVoiceId }
            : {}),
          ...(clip.aiSceneMode === "personas" && clip.aiDialogue
            ? { dialogo: clip.aiDialogue }
            : {}),
          ...(clip.aiSceneMode === "personas"
            ? { velocidad_voz: clip.aiSpeechSpeed ?? 1.1 }
            : {}),
          // Audio input for static-personas dialog (ElevenLabs speech-to-speech
          // instead of TTS). Workflow branches on `dialogo_input_mode`.
          ...(clip.aiSceneMode === "personas" &&
          clip.aiPersonType !== "motion" &&
          clip.aiDialogueInputMode === "audio" &&
          clip.aiDialogueAudioUrl
            ? {
                dialogo_input_mode: "audio",
                audio_url: clip.aiDialogueAudioUrl,
                audio_duration_sec: clip.aiDialogueAudioDurationSec ?? null,
              }
            : clip.aiSceneMode === "personas"
              ? { dialogo_input_mode: "texto" }
              : {}),
          ...(clip.aiMultiEnabled && clip.aiMultiScenes
            ? {
                multiescena: true,
                escenas: clip.aiMultiScenes.map((s) => ({
                  prompt: s.prompt,
                  duration: s.duration,
                })),
              }
            : {}),
        };

        // Resolve asset IDs to signed URLs
        const assetIdsToResolve: string[] = [];
        if (clip.aiRefAssetIds) assetIdsToResolve.push(...clip.aiRefAssetIds);
        if (clip.aiFirstFrameAssetId)
          assetIdsToResolve.push(clip.aiFirstFrameAssetId);
        if (clip.aiLastFrameAssetId)
          assetIdsToResolve.push(clip.aiLastFrameAssetId);
        const assetsById = new Map<string, ProjectAsset>();
        if (assetIdsToResolve.length > 0) {
          const assets = await fetchAssetsByIds(assetIdsToResolve);
          for (const a of assets) assetsById.set(a.id, a);
        }
        const resolveUrl = async (assetId?: string) => {
          if (!assetId) return null;
          const a = assetsById.get(assetId);
          if (!a) return null;
          return await getAssetSignedUrl(a.storage_path);
        };

        if (clip.aiRefAssetIds && clip.aiRefAssetIds.length > 0) {
          const urls = (
            await Promise.all(clip.aiRefAssetIds.map((id) => resolveUrl(id)))
          ).filter((u): u is string => !!u);
          if (urls.length > 0) body.reference_images = urls;
        }
        const firstUrl = await resolveUrl(clip.aiFirstFrameAssetId);
        if (firstUrl) body.first_frame = firstUrl;
        const lastUrl = await resolveUrl(clip.aiLastFrameAssetId);
        if (lastUrl) body.last_frame = lastUrl;

        // Legacy base64 fallback for old projects
        if (!body.first_frame && clip.aiFirstFrame)
          body.first_frame = clip.aiFirstFrame;
        if (!body.last_frame && clip.aiLastFrame)
          body.last_frame = clip.aiLastFrame;
        if (!body.reference_images && clip.aiRefImage)
          body.reference_images = [clip.aiRefImage];

        // Helper con flag atlas/n8n y ruteo parcial por ref_mode
        // ('dialogo_movimiento' sigue en n8n) — timeout de 5 min adentro
        // para que el clip no quede en "generating" eterno. Ver ADR-584.
        const creator = await generateVideoCreator(body);
        if (creator.pending) {
          // 202: el wait del backend venció pero el video sigue generándose.
          throw new Error(
            "La generación tardó demasiado. El video puede haberse generado igual — revisá tu biblioteca de videos en unos minutos, o reintentá.",
          );
        }
        const videoPath = creator.path;

        if (videoPath) {
          // The workflow's `reduce images` node ran by now — tell the
          // sidebar (and any other listener) to refetch the credit balance.
          window.dispatchEvent(new Event("atlas:credits-updated"));

          // Get signed URL from the returned storage path
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(videoPath, 3600);

          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiStatus: "ready" as AiClipStatus,
                    storagePath: videoPath,
                    src: urlData?.signedUrl || undefined,
                    // Intento concluido: un próximo "generar" es un pedido
                    // nuevo y debe llevar execution_id nuevo.
                    aiExecutionId: undefined,
                    aiExecutionParamsKey: undefined,
                  }
                : c,
            ),
          );

          // Probe real video duration and update the clip. Important when the
          // placeholder duration was estimated (e.g. dialogue mode).
          if (urlData?.signedUrl) {
            const probe = document.createElement("video");
            probe.preload = "metadata";
            probe.src = urlData.signedUrl;
            probe.addEventListener("loadedmetadata", () => {
              const dur = probe.duration;
              if (isFinite(dur) && dur > 0) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId
                      ? { ...c, duration: dur, sourceDuration: dur }
                      : c,
                  ),
                );
              }
            });
          }

          // Register the AI video as a project asset so it appears in the Video library
          // and persists across clip deletions.
          (async () => {
            try {
              const asset = await registerExistingAsset({
                projectId: projectId!,
                companyId: companyId!,
                userId: session.user.id,
                storagePath: videoPath,
                type: "video",
                name: clip.aiPrompt?.slice(0, 80) || "Video IA",
                mimeType: "video/mp4",
                durationSeconds: clip.duration,
              });
              if (asset) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId ? { ...c, assetId: asset.id } : c,
                  ),
                );
              }
            } catch (e) {
              console.warn("[VideoEditor] Could not register AI asset:", e);
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? {
                        ...c,
                        aiAssetWarning:
                          "El video se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                      }
                    : c,
                ),
              );
            }
          })();
        } else {
          throw new Error("Generation failed");
        }
      } catch (err) {
        console.error("[VideoEditor] AI generation failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La generación tardó demasiado (más de 5 min). El video puede haberse generado igual — revisalo más tarde en HeyGen, o reintentá."
            : err instanceof Error
              ? err.message
              : "Error desconocido al generar el video.";
        // El execution_id se conserva SOLO si el reintento puede re-engancharse:
        // fallo TÉCNICO (Error plano: timeout/red/202 — el job puede seguir
        // corriendo) Y la request ruteó a atlas (que dedupea por idempotency
        // key). GenerateAdError = el backend CONCLUYÓ → id inútil. Modos
        // n8n-only (dialogo_movimiento) o flag de atlas apagado = n8n, que NO
        // dedupea → conservar el id sería falsa protección.
        const definitivo =
          err instanceof GenerateAdError ||
          !USE_ATLAS_BACKEND ||
          isN8nOnlyCreatorRefMode(refModeForApi);
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "error" as AiClipStatus,
                  aiError: msg,
                  ...(definitivo
                    ? {
                        aiExecutionId: undefined,
                        aiExecutionParamsKey: undefined,
                      }
                    : {}),
                }
              : c,
          ),
        );
      } finally {
        aiVideoInFlightRef.current.delete(clipId);
      }
    },
    [clips, companyId, projectId, aspectRatio, assertCredits],
  );

  // ── Traducción de video con lip-sync (HeyGen Video Translate v3) ────────
  // Flow async en 2 fases:
  //   1) Frontend POST a /traducir_video_creator_start → n8n llama a HeyGen,
  //      recibe `video_translation_id`, devuelve `{ jobId }` rápido.
  //   2) Frontend polea /traducir_video_creator_status cada 15s con el jobId
  //      hasta que HeyGen reporta `completed`. Cuando termina, n8n descarga
  //      el video traducido + lo sube a Supabase + responde con el path.
  //   3) El frontend REEMPLAZA el clip en la timeline con el video nuevo
  //      (mismo startTime + trackIndex, nuevo src/asset/duration).
  //
  // El polling sobrevive a re-renders porque el state vive en el clip
  // (`aiTranslating`, `aiTranslateJobId`), no en estado local del callback.
  // Si el usuario refresca la página mientras está procesando, el flag
  // queda guardado y se podría retomar el polling — eso lo dejamos para
  // después si hace falta. Por ahora si refresca, pierde el polling pero
  // el video traducido igual queda en la biblioteca cuando HeyGen termine
  // (n8n lo sube a Supabase sin importar si el frontend está escuchando).
  const TRANSLATE_POLL_MS = 15_000;
  const TRANSLATE_MAX_POLLS = 80; // ~20 min máximo (15s × 80)

  // Reintenta SOLO el INSERT en `video_project_assets` para un clip cuyo
  // archivo binario YA está en Supabase Storage pero cuyo registro falló
  // (estado: `aiAssetWarning` seteado en el clip). El badge ámbar del clip
  // dispara esto al hacer click. No re-genera el video/audio ni descuenta
  // créditos — solo intenta hacer el INSERT con los datos del clip.
  const retryAssetRegistration = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || !clip.storagePath || !clip.aiAssetWarning) return;
      if (!companyId || !projectId) return;

      const assetType: "video" | "audio" =
        clip.type === "audio" ? "audio" : "video";
      const mimeType = assetType === "audio" ? "audio/mpeg" : "video/mp4";
      const fallbackName =
        assetType === "audio"
          ? clip.aiAudioType === "music"
            ? "Música IA"
            : "Voz IA"
          : "Video IA";
      const name = (clip.aiPrompt || clip.name || fallbackName).slice(0, 80);

      setRetryingAssetForClipId(clipId);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        const asset = await registerExistingAsset({
          projectId,
          companyId,
          userId: session.user.id,
          storagePath: clip.storagePath,
          type: assetType,
          name,
          mimeType,
          ...(assetType === "video" && clip.duration
            ? { durationSeconds: clip.duration }
            : {}),
        });

        if (!asset) throw new Error("El registro devolvió null");

        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  assetId: asset.id,
                  aiAssetWarning: undefined,
                }
              : c,
          ),
        );
      } catch (e) {
        console.warn("[VideoEditor] Retry asset registration failed:", e);
        const msg = e instanceof Error ? e.message : "Error desconocido";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiAssetWarning: `Reintento fallido: ${msg}. Probá de nuevo en unos segundos.`,
                }
              : c,
          ),
        );
      } finally {
        setRetryingAssetForClipId(null);
      }
    },
    [clips, companyId, projectId],
  );

  // Reintenta generar la signed URL de un clip cuyo `loadError` está seteado.
  // El badge rojo en el clip dispara esto al hacer click. Si la signed URL
  // se genera bien, limpia `loadError` y setea `src` para que el clip
  // vuelva a ser reproducible.
  const retryLoadSignedUrl = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || !clip.storagePath || !clip.loadError) return;

      setRetryingLoadForClipId(clipId);
      try {
        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(clip.storagePath, 3600);
        if (!urlData?.signedUrl) {
          throw new Error(
            "El archivo no se encontró en el servidor. Probá refrescar la página o regenerá el clip.",
          );
        }
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, src: urlData.signedUrl, loadError: undefined }
              : c,
          ),
        );
      } catch (e) {
        console.warn("[VideoEditor] Retry load signed URL failed:", e);
        const msg = e instanceof Error ? e.message : "Error desconocido";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, loadError: `Reintento fallido: ${msg}` }
              : c,
          ),
        );
      } finally {
        setRetryingLoadForClipId(null);
      }
    },
    [clips],
  );

  // "Cambiar voz" de un clip de AUDIO (Fase 1): pasa el audio del clip por
  // speech-to-speech (ElevenLabs) con la voz elegida y reemplaza el clip
  // in-place. El S2S preserva ritmo/entonación, así que suena natural. Mismo
  // patrón async que traducción/borrado (shimmer + replace in-place).
  const changeClipVoice = useCallback(
    async (clipId: string, voiceId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      if (clip.voiceChangeStatus === "processing") return;
      const isAudioClip = clip.type === "audio";
      const isVideoClip =
        clip.type === "video" || clip.type === "ai-generated";
      if (!isAudioClip && !isVideoClip) return;

      // El backend exige que la URL de entrada sea firmada de Supabase.
      // Firmamos fresca desde storagePath; si no hay, usamos src sólo si ya
      // es una URL de Supabase.
      let sourceUrl: string | null = null;
      if (clip.storagePath)
        sourceUrl = await getAssetSignedUrl(clip.storagePath);
      if (!sourceUrl && clip.src && clip.src.includes(".supabase.co"))
        sourceUrl = clip.src;
      if (!sourceUrl) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  voiceChangeStatus: "error" as const,
                  voiceChangeError:
                    "No se pudo leer el clip. Recargá la página e intentá de nuevo.",
                }
              : c,
          ),
        );
        return;
      }
      const durationSec = clip.sourceDuration || clip.duration;

      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                voiceChangeStatus: "processing" as const,
                voiceChangeError: undefined,
                voiceChangeStartedAt: Date.now(),
              }
            : c,
        ),
      );

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        // Clip de audio → S2S directo (mp3). Clip de video → endpoint que
        // extrae el audio, S2S y re-muxea el audio nuevo al video (mp4).
        let newPath: string;
        if (isAudioClip) {
          const body: Record<string, any> = {
            user_id: session.user.id,
            ref_mode: "voz",
            voz_id: voiceId,
            project_id: projectId,
            // Trackeo (ADR-585): audio generado desde el Estudio.
            modo_utilizado: "estudio_audio",
            velocidad_voz: 1.0,
            dialogo_input_mode: "audio",
            audio_url: sourceUrl,
            audio_duration_sec: durationSec,
          };
          newPath = (await generateAudioCreator(body)).path;
        } else {
          newPath = (
            await changeClipVoiceVideo({
              clipUrl: sourceUrl,
              voiceId,
              durationSec,
              projectId: projectId ?? null,
            })
          ).path;
        }
        window.dispatchEvent(new Event("atlas:credits-updated"));

        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(newPath, 3600);

        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  voiceChangeStatus: undefined,
                  voiceChangeError: undefined,
                  voiceChangeStartedAt: undefined,
                  storagePath: newPath,
                  // Si la firma falló NO dejamos el src viejo (reproduciría la
                  // voz ANTERIOR, y se rompe al expirar esa URL): apuntamos al
                  // media nuevo con src vacío + loadError → el badge de reintento
                  // lo re-firma desde el storagePath nuevo (retryLoadForClip).
                  src: urlData?.signedUrl || undefined,
                  loadError: urlData?.signedUrl
                    ? undefined
                    : "No pudimos cargar el video con la voz nueva. Tocá para reintentar.",
                }
              : c,
          ),
        );

        // Probe de la duración real del source (sólo audio; el video conserva
        // su duración por el -c:v copy). NO tocamos `duration` para no
        // redimensionar el clip ya colocado en la timeline.
        if (isAudioClip && urlData?.signedUrl) {
          const probe = new Audio();
          probe.preload = "metadata";
          probe.src = urlData.signedUrl;
          probe.addEventListener("loadedmetadata", () => {
            const dur = probe.duration;
            if (isFinite(dur) && dur > 0) {
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId ? { ...c, sourceDuration: dur } : c,
                ),
              );
            }
          });
        }

        // Registrar el asset nuevo en la biblioteca del proyecto.
        if (companyId && projectId) {
          try {
            const asset = await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: newPath,
              type: isAudioClip ? "audio" : "video",
              name: isAudioClip
                ? "Voz IA (cambiada)"
                : "Video (voz cambiada)",
              mimeType: isAudioClip ? "audio/mpeg" : "video/mp4",
            });
            if (asset)
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId ? { ...c, assetId: asset.id } : c,
                ),
              );
          } catch (e) {
            console.warn(
              "[VideoEditor] change-voice asset register failed:",
              e,
            );
          }
        }
      } catch (err) {
        console.error("[VideoEditor] change clip voice failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "El cambio de voz tardó demasiado (más de 5 min). Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al cambiar la voz.";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  voiceChangeStatus: "error" as const,
                  voiceChangeError: msg,
                }
              : c,
          ),
        );
      }
    },
    [clips, projectId, companyId],
  );

  const translateClipToEnglish = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      if (clip.type !== "video" && clip.type !== "ai-generated") return;
      if (clip.aiTranslating) return; // ya está en proceso
      if (!clip.src) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslateError:
                    "El clip no tiene un video cargado. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }
      if (!companyId || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslateError:
                    "Falta información del proyecto. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }

      const cost = creditsForVideoTranslate({ durationSec: clip.duration });
      if (!assertCredits(cost)) return;

      // Marcar el clip como en proceso (limpia errores previos).
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                aiTranslating: true,
                aiTranslateError: undefined,
                aiTranslateJobId: undefined,
                aiTranslateStartedAt: Date.now(),
              }
            : c,
        ),
      );

      const failWith = (msg: string) => {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslating: false,
                  aiTranslateError: msg,
                  aiTranslateJobId: undefined,
                }
              : c,
          ),
        );
      };

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        // Refrescar signed URL antes de mandarla a HeyGen. clip.src puede
        // ser una URL que ya tenga 1 hora (las signed URLs de Supabase
        // expiran a la hora por default), y HeyGen tarda varios minutos en
        // bajar el video — si llega a tocarlo cuando ya expiró, falla con
        // "Failed to download video from url". Generamos una nueva con 3h
        // de validez, suficiente para que HeyGen termine sin riesgo.
        let videoUrlForHeygen = clip.src;
        if (clip.storagePath) {
          try {
            const { data: urlData } = await supabase.storage
              .from("video-project-assets")
              .createSignedUrl(clip.storagePath, 60 * 60 * 3);
            if (urlData?.signedUrl) videoUrlForHeygen = urlData.signedUrl;
          } catch (e) {
            console.warn(
              "[VideoEditor] No pude refrescar signed URL, uso la del clip:",
              e,
            );
          }
        }

        // 1) Kickoff (atlas o n8n según flag — ADR-600; sin créditos el atlas
        // responde 402 y el helper tira el copy de saldo).
        const jobId = await postTranslateStart({
          user_id: session.user.id,
          company_id: companyId,
          project_id: projectId,
          clip_id: clipId,
          video_url: videoUrlForHeygen,
          duration_sec: clip.duration,
          target_language: "English",
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        });

        // Guardar el jobId en el clip por si el usuario refresca o queremos
        // mostrar el id en debug.
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId ? { ...c, aiTranslateJobId: jobId } : c,
          ),
        );

        // Créditos los descontó n8n en el kickoff — notificamos a la sidebar.
        window.dispatchEvent(new Event("atlas:credits-updated"));

        // 2) Polling
        let polls = 0;
        // Loop manual con sleep — más simple que recursión + setTimeout.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          polls++;
          if (polls > TRANSLATE_MAX_POLLS) {
            throw new Error(
              "La traducción tardó demasiado (más de 20 min). El video puede haberse generado igual — revisalo más tarde en la biblioteca, o reintentá.",
            );
          }
          await new Promise((r) => setTimeout(r, TRANSLATE_POLL_MS));

          const statusJson = await postTranslateStatus({
            job_id: jobId,
            project_id: projectId,
            company_id: companyId,
          });
          if (!statusJson) continue; // error transient, reintentamos en el próximo poll

          if (statusJson.status === "failed") {
            throw new Error(
              statusJson.error || "HeyGen reportó que la traducción falló.",
            );
          }
          if (statusJson.status !== "completed") {
            continue; // running / pending → seguir polleando
          }
          if (!statusJson.path) {
            throw new Error(
              "Traducción completada pero el backend no devolvió path del video.",
            );
          }

          // 3) Completado: registrar el asset y reemplazar el clip.
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(statusJson.path, 3600);
          const newSrc = urlData?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el video traducido.");

          let newAssetId: string | undefined;
          try {
            const asset = await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: statusJson.path,
              type: "video",
              name: `${clip.name || "Video"} (EN)`.slice(0, 80),
              mimeType: "video/mp4",
            });
            if (asset) newAssetId = asset.id;
          } catch (e) {
            console.warn(
              "[VideoEditor] Translated video registered but asset record failed:",
              e,
            );
          }

          // Probar la duración real del video traducido (HeyGen puede
          // ajustar la duración levemente para que el lip-sync calce).
          const probeDuration = async (): Promise<number | null> =>
            new Promise((resolve) => {
              const probe = document.createElement("video");
              probe.preload = "metadata";
              probe.muted = true;
              probe.src = newSrc;
              const cleanup = () => {
                probe.removeAttribute("src");
                probe.load?.();
              };
              probe.onloadedmetadata = () => {
                const d = probe.duration;
                cleanup();
                resolve(isFinite(d) && d > 0 ? d : null);
              };
              probe.onerror = () => {
                cleanup();
                resolve(null);
              };
            });
          const newDuration = (await probeDuration()) ?? clip.duration;

          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    src: newSrc,
                    storagePath: statusJson.path,
                    assetId: newAssetId ?? c.assetId,
                    duration: newDuration,
                    sourceDuration: newDuration,
                    mediaOffset: 0,
                    aiTranslating: false,
                    aiTranslateError: undefined,
                    aiTranslateJobId: undefined,
                  }
                : c,
            ),
          );
          // Toca el sonido de "listo" si está disponible.
          try {
            if (finishSoundRef.current) {
              finishSoundRef.current.currentTime = 0;
              finishSoundRef.current.play().catch(() => {});
            }
          } catch {}
          return;
        }
      } catch (err) {
        console.error("[VideoEditor] translateClipToEnglish failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La conexión con el servidor tardó demasiado. Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al traducir el video.";
        failWith(msg);
      }
    },
    [clips, companyId, projectId, assertCredits],
  );

  // Borra un objeto de un clip de video por descripción de texto (Bria
  // video-erase-object via n8n). Mismo patrón async que translateClipToEnglish:
  // valida → assertCredits → POST start → poll status → registra asset →
  // swapea el clip. Guarda el original para revertir. Fire-and-forget.
  const eraseObjectInClip = useCallback(
    async (clipId: string, prompt: string, confirmLongClip: boolean) => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip) return;
      if (clip.type !== "video" && clip.type !== "ai-generated") {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, eraseError: "Solo se puede borrar objetos en clips de video." }
              : c,
          ),
        );
        return;
      }
      if (clip.eraseStatus === "processing") return; // ya en proceso
      if (!clip.storagePath || !clip.src) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  eraseError:
                    "El clip todavía no terminó de subirse a la nube. Esperá unos segundos y reintentá.",
                }
              : c,
          ),
        );
        return;
      }
      if (!companyId || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, eraseError: "Falta info del proyecto. Recargá la página." }
              : c,
          ),
        );
        return;
      }
      if (clip.duration > ERASE_SOFT_CAP_SEC && !confirmLongClip) {
        // backstop: el Director debería haber confirmado antes; si no, frenamos.
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  eraseError: `El clip dura ${Math.round(clip.duration)}s (> ${ERASE_SOFT_CAP_SEC}s). Confirmá que querés procesarlo igual.`,
                }
              : c,
          ),
        );
        return;
      }

      const cost = creditsForErase({ durationSec: clip.duration });
      if (!assertCredits(cost)) return;

      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                eraseStatus: "processing",
                eraseError: undefined,
                eraseJobId: undefined,
                eraseStartedAt: Date.now(),
              }
            : c,
        ),
      );

      const failWith = (msg: string) => {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, eraseStatus: "error", eraseError: msg, eraseJobId: undefined }
              : c,
          ),
        );
      };

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        // Signed URL fresca de 3h (n8n + Bria tardan; evitamos que expire).
        let videoUrl = clip.src;
        try {
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(clip.storagePath, 60 * 60 * 3);
          if (urlData?.signedUrl) videoUrl = urlData.signedUrl;
        } catch (e) {
          console.warn("[VideoEditor][erase] no pude refrescar signed URL:", e);
        }

        // 1) Kickoff
        // Atlas unifica bria+sam2 en /v1/videos/erase (sin points = marca de
        // agua); flag OFF vuelve al par bria_erase_* de n8n. ADR-601.
        const startRes = await postVideoEraseStart("bria", {
          user_id: session.user.id,
          company_id: companyId,
          project_id: projectId,
          clip_id: clipId,
          video_url: videoUrl,
          duration_sec: clip.duration,
          prompt,
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        });
        if (!startRes.ok) throw new Error(`Start failed: ${startRes.status}`);
        const startJson = await startRes.json();
        if (startJson.response !== "success" || !startJson.jobId) {
          throw new Error(startJson.error || "El backend no devolvió un jobId válido.");
        }
        const jobId: string = startJson.jobId;

        setClips((prev) =>
          prev.map((c) => (c.id === clipId ? { ...c, eraseJobId: jobId } : c)),
        );
        window.dispatchEvent(new Event("atlas:credits-updated"));

        // 2) Polling
        let polls = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          polls++;
          if (polls > ERASE_MAX_POLLS) {
            throw new Error(
              "El borrado tardó demasiado. El video puede haberse generado igual — revisalo más tarde en Archivos, o reintentá.",
            );
          }
          await new Promise((r) => setTimeout(r, ERASE_POLL_MS));

          const statusRes = await postVideoEraseStatus("bria", {
            job_id: jobId,
            project_id: projectId,
            company_id: companyId,
          });
          if (!statusRes.ok) continue; // transient → reintentar
          const statusJson = await statusRes.json();

          if (statusJson.status === "error" || statusJson.status === "failed") {
            throw new Error(statusJson.error || "El borrado falló en el servidor.");
          }
          if (statusJson.status !== "done") continue; // processing → seguir
          if (!statusJson.storagePath) {
            throw new Error("Borrado completado pero el backend no devolvió storagePath.");
          }

          // 3) Completado: firmar, registrar asset en Archivos, swapear.
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(statusJson.storagePath, 3600);
          const newSrc = urlData?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el video borrado.");

          // El resultado SIEMPRE queda en Archivos (ya costó plata).
          try {
            await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: statusJson.storagePath,
              type: "video",
              name: `${clip.name || "Video"} (sin objeto)`.slice(0, 80),
              mimeType: "video/mp4",
            });
          } catch (e) {
            console.warn("[VideoEditor][erase] asset record failed:", e);
          }

          const current = clipsRef.current.find((c) => c.id === clipId);
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    // guardar original para revertir
                    originalStoragePathBeforeErase: current?.storagePath,
                    originalSrcBeforeErase: current?.src,
                    src: newSrc,
                    storagePath: statusJson.storagePath,
                    eraseStatus: undefined,
                    eraseError: undefined,
                    eraseJobId: undefined,
                  }
                : c,
            ),
          );
          try {
            if (finishSoundRef.current) {
              finishSoundRef.current.currentTime = 0;
              finishSoundRef.current.play().catch(() => {});
            }
          } catch {}
          return;
        }
      } catch (err) {
        console.error("[VideoEditor][erase] eraseObjectInClip failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La conexión con el servidor tardó demasiado. Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al borrar el objeto.";
        failWith(msg);
      }
    },
    [companyId, projectId, assertCredits],
  );

  // ── Pincel inteligente (SAM2) ─────────────────────────────────────────────
  // Captura el frame actual del clip a un dataURL y abre el panel del pincel.
  const openEraseBrush = useCallback((clipId: string) => {
    const clip = clipsRef.current.find((c) => c.id === clipId);
    if (!clip || (clip.type !== "video" && clip.type !== "ai-generated")) return;
    if (!clip.storagePath) {
      setUploadErrorMsg("El clip todavía no terminó de subirse. Esperá unos segundos.");
      return;
    }
    const vid = mediaRefs.current.get(clipId) as HTMLVideoElement | undefined;
    if (!vid || !vid.videoWidth) {
      setUploadErrorMsg("El frame del clip no está cargado. Movete sobre el clip y reintentá.");
      return;
    }
    const cv = document.createElement("canvas");
    cv.width = vid.videoWidth; cv.height = vid.videoHeight;
    cv.getContext("2d")!.drawImage(vid, 0, 0);
    // toDataURL falla con SecurityError si el canvas quedó "tainted" (video
    // cross-origin cargado sin CORS). El <video> ya monta con crossOrigin, pero
    // si el navegador sirvió una respuesta cacheada sin CORS, el canvas puede
    // seguir sucio → mensaje claro y pedirle recargar, en vez de un crash.
    let frameUrl: string;
    try {
      frameUrl = cv.toDataURL("image/jpeg", 0.9);
    } catch (e) {
      console.error("[erase-brush] canvas tainted en toDataURL:", e);
      setUploadErrorMsg(
        "No pude capturar el frame del clip (problema de seguridad del navegador). Recargá la página (F5) y reintentá.",
      );
      return;
    }
    setEraseBrushFrame({ url: frameUrl, w: vid.videoWidth, h: vid.videoHeight });
    setEraseBrushClipId(clipId);
    // El pincel ahora es una MODAL grande (no la sidebar). Se renderiza
    // top-level cuando hay eraseBrushFrame + eraseBrushClipId + companyId.
  }, []);

  // Sube el frame (dataURL→blob) a Supabase, lo firma y llama a SAM2 para el
  // preview de la máscara. El panel queda puro de UI.
  const requestBrushMask = useCallback(
    async (marks: EraseMark[]): Promise<string> => {
      if (!eraseBrushFrame || !companyId || !projectId) throw new Error("Falta info del frame.");
      // dataURL → blob
      const blob = await (await fetch(eraseBrushFrame.url)).blob();
      const path = `${companyId}/${projectId}/_erase_frame.jpg`;
      const { error: upErr } = await supabase.storage
        .from("video-project-assets")
        .upload(path, blob, { upsert: true, contentType: "image/jpeg" });
      if (upErr) throw new Error("No se pudo subir el frame.");
      const { data } = await supabase.storage.from("video-project-assets").createSignedUrl(path, 3600);
      if (!data?.signedUrl) throw new Error("No se pudo firmar el frame.");
      return requestSam2Mask({ frameUrl: data.signedUrl, marks, companyId });
    },
    [eraseBrushFrame, companyId, projectId],
  );

  // Job async del pincel (espeja eraseObjectInClip): start→poll→swap apuntando
  // a los endpoints SAM2 y mandando las marcas + el keyframe.
  const runBrushErase = useCallback(
    async (clipId: string, marks: EraseMark[], frame: { w: number; h: number }) => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip || !clip.storagePath || !companyId || !projectId) return;
      setClips((prev) => prev.map((c) => c.id === clipId
        ? { ...c, eraseStatus: "processing", eraseError: undefined, eraseStartedAt: Date.now() } : c));
      // Mismo patrón que la traducción: el error queda guardado en el clip y se
      // muestra como badge clickeable en la timeline (no un toast ad-hoc).
      const fail = (msg: string) => {
        setClips((prev) => prev.map((c) => c.id === clipId ? { ...c, eraseStatus: "error", eraseError: msg } : c));
      };
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        let videoUrl = clip.src!;
        try {
          const { data } = await supabase.storage.from("video-project-assets")
            .createSignedUrl(clip.storagePath, 60 * 60 * 3);
          if (data?.signedUrl) videoUrl = data.signedUrl;
        } catch {}
        // Atlas unifica bria+sam2 en /v1/videos/erase; flag OFF vuelve al par
        // sam2_erase_* de n8n. ADR-601.
        const startRes = await postVideoEraseStart("sam2", {
          user_id: session.user.id, company_id: companyId, project_id: projectId, clip_id: clipId,
          video_url: videoUrl, duration_sec: clip.duration,
          points: marksToSamPoints(marks), frame_width: frame.w, frame_height: frame.h,
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        });
        const startRaw = await startRes.text();
        if (!startRes.ok) {
          console.error("[erase-brush][start] HTTP", startRes.status, "→", startRaw.slice(0, 600));
          throw new Error(`Start falló (HTTP ${startRes.status}). ${startRaw.slice(0, 200)}`);
        }
        let sj: any;
        try { sj = JSON.parse(startRaw); }
        catch {
          console.error("[erase-brush][start] respuesta NO-JSON:", JSON.stringify(startRaw.slice(0, 600)));
          throw new Error("Respuesta inválida al iniciar el borrado. Revisá 'sam2_erase_start' en n8n.");
        }
        if (sj.response !== "success" || !sj.jobId) throw new Error(sj.error || "Sin jobId.");
        const jobId: string = sj.jobId;
        setClips((prev) => prev.map((c) => c.id === clipId ? { ...c, eraseJobId: jobId } : c));
        window.dispatchEvent(new Event("atlas:credits-updated"));
        let polls = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          polls++;
          if (polls > 300) throw new Error("Tardó demasiado. Revisá en Archivos más tarde.");
          await new Promise((r) => setTimeout(r, 8000));
          const stRes = await postVideoEraseStatus("sam2", {
            job_id: jobId, project_id: projectId, company_id: companyId,
          });
          if (!stRes.ok) continue;
          const stRaw = await stRes.text();
          let st: any;
          try { st = JSON.parse(stRaw); }
          catch {
            console.warn("[erase-brush][status] respuesta NO-JSON (reintento):", JSON.stringify(stRaw.slice(0, 300)));
            continue;
          }
          if (st.status === "error" || st.status === "failed") throw new Error(st.error || "Falló en el servidor.");
          if (st.status !== "done") continue;
          if (!st.storagePath) throw new Error("Sin storagePath.");
          const { data: urlData } = await supabase.storage.from("video-project-assets").createSignedUrl(st.storagePath, 3600);
          const newSrc = urlData?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el resultado.");
          try {
            await registerExistingAsset({ projectId, companyId, userId: session.user.id,
              storagePath: st.storagePath, type: "video", name: `${clip.name || "Video"} (sin objeto)`.slice(0, 80), mimeType: "video/mp4" });
          } catch {}
          const cur = clipsRef.current.find((c) => c.id === clipId);
          setClips((prev) => prev.map((c) => c.id === clipId
            ? { ...c, originalStoragePathBeforeErase: cur?.storagePath, originalSrcBeforeErase: cur?.src,
                src: newSrc, storagePath: st.storagePath, eraseStatus: undefined, eraseError: undefined, eraseJobId: undefined }
            : c));
          // Éxito: el clip se reemplaza in-place y suena el "listo" (mismo
          // patrón que la traducción). El shimmer desaparece solo.
          try { if (finishSoundRef.current) { finishSoundRef.current.currentTime = 0; finishSoundRef.current.play().catch(() => {}); } } catch {}
          return;
        }
      } catch (err: any) {
        fail(err?.message || "Error desconocido.");
      }
    },
    [companyId, projectId],
  );

  const addAiSceneClip = (mode: "generate" | "draft") => {
    if (mode === "generate" && !assertCredits(scenePanelCost)) return;
    const aiTrack = getTrackForClipType("ai-generated");
    const promptPreview = scenePrompt.trim()
      ? scenePrompt.trim().slice(0, 30)
      : "Escena IA";

    // If editing an existing clip, update it in place
    const isEditing = !!editingClipId;
    const clipId = isEditing ? editingClipId! : generateId();

    // For dialogue scenes, estimate duration from the spoken text so the
    // placeholder clip length approximates reality before generation finishes.
    // The real duration is probed once the video is ready.
    // When the user provided audio instead of text, the audio's measured
    // duration is the real value (no estimation needed).
    const isStaticDialogAudioMode =
      sceneMode === "personas" &&
      scenePersonType === "static" &&
      sceneDialogueInputMode === "audio" &&
      sceneDialogueAudio;
    const estimatedFromDialogue = isStaticDialogAudioMode
      ? sceneDialogueAudio!.durationSec
      : sceneMode === "personas" && sceneDialogue.trim()
        ? estimateSpeechDuration(sceneDialogue, sceneSpeechSpeed)
        : 0;

    // Multi-scene mode (General + Reference only): collapse the list into a
    // composite prompt for the clip preview, use the sum as the clip duration.
    const isMultiScene =
      sceneMode === "general" &&
      sceneRefMode === "reference" &&
      sceneMultiEnabled;
    const multiScenesPayload = isMultiScene
      ? sceneMultiList.map((s) => ({
          prompt: s.prompt.trim(),
          duration: s.duration,
        }))
      : null;
    const multiPromptCombined = isMultiScene
      ? sceneMultiList
          .map((s, i) => `Escena ${i + 1} (${s.duration}s): ${s.prompt.trim()}`)
          .filter((s) => s.endsWith(": ") === false)
          .join("\n\n")
      : null;
    const multiTotalDuration = isMultiScene
      ? sceneMultiList.reduce((sum, s) => sum + s.duration, 0)
      : 0;

    const effectiveDuration = isMultiScene
      ? Math.max(2, Math.min(60, multiTotalDuration))
      : estimatedFromDialogue > 0
        ? Math.max(2, Math.min(60, estimatedFromDialogue))
        : sceneDuration;

    const effectivePrompt = isMultiScene
      ? multiPromptCombined || undefined
      : scenePrompt.trim() || undefined;

    const updatedFields: Partial<TimelineClip> = {
      name: mode === "draft" ? `Borrador: ${promptPreview}` : promptPreview,
      duration: effectiveDuration,
      aiPrompt: effectivePrompt,
      aiFirstFrameAssetId: sceneFirstFrameAssetId || undefined,
      aiLastFrameAssetId: sceneLastFrameAssetId || undefined,
      aiRefAssetIds:
        sceneRefAssetIds.length > 0 ? [...sceneRefAssetIds] : undefined,
      aiRefMode: sceneRefMode,
      aiQuality: sceneQuality,
      aiResolution: sceneResolution,
      aiSceneMode: sceneMode,
      aiPersonType: sceneMode === "personas" ? scenePersonType : undefined,
      aiDialogue:
        sceneMode === "personas"
          ? sceneDialogue.trim() || undefined
          : undefined,
      aiSpeechSpeed: sceneMode === "personas" ? sceneSpeechSpeed : undefined,
      aiVoiceId:
        sceneMode === "personas" && audioAiVoiceId ? audioAiVoiceId : undefined,
      aiVoiceName:
        sceneMode === "personas" && audioAiVoiceName
          ? audioAiVoiceName
          : undefined,
      aiMultiEnabled: isMultiScene || undefined,
      aiMultiScenes: multiScenesPayload || undefined,
      // Dialog input mode (text vs audio) — only meaningful for static personas
      aiDialogueInputMode:
        sceneMode === "personas" && scenePersonType === "static"
          ? sceneDialogueInputMode
          : undefined,
      aiDialogueAudioUrl:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.url
          : undefined,
      aiDialogueAudioStoragePath:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.storagePath
          : undefined,
      aiDialogueAudioDurationSec:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.durationSec
          : undefined,
      aiDialogueAudioAssetId:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.assetId
          : undefined,
      aiStatus: mode === "draft" ? "draft" : "generating",
      aiGenerationStartedAt: mode === "draft" ? undefined : Date.now(),
    };

    if (isEditing) {
      setClips((prev) =>
        prev.map((c) => (c.id === clipId ? { ...c, ...updatedFields } : c)),
      );
    } else {
      const newClip: TimelineClip = {
        id: clipId,
        type: "ai-generated",
        startTime: getNextStartTime(clips, aiTrack),
        color: aiTrack.color,
        ...updatedFields,
      } as TimelineClip;
      setClips((prev) => [...prev, newClip]);
    }
    setSelectedClipId(clipId);
    setScenePrompt("");
    setSceneDialogue("");
    setSceneFirstFrameAssetId(null);
    setSceneLastFrameAssetId(null);
    setSceneRefAssetIds([]);
    setSceneDuration(5);
    setSceneSpeechSpeed(1.1);
    setSceneQuality("estandar");
    setSceneResolution("720p");
    setSceneMultiEnabled(false);
    setSceneMultiList([{ id: generateId(), prompt: "", duration: 5 }]);
    setEditingClipId(null);
    setActivePanel("none");

    // If generating, call API after adding the clip
    if (mode === "generate") {
      // Use setTimeout to let state update first, then call with the clip data directly
      setTimeout(async () => {
        // Doble submit del panel (o carrera con Reintentar) para el mismo
        // clip: el primer disparo ya está generando — no-op (ver
        // aiVideoInFlightRef).
        if (aiVideoInFlightRef.current.has(clipId)) return;
        aiVideoInFlightRef.current.add(clipId);
        // Fuera del try: el catch lo necesita para decidir si el execution_id
        // se conserva (solo tiene sentido si la request ruteó a atlas).
        const refModeForApi =
          updatedFields.aiSceneMode === "personas"
            ? updatedFields.aiPersonType === "motion"
              ? "dialogo_movimiento"
              : "dialogo"
            : updatedFields.aiRefMode || "none";
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session?.user) throw new Error("No session");
          // Mismo pedido que un intento previo sin concluir (p. ej. reintento
          // re-abriendo el panel sin cambiar nada) → mismo execution_id: el
          // backend dedupea y no cobra un segundo video. Si cambió algo, o el
          // intento previo concluyó, es un pedido nuevo con id nuevo.
          const prevClip = isEditing
            ? clips.find((c) => c.id === clipId)
            : undefined;
          const attemptRef = creatorAttemptRef(
            prevClip?.aiExecutionId && prevClip.aiExecutionParamsKey
              ? {
                  executionId: prevClip.aiExecutionId,
                  paramsKey: prevClip.aiExecutionParamsKey,
                }
              : null,
            creatorParamsKey({
              prompt: updatedFields.aiPrompt,
              duration: Math.max(1, Math.round(updatedFields.duration ?? 0)),
              refMode: refModeForApi,
              resolution: updatedFields.aiResolution || "720p",
              quality: updatedFields.aiQuality || "estandar",
              aspectRatio,
              speechSpeed:
                updatedFields.aiSceneMode === "personas"
                  ? (updatedFields.aiSpeechSpeed ?? 1.1)
                  : null,
              multiScenes:
                updatedFields.aiMultiEnabled && updatedFields.aiMultiScenes
                  ? updatedFields.aiMultiScenes.map((s) => ({
                      prompt: s.prompt,
                      duration: s.duration,
                    }))
                  : null,
              refAssetIds: updatedFields.aiRefAssetIds ?? null,
              firstFrameAssetId: updatedFields.aiFirstFrameAssetId ?? null,
              lastFrameAssetId: updatedFields.aiLastFrameAssetId ?? null,
              voiceId: updatedFields.aiVoiceId ?? null,
              dialogue: updatedFields.aiDialogue ?? null,
              dialogueInputMode: updatedFields.aiDialogueInputMode ?? null,
              audioUrl: updatedFields.aiDialogueAudioUrl ?? null,
            }),
          );
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiExecutionId: attemptRef.executionId,
                    aiExecutionParamsKey: attemptRef.paramsKey,
                  }
                : c,
            ),
          );
          const body: Record<string, any> = {
            user_id: session.user.id,
            project_id: projectId,
            company_id: companyId,
            execution_id: attemptRef.executionId,
            prompt: updatedFields.aiPrompt,
            // La API espera segundos enteros. `updatedFields.duration` puede
            // traer decimales por otros caminos además del editar: la
            // estimación de habla (`estimateSpeechDuration`) y la duración
            // medida del audio de diálogo. Redondeamos acá para que ningún
            // float salga por el request, venga de donde venga.
            duration: Math.max(1, Math.round(updatedFields.duration ?? 0)),
            ref_mode: refModeForApi,
            // Trackeo (ADR-585): video generado desde el Estudio.
            modo_utilizado: "estudio_video",
            aspect_ratio: aspectRatio,
            quality: updatedFields.aiQuality || "estandar",
            resolution: updatedFields.aiResolution || "720p",
            ...(updatedFields.aiSceneMode === "personas" &&
            updatedFields.aiVoiceId
              ? { voz_id: updatedFields.aiVoiceId }
              : {}),
            ...(updatedFields.aiSceneMode === "personas" &&
            updatedFields.aiDialogue
              ? { dialogo: updatedFields.aiDialogue }
              : {}),
            ...(updatedFields.aiSceneMode === "personas"
              ? { velocidad_voz: updatedFields.aiSpeechSpeed ?? 1.1 }
              : {}),
            ...(updatedFields.aiSceneMode === "personas" &&
            updatedFields.aiPersonType !== "motion" &&
            updatedFields.aiDialogueInputMode === "audio" &&
            updatedFields.aiDialogueAudioUrl
              ? {
                  dialogo_input_mode: "audio",
                  audio_url: updatedFields.aiDialogueAudioUrl,
                  audio_duration_sec:
                    updatedFields.aiDialogueAudioDurationSec ?? null,
                }
              : updatedFields.aiSceneMode === "personas"
                ? { dialogo_input_mode: "texto" }
                : {}),
            ...(updatedFields.aiMultiEnabled && updatedFields.aiMultiScenes
              ? {
                  multiescena: true,
                  escenas: updatedFields.aiMultiScenes.map((s) => ({
                    prompt: s.prompt,
                    duration: s.duration,
                  })),
                }
              : {}),
          };

          // Resolve asset IDs to signed URLs
          const idsToResolve: string[] = [];
          if (updatedFields.aiRefAssetIds)
            idsToResolve.push(...updatedFields.aiRefAssetIds);
          if (updatedFields.aiFirstFrameAssetId)
            idsToResolve.push(updatedFields.aiFirstFrameAssetId);
          if (updatedFields.aiLastFrameAssetId)
            idsToResolve.push(updatedFields.aiLastFrameAssetId);
          const assetsById = new Map<string, ProjectAsset>();
          if (idsToResolve.length > 0) {
            const fetched = await fetchAssetsByIds(idsToResolve);
            for (const a of fetched) assetsById.set(a.id, a);
          }
          const resolveUrl = async (assetId?: string) => {
            if (!assetId) return null;
            const a = assetsById.get(assetId);
            if (!a) return null;
            return await getAssetSignedUrl(a.storage_path);
          };

          if (
            updatedFields.aiRefAssetIds &&
            updatedFields.aiRefAssetIds.length > 0
          ) {
            const urls = (
              await Promise.all(
                updatedFields.aiRefAssetIds.map((id) => resolveUrl(id)),
              )
            ).filter((u): u is string => !!u);
            if (urls.length > 0) body.reference_images = urls;
          }
          const firstUrl = await resolveUrl(updatedFields.aiFirstFrameAssetId);
          if (firstUrl) body.first_frame = firstUrl;
          const lastUrl = await resolveUrl(updatedFields.aiLastFrameAssetId);
          if (lastUrl) body.last_frame = lastUrl;

          // Helper con flag atlas/n8n y ruteo parcial por ref_mode
          // ('dialogo_movimiento' sigue en n8n) — timeout de 5 min y manejo
          // de los 200-vacío/no-JSON de n8n adentro. Ver ADR-584.
          const creator = await generateVideoCreator(body);
          if (creator.pending) {
            // 202: el wait del backend venció, el video sigue generándose.
            throw new Error(
              "La generación tardó demasiado. El video puede haberse generado igual — revisá tu biblioteca de videos en unos minutos, o reintentá.",
            );
          }
          const videoPath = creator.path;

          if (videoPath) {
            // Credits were just deducted by the workflow — notify the sidebar.
            window.dispatchEvent(new Event("atlas:credits-updated"));

            const { data: urlData } = await supabase.storage
              .from("video-project-assets")
              .createSignedUrl(videoPath, 3600);

            setClips((prev) =>
              prev.map((c) =>
                c.id === clipId
                  ? {
                      ...c,
                      aiStatus: "ready" as AiClipStatus,
                      storagePath: videoPath,
                      src: urlData?.signedUrl || undefined,
                      // Intento concluido: un próximo "generar" es un pedido
                      // nuevo y debe llevar execution_id nuevo.
                      aiExecutionId: undefined,
                      aiExecutionParamsKey: undefined,
                    }
                  : c,
              ),
            );

            // Probe real video duration; important because dialogue placeholders
            // use an estimated duration until the video is ready.
            if (urlData?.signedUrl) {
              const probe = document.createElement("video");
              probe.preload = "metadata";
              probe.src = urlData.signedUrl;
              probe.addEventListener("loadedmetadata", () => {
                const dur = probe.duration;
                if (isFinite(dur) && dur > 0) {
                  setClips((prev) =>
                    prev.map((c) =>
                      c.id === clipId
                        ? { ...c, duration: dur, sourceDuration: dur }
                        : c,
                    ),
                  );
                }
              });
            }

            // Register the AI video as a project asset (appears in Video library, persists after clip deletion)
            try {
              const asset = await registerExistingAsset({
                projectId: projectId!,
                companyId: companyId!,
                userId: session.user.id,
                storagePath: videoPath,
                type: "video",
                name: updatedFields.aiPrompt?.slice(0, 80) || "Video IA",
                mimeType: "video/mp4",
                durationSeconds: updatedFields.duration,
              });
              if (asset) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId ? { ...c, assetId: asset.id } : c,
                  ),
                );
              }
            } catch (e) {
              console.warn("[VideoEditor] Could not register AI asset:", e);
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? {
                        ...c,
                        aiAssetWarning:
                          "El video se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                      }
                    : c,
                ),
              );
            }
          } else {
            throw new Error("Generation failed");
          }
        } catch (err) {
          console.error("[VideoEditor] AI generation failed:", err);
          const msg =
            err instanceof DOMException && err.name === "TimeoutError"
              ? "La generación tardó demasiado (más de 5 min). El video puede haberse generado igual — revisalo más tarde en HeyGen, o reintentá."
              : err instanceof Error
                ? err.message
                : "Error desconocido al generar el video.";
          // El execution_id se conserva SOLO si el reintento puede
          // re-engancharse: fallo TÉCNICO (Error plano: timeout/red/202) Y
          // request ruteada a atlas (que dedupea). GenerateAdError = concluyó;
          // modos n8n-only o flag apagado = n8n, sin dedupe → id inútil.
          const definitivo =
            err instanceof GenerateAdError ||
            !USE_ATLAS_BACKEND ||
            isN8nOnlyCreatorRefMode(refModeForApi);
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiStatus: "error" as AiClipStatus,
                    aiError: msg,
                    ...(definitivo
                      ? {
                          aiExecutionId: undefined,
                          aiExecutionParamsKey: undefined,
                        }
                      : {}),
                  }
                : c,
            ),
          );
        } finally {
          aiVideoInFlightRef.current.delete(clipId);
        }
      }, 0);
    }
  };

  // ── Add AI audio clip (voice-over or music) ──────────────────────────────
  // When mode === 'generate' we POST to the generar_audio_creator webhook.
  // When mode === 'draft' we just place a draft clip on the audio track.
  const addAiAudioClip = (mode: "generate" | "draft") => {
    if (mode === "generate" && !assertCredits(audioPanelCost)) return;
    const audioTrack = getTrackForClipType("audio");
    const rawPrompt = audioAiPrompt.trim();
    const typeLabel = audioAiType === "voice" ? "Voz IA" : "Música IA";
    const preview = rawPrompt ? rawPrompt.slice(0, 30) : typeLabel;
    const name =
      mode === "draft" ? `Borrador: ${preview}` : `${typeLabel}: ${preview}`;

    const isEditing = !!editingClipId;
    const clipId = isEditing ? editingClipId! : generateId();

    // For voice audios, estimate duration from the narration text so the
    // placeholder clip length approximates reality before generation finishes.
    // The real duration is probed once the audio is ready (see below).
    // When the user provided their own audio, that duration is the truth.
    const isVoiceAudioMode =
      audioAiType === "voice" &&
      audioAiDialogueInputMode === "audio" &&
      audioAiDialogueAudio;
    const estimatedFromVoice = isVoiceAudioMode
      ? audioAiDialogueAudio!.durationSec
      : audioAiType === "voice" && rawPrompt
        ? estimateSpeechDuration(rawPrompt, audioAiSpeechSpeed)
        : 0;
    const effectiveAudioDuration =
      estimatedFromVoice > 0
        ? Math.max(1, Math.min(120, estimatedFromVoice))
        : audioAiDuration;

    const updatedFields: Partial<TimelineClip> = {
      name: name.length > 40 ? name.slice(0, 37) + "..." : name,
      duration: effectiveAudioDuration,
      aiPrompt: rawPrompt || undefined,
      aiAudioType: audioAiType,
      aiVoiceId: audioAiVoiceId || undefined,
      aiVoiceName: audioAiVoiceName || undefined,
      aiInstrumentalOnly:
        audioAiType === "music" ? audioAiInstrumentalOnly : undefined,
      aiSpeechSpeed: audioAiType === "voice" ? audioAiSpeechSpeed : undefined,
      // Voice mode: text vs audio input
      aiDialogueInputMode:
        audioAiType === "voice" ? audioAiDialogueInputMode : undefined,
      aiDialogueAudioUrl:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.url
          : undefined,
      aiDialogueAudioStoragePath:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.storagePath
          : undefined,
      aiDialogueAudioDurationSec:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.durationSec
          : undefined,
      aiDialogueAudioAssetId:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.assetId
          : undefined,
      aiStatus: mode === "draft" ? "draft" : "generating",
      aiGenerationStartedAt: mode === "draft" ? undefined : Date.now(),
    };

    if (isEditing) {
      setClips((prev) =>
        prev.map((c) => (c.id === clipId ? { ...c, ...updatedFields } : c)),
      );
    } else {
      const newClip: TimelineClip = {
        id: clipId,
        type: "audio",
        startTime: getNextStartTime(clips, audioTrack),
        color: audioTrack.color,
        volume: 1,
        ...updatedFields,
      } as TimelineClip;
      setClips((prev) => [...prev, newClip]);
    }
    setSelectedClipId(clipId);

    // Snapshot before we reset panel state
    const refMode: "voz" | "musica" =
      audioAiType === "voice" ? "voz" : "musica";
    const voiceId = audioAiVoiceId;
    const promptText = rawPrompt;
    const instrumentalOnly =
      audioAiType === "music" ? audioAiInstrumentalOnly : false;
    const speechSpeed = audioAiSpeechSpeed;
    const dialogueInputMode =
      audioAiType === "voice" ? audioAiDialogueInputMode : "text";
    const dialogueAudio =
      audioAiType === "voice" && audioAiDialogueInputMode === "audio"
        ? audioAiDialogueAudio
        : null;

    // Reset panel state and close
    setAudioAiPrompt("");
    setAudioAiDuration(8);
    setAudioAiType("voice");
    setAudioAiVoiceId(null);
    setAudioAiVoiceName("");
    setAudioAiInstrumentalOnly(false);
    setAudioAiSpeechSpeed(1.1);
    setAudioAiDialogueInputMode("text");
    setAudioAiDialogueAudio(null);
    setEditingClipId(null);
    setActivePanel("none");

    // Fire webhook on generate
    if (mode === "generate") {
      setTimeout(async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session?.user) throw new Error("No session");

          const body: Record<string, any> = {
            user_id: session.user.id,
            ref_mode: refMode,
            voz_id: voiceId || "",
            project_id: projectId,
            // Trackeo (ADR-585): audio generado desde el Estudio.
            modo_utilizado: "estudio_audio",
            text: promptText,
            instrumental_only: instrumentalOnly,
            ...(refMode === "voz" ? { velocidad_voz: speechSpeed } : {}),
            // Audio input branch (voice-only): the workflow runs speech-to-speech
            // using the user's audio instead of TTS over `text`.
            ...(refMode === "voz" && dialogueInputMode === "audio" && dialogueAudio
              ? {
                  dialogo_input_mode: "audio",
                  audio_url: dialogueAudio.url,
                  audio_duration_sec: dialogueAudio.durationSec,
                }
              : refMode === "voz"
                ? { dialogo_input_mode: "texto" }
                : {}),
          };

          // Helper con flag atlas/n8n (ADR-583) — timeout de 5 min adentro.
          const { path: audioPath } = await generateAudioCreator(body);

          // Credits were just deducted by the workflow — notify the sidebar.
          window.dispatchEvent(new Event("atlas:credits-updated"));

          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(audioPath, 3600);

          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiStatus: "ready" as AiClipStatus,
                    storagePath: audioPath,
                    src: urlData?.signedUrl || undefined,
                  }
                : c,
            ),
          );

          // Probe the actual audio duration and update the clip so the timeline reflects reality
          if (urlData?.signedUrl) {
            const probe = new Audio();
            probe.preload = "metadata";
            probe.src = urlData.signedUrl;
            probe.addEventListener("loadedmetadata", () => {
              const dur = probe.duration;
              if (isFinite(dur) && dur > 0) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId
                      ? { ...c, duration: dur, sourceDuration: dur }
                      : c,
                  ),
                );
              }
            });
          }

          // Register the AI audio as a project asset so it appears in the Audio library
          if (companyId && projectId) {
            try {
              const asset = await registerExistingAsset({
                projectId,
                companyId,
                userId: session.user.id,
                storagePath: audioPath,
                type: "audio",
                name: (
                  promptText || (refMode === "musica" ? "Música IA" : "Voz IA")
                ).slice(0, 80),
                mimeType: "audio/mpeg",
              });
              if (asset) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId ? { ...c, assetId: asset.id } : c,
                  ),
                );
              }
            } catch (e) {
              console.warn(
                "[VideoEditor] Failed to register AI audio asset:",
                e,
              );
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? {
                        ...c,
                        aiAssetWarning:
                          "El audio se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                      }
                    : c,
                ),
              );
            }
          }
        } catch (err) {
          console.error("[VideoEditor] AI audio generation failed:", err);
          const msg =
            err instanceof DOMException && err.name === "TimeoutError"
              ? "La generación tardó demasiado (más de 5 min). Probá de nuevo."
              : err instanceof Error
                ? err.message
                : "Error desconocido al generar el audio.";
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? { ...c, aiStatus: "error" as AiClipStatus, aiError: msg }
                : c,
            ),
          );
        }
      }, 0);
    }
  };

  // ── Download selected clip ────────────────────────────────────────────────
  // Fetches the clip's source (blob or signed URL) and triggers a browser
  // download with a reasonable filename/extension based on the clip type.
  const downloadSelectedClip = async () => {
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip?.src) return;
    try {
      const res = await fetch(clip.src);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const defaultExt =
        clip.type === "audio" ? "mp3" : clip.type === "image" ? "png" : "mp4";
      const safeName = (clip.name || "clip").trim().replace(/[^\w\-. ]+/g, "_");
      const finalName = /\.[a-z0-9]{2,5}$/i.test(safeName)
        ? safeName
        : `${safeName}.${defaultExt}`;
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.error("[VideoEditor] downloadSelectedClip failed:", err);
    }
  };

  // ── Transitions ───────────────────────────────────────────────────────────
  // The transition is owned by the SECOND clip in the pair as `transitionIn`.
  // We DO NOT move clips when a transition is added or its duration
  // changes — the renderer extends the outgoing clip's playback past its
  // visual end during the fade window, so visually nothing on the timeline
  // shifts. See `findTransitionSuccessor` + `isClipActiveAtTime` for the
  // playback-side machinery.
  const setClipTransition = useCallback(
    (rightClipId: string, type: TransitionType, durationSec: number) => {
      setClips((prev) =>
        prev.map((c) =>
          c.id === rightClipId
            ? { ...c, transitionIn: { type, durationSec } }
            : c,
        ),
      );
    },
    [],
  );

  const removeClipTransition = useCallback((rightClipId: string) => {
    setClips((prev) =>
      prev.map((c) => {
        if (c.id !== rightClipId || !c.transitionIn) return c;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { transitionIn, ...rest } = c;
        return rest as TimelineClip;
      }),
    );
  }, []);

  // ── Delete / Duplicate ────────────────────────────────────────────────────
  const deleteSelectedClip = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    setClips((prev) => {
      const next = prev.filter((c) => c.id !== selectedClipId);
      // Solo revocar el blob si NINGÚN otro clip restante lo comparte. Split,
      // duplicar y autocut crean varios clips que apuntan al MISMO src; revocar
      // al borrar uno mataría el video de los hermanos (ERR_FILE_NOT_FOUND).
      if (
        clip?.src &&
        clip.src.startsWith("blob:") &&
        !next.some((c) => c.src === clip.src)
      ) {
        URL.revokeObjectURL(clip.src);
      }
      return next;
    });
    setSelectedClipId(null);
  };

  // Remove every clip that belongs to the same subtitle batch as the given
  // groupId. Used by the toolbar's split delete button so the user can wipe
  // an entire round of generated subtitles in one click without having to
  // shift-select each caption. Text clips don't have blob URLs, but we still
  // call revokeObjectURL defensively on any `src` for symmetry.
  const deleteSubtitleGroup = (groupId: string) => {
    if (!groupId) return;
    setClips((prev) => {
      const next = prev.filter((c) => c.subtitleGroupId !== groupId);
      // Igual que en deleteSelectedClip: revocar el blob de cada clip borrado
      // solo si ningún clip sobreviviente lo comparte.
      for (const c of prev) {
        if (
          c.subtitleGroupId === groupId &&
          c.src &&
          c.src.startsWith("blob:") &&
          !next.some((k) => k.src === c.src)
        ) {
          URL.revokeObjectURL(c.src);
        }
      }
      return next;
    });
    setSelectedClipId(null);
  };

  // Delete the selected clip with the Delete/Backspace key (unless typing in an input).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (!selectedClipId) return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable)
          return;
      }
      e.preventDefault();
      deleteSelectedClip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId, clips]);

  const duplicateSelectedClip = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip) return;
    const newClip = {
      ...clip,
      id: generateId(),
      startTime: clip.startTime + clip.duration + 0.5,
    };
    setClips((prev) => [...prev, newClip]);
    setSelectedClipId(newClip.id);
  };

  // ── Separate audio from a video clip ─────────────────────────────────────
  // Mutes the original video and creates a new audio clip referencing the
  // same media file (browsers play the audio track of an MP4 via <audio>).
  // The new audio clip is placed on the first audio track that has no overlap
  // at its timeline position; a new track is created if none is free.
  const separateAudioFromVideoClip = (clipId: string) => {
    const clip = clips.find((c) => c.id === clipId);
    if (!clip) return;
    const isVideoLike =
      clip.type === "video" ||
      (clip.type === "ai-generated" && clip.aiStatus === "ready");
    if (!isVideoLike || !clip.src) return;

    const clipStart = clip.startTime;
    const clipEnd = clip.startTime + clip.duration;
    const overlaps = (a0: number, a1: number, b0: number, b1: number) =>
      a0 < b1 && b0 < a1;

    const audioClips = clips.filter((c) => c.type === "audio");
    const existingIndices = getAudioTrackIndices(clips);

    let targetTrack = 0;
    if (existingIndices.length === 0) {
      targetTrack = 0;
    } else {
      let found = false;
      for (const idx of existingIndices) {
        const trackClips = audioClips.filter(
          (c) => getClipTrackIndex(c) === idx,
        );
        const hasOverlap = trackClips.some((c) =>
          overlaps(clipStart, clipEnd, c.startTime, c.startTime + c.duration),
        );
        if (!hasOverlap) {
          targetTrack = idx;
          found = true;
          break;
        }
      }
      if (!found) targetTrack = existingIndices[existingIndices.length - 1] + 1;
    }

    const newAudioClip: TimelineClip = {
      id: generateId(),
      type: "audio",
      name: `${clip.name} (audio)`,
      startTime: clip.startTime,
      duration: clip.duration,
      color: getAudioTrackColor(targetTrack),
      volume: clip.volume ?? 1,
      src: clip.src,
      storagePath: clip.storagePath,
      mediaOffset: clip.mediaOffset,
      sourceDuration: clip.sourceDuration,
      assetId: clip.assetId,
      trackIndex: targetTrack,
    };

    setClips((prev) => [
      ...prev.map((c) => (c.id === clipId ? { ...c, volume: 0 } : c)),
      newAudioClip,
    ]);
    setSelectedClipId(newAudioClip.id);
  };

  // ── Trim clip via yellow edge handles ──────────────────────────────────
  const handleTrimPointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      clip: TimelineClip,
      edge: "left" | "right",
    ) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const origStart = clip.startTime;
      const origDuration = clip.duration;
      const origOffset = clip.mediaOffset || 0;
      const sourceDur = clip.sourceDuration;
      const MIN_DURATION = 0.1;
      const ppsAtStart = BASE_PPS * zoomRef.current;

      // Snap siblings: other clips on the same track (same type group + trackIndex).
      const trackCfg = getTrackForClipType(clip.type);
      const clipIdx = getClipTrackIndex(clip);
      const siblings = clips.filter(
        (c) =>
          c.id !== clip.id &&
          trackCfg.clipTypes.includes(c.type) &&
          getClipTrackIndex(c) === clipIdx,
      );

      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent) => {
        const deltaSec = (ev.clientX - startX) / ppsAtStart;
        setClips((prev) =>
          prev.map((c) => {
            if (c.id !== clip.id) return c;
            if (edge === "right") {
              let newDuration = origDuration + deltaSec;
              if (newDuration < MIN_DURATION) newDuration = MIN_DURATION;
              if (sourceDur !== undefined) {
                const maxDur = Math.max(MIN_DURATION, sourceDur - origOffset);
                if (newDuration > maxDur) newDuration = maxDur;
              }
              // Snap right edge to a sibling's start edge within SNAP_THRESHOLD.
              const newEnd = origStart + newDuration;
              let bestSnap: number | null = null;
              let bestDist = SNAP_THRESHOLD;
              for (const sib of siblings) {
                const d = Math.abs(newEnd - sib.startTime);
                if (d < bestDist) {
                  bestDist = d;
                  bestSnap = sib.startTime;
                }
              }
              if (bestSnap !== null) {
                let snapped = bestSnap - origStart;
                if (snapped < MIN_DURATION) snapped = MIN_DURATION;
                if (sourceDur !== undefined) {
                  const maxDur = Math.max(MIN_DURATION, sourceDur - origOffset);
                  if (snapped > maxDur) snapped = maxDur;
                }
                newDuration = snapped;
              }
              return { ...c, duration: newDuration };
            } else {
              // Left edge: moving right trims, moving left extends (if mediaOffset allows)
              let newOffset = origOffset + deltaSec;
              if (newOffset < 0) newOffset = 0;
              const maxOffset = origOffset + origDuration - MIN_DURATION;
              if (newOffset > maxOffset) newOffset = maxOffset;
              let offsetDelta = newOffset - origOffset;
              let newStart = origStart + offsetDelta;
              // Snap left edge to a sibling's end edge within SNAP_THRESHOLD.
              let bestSnap: number | null = null;
              let bestDist = SNAP_THRESHOLD;
              for (const sib of siblings) {
                const sibEnd = sib.startTime + sib.duration;
                const d = Math.abs(newStart - sibEnd);
                if (d < bestDist) {
                  bestDist = d;
                  bestSnap = sibEnd;
                }
              }
              if (bestSnap !== null) {
                const snappedStart = bestSnap;
                const snappedDelta = snappedStart - origStart;
                let snappedOffset = origOffset + snappedDelta;
                if (snappedOffset < 0) snappedOffset = 0;
                if (snappedOffset > maxOffset) snappedOffset = maxOffset;
                offsetDelta = snappedOffset - origOffset;
                newStart = origStart + offsetDelta;
                newOffset = snappedOffset;
              }
              return {
                ...c,
                startTime: newStart,
                duration: origDuration - offsetDelta,
                mediaOffset: newOffset,
              };
            }
          }),
        );
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [clips],
  );

  const restoreSelectedClipToOriginal = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip || clip.sourceDuration === undefined) return;
    const offset = clip.mediaOffset || 0;
    setClips((prev) =>
      prev.map((c) => {
        if (c.id !== clip.id) return c;
        return {
          ...c,
          startTime: c.startTime - offset,
          duration: clip.sourceDuration!,
          mediaOffset: 0,
        };
      }),
    );
  };

  const splitSelectedClip = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip) return;
    const MIN_SPLIT_GAP = 0.1; // require at least 0.1s on each side
    const splitAtLocal = currentTime - clip.startTime;
    if (
      splitAtLocal < MIN_SPLIT_GAP ||
      splitAtLocal > clip.duration - MIN_SPLIT_GAP
    )
      return;
    const leftId = clip.id;
    const rightId = generateId();
    const leftDuration = splitAtLocal;
    const rightDuration = clip.duration - splitAtLocal;
    const baseOffset = clip.mediaOffset || 0;
    setClips((prev) => {
      const next: TimelineClip[] = [];
      for (const c of prev) {
        if (c.id !== leftId) {
          next.push(c);
          continue;
        }
        next.push({ ...c, duration: leftDuration });
        next.push({
          ...c,
          id: rightId,
          startTime: c.startTime + leftDuration,
          duration: rightDuration,
          mediaOffset: baseOffset + leftDuration,
        });
      }
      return next;
    });
    setSelectedClipId(rightId);
  };

  // ── Cortar silencios automáticamente ─────────────────────────────────
  // Lee (y si hace falta genera) el transcript del clip, detecta los huecos
  // entre palabras consecutivas que pasan del umbral, y reemplaza el clip
  // por N piezas de "habla" pegadas — cerrando los huecos con ripple-shift
  // de todo lo que viene después en la timeline. Si el clip no tiene
  // ninguna palabra detectada (silencio total / sin habla), no toca nada
  // y muestra un aviso. Soporta clips de video, audio, y ai-generated con
  // audio listo. Best-effort sobre subtítulos linkeados: descarta los que
  // caen completamente dentro de un silencio y remapea el resto a su
  // nueva posición de timeline.
  const cutSilencesFromClip = useCallback(
    async (clipId: string, thresholdSec: number, paddingSec: number) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      const isCuttable =
        clip.type === "video" ||
        clip.type === "audio" ||
        (clip.type === "ai-generated" && clip.aiStatus === "ready");
      if (!isCuttable) {
        setSilenceCutNotice("Este tipo de clip no se puede analizar.");
        return;
      }
      if (!Number.isFinite(thresholdSec) || thresholdSec <= 0) {
        setSilenceCutNotice("Umbral inválido.");
        return;
      }
      try {
        setSilenceCutBusy(clipId);
        // 1. Asegurar transcript: si no está cacheado, llamar al webhook y
        //    persistirlo en el clip (mismo flujo que "Generar subtítulos").
        let transcript = clip.transcript;
        if (
          !transcript ||
          !Array.isArray(transcript.words) ||
          transcript.words.length === 0
        ) {
          let url: string | null = null;
          if (clip.storagePath) {
            try {
              url = await getAssetSignedUrl(clip.storagePath);
            } catch {}
          }
          if (
            !url &&
            typeof clip.src === "string" &&
            /^https?:\/\//.test(clip.src)
          ) {
            url = clip.src;
          }
          if (!url) {
            setSilenceCutNotice("No pudimos acceder al archivo del clip.");
            return;
          }
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (!user?.id) {
            setSilenceCutNotice("Sesión expirada — recargá la página.");
            return;
          }
          const tr = await requestTranscript(url, user.id);
          if (!tr.ok) {
            setSilenceCutNotice(tr.message);
            return;
          }
          transcript = tr.transcript;
          // Cachear para próximas llamadas; igual seguimos abajo con el valor
          // local — el setClips de abajo va a pisar `transcript` con el mismo
          // valor cuando reconstruya el clip.
          setClips((prev) =>
            prev.map((c) => (c.id === clipId ? { ...c, transcript } : c)),
          );
        }

        // 2. Detectar palabras dentro de la ventana visible del clip.
        const offset = clip.mediaOffset ?? 0;
        const winStart = offset;
        const winEnd = offset + clip.duration;
        const visibleWords = transcript.words
          .filter(
            (w) =>
              w &&
              w.type === "word" &&
              typeof w.start === "number" &&
              typeof w.end === "number" &&
              w.end > winStart &&
              w.start < winEnd,
          )
          .map((w) => ({
            // Clampar al window — si una palabra cruza el borde, recortarla.
            start: Math.max(winStart, w.start),
            end: Math.min(winEnd, w.end),
          }))
          .filter((w) => w.end > w.start);
        if (visibleWords.length === 0) {
          setSilenceCutNotice("No se detectó habla en este clip.");
          return;
        }
        visibleWords.sort((a, b) => a.start - b.start);

        // 3. Construir las regiones de silencio en source-media seconds.
        //    Incluye head (antes de la primera palabra) y tail (después de la
        //    última). Usamos un padding chico para no cortar tan a filo que
        //    se pierdan respiraciones / arranques de consonante.
        // Clampar el padding — si es más grande que la mitad del umbral,
        // los cortes quedarían invertidos (end < start). Garantizamos que
        // siempre haya al menos un mínimo de silencio real removido.
        const PADDING = Math.max(0, Math.min(paddingSec, thresholdSec * 0.45));
        const silences: Array<{ start: number; end: number }> = [];
        // Head silence
        const headSilence = visibleWords[0].start - winStart;
        if (headSilence > thresholdSec) {
          silences.push({
            start: winStart,
            end: visibleWords[0].start - PADDING,
          });
        }
        for (let i = 0; i < visibleWords.length - 1; i++) {
          const gap = visibleWords[i + 1].start - visibleWords[i].end;
          if (gap > thresholdSec) {
            silences.push({
              start: visibleWords[i].end + PADDING,
              end: visibleWords[i + 1].start - PADDING,
            });
          }
        }
        // Tail silence
        const tailSilence = winEnd - visibleWords[visibleWords.length - 1].end;
        if (tailSilence > thresholdSec) {
          silences.push({
            start: visibleWords[visibleWords.length - 1].end + PADDING,
            end: winEnd,
          });
        }
        if (silences.length === 0) {
          setSilenceCutNotice("No se encontraron silencios mayores al umbral.");
          return;
        }
        // Sanity: clampar y filtrar silencios degenerados.
        const cleanSilences = silences
          .map((s) => ({
            start: Math.max(winStart, s.start),
            end: Math.min(winEnd, s.end),
          }))
          .filter((s) => s.end - s.start > 0.05)
          .sort((a, b) => a.start - b.start);
        if (cleanSilences.length === 0) {
          setSilenceCutNotice("No se encontraron silencios mayores al umbral.");
          return;
        }

        // 4. Convertir silencios en "speech segments" complementarios sobre
        //    [winStart, winEnd]. Cada speech segment va a ser una pieza nueva
        //    en la timeline (la primera reusa el id original).
        const speechSegments: Array<{ start: number; end: number }> = [];
        let cursor = winStart;
        for (const sil of cleanSilences) {
          if (sil.start > cursor + 1e-3)
            speechSegments.push({ start: cursor, end: sil.start });
          cursor = Math.max(cursor, sil.end);
        }
        if (cursor < winEnd - 1e-3)
          speechSegments.push({ start: cursor, end: winEnd });
        if (speechSegments.length === 0) {
          setSilenceCutNotice(
            "No quedó habla después de cortar los silencios.",
          );
          return;
        }
        const totalRemoved = cleanSilences.reduce(
          (acc, s) => acc + (s.end - s.start),
          0,
        );
        if (totalRemoved < 0.05) {
          setSilenceCutNotice("No se encontraron silencios mayores al umbral.");
          return;
        }

        // 5. Aplicar el cambio en un único setClips (atómico, una entrada de
        //    Undo). Reemplazamos C por N piezas pegadas, ripple-shift de los
        //    clips que arrancan a partir del final de C, y compactamos los
        //    subtítulos linkeados.
        const clipEndTL = clip.startTime + clip.duration;
        setClips((prev) => {
          const next: TimelineClip[] = [];
          for (const c of prev) {
            // (a) El propio clip → expandir en N piezas contiguas.
            if (c.id === clipId) {
              let tlCursor = c.startTime;
              speechSegments.forEach((seg, i) => {
                const pieceDuration = seg.end - seg.start;
                const pieceMediaOffset = seg.start;
                const piece: TimelineClip = {
                  ...c,
                  id: i === 0 ? c.id : generateId(),
                  startTime: tlCursor,
                  duration: pieceDuration,
                  mediaOffset: pieceMediaOffset,
                };
                next.push(piece);
                tlCursor += pieceDuration;
              });
              continue;
            }
            // (b) Subtítulos linkeados al clip que estamos cortando → mapear
            //     según en qué speech segment cae su `linkedMediaStart`. Si
            //     cae en silencio, el caption desaparece. Si sobrevive, su
            //     startTime se calcula contra el offset acumulado del segmento
            //     correspondiente.
            if (
              c.linkedClipId === clipId &&
              typeof c.linkedMediaStart === "number" &&
              typeof c.linkedMediaEnd === "number"
            ) {
              const lms = c.linkedMediaStart;
              // ¿En qué speech segment cae el inicio de la palabra?
              let cumulative = 0;
              let matched: { newStart: number; remap: number } | null = null;
              for (const seg of speechSegments) {
                if (lms >= seg.start && lms < seg.end) {
                  // Posición relativa dentro del segmento + offset acumulado +
                  // startTime original del clip fuente = nuevo startTime de la
                  // caption en timeline.
                  const intoSeg = lms - seg.start;
                  matched = {
                    newStart: clip.startTime + cumulative + intoSeg,
                    remap: 0,
                  };
                  break;
                }
                cumulative += seg.end - seg.start;
              }
              if (!matched) continue; // silenciada → drop
              next.push({ ...c, startTime: matched.newStart });
              continue;
            }
            // (c) Cualquier otro clip que arranca DESPUÉS del fin original
            //     de C → ripple-shift a la izquierda por totalRemoved.
            //     Tolerancia de 1e-3s para no colar al borde.
            if (c.startTime >= clipEndTL - 1e-3) {
              next.push({
                ...c,
                startTime: Math.max(0, c.startTime - totalRemoved),
              });
              continue;
            }
            next.push(c);
          }
          return next;
        });
        // Mostramos un mensaje breve; el usuario ve los cortes aplicados en
        // la timeline.
        const removedRounded = Math.round(totalRemoved * 10) / 10;
        setSilenceCutNotice(
          `Cortados ${cleanSilences.length} silencio(s) (${removedRounded}s).`,
        );
        setSilencePopoverOpen(false);
      } catch (err) {
        console.error("[VideoEditor] cutSilencesFromClip failed", err);
        setSilenceCutNotice(
          err instanceof Error ? err.message : "Falló el corte de silencios.",
        );
      } finally {
        setSilenceCutBusy(null);
      }
    },
    [clips],
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // Mobile blocker
  return (
    <BrandColorsContext.Provider value={brandProfile?.colors || []}>
      <BrandFontsContext.Provider
        value={
          (brandProfile?.fonts || {}) as Record<string, string | undefined>
        }
      >
        <BrandCustomFontsContext.Provider value={customFontFamilies}>
          {/* ── Mobile: Desktop-only message ───────────────────────────────── */}
          <div className="flex md:hidden flex-col items-center justify-center min-h-[60vh] px-6 text-center">
            <div className="w-16 h-16 rounded-lg bg-neutral-100 flex items-center justify-center mb-4">
              <Monitor className="w-7 h-7 text-neutral-400" />
            </div>
            <h2 className="text-[17px] font-semibold text-brand-black/90 mb-2">
              Solo disponible en desktop
            </h2>
            <p className="text-[15px] text-neutral-500 max-w-sm">
              El estudio de video necesita una pantalla más grande para
              funcionar correctamente. Abrilo desde una computadora.
            </p>
          </div>

          {/* ── Desktop: Loading state ──────────────────────────────────── */}
          {/* Mismo marco que el editor: si no, el spinner pinta un rectángulo a
              sangre mientras carga y el editor "salta" al aparecer. */}
          {projectLoading && (
            <div
              className={`hidden md:flex items-center justify-center fixed right-0 bottom-0 z-20 bg-[#F9F9F9] overflow-hidden ${MARCO_PANTALLA} ${BORDE_PANTALLA}`}
              style={{ left: sidebarWidth, top: 'calc(var(--shell-barra-creditos, 0px) + 8px)' }}
            >
              <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
          )}

          {/* ── Desktop: Full editor (fixed to bypass layout padding) ──────── */}
          {/* Flota como la sidebar y como los otros dos editores del shell
              (ADR-2026-08-05-el-estudio-de-video-entra-al-shell): 8px de aire a
              los cuatro lados y esquinas de 16px, con el aire de la izquierda
              dado por `sidebarWidth` (margen + ancho + margen). Sigue en `fixed`
              por lo mismo de siempre: saltearse el padding del layout.
              El gris de adentro es el del shell (#F9F9F9) y no `surface-bg`
              (#F0F2F7), que es el gris AZULADO del chrome viejo: pegado a la
              sidebar se leía como de otra app. Mismo cambio que en "Edición
              Canva" (ADR-2026-08-05-el-editor-flota-como-la-sidebar). */}
          <div
            className={`${projectLoading ? "hidden" : "hidden md:flex"} flex-col bg-[#F9F9F9] overflow-hidden fixed right-0 bottom-0 z-20 ${MARCO_PANTALLA} ${BORDE_PANTALLA}`}
            style={{ left: sidebarWidth, top: 'calc(var(--shell-barra-creditos, 0px) + 8px)' }}
          >
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => handleFileUpload(e, "video")}
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => handleFileUpload(e, "audio")}
            />
            <input
              ref={anyFileInputRef}
              type="file"
              accept="video/*,audio/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const kind: "video" | "audio" = f.type.startsWith("audio/")
                  ? "audio"
                  : "video";
                handleFileUpload(e, kind);
              }}
            />
            <input
              ref={agentUploadInputRef}
              type="file"
              multiple
              accept={agentUploadRequest?.accept || "image/*"}
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) handleAgentUploadFiles(files);
                e.target.value = "";
              }}
            />

            {/* ── Main content: Left panel + center ──────────────────────────
            When the agent chat is visible in 'sidebar' mode we reserve space
            on the right (via padding-right) so the editor content gets
            pushed left instead of being overlapped. In 'full' mode the chat
            stays as an overlay on top of everything, so no padding is added
            (otherwise the expand animation jumps). */}
            <div
              className="relative flex flex-1 overflow-hidden transition-[padding] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
              style={{
                paddingRight:
                  showAgentChat && agentChatMode === "sidebar" ? 510 : 0,
              }}
            >
              {/* ── Left quick-access rail ─────────────────────────────────────
              Transparent vertical strip of icon tiles for the most common
              actions. Each tile is a small white rounded container with the
              icon inside and the label below, matching the look of the
              root-panel cards but compressed so the rail can stay narrow. */}
              {(() => {
                type RailItem = {
                  key: string;
                  label: string;
                  Icon: React.ComponentType<{
                    className?: string;
                    style?: React.CSSProperties;
                  }>;
                  accent: string; // hex color used for the icon, label tint and active background
                  active: boolean;
                  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
                  btnRef?: React.RefObject<HTMLButtonElement | null>;
                };
                const railItems: RailItem[] = [
                  {
                    key: "ai",
                    label: "Generar con IA",
                    Icon: Sparkles,
                    accent: "#1644EB",
                    active:
                      activePanel === "ai-scene" ||
                      activePanel === "ai-image-gen" ||
                      railAiMenuOpen,
                    btnRef: railAiBtnRef,
                    onClick: () => setRailAiMenuOpen((o) => !o),
                  },
                  {
                    // Unified library entry. Replaces the old Videos + Audio
                    // tiles — the panel itself surfaces the type-tabs so the
                    // user keeps direct access while we save vertical room
                    // on the rail and don't visually pre-bias the content.
                    key: "files",
                    label: "Archivos",
                    Icon: activePanel === "files" ? FolderOpen : Folder,
                    accent: "#0EA5E9",
                    active: activePanel === "files",
                    onClick: () =>
                      setActivePanel((p) => (p === "files" ? "none" : "files")),
                  },
                  {
                    key: "text",
                    label: "Texto",
                    Icon: Type,
                    accent: "#F59E0B",
                    active: activePanel === "text",
                    onClick: () =>
                      setActivePanel((p) => (p === "text" ? "none" : "text")),
                  },
                  {
                    key: "animation",
                    label: "Elementos",
                    Icon: Wand2,
                    accent: "#8B5CF6",
                    active: activePanel === "animation",
                    onClick: () =>
                      setActivePanel((p) =>
                        p === "animation" ? "none" : "animation",
                      ),
                  },
                  {
                    // Brand panel: edits the brand_profile attached to this
                    // project (memoria, fuentes favoritas y colores). Mirror
                    // surface for what the agent reads/writes via the
                    // update_brand_* tools.
                    key: "brand",
                    label: "Marca",
                    Icon: Palette,
                    accent: "#22C55E",
                    active: activePanel === "brand",
                    onClick: () =>
                      setActivePanel((p) => (p === "brand" ? "none" : "brand")),
                  },
                ];
                // El rail se pone sólido (bg + border) también cuando el picker
                // de "Cambiar voz" está abierto, para que su fondo blanco no se
                // vea "invadiendo" el rail transparente.
                const railActive = activePanel !== "none" || showClipVoicePicker;
                return (
                  <div
                    className={`shrink-0 w-[76px] flex flex-col items-stretch transition-colors ${
                      railActive
                        ? "bg-surface-card border-r border-black/[0.06]"
                        : "bg-transparent"
                    }`}
                  >
                    {/* Top spacer that visually continues the toolbar over the
                    rail: same h-11, same bg and bottom border, so the topbar
                    appears unbroken across the full width when no panel is
                    open, and lines up with the panel header when one is. */}
                    <div
                      className={`h-11 shrink-0 bg-surface-card ${railActive ? "" : "border-b border-black/[0.06]"}`}
                    />
                    <div className="flex flex-col items-stretch gap-1.5 pt-2 pb-3 px-2">
                      {railItems.map((item) => {
                        const { Icon, accent, active } = item;
                        return (
                          <button
                            key={item.key}
                            ref={item.btnRef}
                            type="button"
                            data-tour={
                              item.key === "ai"
                                ? "generate"
                                : item.key === "files"
                                  ? "files"
                                  : item.key === "brand"
                                    ? "brand"
                                    : undefined
                            }
                            onClick={item.onClick}
                            className="group w-full flex flex-col items-center gap-1 rounded-md py-1.5 transition-colors hover:bg-black/[0.04]"
                            title={item.label}
                          >
                            <div
                              className="w-11 h-11 rounded-md flex items-center justify-center shrink-0 shadow-sm transition-colors"
                              style={{
                                backgroundColor: active
                                  ? `${accent}1F`
                                  : "#FFFFFF",
                              }}
                            >
                              <Icon
                                className="w-5 h-5"
                                style={{ color: accent }}
                              />
                            </div>
                            <span
                              className="text-[11px] leading-tight font-medium text-center transition-colors"
                              style={{
                                color: active ? accent : "rgba(0,0,0,0.7)",
                              }}
                            >
                              {item.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {/* Video / Imagen dropdown for the Generar con IA rail tile */}
                    {railAiMenuOpen &&
                      railAiBtnRef.current &&
                      createPortal(
                        (() => {
                          const r =
                            railAiBtnRef.current!.getBoundingClientRect();
                          return (
                            <>
                              <div
                                className="fixed inset-0 z-[60]"
                                onClick={() => setRailAiMenuOpen(false)}
                              />
                              <div
                                className="fixed z-[61] bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[160px]"
                                style={{ left: r.right + 8, top: r.top }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRailAiMenuOpen(false);
                                    setEditingClipId(null);
                                    setActivePanel("ai-scene");
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                >
                                  <Film
                                    className="w-3.5 h-3.5"
                                    style={{ color: "#1644EB" }}
                                  />
                                  Video
                                </button>
                                <div className="h-px bg-black/[0.06]" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRailAiMenuOpen(false);
                                    setEditingClipId(null);
                                    setActivePanel("ai-audio");
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                >
                                  <Music
                                    className="w-3.5 h-3.5"
                                    style={{ color: "#10B981" }}
                                  />
                                  Audio
                                </button>
                              </div>
                            </>
                          );
                        })(),
                        document.body,
                      )}
                  </div>
                );
              })()}

              {/* ── Left Tools Panel (visible only when a tool is active) ──────
              The whole panel is tagged `data-clip-ui` so the global
              "click outside any clip = deselect" pointerdown listener (see
              the effect that calls `setSelectedClipId(null)`) ignores every
              interaction inside it. Without this, opening a native color
              picker or clicking on hex inputs while editing an animation
              clip would deselect the clip and the editor would snap back to
              the catalog mid-edit. */}
              <div
                data-clip-ui="true"
                className="relative z-20 shrink-0 overflow-hidden transition-[width] duration-250 ease-out"
                style={{ width: activePanel !== "none" ? 510 : 0 }}
              >
                <div className="flex flex-col min-w-[510px] w-[510px] h-full border-r border-black/[0.06] bg-surface-card">
                  {activePanel !== "ai-scene" &&
                    activePanel !== "ai-image-gen" &&
                    activePanel !== "ai-audio" && (
                      <>
                        {/* Panel header — matches main toolbar height (h-11) */}
                        <div className="flex items-center justify-between h-11 px-3 shrink-0">
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            {activePanel === "brand" ? (
                              // Brand panel header: "Marca · " static prefix, then an
                              // inline-editable name (auto-resizing, persists via
                              // persistBrandProfile) followed by a chevron that
                              // toggles a dropdown listing every brand of the
                              // company plus a "Nueva marca" CTA.
                              <div className="flex items-center gap-1.5 min-w-0 relative">
                                <h3 className="text-[15px] font-semibold text-brand-black/90 shrink-0">
                                  Marca ·
                                </h3>
                                {brandProfile ? (
                                  <>
                                    <input
                                      value={brandProfile.name}
                                      onChange={(e) =>
                                        persistBrandProfile({
                                          name: e.target.value,
                                        })
                                      }
                                      onBlur={(e) => {
                                        const trimmed = e.target.value.trim();
                                        if (!trimmed)
                                          persistBrandProfile({
                                            name: "Marca",
                                          });
                                        else if (trimmed !== e.target.value)
                                          persistBrandProfile({
                                            name: trimmed,
                                          });
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          (e.target as HTMLInputElement).blur();
                                      }}
                                      spellCheck={false}
                                      size={Math.max(
                                        brandProfile.name.length,
                                        1,
                                      )}
                                      style={
                                        {
                                          fieldSizing: "content",
                                        } as React.CSSProperties
                                      }
                                      className="min-w-0 bg-transparent text-[15px] font-semibold text-brand-black/90 outline-none rounded px-1 -mx-1 hover:bg-black/[0.04] focus:bg-black/[0.06] transition-colors"
                                      aria-label="Nombre de la marca"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setBrandSwitcherOpen((o) => !o)
                                      }
                                      className="w-6 h-6 inline-flex items-center justify-center rounded-md text-brand-black/50 hover:bg-black/[0.06] transition-colors shrink-0"
                                      aria-label="Cambiar marca"
                                      aria-expanded={brandSwitcherOpen}
                                    >
                                      <ChevronDown
                                        className={`w-4 h-4 transition-transform ${brandSwitcherOpen ? "rotate-180" : ""}`}
                                      />
                                    </button>
                                    {/* Tuerca → editar esta marca en
                                    Configuración → Mis Marcas (el editor solo
                                    muestra; la edición vive en Config). */}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        navigate("/configuracion", {
                                          state: { section: "companyData" },
                                        })
                                      }
                                      className="w-6 h-6 inline-flex items-center justify-center rounded-md text-brand-black/50 hover:bg-black/[0.06] transition-colors shrink-0"
                                      aria-label="Editar marca en Configuración"
                                      title="Editar en Mis Marcas"
                                    >
                                      <Settings className="w-4 h-4" />
                                    </button>
                                    {brandSwitcherOpen && (
                                      <>
                                        <div
                                          className="fixed inset-0 z-40"
                                          onClick={() =>
                                            setBrandSwitcherOpen(false)
                                          }
                                        />
                                        <div className="absolute top-full left-0 mt-1 z-50 w-[260px] rounded-lg border border-black/[0.08] bg-white shadow-lg py-1">
                                          <div className="max-h-[260px] overflow-y-auto">
                                            {companyBrands.length === 0 ? (
                                              <div className="px-3 py-2 text-[12px] text-brand-black/40">
                                                Sin otras marcas
                                              </div>
                                            ) : (
                                              companyBrands.map((b) => {
                                                const active =
                                                  b.id === brandProfile.id;
                                                return (
                                                  <button
                                                    key={b.id}
                                                    type="button"
                                                    onClick={() =>
                                                      switchBrand(b.id)
                                                    }
                                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-left transition-colors ${active ? "bg-black/[0.04] text-brand-black font-medium" : "text-brand-black/85 hover:bg-black/[0.04]"}`}
                                                  >
                                                    <span className="truncate">
                                                      {b.name}
                                                    </span>
                                                    {active && (
                                                      <span className="text-[11px] uppercase tracking-wide shrink-0 text-brand-black/40">
                                                        Actual
                                                      </span>
                                                    )}
                                                  </button>
                                                );
                                              })
                                            )}
                                          </div>
                                          <div className="h-px bg-black/[0.06] my-1" />
                                          <button
                                            type="button"
                                            onClick={createBrandAndAssign}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-brand-black/85 hover:bg-black/[0.04] transition-colors"
                                          >
                                            <Plus className="w-3.5 h-3.5" />
                                            Nueva marca
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-[15px] font-semibold text-brand-black/40 truncate">
                                    Cargando…
                                  </span>
                                )}
                              </div>
                            ) : (
                              <h3 className="text-[15px] font-semibold text-brand-black/90 truncate">
                                {activePanel === "ai-generate"
                                  ? "Generar con IA"
                                  : activePanel === "upload"
                                    ? "Subir archivo"
                                    : activePanel === "audio"
                                      ? "Audio"
                                      : activePanel === "files"
                                        ? "Archivos del proyecto"
                                        : activePanel === "text"
                                          ? "Texto"
                                          : activePanel === "effects"
                                            ? "Efectos"
                                            : activePanel === "transitions"
                                              ? "Transiciones"
                                              : activePanel === "animation"
                                                ? "Elementos"
                                                : activePanel === "agent-upload"
                                                  ? "Subir archivos"
                                                  : activePanel ===
                                                      "edit-video-ia"
                                                    ? "Editar video con IA"
                                                    : ""}
                              </h3>
                            )}
                          </div>
                          <button
                            onClick={() => setActivePanel("none")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors shrink-0"
                          >
                            <Plus className="w-4 h-4 rotate-45" />
                          </button>
                        </div>
                        <div className="h-px bg-black/[0.08]" />
                      </>
                    )}

                  <div
                    className={`flex-1 overflow-y-auto min-h-0 ${activePanel === "ai-scene" || activePanel === "ai-image-gen" || activePanel === "ai-audio" || activePanel === "transitions" || activePanel === "edit-video-ia" ? "" : "p-3"}`}
                  >
                    {/* Panel de edición de video con IA (Gemini Omni). Feature
                    nueva e independiente del editor Sora. Ver ADR-500. */}
                    {activePanel === "edit-video-ia" &&
                      omniEditClipId &&
                      companyId &&
                      projectId &&
                      (() => {
                        const clip = clipsRef.current.find(
                          (c) => c.id === omniEditClipId,
                        );
                        if (!clip || !clip.src) return null;
                        const cId = companyId;
                        const pId = projectId;
                        const omniAspect: "16:9" | "9:16" =
                          aspectRatio === "9:16" ? "9:16" : "16:9";
                        return (
                          <EditarVideoIA
                            clipUrl={clip.src}
                            originalPath={clip.storagePath}
                            durationSec={clip.duration}
                            aspectRatio={omniAspect}
                            projectId={pId}
                            companyId={cId}
                            onClose={() => {
                              setOmniEditClipId(null);
                              setActivePanel("none");
                            }}
                            onSave={async (result, mode) => {
                              try {
                                const {
                                  data: { user },
                                } = await supabase.auth.getUser();
                                const asset = await registerExistingAsset({
                                  projectId: pId,
                                  companyId: cId,
                                  userId: user?.id ?? "",
                                  storagePath: result.path,
                                  type: "video",
                                  name: `${clip.name || "Video"} (editado)`,
                                  mimeType: "video/mp4",
                                  durationSeconds: clip.duration,
                                });
                                if (mode === "replace") {
                                  setClips((prev) =>
                                    prev.map((c) =>
                                      c.id === clip.id
                                        ? {
                                            ...c,
                                            src: result.url,
                                            storagePath: result.path,
                                            assetId: asset?.id ?? c.assetId,
                                          }
                                        : c,
                                    ),
                                  );
                                }
                              } catch (e) {
                                console.error(
                                  "[omni-edit] error al guardar:",
                                  e,
                                );
                                // Re-lanzar: EditarVideoIA lo muestra en su
                                // banner y mantiene el panel abierto para
                                // reintentar (no cierra en silencio).
                                throw e;
                              }
                            }}
                          />
                        );
                      })()}

                    {/* Panel de Mejorar calidad (upscale con Topaz). Ver ADR-596. */}
                    {activePanel === "upscale-video" &&
                      upscaleClipId &&
                      companyId &&
                      projectId &&
                      (() => {
                        const clip = clipsRef.current.find(
                          (c) => c.id === upscaleClipId,
                        );
                        if (!clip || !clip.src) return null;
                        const cId = companyId;
                        const pId = projectId;
                        return (
                          <UpscaleVideoPanel
                            companyId={cId}
                            projectId={pId}
                            clipId={clip.id}
                            clipUrl={clip.src}
                            clipStoragePath={clip.storagePath}
                            durationSec={clip.duration}
                            onClose={() => {
                              setUpscaleClipId(null);
                              setActivePanel("none");
                            }}
                            onSave={async (result) => {
                              const {
                                data: { user },
                              } = await supabase.auth.getUser();
                              const asset = await registerExistingAsset({
                                projectId: pId,
                                companyId: cId,
                                userId: user?.id ?? "",
                                storagePath: result.path,
                                type: "video",
                                name: `${clip.name || "Video"} (mejorado)`,
                                mimeType: "video/mp4",
                                durationSeconds: clip.duration,
                              });
                              setClips((prev) =>
                                prev.map((c) =>
                                  c.id === clip.id
                                    ? {
                                        ...c,
                                        src: result.url,
                                        storagePath: result.path,
                                        assetId: asset?.id ?? c.assetId,
                                      }
                                    : c,
                                ),
                              );
                            }}
                          />
                        );
                      })()}

                    {activePanel === "files" && !modelPickerOpen && (
                      <div className="space-y-3">
                        {/* Hidden multi-type file input shared by the "Subir
                      archivo" CTA. We sniff the MIME and route through the
                      existing per-type uploaders so behavior stays
                      consistent with the rest of the app:
                        · video/audio → handleFileUpload (auto-creates a
                          timeline clip + uploads as asset in background).
                        · image → uploadProjectAsset only (images aren't
                          standalone clips today; they're used as
                          frames/refs inside AI panels). */}
                        <input
                          ref={filesUploadInputRef}
                          type="file"
                          accept="video/*,audio/*,image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file || !companyId || !projectId) return;
                            if (file.type.startsWith("video/")) {
                              setFilesTab("media");
                              // Re-dispatch through the dedicated video input so
                              // we hit the timeline-aware path without
                              // duplicating its 50+ lines of clip wiring.
                              const dt = new DataTransfer();
                              dt.items.add(file);
                              if (fileInputRef.current) {
                                fileInputRef.current.files = dt.files;
                                fileInputRef.current.dispatchEvent(
                                  new Event("change", { bubbles: true }),
                                );
                              }
                              return;
                            }
                            if (file.type.startsWith("audio/")) {
                              setFilesTab("audio");
                              const dt = new DataTransfer();
                              dt.items.add(file);
                              if (audioInputRef.current) {
                                audioInputRef.current.files = dt.files;
                                audioInputRef.current.dispatchEvent(
                                  new Event("change", { bubbles: true }),
                                );
                              }
                              return;
                            }
                            if (file.type.startsWith("image/")) {
                              setFilesTab("media");
                              try {
                                const {
                                  data: { user },
                                } = await supabase.auth.getUser();
                                const asset = await uploadProjectAsset({
                                  file,
                                  projectId,
                                  companyId,
                                  userId: user?.id,
                                });
                                const url = await getAssetSignedUrl(
                                  asset.storage_path,
                                );
                                if (url)
                                  setAssetPreviewUrls((prev) => ({
                                    ...prev,
                                    [asset.id]: url,
                                  }));
                                // Prepend so the new image lands at the top of the gallery.
                                setImageLibraryAssets((prev) => [
                                  asset,
                                  ...prev,
                                ]);
                              } catch (err) {
                                console.error(
                                  "[VideoEditor] image upload failed:",
                                  err,
                                );
                                setUploadErrorMsg(
                                  describeUploadError(err, file.name),
                                );
                              }
                              return;
                            }
                            // No es imagen, video ni audio: el `accept` del
                            // input no alcanza (el usuario puede forzar "todos
                            // los archivos" en el diálogo del sistema), así que
                            // avisamos en vez de no hacer nada.
                            setUploadErrorMsg(
                              describeUploadError(
                                new Error("Unsupported file type"),
                                file.name,
                              ),
                            );
                          }}
                        />

                        {/* Subir archivo / Generar con IA / tabs de tipo:
                       ocultos en modo selección (cuando venís de un slot de
                       escena IA solo se elige una imagen ya existente). */}
                        {!filesPickTarget && (
                          <>
                        {/* Pills de tipo (estilo modal) */}
                        <div
                          className="flex items-center gap-1"
                          data-tour="files-pills"
                        >
                          {(
                            [
                              "media",
                              "audio",
                              "productos",
                              "escenas",
                              "historial",
                            ] as const
                          ).map((t) => {
                            const active = filesTab === t;
                            const PIcon =
                              t === "media"
                                ? Images
                                : t === "audio"
                                  ? Music
                                  : t === "productos"
                                    ? Package
                                    : t === "escenas"
                                      ? Mountain
                                      : Clock;
                            return (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setFilesTab(t)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium transition-colors ${
                                  active
                                    ? "bg-brand-primary text-white"
                                    : "bg-neutral-100 text-brand-black/60 hover:bg-neutral-200"
                                }`}
                              >
                                <PIcon className="w-3.5 h-3.5" />
                                {t === "media"
                                  ? "Multimedia"
                                  : t === "audio"
                                    ? "Audio"
                                    : t === "productos"
                                      ? "Productos"
                                      : t === "escenas"
                                        ? "Escenas"
                                        : "Historial"}
                              </button>
                            );
                          })}
                        </div>
                          </>
                        )}

                        {/* Pills en modo selección (desde un frame de escena):
                       solo Imágenes y Productos, las fuentes válidas para un
                       frame. */}
                        {filesPickTarget && (
                          <div className="flex items-center gap-1">
                            {(
                              [
                                "media",
                                "productos",
                                "escenas",
                                "historial",
                              ] as const
                            ).map((t) => {
                              const active = filesTab === t;
                              const PIcon =
                                t === "media"
                                  ? Images
                                  : t === "productos"
                                    ? Package
                                    : t === "escenas"
                                      ? Mountain
                                      : Clock;
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setFilesTab(t)}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium transition-colors ${
                                    active
                                      ? "bg-brand-primary text-white"
                                      : "bg-neutral-100 text-brand-black/60 hover:bg-neutral-200"
                                  }`}
                                >
                                  <PIcon className="w-3.5 h-3.5" />
                                  {t === "media"
                                    ? "Multimedia"
                                    : t === "productos"
                                      ? "Productos"
                                      : t === "escenas"
                                        ? "Escenas"
                                        : "Historial"}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* 4. Tab content ──────────────────────────────────── */}
                        {filesTab === "audio" && (
                          <div className="pt-1">
                            {libraryLoading && !filesLoadedOnceRef.current ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    filesUploadInputRef.current?.click()
                                  }
                                  className="flex items-center justify-center gap-1.5 p-2.5 rounded-md border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 hover:bg-brand-primary/[0.03] transition-colors"
                                >
                                  <Upload className="w-4 h-4 text-neutral-400" />
                                  <span className="text-[12px] font-medium text-neutral-500">
                                    Subir
                                  </span>
                                </button>
                                {libraryAssets.map((asset) => {
                                  const isPlaying =
                                    playingAudioAssetId === asset.id;
                                  const peaks = audioAssetPeaks[asset.id];
                                  const url = assetPreviewUrls[asset.id];
                                  return (
                                    <div
                                      key={asset.id}
                                      onClick={() => addAssetToTimeline(asset)}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData(
                                          "application/x-atlas-asset-id",
                                          asset.id,
                                        );
                                        e.dataTransfer.effectAllowed = "copy";
                                        const img = buildTimelineClipDragImage(
                                          "audio",
                                          asset.name || "Audio",
                                        );
                                        e.dataTransfer.setDragImage(
                                          img,
                                          100,
                                          20,
                                        );
                                      }}
                                      className="group flex items-center gap-2.5 p-2.5 rounded-md bg-neutral-50 hover:bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all text-left cursor-grab active:cursor-grabbing"
                                      title={asset.name || "Audio"}
                                    >
                                      <div className="w-9 h-9 rounded-sm bg-emerald-50 flex items-center justify-center shrink-0">
                                        <Music className="w-4 h-4 text-emerald-600" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        {isPlaying &&
                                        peaks &&
                                        peaks.length > 0 ? (
                                          <div className="flex items-center h-5 w-full gap-[2px] overflow-hidden">
                                            {peaks.map((p, i) => {
                                              const barRatio =
                                                (i + 0.5) / peaks.length;
                                              const isPlayed =
                                                barRatio <=
                                                audioPlaybackProgress;
                                              const dist = Math.abs(
                                                barRatio -
                                                  audioPlaybackProgress,
                                              );
                                              const pulse =
                                                dist < 0.04 ? 1.35 : 1;
                                              const h = Math.max(
                                                0.2,
                                                p * pulse,
                                              );
                                              return (
                                                <div
                                                  key={i}
                                                  style={{
                                                    width: 2,
                                                    height: `${Math.min(1, h) * 100}%`,
                                                    backgroundColor: isPlayed
                                                      ? "#10b981"
                                                      : "#a7f3d0",
                                                    borderRadius: 1,
                                                    transition:
                                                      "height 80ms linear, background-color 120ms linear",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <p className="text-[13px] font-medium text-brand-black/80 truncate">
                                            {asset.name || "Audio"}
                                          </p>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!url) return;
                                          playAudioPreview(asset.id, url);
                                        }}
                                        disabled={!url}
                                        aria-label={
                                          isPlaying ? "Pausar" : "Reproducir"
                                        }
                                        className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center transition-colors disabled:opacity-50 ${
                                          isPlaying
                                            ? "bg-brand-primary border-brand-primary text-white"
                                            : "bg-white border-neutral-200 text-brand-black/70 hover:bg-brand-primary hover:border-brand-primary hover:text-white"
                                        }`}
                                      >
                                        {isPlaying ? (
                                          <Pause className="w-3 h-3" />
                                        ) : (
                                          <Play className="w-3 h-3 translate-x-[1px]" />
                                        )}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {filesTab === "media" &&
                          (() => {
                            // Merge AI-generated images (newest first, only those
                            // with a resolved signed URL so we don't show empties)
                            // with uploaded image assets. Keyed prefix avoids id
                            // collisions between the two sources.
                            const generatedItems = imageGenResults
                              .filter((r) => !!r.signedUrl)
                              .map((r) => ({
                                key: `gen:${r.id}`,
                                kind: "generated" as const,
                                id: r.id,
                                url: r.signedUrl as string | undefined,
                                name: r.fileName || "Imagen IA",
                                asset: undefined as ProjectAsset | undefined,
                                ts: r.createdAt || "",
                              }));
                            // Ocultar de Multimedia los assets que vienen de
                            // Productos/Escenas/Historial y de Modelos (viven en
                            // su picker, no son media del proyecto).
                            const hiddenAssetIds = new Set<string>([
                              ...Object.values(productSceneAssetMap),
                              ...Object.values(modelAssetMap),
                            ]);
                            // Ocultar también la COPIA-asset de una imagen
                            // generada: cuando se "materializa" (Utilizar / Usar
                            // como referencia / Animar) se sube como asset con el
                            // MISMO nombre que el archivo de la generada. Sin esto
                            // la imagen aparece 2 veces (la del badge IA + el
                            // asset). Matcheamos por nombre, así cubre también las
                            // copias viejas creadas antes de este fix.
                            const genFileNames = new Set(
                              imageGenResults
                                .map((r) => r.fileName)
                                .filter((n): n is string => !!n),
                            );
                            // Ocultar también las fotos de MODELOS por nombre
                            // (el asset se sube como `${nombre}.ext`). Cubre los
                            // huérfanos importados antes de persistir el map y
                            // los duplicados. `modelItems` se carga al abrir
                            // Archivos.
                            const modelNames = new Set(
                              modelItems.map((m) =>
                                (m.nombre || "modelo").slice(0, 40),
                              ),
                            );
                            const baseName = (n: string | null) =>
                              (n || "").replace(/\.[^.]+$/, "");
                            const assetItems = imageLibraryAssets
                              .filter(
                                (a) =>
                                  !hiddenAssetIds.has(a.id) &&
                                  !(a.name && genFileNames.has(a.name)) &&
                                  !modelNames.has(baseName(a.name)),
                              )
                              .map((a) => ({
                                key: `asset:${a.id}`,
                                kind: "asset" as const,
                                id: a.id,
                                url: assetPreviewUrls[a.id] as
                                  | string
                                  | undefined,
                                name: a.name || "Imagen",
                                asset: a as ProjectAsset | undefined,
                                ts: a.created_at || "",
                              }));
                            // Videos del proyecto (solo fuera de modo selección
                            // — un frame es una imagen).
                            const videoItems = filesPickTarget
                              ? []
                              : libraryAssets.map((v) => ({
                                  key: `vid:${v.id}`,
                                  kind: "video" as const,
                                  id: v.id,
                                  url: assetPreviewUrls[v.id] as
                                    | string
                                    | undefined,
                                  name: v.name || "Video",
                                  asset: v as ProjectAsset | undefined,
                                  ts: v.created_at || "",
                                }));
                            // Videos + imágenes subidas + generadas, TODO junto y
                            // ordenado por fecha (más nuevo arriba). Las que no
                            // tienen fecha conocida caen al final.
                            const all = [
                              ...videoItems,
                              ...assetItems,
                              ...generatedItems,
                            ].sort((a, b) =>
                              (b.ts || "").localeCompare(a.ts || ""),
                            );
                            return (
                              <div className="pt-1">
                                {filesPickTarget && (
                                  <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-brand-primary/10 px-3 py-2">
                                    <span className="text-[12px] font-medium text-brand-black/80">
                                      {filesPickTarget.kind === "first"
                                        ? "Elegí imagen para el frame inicial"
                                        : filesPickTarget.kind === "last"
                                          ? "Elegí imagen para el frame final"
                                          : "Elegí imagen de referencia"}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setFilesPickTarget(null);
                                        setActivePanel("ai-scene");
                                      }}
                                      className="text-[12px] text-brand-primary hover:underline shrink-0"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                )}
                                {(imageLibraryLoading || libraryLoading) &&
                                all.length === 0 &&
                                libraryAssets.length === 0 ? (
                                  <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-3 gap-2">
                                    {!filesPickTarget && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          filesUploadInputRef.current?.click()
                                        }
                                        className="aspect-square rounded-md border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1"
                                      >
                                        <Upload className="w-4 h-4 text-neutral-400" />
                                        <span className="text-[11px] font-medium text-neutral-500">
                                          Subir
                                        </span>
                                      </button>
                                    )}
                                    {all.map((item) => {
                                      // Tile de VIDEO (cuadrado, play + badge).
                                      if (item.kind === "video") {
                                        const asset = item.asset;
                                        if (!asset) return null;
                                        const url = item.url;
                                        const isPlaying =
                                          playingVideoAssetId === asset.id;
                                        return (
                                          <div
                                            key={item.key}
                                            onClick={() =>
                                              addAssetToTimeline(asset)
                                            }
                                            draggable
                                            onDragStart={(e) => {
                                              e.dataTransfer.setData(
                                                "application/x-atlas-asset-id",
                                                asset.id,
                                              );
                                              e.dataTransfer.effectAllowed =
                                                "copy";
                                              const img =
                                                buildTimelineClipDragImage(
                                                  "video",
                                                  asset.name || "Video",
                                                  url || null,
                                                );
                                              e.dataTransfer.setDragImage(
                                                img,
                                                100,
                                                20,
                                              );
                                            }}
                                            className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all cursor-grab active:cursor-grabbing"
                                            title={asset.name || "Video"}
                                          >
                                            {url ? (
                                              <video
                                                ref={(el) => {
                                                  if (el)
                                                    videoPreviewElsRef.current.set(
                                                      asset.id,
                                                      el,
                                                    );
                                                  else
                                                    videoPreviewElsRef.current.delete(
                                                      asset.id,
                                                    );
                                                }}
                                                src={url}
                                                className="w-full h-full object-cover pointer-events-none"
                                                muted={!isPlaying}
                                                playsInline
                                                preload="metadata"
                                                onEnded={() =>
                                                  setPlayingVideoAssetId(
                                                    (prev) =>
                                                      prev === asset.id
                                                        ? null
                                                        : prev,
                                                  )
                                                }
                                              />
                                            ) : (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                              </div>
                                            )}
                                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/55 text-white text-[9px] font-semibold uppercase tracking-wide flex items-center gap-0.5 pointer-events-none">
                                              <Film className="w-2.5 h-2.5" />
                                              Video
                                            </div>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!url) return;
                                                toggleVideoPreview(asset.id);
                                              }}
                                              disabled={!url}
                                              aria-label={
                                                isPlaying
                                                  ? "Pausar"
                                                  : "Reproducir"
                                              }
                                              className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                                                isPlaying
                                                  ? "opacity-0 group-hover:opacity-100"
                                                  : "opacity-100"
                                              }`}
                                            >
                                              <span className="w-9 h-9 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center shadow-md transition-transform group-hover:scale-105">
                                                {isPlaying ? (
                                                  <Pause className="w-4 h-4" />
                                                ) : (
                                                  <Play className="w-4 h-4 translate-x-[1px]" />
                                                )}
                                              </span>
                                            </button>
                                          </div>
                                        );
                                      }
                                      // Tile de IMAGEN / generada.
                                      return (
                                      <button
                                        key={item.key}
                                        draggable={!!item.url}
                                        onDragStart={(e) => {
                                          // Same wire format as video/audio so any
                                          // future drop zone can pick it up. AI
                                          // generations don't have a project-asset
                                          // id yet so we skip dragging for those
                                          // (the user can import via the AI panel).
                                          if (item.kind !== "asset") return;
                                          e.dataTransfer.setData(
                                            "application/x-atlas-asset-id",
                                            item.id,
                                          );
                                          e.dataTransfer.effectAllowed = "copy";
                                          const img =
                                            buildTimelineClipDragImage(
                                              "image",
                                              item.name || "Imagen",
                                              item.url || null,
                                            );
                                          e.dataTransfer.setDragImage(
                                            img,
                                            100,
                                            20,
                                          );
                                        }}
                                        onClick={async (e) => {
                                          // Modo selección (viniste de un slot de
                                          // escena IA con "Ver todas en Archivos"):
                                          // asignar y volver, sin el menú genérico.
                                          if (filesPickTarget) {
                                            if (item.kind === "asset") {
                                              assignSceneImage(
                                                filesPickTarget.kind,
                                                item.id,
                                              );
                                              setFilesPickTarget(null);
                                              setActivePanel("ai-scene");
                                              return;
                                            }
                                            // Las generadas no tienen asset id
                                            // todavía: se materializan al vuelo
                                            // (mismo camino que productos /
                                            // escenas / historial) y recién ahí
                                            // se asignan al slot.
                                            if (item.kind === "generated") {
                                              const target = filesPickTarget.kind;
                                              if (
                                                imageGenImportingGenIds.has(
                                                  item.id,
                                                )
                                              )
                                                return;
                                              const token = ++filesPickTokenRef.current;
                                              setImageGenImportingGenIds(
                                                (prev) => {
                                                  const next = new Set(prev);
                                                  next.add(item.id);
                                                  return next;
                                                },
                                              );
                                              try {
                                                const assetId =
                                                  await importGeneratedImageAsAsset(
                                                    item.id,
                                                  );
                                                // La subida tarda: si en el
                                                // medio el usuario canceló o
                                                // tocó otra imagen, esta ya no
                                                // corresponde.
                                                if (
                                                  assetId &&
                                                  filesPickTokenRef.current ===
                                                    token
                                                ) {
                                                  assignSceneImage(
                                                    target,
                                                    assetId,
                                                  );
                                                  setFilesPickTarget(null);
                                                  setActivePanel("ai-scene");
                                                }
                                              } finally {
                                                setImageGenImportingGenIds(
                                                  (prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(item.id);
                                                    return next;
                                                  },
                                                );
                                              }
                                            }
                                            return;
                                          }
                                          const rect = (
                                            e.currentTarget as HTMLElement
                                          ).getBoundingClientRect();
                                          if (item.kind === "generated") {
                                            // Imagen IA → mismo menú contextual que
                                            // las imágenes, pero adaptado (Usar como
                                            // referencia / Animar / Editar). NO abre
                                            // el detalle directo.
                                            setFilesImageMenu((prev) =>
                                              prev?.generatedId === item.id
                                                ? null
                                                : { generatedId: item.id, rect },
                                            );
                                            return;
                                          }
                                          // Asset → toggle the action dropdown
                                          // anchored to this tile.
                                          setFilesImageMenu((prev) =>
                                            prev?.id === item.id
                                              ? null
                                              : { id: item.id, rect },
                                          );
                                        }}
                                        className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all"
                                        title={item.name}
                                      >
                                        {item.url ? (
                                          <img
                                            src={item.url}
                                            alt={item.name}
                                            className="w-full h-full object-cover pointer-events-none"
                                          />
                                        ) : (
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                          </div>
                                        )}
                                        {item.kind === "generated" && (
                                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-brand-primary/85 text-white text-[9px] font-semibold uppercase tracking-wide flex items-center gap-0.5">
                                            <Sparkles className="w-2.5 h-2.5" />
                                            IA
                                          </div>
                                        )}
                                        {item.kind === "generated" &&
                                          imageGenImportingGenIds.has(
                                            item.id,
                                          ) && (
                                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                                            </div>
                                          )}
                                      </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        {/* Productos — catálogo de la marca. Al tocar uno se
                       importa como imagen del proyecto y se abre el mismo menú
                       que las imágenes (Generar IA / Usar como referencia /
                       Animar). */}
                        {(filesTab === "productos" ||
                          filesTab === "escenas" ||
                          filesTab === "historial") &&
                          (() => {
                            const items =
                              filesTab === "escenas"
                                ? filesSceneItems
                                : filesTab === "historial"
                                  ? filesHistorialItems
                                  : filesProductItems;
                            const loading =
                              filesTab === "escenas"
                                ? filesScenesLoading
                                : filesTab === "historial"
                                  ? filesHistorialLoading
                                  : filesProductsLoading;
                            const importingSet =
                              filesTab === "escenas"
                                ? filesImportingSceneIds
                                : filesTab === "historial"
                                  ? filesImportingHistorialIds
                                  : filesImportingProductIds;
                            const setImporting =
                              filesTab === "escenas"
                                ? setFilesImportingSceneIds
                                : filesTab === "historial"
                                  ? setFilesImportingHistorialIds
                                  : setFilesImportingProductIds;
                            const emptyMsg =
                              filesTab === "escenas"
                                ? "No hay escenas todavía."
                                : filesTab === "historial"
                                  ? "No hay imágenes en el historial de esta marca."
                                  : "No hay productos en esta marca.";
                            return (
                              <div className="pt-1">
                                {filesTab === "productos" &&
                                  agentProductRequest && (
                                    <div className="mb-2 rounded-md bg-brand-primary/10 px-3 py-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <span className="text-[12px] font-medium text-brand-black/80">
                                          {agentProductRequest.message}
                                        </span>
                                        <button
                                          onClick={cancelAgentProductPick}
                                          className="text-[12px] text-brand-primary hover:underline shrink-0"
                                        >
                                          Cancelar
                                        </button>
                                      </div>
                                      {agentProductRequest.multiple && (
                                        <button
                                          onClick={() =>
                                            confirmAgentProductPick(
                                              agentProductSelection,
                                            )
                                          }
                                          disabled={
                                            agentProductSelection.length === 0
                                          }
                                          className="mt-2 w-full rounded-md bg-brand-primary text-white text-[12px] font-medium py-1.5 disabled:opacity-40"
                                        >
                                          {agentProductRequest.buttonLabel} (
                                          {agentProductSelection.length})
                                        </button>
                                      )}
                                    </div>
                                  )}
                                {filesPickTarget && (
                                  <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-brand-primary/10 px-3 py-2">
                                    <span className="text-[12px] font-medium text-brand-black/80">
                                      {filesPickTarget.kind === "first"
                                        ? "Elegí imagen para el frame inicial"
                                        : filesPickTarget.kind === "last"
                                          ? "Elegí imagen para el frame final"
                                          : "Elegí imagen de referencia"}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setFilesPickTarget(null);
                                        setActivePanel("ai-scene");
                                      }}
                                      className="text-[12px] text-brand-primary hover:underline shrink-0"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                )}
                                {loading ? (
                                  <ProductGridSkeleton
                                    count={9}
                                    gridClassName="grid grid-cols-3 gap-2"
                                    tileClassName="rounded-md"
                                  />
                                ) : items.length === 0 ? (
                                  <p className="text-[13px] text-neutral-400 px-1 py-6 text-center">
                                    {emptyMsg}
                                  </p>
                                ) : (
                                  <div className="grid grid-cols-3 gap-2">
                                    {items.map((p) => {
                                      const importing = importingSet.has(p.id);
                                      const selected =
                                        filesTab === "productos" &&
                                        !!agentProductRequest?.multiple &&
                                        agentProductSelection.some(
                                          (x) => x.id === p.id,
                                        );
                                      return (
                                        <button
                                          key={`fpick-${p.id}`}
                                          type="button"
                                          title={p.name}
                                          onClick={async (e) => {
                                            const rect = (
                                              e.currentTarget as HTMLElement
                                            ).getBoundingClientRect();
                                            // Modo "selección para el Director"
                                            // (request_product_selection): tocar
                                            // resuelve (simple) o marca (múltiple).
                                            if (
                                              filesTab === "productos" &&
                                              agentProductRequest
                                            ) {
                                              if (agentProductRequest.multiple) {
                                                setAgentProductSelection(
                                                  (prev) =>
                                                    prev.some(
                                                      (x) => x.id === p.id,
                                                    )
                                                      ? prev.filter(
                                                          (x) => x.id !== p.id,
                                                        )
                                                      : [...prev, p],
                                                );
                                              } else {
                                                confirmAgentProductPick([p]);
                                              }
                                              return;
                                            }
                                            if (!filesPickTarget) {
                                              // Archivos: abrir el menú SIN
                                              // importar (lazy — se importa
                                              // recién al elegir una acción).
                                              setFilesImageMenu({
                                                product: p,
                                                rect,
                                              });
                                              return;
                                            }
                                            // Modo selección: importar y asignar
                                            // al frame.
                                            if (importing) return;
                                            const target = filesPickTarget.kind;
                                            const token =
                                              ++filesPickTokenRef.current;
                                            setImporting((prev) => {
                                              const n = new Set(prev);
                                              n.add(p.id);
                                              return n;
                                            });
                                            try {
                                              const assetId =
                                                await importProductToImageAsset(
                                                  p,
                                                );
                                              // Mismo guard que las generadas:
                                              // la importación tarda y el
                                              // usuario puede haber cancelado o
                                              // elegido otra en el medio.
                                              if (
                                                assetId &&
                                                filesPickTokenRef.current ===
                                                  token
                                              ) {
                                                assignSceneImage(
                                                  target,
                                                  assetId,
                                                );
                                                setFilesPickTarget(null);
                                                setActivePanel("ai-scene");
                                              }
                                            } finally {
                                              setImporting((prev) => {
                                                const n = new Set(prev);
                                                n.delete(p.id);
                                                return n;
                                              });
                                            }
                                          }}
                                          className={`group relative aspect-square rounded-md overflow-hidden bg-neutral-100 transition-all ${
                                            selected
                                              ? "ring-2 ring-brand-primary"
                                              : "hover:ring-2 hover:ring-brand-primary/50"
                                          }`}
                                        >
                                          <CachedImg
                                            src={p.imageUrl}
                                            alt={p.name}
                                            className="w-full h-full object-cover pointer-events-none"
                                          />
                                          {importing && (
                                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                                            </div>
                                          )}
                                          {selected && (
                                            <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-brand-primary text-white flex items-center justify-center shadow">
                                              <Check className="w-3.5 h-3.5" />
                                            </div>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        {/* Per-image action menu — anchored under the clicked
                      tile. Fixed positioning so we render at the panel
                      level but visually attach to the tile. */}
                        {filesImageMenu &&
                          (() => {
                            const { rect } = filesImageMenu;
                            // Resuelve el asset id: si el menú es de una imagen
                            // ya importada usa su id; si es de un producto/escena
                            // (lazy), lo importa recién ahora (cacheado, no
                            // duplica).
                            const isGenerated = !!filesImageMenu.generatedId;
                            const resolveAssetId = async (): Promise<
                              string | null
                            > => {
                              if (filesImageMenu.id) return filesImageMenu.id;
                              if (filesImageMenu.product)
                                return importProductToImageAsset(
                                  filesImageMenu.product,
                                );
                              if (filesImageMenu.generatedId)
                                return importGeneratedImageAsAsset(
                                  filesImageMenu.generatedId,
                                );
                              return null;
                            };
                            // Place 4px below the tile, left-aligned, with a
                            // sensible min-width that fits all three labels.
                            const MENU_WIDTH = 200;
                            // Alto aprox del menú (3 ítems). Si no hay espacio
                            // abajo (ej. última fila de la grilla), lo
                            // volteamos hacia arriba del tile.
                            const MENU_HEIGHT = 140;
                            const top =
                              window.innerHeight - rect.bottom >= MENU_HEIGHT + 8
                                ? rect.bottom + 4
                                : Math.max(8, rect.top - MENU_HEIGHT - 4);
                            // Clamp to viewport so the menu doesn't get clipped on
                            // the right edge of narrow panels.
                            const left = Math.min(
                              rect.left,
                              window.innerWidth - MENU_WIDTH - 8,
                            );
                            const close = () => setFilesImageMenu(null);
                            return (
                              <>
                                <div
                                  className="fixed inset-0 z-[60]"
                                  onClick={close}
                                />
                                <div
                                  className="fixed z-[61] bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden"
                                  style={{ top, left, width: MENU_WIDTH }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {!isGenerated && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          const id = await resolveAssetId();
                                          if (!id) return;
                                          // Generar con IA → image-gen panel with this asset preattached.
                                          setImageGenAttachedAssetIds([id]);
                                          setEditingClipId(null);
                                          setActivePanel("ai-image-gen");
                                          close();
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                      >
                                        <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                                        Generar con IA
                                      </button>
                                      <div className="h-px bg-black/[0.06]" />
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const id = await resolveAssetId();
                                      if (!id) return;
                                      // Usar como referencia → ai-scene general + reference mode with this asset.
                                      setEditingClipId(null);
                                      setSceneMode("general");
                                      setSceneRefMode("reference");
                                      setSceneRefAssetIds([id]);
                                      setSceneFirstFrameAssetId(null);
                                      setSceneLastFrameAssetId(null);
                                      setActivePanel("ai-scene");
                                      close();
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                  >
                                    <ImageIcon className="w-3.5 h-3.5 text-brand-black/50" />
                                    Usar como referencia
                                  </button>
                                  <div className="h-px bg-black/[0.06]" />
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const id = await resolveAssetId();
                                      if (!id) return;
                                      // Animar → ai-scene general + frames mode with this asset as first frame.
                                      setEditingClipId(null);
                                      setSceneMode("general");
                                      setSceneRefMode("frames");
                                      setSceneRefAssetIds([]);
                                      setSceneFirstFrameAssetId(id);
                                      setSceneLastFrameAssetId(null);
                                      setActivePanel("ai-scene");
                                      close();
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                  >
                                    <Film className="w-3.5 h-3.5 text-brand-black/50" />
                                    Animar
                                  </button>
                                  {isGenerated && filesImageMenu.generatedId && (
                                    <>
                                      <div className="h-px bg-black/[0.06]" />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          // Editar (solo IA): abre el flujo de
                                          // edición de la imagen generada. Marca
                                          // el origen (Multimedia) para que el
                                          // back vuelva acá, y limpia cualquier
                                          // target de escena (Utilizar no aplica).
                                          const genId =
                                            filesImageMenu.generatedId!;
                                          setImageGenTarget(null);
                                          setImageGenDetailFromFiles(true);
                                          setImageGenDetailId(genId);
                                          setImageGenEditing(true);
                                          setActivePanel("ai-image-gen");
                                          close();
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                      >
                                        <Pencil className="w-3.5 h-3.5 text-brand-black/50" />
                                        Editar
                                      </button>
                                    </>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                      </div>
                    )}

                    {/* Picker de modelos (Modo Diálogo) — mismo estilo que la
                        biblioteca: vive en el panel Archivos, con banner + grilla.
                        El modelo elegido pasa a ser el "Frame inicial". */}
                    {activePanel === "files" && modelPickerOpen && (
                      // En alta de personaje la sección toma TODA la altura
                      // del scroller (flex-col) para que el wrapper de la
                      // vista scrollee él mismo y el footer sticky de
                      // CreateView/UploadFlow quede pegado al fondo del
                      // panel, como en el sheet del catálogo V2.
                      <div
                        className={
                          personajeAddView !== null
                            ? "h-full flex flex-col"
                            : "space-y-3"
                        }
                      >
                        <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-brand-primary/10 px-3 py-2">
                          <span className="text-[12px] font-medium text-brand-black/80">
                            {modelPickKind === "ref"
                              ? "Elegí un personaje de referencia"
                              : modelPickKind === "attach"
                                ? "Elegí un personaje para sumar"
                                : "Elegí el personaje que va a hablar"}
                          </span>
                          <button
                            onClick={() => {
                              setModelPickerOpen(false);
                              setActivePanel(
                                modelPickKind === "attach"
                                  ? "ai-image-gen"
                                  : "ai-scene",
                              );
                            }}
                            className="text-[12px] text-brand-primary hover:underline shrink-0"
                          >
                            Cancelar
                          </button>
                        </div>
                        {personajeAddView !== null ? (
                          // Alta de personaje — mismas vistas que el catálogo
                          // de la V2, con el wrapper del sheet: scroll propio
                          // (flex-1 min-h-0) y px-5 porque el footer sticky
                          // de las vistas usa -mx-5/px-5 para el full-bleed.
                          // Sin fila "Volver": CreateView y UploadFlow traen
                          // su propio Cancelar en el footer.
                          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-1 pb-6 md:pb-0 animate-mode-swap">
                            {personajeAddView === "create" ? (
                              <CreateView
                                userId={currentUserId}
                                companyId={companyId ?? imageGenCompanyId}
                                onBack={() => setPersonajeAddView(null)}
                                onModelCreated={() => {}}
                                onConfirmModel={(m) => {
                                  if (m.thumbnailUrl) {
                                    setModelItems((prev) => [m, ...prev]);
                                  }
                                  setPersonajeAddView(null);
                                }}
                              />
                            ) : (
                              <UploadFlow
                                userId={currentUserId}
                                companyId={companyId ?? imageGenCompanyId}
                                onModelCreated={() => {}}
                                onBack={() => setPersonajeAddView(null)}
                                onUtilizar={(m) => {
                                  if (m.thumbnailUrl) {
                                    setModelItems((prev) => [m, ...prev]);
                                  }
                                  setPersonajeAddView(null);
                                }}
                              />
                            )}
                          </div>
                        ) : modelsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                          </div>
                        ) : (
                          <>
                          {modelItems.length === 0 && (
                            <p className="text-[13px] text-neutral-400 px-1 pt-2 text-center">
                              Todavía no tenés personajes. Crealo con IA o subí
                              una foto.
                            </p>
                          )}
                          <div className="grid grid-cols-3 gap-2">
                            <AddOptionCard
                              label="Agregar"
                              compact
                              className="w-full aspect-[3/4]"
                              options={[
                                {
                                  icon: <Sparkles className="w-3.5 h-3.5" />,
                                  label: "Creá tu personaje",
                                  onClick: () => setPersonajeAddView("create"),
                                },
                                {
                                  icon: <UserPlus className="w-3.5 h-3.5" />,
                                  label: "Cargá tu propio personaje",
                                  onClick: () => setPersonajeAddView("upload"),
                                },
                              ]}
                            />
                            {modelItems.map((m) => {
                              const importing = modelsImportingIds.has(m.id);
                              return (
                                <button
                                  key={`model-${m.id}`}
                                  type="button"
                                  title={m.nombre}
                                  disabled={importing}
                                  onClick={async () => {
                                    if (importing) return;
                                    setModelsImportingIds((prev) => {
                                      const n = new Set(prev);
                                      n.add(m.id);
                                      return n;
                                    });
                                    try {
                                      const assetId =
                                        await importModelToImageAsset(m);
                                      if (assetId) {
                                        if (modelPickKind === "ref") {
                                          // Modo General: el modelo va directo
                                          // como referencia (ahí podés tener
                                          // varias imágenes).
                                          setSceneRefAssetIds((prev) =>
                                            prev.includes(assetId)
                                              ? prev
                                              : [...prev, assetId],
                                          );
                                          setModelPickerOpen(false);
                                          setActivePanel("ai-scene");
                                        } else if (
                                          modelPickKind === "attach"
                                        ) {
                                          // Sumar a la generación de imagen en
                                          // curso (sin resetear prompt/target) y
                                          // volver al panel Generar con IA.
                                          setImageGenAttachedAssetIds((prev) =>
                                            prev.includes(assetId)
                                              ? prev
                                              : [...prev, assetId],
                                          );
                                          setModelPickerOpen(false);
                                          setActivePanel("ai-image-gen");
                                        } else {
                                          // Modo Diálogo: NO lo ponemos crudo como
                                          // Frame inicial. Lo llevamos a Generar
                                          // con IA con el modelo adjunto como
                                          // referencia, apuntando al Frame inicial
                                          // (al "Utilizar" la generada queda ahí).
                                          // Así el usuario lo customiza (ej:
                                          // "vestido de chef") antes de usarlo.
                                          setImageGenAttachedAssetIds([assetId]);
                                          setModelPickerOpen(false);
                                          openImageGenPanel({ kind: "first" });
                                          // Ya eligió modelo acá → ocultar el
                                          // botón "Usar modelo" del panel.
                                          setImageGenFromModelFlow(true);
                                        }
                                      }
                                    } finally {
                                      setModelsImportingIds((prev) => {
                                        const n = new Set(prev);
                                        n.delete(m.id);
                                        return n;
                                      });
                                    }
                                  }}
                                  className="group relative aspect-[3/4] rounded-md overflow-hidden bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all"
                                >
                                  <img
                                    src={m.thumbnailUrl}
                                    alt={m.nombre}
                                    className="w-full h-full object-cover pointer-events-none"
                                  />
                                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1">
                                    <span className="block text-[10px] font-medium text-white truncate">
                                      {m.nombre}
                                    </span>
                                  </div>
                                  {importing && (
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          </>
                        )}
                      </div>
                    )}

                    {activePanel === "ai-generate" && (
                      <div className="space-y-3">
                        <textarea
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="Describí el video que querés crear..."
                          className="w-full h-24 px-3 py-2.5 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-none placeholder:text-neutral-400"
                        />
                        <div>
                          <p className="text-[13px] font-medium text-brand-black/80 mb-1.5">
                            Duración
                          </p>
                          <div className="flex gap-2">
                            {(["3", "5", "10"] as const).map((d) => (
                              <button
                                key={d}
                                onClick={() => setAiDuration(d)}
                                className={`flex-1 h-9 rounded-md text-[13px] font-medium transition-colors ${
                                  aiDuration === d
                                    ? "bg-brand-primary text-white shadow-sm"
                                    : "bg-surface-elevated border border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                                }`}
                              >
                                {d}s
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={handleAiGenerate}
                          disabled={!aiPrompt.trim() || aiGenerating}
                          className={`w-full h-8 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                            !aiPrompt.trim() || aiGenerating
                              ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                              : "bg-brand-primary hover:bg-brand-primary-hover text-white"
                          }`}
                        >
                          {aiGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generando...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generar video
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {activePanel === "upload" && (
                      <div className="space-y-3">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-neutral-300 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                            <Film className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-[15px] font-medium text-brand-black/80">
                              Subir video
                            </p>
                            <p className="text-[11px] text-neutral-400">
                              MP4, MOV, WebM
                            </p>
                          </div>
                        </button>
                        <button
                          onClick={() => audioInputRef.current?.click()}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-neutral-300 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                            <Music className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-[15px] font-medium text-brand-black/80">
                              Subir audio
                            </p>
                            <p className="text-[11px] text-neutral-400">
                              MP3, WAV, AAC
                            </p>
                          </div>
                        </button>
                        <button className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-neutral-300 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors">
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                            <ImageIcon className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-[15px] font-medium text-brand-black/80">
                              Subir imagen
                            </p>
                            <p className="text-[11px] text-neutral-400">
                              JPG, PNG, WebP
                            </p>
                          </div>
                        </button>
                      </div>
                    )}

                    {activePanel === "audio" && (
                      <div className="space-y-3">
                        {[
                          "Música corporativa",
                          "Lofi tranquilo",
                          "Energía pop",
                          "Cinematic épico",
                          "Ambiente natural",
                        ].map((name, i) => (
                          <button
                            key={name}
                            onClick={() => {
                              const audioTrack = getTrackForClipType("audio");
                              const newClip: TimelineClip = {
                                id: generateId(),
                                type: "audio",
                                name,
                                startTime: getNextStartTime(clips, audioTrack),
                                duration: [8, 12, 6, 15, 10][i],
                                color: audioTrack.color,
                                volume: 1,
                              };
                              setClips((prev) => [...prev, newClip]);
                              setSelectedClipId(newClip.id);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-neutral-100 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-sm bg-emerald-50 flex items-center justify-center shrink-0">
                              <Music className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="text-left flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/80 truncate">
                                {name}
                              </p>
                              <p className="text-[11px] text-neutral-400">
                                {[8, 12, 6, 15, 10][i]}s
                              </p>
                            </div>
                            <Plus className="w-4 h-4 text-neutral-400 shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}

                    {activePanel === "text" &&
                      (() => {
                        // When a text clip is selected, the panel edits THAT clip
                        // live (changes flow back to the clip via setClips). When
                        // nothing is selected (or non-text clip), the panel composes
                        // a NEW clip from `textDraft` and appends it on submit.
                        const editingTextClip =
                          selectedClip && selectedClip.type === "text"
                            ? selectedClip
                            : null;
                        const cur = editingTextClip
                          ? {
                              text: editingTextClip.text ?? "",
                              fontFamily:
                                editingTextClip.fontFamily ??
                                DEFAULT_TEXT_CLIP.fontFamily,
                              fontSizePct:
                                editingTextClip.fontSizePct ??
                                DEFAULT_TEXT_CLIP.fontSizePct,
                              fontWeight: (editingTextClip.fontWeight ??
                                DEFAULT_TEXT_CLIP.fontWeight) as
                                | "normal"
                                | "bold",
                              fontStyle: (editingTextClip.fontStyle ??
                                DEFAULT_TEXT_CLIP.fontStyle) as
                                | "normal"
                                | "italic",
                              textColor:
                                editingTextClip.textColor ??
                                DEFAULT_TEXT_CLIP.textColor,
                              textAlign: (editingTextClip.textAlign ??
                                DEFAULT_TEXT_CLIP.textAlign) as
                                | "left"
                                | "center"
                                | "right",
                              enterAnimation:
                                editingTextClip.enterAnimation ?? "none",
                              enterDurationSec:
                                editingTextClip.enterDurationSec ?? 0.5,
                              exitAnimation:
                                editingTextClip.exitAnimation ?? "none",
                              exitDurationSec:
                                editingTextClip.exitDurationSec ?? 0.5,
                            }
                          : textDraft;
                        const patch = (p: Partial<typeof cur>) => {
                          if (editingTextClip) {
                            const namePatch =
                              p.text !== undefined
                                ? {
                                    name:
                                      p.text.length > 20
                                        ? p.text.slice(0, 17) + "…"
                                        : p.text || "Texto",
                                  }
                                : {};
                            setClips((prev) =>
                              patchClipsWithSubtitleSync(
                                prev,
                                editingTextClip.id,
                                { ...p, ...namePatch } as Partial<TimelineClip>,
                              ),
                            );
                          } else {
                            setTextDraft((d) => ({ ...d, ...p }));
                          }
                        };
                        const onSubmit = () => {
                          if (editingTextClip) {
                            // Already live-editing; just close back to root.
                            setActivePanel("root");
                            return;
                          }
                          const t = (cur.text || "").trim();
                          if (!t) return;
                          addTextClip({
                            text: t,
                            fontFamily: cur.fontFamily,
                            fontSizePct: cur.fontSizePct,
                            fontWeight: cur.fontWeight,
                            fontStyle: cur.fontStyle,
                            textColor: cur.textColor,
                            textAlign: cur.textAlign,
                            enterAnimation: cur.enterAnimation,
                            enterDurationSec: cur.enterDurationSec,
                            exitAnimation: cur.exitAnimation,
                            exitDurationSec: cur.exitDurationSec,
                          });
                          // Reset draft so the next time the user opens the panel
                          // they start fresh, not with the previous content.
                          setTextDraft((d) => ({ ...d, text: "" }));
                        };
                        const canSubmit = editingTextClip
                          ? true
                          : (cur.text || "").trim().length > 0;
                        return (
                          <div className="space-y-4">
                            <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                              {editingTextClip
                                ? "Editar texto"
                                : "Añadir texto"}
                            </p>

                            {/* Subtitle-group scope toggle. Only meaningful when the
                        clip was created as part of a "Generar subtítulos"
                        batch (isSubtitle + subtitleGroupId). Off (default)
                        means style edits propagate to every sibling in the
                        group; on means edits affect only this clip. */}
                            {editingTextClip &&
                              editingTextClip.isSubtitle &&
                              editingTextClip.subtitleGroupId && (
                                <label className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-neutral-800/60 hover:bg-neutral-800 transition-colors cursor-pointer">
                                  <span className="text-[12px] text-neutral-200">
                                    Editar solo este subtítulo
                                  </span>
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-purple-500 cursor-pointer"
                                    checked={
                                      !!editingTextClip.subtitleStyleOverride
                                    }
                                    onChange={(e) => {
                                      const next = e.target.checked;
                                      setClips((prev) =>
                                        prev.map((c) =>
                                          c.id === editingTextClip.id
                                            ? {
                                                ...c,
                                                subtitleStyleOverride: next,
                                              }
                                            : c,
                                        ),
                                      );
                                    }}
                                  />
                                </label>
                              )}

                            {/* Live preview */}
                            <div
                              className="rounded-md bg-neutral-900 px-3 py-6 flex items-center justify-center min-h-[80px] overflow-hidden"
                              style={{ textAlign: cur.textAlign }}
                            >
                              <span
                                className="leading-tight whitespace-pre-wrap break-words max-w-full"
                                style={{
                                  fontFamily: fontStackFor(cur.fontFamily),
                                  fontSize: `${Math.max(12, Math.min(48, cur.fontSizePct * 1.6))}px`,
                                  fontWeight:
                                    cur.fontWeight === "bold" ? 700 : 400,
                                  fontStyle: cur.fontStyle,
                                  color: cur.textColor,
                                }}
                              >
                                {cur.text ||
                                  (editingTextClip ? "" : "Vista previa")}
                              </span>
                            </div>

                            {/* Text content */}
                            <div>
                              <label className="block text-[12px] font-medium text-brand-black/70 mb-1.5">
                                Texto
                              </label>
                              <textarea
                                value={cur.text}
                                onChange={(e) =>
                                  patch({ text: e.target.value })
                                }
                                placeholder="Escribí tu texto..."
                                rows={3}
                                maxLength={500}
                                className="w-full text-[14px] text-neutral-900 resize-none border border-neutral-200 rounded-md px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                              />
                            </div>

                            {/* Font */}
                            <div>
                              <label className="block text-[12px] font-medium text-brand-black/70 mb-1.5">
                                Fuente
                              </label>
                              <FontPicker
                                value={cur.fontFamily}
                                onChange={(fam) => patch({ fontFamily: fam })}
                                triggerClassName="w-full h-9 flex items-center justify-between gap-2 text-[14px] text-neutral-900 border border-neutral-200 rounded-md px-2.5 py-2 hover:bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-brand-primary bg-white transition-colors"
                                panelAlign="left"
                              />
                            </div>

                            {/* Size + style + align — single row, matches the top
                        clip toolbar design (− [n] + pill, then B/I, then
                        align buttons). */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {/* Tamaño — pill con − [n] + */}
                              <div className="h-9 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() =>
                                    patch({
                                      fontSizePct: Math.max(
                                        2,
                                        Math.round(
                                          (cur.fontSizePct - 0.5) * 2,
                                        ) / 2,
                                      ),
                                    })
                                  }
                                  className="h-9 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                  title="Reducir tamaño"
                                >
                                  −
                                </button>
                                <NumberField
                                  value={cur.fontSizePct}
                                  min={2}
                                  max={30}
                                  step={0.5}
                                  decimals={1}
                                  onCommit={(n) => patch({ fontSizePct: n })}
                                  className="w-10 h-9 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                  ariaLabel="Tamaño"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    patch({
                                      fontSizePct: Math.min(
                                        30,
                                        Math.round(
                                          (cur.fontSizePct + 0.5) * 2,
                                        ) / 2,
                                      ),
                                    })
                                  }
                                  className="h-9 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                  title="Aumentar tamaño"
                                >
                                  +
                                </button>
                              </div>

                              <div className="w-px h-6 bg-neutral-200 mx-0.5" />

                              <button
                                type="button"
                                onClick={() =>
                                  patch({
                                    fontWeight:
                                      cur.fontWeight === "bold"
                                        ? "normal"
                                        : "bold",
                                  })
                                }
                                className={`w-9 h-9 rounded-md border text-[14px] font-bold transition-colors ${
                                  cur.fontWeight === "bold"
                                    ? "bg-brand-primary text-white border-brand-primary"
                                    : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                                }`}
                                title="Negrita"
                              >
                                B
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  patch({
                                    fontStyle:
                                      cur.fontStyle === "italic"
                                        ? "normal"
                                        : "italic",
                                  })
                                }
                                className={`w-9 h-9 rounded-md border text-[14px] italic transition-colors ${
                                  cur.fontStyle === "italic"
                                    ? "bg-brand-primary text-white border-brand-primary"
                                    : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                                }`}
                                title="Cursiva"
                              >
                                I
                              </button>
                              <div className="w-px h-6 bg-neutral-200 mx-0.5" />
                              {(["left", "center", "right"] as const).map(
                                (a) => (
                                  <button
                                    key={a}
                                    type="button"
                                    onClick={() => patch({ textAlign: a })}
                                    className={`w-9 h-9 rounded-md border inline-flex items-center justify-center transition-colors ${
                                      cur.textAlign === a
                                        ? "bg-brand-primary text-white border-brand-primary"
                                        : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                                    }`}
                                    title={
                                      a === "left"
                                        ? "Izquierda"
                                        : a === "center"
                                          ? "Centrado"
                                          : "Derecha"
                                    }
                                  >
                                    {a === "left" && (
                                      <AlignLeft className="w-4 h-4" />
                                    )}
                                    {a === "center" && (
                                      <AlignCenter className="w-4 h-4" />
                                    )}
                                    {a === "right" && (
                                      <AlignRight className="w-4 h-4" />
                                    )}
                                  </button>
                                ),
                              )}

                              <div className="w-px h-6 bg-neutral-200 mx-0.5" />

                              {/* Color — compact picker that sits inline with the
                          alignment buttons. Uses the shared ColorSwatchPicker
                          so the popover (brand row + generic presets + custom
                          hex) stays consistent with the rest of the editor. */}
                              <ColorSwatchPicker
                                value={cur.textColor}
                                onChange={(c) => patch({ textColor: c })}
                                align="right"
                                ariaLabel="Color del texto"
                                triggerLabel="Color"
                              />
                            </div>

                            {/* Animaciones — entrada / salida lado a lado. Cada
                        columna es un MotionPicker custom con preview
                        animado al hover + su propio input de duración
                        debajo. El picker se expande sobre el contenido
                        siguiente gracias al z-index interno. */}
                            <div className="pt-2 border-t border-neutral-200/70 space-y-2">
                              <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                                Animaciones
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1.5">
                                  <MotionPicker
                                    label="Entrada"
                                    kind="enter"
                                    scope="text"
                                    value={cur.enterAnimation}
                                    onChange={(id) =>
                                      patch({ enterAnimation: id })
                                    }
                                  />
                                  {cur.enterAnimation !== "none" && (
                                    <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                      <NumberField
                                        value={cur.enterDurationSec}
                                        min={0.1}
                                        max={5}
                                        step={0.1}
                                        decimals={1}
                                        onCommit={(n) =>
                                          patch({ enterDurationSec: n })
                                        }
                                        className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                        ariaLabel="Duración entrada"
                                      />
                                      <span className="text-[11px] text-neutral-400">
                                        seg
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-1.5">
                                  <MotionPicker
                                    label="Salida"
                                    kind="exit"
                                    scope="text"
                                    value={cur.exitAnimation}
                                    onChange={(id) =>
                                      patch({ exitAnimation: id })
                                    }
                                  />
                                  {cur.exitAnimation !== "none" && (
                                    <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                      <NumberField
                                        value={cur.exitDurationSec}
                                        min={0.1}
                                        max={5}
                                        step={0.1}
                                        decimals={1}
                                        onCommit={(n) =>
                                          patch({ exitDurationSec: n })
                                        }
                                        className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                        ariaLabel="Duración salida"
                                      />
                                      <span className="text-[11px] text-neutral-400">
                                        seg
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* CTA */}
                            <div className="pt-1">
                              <button
                                type="button"
                                onClick={onSubmit}
                                disabled={!canSubmit}
                                className={`w-full h-10 rounded-md text-[14px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                                  canSubmit
                                    ? "bg-brand-primary hover:bg-brand-primary-hover text-white"
                                    : "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                }`}
                              >
                                {editingTextClip ? (
                                  <>Listo</>
                                ) : (
                                  <>
                                    <Plus className="w-4 h-4" />
                                    Añadir texto
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                    {activePanel === "agent-upload" && agentUploadRequest && (
                      <div className="flex flex-col h-full">
                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto space-y-4">
                          {/* Agent message */}
                          <div className="px-3 py-3 rounded-md bg-brand-primary/5 border border-brand-primary/15">
                            <div className="flex items-start gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-brand-primary/15 flex items-center justify-center shrink-0">
                                <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                              </div>
                              <p className="text-[14px] leading-relaxed text-brand-black/85 whitespace-pre-wrap">
                                {agentUploadRequest.message}
                              </p>
                            </div>
                          </div>

                          {/* Drop / browse area */}
                          <div
                            onClick={() => agentUploadInputRef.current?.click()}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const files = e.dataTransfer.files;
                              if (files && files.length > 0)
                                handleAgentUploadFiles(files);
                            }}
                            className="group cursor-pointer rounded-md border-2 border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03] transition-colors px-4 py-8 flex flex-col items-center justify-center text-center"
                          >
                            <div className="w-11 h-11 rounded-full bg-neutral-100 group-hover:bg-brand-primary/10 flex items-center justify-center mb-2.5 transition-colors">
                              <Upload className="w-5 h-5 text-neutral-500 group-hover:text-brand-primary transition-colors" />
                            </div>
                            <p className="text-[14px] font-medium text-brand-black/80">
                              Hacé click para subir
                            </p>
                            <p className="text-[12px] text-neutral-400 mt-0.5">
                              o arrastrá los archivos acá
                            </p>
                            <p className="text-[11px] text-neutral-400 mt-2">
                              {agentUploadRequest.accept === "video/*"
                                ? "Solo videos"
                                : agentUploadRequest.accept ===
                                    "image/*,video/*"
                                  ? "Imágenes o videos"
                                  : "Solo imágenes"}
                            </p>
                          </div>

                          {/* Uploaded files list */}
                          {agentUploadedAssets.length > 0 && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1 mb-2">
                                {agentUploadedAssets.length}{" "}
                                {agentUploadedAssets.length === 1
                                  ? "archivo subido"
                                  : "archivos subidos"}
                              </p>
                              <div className="space-y-2">
                                {agentUploadedAssets.map((asset) => (
                                  <div
                                    key={asset.id}
                                    className="group flex items-center gap-3 px-2.5 py-2 rounded-md bg-surface-card border border-black/[0.06] shadow-sm"
                                  >
                                    <div className="w-11 h-11 rounded-sm bg-neutral-100 overflow-hidden flex items-center justify-center shrink-0">
                                      {asset.url &&
                                      /\.(mp4|mov|webm)$/i.test(asset.name) ? (
                                        <video
                                          src={asset.url}
                                          className="w-full h-full object-cover"
                                          muted
                                          playsInline
                                          preload="metadata"
                                        />
                                      ) : asset.url ? (
                                        <img
                                          src={asset.url}
                                          alt={asset.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <ImageIcon className="w-4 h-4 text-neutral-400" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[13px] font-medium text-brand-black/85 truncate">
                                        {asset.name}
                                      </p>
                                      <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                                        <Check className="w-3 h-3" /> Subido
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeAgentUploadedAsset(asset.id)
                                      }
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-error hover:bg-error-soft transition-colors shrink-0"
                                      title="Quitar de la selección"
                                    >
                                      <Plus className="w-4 h-4 rotate-45" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {agentUploadInProgress && (
                            <div className="flex items-center justify-center gap-2 py-2 text-[12px] text-neutral-500">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {agentUploadRequest.procesarComoPersonaje
                                ? "Procesando personaje… (~30-60s)"
                                : "Subiendo..."}
                            </div>
                          )}
                        </div>

                        {/* Sticky confirm button */}
                        <div className="pt-3 mt-3 border-t border-black/[0.06]">
                          <button
                            type="button"
                            onClick={confirmAgentUpload}
                            disabled={
                              agentUploadedAssets.length === 0 ||
                              agentUploadInProgress
                            }
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 disabled:bg-neutral-200 disabled:text-neutral-400 disabled:cursor-not-allowed text-white rounded-md font-medium text-[14px] shadow-sm transition-colors"
                          >
                            {agentUploadRequest.buttonLabel}
                          </button>
                        </div>
                      </div>
                    )}

                    {activePanel === "brand" && (
                      <div className="space-y-6">
                        {!brandProfile ? (
                          <div className="rounded-md border border-dashed border-black/[0.12] px-4 py-8 text-center">
                            <Palette className="w-6 h-6 text-brand-black/30 mx-auto mb-2" />
                            <p className="text-[13px] text-brand-black/60">
                              Este proyecto todavía no tiene una marca asociada.
                            </p>
                          </div>
                        ) : (
                          <>
                            {/* iOS-style segmented selector — same visual pattern
                          as the Archivos panel (sliding white thumb on a
                          subtle black-tint track) so the brand panel feels
                          consistent with the rest of the editor. */}
                            {(() => {
                              // Segmented selector — 4 tabs. Thumb position is
                              // computed from the active index so adding/removing
                              // tabs in the future only requires editing the
                              // `tabs` array below.
                              const tabs = [
                                {
                                  id: "memoria" as const,
                                  label: "Marca",
                                  Icon: BookOpen,
                                },
                                {
                                  id: "imagenes" as const,
                                  label: "Logos",
                                  Icon: ImageIcon,
                                },
                              ];
                              const idx = tabs.findIndex(
                                (t) => t.id === brandTab,
                              );
                              const pct = 100 / tabs.length;
                              return (
                                <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
                                  <div
                                    className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                                    style={{
                                      width: `calc(${pct}% - 2px)`,
                                      left:
                                        idx === 0
                                          ? "3px"
                                          : `calc(${pct * idx}% + 1px)`,
                                    }}
                                  />
                                  {tabs.map((t) => {
                                    const TabIcon = t.Icon;
                                    const active = brandTab === t.id;
                                    return (
                                      <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setBrandTab(t.id)}
                                        className={`relative z-10 flex-1 h-9 inline-flex items-center justify-center gap-1.5 text-[12px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                                          active
                                            ? "text-brand-black/90"
                                            : "text-brand-black/50"
                                        }`}
                                      >
                                        <TabIcon className="w-3.5 h-3.5" />
                                        {t.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            {/* ── Marca: vista unificada read-only (Memoria +
                          Tipografía + Colores) con el mismo look que
                          Configuración → Mis Marcas. La identidad se edita
                          desde Configuración; acá solo se muestra y alimenta
                          los selectores de color del editor. */}
                            {brandTab === "memoria" && (
                              <section className="space-y-3">
                                {/* Memoria de la IA */}
                                <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
                                  <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-2">
                                    Memoria de la IA
                                  </div>
                                  {brandProfile.context ? (
                                    <div className="relative text-[13px] text-brand-black/80">
                                      <div
                                        className={
                                          brandMemoriaExpanded
                                            ? "pb-12"
                                            : "line-clamp-[8] pb-10"
                                        }
                                      >
                                        {renderMarkdown(brandProfile.context)}
                                      </div>
                                      {!brandMemoriaExpanded && (
                                        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-surface-elevated via-surface-elevated/80 to-transparent" />
                                      )}
                                      <div className="absolute bottom-0 right-0 z-10">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setBrandMemoriaExpanded((v) => !v)
                                          }
                                          className="h-8 px-3.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors"
                                        >
                                          {brandMemoriaExpanded
                                            ? "Ver menos"
                                            : "Ver más"}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-[13px] text-neutral-500">
                                      No hay información disponible todavía.
                                      Completá la descripción de marca para
                                      generar el análisis.
                                    </div>
                                  )}
                                </div>
                                {/* Tipografía */}
                                {(() => {
                                  const f = (brandProfile.fonts ||
                                    {}) as Record<string, unknown>;
                                  const suggested: string[] = Array.isArray(
                                    (f as any).suggested,
                                  )
                                    ? ((f as any).suggested as unknown[]).filter(
                                        (x): x is string => typeof x === "string",
                                      )
                                    : (Array.from(
                                        new Set(
                                          Object.values(f).filter(
                                            (v): v is string =>
                                              typeof v === "string",
                                          ),
                                        ),
                                      ) as string[]);
                                  const fontStyle =
                                    typeof (f as any).style === "string"
                                      ? ((f as any).style as string)
                                      : "";
                                  if (suggested.length === 0) return null;
                                  return (
                                    <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
                                      <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
                                        Tipografía
                                      </div>
                                      <div className="grid grid-cols-2 gap-3">
                                        {suggested
                                          .slice(0, 2)
                                          .map((font, idx) => (
                                            <div
                                              key={`${font}-${idx}`}
                                              className="flex flex-col items-center justify-center text-center"
                                            >
                                              <div
                                                className="text-[40px] sm:text-[48px] leading-none text-brand-black/90 mb-1"
                                                style={{
                                                  fontFamily: fontStackFor(font),
                                                }}
                                              >
                                                Aa
                                              </div>
                                              <div
                                                className="text-[13px] text-brand-black/80 truncate w-full px-1"
                                                style={{
                                                  fontFamily: fontStackFor(font),
                                                }}
                                              >
                                                {font}
                                              </div>
                                              <div className="text-[10px] text-neutral-500 uppercase tracking-wide mt-1">
                                                {idx === 0
                                                  ? "Principal"
                                                  : "Secundaria"}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                      {fontStyle && (
                                        <p className="text-[11px] text-neutral-500 italic pt-3 mt-3 border-t border-neutral-200/70">
                                          {fontStyle}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}
                                {/* Paleta de colores */}
                                {brandProfile.colors.length > 0 && (
                                  <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
                                    <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
                                      Paleta de colores
                                    </div>
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                      {brandProfile.colors
                                        .slice(0, 6)
                                        .map((color, idx) => (
                                          <div
                                            key={`${color}-${idx}`}
                                            className="flex flex-col items-center gap-1.5"
                                          >
                                            <div
                                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-neutral-200 shadow-sm"
                                              style={{ backgroundColor: color }}
                                              title={color}
                                            />
                                            <div className="text-[9px] sm:text-[10px] text-neutral-500 font-mono">
                                              {color}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </section>
                            )}
                            {/* ── Imágenes (logos + elementos visuales) ─────────── */}
                            {brandTab === "imagenes" && (
                              <section>
                                <h3 className="text-[14px] font-semibold text-brand-black/90">
                                  Logos
                                </h3>
                                <p className="mt-1 mb-3 text-[12px] leading-relaxed text-brand-black/50">
                                  Versiones de tu logo (color, monocromo,
                                  isotipo) que el agente puede usar en cualquier
                                  video. El logo principal se cambia desde
                                  Configuración.
                                </p>
                                {/* Subir un logo lo guarda como brand_asset tipo
                            'logo' — NO toca el logo principal de la marca
                            (logo_url), que solo se edita desde Configuración. */}
                                <input
                                  ref={brandLogoInputRef}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    e.target.value = "";
                                    if (file)
                                      await handleBrandAssetUpload(file, "logo");
                                  }}
                                />
                                {(() => {
                                  const logoItems = brandAssets.filter(
                                    (a) => a.tipo === "logo",
                                  );
                                  const isUploading =
                                    brandAssetsUploading === "logo";
                                  // Checker background so transparent logos read
                                  // as transparent (most logos are PNG/SVG).
                                  const checker: React.CSSProperties = {
                                    backgroundImage:
                                      "linear-gradient(45deg, rgba(0,0,0,0.04) 25%, transparent 25%), linear-gradient(-45deg, rgba(0,0,0,0.04) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.04) 75%), linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.04) 75%)",
                                    backgroundSize: "12px 12px",
                                    backgroundPosition:
                                      "0 0, 0 6px, 6px -6px, -6px 0",
                                  };
                                  const hasNoLogos =
                                    logoItems.length === 0 &&
                                    !brandProfile.logoUrl;
                                  return (
                                    <div className="grid grid-cols-3 gap-2">
                                      {/* Upload tile */}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          brandLogoInputRef.current?.click()
                                        }
                                        disabled={isUploading}
                                        className="aspect-square rounded-md border border-dashed border-black/[0.16] bg-neutral-50 hover:bg-neutral-100 hover:border-black/[0.28] flex flex-col items-center justify-center gap-1 text-neutral-500 transition-colors disabled:opacity-60"
                                      >
                                        {isUploading ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <>
                                            <Plus className="w-4 h-4" />
                                            <span className="text-[11px]">
                                              Subir
                                            </span>
                                          </>
                                        )}
                                      </button>
                                      {/* Logo principal (read-only): viene de
                                  brand_profiles.logo_url; sin botón de borrar
                                  porque solo se cambia desde Configuración. */}
                                      {brandProfile.logoUrl && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            insertLogoOverlay(
                                              brandProfile.logoUrl!,
                                              "Logo",
                                            )
                                          }
                                          className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06] hover:ring-brand-primary/40 transition-shadow"
                                          title="Agregar el logo al video"
                                        >
                                          <img
                                            src={brandProfile.logoUrl}
                                            alt="Logo principal"
                                            className="w-full h-full object-contain p-2 pointer-events-none"
                                            style={checker}
                                          />
                                          <span className="absolute bottom-1 left-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-black/55 text-white">
                                            Principal
                                          </span>
                                          <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[11px] font-medium">
                                            Añadir al video
                                          </span>
                                        </button>
                                      )}
                                      {/* Logos extra subidos (borrables) */}
                                      {brandAssetsLoading && hasNoLogos ? (
                                        <div className="col-span-2 flex items-center justify-center text-neutral-400">
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        </div>
                                      ) : hasNoLogos ? (
                                        <p className="col-span-2 self-center text-[12px] text-neutral-400 px-1">
                                          Subí los logos de tu marca.
                                        </p>
                                      ) : (
                                        logoItems.map((item) => (
                                          <div
                                            key={item.id}
                                            className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06]"
                                            title={item.name || "Logo"}
                                          >
                                            {item.url ? (
                                              <img
                                                src={item.url}
                                                alt={item.name || "Logo"}
                                                className="w-full h-full object-contain p-2 pointer-events-none"
                                                style={checker}
                                              />
                                            ) : (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                              </div>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleBrandAssetDelete(item)
                                              }
                                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                              title="Eliminar"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  );
                                })()}
                              </section>
                            )}
                          </>
                        )}
                      </div>
                    )}


                    {activePanel === "transitions" &&
                      (() => {
                        const targetClip = transitionTargetClipId
                          ? clips.find((c) => c.id === transitionTargetClipId)
                          : null;
                        const currentTransition = targetClip?.transitionIn;
                        // Catalogue. Adding a new transition = add an entry here +
                        // a case in computeIncoming/OutgoingTransitionState.
                        const transitions: Array<{
                          type: TransitionType;
                          label: string;
                        }> = [
                          { type: "crossfade", label: "Cross-fade" },
                          { type: "fade-to-black", label: "Fundido a negro" },
                          { type: "slide", label: "Deslizar derecha" },
                          { type: "slide-left", label: "Deslizar izquierda" },
                          { type: "slide-up", label: "Deslizar arriba" },
                          { type: "slide-down", label: "Deslizar abajo" },
                          { type: "zoom", label: "Zoom" },
                          {
                            type: "stretch-horizontal",
                            label: "Estirar horizontal",
                          },
                          {
                            type: "stretch-vertical",
                            label: "Estirar vertical",
                          },
                          { type: "reveal", label: "Revelación" },
                          { type: "diapositivas", label: "Diapositivas" },
                          { type: "light-leak", label: "Flash" },
                          { type: "plastic", label: "Plastico" },
                          { type: "distorsion", label: "Distorsion" },
                          { type: "vhs", label: "VHS" },
                          { type: "vintage", label: "Vintage" },
                          { type: "glitch", label: "Glitch" },
                        ];
                        // Per-type default durations. Most transitions look
                        // sharp at 0.3s (viral whip range) but some — like
                        // zoom-shake — need more time to play out their staged
                        // phases (blur build → tremor → zoom punch → settle).
                        // Switching types resets to that type's default so the
                        // user lands on a sensible starting point; tweaking the
                        // slider after preserves their value while they stay
                        // on the same type.
                        const defaultDurationFor = (
                          type: TransitionType,
                        ): number => {
                          // `diapositivas` has 3 staged phases — at 0.3s each
                          // phase is too short to register, so default longer.
                          if (type === "diapositivas") return 0.8;
                          // Overlay transitions need the full overlay video to
                          // play through, which is typically ~0.5s for light
                          // leaks. Shorter and the bright peak gets cut off.
                          if (type === "light-leak") return 0.5;
                          if (type === "plastic") return 0.6;
                          if (type === "distorsion") return 0.5;
                          // VHS / Vintage source clips are longer and need the
                          // full play-through to read as the intended effect.
                          if (type === "vhs") return 1.0;
                          if (type === "vintage") return 1.0;
                          return 0.3;
                        };
                        const apply = (type: TransitionType) => {
                          if (!targetClip) return;
                          const sameType = currentTransition?.type === type;
                          const dur = sameType
                            ? currentTransition!.durationSec
                            : defaultDurationFor(type);
                          setClipTransition(targetClip.id, type, dur);
                        };
                        const updateDuration = (newDur: number) => {
                          if (!targetClip || !currentTransition) return;
                          setClipTransition(
                            targetClip.id,
                            currentTransition.type,
                            newDur,
                          );
                        };
                        const remove = () => {
                          if (!targetClip) return;
                          removeClipTransition(targetClip.id);
                        };
                        return (
                          // The transitions panel manages its own padding (parent
                          // `p-3` is suppressed for activePanel==='transitions') so
                          // the footer can sit flush against the panel bottom edge
                          // without sticky/negative-margin tricks. Layout:
                          //   • flex-col filling the panel height
                          //   • scrollable grid in the middle (flex-1, min-h-0)
                          //   • footer pinned at the bottom (shrink-0) — only when
                          //     a transition is active
                          <div className="h-full flex flex-col">
                            <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-3">
                              <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                                Animación
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {transitions.map((tr) => (
                                  <TransitionPreviewTile
                                    key={tr.type}
                                    type={tr.type}
                                    label={tr.label}
                                    aspectRatio={aspectRatio}
                                    isActive={
                                      currentTransition?.type === tr.type
                                    }
                                    onClick={() => apply(tr.type)}
                                    computeIncoming={(t, p) =>
                                      computeIncomingTransitionState(
                                        t as TransitionType,
                                        p,
                                      )
                                    }
                                    computeOutgoing={(t, p) =>
                                      computeOutgoingTransitionState(
                                        t as TransitionType,
                                        p,
                                      )
                                    }
                                    overlayUrl={OVERLAY_TRANSITIONS[tr.type]}
                                  />
                                ))}
                              </div>

                              {!targetClip && (
                                <p className="text-[12.5px] text-brand-black/50 px-1">
                                  Cerrá este panel y tocá el{" "}
                                  <span className="font-medium">+</span> entre
                                  dos clips de video adyacentes para elegir una
                                  transición.
                                </p>
                              )}
                            </div>

                            {currentTransition && (
                              <div className="shrink-0 border-t border-black/[0.06] bg-surface-card p-3 space-y-2">
                                <div className="bg-white ring-1 ring-black/[0.06] rounded-[10px] p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] uppercase tracking-wide font-medium text-brand-black/40">
                                      Duración
                                    </span>
                                    <span className="text-[12.5px] font-semibold text-brand-black/80 tabular-nums">
                                      {currentTransition.durationSec.toFixed(2)}
                                      s
                                    </span>
                                  </div>
                                  <input
                                    type="range"
                                    min={0.15}
                                    max={2}
                                    step={0.05}
                                    value={currentTransition.durationSec}
                                    onChange={(e) =>
                                      updateDuration(parseFloat(e.target.value))
                                    }
                                    className="w-full accent-brand-primary"
                                  />
                                  <div className="flex justify-between text-[10px] text-brand-black/35 mt-1 tabular-nums">
                                    <span>0.15s</span>
                                    <span>2s</span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={remove}
                                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-md text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Quitar transición
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {activePanel === "effects" && (
                      <div className="space-y-3">
                        <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                          Efectos y filtros
                        </p>
                        {[
                          { label: "Fade in/out", desc: "Transición suave" },
                          { label: "Zoom lento", desc: "Ken Burns effect" },
                          {
                            label: "Blanco y negro",
                            desc: "Filtro monocromático",
                          },
                          { label: "Sepia", desc: "Tono cálido vintage" },
                          {
                            label: "Blur de fondo",
                            desc: "Desenfoque gaussiano",
                          },
                          { label: "Cámara lenta", desc: "0.5x velocidad" },
                        ].map((fx) => (
                          <button
                            key={fx.label}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-neutral-100 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-sm bg-violet-50 flex items-center justify-center shrink-0">
                              <Wand2 className="w-4 h-4 text-violet-600" />
                            </div>
                            <div className="text-left">
                              <p className="text-[15px] font-medium text-brand-black/80">
                                {fx.label}
                              </p>
                              <p className="text-[11px] text-neutral-400">
                                {fx.desc}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {activePanel === "animation" &&
                      (() => {
                        // The panel has two modes:
                        //   1. Editor — when the selected clip is an animation, the
                        //      panel renders inputs derived from the template's
                        //      `propsSchema`. Edits flow live back into the clip.
                        //   2. Picker — otherwise, the panel lists every template
                        //      registered in `ANIMATION_TEMPLATES`. Clicking one
                        //      inserts a new clip on the elements track using the
                        //      template's defaults and selects it (which flips the
                        //      panel into edit mode for that clip).
                        const sel = clips.find(
                          (c) =>
                            c.id === selectedClipId && c.type === "animation",
                        );
                        if (sel) {
                          const tpl = getAnimationTemplate(
                            sel.animationTemplate,
                          );
                          if (!tpl) {
                            return (
                              <p className="text-[13px] text-neutral-500 px-1 py-3">
                                Plantilla desconocida (
                                <code>{sel.animationTemplate}</code>).
                                Probablemente fue removida del catálogo.
                              </p>
                            );
                          }
                          // Origen de los valores que muestra el formulario: el
                          // draft de edición. Si por algún motivo todavía no se
                          // inicializó (race entre selección y efecto), caemos a
                          // las props guardadas del clip.
                          const draftProps =
                            animationEditDraft &&
                            animationEditDraft.clipId === sel.id
                              ? animationEditDraft.props
                              : sel.animationProps || {};
                          const currentProps = {
                            ...(tpl.defaultProps as Record<string, unknown>),
                            ...draftProps,
                          };
                          const updateProp = (key: string, value: unknown) => {
                            setAnimationEditDraft((d) =>
                              d && d.clipId === sel.id
                                ? { ...d, props: { ...d.props, [key]: value } }
                                : d,
                            );
                          };
                          // ¿Hay cambios sin guardar? Comparamos las claves del draft
                          // con las del clip persistido. Si algún valor difiere, el
                          // botón de guardar se habilita.
                          const savedProps = sel.animationProps || {};
                          const allKeys = new Set<string>([
                            ...Object.keys(savedProps),
                            ...Object.keys(draftProps),
                          ]);
                          let isDirty = false;
                          for (const k of allKeys) {
                            if (savedProps[k] !== draftProps[k]) {
                              isDirty = true;
                              break;
                            }
                          }
                          const onSave = () => {
                            setClips((prev) =>
                              prev.map((c) =>
                                c.id === sel.id
                                  ? { ...c, animationProps: { ...draftProps } }
                                  : c,
                              ),
                            );
                          };
                          const onCancel = () => {
                            // Descartamos el draft volviendo a copiar las props
                            // guardadas del clip y salimos del modo edición.
                            setAnimationEditDraft(null);
                            setAnimationPreviewLoopClipId(null);
                            setIsPlaying(false);
                            setSelectedClipId(null);
                          };
                          const isLooping =
                            animationPreviewLoopClipId === sel.id && isPlaying;
                          const onTogglePreview = () => {
                            if (isLooping) {
                              setIsPlaying(false);
                              setAnimationPreviewLoopClipId(null);
                            } else {
                              setCurrentTime(sel.startTime);
                              setAnimationPreviewLoopClipId(sel.id);
                              setIsPlaying(true);
                            }
                          };
                          const schema = tpl.propsSchema || {};
                          // Collect every prop key referenced by a text spec's
                          // `styling` table. Those props live inside the per-text
                          // style popover and shouldn't render as separate top-level
                          // rows in the main form.
                          const linkedKeys = new Set<string>();
                          for (const s of Object.values(schema)) {
                            if (s?.type === "text" && s.styling) {
                              for (const k of Object.values(s.styling)) {
                                if (typeof k === "string") linkedKeys.add(k);
                              }
                            }
                          }
                          const allEntries = Object.entries(schema) as Array<
                            [string, AnimationPropSpec]
                          >;
                          // El "Fondo" siempre es lo último que el usuario configura
                          // (es ambiente, no contenido). Detectamos cualquier prop
                          // de tipo color cuya key empiece con `background` y la
                          // movemos al final, preservando el orden relativo del
                          // resto. Sort estable nativo en JS engines modernos.
                          const isBackgroundProp = (
                            key: string,
                            spec: AnimationPropSpec,
                          ) =>
                            spec.type === "color" && /^background/i.test(key);
                          const entries = allEntries
                            .filter(([k]) => !linkedKeys.has(k))
                            .sort(([ka, sa], [kb, sb]) => {
                              const a = isBackgroundProp(ka, sa) ? 1 : 0;
                              const b = isBackgroundProp(kb, sb) ? 1 : 0;
                              return a - b;
                            });
                          return (
                            // `data-clip-ui` keeps the global pointerdown handler
                            // from deselecting the clip when the user interacts with
                            // any input inside this panel. Without it, every click
                            // on a color picker / text field would clear
                            // `selectedClipId` and the panel would snap back to the
                            // catalog mid-edit.
                            <div className="space-y-4" data-clip-ui="true">
                              <div className="px-1">
                                <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400">
                                  Plantilla
                                </p>
                                <p className="text-[14px] font-medium text-brand-black/85 mt-0.5">
                                  {tpl.label}
                                </p>
                                {tpl.description && (
                                  <p className="text-[12px] text-neutral-500 mt-0.5">
                                    {tpl.description}
                                  </p>
                                )}
                              </div>

                              {/* Botón único de previsualización: arranca el playhead
                          al inicio del clip y lo deja en loop hasta que se
                          presione pausa. Reutiliza el player principal —
                          no agregamos un reproductor aparte. */}
                              <button
                                type="button"
                                onClick={onTogglePreview}
                                className={`w-full h-10 inline-flex items-center justify-center gap-2 rounded-md text-[13px] font-medium transition-colors ${
                                  isLooping
                                    ? "bg-brand-primary text-white hover:bg-brand-primary/90"
                                    : "border border-black/[0.08] bg-white text-brand-black/80 hover:bg-neutral-50"
                                }`}
                                title={
                                  isLooping
                                    ? "Detener la previsualización"
                                    : "Reproducir la animación en bucle"
                                }
                              >
                                {isLooping ? (
                                  <>
                                    <Pause className="w-4 h-4" /> Detener
                                    previsualización
                                  </>
                                ) : (
                                  <>
                                    <Play className="w-4 h-4 ml-0.5" />{" "}
                                    Reproducir en bucle
                                  </>
                                )}
                              </button>

                              {/* ── Tamaño y posición ─────────────────────────
                          La posición se cambia arrastrando la animación
                          en el preview (igual que el texto). Acá ofrecemos
                          un slider de tamaño y un atajo para volver al
                          centro. Estos campos viven en el clip (no son
                          props del template) así que escribimos directo
                          sobre `clips` sin pasar por el draft. */}
                              {(() => {
                                const currentScale = sel.animScale ?? 1;
                                const setClipFields = (
                                  patch: Partial<TimelineClip>,
                                ) => {
                                  setClips((prev) =>
                                    prev.map((c) =>
                                      c.id === sel.id ? { ...c, ...patch } : c,
                                    ),
                                  );
                                };
                                const onScaleChange = (next: number) => {
                                  // No re-clampeamos al cambiar la escala — el
                                  // centro puede vivir libremente en [0,100],
                                  // que el usuario decida si quiere algo
                                  // parcialmente fuera de pantalla.
                                  setClipFields({ animScale: next });
                                };
                                return (
                                  <div className="space-y-2 px-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[12px] font-medium text-brand-black/70">
                                        Tamaño
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setClipFields({
                                            animXPct: 50,
                                            animYPct: 50,
                                          })
                                        }
                                        className="text-[11.5px] text-brand-primary hover:underline"
                                        title="Volver al centro de la canvas"
                                      >
                                        Centrar
                                      </button>
                                    </div>
                                    {/* Pill − [N%] + análoga al control de font-size
                                del texto. Trabajamos en porcentaje (5..100)
                                para que el usuario lea un número redondo,
                                y traducimos a la escala interna 0.05..1
                                al commitear. Los handles del SelectionFrame
                                en el preview también escriben sobre este
                                campo, así que el input se mantiene en sync
                                en vivo mientras se arrastra. */}
                                    {(() => {
                                      const pct = Math.round(
                                        currentScale * 100,
                                      );
                                      const stepScale = (delta: number) => {
                                        const nextPct = Math.max(
                                          5,
                                          Math.min(300, pct + delta),
                                        );
                                        onScaleChange(nextPct / 100);
                                      };
                                      return (
                                        <div className="flex items-center gap-2">
                                          <div className="h-8 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                            <button
                                              type="button"
                                              onClick={() => stepScale(-5)}
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Reducir tamaño"
                                            >
                                              −
                                            </button>
                                            <NumberField
                                              value={pct}
                                              min={5}
                                              max={300}
                                              step={1}
                                              decimals={0}
                                              onCommit={(n) =>
                                                onScaleChange(
                                                  Math.max(
                                                    0.05,
                                                    Math.min(3, n / 100),
                                                  ),
                                                )
                                              }
                                              className="w-12 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                              ariaLabel="Tamaño en porcentaje"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => stepScale(5)}
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Aumentar tamaño"
                                            >
                                              +
                                            </button>
                                          </div>
                                          <span className="text-[11px] text-neutral-400">
                                            %
                                          </span>
                                        </div>
                                      );
                                    })()}
                                    {/* Animaciones — entrada / salida lado a lado,
                                con MotionPicker de preview-en-hover. Scope
                                'common' filtra los presets text-only. */}
                                    <div className="pt-3 mt-1 border-t border-neutral-200/70 space-y-2">
                                      <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400">
                                        Animaciones
                                      </p>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1.5">
                                          <MotionPicker
                                            label="Entrada"
                                            kind="enter"
                                            scope="common"
                                            value={sel.enterAnimation ?? "none"}
                                            onChange={(id) =>
                                              setClipFields({
                                                enterAnimation: id,
                                              })
                                            }
                                          />
                                          {(sel.enterAnimation ?? "none") !==
                                            "none" && (
                                            <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                              <NumberField
                                                value={
                                                  sel.enterDurationSec ?? 0.5
                                                }
                                                min={0.1}
                                                max={5}
                                                step={0.1}
                                                decimals={1}
                                                onCommit={(n) =>
                                                  setClipFields({
                                                    enterDurationSec: n,
                                                  })
                                                }
                                                className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                ariaLabel="Duración entrada"
                                              />
                                              <span className="text-[11px] text-neutral-400">
                                                seg
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="space-y-1.5">
                                          <MotionPicker
                                            label="Salida"
                                            kind="exit"
                                            scope="common"
                                            value={sel.exitAnimation ?? "none"}
                                            onChange={(id) =>
                                              setClipFields({
                                                exitAnimation: id,
                                              })
                                            }
                                          />
                                          {(sel.exitAnimation ?? "none") !==
                                            "none" && (
                                            <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                              <NumberField
                                                value={
                                                  sel.exitDurationSec ?? 0.5
                                                }
                                                min={0.1}
                                                max={5}
                                                step={0.1}
                                                decimals={1}
                                                onCommit={(n) =>
                                                  setClipFields({
                                                    exitDurationSec: n,
                                                  })
                                                }
                                                className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                ariaLabel="Duración salida"
                                              />
                                              <span className="text-[11px] text-neutral-400">
                                                seg
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {entries.length === 0 ? (
                                <p className="text-[13px] text-neutral-500 px-1">
                                  Esta plantilla no tiene parámetros editables.
                                </p>
                              ) : (
                                <div className="space-y-3">
                                  {entries.map(([key, spec]) => {
                                    const value = (currentProps[key] ??
                                      "") as string;
                                    if (spec.type === "text") {
                                      const inputCls =
                                        "w-full px-3 py-2 text-[14px] rounded-md border border-black/[0.08] bg-white focus:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/15 transition-colors";
                                      const labelEl = (
                                        <span className="text-[12px] font-medium text-brand-black/70 block mb-1">
                                          {spec.label}
                                        </span>
                                      );
                                      const inputEl = spec.multiline ? (
                                        <textarea
                                          value={value}
                                          onChange={(e) =>
                                            updateProp(key, e.target.value)
                                          }
                                          placeholder={spec.placeholder}
                                          rows={3}
                                          className={`${inputCls} resize-none`}
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          value={value}
                                          onChange={(e) =>
                                            updateProp(key, e.target.value)
                                          }
                                          placeholder={spec.placeholder}
                                          className={inputCls}
                                        />
                                      );
                                      // If this text has `styling`, sit the input
                                      // next to a small "Aa" trigger that opens the
                                      // dedicated style popover for this copy.
                                      if (spec.styling) {
                                        return (
                                          <div key={key}>
                                            {labelEl}
                                            <div className="flex items-start gap-2">
                                              <div className="flex-1 min-w-0">
                                                {inputEl}
                                              </div>
                                              <TextStyleButton
                                                styling={spec.styling}
                                                schema={
                                                  schema as Record<
                                                    string,
                                                    | AnimationPropSpec
                                                    | undefined
                                                  >
                                                }
                                                values={currentProps}
                                                onChange={(k, v) =>
                                                  updateProp(k, v)
                                                }
                                                title={`Estilo de ${spec.label.toLowerCase()}`}
                                              />
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <label key={key} className="block">
                                          {labelEl}
                                          {inputEl}
                                        </label>
                                      );
                                    }
                                    if (spec.type === "boolean") {
                                      const on = !!currentProps[key];
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => updateProp(key, !on)}
                                            aria-pressed={on}
                                            className={`h-7 w-12 inline-flex items-center rounded-full transition-colors ${
                                              on
                                                ? "bg-brand-primary justify-end pr-0.5"
                                                : "bg-neutral-200 justify-start pl-0.5"
                                            }`}
                                          >
                                            <span className="w-6 h-6 rounded-full bg-white shadow-sm" />
                                          </button>
                                        </div>
                                      );
                                    }
                                    if (spec.type === "select") {
                                      // Up to 4 options → segmented control for fast
                                      // scanning. More than that → native <select>
                                      // styled to match the rest of the editor.
                                      const useSegmented =
                                        spec.options.length <= 4;
                                      if (useSegmented) {
                                        return (
                                          <div
                                            key={key}
                                            className="flex items-center justify-between gap-3"
                                          >
                                            <span className="text-[12px] font-medium text-brand-black/70">
                                              {spec.label}
                                            </span>
                                            <div className="inline-flex items-center bg-neutral-100 rounded-md p-0.5">
                                              {spec.options.map((opt) => {
                                                const active =
                                                  opt.value === value;
                                                return (
                                                  <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() =>
                                                      updateProp(key, opt.value)
                                                    }
                                                    className={`h-7 px-2.5 text-[12px] font-medium rounded transition-colors ${
                                                      active
                                                        ? "bg-white text-brand-black shadow-sm"
                                                        : "text-neutral-500 hover:text-neutral-700"
                                                    }`}
                                                  >
                                                    {opt.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <select
                                            value={value}
                                            onChange={(e) =>
                                              updateProp(key, e.target.value)
                                            }
                                            className="h-8 pl-2.5 pr-7 text-[13px] rounded-md border border-black/[0.08] bg-white text-brand-black/85 hover:border-black/[0.16] focus:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/15 transition-colors appearance-none bg-no-repeat bg-right cursor-pointer"
                                            style={{
                                              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
                                              backgroundPosition:
                                                "right 8px center",
                                            }}
                                          >
                                            {spec.options.map((opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                              >
                                                {opt.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      );
                                    }
                                    if (spec.type === "image") {
                                      return (
                                        <div key={key}>
                                          <span className="text-[12px] font-medium text-brand-black/70 block mb-1">
                                            {spec.label}
                                          </span>
                                          <ImagePicker
                                            value={value}
                                            onChange={(url) =>
                                              updateProp(key, url)
                                            }
                                            projectId={projectId}
                                            companyId={companyId}
                                          />
                                        </div>
                                      );
                                    }
                                    if (spec.type === "font") {
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <FontPicker
                                            value={value || "Inter"}
                                            onChange={(fam) =>
                                              updateProp(key, fam)
                                            }
                                            triggerClassName="h-8 w-[160px] pl-2.5 pr-2 flex items-center justify-between gap-1 rounded-md border border-black/[0.08] bg-white text-[13px] text-brand-black/85 hover:border-black/[0.16] transition-colors"
                                            panelAlign="right"
                                            panelMinWidth={220}
                                          />
                                        </div>
                                      );
                                    }
                                    if (spec.type === "number") {
                                      const cur = Number(
                                        currentProps[key] ?? 0,
                                      );
                                      const step = spec.step ?? 1;
                                      const min = spec.min ?? 0;
                                      const max = spec.max ?? 9999;
                                      const dec = spec.decimals ?? 0;
                                      const clamp = (n: number) =>
                                        Math.min(max, Math.max(min, n));
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <div className="h-8 flex items-center rounded-md border border-black/[0.08] bg-white overflow-hidden">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateProp(
                                                  key,
                                                  clamp(cur - step),
                                                )
                                              }
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Reducir"
                                            >
                                              −
                                            </button>
                                            <NumberField
                                              value={cur}
                                              min={min}
                                              max={max}
                                              step={step}
                                              decimals={dec}
                                              onCommit={(n) =>
                                                updateProp(key, n)
                                              }
                                              className="w-12 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                              ariaLabel={spec.label}
                                            />
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateProp(
                                                  key,
                                                  clamp(cur + step),
                                                )
                                              }
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Aumentar"
                                            >
                                              +
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    }
                                    // color
                                    return (
                                      <div
                                        key={key}
                                        className="flex items-center justify-between gap-3"
                                      >
                                        <span className="text-[12px] font-medium text-brand-black/70">
                                          {spec.label}
                                        </span>
                                        <ColorSwatchPicker
                                          value={value}
                                          onChange={(c) => updateProp(key, c)}
                                          ariaLabel={spec.label}
                                          allowTransparent={
                                            spec.allowTransparent
                                          }
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Restaurar valores: sobreescribe el draft con los
                          valores por defecto de la plantilla. No persiste
                          nada hasta que se aprete "Guardar cambios". */}
                              <button
                                type="button"
                                onClick={() => {
                                  setAnimationEditDraft((d) =>
                                    d && d.clipId === sel.id
                                      ? {
                                          ...d,
                                          props: {
                                            ...(tpl.defaultProps as Record<
                                              string,
                                              unknown
                                            >),
                                          },
                                        }
                                      : d,
                                  );
                                }}
                                className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium text-brand-black/65 hover:bg-neutral-100 hover:text-brand-black/85 transition-colors"
                                title="Volver a los valores por defecto de esta plantilla"
                              >
                                <RotateCcw className="w-3.5 h-3.5 text-neutral-500" />
                                Restaurar valores por defecto
                              </button>

                              {/* Acciones de cierre. Cancelar descarta el draft y
                          vuelve al catálogo. Guardar cambios persiste el
                          draft sobre el clip — solo está habilitado cuando
                          hay diferencias respecto a lo guardado. */}
                              <div className="pt-3 border-t border-black/[0.06] flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={onCancel}
                                  className="flex-1 h-9 inline-flex items-center justify-center rounded-md border border-black/[0.08] bg-white text-[13px] font-medium text-brand-black/75 hover:bg-neutral-50 hover:text-brand-black/90 transition-colors"
                                  title="Descartar los cambios y volver al catálogo"
                                >
                                  Cancelar
                                </button>
                                <button
                                  type="button"
                                  onClick={onSave}
                                  disabled={!isDirty}
                                  className={`flex-1 h-9 inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors ${
                                    isDirty
                                      ? "bg-brand-primary text-white hover:bg-brand-primary/90"
                                      : "bg-neutral-200/70 text-neutral-400 cursor-not-allowed"
                                  }`}
                                  title={
                                    isDirty
                                      ? "Aplicar los cambios al clip"
                                      : "No hay cambios para guardar"
                                  }
                                >
                                  <Check className="w-3.5 h-3.5" />
                                  Guardar cambios
                                </button>
                              </div>
                            </div>
                          );
                        }

                        // Picker en dos niveles:
                        //   · Nivel 1 (`elementsCategory === null`): cards grandes
                        //     de categoría (Texto / Imagen / Animación).
                        //   · Nivel 2: contenido específico de la categoría.
                        //     Para 'text' e 'image' es la grilla de templates con
                        //     preview animada; para 'lottie' es el buscador.
                        const templates = Object.values(ANIMATION_TEMPLATES);
                        const insertTemplate = (
                          tpl: (typeof templates)[number],
                          propOverrides?: Record<string, unknown>,
                          name?: string,
                          durationOverrideSec?: number,
                        ) => {
                          const elementsTrack = TRACK_CONFIG.find(
                            (t) => t.type === "elements",
                          )!;
                          const clipId = generateId();
                          // Si el caller pasa una duración explícita (ej. la
                          // duración natural de un Lottie), la respetamos. Sin
                          // eso usábamos siempre los 4s default y el clip cortaba
                          // o estiraba la animación.
                          const duration =
                            durationOverrideSec && durationOverrideSec > 0
                              ? durationOverrideSec
                              : tpl.defaultDurationSec;
                          const newClip: TimelineClip = {
                            id: clipId,
                            type: "animation",
                            name: name ?? tpl.label,
                            startTime: getNextStartTime(clips, elementsTrack),
                            duration,
                            color: elementsTrack.color,
                            animationTemplate: tpl.id,
                            animationProps: {
                              ...(tpl.defaultProps as Record<string, unknown>),
                              ...(propOverrides || {}),
                            },
                            // Defaults de transform: centro de la canvas y escala
                            // 0.6 (≈ 60% del canvas) para que se vea claramente
                            // como un "elemento" movible. Los clips viejos sin
                            // estos campos siguen renderizándose full-screen.
                            animXPct: 50,
                            animYPct: 50,
                            animScale: 0.6,
                          };
                          setClips((prev) => [...prev, newClip]);
                          setSelectedClipId(clipId);
                        };

                        // ── Nivel 1: cards de categoría ──────────────────────
                        if (elementsCategory === null) {
                          const categories: Array<{
                            key: "text" | "image" | "lottie";
                            label: string;
                            description: string;
                            Icon: React.ComponentType<{ className?: string }>;
                            bg: string;
                            iconBg: string;
                            iconColor: string;
                          }> = [
                            {
                              key: "text",
                              label: "Texto",
                              description:
                                "Títulos, números, sellos y más con texto editable.",
                              Icon: Type,
                              bg: "bg-gradient-to-br from-violet-50 to-violet-100/40",
                              iconBg: "bg-violet-100",
                              iconColor: "text-violet-600",
                            },
                            {
                              key: "image",
                              label: "Imagen",
                              description:
                                "Animaciones que muestran una imagen del proyecto.",
                              Icon: ImageIcon,
                              bg: "bg-gradient-to-br from-emerald-50 to-emerald-100/40",
                              iconBg: "bg-emerald-100",
                              iconColor: "text-emerald-600",
                            },
                            {
                              key: "lottie",
                              label: "Animación",
                              description:
                                "Animaciones vectoriales de la biblioteca Lottie.",
                              Icon: Sparkles,
                              bg: "bg-gradient-to-br from-amber-50 to-amber-100/40",
                              iconBg: "bg-amber-100",
                              iconColor: "text-amber-600",
                            },
                          ];
                          return (
                            <div className="space-y-2.5">
                              {categories.map((cat) => (
                                <button
                                  key={cat.key}
                                  type="button"
                                  onClick={() => setElementsCategory(cat.key)}
                                  className={`group w-full text-left rounded-md border border-black/[0.06] shadow-sm hover:border-brand-primary/30 hover:shadow-md hover:-translate-y-px transition-all overflow-hidden ${cat.bg}`}
                                >
                                  <div className="flex items-center gap-3 px-3 py-4">
                                    <div
                                      className={`w-11 h-11 rounded-md ${cat.iconBg} flex items-center justify-center shrink-0`}
                                    >
                                      <cat.Icon
                                        className={`w-5 h-5 ${cat.iconColor}`}
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[15px] font-semibold text-brand-black/90 leading-tight">
                                        {cat.label}
                                      </p>
                                      <p className="text-[12px] text-brand-black/55 mt-0.5 leading-snug">
                                        {cat.description}
                                      </p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          );
                        }

                        // Header común para los niveles 2 con un botón de volver.
                        const backButton = (
                          <button
                            type="button"
                            onClick={() => setElementsCategory(null)}
                            className="inline-flex items-center gap-1 px-2 py-1 -ml-1 rounded-md text-[13px] font-medium text-brand-black/65 hover:bg-neutral-100 hover:text-brand-black/85 transition-colors"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            Volver
                          </button>
                        );

                        // ── Nivel 2: Lottie picker ───────────────────────────
                        if (elementsCategory === "lottie") {
                          const lottieTpl = templates.find(
                            (t) => t.id === "lottie-asset-v1",
                          );
                          return (
                            <div className="space-y-3">
                              {backButton}
                              {lottieTpl ? (
                                <LottiePicker
                                  onPick={(item) =>
                                    insertTemplate(
                                      lottieTpl,
                                      { lottieUrl: item.url },
                                      item.title,
                                      item.durationSec,
                                    )
                                  }
                                />
                              ) : (
                                <p className="text-[13px] text-neutral-500 px-1">
                                  La plantilla de Lottie no está registrada.
                                </p>
                              )}
                            </div>
                          );
                        }

                        // ── Nivel 2: grilla de templates (Texto / Imagen) ─────
                        const items = templates.filter(
                          (t) =>
                            // Excluimos la plantilla de Lottie de las grillas
                            // genéricas — se inserta solo desde el buscador.
                            t.id !== "lottie-asset-v1" &&
                            (t.category || "text") === elementsCategory,
                        );
                        return (
                          <div className="space-y-3">
                            {backButton}
                            {/* Atajo a "texto simple" — la grilla de abajo solo
                        ofrece plantillas animadas de texto, pero a veces
                        el usuario solo quiere un texto plano (sin diseño)
                        sobre el video. Este botón lo manda directo al
                        panel `text` que ya implementa todo el editor de
                        un text-clip básico. */}
                            {elementsCategory === "text" && (
                              <button
                                type="button"
                                onClick={() => setActivePanel("text")}
                                className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-md border border-black/[0.08] bg-white text-[13px] font-medium text-brand-black/80 hover:bg-neutral-50 hover:border-black/[0.16] transition-colors shadow-sm"
                                title="Crear un texto plano sin plantilla animada"
                              >
                                <Plus className="w-4 h-4" />
                                Añadir texto simple
                              </button>
                            )}
                            {items.length === 0 ? (
                              <p className="text-[13px] text-neutral-500 px-1">
                                Todavía no hay plantillas en esta categoría.
                              </p>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {items.map((tpl) => (
                                  <AnimationPreviewTile
                                    key={tpl.id}
                                    template={tpl}
                                    aspectRatio={aspectRatio}
                                    onClick={() => insertTemplate(tpl)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {activePanel === "ai-scene" && (
                      <div className="flex-1 h-full flex flex-col min-w-0">
                        {/* Hidden inputs for uploads */}
                        <input
                          ref={sceneRefImageRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleRefImageUpload}
                        />
                        <input
                          ref={sceneFirstFrameRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFrameUpload(e, "first")}
                        />
                        <input
                          ref={sceneLastFrameRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFrameUpload(e, "last")}
                        />

                        {/* Header */}
                        <div className="flex items-center justify-between px-3 h-12">
                          <h3 className="text-[15px] font-semibold text-brand-black/90">
                            Nueva escena con IA
                          </h3>
                          <button
                            onClick={() => setActivePanel("none")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                          >
                            <Plus className="w-4 h-4 rotate-45" />
                          </button>
                        </div>
                        <div className="h-px bg-black/[0.08]" />

                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                          {/* Scene mode selector (dropdown) */}
                          <div>
                            <div
                              ref={modeDropdownRef}
                              className="relative"
                              data-tour="scene-mode"
                            >
                              <button
                                type="button"
                                onClick={() => setIsModeDropdownOpen((v) => !v)}
                                className="w-full flex items-center justify-between h-10 px-3 bg-white border border-neutral-200 rounded-[10px] text-[14px] font-medium text-brand-black/90 hover:bg-neutral-50 transition-colors"
                              >
                                <span>
                                  {sceneMode === "general"
                                    ? "Modo General"
                                    : "Modo Diálogo"}
                                </span>
                                <ChevronDown
                                  className={`w-4 h-4 text-brand-black/40 transition-transform ${isModeDropdownOpen ? "rotate-180" : ""}`}
                                />
                              </button>
                              {isModeDropdownOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setIsModeDropdownOpen(false)}
                                  />
                                  <div className="absolute left-0 right-0 top-full mt-1.5 z-20 bg-white border border-neutral-200 rounded-[12px] shadow-lg p-1.5 space-y-1">
                                    {[
                                      {
                                        id: "general" as const,
                                        label: "Modo General",
                                        desc: "Para cualquier escena creativa sin personas hablando.",
                                        video: "/images/modo_general.mp4",
                                      },
                                      {
                                        id: "personas" as const,
                                        label: "Modo Diálogo",
                                        desc: "Optimizado para personas hablando con voz sincronizada.",
                                        video: "/images/modo_dialogo.mp4",
                                      },
                                    ].map((opt) => (
                                      <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => {
                                          setSceneMode(opt.id);
                                          setIsModeDropdownOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-3 p-2 rounded-[10px] text-left transition-colors ${
                                          sceneMode === opt.id
                                            ? "bg-brand-primary/[0.06]"
                                            : "hover:bg-neutral-50"
                                        }`}
                                      >
                                        <video
                                          src={opt.video}
                                          autoPlay
                                          loop
                                          muted
                                          playsInline
                                          className="w-24 h-24 object-cover rounded-[10px] shrink-0 bg-neutral-100"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[13px] font-semibold text-brand-black/90">
                                            {opt.label}
                                          </p>
                                          <p className="text-[12px] text-brand-black/50 leading-snug mt-0.5">
                                            {opt.desc}
                                          </p>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Personas-only: single first-frame upload */}
                          {sceneMode === "personas" &&
                            (() => {
                              const firstPreview = sceneFirstFrameAssetId
                                ? assetPreviewUrls[sceneFirstFrameAssetId]
                                : null;
                              return (
                                <div data-tour="dialog-frame">
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                    Frame inicial
                                  </p>
                                  <div
                                    className={`rounded-[10px] border-2 ${sceneFirstFrameAssetId ? "border-solid border-neutral-200" : "border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03]"} transition-colors flex items-center justify-center relative${panelBounceClass("scene-firstframe")}${panelNudgeClass("scene-firstframe")}`}
                                    style={{ height: 110, cursor: "pointer" }}
                                    onClick={() =>
                                      !sceneFirstFrameAssetId &&
                                      setImageSlotMenu({ kind: "first" })
                                    }
                                  >
                                    {sceneFirstFrameAssetId ? (
                                      <>
                                        {firstPreview ? (
                                          <img
                                            src={firstPreview}
                                            alt="Frame inicial"
                                            className="object-contain rounded-[8px]"
                                            style={{
                                              maxWidth: "calc(100% - 16px)",
                                              maxHeight: "calc(100% - 16px)",
                                            }}
                                          />
                                        ) : (
                                          <span className="text-[11px] text-neutral-400">
                                            ...
                                          </span>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSceneFirstFrameAssetId(null);
                                          }}
                                          className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                          style={{ cursor: "pointer" }}
                                        >
                                          <Plus className="w-3 h-3 text-white rotate-45" />
                                        </button>
                                      </>
                                    ) : (
                                      <div className="flex flex-col items-center gap-1.5">
                                        <Plus className="w-5 h-5 text-neutral-300" />
                                        <span className="text-[13px] text-neutral-400 font-medium">
                                          Subí el frame inicial
                                        </span>
                                      </div>
                                    )}
                                    {imageSlotMenu?.kind === "first" && (
                                      <>
                                        <div
                                          className="fixed inset-0 z-30"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setImageSlotMenu(null);
                                          }}
                                        />
                                        <div
                                          onClick={(e) => e.stopPropagation()}
                                          className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-40 bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[180px]"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setImageSlotMenu(null);
                                              sceneFirstFrameRef.current?.click();
                                            }}
                                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                          >
                                            <Upload className="w-3.5 h-3.5 text-brand-black/50" />
                                            Subir imagen
                                          </button>
                                          <div className="h-px bg-black/[0.06]" />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              openImageGenPanel({
                                                kind: "first",
                                              })
                                            }
                                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                          >
                                            <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                                            Generar con IA
                                          </button>
                                          <div className="h-px bg-black/[0.06]" />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setImageSlotMenu(null);
                                              openLibraryPicker("first");
                                            }}
                                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                          >
                                            <FolderOpen className="w-3.5 h-3.5 text-brand-black/50" />
                                            Usar desde biblioteca
                                          </button>
                                          <div className="h-px bg-black/[0.06]" />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setImageSlotMenu(null);
                                              setModelPickKind("first");
                                              setModelPickerOpen(true);
                                              setActivePanel("files");
                                            }}
                                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                          >
                                            <Users className="w-3.5 h-3.5 text-brand-primary" />
                                            Usar personaje
                                          </button>
                                          {clips.some(
                                            (c) =>
                                              c.type === "video" ||
                                              (c.type === "ai-generated" &&
                                                c.aiStatus === "ready"),
                                          ) && (
                                            <>
                                              <div className="h-px bg-black/[0.06]" />
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  startFrameFromVideoPicker({
                                                    kind: "first",
                                                  })
                                                }
                                                className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                              >
                                                <Film className="w-3.5 h-3.5 text-brand-black/50" />
                                                Seleccionar de video
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                          {/* Reference images toggle (General mode only) */}
                          {sceneMode === "general" && (
                            <div data-tour="scene-reference">
                              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                Imágenes de referencia
                              </p>
                              {/* iOS Segmented Control */}
                              <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
                                <div
                                  className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                                  style={{
                                    width: "calc(33.333% - 2px)",
                                    left:
                                      sceneRefMode === "none"
                                        ? "3px"
                                        : sceneRefMode === "reference"
                                          ? "calc(33.333% + 1px)"
                                          : "calc(66.666% - 1px)",
                                  }}
                                />
                                {(["none", "reference", "frames"] as const).map(
                                  (mode) => (
                                    <button
                                      key={mode}
                                      type="button"
                                      onClick={() => setSceneRefMode(mode)}
                                      className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                                        sceneRefMode === mode
                                          ? "text-brand-black/90"
                                          : "text-brand-black/50"
                                      }`}
                                    >
                                      {mode === "none"
                                        ? "Ninguna"
                                        : mode === "reference"
                                          ? "Referencia"
                                          : "Frames"}
                                    </button>
                                  ),
                                )}
                              </div>

                              {/* Description */}
                              {sceneRefMode === "reference" && (
                                <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                                  Subí una imagen como referencia visual para la
                                  escena.
                                </p>
                              )}
                              {sceneRefMode === "frames" && (
                                <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                                  Definí el frame de inicio y fin para guiar la
                                  transición.
                                </p>
                              )}

                              {/* Reference images – horizontal list */}
                              {sceneRefMode === "reference" && (
                                <div
                                  className={`mt-3 flex gap-2 overflow-x-auto pb-1${panelBounceClass("scene-refs")}`}
                                  style={{ scrollbarWidth: "thin" }}
                                >
                                  {sceneRefAssetIds.map((assetId, idx) => {
                                    const previewUrl =
                                      assetPreviewUrls[assetId];
                                    return (
                                      <div
                                        key={assetId}
                                        className="relative shrink-0 rounded-[10px] overflow-hidden bg-neutral-100"
                                        style={{ height: 110 }}
                                      >
                                        {previewUrl ? (
                                          <img
                                            src={previewUrl}
                                            alt={`Ref ${idx + 1}`}
                                            className="h-full w-auto object-contain rounded-[10px]"
                                          />
                                        ) : (
                                          <div className="h-full w-[110px] flex items-center justify-center">
                                            <span className="text-[11px] text-neutral-400">
                                              ...
                                            </span>
                                          </div>
                                        )}
                                        <button
                                          onClick={() =>
                                            setSceneRefAssetIds((prev) =>
                                              prev.filter(
                                                (id) => id !== assetId,
                                              ),
                                            )
                                          }
                                          className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                          style={{ cursor: "pointer" }}
                                        >
                                          <Plus className="w-3 h-3 text-white rotate-45" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                  <div className="relative flex-1 min-w-[80px]">
                                    <button
                                      onClick={(e) => {
                                        const rect = (
                                          e.currentTarget as HTMLElement
                                        ).getBoundingClientRect();
                                        setImageSlotMenuRect({
                                          top: rect.bottom + 6,
                                          left:
                                            rect.left + rect.width / 2 - 100,
                                          width: 200,
                                        });
                                        setImageSlotMenu({ kind: "ref" });
                                      }}
                                      className={`w-full rounded-[10px] border-2 border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1${panelNudgeClass("scene-ref")}`}
                                      style={{ height: 110, cursor: "pointer" }}
                                    >
                                      <Plus className="w-4 h-4 text-neutral-300" />
                                      <span className="text-[11px] text-neutral-400 font-medium">
                                        {sceneRefAssetIds.length === 0
                                          ? "Añadir imagen"
                                          : "Añadir otra"}
                                      </span>
                                    </button>
                                    {imageSlotMenu?.kind === "ref" &&
                                      imageSlotMenuRect && (
                                        <>
                                          <div
                                            className="fixed inset-0 z-[60]"
                                            onClick={() => {
                                              setImageSlotMenu(null);
                                              setImageSlotMenuRect(null);
                                            }}
                                          />
                                          <div
                                            className="fixed z-[61] bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden"
                                            style={{
                                              top: imageSlotMenuRect.top,
                                              left: imageSlotMenuRect.left,
                                              width: imageSlotMenuRect.width,
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setImageSlotMenu(null);
                                                setImageSlotMenuRect(null);
                                                sceneRefImageRef.current?.click();
                                              }}
                                              className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                            >
                                              <Upload className="w-3.5 h-3.5 text-brand-black/50" />
                                              Subir imagen
                                            </button>
                                            <div className="h-px bg-black/[0.06]" />
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setImageSlotMenuRect(null);
                                                openImageGenPanel({
                                                  kind: "ref",
                                                });
                                              }}
                                              className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                            >
                                              <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                                              Generar con IA
                                            </button>
                                            <div className="h-px bg-black/[0.06]" />
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setImageSlotMenu(null);
                                                setImageSlotMenuRect(null);
                                                openLibraryPicker("ref");
                                              }}
                                              className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                            >
                                              <FolderOpen className="w-3.5 h-3.5 text-brand-black/50" />
                                              Usar desde biblioteca
                                            </button>
                                            <div className="h-px bg-black/[0.06]" />
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setImageSlotMenu(null);
                                                setImageSlotMenuRect(null);
                                                setModelPickKind("ref");
                                                setModelPickerOpen(true);
                                                setActivePanel("files");
                                              }}
                                              className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                            >
                                              <Users className="w-3.5 h-3.5 text-brand-primary" />
                                              Usar personaje
                                            </button>
                                            {clips.some(
                                              (c) =>
                                                c.type === "video" ||
                                                (c.type === "ai-generated" &&
                                                  c.aiStatus === "ready"),
                                            ) && (
                                              <>
                                                <div className="h-px bg-black/[0.06]" />
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    startFrameFromVideoPicker({
                                                      kind: "ref",
                                                    })
                                                  }
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <Film className="w-3.5 h-3.5 text-brand-black/50" />
                                                  Seleccionar de video
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        </>
                                      )}
                                  </div>
                                </div>
                              )}

                              {/* Start/end frames */}
                              {sceneRefMode === "frames" &&
                                (() => {
                                  const firstPreview = sceneFirstFrameAssetId
                                    ? assetPreviewUrls[sceneFirstFrameAssetId]
                                    : null;
                                  const lastPreview = sceneLastFrameAssetId
                                    ? assetPreviewUrls[sceneLastFrameAssetId]
                                    : null;
                                  return (
                                    <div className="flex gap-3 mt-3">
                                      <div
                                        className={`flex-1 rounded-[10px] border-2 ${sceneFirstFrameAssetId ? "border-solid border-neutral-200" : "border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03]"} transition-colors flex items-center justify-center relative${panelBounceClass("scene-firstframe")}${panelNudgeClass("scene-firstframe")}`}
                                        style={{
                                          height: 110,
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          !sceneFirstFrameAssetId &&
                                          setImageSlotMenu({ kind: "first" })
                                        }
                                      >
                                        {imageSlotMenu?.kind === "first" &&
                                          !sceneFirstFrameAssetId && (
                                            <>
                                              <div
                                                className="fixed inset-0 z-30"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setImageSlotMenu(null);
                                                }}
                                              />
                                              <div
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-40 bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[180px]"
                                              >
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setImageSlotMenu(null);
                                                    sceneFirstFrameRef.current?.click();
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <Upload className="w-3.5 h-3.5 text-brand-black/50" />
                                                  Subir imagen
                                                </button>
                                                <div className="h-px bg-black/[0.06]" />
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    openImageGenPanel({
                                                      kind: "first",
                                                    })
                                                  }
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                                                  Generar con IA
                                                </button>
                                                <div className="h-px bg-black/[0.06]" />
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setImageSlotMenu(null);
                                                    openLibraryPicker("first");
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <FolderOpen className="w-3.5 h-3.5 text-brand-black/50" />
                                                  Usar desde biblioteca
                                                </button>
                                                {clips.some(
                                                  (c) =>
                                                    c.type === "video" ||
                                                    (c.type ===
                                                      "ai-generated" &&
                                                      c.aiStatus === "ready"),
                                                ) && (
                                                  <>
                                                    <div className="h-px bg-black/[0.06]" />
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        startFrameFromVideoPicker(
                                                          { kind: "first" },
                                                        )
                                                      }
                                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                    >
                                                      <Film className="w-3.5 h-3.5 text-brand-black/50" />
                                                      Seleccionar de video
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </>
                                          )}
                                        {sceneFirstFrameAssetId ? (
                                          <>
                                            {firstPreview ? (
                                              <img
                                                src={firstPreview}
                                                alt="Primer frame"
                                                className="object-contain rounded-[8px]"
                                                style={{
                                                  maxWidth: "calc(100% - 16px)",
                                                  maxHeight:
                                                    "calc(100% - 16px)",
                                                }}
                                              />
                                            ) : (
                                              <span className="text-[11px] text-neutral-400">
                                                ...
                                              </span>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSceneFirstFrameAssetId(null);
                                              }}
                                              className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                              style={{ cursor: "pointer" }}
                                            >
                                              <Plus className="w-3 h-3 text-white rotate-45" />
                                            </button>
                                          </>
                                        ) : (
                                          <div className="flex flex-col items-center gap-1.5">
                                            <Plus className="w-5 h-5 text-neutral-300" />
                                            <span className="text-[13px] text-neutral-400 font-medium">
                                              Inicio
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <div
                                        className={`flex-1 rounded-[10px] border-2 ${sceneLastFrameAssetId ? "border-solid border-neutral-200" : "border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03]"} transition-colors flex items-center justify-center relative${panelBounceClass("scene-lastframe")}${panelNudgeClass("scene-lastframe")}`}
                                        style={{
                                          height: 110,
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          !sceneLastFrameAssetId &&
                                          setImageSlotMenu({ kind: "last" })
                                        }
                                      >
                                        {imageSlotMenu?.kind === "last" &&
                                          !sceneLastFrameAssetId && (
                                            <>
                                              <div
                                                className="fixed inset-0 z-30"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setImageSlotMenu(null);
                                                }}
                                              />
                                              <div
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-40 bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[180px]"
                                              >
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setImageSlotMenu(null);
                                                    sceneLastFrameRef.current?.click();
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <Upload className="w-3.5 h-3.5 text-brand-black/50" />
                                                  Subir imagen
                                                </button>
                                                <div className="h-px bg-black/[0.06]" />
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    openImageGenPanel({
                                                      kind: "last",
                                                    })
                                                  }
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                                                  Generar con IA
                                                </button>
                                                <div className="h-px bg-black/[0.06]" />
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setImageSlotMenu(null);
                                                    openLibraryPicker("last");
                                                  }}
                                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                >
                                                  <FolderOpen className="w-3.5 h-3.5 text-brand-black/50" />
                                                  Usar desde biblioteca
                                                </button>
                                                {clips.some(
                                                  (c) =>
                                                    c.type === "video" ||
                                                    (c.type ===
                                                      "ai-generated" &&
                                                      c.aiStatus === "ready"),
                                                ) && (
                                                  <>
                                                    <div className="h-px bg-black/[0.06]" />
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        startFrameFromVideoPicker(
                                                          { kind: "last" },
                                                        )
                                                      }
                                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                                    >
                                                      <Film className="w-3.5 h-3.5 text-brand-black/50" />
                                                      Seleccionar de video
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </>
                                          )}
                                        {sceneLastFrameAssetId ? (
                                          <>
                                            {lastPreview ? (
                                              <img
                                                src={lastPreview}
                                                alt="Último frame"
                                                className="object-contain rounded-[8px]"
                                                style={{
                                                  maxWidth: "calc(100% - 16px)",
                                                  maxHeight:
                                                    "calc(100% - 16px)",
                                                }}
                                              />
                                            ) : (
                                              <span className="text-[11px] text-neutral-400">
                                                ...
                                              </span>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSceneLastFrameAssetId(null);
                                              }}
                                              className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                              style={{ cursor: "pointer" }}
                                            >
                                              <Plus className="w-3 h-3 text-white rotate-45" />
                                            </button>
                                          </>
                                        ) : (
                                          <div className="flex flex-col items-center gap-1.5">
                                            <Plus className="w-5 h-5 text-neutral-300" />
                                            <span className="text-[13px] text-neutral-400 font-medium">
                                              Final
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                            </div>
                          )}


                          {/* Static dialog: voice selector below Frame inicial */}
                          {sceneMode === "personas" &&
                            scenePersonType === "static" && (
                              <div data-tour="dialog-voice">
                                <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                  Voz
                                </p>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowAudioAiVoiceSelector(true)
                                  }
                                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                                >
                                  <span className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                                      <Mic className="w-4 h-4 text-brand-primary" />
                                    </div>
                                    <div className="flex flex-col items-start min-w-0">
                                      <span className="text-[13px] text-brand-black/45 leading-tight">
                                        {audioAiVoiceId
                                          ? "Voz seleccionada"
                                          : "Seleccioná una voz"}
                                      </span>
                                      <span
                                        className={`text-[15px] font-medium leading-tight truncate max-w-full ${audioAiVoiceId ? "text-brand-black/90" : "text-brand-black/50"}`}
                                      >
                                        {audioAiVoiceName || "Ninguna"}
                                      </span>
                                    </div>
                                  </span>
                                  <ChevronRight className="w-4 h-4 text-brand-black/40 shrink-0" />
                                </button>
                              </div>
                            )}

                          {/* Prompt — General or Static dialog: single textarea */}
                          {(sceneMode !== "personas" ||
                            scenePersonType === "static") &&
                            !(
                              sceneMode === "general" &&
                              sceneRefMode === "reference" &&
                              sceneMultiEnabled
                            ) && (
                              <div
                                data-tour={
                                  sceneMode === "personas"
                                    ? "dialog-text"
                                    : "scene-prompt"
                                }
                              >
                                <div className="flex items-center justify-between mb-2 gap-3">
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">
                                    {sceneMode === "personas"
                                      ? "Diálogo de la escena"
                                      : "Prompt de la escena"}
                                  </p>
                                  {sceneMode === "personas" &&
                                    scenePersonType === "static" && (
                                      <div className="inline-flex p-0.5 bg-neutral-100 rounded-md text-[12px] font-medium">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSceneDialogueInputMode("text")
                                          }
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                            sceneDialogueInputMode === "text"
                                              ? "bg-white text-brand-black/90 shadow-sm"
                                              : "text-brand-black/55 hover:text-brand-black/80"
                                          }`}
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                          Escribir
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSceneDialogueInputMode("audio")
                                          }
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                            sceneDialogueInputMode === "audio"
                                              ? "bg-white text-brand-black/90 shadow-sm"
                                              : "text-brand-black/55 hover:text-brand-black/80"
                                          }`}
                                        >
                                          <Mic className="w-3.5 h-3.5" />
                                          Grabar
                                        </button>
                                      </div>
                                    )}
                                </div>
                                {sceneMode === "personas" &&
                                scenePersonType === "static" &&
                                sceneDialogueInputMode === "audio" ? (
                                  <DialogueAudioInput
                                    value={sceneDialogueAudio}
                                    onChange={setSceneDialogueAudio}
                                    projectId={projectId || null}
                                    companyId={companyId}
                                  />
                                ) : (
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("scene-prompt")}`}
                                  >
                                    <textarea
                                      value={scenePrompt}
                                      onChange={(e) =>
                                        setScenePrompt(e.target.value)
                                      }
                                      placeholder={
                                        sceneMode === "personas"
                                          ? "Escribí el diálogo que dirán en la escena..."
                                          : "Describí qué debería pasar en esta escena..."
                                      }
                                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[140px] placeholder:text-neutral-400"
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Multi-scene list — General + Reference + multi only */}
                          {sceneMode === "general" &&
                            sceneRefMode === "reference" &&
                            sceneMultiEnabled && (
                              <div>
                                <style>{`
                          @keyframes scene-multi-shake {
                            0%, 100% { transform: translateX(0); }
                            15% { transform: translateX(-3px); }
                            30% { transform: translateX(3px); }
                            45% { transform: translateX(-2px); }
                            60% { transform: translateX(2px); }
                            80% { transform: translateX(-1px); }
                          }
                          @keyframes scene-multi-max-pop {
                            0% { opacity: 0; transform: scale(0.85); }
                            45% { opacity: 1; transform: scale(1.08); }
                            100% { opacity: 1; transform: scale(1); }
                          }
                        `}</style>
                                <div className="flex items-baseline justify-between mb-2">
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">
                                    Escenas
                                  </p>
                                  <span
                                    key={multiHitTick}
                                    className={`text-[12px] font-medium tabular-nums inline-block ${
                                      sceneMultiTotal > sceneMultiMaxTotal ||
                                      multiMaxHitId
                                        ? "text-error"
                                        : "text-brand-black/45"
                                    }`}
                                    style={
                                      multiHitTick > 0
                                        ? {
                                            animation:
                                              "scene-multi-shake 0.45s ease",
                                          }
                                        : undefined
                                    }
                                  >
                                    Total: {sceneMultiTotal}s /{" "}
                                    {sceneMultiMaxTotal}s
                                  </span>
                                </div>
                                <div className="flex flex-col gap-2.5">
                                  {sceneMultiList.map((scene, idx) => {
                                    const others =
                                      sceneMultiTotal - scene.duration;
                                    return (
                                      <div
                                        key={scene.id}
                                        className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
                                      >
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 bg-neutral-50/60">
                                          <span className="text-[12px] font-semibold uppercase tracking-wide text-brand-black/60">
                                            Escena {idx + 1}
                                          </span>
                                          {sceneMultiList.length > 1 && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setSceneMultiList((prev) =>
                                                  prev.filter(
                                                    (s) => s.id !== scene.id,
                                                  ),
                                                )
                                              }
                                              className="w-6 h-6 inline-flex items-center justify-center rounded text-brand-black/40 hover:text-error hover:bg-error/10 transition-colors"
                                              aria-label="Quitar escena"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                        </div>
                                        <div className="px-3 pt-2.5 pb-3">
                                          <textarea
                                            value={scene.prompt}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setSceneMultiList((prev) =>
                                                prev.map((s) =>
                                                  s.id === scene.id
                                                    ? { ...s, prompt: v }
                                                    : s,
                                                ),
                                              );
                                            }}
                                            placeholder={`Describí el ángulo / momento de la escena ${idx + 1}...`}
                                            className="w-full text-[14px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[60px] placeholder:text-neutral-400"
                                          />
                                          <div className="flex items-center gap-2.5 pt-2 mt-1.5 border-t border-neutral-100">
                                            <span className="text-[11px] text-brand-black/45 uppercase tracking-wide shrink-0">
                                              Duración
                                            </span>
                                            {(() => {
                                              const TOTAL_BARS = 50;
                                              const MIN_DUR = SCENE_MULTI_MIN;
                                              // Estándar: 4..10 por toma. Profesional
                                              // (Seedance 2.5): hasta 30 por toma — el
                                              // API solo limita la SUMA (4-30s).
                                              const MAX_DUR = sceneMultiMaxPerScene;
                                              const RANGE = Math.max(
                                                1,
                                                MAX_DUR - MIN_DUR,
                                              );
                                              // Cap dragged value so total never exceeds the global limit.
                                              const allowedMax = Math.max(
                                                MIN_DUR,
                                                Math.min(
                                                  MAX_DUR,
                                                  sceneMultiMaxTotal - others,
                                                ),
                                              );
                                              const calcVal = (
                                                clientX: number,
                                                rect: DOMRect,
                                              ) => {
                                                const raw =
                                                  Math.round(
                                                    ((clientX - rect.left) /
                                                      rect.width) *
                                                      RANGE,
                                                  ) + MIN_DUR;
                                                if (raw > allowedMax)
                                                  triggerMultiMaxHit(scene.id);
                                                return Math.max(
                                                  MIN_DUR,
                                                  Math.min(allowedMax, raw),
                                                );
                                              };
                                              const lastSelectedIdx =
                                                Math.round(
                                                  ((Math.min(
                                                    scene.duration,
                                                    MAX_DUR,
                                                  ) -
                                                    MIN_DUR) /
                                                    RANGE) *
                                                    (TOTAL_BARS - 1),
                                                );
                                              return (
                                                <div
                                                  className="flex items-center flex-1 select-none"
                                                  style={{
                                                    height: 24,
                                                    touchAction: "none",
                                                    cursor: "pointer",
                                                  }}
                                                  onPointerDown={(e) => {
                                                    const el = e.currentTarget;
                                                    el.setPointerCapture(
                                                      e.pointerId,
                                                    );
                                                    const rect =
                                                      el.getBoundingClientRect();
                                                    const apply = (
                                                      cx: number,
                                                    ) => {
                                                      const v = calcVal(
                                                        cx,
                                                        rect,
                                                      );
                                                      setSceneMultiList(
                                                        (prev) =>
                                                          prev.map((s) =>
                                                            s.id === scene.id
                                                              ? {
                                                                  ...s,
                                                                  duration: v,
                                                                }
                                                              : s,
                                                          ),
                                                      );
                                                    };
                                                    apply(e.clientX);
                                                    const onMove = (
                                                      ev: PointerEvent,
                                                    ) => apply(ev.clientX);
                                                    const onUp = () => {
                                                      el.removeEventListener(
                                                        "pointermove",
                                                        onMove,
                                                      );
                                                      el.removeEventListener(
                                                        "pointerup",
                                                        onUp,
                                                      );
                                                    };
                                                    el.addEventListener(
                                                      "pointermove",
                                                      onMove,
                                                    );
                                                    el.addEventListener(
                                                      "pointerup",
                                                      onUp,
                                                    );
                                                  }}
                                                >
                                                  {Array.from(
                                                    { length: TOTAL_BARS },
                                                    (_, i) => {
                                                      const isSelected =
                                                        i <= lastSelectedIdx;
                                                      const ratio =
                                                        lastSelectedIdx > 0
                                                          ? i / lastSelectedIdx
                                                          : 1;
                                                      const opacity =
                                                        0.3 + ratio * 0.7;
                                                      return (
                                                        <div
                                                          key={i}
                                                          style={{
                                                            flex: "1 1 0%",
                                                            display: "flex",
                                                            justifyContent:
                                                              "center",
                                                          }}
                                                        >
                                                          <div
                                                            style={{
                                                              width: 1.5,
                                                              height: 20,
                                                              borderRadius: 9999,
                                                              backgroundColor:
                                                                isSelected
                                                                  ? `rgba(22, 68, 235, ${opacity})`
                                                                  : "#d4d4d8",
                                                              transition:
                                                                "background-color 0.2s",
                                                            }}
                                                          />
                                                        </div>
                                                      );
                                                    },
                                                  )}
                                                </div>
                                              );
                                            })()}
                                            {multiMaxHitId === scene.id ? (
                                              <span
                                                key={multiHitTick}
                                                className="text-[12px] font-semibold text-error uppercase tabular-nums whitespace-nowrap inline-block"
                                                style={{
                                                  minWidth: 26,
                                                  textAlign: "right",
                                                  animation:
                                                    "scene-multi-max-pop 0.45s ease",
                                                }}
                                              >
                                                Max
                                              </span>
                                            ) : (
                                              <span
                                                className="text-[13px] font-semibold text-brand-primary tabular-nums whitespace-nowrap"
                                                style={{
                                                  minWidth: 26,
                                                  textAlign: "right",
                                                }}
                                              >
                                                {scene.duration}s
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {(() => {
                                  const canAdd =
                                    sceneMultiMaxTotal - sceneMultiTotal >=
                                    SCENE_MULTI_MIN;
                                  return (
                                    <button
                                      type="button"
                                      disabled={!canAdd}
                                      onClick={() => {
                                        if (!canAdd) return;
                                        const remaining =
                                          sceneMultiMaxTotal -
                                          sceneMultiTotal;
                                        const newDur = Math.max(
                                          SCENE_MULTI_MIN,
                                          Math.min(SCENE_MULTI_MAX, remaining),
                                        );
                                        setSceneMultiList((prev) => [
                                          ...prev,
                                          {
                                            id: generateId(),
                                            prompt: "",
                                            duration: newDur,
                                          },
                                        ]);
                                      }}
                                      className={`mt-2.5 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed text-[13px] font-medium transition-colors ${
                                        canAdd
                                          ? "border-brand-primary/30 text-brand-primary hover:bg-brand-primary/5"
                                          : "border-neutral-200 text-neutral-300 cursor-not-allowed"
                                      }`}
                                      title={
                                        canAdd
                                          ? "Añadir escena"
                                          : `Sin espacio: límite total ${sceneMultiMaxTotal}s`
                                      }
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                      Añadir escena
                                    </button>
                                  );
                                })()}
                              </div>
                            )}

                          {/* Motion dialog: Prompt + Diálogo + Voz + Duración */}
                          {sceneMode === "personas" &&
                            scenePersonType === "motion" && (
                              <>
                                <div>
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                    Prompt de la escena
                                  </p>
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("scene-prompt")}`}
                                  >
                                    <textarea
                                      value={scenePrompt}
                                      onChange={(e) =>
                                        setScenePrompt(e.target.value)
                                      }
                                      placeholder="Describí qué debería pasar en esta escena..."
                                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[100px] placeholder:text-neutral-400"
                                    />
                                  </div>
                                </div>

                                <div data-tour="dialog-text">
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                    Diálogo de la escena
                                  </p>
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("scene-dialogue")}`}
                                  >
                                    <textarea
                                      value={sceneDialogue}
                                      onChange={(e) =>
                                        setSceneDialogue(e.target.value)
                                      }
                                      placeholder="Escribí el diálogo que dirán en la escena..."
                                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px] placeholder:text-neutral-400"
                                    />
                                  </div>
                                </div>

                                {/* Voice selector */}
                                <div data-tour="dialog-voice">
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                    Voz
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowAudioAiVoiceSelector(true)
                                    }
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                                  >
                                    <span className="flex items-center gap-3 min-w-0">
                                      <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                                        <Mic className="w-4 h-4 text-brand-primary" />
                                      </div>
                                      <div className="flex flex-col items-start min-w-0">
                                        <span className="text-[13px] text-brand-black/45 leading-tight">
                                          {audioAiVoiceId
                                            ? "Voz seleccionada"
                                            : "Seleccioná una voz"}
                                        </span>
                                        <span
                                          className={`text-[15px] font-medium leading-tight truncate max-w-full ${audioAiVoiceId ? "text-brand-black/90" : "text-brand-black/50"}`}
                                        >
                                          {audioAiVoiceName || "Ninguna"}
                                        </span>
                                      </div>
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-brand-black/40 shrink-0" />
                                  </button>
                                </div>

                                {/* Duration — thin vertical bars, snapping to UGC options */}
                                <div>
                                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                    Duración
                                  </p>
                                  <div className="flex items-center gap-2.5">
                                    {(() => {
                                      const DURATION_OPTIONS = [
                                        4, 6, 8, 12, 14, 16, 20, 22, 24, 28, 30,
                                        32,
                                      ];
                                      const TOTAL_BARS = 90;
                                      const snapFromRatio = (ratio: number) => {
                                        const clamped = Math.max(
                                          0,
                                          Math.min(1, ratio),
                                        );
                                        const idx = Math.round(
                                          clamped *
                                            (DURATION_OPTIONS.length - 1),
                                        );
                                        return DURATION_OPTIONS[idx];
                                      };
                                      const currentIdx = Math.max(
                                        0,
                                        DURATION_OPTIONS.indexOf(sceneDuration),
                                      );
                                      const lastSelectedIdx = Math.round(
                                        (currentIdx /
                                          (DURATION_OPTIONS.length - 1)) *
                                          (TOTAL_BARS - 1),
                                      );
                                      return (
                                        <div
                                          className="flex items-center flex-1 select-none"
                                          style={{
                                            height: 36,
                                            touchAction: "none",
                                            cursor: "pointer",
                                          }}
                                          onPointerDown={(e) => {
                                            const el = e.currentTarget;
                                            el.setPointerCapture(e.pointerId);
                                            const rect =
                                              el.getBoundingClientRect();
                                            const pick = (clientX: number) =>
                                              setSceneDuration(
                                                snapFromRatio(
                                                  (clientX - rect.left) /
                                                    rect.width,
                                                ),
                                              );
                                            pick(e.clientX);
                                            const onMove = (ev: PointerEvent) =>
                                              pick(ev.clientX);
                                            const onUp = () => {
                                              el.removeEventListener(
                                                "pointermove",
                                                onMove,
                                              );
                                              el.removeEventListener(
                                                "pointerup",
                                                onUp,
                                              );
                                            };
                                            el.addEventListener(
                                              "pointermove",
                                              onMove,
                                            );
                                            el.addEventListener(
                                              "pointerup",
                                              onUp,
                                            );
                                          }}
                                        >
                                          {Array.from(
                                            { length: TOTAL_BARS },
                                            (_, i) => {
                                              const isSelected =
                                                i <= lastSelectedIdx;
                                              const ratio =
                                                lastSelectedIdx > 0
                                                  ? i / lastSelectedIdx
                                                  : 1;
                                              const opacity = 0.3 + ratio * 0.7;
                                              const distFromEdge = Math.abs(
                                                i - lastSelectedIdx,
                                              );
                                              const delayMs = Math.min(
                                                distFromEdge * 4,
                                                120,
                                              );
                                              return (
                                                <div
                                                  key={i}
                                                  style={{
                                                    flex: "1 1 0%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      width: 1.5,
                                                      height: 32,
                                                      borderRadius: 9999,
                                                      backgroundColor:
                                                        isSelected
                                                          ? `rgba(22, 68, 235, ${opacity})`
                                                          : "#d4d4d8",
                                                      transition: `background-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) ${delayMs}ms`,
                                                    }}
                                                  />
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <span
                                      className="text-[15px] font-semibold text-brand-primary tabular-nums whitespace-nowrap"
                                      style={{
                                        minWidth: 32,
                                        textAlign: "right",
                                      }}
                                    >
                                      {sceneDuration}s
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}

                          {/* Duration selector – thin vertical lines */}
                          {sceneMode !== "personas" &&
                            !(
                              sceneMode === "general" &&
                              sceneRefMode === "reference" &&
                              sceneMultiEnabled
                            ) && (
                              <div>
                                <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                  Duración
                                </p>
                                <div className="flex items-center gap-2.5">
                                  {(() => {
                                    const TOTAL_BARS = 90;
                                    const MIN_DUR = 4;
                                    // Profesional (Seedance 2.5) llega a 30s;
                                    // el resto mantiene el tope histórico.
                                    const MAX_DUR =
                                      sceneQuality === "profesional" ? 30 : 15;
                                    const RANGE = MAX_DUR - MIN_DUR;
                                    const calcVal = (
                                      clientX: number,
                                      rect: DOMRect,
                                    ) =>
                                      Math.max(
                                        MIN_DUR,
                                        Math.min(
                                          MAX_DUR,
                                          Math.round(
                                            ((clientX - rect.left) /
                                              rect.width) *
                                              RANGE,
                                          ) + MIN_DUR,
                                        ),
                                      );
                                    return (
                                      <div
                                        className="flex items-center flex-1 select-none"
                                        style={{
                                          height: 36,
                                          touchAction: "none",
                                          cursor: "pointer",
                                        }}
                                        onPointerDown={(e) => {
                                          const el = e.currentTarget;
                                          el.setPointerCapture(e.pointerId);
                                          const rect =
                                            el.getBoundingClientRect();
                                          setSceneDuration(
                                            calcVal(e.clientX, rect),
                                          );
                                          const onMove = (ev: PointerEvent) =>
                                            setSceneDuration(
                                              calcVal(ev.clientX, rect),
                                            );
                                          const onUp = () => {
                                            el.removeEventListener(
                                              "pointermove",
                                              onMove,
                                            );
                                            el.removeEventListener(
                                              "pointerup",
                                              onUp,
                                            );
                                          };
                                          el.addEventListener(
                                            "pointermove",
                                            onMove,
                                          );
                                          el.addEventListener(
                                            "pointerup",
                                            onUp,
                                          );
                                        }}
                                      >
                                        {(() => {
                                          // Index of the rightmost selected bar
                                          const lastSelectedIdx = Math.round(
                                            ((sceneDuration - MIN_DUR) /
                                              RANGE) *
                                              (TOTAL_BARS - 1),
                                          );
                                          return Array.from(
                                            { length: TOTAL_BARS },
                                            (_, i) => {
                                              const isSelected =
                                                i <= lastSelectedIdx;
                                              // Gradient: rightmost selected is darkest, fading left
                                              const ratio =
                                                lastSelectedIdx > 0
                                                  ? i / lastSelectedIdx
                                                  : 1;
                                              const opacity = 0.3 + ratio * 0.7;
                                              // Staggered animation: bars closer to the edge animate a tiny bit later
                                              const distFromEdge = Math.abs(
                                                i - lastSelectedIdx,
                                              );
                                              const delayMs = Math.min(
                                                distFromEdge * 4,
                                                120,
                                              );
                                              return (
                                                <div
                                                  key={i}
                                                  style={{
                                                    flex: "1 1 0%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      width: 1.5,
                                                      height: 32,
                                                      borderRadius: 9999,
                                                      backgroundColor:
                                                        isSelected
                                                          ? `rgba(22, 68, 235, ${opacity})`
                                                          : "#d4d4d8",
                                                      transition: `background-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) ${delayMs}ms`,
                                                    }}
                                                  />
                                                </div>
                                              );
                                            },
                                          );
                                        })()}
                                      </div>
                                    );
                                  })()}
                                  <span
                                    className="text-[15px] font-semibold text-brand-primary tabular-nums whitespace-nowrap"
                                    style={{ minWidth: 32, textAlign: "right" }}
                                  >
                                    {sceneDuration}s
                                  </span>
                                </div>
                              </div>
                            )}

                          {/* Speech speed — dialog mode only (0.5x .. 1.5x, step 0.05, default 1.1) */}
                          {sceneMode === "personas" && (
                            <div>
                              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                Velocidad de habla
                              </p>
                              <div className="flex items-center gap-2.5">
                                {(() => {
                                  const TOTAL_BARS = 90;
                                  const MIN_SPD = 0.5;
                                  const MAX_SPD = 1.5;
                                  const STEP = 0.05;
                                  const RANGE = MAX_SPD - MIN_SPD;
                                  const STEP_COUNT = Math.round(RANGE / STEP);
                                  const calcVal = (
                                    clientX: number,
                                    rect: DOMRect,
                                  ) => {
                                    const ratio = Math.max(
                                      0,
                                      Math.min(
                                        1,
                                        (clientX - rect.left) / rect.width,
                                      ),
                                    );
                                    const stepIdx = Math.round(
                                      ratio * STEP_COUNT,
                                    );
                                    return (
                                      Math.round(
                                        (MIN_SPD + stepIdx * STEP) * 100,
                                      ) / 100
                                    );
                                  };
                                  return (
                                    <div
                                      className="flex items-center flex-1 select-none"
                                      style={{
                                        height: 36,
                                        touchAction: "none",
                                        cursor: "pointer",
                                      }}
                                      onPointerDown={(e) => {
                                        const el = e.currentTarget;
                                        el.setPointerCapture(e.pointerId);
                                        const rect = el.getBoundingClientRect();
                                        setSceneSpeechSpeed(
                                          calcVal(e.clientX, rect),
                                        );
                                        const onMove = (ev: PointerEvent) =>
                                          setSceneSpeechSpeed(
                                            calcVal(ev.clientX, rect),
                                          );
                                        const onUp = () => {
                                          el.removeEventListener(
                                            "pointermove",
                                            onMove,
                                          );
                                          el.removeEventListener(
                                            "pointerup",
                                            onUp,
                                          );
                                        };
                                        el.addEventListener(
                                          "pointermove",
                                          onMove,
                                        );
                                        el.addEventListener("pointerup", onUp);
                                      }}
                                    >
                                      {(() => {
                                        const ratio =
                                          (sceneSpeechSpeed - MIN_SPD) / RANGE;
                                        const lastSelectedIdx = Math.round(
                                          ratio * (TOTAL_BARS - 1),
                                        );
                                        return Array.from(
                                          { length: TOTAL_BARS },
                                          (_, i) => {
                                            const isSelected =
                                              i <= lastSelectedIdx;
                                            const r =
                                              lastSelectedIdx > 0
                                                ? i / lastSelectedIdx
                                                : 1;
                                            const opacity = 0.3 + r * 0.7;
                                            const distFromEdge = Math.abs(
                                              i - lastSelectedIdx,
                                            );
                                            const delayMs = Math.min(
                                              distFromEdge * 4,
                                              120,
                                            );
                                            return (
                                              <div
                                                key={i}
                                                style={{
                                                  flex: "1 1 0%",
                                                  display: "flex",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: 1.5,
                                                    height: 32,
                                                    borderRadius: 9999,
                                                    backgroundColor: isSelected
                                                      ? `rgba(22, 68, 235, ${opacity})`
                                                      : "#d4d4d8",
                                                    transition: `background-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) ${delayMs}ms`,
                                                  }}
                                                />
                                              </div>
                                            );
                                          },
                                        );
                                      })()}
                                    </div>
                                  );
                                })()}
                                <span
                                  className="text-[15px] font-semibold text-brand-primary tabular-nums whitespace-nowrap"
                                  style={{ minWidth: 44, textAlign: "right" }}
                                >
                                  x {sceneSpeechSpeed.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Calidad + Resolución */}
                          <div className="flex gap-2">
                            {/* Calidad dropdown */}
                            {sceneMode !== "personas" && (
                              <div className="relative flex-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setQualityMenuOpen((o) => !o);
                                    setResolutionMenuOpen(false);
                                  }}
                                  className="w-full flex items-center justify-between px-4 py-3 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
                                >
                                  <div className="flex flex-col items-start leading-tight">
                                    <span className="text-[11px] text-brand-black/40 uppercase tracking-wide">
                                      Calidad
                                    </span>
                                    <span className="text-[14px] font-medium text-brand-black/80">
                                      {sceneQuality === "profesional"
                                        ? "Profesional"
                                        : "Estándar"}
                                    </span>
                                  </div>
                                  <ChevronDown
                                    className={`w-4 h-4 text-neutral-400 transition-transform ${qualityMenuOpen ? "rotate-180" : ""}`}
                                  />
                                </button>
                                {qualityMenuOpen && (
                                  <>
                                    <div
                                      className="fixed inset-0 z-10"
                                      onClick={() => setQualityMenuOpen(false)}
                                    />
                                    <div className="absolute left-0 right-0 bottom-full mb-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden">
                                      {(
                                        ["estandar", "profesional"] as const
                                      ).map((q) => (
                                        <button
                                          key={q}
                                          type="button"
                                          onClick={() => {
                                            setSceneQuality(q);
                                            setQualityMenuOpen(false);
                                          }}
                                          className={`w-full text-left px-3 py-2.5 hover:bg-neutral-50 transition-colors flex flex-col items-start gap-0.5 ${sceneQuality === q ? "text-brand-primary" : "text-brand-black/80"}`}
                                        >
                                          <span
                                            className={`text-[14px] ${sceneQuality === q ? "font-medium" : ""}`}
                                          >
                                            {q === "profesional"
                                              ? "Profesional"
                                              : "Estándar"}
                                          </span>
                                          <span className="text-[11px] text-brand-black/45 leading-tight">
                                            {q === "profesional"
                                              ? "El mejor modelo, videos de hasta 30 segundos. Consume más créditos"
                                              : "Buena calidad, videos de hasta 15 segundos"}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Resolución dropdown */}
                            <div className="relative flex-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setResolutionMenuOpen((o) => !o);
                                  setQualityMenuOpen(false);
                                }}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
                              >
                                <div className="flex flex-col items-start leading-tight">
                                  <span className="text-[11px] text-brand-black/40 uppercase tracking-wide">
                                    Resolución
                                  </span>
                                  <span className="text-[14px] font-medium text-brand-black/80">
                                    {sceneResolution}
                                  </span>
                                </div>
                                <ChevronDown
                                  className={`w-4 h-4 text-neutral-400 transition-transform ${resolutionMenuOpen ? "rotate-180" : ""}`}
                                />
                              </button>
                              {resolutionMenuOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setResolutionMenuOpen(false)}
                                  />
                                  <div className="absolute left-0 right-0 bottom-full mb-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden">
                                    {(["480p", "720p", "1080p"] as const)
                                      .filter(
                                        (r) =>
                                          !(
                                            sceneMode === "personas" &&
                                            r === "480p"
                                          ),
                                      )
                                      .map((r) => (
                                        <button
                                          key={r}
                                          type="button"
                                          onClick={() => {
                                            setSceneResolution(r);
                                            setResolutionMenuOpen(false);
                                          }}
                                          className={`w-full text-left px-4 py-2.5 text-[14px] transition-colors ${
                                            sceneResolution === r
                                              ? "text-brand-primary font-medium hover:bg-neutral-50"
                                              : "text-brand-black/80 hover:bg-neutral-50"
                                          }`}
                                        >
                                          {r}
                                        </button>
                                      ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Multiescena toggle — General + Reference only */}
                          {sceneMode === "general" &&
                            sceneRefMode === "reference" && (
                              <div className="bg-white border border-neutral-200 rounded-lg p-3.5">
                                <div className="flex items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <Film className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                                      <span className="text-[14px] font-semibold text-neutral-900">
                                        Multiescena
                                      </span>
                                    </div>
                                    <p className="text-[12.5px] leading-relaxed text-brand-black/55 mt-1">
                                      Crea un video con múltiples ángulos de la
                                      misma escena. Definí cada toma con su
                                      prompt y duración (máx.{" "}
                                      {sceneMultiMaxTotal}s totales).
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={sceneMultiEnabled}
                                    onClick={() =>
                                      setSceneMultiEnabled((v) => !v)
                                    }
                                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                      sceneMultiEnabled
                                        ? "bg-brand-primary"
                                        : "bg-neutral-200"
                                    }`}
                                  >
                                    <span
                                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        sceneMultiEnabled
                                          ? "translate-x-5"
                                          : "translate-x-0"
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                            )}
                        </div>

                        {/* Bottom: Director + Generar */}
                        <div className="shrink-0 px-3 py-3 border-t border-black/[0.06]">
                          {(() => {
                            const isMulti =
                              sceneMode === "general" &&
                              sceneRefMode === "reference" &&
                              sceneMultiEnabled;
                            const overLimit =
                              isMulti && sceneMultiTotal > sceneMultiMaxTotal;
                            // Dialogue mode (HeyGen + Veo) always needs a
                            // selected voice — the workflow plugs it into the
                            // ElevenLabs URL, which 404s when the id is empty.
                            const missingVoice =
                              sceneMode === "personas" && !audioAiVoiceId;
                            // Audio input mode also needs the user's audio
                            // uploaded (no URL → nothing to feed s2s).
                            const missingAudio =
                              sceneMode === "personas" &&
                              scenePersonType === "static" &&
                              sceneDialogueInputMode === "audio" &&
                              !sceneDialogueAudio;
                            const disabled =
                              overLimit || missingVoice || missingAudio;
                            const disabledReason = overLimit
                              ? `La duración total supera los ${sceneMultiMaxTotal}s de la calidad ${sceneQuality === "profesional" ? "Profesional" : "Estándar"} — quitá una escena o bajá duraciones`
                              : missingVoice
                                ? "Elegí una voz primero"
                                : missingAudio
                                  ? "Grabá o subí un audio primero"
                                  : undefined;
                            return (
                              <div className="flex gap-2">
                                <DirectorButton
                                  showAgentChat={showAgentChat}
                                  busy={agentStreaming || !!activeAsk}
                                  onClick={() =>
                                    setShowAgentChat(!showAgentChat)
                                  }
                                />
                                <button
                                  onClick={() => {
                                    if (!disabled) addAiSceneClip("generate");
                                  }}
                                  disabled={disabled}
                                  title={disabledReason}
                                  className={`flex-1 py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                                    disabled
                                      ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                      : "bg-brand-primary hover:bg-brand-primary-hover text-white"
                                  }`}
                                >
                                  <Sparkles className="w-4 h-4" />
                                  Generar
                                  {!disabled && (
                                    <CreditBadge
                                      credits={scenePanelCost}
                                      tone="on-primary"
                                      size="sm"
                                    />
                                  )}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {activePanel === "ai-audio" && (
                      <div className="flex-1 h-full flex flex-col min-w-0">
                        {/* Header */}
                        <div className="flex items-center justify-between px-3 h-12">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <button
                              onClick={() => {
                                setFilesTab("audio");
                                setActivePanel("files");
                              }}
                              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/50 hover:bg-black/[0.06] transition-colors shrink-0"
                              title="Volver"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <h3 className="text-[15px] font-semibold text-brand-black/90 truncate">
                              Nuevo audio con IA
                            </h3>
                          </div>
                          <button
                            onClick={() => setActivePanel("none")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                          >
                            <Plus className="w-4 h-4 rotate-45" />
                          </button>
                        </div>
                        <div className="h-px bg-black/[0.08]" />

                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                          {/* Type selector (Voz en off / Música) — segmented control */}
                          <div data-tour="audio-type">
                            <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                              Tipo de audio
                            </p>
                            <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
                              <div
                                className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                                style={{
                                  width: "calc(50% - 2px)",
                                  left:
                                    audioAiType === "voice"
                                      ? "3px"
                                      : "calc(50% + 1px)",
                                }}
                              />
                              {(["voice", "music"] as const).map((t) => (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setAudioAiType(t)}
                                  className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center gap-1.5 text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                                    audioAiType === t
                                      ? "text-brand-black/90"
                                      : "text-brand-black/50"
                                  }`}
                                >
                                  {t === "voice" ? (
                                    <Mic className="w-3.5 h-3.5" />
                                  ) : (
                                    <Music className="w-3.5 h-3.5" />
                                  )}
                                  {t === "voice" ? "Voz en off" : "Música"}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Voz — visible only in voice mode */}
                          {audioAiType === "voice" && (
                            <div>
                              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                Voz
                              </p>
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAudioAiVoiceSelector(true)
                                }
                                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                              >
                                <span className="flex items-center gap-3 min-w-0">
                                  <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center shrink-0">
                                    <Mic className="w-4 h-4 text-brand-primary" />
                                  </div>
                                  <div className="flex flex-col items-start min-w-0">
                                    <span className="text-[13px] text-brand-black/45 leading-tight">
                                      {audioAiVoiceId
                                        ? "Voz seleccionada"
                                        : "Seleccioná una voz"}
                                    </span>
                                    <span
                                      className={`text-[15px] font-medium leading-tight truncate max-w-full ${audioAiVoiceId ? "text-brand-black/90" : "text-brand-black/50"}`}
                                    >
                                      {audioAiVoiceName || "Ninguna"}
                                    </span>
                                  </div>
                                </span>
                                <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                              </button>
                            </div>
                          )}

                          {/* Prompt — label & placeholder change with type */}
                          <div data-tour="audio-text">
                            <div className="flex items-center justify-between mb-2 gap-3">
                              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">
                                {audioAiType === "voice"
                                  ? "Diálogo"
                                  : "Descripción musical"}
                              </p>
                              {audioAiType === "voice" && (
                                <div className="inline-flex p-0.5 bg-neutral-100 rounded-md text-[12px] font-medium">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAudioAiDialogueInputMode("text")
                                    }
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                      audioAiDialogueInputMode === "text"
                                        ? "bg-white text-brand-black/90 shadow-sm"
                                        : "text-brand-black/55 hover:text-brand-black/80"
                                    }`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                    Escribir
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAudioAiDialogueInputMode("audio")
                                    }
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                      audioAiDialogueInputMode === "audio"
                                        ? "bg-white text-brand-black/90 shadow-sm"
                                        : "text-brand-black/55 hover:text-brand-black/80"
                                    }`}
                                  >
                                    <Mic className="w-3.5 h-3.5" />
                                    Grabar
                                  </button>
                                </div>
                              )}
                            </div>
                            {audioAiType === "voice" &&
                            audioAiDialogueInputMode === "audio" ? (
                              <DialogueAudioInput
                                value={audioAiDialogueAudio}
                                onChange={setAudioAiDialogueAudio}
                                projectId={projectId || null}
                                companyId={companyId}
                              />
                            ) : (
                              <div
                                className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("audio-prompt")}`}
                              >
                                <textarea
                                  value={audioAiPrompt}
                                  onChange={(e) =>
                                    setAudioAiPrompt(e.target.value)
                                  }
                                  placeholder={
                                    audioAiType === "voice"
                                      ? "Escribí lo que querés que diga la voz..."
                                      : "Describí la música que querés generar (género, instrumentos, mood)..."
                                  }
                                  className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[140px] placeholder:text-neutral-400"
                                />
                              </div>
                            )}
                            {audioAiType === "voice" &&
                              audioAiDialogueInputMode === "text" && (
                                <p className="mt-2 px-0.5 text-[13px] text-[#3C3C43]/60 leading-snug">
                                  Tip: usá puntuación (comas, puntos, signos)
                                  para que el diálogo tenga las pausas y el tono
                                  adecuados.
                                </p>
                              )}
                          </div>

                          {/* Speech speed — voice mode only (0.5x .. 1.5x, step 0.05, default 1.1) */}
                          {audioAiType === "voice" && (
                            <div>
                              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                Velocidad de habla
                              </p>
                              <div className="flex items-center gap-2.5">
                                {(() => {
                                  const TOTAL_BARS = 90;
                                  const MIN_SPD = 0.5;
                                  const MAX_SPD = 1.5;
                                  const STEP = 0.05;
                                  const RANGE = MAX_SPD - MIN_SPD;
                                  const STEP_COUNT = Math.round(RANGE / STEP);
                                  const calcVal = (
                                    clientX: number,
                                    rect: DOMRect,
                                  ) => {
                                    const ratio = Math.max(
                                      0,
                                      Math.min(
                                        1,
                                        (clientX - rect.left) / rect.width,
                                      ),
                                    );
                                    const stepIdx = Math.round(
                                      ratio * STEP_COUNT,
                                    );
                                    return (
                                      Math.round(
                                        (MIN_SPD + stepIdx * STEP) * 100,
                                      ) / 100
                                    );
                                  };
                                  return (
                                    <div
                                      className="flex items-center flex-1 select-none"
                                      style={{
                                        height: 36,
                                        touchAction: "none",
                                        cursor: "pointer",
                                      }}
                                      onPointerDown={(e) => {
                                        const el = e.currentTarget;
                                        el.setPointerCapture(e.pointerId);
                                        const rect = el.getBoundingClientRect();
                                        setAudioAiSpeechSpeed(
                                          calcVal(e.clientX, rect),
                                        );
                                        const onMove = (ev: PointerEvent) =>
                                          setAudioAiSpeechSpeed(
                                            calcVal(ev.clientX, rect),
                                          );
                                        const onUp = () => {
                                          el.removeEventListener(
                                            "pointermove",
                                            onMove,
                                          );
                                          el.removeEventListener(
                                            "pointerup",
                                            onUp,
                                          );
                                        };
                                        el.addEventListener(
                                          "pointermove",
                                          onMove,
                                        );
                                        el.addEventListener("pointerup", onUp);
                                      }}
                                    >
                                      {(() => {
                                        const ratio =
                                          (audioAiSpeechSpeed - MIN_SPD) /
                                          RANGE;
                                        const lastSelectedIdx = Math.round(
                                          ratio * (TOTAL_BARS - 1),
                                        );
                                        return Array.from(
                                          { length: TOTAL_BARS },
                                          (_, i) => {
                                            const isSelected =
                                              i <= lastSelectedIdx;
                                            const r =
                                              lastSelectedIdx > 0
                                                ? i / lastSelectedIdx
                                                : 1;
                                            const opacity = 0.3 + r * 0.7;
                                            const distFromEdge = Math.abs(
                                              i - lastSelectedIdx,
                                            );
                                            const delayMs = Math.min(
                                              distFromEdge * 4,
                                              120,
                                            );
                                            return (
                                              <div
                                                key={i}
                                                style={{
                                                  flex: "1 1 0%",
                                                  display: "flex",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: 1.5,
                                                    height: 32,
                                                    borderRadius: 9999,
                                                    backgroundColor: isSelected
                                                      ? `rgba(22, 68, 235, ${opacity})`
                                                      : "#d4d4d8",
                                                    transition: `background-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) ${delayMs}ms`,
                                                  }}
                                                />
                                              </div>
                                            );
                                          },
                                        );
                                      })()}
                                    </div>
                                  );
                                })()}
                                <span
                                  className="text-[15px] font-semibold text-brand-primary tabular-nums whitespace-nowrap"
                                  style={{ minWidth: 44, textAlign: "right" }}
                                >
                                  x {audioAiSpeechSpeed.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Instrumental-only toggle — music mode only */}
                          {audioAiType === "music" && (
                            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg">
                              <div className="flex flex-col min-w-0">
                                <span className="text-[14px] font-medium text-brand-black/90">
                                  Solo música instrumental
                                </span>
                                <span className="text-[12px] text-brand-black/50">
                                  Generá sin voces ni letras
                                </span>
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={audioAiInstrumentalOnly}
                                onClick={() =>
                                  setAudioAiInstrumentalOnly((v) => !v)
                                }
                                className={`relative shrink-0 w-10 h-6 rounded-full transition-colors ${
                                  audioAiInstrumentalOnly
                                    ? "bg-brand-primary"
                                    : "bg-black/[0.15]"
                                }`}
                              >
                                <span
                                  className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"
                                  style={{
                                    transform: audioAiInstrumentalOnly
                                      ? "translateX(16px)"
                                      : "translateX(0)",
                                  }}
                                />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Bottom: Director + Generar */}
                        <div className="shrink-0 px-3 py-3 border-t border-black/[0.06]">
                          {(() => {
                            // Block reasons, by priority. Each branch sets the
                            // tooltip so the user knows what's missing.
                            const isVoiceTextEmpty =
                              audioAiType === "voice" &&
                              audioAiDialogueInputMode === "text" &&
                              !audioAiPrompt.trim();
                            const isVoiceAudioMissing =
                              audioAiType === "voice" &&
                              audioAiDialogueInputMode === "audio" &&
                              !audioAiDialogueAudio;
                            const isVoiceWithoutVoiceId =
                              audioAiType === "voice" && !audioAiVoiceId;
                            const isMusicEmpty =
                              audioAiType === "music" && !audioAiPrompt.trim();
                            const disabled =
                              isVoiceTextEmpty ||
                              isVoiceAudioMissing ||
                              isVoiceWithoutVoiceId ||
                              isMusicEmpty;
                            const disabledReason = isVoiceAudioMissing
                              ? "Grabá o subí un audio primero"
                              : isVoiceWithoutVoiceId
                                ? "Elegí una voz primero"
                                : isVoiceTextEmpty
                                  ? "Escribí el diálogo primero"
                                  : isMusicEmpty
                                    ? "Describí la música primero"
                                    : undefined;
                            return (
                              <div className="flex gap-2">
                                <DirectorButton
                                  showAgentChat={showAgentChat}
                                  busy={agentStreaming || !!activeAsk}
                                  onClick={() =>
                                    setShowAgentChat(!showAgentChat)
                                  }
                                />
                                <button
                                  onClick={() => {
                                    if (!disabled) addAiAudioClip("generate");
                                  }}
                                  disabled={disabled}
                                  title={disabledReason}
                                  className={`flex-1 py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                                    disabled
                                      ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                      : "bg-brand-primary hover:bg-brand-primary-hover text-white"
                                  }`}
                                >
                                  <Sparkles className="w-4 h-4" />
                                  Generar
                                  {!disabled && (
                                    <CreditBadge
                                      credits={audioPanelCost}
                                      tone="on-primary"
                                      size="sm"
                                    />
                                  )}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {activePanel === "ai-image-gen" &&
                      (() => {
                        const detail = imageGenDetailId
                          ? imageGenResults.find(
                              (r) => r.id === imageGenDetailId,
                            )
                          : null;
                        const slotPreviewUrl = slotPreview
                          ? assetPreviewUrls[slotPreview.assetId]
                          : null;
                        const slotLabel = slotPreview
                          ? slotPreview.target.kind === "first"
                            ? "Primer frame"
                            : slotPreview.target.kind === "last"
                              ? "Último frame"
                              : "Imagen de referencia"
                          : "";
                        const examples = [
                          "Un atardecer cinematográfico sobre montañas nevadas",
                          "Una taza de café humeante sobre una mesa de madera rústica",
                          "Un paisaje futurista con luces de neón al anochecer",
                          "Una flor delicada con gotas de rocío y fondo desenfocado",
                        ];
                        // Assets que son materialización de una generada / producto
                        // / escena / historial: NO deben aparecer como "subidas" en
                        // Multimedia (la generada se muestra como tile IA; los
                        // productos/escenas viven en su pill). Evita duplicados.
                        const materializedAssetIds = new Set<string>([
                          ...Object.values(imageGenImportedMap),
                          ...Object.values(imageGenProductImportedMap),
                          ...Object.values(productSceneAssetMap),
                          ...Object.values(modelAssetMap),
                        ]);
                        // Nombres de archivo de las generadas: el asset-copia de
                        // una generada se sube con ese mismo nombre, así lo
                        // detectamos para no duplicarlo en "Subidas".
                        const generatedFileNames = new Set(
                          imageGenResults
                            .map((r) => r.fileName)
                            .filter((n): n is string => !!n),
                        );
                        // ¿Mostrar este id como "subida"? No si es la copia de
                        // una generada/producto, ni si no es una imagen (video /
                        // asset roto).
                        // Fotos de modelos (por nombre) — mismo criterio que en
                        // Multimedia: no son "subidas", viven en su picker.
                        const modelNames = new Set(
                          modelItems.map((m) =>
                            (m.nombre || "modelo").slice(0, 40),
                          ),
                        );
                        const showAsUpload = (id: string): boolean => {
                          if (materializedAssetIds.has(id)) return false;
                          const meta = galleryAssetMeta[id];
                          if (!meta) return true; // sin meta aún: mostrar (carga)
                          if (meta.type !== "image") return false; // video, etc.
                          if (meta.name && generatedFileNames.has(meta.name))
                            return false; // copia de una generada
                          if (
                            meta.name &&
                            modelNames.has(meta.name.replace(/\.[^.]+$/, ""))
                          )
                            return false; // foto de un modelo
                          return true;
                        };
                        // La grilla del panel de generar muestra SOLO lo que se
                        // generó en esta sesión (el historial completo está en
                        // Multimedia/Historial, no tiene sentido repetirlo acá).
                        const sessionGenResults = imageGenResults.filter(
                          (r) => r.session,
                        );
                        return (
                          <div className="flex-1 h-full flex flex-col min-w-0 relative">
                            {/* Header */}
                            <div className="flex items-center gap-2 px-3 h-12">
                              <button
                                onClick={backToSceneFromImageGen}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/60 hover:bg-black/[0.1] transition-colors"
                                title="Volver"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <h3 className="text-[15px] font-semibold text-brand-black/90">
                                {slotPreview
                                  ? slotLabel
                                  : detail
                                    ? "Imagen generada"
                                    : "Generar imagen con IA"}
                              </h3>
                            </div>
                            <div className="h-px bg-black/[0.08]" />

                            {/* Content */}
                            {slotPreview ? (
                              /* ── Slot-preview view ────────────────────────────── */
                              <div className="flex-1 min-h-0 flex flex-col bg-white">
                                {/* Image viewer */}
                                <div className="flex-1 min-h-0 relative flex items-center justify-center p-3">
                                  {slotPreviewUrl ? (
                                    <img
                                      src={slotPreviewUrl}
                                      alt="Imagen seleccionada"
                                      className="max-w-full max-h-full object-contain rounded-2xl"
                                    />
                                  ) : (
                                    <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                                  )}
                                </div>

                                {/* Action bar — Utilizar / Editar OR edit prompt card */}
                                {slotPreviewEditing ? (
                                  <div className="flex-shrink-0 px-3 pb-3 pt-2 bg-white">
                                    <div
                                      className={`bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 py-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]${panelBounceClass("slot-edit-prompt")}`}
                                    >
                                      <textarea
                                        value={slotPreviewEditPrompt}
                                        onChange={(e) =>
                                          setSlotPreviewEditPrompt(
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Describí el cambio que querés hacer a la imagen..."
                                        className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[90px]"
                                        autoFocus
                                      />
                                      <div className="flex items-center justify-between gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSlotPreviewEditing(false);
                                            setSlotPreviewEditPrompt("");
                                          }}
                                          className="py-2 inline-flex items-center gap-1.5 text-[13px] text-brand-black/60 hover:text-brand-black/90 transition-colors"
                                        >
                                          <ChevronLeft className="w-3.5 h-3.5" />
                                          Cancelar
                                        </button>
                                        <button
                                          type="button"
                                          onClick={submitSlotPreviewEdit}
                                          disabled={
                                            !slotPreviewEditPrompt.trim()
                                          }
                                          className="py-2 px-4 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          <Sparkles className="w-3.5 h-3.5" />
                                          Editar
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex-shrink-0 px-3 pt-2 pb-3 bg-white">
                                    <div className="grid grid-cols-2 gap-2">
                                      <button
                                        onClick={useSlotPreviewImage}
                                        disabled={!slotPreviewUrl}
                                        className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${slotPreviewUrl ? "bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                                      >
                                        <Check className="w-5 h-5" />
                                        <span>Utilizar</span>
                                      </button>
                                      <button
                                        onClick={() =>
                                          setSlotPreviewEditing(true)
                                        }
                                        disabled={!slotPreviewUrl}
                                        className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-brand-black/80 border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Pencil className="w-5 h-5" />
                                        <span>Editar</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : detail ? (
                              /* ── Detail view (mirrors mobile GeneratedImagesInline) ── */
                              <div className="flex-1 min-h-0 flex flex-col bg-white">
                                {/* Image viewer with horizontal snap-scroll between all results */}
                                <div className="flex-1 min-h-0 relative">
                                  {imageGenEditSubmitting && (
                                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                                        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                                        <span className="text-[13px] font-medium text-neutral-700">
                                          Editando imagen
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  <div
                                    ref={imageGenDetailScrollRef}
                                    className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                    style={{
                                      scrollbarWidth: "none",
                                      msOverflowStyle: "none",
                                      WebkitOverflowScrolling: "touch",
                                    }}
                                  >
                                    {imageGenResults.map((r) => (
                                      <div
                                        key={`detail-snap-${r.id}`}
                                        className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center p-3"
                                      >
                                        {r.signedUrl ? (
                                          <img
                                            src={r.signedUrl}
                                            alt={`Generada ${r.id}`}
                                            className="max-w-full max-h-full object-contain rounded-2xl"
                                            onError={() =>
                                              refreshGenImageSignedUrl(r.id)
                                            }
                                          />
                                        ) : (
                                          <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Action bar — 3 buttons OR edit prompt card when editing */}
                                {imageGenEditing ? (
                                  <div className="flex-shrink-0 px-3 pb-3 pt-2 bg-white">
                                    <div
                                      className={`bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 py-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] ${imageGenEditSubmitting ? "shine-effect" : ""}${panelBounceClass("gen-edit-prompt")}`}
                                    >
                                      <textarea
                                        value={imageGenEditPrompt}
                                        onChange={(e) =>
                                          setImageGenEditPrompt(e.target.value)
                                        }
                                        placeholder="Describí el cambio que querés hacer a la imagen..."
                                        disabled={imageGenEditSubmitting}
                                        className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[90px] disabled:cursor-not-allowed"
                                        autoFocus
                                      />
                                      <div className="flex items-center justify-between gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setImageGenEditing(false);
                                            setImageGenEditPrompt("");
                                            setImageGenEditError(null);
                                          }}
                                          disabled={imageGenEditSubmitting}
                                          className="py-2 inline-flex items-center gap-1.5 text-[13px] text-brand-black/60 hover:text-brand-black/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          <ChevronLeft className="w-3.5 h-3.5" />
                                          Cancelar
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleEditImage}
                                          disabled={
                                            !imageGenEditPrompt.trim() ||
                                            imageGenEditSubmitting
                                          }
                                          className="py-2 px-4 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {imageGenEditSubmitting ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <Sparkles className="w-3.5 h-3.5" />
                                          )}
                                          Editar
                                          <CreditBadge
                                            credits={creditsForImage()}
                                            tone="on-primary"
                                            size="sm"
                                          />
                                        </button>
                                      </div>
                                    </div>
                                    {imageGenEditError && (
                                      <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-[8px] text-[12px] text-red-700">
                                        {imageGenEditError}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex-shrink-0 px-3 pt-2 pb-3 bg-white">
                                    <div
                                      className={`grid gap-2 ${imageGenTarget ? "grid-cols-3" : "grid-cols-2"}`}
                                    >
                                      {/* Utilizar — SOLO cuando hay un slot de
                                          escena al que asignar (frame inicial/
                                          final/referencia). Desde Multimedia no
                                          tiene sentido (subía un duplicado y
                                          cerraba), así que no aparece. */}
                                      {imageGenTarget && (
                                        <button
                                          onClick={() =>
                                            useGeneratedImage(detail)
                                          }
                                          disabled={!detail.signedUrl}
                                          className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${detail.signedUrl ? "bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                                        >
                                          <Check className="w-5 h-5" />
                                          <span>Utilizar</span>
                                        </button>
                                      )}
                                      {/* Editar */}
                                      <button
                                        onClick={() => setImageGenEditing(true)}
                                        disabled={!detail.signedUrl}
                                        className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-brand-black/80 border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Pencil className="w-5 h-5" />
                                        <span>Editar</span>
                                      </button>
                                      {/* Eliminar */}
                                      <button
                                        onClick={() =>
                                          deleteGeneratedImage(detail.id)
                                        }
                                        className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-red-600 border border-neutral-200 hover:bg-red-50 hover:border-red-200"
                                      >
                                        <Trash2 className="w-5 h-5" />
                                        <span>Eliminar</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              /* ── Grid + Prompt view ──────────────────────────── */
                              <div className="flex-1 min-h-0 flex flex-col relative">
                                {/* Top: grid (if results) or examples/historial.
                            Stays in normal flow; the prompt card below is
                            absolutely positioned and overlays this area
                            when the user expands the gallery. */}
                                <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4 min-h-0">
                                  {showImageGenHistorial ? (
                                    <div>
                                      <p className="text-[12px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                        Historial
                                      </p>
                                      {imageGenHistorial.length === 0 ? (
                                        <div className="px-3 py-6 text-[13px] text-brand-black/50 text-center bg-white border border-neutral-200 rounded-[10px]">
                                          Sin historial aún
                                        </div>
                                      ) : (
                                        <div className="space-y-1.5">
                                          {imageGenHistorial.map((p, i) => (
                                            <button
                                              key={i}
                                              type="button"
                                              onClick={() => {
                                                setImageGenPrompt(p);
                                                setShowImageGenHistorial(false);
                                              }}
                                              className="w-full text-left px-3 py-2 rounded-[8px] bg-white border border-neutral-200 text-[13px] text-brand-black/80 hover:bg-neutral-50 transition-colors"
                                            >
                                              {p}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : sessionGenResults.length > 0 ||
                                    imageGenPendingCount > 0 ? (
                                    <div className="grid grid-cols-3 gap-2">
                                      {/* Pending placeholders (no ID yet) */}
                                      {Array.from({
                                        length: imageGenPendingCount,
                                      }).map((_, i) => (
                                        <div
                                          key={`pending-${i}`}
                                          className="relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100"
                                        >
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-24 h-24">
                                              <DotLottieReact
                                                src="https://lottie.host/848266a7-3f51-41c9-bbc0-b55500402910/XzeuQRXrLQ.lottie"
                                                autoplay
                                                loop
                                                style={{
                                                  width: "100%",
                                                  height: "100%",
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      {sessionGenResults.map((r) => (
                                        <button
                                          key={r.id}
                                          type="button"
                                          onClick={() =>
                                            r.signedUrl &&
                                            setImageGenDetailId(r.id)
                                          }
                                          disabled={!r.signedUrl}
                                          className="relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100 transition-transform hover:scale-[0.98] disabled:cursor-default"
                                        >
                                          {r.signedUrl ? (
                                            <img
                                              src={r.signedUrl}
                                              alt="Generada"
                                              className="w-full h-full object-cover"
                                              onError={() =>
                                                refreshGenImageSignedUrl(r.id)
                                              }
                                            />
                                          ) : (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <div className="w-24 h-24">
                                                <DotLottieReact
                                                  src="https://lottie.host/848266a7-3f51-41c9-bbc0-b55500402910/XzeuQRXrLQ.lottie"
                                                  autoplay
                                                  loop
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <div>
                                      <p className="text-[12px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                        Ejemplos
                                      </p>
                                      <div className="space-y-1.5">
                                        {examples.map((ex, i) => (
                                          <button
                                            key={i}
                                            type="button"
                                            onClick={() =>
                                              setImageGenPrompt(ex)
                                            }
                                            className="w-full text-left px-3 py-2 rounded-[8px] bg-white border border-neutral-200 text-[13px] text-brand-black/80 hover:bg-neutral-50 transition-colors"
                                          >
                                            {ex}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {imageGenError && (
                                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-[8px] text-[12px] text-red-700">
                                      {imageGenError}
                                    </div>
                                  )}
                                </div>

                                {/* Inline "Imágenes del proyecto" gallery overlay.
                            Positioned absolutely between the top of the panel
                            content area and the top of the prompt card
                            section (whose live height is tracked via a ref +
                            ResizeObserver). This way it OVERLAYS the examples
                            grid without displacing it, and reaches exactly
                            from the panel header down to the card. */}
                                <div
                                  className="absolute left-3 right-3 top-3 z-10 transition-[bottom,opacity,transform] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
                                  style={{
                                    bottom: imageGenPromptSectionH + 4,
                                    opacity: imageGenGalleryOpen ? 1 : 0,
                                    transform: imageGenGalleryOpen
                                      ? "translateY(0)"
                                      : "translateY(8px)",
                                    pointerEvents: imageGenGalleryOpen
                                      ? "auto"
                                      : "none",
                                  }}
                                  aria-hidden={!imageGenGalleryOpen}
                                >
                                  <div className="h-full bg-white border border-neutral-200 rounded-[14px] shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] pl-5 pr-4 py-3 flex flex-col">
                                    <div className="flex items-center justify-between mb-2 shrink-0">
                                      <p className="text-[11px] font-medium text-brand-black/45 uppercase tracking-wide">
                                        Imágenes del proyecto
                                      </p>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setImageGenGalleryOpen(false)
                                        }
                                        className="w-6 h-6 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:text-brand-black/80 hover:bg-black/[0.06] transition-colors"
                                        tabIndex={imageGenGalleryOpen ? 0 : -1}
                                        aria-label="Cerrar galería"
                                      >
                                        <Plus className="w-3.5 h-3.5 rotate-45" />
                                      </button>
                                    </div>
                                    {/* Source pills: Subidas (asset images uploaded by
                                the user) vs Generadas (AI-generated images
                                already in the project). */}
                                    <div className="flex items-center gap-1.5 mb-2 shrink-0">
                                      {(
                                        [
                                          {
                                            key: "uploaded",
                                            label: "Multimedia",
                                            Icon: ImageIcon,
                                          },
                                          {
                                            key: "productos",
                                            label: "Productos",
                                            Icon: Package,
                                          },
                                          {
                                            key: "escenas",
                                            label: "Escenas",
                                            Icon: Mountain,
                                          },
                                          {
                                            key: "historial",
                                            label: "Historial",
                                            Icon: Clock,
                                          },
                                        ] as const
                                      ).map((p) => {
                                        const active =
                                          imageGenGalleryFilter === p.key;
                                        const PIcon = p.Icon;
                                        return (
                                          <button
                                            key={p.key}
                                            type="button"
                                            onClick={() =>
                                              setImageGenGalleryFilter(p.key)
                                            }
                                            tabIndex={
                                              imageGenGalleryOpen ? 0 : -1
                                            }
                                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium transition-colors ${
                                              active
                                                ? "bg-brand-primary text-white"
                                                : "bg-neutral-100 text-brand-black/60 hover:bg-neutral-200"
                                            }`}
                                          >
                                            <PIcon className="w-3.5 h-3.5" />
                                            {p.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                    {/* p-1 gives ring-offset-2 (4px) clearance so
                                the selected-tile blue ring isn't clipped
                                by the overflow-auto container. */}
                                    <div className="flex-1 min-h-0 overflow-y-auto p-1">
                                      {imageGenGalleryFilter === "generated" ? (
                                        (() => {
                                          const ready = imageGenResults.filter(
                                            (r) => !!r.signedUrl,
                                          );
                                          if (ready.length === 0) {
                                            return (
                                              <p className="text-[12px] text-neutral-400 px-1 py-6 text-center">
                                                Todavía no hay imágenes
                                                generadas en este proyecto.
                                              </p>
                                            );
                                          }
                                          return (
                                            <div className="grid grid-cols-3 gap-2">
                                              {ready.map((r) => {
                                                const importedAssetId =
                                                  imageGenImportedMap[r.id];
                                                const isAttached =
                                                  !!importedAssetId &&
                                                  imageGenAttachedAssetIds.includes(
                                                    importedAssetId,
                                                  );
                                                const isImporting =
                                                  imageGenImportingGenIds.has(
                                                    r.id,
                                                  );
                                                return (
                                                  <div
                                                    key={`gen-${r.id}`}
                                                    onClick={async () => {
                                                      if (isImporting) return;
                                                      if (importedAssetId) {
                                                        setImageGenAttachedAssetIds(
                                                          (prev) =>
                                                            isAttached
                                                              ? prev.filter(
                                                                  (x) =>
                                                                    x !==
                                                                    importedAssetId,
                                                                )
                                                              : [
                                                                  ...prev,
                                                                  importedAssetId,
                                                                ],
                                                        );
                                                        return;
                                                      }
                                                      setImageGenImportingGenIds(
                                                        (prev) => {
                                                          const next = new Set(
                                                            prev,
                                                          );
                                                          next.add(r.id);
                                                          return next;
                                                        },
                                                      );
                                                      try {
                                                        const newId =
                                                          await importGeneratedImageAsAsset(
                                                            r.id,
                                                          );
                                                        if (newId) {
                                                          setImageGenGalleryAssetIds(
                                                            (prev) =>
                                                              prev.includes(
                                                                newId,
                                                              )
                                                                ? prev
                                                                : [
                                                                    ...prev,
                                                                    newId,
                                                                  ],
                                                          );
                                                          setImageGenAttachedAssetIds(
                                                            (prev) =>
                                                              prev.includes(
                                                                newId,
                                                              )
                                                                ? prev
                                                                : [
                                                                    ...prev,
                                                                    newId,
                                                                  ],
                                                          );
                                                        }
                                                      } finally {
                                                        setImageGenImportingGenIds(
                                                          (prev) => {
                                                            const next =
                                                              new Set(prev);
                                                            next.delete(r.id);
                                                            return next;
                                                          },
                                                        );
                                                      }
                                                    }}
                                                    className={`group relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100 cursor-pointer transition-all ${isAttached ? "ring-2 ring-brand-primary ring-offset-2" : "hover:scale-[0.98]"}`}
                                                  >
                                                    <img
                                                      src={r.signedUrl!}
                                                      alt="Imagen generada"
                                                      className="w-full h-full object-cover pointer-events-none"
                                                    />
                                                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-brand-primary/85 text-white text-[9px] font-semibold uppercase tracking-wide flex items-center gap-0.5 pointer-events-none">
                                                      <Sparkles className="w-2.5 h-2.5" />
                                                      IA
                                                    </div>
                                                    {isAttached && (
                                                      <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-primary flex items-center justify-center pointer-events-none">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                      </div>
                                                    )}
                                                    {isImporting && (
                                                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
                                                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          );
                                        })()
                                      ) : imageGenGalleryFilter ===
                                          "productos" ||
                                        imageGenGalleryFilter === "escenas" ||
                                        imageGenGalleryFilter ===
                                          "historial" ? (
                                        <div className="grid grid-cols-3 gap-2">
                                          {(imageGenGalleryFilter === "escenas"
                                            ? imageGenScenesLoading
                                            : imageGenGalleryFilter ===
                                                "historial"
                                              ? imageGenHistorialLoading
                                              : imageGenProductsLoading) ? (
                                            <ProductGridSkeleton
                                              bare
                                              count={9}
                                              tileClassName="rounded-[10px]"
                                            />
                                          ) : (imageGenGalleryFilter ===
                                              "escenas"
                                              ? imageGenSceneItems
                                              : imageGenGalleryFilter ===
                                                  "historial"
                                                ? imageGenHistorialItems
                                                : imageGenProductItems
                                            ).length === 0 ? (
                                            <p className="col-span-3 text-[12px] text-neutral-400 px-1 py-6 text-center">
                                              {imageGenGalleryFilter === "escenas"
                                                ? "No hay escenas todavía."
                                                : imageGenGalleryFilter ===
                                                    "historial"
                                                  ? "No hay imágenes en el historial."
                                                  : "No hay productos en esta marca."}
                                            </p>
                                          ) : (
                                            (imageGenGalleryFilter === "escenas"
                                              ? imageGenSceneItems
                                              : imageGenGalleryFilter ===
                                                  "historial"
                                                ? imageGenHistorialItems
                                                : imageGenProductItems
                                            ).map((p) => {
                                              const importedAssetId =
                                                imageGenProductImportedMap[p.id];
                                              const isAttached =
                                                !!importedAssetId &&
                                                imageGenAttachedAssetIds.includes(
                                                  importedAssetId,
                                                );
                                              const isImporting =
                                                imageGenImportingProductIds.has(
                                                  p.id,
                                                );
                                              return (
                                                <div
                                                  key={`prod-${p.id}`}
                                                  title={p.name}
                                                  onClick={async () => {
                                                    if (isImporting) return;
                                                    if (importedAssetId) {
                                                      setImageGenAttachedAssetIds(
                                                        (prev) =>
                                                          isAttached
                                                            ? prev.filter(
                                                                (x) =>
                                                                  x !==
                                                                  importedAssetId,
                                                              )
                                                            : [
                                                                ...prev,
                                                                importedAssetId,
                                                              ],
                                                      );
                                                      return;
                                                    }
                                                    setImageGenImportingProductIds(
                                                      (prev) => {
                                                        const next = new Set(
                                                          prev,
                                                        );
                                                        next.add(p.id);
                                                        return next;
                                                      },
                                                    );
                                                    try {
                                                      const newId =
                                                        await importProductImageForGen(
                                                          p,
                                                        );
                                                      if (newId) {
                                                        setImageGenAttachedAssetIds(
                                                          (prev) =>
                                                            prev.includes(newId)
                                                              ? prev
                                                              : [
                                                                  ...prev,
                                                                  newId,
                                                                ],
                                                        );
                                                      }
                                                    } finally {
                                                      setImageGenImportingProductIds(
                                                        (prev) => {
                                                          const next = new Set(
                                                            prev,
                                                          );
                                                          next.delete(p.id);
                                                          return next;
                                                        },
                                                      );
                                                    }
                                                  }}
                                                  className={`group relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100 cursor-pointer transition-all ${isAttached ? "ring-2 ring-brand-primary ring-offset-2" : "hover:scale-[0.98]"}`}
                                                >
                                                  <CachedImg
                                                    src={p.imageUrl}
                                                    alt={p.name}
                                                    className="w-full h-full object-cover pointer-events-none"
                                                  />
                                                  {isAttached && (
                                                    <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-primary flex items-center justify-center pointer-events-none">
                                                      <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                  )}
                                                  {isImporting && (
                                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
                                                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-3 gap-2">
                                          {/* Subir imagen card */}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              imageGenFileInputRef.current?.click()
                                            }
                                            disabled={imageGenUploading}
                                            tabIndex={
                                              imageGenGalleryOpen ? 0 : -1
                                            }
                                            className="aspect-square rounded-[10px] border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1 disabled:opacity-50"
                                          >
                                            {imageGenUploading ? (
                                              <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                                            ) : (
                                              <>
                                                <Upload className="w-4 h-4 text-neutral-400" />
                                                <span className="text-[11px] font-medium text-neutral-500">
                                                  Subir
                                                </span>
                                              </>
                                            )}
                                          </button>
                                          {imageGenGalleryAssetIds
                                            .filter(showAsUpload)
                                            .map((id) => {
                                            const isAttached =
                                              imageGenAttachedAssetIds.includes(
                                                id,
                                              );
                                            const url = assetPreviewUrls[id];
                                            const menuOpen =
                                              imageGenGalleryMenuId === id;
                                            return (
                                              <div
                                                key={`gallery-${id}`}
                                                onClick={() => {
                                                  if (menuOpen) {
                                                    setImageGenGalleryMenuId(
                                                      null,
                                                    );
                                                    return;
                                                  }
                                                  setImageGenAttachedAssetIds(
                                                    (prev) =>
                                                      isAttached
                                                        ? prev.filter(
                                                            (x) => x !== id,
                                                          )
                                                        : [...prev, id],
                                                  );
                                                }}
                                                className={`group relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100 cursor-pointer transition-all ${isAttached ? "ring-2 ring-brand-primary ring-offset-2" : "hover:scale-[0.98]"}`}
                                              >
                                                {url ? (
                                                  <img
                                                    src={url}
                                                    alt="Imagen del proyecto"
                                                    className="w-full h-full object-cover pointer-events-none"
                                                  />
                                                ) : (
                                                  <Loader2 className="w-5 h-5 text-neutral-400 animate-spin absolute inset-0 m-auto" />
                                                )}
                                                {isAttached && (
                                                  <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-primary flex items-center justify-center pointer-events-none">
                                                    <Check className="w-2.5 h-2.5 text-white" />
                                                  </div>
                                                )}
                                                {!isAttached && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setImageGenGalleryMenuId(
                                                        menuOpen ? null : id,
                                                      );
                                                    }}
                                                    className={`absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-opacity ${menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                                                  >
                                                    <MoreHorizontal className="w-3 h-3 text-white" />
                                                  </button>
                                                )}
                                                {menuOpen && (
                                                  <div
                                                    className="absolute top-7 right-1 z-20 min-w-[130px] rounded-[8px] bg-white border border-neutral-200 shadow-lg overflow-hidden"
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                  >
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        setImageGenGalleryAssetIds(
                                                          (prev) =>
                                                            prev.filter(
                                                              (x) => x !== id,
                                                            ),
                                                        );
                                                        setImageGenAttachedAssetIds(
                                                          (prev) =>
                                                            prev.filter(
                                                              (x) => x !== id,
                                                            ),
                                                        );
                                                        setImageGenGalleryMenuId(
                                                          null,
                                                        );
                                                      }}
                                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-red-600 hover:bg-red-50 text-left"
                                                    >
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                      Eliminar
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                          {/* Generadas con IA — mismo grid que las
                                              subidas (badge IA). Click = adjuntar
                                              (materializa deduplicado). */}
                                          {imageGenResults
                                            .filter((r) => !!r.signedUrl)
                                            .map((r) => {
                                              const importedAssetId =
                                                imageGenImportedMap[r.id];
                                              const isAttached =
                                                !!importedAssetId &&
                                                imageGenAttachedAssetIds.includes(
                                                  importedAssetId,
                                                );
                                              const isImporting =
                                                imageGenImportingGenIds.has(
                                                  r.id,
                                                );
                                              return (
                                                <div
                                                  key={`gen-${r.id}`}
                                                  onClick={async () => {
                                                    if (isImporting) return;
                                                    if (importedAssetId) {
                                                      setImageGenAttachedAssetIds(
                                                        (prev) =>
                                                          isAttached
                                                            ? prev.filter(
                                                                (x) =>
                                                                  x !==
                                                                  importedAssetId,
                                                              )
                                                            : [
                                                                ...prev,
                                                                importedAssetId,
                                                              ],
                                                      );
                                                      return;
                                                    }
                                                    setImageGenImportingGenIds(
                                                      (prev) => {
                                                        const next = new Set(
                                                          prev,
                                                        );
                                                        next.add(r.id);
                                                        return next;
                                                      },
                                                    );
                                                    try {
                                                      const newId =
                                                        await importGeneratedImageAsAsset(
                                                          r.id,
                                                        );
                                                      if (newId)
                                                        setImageGenAttachedAssetIds(
                                                          (prev) =>
                                                            prev.includes(newId)
                                                              ? prev
                                                              : [
                                                                  ...prev,
                                                                  newId,
                                                                ],
                                                        );
                                                    } finally {
                                                      setImageGenImportingGenIds(
                                                        (prev) => {
                                                          const next = new Set(
                                                            prev,
                                                          );
                                                          next.delete(r.id);
                                                          return next;
                                                        },
                                                      );
                                                    }
                                                  }}
                                                  className={`group relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100 cursor-pointer transition-all ${isAttached ? "ring-2 ring-brand-primary ring-offset-2" : "hover:scale-[0.98]"}`}
                                                >
                                                  <img
                                                    src={r.signedUrl!}
                                                    alt="Imagen generada"
                                                    className="w-full h-full object-cover pointer-events-none"
                                                  />
                                                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-brand-primary/85 text-white text-[9px] font-semibold uppercase tracking-wide flex items-center gap-0.5 pointer-events-none">
                                                    <Sparkles className="w-2.5 h-2.5" />
                                                    IA
                                                  </div>
                                                  {isAttached && (
                                                    <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-primary flex items-center justify-center pointer-events-none">
                                                      <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                  )}
                                                  {isImporting && (
                                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
                                                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Bottom: prompt card (with shadow) — Añadir /
                            Historial left, Generar right. Stays in normal
                            flow at the bottom; its measured height drives
                            the gallery overlay's bottom offset above. */}
                                <div
                                  ref={imageGenPromptSectionRef}
                                  className="shrink-0 px-3 pb-3 pt-2"
                                >
                                  <input
                                    ref={imageGenFileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageGenAttachUpload}
                                    className="hidden"
                                  />
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 py-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] flex flex-col${panelBounceClass("image-prompt")}`}
                                  >
                                    {imageGenAttachedAssetIds.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mb-2">
                                        {imageGenAttachedAssetIds.map((id) => (
                                          <div
                                            key={`attached-${id}`}
                                            onClick={() =>
                                              assetPreviewUrls[id] &&
                                              setImageGenAttachedPreviewId(id)
                                            }
                                            className="relative w-14 h-14 rounded-[8px] overflow-hidden border border-neutral-200 bg-neutral-100 group cursor-zoom-in"
                                          >
                                            {assetPreviewUrls[id] ? (
                                              <img
                                                src={assetPreviewUrls[id]}
                                                alt="Adjunto"
                                                className="w-full h-full object-cover pointer-events-none"
                                              />
                                            ) : (
                                              <Loader2 className="w-4 h-4 text-neutral-400 animate-spin absolute inset-0 m-auto" />
                                            )}
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setImageGenAttachedAssetIds(
                                                  (prev) =>
                                                    prev.filter(
                                                      (x) => x !== id,
                                                    ),
                                                );
                                              }}
                                              className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                                            >
                                              <Plus className="w-2.5 h-2.5 text-white rotate-45" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <textarea
                                      ref={imageGenPromptRef}
                                      value={imageGenPrompt}
                                      onChange={(e) =>
                                        setImageGenPrompt(e.target.value)
                                      }
                                      rows={1}
                                      placeholder="Describí la imagen que querés generar..."
                                      className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[44px] max-h-[200px] overflow-y-auto mb-2"
                                    />
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (imageGenGalleryOpen) {
                                              setImageGenGalleryOpen(false);
                                              return;
                                            }
                                            // Siempre abrimos el menú: aunque no
                                            // haya imágenes SUBIDAS puede haber
                                            // generadas, productos, escenas o
                                            // historial. El atajo al explorador
                                            // de archivos dejaba al usuario sin
                                            // acceso a ninguna de esas fuentes
                                            // (el menú ya trae su tile "Subir").
                                            setShowImageGenHistorial(false);
                                            setImageGenGalleryOpen(true);
                                          }}
                                          disabled={imageGenUploading}
                                          className={`py-2 px-2 inline-flex items-center gap-1.5 text-[13px] rounded-[8px] transition-colors disabled:opacity-50 ${imageGenGalleryOpen ? "text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/15" : "text-brand-black/60 hover:text-brand-black/90"}${panelNudgeClass("image-attach")}`}
                                        >
                                          {imageGenUploading ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <Plus className="w-3.5 h-3.5" />
                                          )}
                                          Añadir
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setShowImageGenHistorial((v) => !v)
                                          }
                                          className={`py-2 px-2 inline-flex items-center gap-1.5 text-[13px] transition-colors ${showImageGenHistorial ? "text-brand-primary" : "text-brand-black/60 hover:text-brand-black/90"}`}
                                        >
                                          <Clock className="w-3.5 h-3.5" />
                                          Historial
                                        </button>
                                        {/* Usar modelo — adjunta un modelo como
                                            referencia a la generación en curso.
                                            Por default sí; se oculta solo cuando
                                            ya entraste vía "Usar modelo" de Modo
                                            Diálogo (ya hay un modelo elegido). */}
                                        {!imageGenFromModelFlow && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setImageGenGalleryOpen(false);
                                              setShowImageGenHistorial(false);
                                              setModelPickKind("attach");
                                              setModelPickerOpen(true);
                                              setActivePanel("files");
                                            }}
                                            className="py-2 px-2 inline-flex items-center gap-1.5 text-[13px] text-brand-black/60 hover:text-brand-black/90 transition-colors"
                                          >
                                            <Users className="w-3.5 h-3.5" />
                                            Usar personaje
                                          </button>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => {
                                          void startImageGeneration();
                                        }}
                                        disabled={!imageGenPrompt.trim()}
                                        className="py-2 px-4 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Sparkles className="w-3.5 h-3.5" />
                                        Generar
                                        <CreditBadge
                                          credits={creditsForImage()}
                                          tone="on-primary"
                                          size="sm"
                                        />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* ── Enlarged preview overlay for attached image ── */}
                            {imageGenAttachedPreviewId &&
                              assetPreviewUrls[imageGenAttachedPreviewId] && (
                                <div
                                  className="absolute inset-0 z-30 flex items-center justify-center p-6 bg-white/30 backdrop-blur-[4px] animate-in fade-in duration-150"
                                  onClick={() =>
                                    setImageGenAttachedPreviewId(null)
                                  }
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setImageGenAttachedPreviewId(null);
                                    }}
                                    className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                                  >
                                    <Plus className="w-4 h-4 rotate-45" />
                                  </button>
                                  <img
                                    src={
                                      assetPreviewUrls[
                                        imageGenAttachedPreviewId
                                      ]
                                    }
                                    alt="Vista ampliada"
                                    onClick={(e) => e.stopPropagation()}
                                    className="max-w-full max-h-full object-contain rounded-[12px] shadow-2xl"
                                  />
                                </div>
                              )}
                          </div>
                        );
                      })()}
                  </div>
                </div>
              </div>

              {/* ── Cambiar voz del clip — sidebar izquierda, homogénea con el
                   resto de herramientas (mismo patrón/estilo que el selector de
                   voz de acá abajo). Se abre desde la barra IA de un clip. ── */}
              <div
                className="relative z-10 shrink-0 overflow-hidden transition-[width] duration-250 ease-out"
                style={{ width: showClipVoicePicker ? 380 : 0 }}
              >
                {showClipVoicePicker &&
                  (() => {
                    // Costo estimado del cambio de voz = misma fórmula S2S que
                    // el panel de audio (creditsForVoice sobre ~12.5 chars/seg),
                    // con la duración del clip target.
                    const target = clips.find(
                      (c) => c.id === changeVoiceClipId,
                    );
                    const durSec = target
                      ? target.sourceDuration || target.duration
                      : 0;
                    const estCredits = creditsForVoice(
                      Math.ceil(durSec * 12.5),
                    );
                    return (
                      <ClipVoicePicker
                        open={showClipVoicePicker}
                        userId={currentUserId}
                        companyId={companyId}
                        estimatedCredits={estCredits}
                        onGenerate={(voiceId) => {
                          setShowClipVoicePicker(false);
                          const t = changeVoiceClipId;
                          setChangeVoiceClipId(null);
                          if (t) void changeClipVoice(t, voiceId);
                        }}
                        onClose={() => {
                          setShowClipVoicePicker(false);
                          setChangeVoiceClipId(null);
                        }}
                      />
                    );
                  })()}
              </div>

              {/* ── Voice selector — secondary sidebar to the right of tools panel ── */}
              <div
                className="relative z-10 shrink-0 overflow-hidden transition-[width] duration-250 ease-out"
                style={{
                  width:
                    showAudioAiVoiceSelector &&
                    ((activePanel === "ai-audio" && audioAiType === "voice") ||
                      (activePanel === "ai-scene" && sceneMode === "personas"))
                      ? 380
                      : 0,
                }}
              >
                <div className="flex flex-col min-w-[380px] w-[380px] h-full border-r border-black/[0.06] bg-surface-card">
                  {audioAiVoiceView === "create" ? (
                    <VoiceCloneCreator
                      userId={currentUserId ?? ""}
                      companyId={companyId ?? ""}
                      onCreated={(voice) => {
                        setClonedVoices((prev) => [voice, ...prev]);
                        setAudioAiVoiceView("list");
                        setAudioAiVoiceTab("mis-voces");
                        // Auto-seleccionar la voz recién creada
                        setAudioAiVoiceId(voice.elevenlabsVoiceId);
                        setAudioAiVoiceName(voice.nombre);
                      }}
                      onBack={() => setAudioAiVoiceView("list")}
                    />
                  ) : (
                    <>
                  <div className="flex items-center justify-between px-3 h-12">
                    <h3 className="text-[15px] font-semibold text-brand-black/90">
                      Seleccionar voz
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (audioAiVoicePreviewRef.current) {
                          audioAiVoicePreviewRef.current.pause();
                          audioAiVoicePreviewRef.current = null;
                        }
                        setAudioAiVoicePlayingId(null);
                        setShowAudioAiVoiceSelector(false);
                      }}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    >
                      <Plus className="w-4 h-4 rotate-45" />
                    </button>
                  </div>
                  <div className="h-px bg-black/[0.08]" />

                  {/* Tabs: Mis voces / Voces Atlas */}
                  <div className="px-3 pt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAudioAiVoiceTab("mis-voces")}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                        audioAiVoiceTab === "mis-voces"
                          ? "bg-brand-primary text-white"
                          : "bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]"
                      }`}
                    >
                      Mis voces
                      {clonedVoices.length > 0
                        ? ` (${clonedVoices.length})`
                        : ""}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAudioAiVoiceTab("voces-atlas")}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                        audioAiVoiceTab === "voces-atlas"
                          ? "bg-brand-primary text-white"
                          : "bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]"
                      }`}
                    >
                      Voces Atlas
                    </button>
                  </div>

                  {audioAiVoiceTab === "mis-voces" ? (
                    <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
                      {clonedVoicesLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                        </div>
                      ) : clonedVoices.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                          <Mic className="w-10 h-10 text-brand-black/30" />
                          <p className="text-[14px] text-brand-black/60">
                            Todavía no clonaste ninguna voz.
                          </p>
                        </div>
                      ) : (
                        clonedVoices.map((voice) => {
                          const isSelected =
                            audioAiVoiceId === voice.elevenlabsVoiceId;
                          return (
                            <button
                              key={voice.id}
                              type="button"
                              onClick={() => {
                                setAudioAiVoiceId(voice.elevenlabsVoiceId);
                                setAudioAiVoiceName(voice.nombre);
                                if (audioAiVoicePreviewRef.current) {
                                  audioAiVoicePreviewRef.current.pause();
                                  audioAiVoicePreviewRef.current = null;
                                }
                                setAudioAiVoicePlayingId(null);
                                setShowAudioAiVoiceSelector(false);
                              }}
                              className={`w-full text-left rounded-[10px] p-3 transition-colors ${
                                isSelected
                                  ? "bg-brand-primary/[0.06] ring-2 ring-brand-primary"
                                  : "bg-white ring-1 ring-black/[0.06] hover:bg-black/[0.02]"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Mic className="w-4 h-4 text-brand-primary shrink-0" />
                                <span className="text-[14px] font-medium text-brand-black/90 truncate">
                                  {voice.nombre}
                                </span>
                                {isSelected && (
                                  <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                                )}
                              </div>
                              <p className="text-[12px] text-brand-black/50 mt-0.5">
                                Voz clonada
                              </p>
                            </button>
                          );
                        })
                      )}
                      <button
                        type="button"
                        onClick={() => setAudioAiVoiceView("create")}
                        disabled={!currentUserId || !companyId}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-md bg-white border border-dashed border-brand-primary/40 hover:border-brand-primary text-brand-primary text-[14px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                        Crear nueva voz clonada
                      </button>
                    </div>
                  ) : (
                    <>
                  {/* Filtros (dropdowns custom en una fila): género + idioma.
                  Mismo patrón visual que los dropdowns de Calidad/Resolución
                  del scene panel — botón con label arriba + valor abajo, y
                  un panel flotante abajo con las opciones. Las opciones se
                  derivan dinámicamente de los datos del provider. */}
                  <div className="px-3 pt-3 pb-2 flex items-center gap-2">
                    {(() => {
                      const sexOptions = [
                        "Todas",
                        ...Array.from(
                          new Set(
                            audioAiVoices.map((v) => v.sexo).filter(Boolean),
                          ),
                        ).sort(),
                      ];
                      const langOptions = [
                        "Todas",
                        ...Array.from(
                          new Set(
                            audioAiVoices.map((v) => v.idioma).filter(Boolean),
                          ),
                        ).sort(),
                      ];
                      const sexLabel =
                        audioAiVoiceSexFilter === "Todas"
                          ? "Todas"
                          : audioAiVoiceSexFilter;
                      const langLabel =
                        audioAiVoiceLanguageFilter === "Todas"
                          ? "Todos"
                          : audioAiVoiceLanguageFilter;
                      return (
                        <>
                          {/* Género */}
                          <div className="relative flex-1">
                            <button
                              type="button"
                              onClick={() => {
                                setVoiceSexMenuOpen((o) => !o);
                                setVoiceLangMenuOpen(false);
                              }}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
                            >
                              <div className="flex flex-col items-start leading-tight min-w-0">
                                <span className="text-[10px] text-brand-black/40 uppercase tracking-wide">
                                  Género
                                </span>
                                <span className="text-[13px] font-medium text-brand-black/80 truncate max-w-full">
                                  {sexLabel}
                                </span>
                              </div>
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-neutral-400 shrink-0 transition-transform ${voiceSexMenuOpen ? "rotate-180" : ""}`}
                              />
                            </button>
                            {voiceSexMenuOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setVoiceSexMenuOpen(false)}
                                />
                                <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                                  {sexOptions.map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => {
                                        setAudioAiVoiceSexFilter(opt);
                                        setVoiceSexMenuOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors text-[13px] ${
                                        audioAiVoiceSexFilter === opt
                                          ? "text-brand-primary font-medium"
                                          : "text-brand-black/80"
                                      }`}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          {/* Idioma */}
                          <div className="relative flex-1">
                            <button
                              type="button"
                              onClick={() => {
                                setVoiceLangMenuOpen((o) => !o);
                                setVoiceSexMenuOpen(false);
                              }}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
                            >
                              <div className="flex flex-col items-start leading-tight min-w-0">
                                <span className="text-[10px] text-brand-black/40 uppercase tracking-wide">
                                  Idioma
                                </span>
                                <span className="text-[13px] font-medium text-brand-black/80 truncate max-w-full">
                                  {langLabel}
                                </span>
                              </div>
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-neutral-400 shrink-0 transition-transform ${voiceLangMenuOpen ? "rotate-180" : ""}`}
                              />
                            </button>
                            {voiceLangMenuOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setVoiceLangMenuOpen(false)}
                                />
                                <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                                  {langOptions.map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => {
                                        setAudioAiVoiceLanguageFilter(opt);
                                        setVoiceLangMenuOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors text-[13px] ${
                                        audioAiVoiceLanguageFilter === opt
                                          ? "text-brand-primary font-medium"
                                          : "text-brand-black/80"
                                      }`}
                                    >
                                      {opt === "Todas" ? "Todos" : opt}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Voices list */}
                  <div className="flex-1 overflow-y-auto px-3 pb-3">
                    {audioAiVoicesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                      </div>
                    ) : (
                      (() => {
                        // AND ambos filtros — una voz solo aparece si pasa el de
                        // género Y el de idioma. 'Todas' actúa como "no filtrar
                        // por esa dimensión".
                        const filtered = audioAiVoices.filter((v) => {
                          if (
                            audioAiVoiceSexFilter !== "Todas" &&
                            v.sexo !== audioAiVoiceSexFilter
                          )
                            return false;
                          if (
                            audioAiVoiceLanguageFilter !== "Todas" &&
                            v.idioma !== audioAiVoiceLanguageFilter
                          )
                            return false;
                          return true;
                        });
                        if (filtered.length === 0) {
                          return (
                            <div className="flex items-center justify-center py-12">
                              <p className="text-[13px] text-brand-black/50">
                                No se encontraron voces.
                              </p>
                            </div>
                          );
                        }
                        return (
                          <div className="flex flex-col gap-2">
                            {filtered.map((voice) => {
                              const isSelected = audioAiVoiceId === voice.id;
                              const isPlaying =
                                audioAiVoicePlayingId === voice.id;
                              const shortName = voice.nombre?.includes(" - ")
                                ? voice.nombre.split(" - ")[0]
                                : voice.nombre || "Sin nombre";
                              const desc =
                                voice.descripcion &&
                                !voice.descripcion.includes("SILENT THOUGHT") &&
                                !voice.descripcion.includes("\\n")
                                  ? voice.descripcion.length > 100
                                    ? voice.descripcion.slice(0, 100) + "…"
                                    : voice.descripcion
                                  : "";
                              return (
                                <button
                                  key={voice.id}
                                  type="button"
                                  onClick={() => {
                                    setAudioAiVoiceId(voice.id);
                                    setAudioAiVoiceName(shortName);
                                    if (audioAiVoicePreviewRef.current) {
                                      audioAiVoicePreviewRef.current.pause();
                                      audioAiVoicePreviewRef.current = null;
                                    }
                                    setAudioAiVoicePlayingId(null);
                                    setShowAudioAiVoiceSelector(false);
                                  }}
                                  className={`w-full text-left rounded-[10px] p-3 transition-all ${
                                    isSelected
                                      ? "bg-brand-primary/[0.06] ring-2 ring-brand-primary"
                                      : "bg-white ring-1 ring-black/[0.06] hover:bg-black/[0.02]"
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[14px] font-medium text-brand-black/90 truncate">
                                          {shortName}
                                        </span>
                                        {isSelected && (
                                          <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                                        )}
                                      </div>
                                      {(voice.idioma || voice.acento) && (
                                        <p className="text-[12px] text-[#3C3C43]/40 mt-0.5 flex items-center gap-1.5">
                                          {voice.idioma && (
                                            <span>{voice.idioma}</span>
                                          )}
                                          {voice.idioma && voice.acento && (
                                            <span className="text-[#3C3C43]/25">
                                              ·
                                            </span>
                                          )}
                                          {voice.acento && (
                                            <span>{voice.acento}</span>
                                          )}
                                        </p>
                                      )}
                                      {(voice.sexo || desc) && (
                                        <p className="text-[12px] text-[#3C3C43]/60 mt-1 leading-relaxed line-clamp-2">
                                          {voice.sexo && (
                                            <span className="font-medium">
                                              {voice.sexo}
                                            </span>
                                          )}
                                          {voice.sexo && desc && (
                                            <span> · </span>
                                          )}
                                          {desc}
                                        </p>
                                      )}
                                    </div>
                                    {voice.preview_url && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            audioAiVoicePlayingId === voice.id
                                          ) {
                                            if (
                                              audioAiVoicePreviewRef.current
                                            ) {
                                              audioAiVoicePreviewRef.current.pause();
                                              audioAiVoicePreviewRef.current =
                                                null;
                                            }
                                            setAudioAiVoicePlayingId(null);
                                            return;
                                          }
                                          if (audioAiVoicePreviewRef.current) {
                                            audioAiVoicePreviewRef.current.pause();
                                            audioAiVoicePreviewRef.current =
                                              null;
                                          }
                                          const audio = new Audio(
                                            voice.preview_url,
                                          );
                                          audioAiVoicePreviewRef.current =
                                            audio;
                                          setAudioAiVoicePlayingId(voice.id);
                                          audio
                                            .play()
                                            .catch(() =>
                                              setAudioAiVoicePlayingId(null),
                                            );
                                          audio.addEventListener(
                                            "ended",
                                            () => {
                                              setAudioAiVoicePlayingId(null);
                                              audioAiVoicePreviewRef.current =
                                                null;
                                            },
                                          );
                                        }}
                                        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                          isPlaying
                                            ? "bg-brand-primary text-white"
                                            : "bg-black/[0.05] text-brand-black/50 hover:bg-black/[0.08]"
                                        }`}
                                      >
                                        {isPlaying ? (
                                          <Pause className="w-4 h-4" />
                                        ) : (
                                          <Play className="w-4 h-4 ml-0.5" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()
                    )}
                  </div>
                    </>
                  )}
                    </>
                  )}
                </div>
              </div>

              {/* ── Agent Chatbot Overlay (docked to the right edge) ─────────────
              Two widths while visible:
               • 'sidebar' → fixed 510px rail (matches the left tools panel)
               • 'full'    → covers the whole editor area, respecting any open
                             AI panel on the left (left: 510 when one is open).
              Animation slides the panel in from the right edge. */}
              <div
                className={`absolute top-0 bottom-0 right-0 z-40 overflow-hidden transition-[left] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
                  showAgentChat ? "pointer-events-auto" : "pointer-events-none"
                }`}
                style={(() => {
                  // The voice selector is a 380px secondary sidebar that opens to
                  // the right of the tools panel when picking a voice in ai-audio
                  // or ai-scene (personas). In full mode, the chat overlay would
                  // otherwise cover it — push the chat right when it's open.
                  const voiceSelectorOpen =
                    showAudioAiVoiceSelector &&
                    ((activePanel === "ai-audio" && audioAiType === "voice") ||
                      (activePanel === "ai-scene" && sceneMode === "personas"));
                  if (agentChatMode === "sidebar") {
                    return { left: "calc(100% - 510px)" };
                  }
                  // 76px rail + (510px panel when open) + (380px voice selector when open)
                  const baseLeft = 76 + (activePanel !== "none" ? 510 : 0);
                  return { left: baseLeft + (voiceSelectorOpen ? 380 : 0) };
                })()}
              >
                <div
                  className={`absolute inset-0 flex flex-col bg-surface-card border-l border-black/[0.06] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
                    showAgentChat
                      ? "translate-x-0 opacity-100"
                      : "translate-x-full opacity-0"
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-3 h-12">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full bg-brand-primary shrink-0" />
                      <h3 className="text-[15px] font-semibold text-brand-black/90 truncate">
                        Director de estudio
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="relative">
                        <button
                          onClick={() => setAgentMenuOpen((v) => !v)}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors"
                          aria-label="Menú del chat"
                        >
                          <Menu className="w-4 h-4" />
                        </button>
                        {agentMenuOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => {
                                setAgentMenuOpen(false);
                                setAgentMenuView("menu");
                              }}
                            />
                            {agentMenuView === "menu" ? (
                              <div className="absolute right-0 mt-1 w-48 rounded-lg bg-white border border-black/[0.08] shadow-lg overflow-hidden z-50">
                                <button
                                  onClick={() => {
                                    setAgentMenuOpen(false);
                                    setActivePanel("brand");
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-100 text-left"
                                >
                                  <Palette className="w-3.5 h-3.5" />
                                  Marca
                                </button>
                                <div className="h-px bg-black/[0.06]" />
                                <button
                                  onClick={() => {
                                    void openConsumoView();
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-100 text-left"
                                >
                                  <Sparkle className="w-3.5 h-3.5" />
                                  Consumo
                                </button>
                                <div className="h-px bg-black/[0.06]" />
                                <button
                                  onClick={async () => {
                                    setAgentMenuOpen(false);
                                    setAgentMenuView("menu");
                                    // Reset UI chat state to initial greeting.
                                    setAgentMessages([
                                      {
                                        role: "assistant",
                                        text: AGENT_INITIAL_GREETING,
                                      },
                                    ]);
                                    // Borrar también la memoria estructurada.
                                    conversationRef.current = [];
                                    setActiveAsk(null);
                                    setAskFormState({});
                                    setAskCustomState({});
                                    askResolverRef.current = null;
                                    setAgentInput("");
                                    setAgentAttachments([]);
                                    // Delete persisted history for this project.
                                    if (projectId) {
                                      const { error } = await supabase
                                        .from("agent_messages")
                                        .delete()
                                        .eq("project_id", projectId);
                                      if (error)
                                        console.error(
                                          "delete agent_messages failed",
                                          error,
                                        );
                                    }
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-100 text-left"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Nuevo chat
                                </button>
                              </div>
                            ) : (
                              // Consumo detail — same anchor/width family as the
                              // menu list, just slightly wider so the explanatory
                              // copy reads comfortably without wrapping mid-word.
                              <div className="absolute right-0 mt-1 w-64 rounded-lg bg-white border border-black/[0.08] shadow-lg overflow-hidden z-50">
                                <div className="flex items-center gap-1.5 px-2 pt-2 pb-1">
                                  <button
                                    onClick={() => setAgentMenuView("menu")}
                                    className="w-7 h-7 inline-flex items-center justify-center rounded-md text-brand-black/50 hover:bg-black/[0.06] transition-colors"
                                    aria-label="Volver"
                                  >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                  </button>
                                  <span className="text-[13px] font-medium text-brand-black/80">
                                    Consumo
                                  </span>
                                </div>
                                <div className="h-px bg-black/[0.06]" />
                                <div className="px-3 py-3">
                                  <div className="flex items-center gap-2.5 mb-2.5">
                                    <div className="w-9 h-9 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
                                      <Sparkle className="w-4 h-4 text-brand-primary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      {consumoLoading ? (
                                        <div className="flex items-center gap-1.5 text-neutral-500">
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          <span className="text-[12.5px]">
                                            Calculando…
                                          </span>
                                        </div>
                                      ) : consumoError ? (
                                        <div className="text-[12.5px] text-red-700 leading-snug">
                                          {consumoError}
                                        </div>
                                      ) : (
                                        <div className="flex items-baseline gap-1.5">
                                          <span className="text-[22px] font-semibold text-brand-black tabular-nums leading-none">
                                            {consumoCredits ?? 0}
                                          </span>
                                          <span className="text-[12px] text-neutral-500">
                                            {(consumoCredits ?? 0) === 1
                                              ? "crédito"
                                              : "créditos"}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-[11.5px] text-neutral-500 leading-relaxed">
                                    El director consume créditos en cada
                                    interacción. Las acciones más complejas —
                                    planes con varios pasos, edición de muchos
                                    clips o generaciones encadenadas — gastan
                                    más.
                                  </p>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          setAgentChatMode((m) =>
                            m === "sidebar" ? "full" : "sidebar",
                          )
                        }
                        className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors"
                        aria-label={
                          agentChatMode === "sidebar"
                            ? "Expandir al editor"
                            : "Contraer a sidebar"
                        }
                        title={
                          agentChatMode === "sidebar" ? "Expandir" : "Contraer"
                        }
                      >
                        {agentChatMode === "sidebar" ? (
                          <ChevronsLeft className="w-4 h-4" />
                        ) : (
                          <ChevronsRight className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setShowAgentChat(false)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                        aria-label="Minimizar director"
                        title="Minimizar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="h-px bg-black/[0.08]" />

                  {/* Messages */}
                  <div
                    ref={agentMessagesScrollRef}
                    onWheel={markUserScrollIntent}
                    onTouchMove={markUserScrollIntent}
                    onKeyDown={markUserScrollIntent}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const distanceFromBottom =
                        el.scrollHeight - el.scrollTop - el.clientHeight;
                      const atBottom = distanceFromBottom < 80;
                      setAgentChatAtBottom(atBottom);
                      // Only release the pin if the scroll was actually driven
                      // by the user (wheel/touch/keyboard within the last 250ms).
                      // Programmatic scrolls (our rAF re-anchor, layout shifts,
                      // browser scroll restoration) don't count.
                      const wasUserDriven =
                        Date.now() - userScrollIntentRef.current < 250;
                      if (
                        atBottom &&
                        pinUserToTopRef.current &&
                        wasUserDriven
                      ) {
                        pinUserToTopRef.current = false;
                      }
                    }}
                    className={`flex-1 overflow-y-auto py-4 space-y-3 ${agentChatMode === "sidebar" ? "px-4" : "px-40"}`}
                  >
                    {(() => {
                      let _lastUserIdx = -1;
                      for (let k = agentMessages.length - 1; k >= 0; k--) {
                        if (agentMessages[k].role === "user") {
                          _lastUserIdx = k;
                          break;
                        }
                      }
                      return agentMessages.map((msg, i) => {
                        if (msg.role === "tool") {
                          const ToolIcon = msg.icon;
                          // La tool está "en curso" si es el último mensaje y el
                          // agente sigue trabajando. Solo ahí mostramos el
                          // cronómetro vivo + el hint de "puede tardar" → prueba
                          // de que NO está colgado. Una tool ya superada por otro
                          // mensaje (o al recargar) no tickea.
                          const toolRunning =
                            agentStreaming && i === agentMessages.length - 1;
                          return (
                            <div
                              key={i}
                              className="flex flex-col items-start gap-0.5 animate-in fade-in slide-in-from-top-2 duration-300 ease-out"
                            >
                              <p className="inline-flex items-center gap-2 text-[15px] text-neutral-400">
                                {ToolIcon ? (
                                  <ToolIcon className="w-4 h-4 shrink-0" />
                                ) : null}
                                <span>{msg.text || ""}</span>
                                {toolRunning && msg.startedAt ? (
                                  <ElapsedBadge
                                    startedAt={msg.startedAt}
                                    color="#a3a3a3"
                                  />
                                ) : null}
                              </p>
                              {toolRunning && msg.hint ? (
                                <span className="pl-6 text-[13px] text-neutral-500 italic">
                                  {msg.hint}
                                </span>
                              ) : null}
                            </div>
                          );
                        }
                        if (msg.role === "thinking") {
                          const isExpanded = expandedThinkingIndices.has(i);
                          const isRedacted = !!msg.thinkingRedactedData;
                          // Streaming if no signature/redactedData yet AND it's the
                          // most recent thinking row AND we're still streaming.
                          let lastThinkingIdx = -1;
                          for (let k = agentMessages.length - 1; k >= 0; k--) {
                            if (agentMessages[k].role === "thinking") {
                              lastThinkingIdx = k;
                              break;
                            }
                          }
                          const isStreaming =
                            agentStreaming &&
                            !msg.thinkingSignature &&
                            !msg.thinkingRedactedData &&
                            i === lastThinkingIdx;
                          const label = isRedacted
                            ? "Pensamiento privado"
                            : isStreaming
                              ? "Pensando…"
                              : "Pensó por un momento";
                          const hasContent =
                            !!(msg.text && msg.text.trim()) && !isRedacted;
                          return (
                            <div
                              key={i}
                              className="flex flex-col items-start gap-1 animate-in fade-in slide-in-from-top-2 duration-300 ease-out"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  if (hasContent) toggleThinkingExpanded(i);
                                }}
                                disabled={!hasContent}
                                className={`inline-flex items-center gap-2 text-[15px] text-neutral-400 ${hasContent ? "hover:text-neutral-500 cursor-pointer" : "cursor-default"}`}
                              >
                                <Brain className="w-4 h-4 shrink-0" />
                                <span>{label}</span>
                                {hasContent && (
                                  <ChevronRight
                                    className={`w-4 h-4 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                                  />
                                )}
                              </button>
                              {isExpanded && hasContent && (
                                <p className="pl-6 pr-2 text-[15px] text-neutral-400 whitespace-pre-wrap break-words">
                                  {msg.text}
                                </p>
                              )}
                            </div>
                          );
                        }
                        if (msg.role === "ask") {
                          // Only render the answered summary here; the pending UI
                          // lives in a floating panel above the input field.
                          if (!msg.answered) return null;
                          const questions = msg.questions || [];
                          return (
                            <div
                              key={i}
                              className="flex justify-end animate-in fade-in slide-in-from-top-2 duration-300 ease-out"
                            >
                              <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[15px] leading-relaxed bg-neutral-100 text-brand-black/80 rounded-br-md space-y-2">
                                {questions.map((q) => {
                                  const a = msg.answers?.[q.id];
                                  const val = Array.isArray(a)
                                    ? a.join(", ")
                                    : a || "—";
                                  return (
                                    <div key={q.id}>
                                      <p className="text-[15px] font-semibold text-brand-black/50">
                                        {q.question}
                                      </p>
                                      <p className="text-[15px]">{val}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        if (msg.role === "user") {
                          const canRewind = !!msg.text && !agentStreaming;
                          const isLatestUser = i === _lastUserIdx;
                          return (
                            <div
                              key={i}
                              ref={isLatestUser ? latestUserMsgRef : undefined}
                              className="group flex justify-end !mt-12 !mb-8 animate-in fade-in slide-in-from-top-2 duration-300 ease-out"
                            >
                              <div className="max-w-[85%] flex flex-col items-end gap-1.5">
                                {msg.attachments &&
                                  msg.attachments.length > 0 && (
                                    <div className="flex flex-wrap justify-end gap-1.5">
                                      {msg.attachments.map((att, ai) => (
                                        <img
                                          key={att.assetId || `att-${ai}`}
                                          src={att.url}
                                          alt="Adjunto"
                                          onClick={() =>
                                            setAgentPreviewUrl(att.url)
                                          }
                                          className="w-28 h-28 object-cover rounded-[10px] border border-neutral-200 cursor-zoom-in"
                                        />
                                      ))}
                                    </div>
                                  )}
                                {msg.text && (
                                  <div className="px-3.5 py-2.5 rounded-2xl text-[15px] leading-relaxed space-y-1 bg-neutral-100 text-brand-black/80 rounded-br-md">
                                    {renderMarkdown(msg.text)}
                                  </div>
                                )}
                                {msg.text && (
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        canRewind && requestChatRewind(i)
                                      }
                                      disabled={!canRewind}
                                      title="Volver a este mensaje"
                                      aria-label="Volver a este mensaje"
                                      className="w-7 h-7 inline-flex items-center justify-center rounded-md text-brand-black/45 hover:text-brand-black hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      <RotateCcw className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyChatMessage(i, msg.text || "")
                                      }
                                      title={
                                        chatCopiedIndex === i
                                          ? "Copiado"
                                          : "Copiar"
                                      }
                                      aria-label="Copiar mensaje"
                                      className="w-7 h-7 inline-flex items-center justify-center rounded-md text-brand-black/45 hover:text-brand-black hover:bg-neutral-100 transition-colors"
                                    >
                                      {chatCopiedIndex === i ? (
                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                      ) : (
                                        <Copy className="w-3.5 h-3.5" />
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        // Burbuja de ERROR del turno: texto en rojo + botón
                        // "Reintentar" (solo si es el ÚLTIMO mensaje → nunca
                        // sobre un error viejo del medio del historial).
                        const isErrorBubble = !!msg.error;
                        const canRetry =
                          isErrorBubble && i === agentMessages.length - 1;
                        return (
                          <div
                            key={i}
                            className="flex justify-start animate-in fade-in slide-in-from-top-2 duration-300 ease-out"
                          >
                            <div className="max-w-[95%] text-[15px] leading-relaxed space-y-1">
                              <div
                                className={
                                  isErrorBubble
                                    ? "text-red-600/90"
                                    : "text-brand-black/80"
                                }
                              >
                                {renderMarkdown(msg.text || "")}
                              </div>
                              {canRetry && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (agentStreaming) return;
                                    const snap = lastFailedTurnRef.current;
                                    if (!snap) return;
                                    void sendAgentMessage("", {
                                      resumeApiMessages: snap.apiMessages,
                                    });
                                  }}
                                  disabled={agentStreaming}
                                  className="mt-1 inline-flex items-center gap-1.5 text-[13px] font-medium text-red-600 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                  Reintentar
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    {/* IA loading animation — sits just below the last message.
                  Plays while the agent is generating; paused on frame 0
                  otherwise (see iaLoadingVideoRef effect). */}
                    <div className="flex justify-start !mt-6 !mb-10">
                      <video
                        ref={iaLoadingVideoRef}
                        src="/images/animacion_ia_carga.mp4"
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="w-11 h-11 rounded-full object-cover"
                      />
                    </div>
                    <div ref={agentMessagesEndRef} />
                    {/* Breathing room below the last message — also guarantees
                  the scroll container has enough slack for the "scroll user
                  message to top" behavior to land correctly. */}
                    <div aria-hidden className="shrink-0 h-[40vh]" />
                  </div>

                  {/* Input */}
                  <div
                    className={`shrink-0 pb-6 pt-0 relative ${agentChatMode === "sidebar" ? "px-4" : "px-32"}`}
                  >
                    {/* Floating scroll-to-bottom arrow — only while scrolled away from the bottom. */}
                    <button
                      type="button"
                      aria-label="Ir al final del chat"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollAgentChatToBottom("smooth");
                      }}
                      className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-20 w-9 h-9 rounded-full bg-white border border-neutral-200 shadow-[0_4px_14px_rgba(0,0,0,0.1)] text-brand-black/70 hover:text-brand-black hover:bg-neutral-50 flex items-center justify-center transition-opacity duration-200 ${
                        agentChatAtBottom
                          ? "opacity-0 pointer-events-none"
                          : "opacity-100 pointer-events-auto"
                      }`}
                    >
                      <ChevronDown className="w-4 h-4 pointer-events-none" />
                    </button>
                    <input
                      ref={agentFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (e.target) e.target.value = "";
                        if (!projectId || !companyId) return;
                        files.forEach(async (f) => {
                          const localId = `${Date.now()}-${Math.random()}`;
                          const blobUrl = URL.createObjectURL(f);
                          setAgentAttachments((prev) => [
                            ...prev,
                            { id: localId, url: blobUrl, uploading: true },
                          ]);
                          try {
                            const {
                              data: { user },
                            } = await supabase.auth.getUser();
                            const asset = await uploadProjectAsset({
                              file: f,
                              projectId,
                              companyId,
                              userId: user?.id,
                            });
                            setAgentAttachments((prev) =>
                              prev.map((a) =>
                                a.id === localId
                                  ? {
                                      ...a,
                                      assetId: asset.id,
                                      uploading: false,
                                    }
                                  : a,
                              ),
                            );
                            // Make the image available in the image-gen "Añadir" gallery too.
                            setAssetPreviewUrls((prev) =>
                              prev[asset.id]
                                ? prev
                                : { ...prev, [asset.id]: blobUrl },
                            );
                            setImageGenGalleryAssetIds((prev) =>
                              prev.includes(asset.id)
                                ? prev
                                : [...prev, asset.id],
                            );
                          } catch (err) {
                            console.error(
                              "[VideoEditor] agent attach upload failed:",
                              err,
                            );
                            URL.revokeObjectURL(blobUrl);
                            setAgentAttachments((prev) =>
                              prev.filter((a) => a.id !== localId),
                            );
                          }
                        });
                      }}
                      className="hidden"
                    />
                    <div className="bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 pt-5 pb-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]">
                      {activeAsk ? (
                        (() => {
                          const { questions, currentIndex } = activeAsk;
                          const q = questions[currentIndex];
                          if (!q) return null;
                          const v = askFormState[q.id];
                          const custom = askCustomState[q.id] || "";
                          const total = questions.length;
                          const isLast = currentIndex === total - 1;
                          const isFirst = currentIndex === 0;
                          const currentAnswered =
                            q.type === "text"
                              ? (typeof v === "string" &&
                                  v.trim().length > 0) ||
                                custom.trim().length > 0
                              : q.type === "multi"
                                ? (Array.isArray(v) && v.length > 0) ||
                                  custom.trim().length > 0
                                : (typeof v === "string" && v.length > 0) ||
                                  custom.trim().length > 0;
                          // If the streaming resolver is gone (e.g. the user reloaded
                          // the page mid-ask), format the answers into a plain-text
                          // user message so the agent picks up the conversation again.
                          const sendAsUserMessage = (
                            answers: Record<string, string | string[]>,
                          ) => {
                            const lines: string[] = [];
                            for (const qq of questions) {
                              const a = answers[qq.id];
                              const aStr = Array.isArray(a)
                                ? a.filter(Boolean).join(", ")
                                : a || "";
                              lines.push(
                                `- ${qq.question} → ${aStr || "(omitida)"}`,
                              );
                            }
                            setTimeout(
                              () =>
                                sendAgentMessage(
                                  `Respuestas:\n${lines.join("\n")}`,
                                ),
                              0,
                            );
                          };
                          const finishSession = (mode: "submit" | "cancel") => {
                            if (mode === "submit") {
                              const answers: Record<string, string | string[]> =
                                {};
                              for (const qq of questions) {
                                const vv = askFormState[qq.id];
                                const cc = (askCustomState[qq.id] || "").trim();
                                if (qq.type === "multi") {
                                  const arr = Array.isArray(vv) ? [...vv] : [];
                                  if (cc) arr.push(cc);
                                  answers[qq.id] = arr;
                                } else {
                                  answers[qq.id] =
                                    cc || (typeof vv === "string" ? vv : "");
                                }
                              }
                              setAgentMessages((prev) => [
                                ...prev,
                                {
                                  role: "ask",
                                  questions,
                                  answers,
                                  answered: true,
                                },
                              ]);
                              setTimeout(
                                () =>
                                  agentMessagesEndRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                  }),
                                30,
                              );
                              const resolver = askResolverRef.current;
                              askResolverRef.current = null;
                              setActiveAsk(null);
                              setAskFormState({});
                              setAskCustomState({});
                              if (resolver) resolver(JSON.stringify(answers));
                              else sendAsUserMessage(answers);
                            } else {
                              setAgentMessages((prev) => [
                                ...prev,
                                {
                                  role: "tool",
                                  text: "Preferí responder en texto libre",
                                  icon: Hand,
                                },
                              ]);
                              const resolver = askResolverRef.current;
                              askResolverRef.current = null;
                              setActiveAsk(null);
                              setAskFormState({});
                              setAskCustomState({});
                              resolver?.(
                                JSON.stringify({
                                  cancelled: true,
                                  reason: "user_prefers_free_text",
                                }),
                              );
                            }
                          };
                          const goNext = () => {
                            if (!currentAnswered) return;
                            if (isLast) finishSession("submit");
                            else
                              setActiveAsk({
                                questions,
                                currentIndex: currentIndex + 1,
                              });
                          };
                          const goBack = () => {
                            if (isFirst) return;
                            setActiveAsk({
                              questions,
                              currentIndex: currentIndex - 1,
                            });
                          };
                          // For single-choice questions, clicking an option auto-advances
                          // to the next question (or submits if last). For multi, clicking
                          // toggles selection and the user advances via the button.
                          const selectSingle = (opt: string) => {
                            setAskFormState((prev) => ({
                              ...prev,
                              [q.id]: opt,
                            }));
                            if (isLast) {
                              // Build answers including this just-selected opt
                              const answers: Record<string, string | string[]> =
                                {};
                              for (const qq of questions) {
                                if (qq.id === q.id) {
                                  answers[qq.id] = opt;
                                  continue;
                                }
                                const vv = askFormState[qq.id];
                                const cc = (askCustomState[qq.id] || "").trim();
                                if (qq.type === "multi") {
                                  const arr = Array.isArray(vv) ? [...vv] : [];
                                  if (cc) arr.push(cc);
                                  answers[qq.id] = arr;
                                } else {
                                  answers[qq.id] =
                                    cc || (typeof vv === "string" ? vv : "");
                                }
                              }
                              setAgentMessages((prev) => [
                                ...prev,
                                {
                                  role: "ask",
                                  questions,
                                  answers,
                                  answered: true,
                                },
                              ]);
                              setTimeout(
                                () =>
                                  agentMessagesEndRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                  }),
                                30,
                              );
                              const resolver = askResolverRef.current;
                              askResolverRef.current = null;
                              setActiveAsk(null);
                              setAskFormState({});
                              setAskCustomState({});
                              if (resolver) resolver(JSON.stringify(answers));
                              else sendAsUserMessage(answers);
                            } else {
                              setActiveAsk({
                                questions,
                                currentIndex: currentIndex + 1,
                              });
                            }
                          };
                          const skipCurrent = () => {
                            // "Omitir" only skips the current question (keeping any
                            // values the user may have partially entered — since
                            // askFormState/askCustomState are not cleared here).
                            // On the last question it submits whatever has been
                            // collected so far.
                            if (isLast) finishSession("submit");
                            else
                              setActiveAsk({
                                questions,
                                currentIndex: currentIndex + 1,
                              });
                          };
                          return (
                            <div className="-mx-5 -mt-5 -mb-3">
                              {/* Header: question + nav + close */}
                              <div className="flex items-start gap-3 px-5 py-3">
                                <p className="flex-1 min-w-0 text-[15px] font-medium text-brand-black/90 leading-snug break-words">
                                  {q.question}
                                </p>
                                <div className="flex items-center gap-1 text-brand-black/50 shrink-0 mt-0.5">
                                  <button
                                    type="button"
                                    onClick={goBack}
                                    disabled={isFirst}
                                    aria-label="Pregunta anterior"
                                    className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-black/[0.05] hover:text-brand-black/80 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                  >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                  </button>
                                  <span className="text-[13px] font-medium tabular-nums select-none">
                                    {currentIndex + 1} de {total}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!isLast)
                                        setActiveAsk({
                                          questions,
                                          currentIndex: currentIndex + 1,
                                        });
                                    }}
                                    disabled={isLast}
                                    aria-label="Pregunta siguiente"
                                    className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-black/[0.05] hover:text-brand-black/80 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                  >
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => finishSession("cancel")}
                                  aria-label="Cerrar"
                                  className="w-6 h-6 inline-flex items-center justify-center rounded text-brand-black/50 hover:bg-black/[0.05] hover:text-brand-black/80 transition-colors"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              {/* Options list (single/multi).
                          single → numbered circles, click auto-advances.
                          multi → checkbox squares, click toggles; user hits Siguiente. */}
                              {(q.type === "single" || q.type === "multi") &&
                                (q.options || []).length > 0 && (
                                  <div>
                                    {(q.options || []).map((opt, idx) => {
                                      const arr = Array.isArray(v) ? v : [];
                                      const selected =
                                        q.type === "multi"
                                          ? arr.includes(opt)
                                          : v === opt;
                                      return (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() => {
                                            if (q.type === "multi") {
                                              setAskFormState((prev) => {
                                                const prevArr = Array.isArray(
                                                  prev[q.id],
                                                )
                                                  ? (prev[q.id] as string[])
                                                  : [];
                                                const nextArr =
                                                  prevArr.includes(opt)
                                                    ? prevArr.filter(
                                                        (x) => x !== opt,
                                                      )
                                                    : [...prevArr, opt];
                                                return {
                                                  ...prev,
                                                  [q.id]: nextArr,
                                                };
                                              });
                                            } else {
                                              selectSingle(opt);
                                            }
                                          }}
                                          className={`group w-full flex items-start gap-3 px-5 py-3 text-left border-t border-black/[0.06] transition-colors ${
                                            selected
                                              ? "bg-brand-primary text-white"
                                              : "hover:bg-black/[0.03]"
                                          }`}
                                        >
                                          {q.type === "multi" ? (
                                            <span
                                              className={`shrink-0 w-5 h-5 inline-flex items-center justify-center rounded-[5px] border transition-colors ${
                                                selected
                                                  ? "bg-white border-white text-brand-primary"
                                                  : "bg-white border-black/[0.2] text-transparent group-hover:border-black/[0.35]"
                                              }`}
                                            >
                                              <Check
                                                className="w-3 h-3"
                                                strokeWidth={3}
                                              />
                                            </span>
                                          ) : (
                                            <span
                                              className={`shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full text-[12px] font-semibold tabular-nums transition-colors ${
                                                selected
                                                  ? "bg-white/20 text-white"
                                                  : "bg-black/[0.05] text-brand-black/50 group-hover:text-brand-black/70"
                                              }`}
                                            >
                                              {idx + 1}
                                            </span>
                                          )}
                                          <span
                                            className={`flex-1 min-w-0 text-[15px] font-medium leading-snug whitespace-normal break-words self-center ${selected ? "text-white" : "text-brand-black/80"}`}
                                          >
                                            {opt}
                                          </span>
                                          {q.type === "single" && (
                                            <ArrowRight
                                              className={`shrink-0 w-4 h-4 transition-opacity ${
                                                selected
                                                  ? "text-white opacity-100"
                                                  : "text-brand-black/40 opacity-0 group-hover:opacity-100"
                                              }`}
                                            />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                              {/* Bottom row: "Algo más" custom input + Omitir */}
                              <div className="flex items-center gap-3 px-5 py-3 border-t border-black/[0.06]">
                                <span className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-black/[0.05] text-brand-black/50">
                                  <Pencil className="w-3 h-3" />
                                </span>
                                {q.type === "text" ||
                                q.allowCustom !== false ? (
                                  <input
                                    type="text"
                                    value={custom}
                                    onChange={(e) =>
                                      setAskCustomState((prev) => ({
                                        ...prev,
                                        [q.id]: e.target.value,
                                      }))
                                    }
                                    placeholder={
                                      q.type === "text"
                                        ? "Escribí tu respuesta…"
                                        : "Algo más"
                                    }
                                    className="flex-1 min-w-0 text-[15px] font-medium text-brand-black/80 placeholder:text-brand-black/40 bg-transparent border-none outline-none focus:ring-0 p-0"
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Enter" &&
                                        currentAnswered
                                      ) {
                                        e.preventDefault();
                                        goNext();
                                      }
                                    }}
                                    autoFocus={q.type === "text"}
                                  />
                                ) : (
                                  <span className="flex-1 min-w-0 text-[15px] text-brand-black/30">
                                    Algo más
                                  </span>
                                )}
                                {(() => {
                                  const label = isLast
                                    ? "Enviar"
                                    : currentAnswered
                                      ? "Siguiente"
                                      : "Omitir";
                                  const solid = currentAnswered || isLast;
                                  return (
                                    <button
                                      type="button"
                                      onClick={
                                        currentAnswered ? goNext : skipCurrent
                                      }
                                      className={`shrink-0 py-1.5 px-3.5 rounded-md text-[13px] font-medium transition-colors ${
                                        solid
                                          ? "bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm"
                                          : "bg-white border border-black/[0.12] text-brand-black/70 hover:bg-black/[0.03]"
                                      }`}
                                    >
                                      {label}
                                    </button>
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          {agentAttachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {agentAttachments.map((att) => (
                                <div
                                  key={`agent-att-${att.id}`}
                                  onClick={() =>
                                    !att.uploading &&
                                    setAgentPreviewUrl(att.url)
                                  }
                                  className="relative w-14 h-14 rounded-[8px] overflow-hidden border border-neutral-200 bg-neutral-100 group cursor-zoom-in"
                                >
                                  <img
                                    src={att.url}
                                    alt="Adjunto"
                                    className="w-full h-full object-cover pointer-events-none"
                                  />
                                  {att.uploading && (
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAgentAttachments((prev) =>
                                        prev.filter((a) => a.id !== att.id),
                                      );
                                    }}
                                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                                  >
                                    <Plus className="w-2.5 h-2.5 text-white rotate-45" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <textarea
                            ref={agentInputRef}
                            value={agentInput}
                            onChange={(e) => setAgentInput(e.target.value)}
                            onKeyDown={(e) => {
                              // Enter envía; Shift+Enter = salto de línea. Enter
                              // SOLO nunca hace salto (o envía, o no hace nada).
                              if (e.key !== "Enter" || e.shiftKey) return;
                              e.preventDefault();
                              const anyUploading = agentAttachments.some(
                                (a) => a.uploading,
                              );
                              if (
                                agentInput.trim() &&
                                !agentStreaming &&
                                !anyUploading
                              ) {
                                sendAgentMessage(agentInput);
                              }
                            }}
                            rows={1}
                            placeholder="Escribí un mensaje..."
                            className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[44px] max-h-[200px] overflow-y-auto mb-2"
                          />
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  agentFileInputRef.current?.click()
                                }
                                className="py-2 px-2 inline-flex items-center gap-1.5 text-[13px] text-brand-black/60 hover:text-brand-black/90 transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Añadir
                              </button>
                            </div>
                            {agentStreaming ? (
                              <button
                                onClick={stopAgentTurn}
                                aria-label="Detener"
                                title="Detener"
                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm transition-colors"
                              >
                                <Square className="w-3.5 h-3.5 fill-current" />
                              </button>
                            ) : (
                              <button
                                onClick={() => sendAgentMessage(agentInput)}
                                disabled={
                                  !agentInput.trim() ||
                                  agentAttachments.some((a) => a.uploading)
                                }
                                aria-label="Enviar"
                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ArrowUp className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── Enlarged preview overlay for agent-chat images ── */}
                  {agentPreviewUrl && (
                    <div
                      className="absolute inset-0 z-30 flex items-center justify-center p-6 bg-white/30 backdrop-blur-[4px] animate-in fade-in duration-150"
                      onClick={() => setAgentPreviewUrl(null)}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAgentPreviewUrl(null);
                        }}
                        className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                      >
                        <Plus className="w-4 h-4 rotate-45" />
                      </button>
                      <img
                        src={agentPreviewUrl}
                        alt="Vista ampliada"
                        onClick={(e) => e.stopPropagation()}
                        className="max-w-full max-h-full object-contain rounded-[12px] shadow-2xl"
                      />
                    </div>
                  )}

                  {/* ── Rewind-to-message confirmation modal ───────────────────── */}
                  {chatRewindConfirm && (
                    <div
                      className="absolute inset-0 z-40 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
                      onClick={() => setChatRewindConfirm(null)}
                    >
                      <div
                        className="bg-surface-card rounded-lg shadow-xl border border-neutral-200 max-w-sm w-full p-5 animate-in zoom-in-95 fade-in duration-150"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-[17px] font-semibold text-brand-black">
                            Volver a este mensaje
                          </h3>
                          <button
                            onClick={() => setChatRewindConfirm(null)}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                            aria-label="Cerrar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mb-6">
                          <div className="w-10 h-10 rounded-full bg-error-soft flex items-center justify-center mx-auto mb-3">
                            <AlertCircle className="w-5 h-5 text-error" />
                          </div>
                          <p className="text-[14px] text-neutral-600 text-center leading-relaxed">
                            Se eliminarán{" "}
                            <span className="font-semibold text-neutral-900">
                              {chatRewindConfirm.deleteCount} mensaje
                              {chatRewindConfirm.deleteCount === 1 ? "" : "s"}
                            </span>{" "}
                            del chat actual y vas a poder editar y reenviar tu
                            mensaje. Esta acción no se puede deshacer.
                          </p>
                        </div>
                        <div className="flex justify-end gap-2.5">
                          <button
                            type="button"
                            onClick={() => setChatRewindConfirm(null)}
                            className="h-10 px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[14px] shadow-sm transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={confirmChatRewind}
                            className="h-10 px-5 inline-flex items-center justify-center gap-2 bg-error hover:bg-red-600 text-white rounded-md font-medium text-[14px] shadow-sm transition-colors"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Volver
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Center: Toolbar + Preview + Timeline ─────────────────────── */}
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* ── Horizontal Toolbar (above preview, aligned with the panel
                header so it shifts right when a tool panel is open) ───── */}
                <div className="flex items-center justify-between h-11 px-4 border-b border-black/[0.06] bg-surface-card shrink-0">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={undo}
                      disabled={!canUndo}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-brand-black/40 hover:bg-neutral-100 hover:text-brand-black/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brand-black/40"
                      title="Deshacer (⌘Z)"
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={redo}
                      disabled={!canRedo}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-brand-black/40 hover:bg-neutral-100 hover:text-brand-black/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brand-black/40"
                      title="Rehacer (⌘⇧Z)"
                    >
                      <Redo2 className="w-4 h-4" />
                    </button>
                    <div className="w-px h-5 bg-neutral-200 mx-1" />
                    <button
                      onClick={() => navigate("/editor-video")}
                      className="h-8 px-3 flex items-center gap-1.5 rounded-md text-[13px] font-medium text-brand-black/60 hover:bg-neutral-100 transition-colors"
                    >
                      <FolderOpen className="w-4 h-4" />
                      Proyectos
                    </button>
                    <div className="w-px h-5 bg-neutral-200 mx-1" />
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          (e.target as HTMLInputElement).blur();
                      }}
                      size={Math.max(10, Math.min(projectName.length + 1, 60))}
                      className="text-[13px] font-medium text-brand-black/90 bg-transparent border-none outline-none rounded px-1 -mx-1 min-w-[80px] max-w-[520px] hover:bg-neutral-100 focus:bg-white focus:ring-1 focus:ring-brand-primary/30 transition-colors"
                      spellCheck={false}
                    />
                    {/* Brand badge: shows the active brand and exposes the same
                    switcher dropdown found in the Marca panel header so the
                    user can hop between brands or create a new one without
                    opening the side panel. */}
                    {brandProfile && (
                      <div className="relative ml-1">
                        <button
                          type="button"
                          onClick={() => setBrandBadgeOpen((o) => !o)}
                          className="h-7 inline-flex items-center px-2.5 rounded-full border border-black/[0.08] bg-neutral-50 hover:bg-neutral-100 text-[12px] font-medium text-brand-black/75 transition-colors max-w-[200px]"
                          aria-expanded={brandBadgeOpen}
                          aria-label="Cambiar marca"
                        >
                          <span className="truncate">{brandProfile.name}</span>
                        </button>
                        {brandBadgeOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-30"
                              onClick={() => setBrandBadgeOpen(false)}
                              aria-hidden
                            />
                            <div className="absolute left-0 top-full mt-1 z-40 w-56 rounded-lg bg-white border border-black/[0.08] shadow-[0_8px_24px_rgba(0,0,0,0.08)] overflow-hidden">
                              <div className="py-1 max-h-64 overflow-y-auto">
                                {companyBrands.length === 0 ? (
                                  <div className="px-3 py-2 text-[12px] text-brand-black/40">
                                    Sin otras marcas
                                  </div>
                                ) : (
                                  companyBrands.map((b) => {
                                    const active = b.id === brandProfile.id;
                                    return (
                                      <button
                                        key={b.id}
                                        type="button"
                                        onClick={() => {
                                          setBrandBadgeOpen(false);
                                          void switchBrand(b.id);
                                        }}
                                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-left transition-colors ${active ? "bg-black/[0.04] text-brand-black font-medium" : "text-brand-black/85 hover:bg-black/[0.04]"}`}
                                      >
                                        <span className="truncate">
                                          {b.name}
                                        </span>
                                        {active && (
                                          <span className="text-[11px] uppercase tracking-wide shrink-0 text-brand-black/40">
                                            Actual
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                              <div className="h-px bg-black/[0.06] my-1" />
                              <button
                                type="button"
                                onClick={() => {
                                  setBrandBadgeOpen(false);
                                  void createBrandAndAssign();
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-brand-black/85 hover:bg-black/[0.04] transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Nueva marca
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-tour="export"
                      onClick={() => setExportMenuOpen(true)}
                      className="py-2 px-4 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Exportar
                    </button>
                  </div>
                </div>

                {/* ── Preview area ─────────────────────────────────────────────── */}
                <div className="flex-1 flex items-stretch justify-center bg-brand-black/[0.03] min-h-0 relative px-4 pt-12 pb-4">
                  {/* Floating Director button — sits just below the Exportar
                  button in the topbar, reuses the DirectorButton design.
                  Hidden while the chat is open (the chat header has its own
                  close control) so it doesn't slip under the sidebar overlay. */}
                  {!showAgentChat && (
                    <div
                      ref={directorBtnRef}
                      data-tour="director"
                      className="absolute top-3 right-3 z-20 bg-white rounded-md shadow-sm"
                    >
                      <DirectorButton
                        showAgentChat={false}
                        busy={agentStreaming || !!activeAsk}
                        onClick={() => setShowAgentChat(true)}
                      />
                    </div>
                  )}
                  {/* Clip action toolbar (absolute, overlays the preview area) */}
                  {selectedClip &&
                    (() => {
                      // Counter for label-collapsing. Buttons drop their labels
                      // starting from the RIGHT edge (least-important actions
                      // like Eliminar/Descargar/Duplicar) toward the left as the
                      // toolbar runs out of horizontal room.
                      let _ci = 0;
                      const tbBtn = (opts: {
                        onClick?: () => void;
                        disabled?: boolean;
                        title?: string;
                        danger?: boolean;
                        icon: React.ReactNode;
                        label: string;
                      }) => {
                        const idx = _ci++;
                        // Distance from the right edge: 0 = rightmost button.
                        const fromRight = toolbarBtnCount - 1 - idx;
                        const iconOnly = fromRight < toolbarCollapsed;
                        const colors = opts.danger
                          ? "text-red-600 hover:bg-red-50"
                          : "text-neutral-700 hover:bg-neutral-100";
                        return (
                          <button
                            onClick={opts.onClick}
                            disabled={opts.disabled}
                            title={opts.title}
                            className={`h-8 flex items-center rounded-md text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${colors} ${iconOnly ? "w-8 justify-center" : "px-2.5 gap-1.5"}`}
                          >
                            {opts.icon}
                            <span
                              data-clip-tb-label
                              className={iconOnly ? "hidden" : ""}
                            >
                              {opts.label}
                            </span>
                          </button>
                        );
                      };
                      return (
                        <div
                          data-clip-ui="true"
                          ref={clipToolbarRef}
                          className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-0.5 bg-white border border-neutral-200 rounded-lg shadow-sm px-1.5 py-1 whitespace-nowrap"
                        >
                          {/* 1. Editar (AI clip or AI-audio) */}
                          {selectedClip.type === "ai-generated" && (
                            <>
                              {tbBtn({
                                onClick: () => editAiClip(selectedClip.id),
                                title: "Editar clip IA",
                                icon: <Pencil className="w-3.5 h-3.5" />,
                                label: "Editar",
                              })}
                              <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                            </>
                          )}
                          {selectedClip.type === "animation" && (
                            <>
                              {tbBtn({
                                onClick: () => setActivePanel("animation"),
                                title: "Editar animación",
                                icon: <Pencil className="w-3.5 h-3.5" />,
                                label: "Editar",
                              })}
                              <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                            </>
                          )}
                          {selectedClip.type === "audio" &&
                            selectedClip.aiPrompt && (
                              <>
                                {tbBtn({
                                  onClick: () =>
                                    editAiAudioClip(selectedClip.id),
                                  title: "Editar clip de audio IA",
                                  icon: <Pencil className="w-3.5 h-3.5" />,
                                  label: "Editar",
                                })}
                                <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                              </>
                            )}

                          {/* 1b. "IA ▾" — agrupa las acciones de IA sobre el
                          clip (Editar con IA · Borrar objeto · Traducir ·
                          Subtítulos) en un dropdown para descongestionar la
                          barra. Cada ítem conserva su condición de visibilidad,
                          su estado (cargando/deshabilitado) y su tooltip
                          originales. Ver ADR-500. */}
                          {(() => {
                            const isVideo = selectedClip.type === "video";
                            const isAiReady =
                              selectedClip.type === "ai-generated" &&
                              selectedClip.aiStatus === "ready";
                            const videoOrReady = isVideo || isAiReady;
                            const iaItems: Array<{
                              label: string;
                              icon: React.ReactNode;
                              onClick: () => void;
                              disabled?: boolean;
                              title?: string;
                              note?: string;
                            }> = [];
                            // Editar con IA (Gemini Omni) — clips de video/IA ≤10s.
                            if (
                              (selectedClip.type === "video" ||
                                selectedClip.type === "ai-generated") &&
                              (selectedClip.type !== "ai-generated" ||
                                selectedClip.aiStatus === "ready")
                            ) {
                              const tooLong = selectedClip.duration > 10;
                              const notReady = !selectedClip.src;
                              iaItems.push({
                                label: "Editar con IA",
                                icon: <Wand2 className="w-4 h-4" />,
                                disabled: tooLong || notReady,
                                note: tooLong ? "Máx. 10s" : undefined,
                                title: tooLong
                                  ? "El video es muy largo para editarlo (máx. 10s)"
                                  : notReady
                                    ? "El clip todavía no está listo"
                                    : "Editar el clip con IA",
                                onClick: () => {
                                  setOmniEditClipId(selectedClip.id);
                                  setActivePanel("edit-video-ia");
                                },
                              });
                            }
                            // Borrar objeto (pincel SAM2) — solo video/IA.
                            if (
                              selectedClip.type === "video" ||
                              selectedClip.type === "ai-generated"
                            ) {
                              iaItems.push({
                                label: "Borrar objeto",
                                icon: <Eraser className="w-4 h-4" />,
                                title: "Borrado inteligente",
                                onClick: () =>
                                  selectedClipId &&
                                  openEraseBrush(selectedClipId),
                              });
                            }
                            // Traducir al inglés (HeyGen) — video/IA lista.
                            if (videoOrReady) {
                              const cost = creditsForVideoTranslate({
                                durationSec: selectedClip.duration,
                              });
                              const translating = !!selectedClip.aiTranslating;
                              const errMsg = selectedClip.aiTranslateError;
                              iaItems.push({
                                label: translating
                                  ? "Traduciendo…"
                                  : "Traducir al inglés",
                                icon: translating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Languages className="w-4 h-4" />
                                ),
                                disabled: translating,
                                // Sin "N cr" en el botón: el costo se muestra en
                                // el modal de confirmación de traducción.
                                note: undefined,
                                title: translating
                                  ? "Traduciendo al inglés… (puede tardar varios minutos)"
                                  : errMsg
                                    ? `Error: ${errMsg} — clickeá para reintentar (cuesta ${cost} créditos)`
                                    : `Doblar el clip al inglés con lip-sync. Cuesta ${cost} créditos.`,
                                onClick: () =>
                                  setTranslateConfirmClipId(selectedClip.id),
                              });
                            }
                            // Mejorar calidad (upscale con Topaz) — video/IA lista.
                            if (videoOrReady) {
                              const notReady = !selectedClip.src;
                              iaItems.push({
                                label: "Mejorar calidad",
                                icon: <Sparkles className="w-4 h-4" />,
                                disabled: notReady,
                                title: notReady
                                  ? "El clip todavía no está listo"
                                  : "Aumentar la resolución y nitidez del clip con IA (Topaz).",
                                onClick: () => {
                                  setUpscaleClipId(selectedClip.id);
                                  setActivePanel("upscale-video");
                                },
                              });
                            }
                            // Subtítulos — video/audio/IA lista.
                            if (
                              selectedClip.type === "video" ||
                              selectedClip.type === "audio" ||
                              isAiReady
                            ) {
                              const loading =
                                subtitlesLoading &&
                                subtitlesSourceClipId === selectedClip.id;
                              iaItems.push({
                                label: "Subtítulos",
                                icon: loading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Captions className="w-4 h-4" />
                                ),
                                disabled: subtitlesLoading,
                                title:
                                  "Transcribir este clip y agregar subtítulos al timeline",
                                onClick: () =>
                                  openSubtitlesModalForClip(selectedClip.id),
                              });
                            }
                            // Cambiar voz — audio (S2S directo) y video/IA lista
                            // (extrae audio + S2S + re-muxea en el backend). Se
                            // muestra en clips que pueden tener a alguien hablando.
                            if (
                              selectedClip.type === "audio" ||
                              selectedClip.type === "video" ||
                              isAiReady
                            ) {
                              const changing =
                                selectedClip.voiceChangeStatus === "processing";
                              iaItems.push({
                                label: changing
                                  ? "Cambiando voz…"
                                  : "Cambiar voz",
                                icon: changing ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mic className="w-4 h-4" />
                                ),
                                disabled: changing,
                                title:
                                  "Cambiar la voz por una voz IA (mantiene el ritmo y la entonación)",
                                onClick: () => {
                                  setChangeVoiceClipId(selectedClip.id);
                                  setShowClipVoicePicker(true);
                                },
                              });
                            }
                            if (iaItems.length === 0) return null;
                            return (
                              <>
                                <div className="relative">
                                  {tbBtn({
                                    onClick: () =>
                                      setClipIaMenuOpen((v) => !v),
                                    title: "Acciones de IA",
                                    icon: <Wand2 className="w-3.5 h-3.5" />,
                                    label: "IA",
                                  })}
                                  {clipIaMenuOpen && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-20"
                                        onClick={() => setClipIaMenuOpen(false)}
                                      />
                                      <div className="absolute top-full left-0 mt-1 z-30 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg py-1">
                                        {iaItems.map((it, i) => (
                                          <button
                                            key={i}
                                            disabled={it.disabled}
                                            title={it.title}
                                            onClick={() => {
                                              setClipIaMenuOpen(false);
                                              it.onClick();
                                            }}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-neutral-700 hover:bg-neutral-100 text-left disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                          >
                                            {it.icon}
                                            <span className="flex-1">
                                              {it.label}
                                            </span>
                                            {it.note && (
                                              <span className="text-[11px] text-neutral-400">
                                                {it.note}
                                              </span>
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                              </>
                            );
                          })()}

                          {/* 2a. Controles de texto (sólo cuando hay un text clip
                          seleccionado). Diseño "pill" inspirado en editores
                          tipo Notion/Canva: duración → fuente → tamaño →
                          color → estilos → alineación. */}
                          {selectedClip.type === "text" &&
                            (() => {
                              const ft = selectedClip;
                              const patchText = (p: Partial<TimelineClip>) => {
                                const namePatch =
                                  typeof p.text === "string"
                                    ? {
                                        name:
                                          p.text.length > 20
                                            ? p.text.slice(0, 17) + "…"
                                            : p.text || "Texto",
                                      }
                                    : {};
                                setClips((prev) =>
                                  patchClipsWithSubtitleSync(prev, ft.id, {
                                    ...p,
                                    ...namePatch,
                                  }),
                                );
                              };
                              const curFont =
                                ft.fontFamily ?? DEFAULT_TEXT_CLIP.fontFamily;
                              const curSize =
                                ft.fontSizePct ?? DEFAULT_TEXT_CLIP.fontSizePct;
                              const curColor =
                                ft.textColor ?? DEFAULT_TEXT_CLIP.textColor;
                              const curWeight = (ft.fontWeight ??
                                DEFAULT_TEXT_CLIP.fontWeight) as
                                | "normal"
                                | "bold";
                              const curStyle = (ft.fontStyle ??
                                DEFAULT_TEXT_CLIP.fontStyle) as
                                | "normal"
                                | "italic";
                              const curAlign = (ft.textAlign ??
                                DEFAULT_TEXT_CLIP.textAlign) as
                                | "left"
                                | "center"
                                | "right";
                              const stepSize = (delta: number) => {
                                const next = Math.max(
                                  2,
                                  Math.min(
                                    30,
                                    Math.round((curSize + delta) * 2) / 2,
                                  ),
                                );
                                patchText({ fontSizePct: next });
                              };
                              return (
                                <>
                                  {/* Duración — click abre popover con stepper */}
                                  <div className="relative">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setTextToolbarPopover((p) =>
                                          p === "duration" ? null : "duration",
                                        )
                                      }
                                      className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md text-[13px] font-medium transition-colors ${
                                        textToolbarPopover === "duration"
                                          ? "bg-neutral-100 text-neutral-900"
                                          : "text-neutral-700 hover:bg-neutral-100"
                                      }`}
                                      title="Duración del clip (click para editar)"
                                    >
                                      <Clock className="w-3.5 h-3.5 text-neutral-500" />
                                      <span className="tabular-nums">
                                        {ft.duration.toFixed(1)}s
                                      </span>
                                    </button>
                                    {textToolbarPopover === "duration" && (
                                      <div
                                        data-clip-ui="true"
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-neutral-200 rounded-lg shadow-md p-2 flex items-center gap-1"
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = Math.max(
                                              0.5,
                                              Math.round(
                                                (ft.duration - 0.5) * 10,
                                              ) / 10,
                                            );
                                            patchText({ duration: next });
                                          }}
                                          className="h-8 w-8 flex items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 transition-colors"
                                          title="Reducir 0.5s"
                                        >
                                          −
                                        </button>
                                        <NumberField
                                          value={ft.duration}
                                          min={0.5}
                                          max={60}
                                          step={0.1}
                                          decimals={1}
                                          onCommit={(n) =>
                                            patchText({ duration: n })
                                          }
                                          className="w-16 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-neutral-50 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                          ariaLabel="Duración en segundos"
                                        />
                                        <span className="text-[12px] text-neutral-500 px-0.5">
                                          s
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = Math.min(
                                              60,
                                              Math.round(
                                                (ft.duration + 0.5) * 10,
                                              ) / 10,
                                            );
                                            patchText({ duration: next });
                                          }}
                                          className="h-8 w-8 flex items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 transition-colors"
                                          title="Aumentar 0.5s"
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  <div className="w-px h-5 bg-neutral-200 mx-0.5" />

                                  {/* Fuente — pill de ancho fijo para que el toolbar no
                            salte al cambiar de fuente seleccionada. Usa el
                            FontPicker custom para que cada opción se vea
                            renderizada con su propia tipografía. */}
                                  <FontPicker
                                    value={curFont}
                                    onChange={(fam) =>
                                      patchText({ fontFamily: fam })
                                    }
                                    triggerClassName="h-8 w-[150px] pl-2.5 pr-1.5 flex items-center justify-between gap-1 rounded-md border border-neutral-200 bg-white text-[13px] text-neutral-800 hover:bg-neutral-50 transition-colors"
                                    panelAlign="left"
                                  />

                                  {/* Tamaño — pill con − [n] + */}
                                  <div className="ml-1 h-8 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => stepSize(-0.5)}
                                      className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                      title="Reducir tamaño"
                                    >
                                      −
                                    </button>
                                    <NumberField
                                      value={curSize}
                                      min={2}
                                      max={30}
                                      step={0.5}
                                      decimals={1}
                                      onCommit={(n) =>
                                        patchText({ fontSizePct: n })
                                      }
                                      className="w-10 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                      ariaLabel="Tamaño"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => stepSize(0.5)}
                                      className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                      title="Aumentar tamaño"
                                    >
                                      +
                                    </button>
                                  </div>

                                  {/* Color — "A" con barra de color debajo. Click abre
                            un popover con swatches predefinidos + botón "+"
                            para abrir el color picker nativo. */}
                                  <div className="relative ml-1">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setTextToolbarPopover((p) =>
                                          p === "color" ? null : "color",
                                        )
                                      }
                                      className={`h-8 px-2 flex flex-col items-center justify-center rounded-md transition-colors ${
                                        textToolbarPopover === "color"
                                          ? "bg-neutral-100"
                                          : "hover:bg-neutral-100"
                                      }`}
                                      title="Color del texto"
                                    >
                                      <span className="text-[13px] font-semibold leading-none text-neutral-800">
                                        A
                                      </span>
                                      <span
                                        className="mt-0.5 block h-[3px] w-4 rounded-full"
                                        style={{
                                          backgroundColor: curColor,
                                          boxShadow:
                                            "0 0 0 1px rgba(0,0,0,0.06)",
                                        }}
                                      />
                                    </button>
                                    {textToolbarPopover === "color" && (
                                      <div
                                        data-clip-ui="true"
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-neutral-200 rounded-lg shadow-md p-2"
                                        style={{ width: 200 }}
                                      >
                                        {(() => {
                                          // Same brand-color row pattern as ColorSwatchPicker
                                          // — rendered above the generic preset grid only when
                                          // the project has a brand palette.
                                          const seen = new Set<string>();
                                          const cleanBrand = (
                                            brandProfile?.colors || []
                                          )
                                            .map((c) =>
                                              typeof c === "string"
                                                ? c.trim()
                                                : "",
                                            )
                                            .filter((c) =>
                                              /^#?[0-9a-fA-F]{3,8}$/.test(c),
                                            )
                                            .map((c) =>
                                              c.startsWith("#") ? c : `#${c}`,
                                            )
                                            .filter((c) => {
                                              const k = c.toLowerCase();
                                              if (seen.has(k)) return false;
                                              seen.add(k);
                                              return true;
                                            });
                                          if (cleanBrand.length === 0)
                                            return null;
                                          return (
                                            <>
                                              <div className="text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5">
                                                De tu marca
                                              </div>
                                              <div className="flex flex-wrap gap-1.5 mb-2">
                                                {cleanBrand.map((color) => {
                                                  const isActive =
                                                    color.toLowerCase() ===
                                                    curColor.toLowerCase();
                                                  return (
                                                    <button
                                                      key={`brand-${color}`}
                                                      type="button"
                                                      onClick={() => {
                                                        patchText({
                                                          textColor: color,
                                                        });
                                                        setTextToolbarPopover(
                                                          null,
                                                        );
                                                      }}
                                                      className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                                                        isActive
                                                          ? "ring-2 ring-brand-primary ring-offset-1"
                                                          : ""
                                                      }`}
                                                      style={{
                                                        backgroundColor: color,
                                                        boxShadow:
                                                          color.toLowerCase() ===
                                                          "#ffffff"
                                                            ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                                                            : undefined,
                                                      }}
                                                      title={`${color} (marca)`}
                                                      aria-label={`Color de marca ${color}`}
                                                    />
                                                  );
                                                })}
                                              </div>
                                              <div className="h-px bg-neutral-200 -mx-2 mb-2" />
                                              <div className="text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5">
                                                Generales
                                              </div>
                                            </>
                                          );
                                        })()}
                                        <div className="grid grid-cols-6 gap-1.5">
                                          {TEXT_COLOR_PRESETS.map((color) => {
                                            const isActive =
                                              color.toLowerCase() ===
                                              curColor.toLowerCase();
                                            return (
                                              <button
                                                key={color}
                                                type="button"
                                                onClick={() => {
                                                  patchText({
                                                    textColor: color,
                                                  });
                                                  setTextToolbarPopover(null);
                                                }}
                                                className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                                                  isActive
                                                    ? "ring-2 ring-brand-primary ring-offset-1"
                                                    : ""
                                                }`}
                                                style={{
                                                  backgroundColor: color,
                                                  boxShadow:
                                                    color.toLowerCase() ===
                                                    "#ffffff"
                                                      ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                                                      : undefined,
                                                }}
                                                title={color}
                                                aria-label={`Color ${color}`}
                                              />
                                            );
                                          })}
                                          {/* Botón "+" para color personalizado */}
                                          <label
                                            className="w-7 h-7 rounded-md flex items-center justify-center border border-dashed border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 cursor-pointer relative transition-colors"
                                            title="Color personalizado"
                                          >
                                            <Plus className="w-3.5 h-3.5" />
                                            <input
                                              type="color"
                                              value={curColor}
                                              onChange={(e) =>
                                                patchText({
                                                  textColor: e.target.value,
                                                })
                                              }
                                              className="absolute inset-0 opacity-0 cursor-pointer"
                                            />
                                          </label>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Negrita */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      patchText({
                                        fontWeight:
                                          curWeight === "bold"
                                            ? "normal"
                                            : "bold",
                                      })
                                    }
                                    className={`ml-0.5 h-8 w-8 flex items-center justify-center rounded-md text-[14px] font-bold transition-colors ${
                                      curWeight === "bold"
                                        ? "bg-neutral-900 text-white"
                                        : "text-neutral-700 hover:bg-neutral-100"
                                    }`}
                                    title="Negrita"
                                  >
                                    B
                                  </button>

                                  {/* Cursiva */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      patchText({
                                        fontStyle:
                                          curStyle === "italic"
                                            ? "normal"
                                            : "italic",
                                      })
                                    }
                                    className={`h-8 w-8 flex items-center justify-center rounded-md text-[14px] italic font-serif transition-colors ${
                                      curStyle === "italic"
                                        ? "bg-neutral-900 text-white"
                                        : "text-neutral-700 hover:bg-neutral-100"
                                    }`}
                                    title="Cursiva"
                                  >
                                    I
                                  </button>

                                  {/* Alineación — botón que cicla L → C → R */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const order: Array<
                                        "left" | "center" | "right"
                                      > = ["left", "center", "right"];
                                      const next =
                                        order[
                                          (order.indexOf(curAlign) + 1) %
                                            order.length
                                        ];
                                      patchText({ textAlign: next });
                                    }}
                                    className="h-8 w-8 flex items-center justify-center rounded-md text-neutral-700 hover:bg-neutral-100 transition-colors"
                                    title={`Alineación: ${curAlign === "left" ? "izquierda" : curAlign === "center" ? "centrada" : "derecha"}`}
                                  >
                                    {curAlign === "left" && (
                                      <AlignLeft className="w-4 h-4" />
                                    )}
                                    {curAlign === "center" && (
                                      <AlignCenter className="w-4 h-4" />
                                    )}
                                    {curAlign === "right" && (
                                      <AlignRight className="w-4 h-4" />
                                    )}
                                  </button>

                                  {/* Animar — un solo botón en la toolbar que abre
                            un popover con los dos pickers (Entrada y
                            Salida) lado a lado. Mucho más compacto que
                            tener los dos triggers expuestos en la barra,
                            y deja que el popover se ensanche para que
                            cada grid de presets respire. */}
                                  <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                                  <div className="relative">
                                    {(() => {
                                      const enterId =
                                        ft.enterAnimation ?? "none";
                                      const exitId = ft.exitAnimation ?? "none";
                                      const hasMotion =
                                        enterId !== "none" || exitId !== "none";
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setTextToolbarPopover((p) =>
                                                p === "motion"
                                                  ? null
                                                  : "motion",
                                              )
                                            }
                                            className={`h-8 px-2.5 flex items-center gap-1.5 rounded-md text-[13px] font-medium transition-colors ${
                                              textToolbarPopover === "motion"
                                                ? "bg-neutral-100 text-neutral-900"
                                                : hasMotion
                                                  ? "text-brand-primary hover:bg-neutral-100"
                                                  : "text-neutral-700 hover:bg-neutral-100"
                                            }`}
                                            title="Animaciones de entrada y salida"
                                          >
                                            <Wand2 className="w-3.5 h-3.5" />
                                            <span>Animar</span>
                                            {hasMotion && (
                                              <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-brand-primary" />
                                            )}
                                          </button>
                                          {textToolbarPopover === "motion" && (
                                            <div
                                              data-clip-ui="true"
                                              className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-neutral-200 rounded-lg shadow-md p-3"
                                              style={{ width: 360 }}
                                            >
                                              <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-1.5">
                                                  <MotionPicker
                                                    label="Entrada"
                                                    kind="enter"
                                                    scope="text"
                                                    value={enterId}
                                                    onChange={(id) =>
                                                      patchText({
                                                        enterAnimation: id,
                                                      })
                                                    }
                                                  />
                                                  {enterId !== "none" && (
                                                    <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                                      <NumberField
                                                        value={
                                                          ft.enterDurationSec ??
                                                          0.5
                                                        }
                                                        min={0.1}
                                                        max={5}
                                                        step={0.1}
                                                        decimals={1}
                                                        onCommit={(n) =>
                                                          patchText({
                                                            enterDurationSec: n,
                                                          })
                                                        }
                                                        className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                        ariaLabel="Duración entrada"
                                                      />
                                                      <span className="text-[11px] text-neutral-400">
                                                        seg
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="space-y-1.5">
                                                  <MotionPicker
                                                    label="Salida"
                                                    kind="exit"
                                                    scope="text"
                                                    value={exitId}
                                                    onChange={(id) =>
                                                      patchText({
                                                        exitAnimation: id,
                                                      })
                                                    }
                                                  />
                                                  {exitId !== "none" && (
                                                    <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                                      <NumberField
                                                        value={
                                                          ft.exitDurationSec ??
                                                          0.5
                                                        }
                                                        min={0.1}
                                                        max={5}
                                                        step={0.1}
                                                        decimals={1}
                                                        onCommit={(n) =>
                                                          patchText({
                                                            exitDurationSec: n,
                                                          })
                                                        }
                                                        className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                        ariaLabel="Duración salida"
                                                      />
                                                      <span className="text-[11px] text-neutral-400">
                                                        seg
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </>
                              );
                            })()}

                          {/* 2. Dividir (todos menos texto: dividir un texto no aporta) */}
                          {selectedClip.type !== "text" &&
                            (() => {
                              const localSplit =
                                currentTime - selectedClip.startTime;
                              const canSplit =
                                localSplit > 0.1 &&
                                localSplit < selectedClip.duration - 0.1;
                              return tbBtn({
                                onClick: splitSelectedClip,
                                disabled: !canSplit,
                                title: canSplit
                                  ? "Dividir clip en el punto actual"
                                  : "Ubicá el selector de tiempo dentro del clip para dividirlo",
                                icon: <Scissors className="w-3.5 h-3.5" />,
                                label: "Dividir",
                              });
                            })()}

                          {/* 3. Volumen (video/audio/ai) */}
                          {(selectedClip.type === "video" ||
                            selectedClip.type === "audio" ||
                            selectedClip.type === "ai-generated") && (
                            <>
                              <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                              <div className="relative">
                                {(() => {
                                  const idx = _ci++;
                                  const fromRight = toolbarBtnCount - 1 - idx;
                                  const iconOnly = fromRight < toolbarCollapsed;
                                  return (
                                    <button
                                      onClick={() =>
                                        setVolumePopoverOpen((v) => !v)
                                      }
                                      className={`h-8 flex items-center rounded-md text-[13px] font-medium transition-colors ${iconOnly ? "w-8 justify-center" : "px-2.5 gap-1.5"} ${volumePopoverOpen ? "bg-neutral-100 text-neutral-900" : "text-neutral-700 hover:bg-neutral-100"}`}
                                      title="Ajustar volumen del clip"
                                    >
                                      {(selectedClip.volume ?? 1) === 0 ? (
                                        <VolumeX className="w-3.5 h-3.5" />
                                      ) : (
                                        <Volume2 className="w-3.5 h-3.5" />
                                      )}
                                      <span
                                        data-clip-tb-label
                                        className={iconOnly ? "hidden" : ""}
                                      >
                                        Volumen
                                      </span>
                                    </button>
                                  );
                                })()}
                                {volumePopoverOpen && (
                                  <div
                                    data-clip-ui="true"
                                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-neutral-200 rounded-lg shadow-md p-3 flex items-center gap-3"
                                    style={{ minWidth: 260 }}
                                  >
                                    <button
                                      onClick={() =>
                                        setClips((prev) =>
                                          prev.map((c) =>
                                            c.id === selectedClip.id
                                              ? {
                                                  ...c,
                                                  volume:
                                                    (c.volume ?? 1) === 0
                                                      ? 1
                                                      : 0,
                                                }
                                              : c,
                                          ),
                                        )
                                      }
                                      className="shrink-0 text-neutral-600 hover:text-neutral-900"
                                      title={
                                        (selectedClip.volume ?? 1) === 0
                                          ? "Activar audio"
                                          : "Silenciar"
                                      }
                                    >
                                      {(selectedClip.volume ?? 1) === 0 ? (
                                        <VolumeX className="w-4 h-4" />
                                      ) : (
                                        <Volume2 className="w-4 h-4" />
                                      )}
                                    </button>
                                    <div className="flex-1 relative flex items-center">
                                      <input
                                        type="range"
                                        min={0}
                                        max={2}
                                        step={0.01}
                                        value={selectedClip.volume ?? 1}
                                        onChange={(e) => {
                                          const v = parseFloat(e.target.value);
                                          setClips((prev) =>
                                            prev.map((c) =>
                                              c.id === selectedClip.id
                                                ? { ...c, volume: v }
                                                : c,
                                            ),
                                          );
                                        }}
                                        className="w-full accent-brand-primary"
                                      />
                                      {/* 100% tick marker */}
                                      <span
                                        aria-hidden
                                        className="pointer-events-none absolute top-1/2 -translate-y-1/2 w-px h-2.5 bg-neutral-400/70"
                                        style={{ left: "50%" }}
                                      />
                                    </div>
                                    <span
                                      className={`shrink-0 text-[12px] font-medium tabular-nums w-10 text-right ${(selectedClip.volume ?? 1) > 1 ? "text-brand-primary" : "text-neutral-600"}`}
                                    >
                                      {Math.round(
                                        (selectedClip.volume ?? 1) * 100,
                                      )}
                                      %
                                    </span>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* 3c. Eliminar silencios (video/audio/ai-generated). Abre un
                          popover con inputs para el umbral de silencio y el
                          padding (cuánto aire dejar a cada lado de cada tramo
                          de habla) + un botón que dispara la acción. Reutiliza
                          el flujo de transcripción — si el clip ya tiene
                          transcript cacheado (de una llamada anterior o del
                          generador de subtítulos), no lo vuelve a pedir.
                          Mientras procesa, los inputs colapsan con una
                          animación sutil y el icono de la toolbar NO cambia
                          (el spinner vive sólo en el botón del popover). */}
                          {(selectedClip.type === "video" ||
                            selectedClip.type === "audio" ||
                            (selectedClip.type === "ai-generated" &&
                              selectedClip.aiStatus === "ready")) && (
                            <>
                              <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                              <div className="relative">
                                {(() => {
                                  const idx = _ci++;
                                  const fromRight = toolbarBtnCount - 1 - idx;
                                  const iconOnly = fromRight < toolbarCollapsed;
                                  return (
                                    <button
                                      onClick={() =>
                                        setSilencePopoverOpen((v) => !v)
                                      }
                                      className={`h-8 flex items-center rounded-md text-[13px] font-medium transition-colors ${iconOnly ? "w-8 justify-center" : "px-2.5 gap-1.5"} ${silencePopoverOpen ? "bg-neutral-100 text-neutral-900" : "text-neutral-700 hover:bg-neutral-100"}`}
                                      title="Eliminar silencios automáticamente"
                                    >
                                      <ScissorsLineDashed className="w-3.5 h-3.5" />
                                      <span
                                        data-clip-tb-label
                                        className={iconOnly ? "hidden" : ""}
                                      >
                                        Eliminar silencios
                                      </span>
                                    </button>
                                  );
                                })()}
                                {silencePopoverOpen &&
                                  (() => {
                                    const busy =
                                      silenceCutBusy === selectedClip.id;
                                    return (
                                      <div
                                        data-clip-ui="true"
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-neutral-200 rounded-lg shadow-md p-3 flex flex-col overflow-hidden whitespace-normal"
                                        style={{ width: 280 }}
                                      >
                                        {/* Grupo de inputs — se colapsa cuando `busy` con
                                transición de opacity + max-height para un
                                efecto sutil tipo "fold up". */}
                                        <div
                                          className={`flex flex-col gap-2.5 transition-all duration-300 ease-out overflow-hidden ${busy ? "max-h-0 opacity-0 -mb-2.5 pointer-events-none" : "max-h-[400px] opacity-100 mb-2.5"}`}
                                          aria-hidden={busy}
                                        >
                                          {/* Umbral de silencio */}
                                          <div className="text-[12px] text-neutral-600 leading-snug">
                                            Eliminar silencios de más de
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-9 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const next = Math.max(
                                                    0.1,
                                                    Math.round(
                                                      (silenceThresholdSec -
                                                        0.1) *
                                                        10,
                                                    ) / 10,
                                                  );
                                                  setSilenceThresholdSec(next);
                                                }}
                                                className="h-full w-8 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                                title="Reducir 0.1s"
                                              >
                                                −
                                              </button>
                                              <NumberField
                                                value={silenceThresholdSec}
                                                min={0.1}
                                                max={10}
                                                step={0.1}
                                                decimals={1}
                                                onCommit={(n) =>
                                                  setSilenceThresholdSec(n)
                                                }
                                                className="flex-1 h-full text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                ariaLabel="Umbral de silencio en segundos"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const next = Math.min(
                                                    10,
                                                    Math.round(
                                                      (silenceThresholdSec +
                                                        0.1) *
                                                        10,
                                                    ) / 10,
                                                  );
                                                  setSilenceThresholdSec(next);
                                                }}
                                                className="h-full w-8 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                                title="Aumentar 0.1s"
                                              >
                                                +
                                              </button>
                                            </div>
                                            <span className="text-[12px] text-neutral-500">
                                              seg
                                            </span>
                                          </div>

                                          {/* Padding (head/tail) */}
                                          <div className="text-[12px] text-neutral-600 leading-snug pt-1">
                                            Dejar un margen de
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-9 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const next = Math.max(
                                                    0,
                                                    Math.round(
                                                      (silencePaddingSec -
                                                        0.02) *
                                                        100,
                                                    ) / 100,
                                                  );
                                                  setSilencePaddingSec(next);
                                                }}
                                                className="h-full w-8 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                                title="Reducir 0.02s"
                                              >
                                                −
                                              </button>
                                              <NumberField
                                                value={silencePaddingSec}
                                                min={0}
                                                max={1}
                                                step={0.02}
                                                decimals={2}
                                                onCommit={(n) =>
                                                  setSilencePaddingSec(n)
                                                }
                                                className="flex-1 h-full text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                ariaLabel="Margen antes y después de cada tramo de habla en segundos"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const next = Math.min(
                                                    1,
                                                    Math.round(
                                                      (silencePaddingSec +
                                                        0.02) *
                                                        100,
                                                    ) / 100,
                                                  );
                                                  setSilencePaddingSec(next);
                                                }}
                                                className="h-full w-8 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                                title="Aumentar 0.02s"
                                              >
                                                +
                                              </button>
                                            </div>
                                            <span className="text-[12px] text-neutral-500">
                                              seg
                                            </span>
                                          </div>
                                          <div className="text-[11px] text-neutral-400 leading-snug -mt-1">
                                            Antes y después de cada parte con
                                            habla, para que los cortes no suenen
                                            bruscos.
                                          </div>
                                        </div>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            void cutSilencesFromClip(
                                              selectedClip.id,
                                              silenceThresholdSec,
                                              silencePaddingSec,
                                            )
                                          }
                                          disabled={busy}
                                          className="h-9 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-80 disabled:cursor-wait text-white text-[13px] font-medium shadow-sm transition-colors"
                                        >
                                          {busy ? (
                                            <>
                                              <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                                              Procesando…
                                            </>
                                          ) : (
                                            <>
                                              <ScissorsLineDashed className="w-3.5 h-3.5" />{" "}
                                              Eliminar silencios
                                            </>
                                          )}
                                        </button>
                                        {silenceCutNotice && !busy && (
                                          <div className="text-[11px] text-neutral-500 leading-snug mt-2">
                                            {silenceCutNotice}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                              </div>
                            </>
                          )}

                          {/* 4-7. "Más ▾" — agrupa las acciones menos usadas
                          (Extender · Separar audio · Restaurar · Duplicar ·
                          Descargar) en un dropdown para descongestionar la barra. */}
                          {(() => {
                            const videoOrReady =
                              selectedClip.type === "video" ||
                              (selectedClip.type === "ai-generated" &&
                                selectedClip.aiStatus === "ready");
                            const hasBeenTrimmed =
                              selectedClip.sourceDuration !== undefined &&
                              (Math.abs(
                                selectedClip.duration -
                                  selectedClip.sourceDuration,
                              ) > 0.05 ||
                                (selectedClip.mediaOffset || 0) > 0.05);
                            const moreItems: Array<{
                              label: string;
                              icon: React.ReactNode;
                              onClick: () => void;
                            }> = [];
                            if (videoOrReady)
                              moreItems.push({
                                label: "Extender video",
                                icon: <PanelLeftOpen className="w-4 h-4" />,
                                onClick: () => extendVideoClip(selectedClip.id),
                              });
                            if (videoOrReady)
                              moreItems.push({
                                label: "Separar audio",
                                icon: <Music className="w-4 h-4" />,
                                onClick: () =>
                                  separateAudioFromVideoClip(selectedClip.id),
                              });
                            if (hasBeenTrimmed)
                              moreItems.push({
                                label: "Restaurar",
                                icon: <RotateCcw className="w-4 h-4" />,
                                onClick: restoreSelectedClipToOriginal,
                              });
                            moreItems.push({
                              label: "Duplicar",
                              icon: <Copy className="w-4 h-4" />,
                              onClick: duplicateSelectedClip,
                            });
                            if (selectedClip.src)
                              moreItems.push({
                                label: "Descargar",
                                icon: <Download className="w-4 h-4" />,
                                onClick: downloadSelectedClip,
                              });
                            if (moreItems.length === 0) return null;
                            return (
                              <>
                                <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                                <div className="relative">
                                  {tbBtn({
                                    onClick: () =>
                                      setClipMoreMenuOpen((v) => !v),
                                    title: "Más opciones",
                                    icon: (
                                      <MoreHorizontal className="w-3.5 h-3.5" />
                                    ),
                                    label: "Más",
                                  })}
                                  {clipMoreMenuOpen && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-20"
                                        onClick={() =>
                                          setClipMoreMenuOpen(false)
                                        }
                                      />
                                      <div className="absolute top-full right-0 mt-1 z-30 w-52 bg-white border border-neutral-200 rounded-lg shadow-lg py-1">
                                        {moreItems.map((it, i) => (
                                          <button
                                            key={i}
                                            onClick={() => {
                                              setClipMoreMenuOpen(false);
                                              it.onClick();
                                            }}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-neutral-700 hover:bg-neutral-100 text-left"
                                          >
                                            {it.icon}
                                            {it.label}
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </>
                            );
                          })()}

                          {/* 8. Eliminar. For subtitle clips we surface a split
                      button: the main half still deletes just the selected
                      caption (keeps keyboard shortcut + prior behaviour
                      consistent), the caret opens a tiny menu to wipe the
                      entire subtitle batch at once. */}
                          <div className="w-px h-5 bg-neutral-200 mx-0.5" />
                          {selectedClip.type === "text" &&
                          selectedClip.isSubtitle &&
                          selectedClip.subtitleGroupId ? (
                            <div className="relative flex items-center">
                              {tbBtn({
                                onClick: deleteSelectedClip,
                                title: "Eliminar este subtítulo",
                                danger: true,
                                icon: <Trash2 className="w-3.5 h-3.5" />,
                                label: "Eliminar",
                              })}
                              <button
                                onClick={() =>
                                  setSubtitleDeleteMenuOpen((v) => !v)
                                }
                                title="Más opciones de eliminación"
                                className="h-8 w-6 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 transition-colors"
                                aria-haspopup="menu"
                                aria-expanded={subtitleDeleteMenuOpen}
                              >
                                <ChevronDown
                                  className={`w-3.5 h-3.5 transition-transform ${subtitleDeleteMenuOpen ? "rotate-180" : ""}`}
                                />
                              </button>
                              {subtitleDeleteMenuOpen && (
                                <>
                                  {/* Click-outside backdrop: captures the first click
                              anywhere else to close the menu. Pointer-events
                              are intentionally limited to the backdrop so the
                              click lands on it, not on the underlying button. */}
                                  <div
                                    className="fixed inset-0 z-20"
                                    onClick={() =>
                                      setSubtitleDeleteMenuOpen(false)
                                    }
                                  />
                                  <div
                                    role="menu"
                                    className="absolute top-full right-0 mt-1 min-w-[220px] bg-white border border-neutral-200 rounded-md shadow-lg py-1 z-30"
                                  >
                                    <button
                                      role="menuitem"
                                      onClick={() => {
                                        setSubtitleDeleteMenuOpen(false);
                                        deleteSelectedClip();
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[13px] text-neutral-700 hover:bg-neutral-100 flex items-center gap-2"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      Eliminar seleccionado
                                    </button>
                                    <button
                                      role="menuitem"
                                      onClick={() => {
                                        const gid =
                                          selectedClip.subtitleGroupId;
                                        setSubtitleDeleteMenuOpen(false);
                                        if (gid) deleteSubtitleGroup(gid);
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[13px] text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      Eliminar todos los subtítulos
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            tbBtn({
                              onClick: deleteSelectedClip,
                              title: "Eliminar clip",
                              danger: true,
                              icon: <Trash2 className="w-3.5 h-3.5" />,
                              label: "Eliminar",
                            })
                          )}
                        </div>
                      );
                    })()}
                  <div
                    ref={previewRef}
                    className="relative bg-black rounded-md overflow-hidden shadow-md"
                    style={{
                      height: "100%",
                      maxWidth: "100%",
                      aspectRatio:
                        aspectRatio === "16:9"
                          ? "16/9"
                          : aspectRatio === "9:16"
                            ? "9/16"
                            : "3/4",
                    }}
                  >
                    {/* Directional motion-blur filter — only mounted while a
                    slide transition is in progress. The stdDeviation is
                    pinned to one axis so the blur streaks in the direction
                    of motion (the trendy "viral whip" look) instead of
                    spreading omnidirectionally like CSS `blur()`. */}
                    {motionBlurState && (
                      <svg
                        aria-hidden
                        style={{
                          position: "absolute",
                          width: 0,
                          height: 0,
                          pointerEvents: "none",
                        }}
                      >
                        <filter
                          id={MOTION_BLUR_FILTER_ID}
                          x="-10%"
                          y="-10%"
                          width="120%"
                          height="120%"
                        >
                          <feGaussianBlur
                            stdDeviation={
                              motionBlurState.direction === "x"
                                ? `${motionBlurState.px} 0`
                                : `0 ${motionBlurState.px}`
                            }
                          />
                        </filter>
                      </svg>
                    )}
                    {/* White swipe line for the `reveal` transition. Rendered
                    above the videos with a soft glow so the edge reads
                    clearly against any content. */}
                    {revealLineState && (
                      <div
                        className="absolute top-0 bottom-0 z-20 pointer-events-none"
                        style={{
                          left: `${revealLineState.leftPct}%`,
                          width: 4,
                          transform: "translateX(-2px)",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 0 16px 4px rgba(255,255,255,0.6)",
                        }}
                      />
                    )}
                    {/* Overlay video for the `light-leak`-style transitions.
                    Mounted only while one is in progress. Composited on
                    top of the video clips with `mix-blend-mode: screen`,
                    which treats the overlay's black background as
                    transparent and lets the bright effect through. The
                    sync effect below seeks this element's currentTime
                    to match the transition's normalised progress. */}
                    {overlayState && (
                      <video
                        key={overlayState.url}
                        ref={overlayVideoRef}
                        src={overlayState.url}
                        className="absolute inset-0 w-full h-full object-cover z-30 pointer-events-none"
                        style={{ mixBlendMode: "screen" }}
                        playsInline
                        muted
                        preload="auto"
                      />
                    )}
                    {/* Render a <video> for each video clip. During a normal cut
                    only one is visible at a time; during a transition window
                    two clips can overlap with complementary opacities so we
                    use opacity instead of display:none to avoid having to
                    re-mount videos when a transition starts/ends. */}
                    {clips
                      .filter(
                        (c) =>
                          (c.type === "video" || c.type === "ai-generated") &&
                          c.src,
                      )
                      .map((clip) => {
                        // Use the transition-aware active check so video clips
                        // keep rendering past their visual end during a successor's
                        // transition. Without this the outgoing clip would snap to
                        // black at exactly its endTime, breaking the fade.
                        const isActive = isClipActiveAtTime(
                          clip,
                          currentTime,
                          clips,
                        );
                        const state = isActive
                          ? computeClipRenderStateAtTime(
                              clip,
                              currentTime,
                              clips,
                            )
                          : { opacity: 0 };
                        return (
                          <video
                            key={clip.id}
                            ref={(el) => {
                              if (el) mediaRefs.current.set(clip.id, el);
                              else mediaRefs.current.delete(clip.id);
                            }}
                            src={clip.src}
                            // CORS anónimo: las signed URLs de Supabase devuelven
                            // Access-Control-Allow-Origin: *, así que pedir el video
                            // con CORS NO rompe la reproducción y permite dibujarlo
                            // en un canvas sin "ensuciarlo" (necesario para capturar
                            // el frame del pincel de borrado vía toDataURL).
                            crossOrigin="anonymous"
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                              opacity: state.opacity,
                              transform: buildClipTransform(state),
                              filter: buildClipFilter(state),
                              clipPath: buildClipClipPath(state),
                              // Hide entirely outside the active range so the GPU
                              // doesn't waste cycles compositing transparent layers.
                              visibility: isActive ? "visible" : "hidden",
                            }}
                            playsInline
                            preload="auto"
                            onLoadedMetadata={(e) => {
                              const dur = (e.currentTarget as HTMLVideoElement)
                                .duration;
                              if (!isFinite(dur) || dur <= 0) return;
                              setClips((prev) =>
                                prev.map((c) =>
                                  c.id === clip.id && c.sourceDuration !== dur
                                    ? { ...c, sourceDuration: dur }
                                    : c,
                                ),
                              );
                            }}
                          />
                        );
                      })}

                    {/* Render hidden <audio> elements for audio clips */}
                    {clips
                      .filter((c) => c.type === "audio" && c.src)
                      .map((clip) => (
                        <audio
                          key={clip.id}
                          ref={(el) => {
                            if (el) mediaRefs.current.set(clip.id, el);
                            else mediaRefs.current.delete(clip.id);
                          }}
                          src={clip.src}
                          preload="auto"
                          onLoadedMetadata={(e) => {
                            const dur = (e.currentTarget as HTMLAudioElement)
                              .duration;
                            if (!isFinite(dur) || dur <= 0) return;
                            setClips((prev) =>
                              prev.map((c) =>
                                c.id === clip.id && c.sourceDuration !== dur
                                  ? { ...c, sourceDuration: dur }
                                  : c,
                              ),
                            );
                          }}
                        />
                      ))}

                    {/* Text clip overlays — one positioned, draggable layer per
                    active text clip. Coordinates are stored as percentages
                    so the same clip looks identical on any aspect ratio /
                    player size. Font size derives from the live canvas
                    height so what you see in the editor matches the export. */}
                    {/* ── Animation clips ──────────────────────────────────
                    Time-deterministic React components driven by Remotion's
                    <Player>. They cover the full preview canvas; if the
                    template uses a transparent background they composite on
                    top of any active video, otherwise they replace it.
                    Composition resolution is fixed per aspect ratio so the
                    in-editor preview matches what the export pipeline will
                    eventually render server-side. */}
                    {activeAnimationClips.map((clip) => {
                      const compW =
                        aspectRatio === "9:16"
                          ? 1080
                          : aspectRatio === "4:3"
                            ? 1440
                            : 1920;
                      const compH =
                        aspectRatio === "9:16"
                          ? 1920
                          : aspectRatio === "4:3"
                            ? 1080
                            : 1080;
                      // Posición + escala — defaults respetan el layout previo
                      // (centro, sin escalar) si el clip viene de un proyecto
                      // antiguo sin estos campos.
                      const xPct = clip.animXPct ?? 50;
                      const yPct = clip.animYPct ?? 50;
                      const scale = clip.animScale ?? 1;
                      const isSelected = selectedClipId === clip.id;

                      // ── Background extraído del template ────────────────
                      // Si el template define un prop `background` de tipo
                      // color, lo sacamos del flujo normal y lo pintamos como
                      // capa fija full-canvas detrás del elemento movible.
                      // Esto evita que el fondo se mueva/encoja con el
                      // wrapper: el elemento queda como un objeto sobre un
                      // "telón" estático, que era el comportamiento pedido.
                      const tpl = getAnimationTemplate(
                        clip.animationTemplate as string,
                      );
                      const sourceProps = (
                        animationEditDraft &&
                        animationEditDraft.clipId === clip.id
                          ? animationEditDraft.props
                          : clip.animationProps || {}
                      ) as Record<string, unknown>;
                      const bgSpec = tpl?.propsSchema?.background;
                      const hasBgProp = bgSpec?.type === "color";
                      const bgValue = hasBgProp
                        ? (sourceProps.background as string | undefined)
                        : undefined;
                      // Solo dibujamos la capa fija si hay un color definido
                      // y no es transparente — un fondo transparente equivale
                      // a "sin fondo", así que evitamos un div inerte de más.
                      const bgCovers =
                        hasBgProp &&
                        typeof bgValue === "string" &&
                        bgValue !== "transparent" &&
                        bgValue !== "";
                      // Cuando movemos el background a la capa fija, le
                      // pasamos `transparent` al template para que no lo
                      // pinte por dentro y termine duplicado.
                      const propsForTemplate = hasBgProp
                        ? { ...sourceProps, background: "transparent" }
                        : sourceProps;

                      return (
                        <Fragment key={clip.id}>
                          {bgCovers && (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{ background: bgValue }}
                            />
                          )}
                          <AnimationCanvasItem
                            clip={clip}
                            isSelected={isSelected}
                            xPct={xPct}
                            yPct={yPct}
                            scale={scale}
                            compW={compW}
                            compH={compH}
                            localTime={Math.max(
                              0,
                              currentTime - clip.startTime,
                            )}
                            propsForTemplate={propsForTemplate}
                            previewRef={previewRef}
                            onPointerDown={handleAnimPointerDown}
                            onPointerMove={handleAnimPointerMove}
                            onPointerUp={handleAnimPointerUp}
                            onResize={handleAnimResize(clip)}
                          />
                        </Fragment>
                      );
                    })}

                    {activeTextClips.map((clip) => {
                      const isSelected = selectedClipId === clip.id;
                      const isEditing = editingTextId === clip.id;
                      const sizePx =
                        (previewSize.h || 1) *
                        ((clip.fontSizePct ?? DEFAULT_TEXT_CLIP.fontSizePct) /
                          100);
                      // Calculamos motion del clip — pero la *desactivamos* en
                      // modo edición para que el usuario pueda escribir sin que
                      // las letras se desvanezcan o salten mientras tipea.
                      const localTime = currentTime - clip.startTime;
                      const motion = isEditing
                        ? {
                            wrapperStyle: {} as React.CSSProperties,
                            itemPreset: undefined,
                          }
                        : computeClipMotion({
                            localTime,
                            duration: clip.duration,
                            enterId: clip.enterAnimation,
                            exitId: clip.exitAnimation,
                            enterDur: clip.enterDurationSec,
                            exitDur: clip.exitDurationSec,
                          });
                      const textValue = clip.text || "";
                      // Split por items si el preset lo requiere (typewriter,
                      // cascade). Cada item se renderiza como `<span>` con su
                      // propio estilo. En modo edición usamos el render plano
                      // contenteditable (no se puede tipear sobre spans
                      // partidos sin un editor real).
                      const itemRender =
                        !isEditing && motion.itemPreset
                          ? (() => {
                              const { splitMode, styleAt } = motion.itemPreset;
                              const items = splitTextForMotion(
                                textValue,
                                splitMode,
                              );
                              return (
                                <>
                                  {items.map((item, i) => (
                                    <span
                                      key={i}
                                      style={styleAt(i, items.length)}
                                      // `whiteSpace: pre` preserva los espacios
                                      // que quedan adentro del item (modo words)
                                      // o el espacio en sí cuando es chars.
                                      // Sin esto el browser los colapsa.
                                      // eslint-disable-next-line react/forbid-dom-props
                                    >
                                      {item === " " ? "\u00A0" : item}
                                    </span>
                                  ))}
                                </>
                              );
                            })()
                          : null;
                      return (
                        <div
                          key={clip.id}
                          data-text-clip-id={clip.id}
                          onPointerDown={(e) => handleTextPointerDown(e, clip)}
                          onPointerMove={handleTextPointerMove}
                          onPointerUp={handleTextPointerUp}
                          onPointerCancel={handleTextPointerUp}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setSelectedClipId(clip.id);
                            setEditingTextId(clip.id);
                          }}
                          className={`absolute touch-none ${
                            isEditing
                              ? "cursor-text"
                              : isSelected
                                ? "cursor-grabbing select-none"
                                : "cursor-grab select-none"
                          }`}
                          style={{
                            left: `${clip.textXPct ?? DEFAULT_TEXT_CLIP.textXPct}%`,
                            top: `${clip.textYPct ?? DEFAULT_TEXT_CLIP.textYPct}%`,
                            // Componemos el translate base con el motion
                            // wrapper style. El motion define `transform`
                            // adicional (slide / scale); lo concatenamos para
                            // que ambos coexistan.
                            transform: motion.wrapperStyle.transform
                              ? `translate(-50%, -50%) ${motion.wrapperStyle.transform}`
                              : "translate(-50%, -50%)",
                            opacity: motion.wrapperStyle.opacity,
                            filter: motion.wrapperStyle.filter,
                            // Ancho explícito si el usuario reseteó la caja
                            // desde el preview; sino mantenemos el ancho
                            // natural acotado por `maxWidth`.
                            width:
                              typeof clip.textWidthPct === "number"
                                ? `${clip.textWidthPct}%`
                                : undefined,
                            // Cuando el usuario fijó un ancho explícito desde
                            // el preview no le imponemos un cap — puede
                            // extender la caja más allá de la canvas si así
                            // lo desea. El cap default sólo aplica al ancho
                            // natural (sin resize manual).
                            maxWidth:
                              typeof clip.textWidthPct === "number"
                                ? undefined
                                : "92%",
                            textAlign:
                              clip.textAlign ?? DEFAULT_TEXT_CLIP.textAlign,
                            // Outline solo en modo edición (focus inline);
                            // la selección normal usa el SelectionFrame
                            // amarillo de abajo, con manijas para resizear.
                            outline: isEditing
                              ? "1px solid rgba(22,68,235,0.85)"
                              : "none",
                            outlineOffset: 4,
                            padding: "2px 4px",
                          }}
                        >
                          {/* Marco de selección con manijas de resize.
                          Solo en modo selección (no edición), porque
                          tipear adentro y a la vez ofrecer handles es
                          confuso y los handles "atrapan" clicks que
                          el contenteditable necesita para colocar el
                          caret. El componente es self-contained y
                          captura sus propios pointer events. */}
                          {isSelected && !isEditing && (
                            <SelectionFrame
                              // Handles N/S no aplican en texto: el alto
                              // del wrapper sigue al contenido (font-size
                              // + wrap), arrastrar arriba/abajo no tiene
                              // semántica clara.
                              handles={["nw", "ne", "sw", "se", "e", "w"]}
                              onResize={handleTextResize(clip)}
                            />
                          )}
                          <span
                            // Re-key to force a fresh DOM node when entering /
                            // exiting edit mode (avoids stale contenteditable
                            // text after an external clip update).
                            key={
                              isEditing
                                ? `edit-${clip.text}`
                                : `view-${clip.text}`
                            }
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            spellCheck={isEditing}
                            onBlur={(e) => {
                              if (!isEditing) return;
                              const next = (
                                e.currentTarget.innerText || ""
                              ).replace(/\n{3,}/g, "\n\n");
                              setClips((prev) =>
                                prev.map((c) =>
                                  c.id === clip.id
                                    ? {
                                        ...c,
                                        text: next,
                                        name:
                                          next.length > 20
                                            ? next.slice(0, 17) + "…"
                                            : next || "Texto",
                                      }
                                    : c,
                                ),
                              );
                              setEditingTextId(null);
                            }}
                            onKeyDown={(e) => {
                              if (!isEditing) return;
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                (e.currentTarget as HTMLSpanElement).blur();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                // Restore original content and exit
                                (e.currentTarget as HTMLSpanElement).innerText =
                                  clip.text || "";
                                setEditingTextId(null);
                              }
                            }}
                            className="block whitespace-pre-wrap break-words leading-tight outline-none"
                            style={{
                              fontFamily: fontStackFor(clip.fontFamily),
                              fontSize: `${sizePx}px`,
                              fontWeight:
                                clip.fontWeight === "bold" ? 700 : 400,
                              fontStyle: clip.fontStyle ?? "normal",
                              color:
                                clip.textColor ?? DEFAULT_TEXT_CLIP.textColor,
                              // Subtle shadow keeps light text legible over busy
                              // video backgrounds without forcing the user to
                              // pick a specific contrast color.
                              textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                              minWidth: isEditing ? "1ch" : undefined,
                            }}
                          >
                            {itemRender ?? textValue}
                          </span>
                        </div>
                      );
                    })}

                    {/* Empty state — shown only when there is nothing visible at
                    the playhead (no active video AND no active text). */}
                    {!activeVideoClip && activeTextClips.length === 0 && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
                        <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                          <Film className="w-7 h-7 text-white/40" />
                        </div>
                        <p className="text-[15px] text-white/50 font-medium">
                          Vista previa del video
                        </p>
                        <p className="text-[13px] text-white/30">
                          Agregá clips al timeline para comenzar
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Transport + Timeline ─────────────────────────────────── */}
                <div className="shrink-0 flex flex-col px-4 pt-0 pb-3">
                  {/* ── Play controls: time left | play | time right ──────────── */}
                  <div className="relative flex items-center justify-center gap-4 mb-3">
                    <span className="text-[15px] font-semibold text-brand-black/70 tabular-nums select-none">
                      {formatTime(currentTime)}
                    </span>
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="w-11 h-11 flex items-center justify-center rounded-full bg-surface-card shadow-sm hover:shadow transition-shadow"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 text-brand-black/80" />
                      ) : (
                        <Play className="w-5 h-5 text-brand-black/80 ml-0.5" />
                      )}
                    </button>
                    <span className="text-[15px] font-medium text-brand-black/40 tabular-nums select-none">
                      {formatTime(
                        contentDuration > 0 ? contentDuration : duration,
                      )}
                    </span>
                    <button
                      type="button"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setTimelineMinimized((v) => !v);
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
                      style={{ pointerEvents: "auto", touchAction: "none" }}
                      title={
                        timelineMinimized
                          ? "Expandir pistas"
                          : "Minimizar pistas"
                      }
                    >
                      {timelineMinimized ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                      {timelineMinimized ? "Expandir" : "Minimizar"}
                    </button>
                  </div>

                  {/* ── Unified timeline area (ruler + tracks + playhead) ─────── */}
                  {(() => {
                    const trackIcons: Record<TrackType, React.ReactNode> = {
                      video: <Film className="w-3.5 h-3.5" />,
                      audio: <Music className="w-3.5 h-3.5" />,
                      elements: <Type className="w-3.5 h-3.5" />,
                    };

                    return (
                      <div className="flex items-start gap-2 mb-2">
                        {/* Track labels (outside pointer area) */}
                        <div
                          className="shrink-0 flex flex-col"
                          style={{ width: 76 }}
                        >
                          {/* Spacer for ruler height */}
                          <div style={{ height: 28 }} />
                          <div
                            style={{
                              maxHeight: timelineMinimized ? 0 : 800,
                              opacity: timelineMinimized ? 0 : 1,
                              overflow: "hidden",
                              transition:
                                "max-height 280ms ease, opacity 220ms ease",
                            }}
                          >
                            {displayRows.map((row) => (
                              <div
                                key={row.key}
                                className="flex items-center gap-1.5"
                                style={{
                                  height: 48,
                                  marginTop: 6,
                                  color: row.color,
                                }}
                              >
                                <span className="shrink-0">
                                  {trackIcons[row.trackType]}
                                </span>
                                <span
                                  className="whitespace-nowrap"
                                  style={{ fontSize: 11, fontWeight: 500 }}
                                >
                                  {row.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Ruler + Tracks + Playhead container (horizontally scrollable, native scrollbar hidden) */}
                        <div className="flex-1 min-w-0 relative group">
                          <div
                            ref={timelineAreaRef}
                            data-tour="timeline"
                            className="relative w-full select-none overflow-x-auto overflow-y-hidden scrollbar-hide"
                            style={{
                              cursor: isDraggingPlayhead
                                ? "ew-resize"
                                : "pointer",
                            }}
                            onPointerDown={handleTimelinePointerDown}
                            onPointerMove={handleTimelinePointerMove}
                            onPointerUp={handleTimelinePointerUp}
                            onScroll={(e) =>
                              setTimelineScrollLeft(
                                (e.currentTarget as HTMLDivElement).scrollLeft,
                              )
                            }
                            onDragOver={(e) => {
                              if (
                                e.dataTransfer.types.includes(
                                  "application/x-atlas-asset-id",
                                )
                              ) {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                              }
                            }}
                            onDrop={async (e) => {
                              const assetId = e.dataTransfer.getData(
                                "application/x-atlas-asset-id",
                              );
                              if (!assetId) return;
                              e.preventDefault();
                              const asset = libraryAssets.find(
                                (a) => a.id === assetId,
                              );
                              if (!asset) return;
                              const startTime = clientXToSeconds(e.clientX);
                              await addAssetToTimeline(asset, { startTime });
                            }}
                          >
                            <div
                              className="relative"
                              style={{ width: timelineWidth, minWidth: "100%" }}
                            >
                              {/* ── Ruler ────────────────────────────────────────────── */}
                              {/* `data-timeline-scrub`: mover el cursor NO
                              deselecciona el clip. Sin esto no se podía
                              seleccionar un clip y después buscar el punto de
                              corte — el arrastre mataba la selección y con ella
                              el botón Dividir. Ver
                              ADR-2026-08-11-mover-el-cursor-no-deselecciona. */}
                              <div
                                ref={timelineRef}
                                data-timeline-scrub="true"
                                className="relative"
                                style={{ height: 28, width: timelineWidth }}
                              >
                                <div
                                  className="absolute bottom-0 left-0"
                                  style={{
                                    height: 1,
                                    width: timelineWidth,
                                    backgroundColor: "rgba(2,6,43,0.15)",
                                  }}
                                />
                                {(() => {
                                  // Tick density in seconds — based on how many pixels per second we have
                                  let labelInterval: number;
                                  let tickInterval: number;
                                  if (pps >= 100) {
                                    labelInterval = 1;
                                    tickInterval = 0.5;
                                  } else if (pps >= 60) {
                                    labelInterval = 2;
                                    tickInterval = 1;
                                  } else if (pps >= 30) {
                                    labelInterval = 5;
                                    tickInterval = 1;
                                  } else if (pps >= 15) {
                                    labelInterval = 10;
                                    tickInterval = 2;
                                  } else {
                                    labelInterval = 30;
                                    tickInterval = 5;
                                  }

                                  const els: React.ReactNode[] = [];
                                  const steps = Math.ceil(
                                    timelineDuration / tickInterval,
                                  );
                                  for (let i = 0; i <= steps; i++) {
                                    const sec = i * tickInterval;
                                    if (sec > timelineDuration) break;
                                    const leftPx = sec * pps;
                                    const isLabeled =
                                      Math.abs(sec % labelInterval) < 1e-6;
                                    const tickH = isLabeled ? 10 : 5;
                                    const tickColor = isLabeled
                                      ? "rgba(2,6,43,0.25)"
                                      : "rgba(2,6,43,0.12)";
                                    els.push(
                                      <div
                                        key={`tk-${sec}`}
                                        className="absolute"
                                        style={{
                                          left: leftPx,
                                          bottom: 0,
                                          height: tickH,
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: 1,
                                            height: tickH,
                                            backgroundColor: tickColor,
                                          }}
                                        />
                                        {isLabeled && (
                                          <span
                                            className="absolute whitespace-nowrap tabular-nums font-medium select-none"
                                            style={{
                                              bottom: tickH + 3,
                                              left: 2,
                                              fontSize: 11,
                                              color: "rgba(2,6,43,0.4)",
                                            }}
                                          >
                                            {sec === 0 ? "0s" : `${sec}s`}
                                          </span>
                                        )}
                                      </div>,
                                    );
                                  }
                                  return els;
                                })()}
                              </div>

                              {/* ── Dynamic track containers (collapsible) ─────────── */}
                              <div
                                style={{
                                  maxHeight: timelineMinimized ? 0 : 800,
                                  opacity: timelineMinimized ? 0 : 1,
                                  overflow: "hidden",
                                  transition:
                                    "max-height 280ms ease, opacity 220ms ease",
                                }}
                              >
                                {displayRows.map((row) => {
                                  const trackClips = clips.filter(
                                    row.matchClip,
                                  );
                                  return (
                                    <div
                                      key={row.key}
                                      className="relative flex items-center overflow-hidden"
                                      style={{
                                        height: 48,
                                        width: timelineWidth,
                                        borderRadius: 8,
                                        backgroundColor: "rgba(2,6,43,0.06)",
                                        marginTop: 6,
                                      }}
                                    >
                                      {trackClips.length > 0 ? (
                                        trackClips.map((clip) => {
                                          const CLIP_VISUAL_GAP_PX = 3;
                                          const leftPx = clip.startTime * pps;
                                          const widthPx = Math.max(
                                            clip.duration * pps -
                                              CLIP_VISUAL_GAP_PX,
                                            2,
                                          );
                                          const isDragged =
                                            draggingClipRef.current?.id ===
                                            clip.id;
                                          const isAiClip =
                                            clip.type === "ai-generated";
                                          const isMedia =
                                            clip.type === "video" ||
                                            (isAiClip &&
                                              clip.aiStatus === "ready");
                                          const showFrameStrip =
                                            isMedia && !!clip.src;
                                          const isSelected =
                                            clip.id === selectedClipId;
                                          const thumbs =
                                            clipThumbnails[clip.id];
                                          // When there is a frame strip, skip the built-in color border; selection uses yellow handles instead
                                          return (
                                            <div
                                              key={clip.id}
                                              data-clip-id={clip.id}
                                              data-is-media={
                                                isMedia ? "1" : "0"
                                              }
                                              onPointerDown={(e) =>
                                                handleClipPointerDown(e, clip)
                                              }
                                              className={`absolute flex items-center overflow-visible${bounceClipIds.has(clip.id) ? " clip-bounce" : ""}`}
                                              style={{
                                                left: leftPx,
                                                width: widthPx,
                                                top: 4,
                                                bottom: 4,
                                                cursor: isDragged
                                                  ? "grabbing"
                                                  : "grab",
                                                touchAction: "none",
                                                opacity:
                                                  isDragged && snapTarget
                                                    ? 0.5
                                                    : 1,
                                                transition: "opacity 0.1s",
                                                zIndex: isSelected
                                                  ? 20
                                                  : isDragged
                                                    ? 15
                                                    : 1,
                                              }}
                                            >
                                              {/* Subtitle generation overlay. Rendered
                                        above the inner content (frame strip
                                        / waveform / AI preview) so it reads
                                        clearly while the webhook is running.
                                        The shimmer uses the .clip-shimmer
                                        class + ::before pseudo defined in
                                        index.css. */}
                                              {subtitlesLoading &&
                                                subtitlesSourceClipId ===
                                                  clip.id && (
                                                  <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none pl-2">
                                                    <div
                                                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                      style={{
                                                        maxWidth:
                                                          "calc(100% - 12px)",
                                                      }}
                                                    >
                                                      <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                                                      {widthPx >= 120 && (
                                                        <span className="truncate">
                                                          Generando subtítulos…
                                                        </span>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}

                                              {/* Video translation overlay — mismo patrón que subtítulos.
                                              Si el clip lleva varios segundos traduciéndose
                                              también mostramos un contador alineado a la
                                              derecha (mismo patrón visual que el badge
                                              elapsed de generación de video AI). */}
                                              {clip.aiTranslating && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{
                                                      maxWidth:
                                                        "calc(100% - 12px)",
                                                    }}
                                                  >
                                                    <Languages className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && (
                                                      <span className="truncate">
                                                        Traduciendo al inglés…
                                                      </span>
                                                    )}
                                                  </div>
                                                  {clip.aiTranslateStartedAt &&
                                                    widthPx >= 160 && (
                                                      <span className="ml-auto">
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.aiTranslateStartedAt
                                                          }
                                                          color="#ffffff"
                                                        />
                                                      </span>
                                                    )}
                                                </div>
                                              )}

                                              {/* Object-erase overlay — mismo
                                              patrón visual que traducción: shimmer
                                              + pill con icono y, si el clip es
                                              ancho, contador de tiempo a la derecha
                                              para que el cliente sepa que está
                                              andando. */}
                                              {clip.eraseStatus === "processing" && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{
                                                      maxWidth:
                                                        "calc(100% - 12px)",
                                                    }}
                                                  >
                                                    <Eraser className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && (
                                                      <span className="truncate">
                                                        Borrando objeto…
                                                      </span>
                                                    )}
                                                  </div>
                                                  {clip.eraseStartedAt &&
                                                    widthPx >= 160 && (
                                                      <span className="ml-auto">
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.eraseStartedAt
                                                          }
                                                          color="#ffffff"
                                                        />
                                                      </span>
                                                    )}
                                                </div>
                                              )}

                                              {/* Cambiar-voz overlay — mismo
                                              patrón visual que traducción/borrado:
                                              shimmer + pill. */}
                                              {clip.voiceChangeStatus ===
                                                "processing" && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{
                                                      maxWidth:
                                                        "calc(100% - 12px)",
                                                    }}
                                                  >
                                                    <Mic className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && (
                                                      <span className="truncate">
                                                        Cambiando voz…
                                                      </span>
                                                    )}
                                                  </div>
                                                  {clip.voiceChangeStartedAt &&
                                                    widthPx >= 160 && (
                                                      <span className="ml-auto">
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.voiceChangeStartedAt
                                                          }
                                                          color="#ffffff"
                                                        />
                                                      </span>
                                                    )}
                                                </div>
                                              )}

                                              {/* Muted badge — audio/video/AI clips with volume 0 */}
                                              {(clip.type === "audio" ||
                                                clip.type === "video" ||
                                                clip.type === "ai-generated") &&
                                                (clip.volume ?? 1) === 0 && (
                                                  <div
                                                    className="absolute z-30 flex items-center justify-center rounded-full bg-black/75 text-white shadow-sm pointer-events-none"
                                                    style={{
                                                      top: 3,
                                                      left: 3,
                                                      width: 16,
                                                      height: 16,
                                                    }}
                                                    title="Silenciado"
                                                  >
                                                    <VolumeX className="w-2.5 h-2.5" />
                                                  </div>
                                                )}

                                              {/* Load error badge: la signed URL del clip
                                              no se pudo generar al abrir el proyecto
                                              (típico: archivo eliminado del bucket o
                                              error de conexión). Click para reintentar
                                              SOLO la generación de la signed URL. */}
                                              {clip.loadError &&
                                                (() => {
                                                  const isRetrying =
                                                    retryingLoadForClipId ===
                                                    clip.id;
                                                  return (
                                                    <button
                                                      type="button"
                                                      onPointerDown={(e) => {
                                                        e.stopPropagation();
                                                        void retryLoadSignedUrl(
                                                          clip.id,
                                                        );
                                                      }}
                                                      disabled={isRetrying}
                                                      className="absolute z-30 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed text-white shadow-sm transition-colors"
                                                      style={{
                                                        bottom: 3,
                                                        right: 3,
                                                        width: 18,
                                                        height: 18,
                                                      }}
                                                      title={
                                                        isRetrying
                                                          ? "Reintentando cargar el archivo…"
                                                          : `${clip.loadError} (click para reintentar)`
                                                      }
                                                    >
                                                      {isRetrying ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                      ) : (
                                                        <AlertCircle className="w-3 h-3" />
                                                      )}
                                                    </button>
                                                  );
                                                })()}

                                              {/* Erase-object error badge: el borrado de
                                              objeto falló. Mismo patrón que loadError —
                                              badge rojo clickeable; al tocarlo se reabre
                                              el pincel para reintentar (re-marcar el
                                              objeto). El error va en el tooltip. */}
                                              {clip.eraseStatus === "error" && (
                                                <button
                                                  type="button"
                                                  onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    setClips((prev) =>
                                                      prev.map((c) =>
                                                        c.id === clip.id
                                                          ? {
                                                              ...c,
                                                              eraseStatus: undefined,
                                                              eraseError: undefined,
                                                            }
                                                          : c,
                                                      ),
                                                    );
                                                    openEraseBrush(clip.id);
                                                  }}
                                                  className="absolute z-30 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors"
                                                  style={{
                                                    bottom: 3,
                                                    right: 3,
                                                    width: 18,
                                                    height: 18,
                                                  }}
                                                  title={`No se pudo borrar el objeto: ${clip.eraseError || "error desconocido"} (click para reintentar)`}
                                                >
                                                  <Eraser className="w-3 h-3" />
                                                </button>
                                              )}

                                              {/* Cambiar-voz error badge: el S2S
                                              falló. Badge rojo clickeable; al
                                              tocarlo reabre el picker para
                                              reintentar. El error va en el tooltip. */}
                                              {clip.voiceChangeStatus === "error" && (
                                                <button
                                                  type="button"
                                                  onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    setClips((prev) =>
                                                      prev.map((c) =>
                                                        c.id === clip.id
                                                          ? {
                                                              ...c,
                                                              voiceChangeStatus:
                                                                undefined,
                                                              voiceChangeError:
                                                                undefined,
                                                            }
                                                          : c,
                                                      ),
                                                    );
                                                    setChangeVoiceClipId(clip.id);
                                                    setShowClipVoicePicker(true);
                                                  }}
                                                  className="absolute z-30 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors"
                                                  style={{
                                                    bottom: 3,
                                                    right: 3,
                                                    width: 18,
                                                    height: 18,
                                                  }}
                                                  title={`No se pudo cambiar la voz: ${clip.voiceChangeError || "error desconocido"} (click para reintentar)`}
                                                >
                                                  <Mic className="w-3 h-3" />
                                                </button>
                                              )}

                                              {/* Asset-register warning badge: el archivo AI
                                              se generó OK pero no pudo guardarse en la
                                              biblioteca del proyecto. Click para reintentar
                                              SOLO el INSERT (no se re-genera el archivo
                                              ni se descuentan créditos). */}
                                              {clip.aiAssetWarning &&
                                                (() => {
                                                  const isRetrying =
                                                    retryingAssetForClipId ===
                                                    clip.id;
                                                  return (
                                                    <button
                                                      type="button"
                                                      onPointerDown={(e) => {
                                                        e.stopPropagation();
                                                        void retryAssetRegistration(
                                                          clip.id,
                                                        );
                                                      }}
                                                      disabled={isRetrying}
                                                      className="absolute z-30 flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-70 disabled:cursor-not-allowed text-white shadow-sm transition-colors"
                                                      style={{
                                                        top: 3,
                                                        right: 3,
                                                        width: 18,
                                                        height: 18,
                                                      }}
                                                      title={
                                                        isRetrying
                                                          ? "Reintentando guardar en biblioteca…"
                                                          : `${clip.aiAssetWarning} (click para reintentar)`
                                                      }
                                                    >
                                                      {isRetrying ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                      ) : (
                                                        <AlertCircle className="w-3 h-3" />
                                                      )}
                                                    </button>
                                                  );
                                                })()}

                                              {/* Inner content: frame strip OR colored body */}
                                              <div
                                                className="relative w-full h-full overflow-hidden flex items-center"
                                                style={{
                                                  borderRadius: isSelected
                                                    ? 0
                                                    : 6,
                                                  backgroundColor:
                                                    showFrameStrip
                                                      ? "#0a0a0a"
                                                      : clip.color + "20",
                                                  borderLeft: showFrameStrip
                                                    ? "none"
                                                    : `3px solid ${clip.color}`,
                                                  outline: isSelected
                                                    ? "2px solid #F5C518"
                                                    : "none",
                                                  outlineOffset: 0,
                                                }}
                                              >
                                                {showFrameStrip ? (
                                                  thumbs &&
                                                  thumbs.length > 0 ? (
                                                    (() => {
                                                      // Fixed-width film-strip frames. Each frame slot is anchored to a fixed pixel position
                                                      // within the clip — so its sampled source time is `mediaOffset + (pixelCenter / pps)`.
                                                      // This keeps already-visible frames stable when the user trims/extends the clip: frames
                                                      // only appear or disappear at the edges, instead of all the thumbnails shifting.
                                                      const FRAME_WIDTH = 56;
                                                      const frameCount =
                                                        Math.max(
                                                          1,
                                                          Math.ceil(
                                                            widthPx /
                                                              FRAME_WIDTH,
                                                          ) + 1,
                                                        );
                                                      const offset =
                                                        clip.mediaOffset || 0;
                                                      const totalSrcDuration =
                                                        clip.sourceDuration ??
                                                        clip.duration;
                                                      const frames: React.ReactNode[] =
                                                        [];
                                                      for (
                                                        let i = 0;
                                                        i < frameCount;
                                                        i++
                                                      ) {
                                                        const pixelCenter =
                                                          i * FRAME_WIDTH +
                                                          FRAME_WIDTH / 2;
                                                        const sourceTime =
                                                          offset +
                                                          (pps > 0
                                                            ? pixelCenter / pps
                                                            : 0);
                                                        const ratio =
                                                          totalSrcDuration > 0
                                                            ? sourceTime /
                                                              totalSrcDuration
                                                            : 0;
                                                        const thumbIdx =
                                                          Math.min(
                                                            thumbs.length - 1,
                                                            Math.max(
                                                              0,
                                                              Math.floor(
                                                                ratio *
                                                                  thumbs.length,
                                                              ),
                                                            ),
                                                          );
                                                        frames.push(
                                                          <div
                                                            key={i}
                                                            className="h-full shrink-0"
                                                            style={{
                                                              width:
                                                                FRAME_WIDTH,
                                                              backgroundImage: `url(${thumbs[thumbIdx]})`,
                                                              backgroundSize:
                                                                "cover",
                                                              backgroundPosition:
                                                                "center",
                                                            }}
                                                          />,
                                                        );
                                                      }
                                                      return (
                                                        <div className="flex items-stretch w-full h-full overflow-hidden">
                                                          {frames}
                                                        </div>
                                                      );
                                                    })()
                                                  ) : (
                                                    <div className="w-full h-full bg-gradient-to-r from-neutral-800 via-neutral-700 to-neutral-800 animate-pulse" />
                                                  )
                                                ) : isAiClip ? (
                                                  <div className="flex items-center w-full h-full px-1 gap-1 min-w-0">
                                                    {clip.aiFirstFrame && (
                                                      <img
                                                        src={clip.aiFirstFrame}
                                                        alt=""
                                                        className="h-full w-auto rounded-sm object-cover shrink-0"
                                                        style={{ maxWidth: 28 }}
                                                      />
                                                    )}
                                                    {clip.aiStatus ===
                                                      "generating" && (
                                                      <Loader2
                                                        className="w-3.5 h-3.5 animate-spin shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    )}
                                                    {clip.aiStatus ===
                                                      "draft" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors"
                                                      >
                                                        <Sparkles className="w-3 h-3" />
                                                        Generar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    {clip.aiStatus ===
                                                      "error" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        title={
                                                          clip.aiError ||
                                                          "Volver a generar"
                                                        }
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                      >
                                                        Reintentar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    <span
                                                      className="text-[11px] font-medium truncate select-none flex-1 min-w-0 px-0.5"
                                                      style={{
                                                        color: clip.color,
                                                      }}
                                                    >
                                                      {clip.aiStatus ===
                                                      "generating"
                                                        ? "Generando..."
                                                        : clip.aiPrompt ||
                                                          clip.name}
                                                    </span>
                                                    {clip.aiStatus ===
                                                      "generating" &&
                                                      clip.aiGenerationStartedAt && (
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.aiGenerationStartedAt
                                                          }
                                                          color={clip.color}
                                                        />
                                                      )}
                                                    {clip.aiLastFrame && (
                                                      <img
                                                        src={clip.aiLastFrame}
                                                        alt=""
                                                        className="h-full w-auto rounded-sm object-cover shrink-0"
                                                        style={{ maxWidth: 28 }}
                                                      />
                                                    )}
                                                  </div>
                                                ) : clip.type === "audio" &&
                                                  clip.src &&
                                                  audioPeaks[clip.id] ? (
                                                  (() => {
                                                    // Anchor each bar to a fixed pixel inside the clip, and index the peak
                                                    // by absolute source time. That way trimming only adds/removes bars at
                                                    // the edges — the rest of the waveform stays stable.
                                                    const peaks =
                                                      audioPeaks[clip.id];
                                                    const BAR_WIDTH = 2;
                                                    const GAP = 1;
                                                    const STEP =
                                                      BAR_WIDTH + GAP;
                                                    const innerWidth = Math.max(
                                                      widthPx,
                                                      2,
                                                    );
                                                    const barCount = Math.max(
                                                      1,
                                                      Math.floor(
                                                        innerWidth / STEP,
                                                      ),
                                                    );
                                                    const offset =
                                                      clip.mediaOffset || 0;
                                                    const totalSrcDuration =
                                                      clip.sourceDuration ??
                                                      clip.duration;
                                                    const bars: React.ReactNode[] =
                                                      [];
                                                    for (
                                                      let i = 0;
                                                      i < barCount;
                                                      i++
                                                    ) {
                                                      const pixelCenter =
                                                        i * STEP + STEP / 2;
                                                      const sourceTime =
                                                        offset +
                                                        (pps > 0
                                                          ? pixelCenter / pps
                                                          : 0);
                                                      const ratio =
                                                        totalSrcDuration > 0
                                                          ? sourceTime /
                                                            totalSrcDuration
                                                          : 0;
                                                      const idx = Math.min(
                                                        peaks.length - 1,
                                                        Math.max(
                                                          0,
                                                          Math.floor(
                                                            ratio *
                                                              peaks.length,
                                                          ),
                                                        ),
                                                      );
                                                      const h = Math.max(
                                                        0.08,
                                                        peaks[idx],
                                                      );
                                                      bars.push(
                                                        <div
                                                          key={i}
                                                          style={{
                                                            width: BAR_WIDTH,
                                                            height: `${h * 100}%`,
                                                            backgroundColor:
                                                              clip.color,
                                                            borderRadius: 1,
                                                            opacity: 0.9,
                                                            marginRight: GAP,
                                                            flexShrink: 0,
                                                          }}
                                                        />,
                                                      );
                                                    }
                                                    return (
                                                      <div className="flex items-center w-full h-full overflow-hidden">
                                                        {bars}
                                                      </div>
                                                    );
                                                  })()
                                                ) : clip.type === "audio" &&
                                                  clip.aiPrompt ? (
                                                  <div className="flex items-center w-full h-full px-2 gap-1.5 min-w-0">
                                                    {clip.aiStatus ===
                                                      "generating" && (
                                                      <Loader2
                                                        className="w-3.5 h-3.5 animate-spin shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    )}
                                                    {clip.aiStatus ===
                                                      "draft" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiAudioClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors"
                                                      >
                                                        <Sparkles className="w-3 h-3" />
                                                        Generar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    {clip.aiStatus ===
                                                      "error" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiAudioClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        title={
                                                          clip.aiError ||
                                                          "Volver a generar"
                                                        }
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                      >
                                                        Reintentar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    {clip.aiAudioType ===
                                                    "music" ? (
                                                      <Music
                                                        className="w-3 h-3 shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    ) : (
                                                      <Mic
                                                        className="w-3 h-3 shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    )}
                                                    <span
                                                      className="text-[11px] font-medium truncate select-none flex-1 min-w-0"
                                                      style={{
                                                        color: clip.color,
                                                      }}
                                                    >
                                                      {clip.aiStatus ===
                                                      "generating"
                                                        ? "Generando..."
                                                        : clip.aiPrompt ||
                                                          clip.name}
                                                    </span>
                                                    {clip.aiStatus ===
                                                      "generating" &&
                                                      clip.aiGenerationStartedAt && (
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.aiGenerationStartedAt
                                                          }
                                                          color={clip.color}
                                                        />
                                                      )}
                                                  </div>
                                                ) : (
                                                  <span
                                                    className="text-[11px] font-medium truncate select-none px-2.5"
                                                    style={{
                                                      color: clip.color,
                                                    }}
                                                  >
                                                    {clip.name}
                                                  </span>
                                                )}
                                              </div>

                                              {/* Selection handles (yellow) — draggable to trim */}
                                              {isSelected && (
                                                <>
                                                  <div
                                                    onPointerDown={(e) =>
                                                      handleTrimPointerDown(
                                                        e,
                                                        clip,
                                                        "left",
                                                      )
                                                    }
                                                    className="absolute top-0 bottom-0 flex items-center justify-center"
                                                    style={{
                                                      left: -8,
                                                      width: 10,
                                                      backgroundColor:
                                                        "#F5C518",
                                                      borderTopLeftRadius: 4,
                                                      borderBottomLeftRadius: 4,
                                                      cursor: "ew-resize",
                                                      touchAction: "none",
                                                    }}
                                                  >
                                                    <ChevronLeft
                                                      className="w-3 h-3"
                                                      strokeWidth={3}
                                                      style={{ color: "#000" }}
                                                    />
                                                  </div>
                                                  <div
                                                    onPointerDown={(e) =>
                                                      handleTrimPointerDown(
                                                        e,
                                                        clip,
                                                        "right",
                                                      )
                                                    }
                                                    className="absolute top-0 bottom-0 flex items-center justify-center"
                                                    style={{
                                                      right: -8,
                                                      width: 10,
                                                      backgroundColor:
                                                        "#F5C518",
                                                      borderTopRightRadius: 4,
                                                      borderBottomRightRadius: 4,
                                                      cursor: "ew-resize",
                                                      touchAction: "none",
                                                    }}
                                                  >
                                                    <ChevronRight
                                                      className="w-3 h-3"
                                                      strokeWidth={3}
                                                      style={{ color: "#000" }}
                                                    />
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })
                                      ) : row.trackType === "video" ? (
                                        <button
                                          onPointerDown={(e) => {
                                            e.stopPropagation();
                                            setActivePanel("ai-scene");
                                          }}
                                          className="flex items-center justify-center transition-colors"
                                          style={{
                                            marginLeft: 8,
                                            width: 32,
                                            height: 32,
                                            borderRadius: 6,
                                            backgroundColor:
                                              "rgba(22,68,235,0.08)",
                                            color: "rgba(22,68,235,0.5)",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "rgba(22,68,235,0.15)";
                                            e.currentTarget.style.color =
                                              "rgba(22,68,235,0.8)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "rgba(22,68,235,0.08)";
                                            e.currentTarget.style.color =
                                              "rgba(22,68,235,0.5)";
                                          }}
                                        >
                                          <Plus className="w-4 h-4" />
                                        </button>
                                      ) : (
                                        <span
                                          className="text-[11px] select-none"
                                          style={{
                                            marginLeft: 10,
                                            color: "rgba(2,6,43,0.15)",
                                          }}
                                        >
                                          Vacío
                                        </span>
                                      )}

                                      {/* ── Transition + indicators (only on video rows) ────
                                Appear at the boundary between every adjacent
                                video pair. Subtle by default, brighter when a
                                transition is already configured, and
                                animated on hover so the affordance is
                                discoverable without being noisy. */}
                                      {row.trackType === "video" &&
                                        trackClips.length > 1 &&
                                        (() => {
                                          const list = [...trackClips].sort(
                                            (a, b) => a.startTime - b.startTime,
                                          );
                                          const indicators: React.ReactNode[] =
                                            [];
                                          for (
                                            let i = 0;
                                            i < list.length - 1;
                                            i++
                                          ) {
                                            const left = list[i];
                                            const right = list[i + 1];
                                            const leftEnd =
                                              left.startTime + left.duration;
                                            const gap =
                                              right.startTime - leftEnd;
                                            if (
                                              gap > ADJACENCY_GAP_LIMIT ||
                                              gap < -ADJACENCY_OVERLAP_LIMIT
                                            )
                                              continue;
                                            const boundarySec =
                                              (leftEnd + right.startTime) / 2;
                                            const xPx = boundarySec * pps;
                                            const hasTransition =
                                              !!right.transitionIn;
                                            indicators.push(
                                              <button
                                                key={`tr-${left.id}-${right.id}`}
                                                type="button"
                                                data-tour={
                                                  i === 0
                                                    ? "clip-transition"
                                                    : undefined
                                                }
                                                onPointerDown={(e) =>
                                                  e.stopPropagation()
                                                }
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setTransitionTargetClipId(
                                                    right.id,
                                                  );
                                                  setActivePanel("transitions");
                                                }}
                                                className="group absolute z-30 flex items-center justify-center transition-all"
                                                style={{
                                                  left: xPx - 9,
                                                  top: "calc(50% - 9px)",
                                                  width: 18,
                                                  height: 18,
                                                  borderRadius: 9,
                                                  backgroundColor: hasTransition
                                                    ? "rgba(22,68,235,0.85)"
                                                    : "rgba(2,6,43,0.45)",
                                                  color: "#fff",
                                                  boxShadow:
                                                    "0 1px 3px rgba(0,0,0,0.25)",
                                                  cursor: "pointer",
                                                }}
                                                onMouseEnter={(e) => {
                                                  const el = e.currentTarget;
                                                  el.style.transform =
                                                    "scale(1.2)";
                                                  el.style.backgroundColor =
                                                    hasTransition
                                                      ? "#1644EB"
                                                      : "rgba(2,6,43,0.85)";
                                                }}
                                                onMouseLeave={(e) => {
                                                  const el = e.currentTarget;
                                                  el.style.transform =
                                                    "scale(1)";
                                                  el.style.backgroundColor =
                                                    hasTransition
                                                      ? "rgba(22,68,235,0.85)"
                                                      : "rgba(2,6,43,0.45)";
                                                }}
                                                title={
                                                  hasTransition
                                                    ? `Transición: ${right.transitionIn!.type}`
                                                    : "Agregar transición"
                                                }
                                              >
                                                <Plus
                                                  className="w-3 h-3"
                                                  strokeWidth={3}
                                                />
                                              </button>,
                                            );
                                          }
                                          return <>{indicators}</>;
                                        })()}

                                      {/* ── Snap ghost silhouette ─────────────────────────── */}
                                      {snapTarget &&
                                        (() => {
                                          const draggedClip = clips.find(
                                            (c) => c.id === snapTarget.clipId,
                                          );
                                          if (
                                            !draggedClip ||
                                            !row.matchClip(draggedClip)
                                          )
                                            return null;
                                          const ghostLeftPx =
                                            snapTarget.startTime * pps;
                                          const ghostWidthPx = Math.max(
                                            draggedClip.duration * pps,
                                            2,
                                          );
                                          return (
                                            <div
                                              className="absolute pointer-events-none"
                                              style={{
                                                left: ghostLeftPx,
                                                width: ghostWidthPx,
                                                top: 4,
                                                bottom: 4,
                                                borderRadius: 6,
                                                border: `2px dashed ${draggedClip.color}`,
                                                backgroundColor:
                                                  draggedClip.color + "10",
                                                transition:
                                                  "left 0.1s ease-out, width 0.1s ease-out",
                                              }}
                                            />
                                          );
                                        })()}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* ── Unified playhead (ruler through all tracks) ──────── */}
                              <div
                                className="absolute"
                                style={{
                                  left: currentTime * pps,
                                  top: 0,
                                  bottom: 0,
                                  zIndex: 30,
                                  pointerEvents: "none",
                                }}
                              >
                                <div
                                  className="absolute left-1/2 -translate-x-1/2"
                                  style={{
                                    top: 0,
                                    width: 0,
                                    height: 0,
                                    borderLeft: "5px solid transparent",
                                    borderRight: "5px solid transparent",
                                    borderTop: "7px solid #02062B",
                                  }}
                                />
                                <div
                                  className="absolute left-1/2 -translate-x-1/2"
                                  style={{
                                    top: 7,
                                    bottom: 0,
                                    width: 1,
                                    backgroundColor: "rgba(2,6,43,0.5)",
                                  }}
                                />
                                {/* Zona de agarre invisible (cursor ew-resize).
                                Marcada como scrub: ya se veía arrastrable, y
                                agarrarla deseleccionaba el clip. */}
                                <div
                                  data-timeline-scrub="true"
                                  className="absolute left-1/2 -translate-x-1/2"
                                  style={{
                                    top: 0,
                                    bottom: 0,
                                    width: 12,
                                    cursor: "ew-resize",
                                    pointerEvents: "auto",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          {/* Playhead time label — only visible while dragging the playhead, rendered OUTSIDE the scroll container to avoid clipping */}
                          {isDraggingPlayhead &&
                            (() => {
                              const playheadX =
                                currentTime * pps - timelineScrollLeft;
                              if (
                                playheadX < -40 ||
                                (timelineViewportWidth > 0 &&
                                  playheadX > timelineViewportWidth + 40)
                              )
                                return null;
                              return (
                                <div
                                  className="absolute pointer-events-none z-20"
                                  style={{ left: playheadX, top: -22 }}
                                >
                                  <div className="px-2 py-0.5 rounded text-[11px] font-semibold tabular-nums whitespace-nowrap -translate-x-1/2 bg-brand-black text-white shadow-md">
                                    {formatTime(currentTime)}
                                  </div>
                                </div>
                              );
                            })()}
                          {/* Scrollbar row — always reserved so the timeline doesn't shift when the thumb appears/disappears.
                        Thumb represents the content span (0 → last clip end); scrolling past content into the tail shrinks it. */}
                          <div
                            className="relative w-full"
                            style={{ height: 8, marginTop: 4 }}
                          >
                            {(() => {
                              const contentPx = contentDuration * pps;
                              if (timelineViewportWidth <= 0) return null;
                              const contentOverflows =
                                contentPx > timelineViewportWidth;
                              const scrolledIntoTail = timelineScrollLeft > 0;
                              if (!contentOverflows && !scrolledIntoTail)
                                return null;
                              const virtualTotal = Math.max(
                                contentPx,
                                timelineScrollLeft + timelineViewportWidth,
                              );
                              const maxScroll = Math.max(
                                0,
                                timelineScrollWidth - timelineViewportWidth,
                              );
                              const thumbWidth = Math.max(
                                (timelineViewportWidth / virtualTotal) *
                                  timelineViewportWidth,
                                32,
                              );
                              const thumbLeft =
                                virtualTotal > 0
                                  ? (timelineScrollLeft / virtualTotal) *
                                    timelineViewportWidth
                                  : 0;
                              const onThumbPointerDown = (
                                e: React.PointerEvent<HTMLDivElement>,
                              ) => {
                                e.stopPropagation();
                                e.preventDefault();
                                const startX = e.clientX;
                                const startScrollLeft = timelineScrollLeft;
                                const startVirtualTotal = virtualTotal;
                                const areaEl = timelineAreaRef.current;
                                (
                                  e.currentTarget as HTMLDivElement
                                ).setPointerCapture(e.pointerId);
                                const onMove = (ev: PointerEvent) => {
                                  const dx = ev.clientX - startX;
                                  const scrollDelta =
                                    (dx / timelineViewportWidth) *
                                    startVirtualTotal;
                                  const newScroll = Math.max(
                                    0,
                                    Math.min(
                                      maxScroll,
                                      startScrollLeft + scrollDelta,
                                    ),
                                  );
                                  if (areaEl) areaEl.scrollLeft = newScroll;
                                };
                                const onUp = () => {
                                  window.removeEventListener(
                                    "pointermove",
                                    onMove,
                                  );
                                  window.removeEventListener("pointerup", onUp);
                                };
                                window.addEventListener("pointermove", onMove);
                                window.addEventListener("pointerup", onUp);
                              };
                              return (
                                <div
                                  onPointerDown={onThumbPointerDown}
                                  onMouseEnter={(e) => {
                                    (
                                      e.currentTarget as HTMLDivElement
                                    ).style.backgroundColor =
                                      "rgba(2,6,43,0.35)";
                                  }}
                                  onMouseLeave={(e) => {
                                    (
                                      e.currentTarget as HTMLDivElement
                                    ).style.backgroundColor =
                                      "rgba(2,6,43,0.18)";
                                  }}
                                  className="absolute rounded-full transition-colors"
                                  style={{
                                    left: thumbLeft,
                                    width: thumbWidth,
                                    height: "100%",
                                    cursor: "grab",
                                    backgroundColor: "rgba(2,6,43,0.18)",
                                  }}
                                />
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Add track button (collapsible) ─────────────────────────── */}
                  <div
                    style={{
                      maxHeight: timelineMinimized ? 0 : 60,
                      opacity: timelineMinimized ? 0 : 1,
                      overflow: "hidden",
                      transition: "max-height 280ms ease, opacity 220ms ease",
                    }}
                  >
                    <div
                      className="flex items-center gap-1"
                      style={{ marginLeft: 76 }}
                    >
                      <button
                        onClick={() => anyFileInputRef.current?.click()}
                        className="flex items-center gap-1.5 transition-colors rounded-md"
                        style={{
                          height: 30,
                          paddingLeft: 8,
                          paddingRight: 10,
                          fontSize: 13,
                          fontWeight: 500,
                          color: "rgba(2,6,43,0.35)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "rgba(2,6,43,0.04)";
                          e.currentTarget.style.color = "rgba(2,6,43,0.55)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "rgba(2,6,43,0.35)";
                        }}
                        title="Subir video o audio"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Subir archivo
                      </button>
                      <button
                        ref={aiGenerateBtnRef}
                        type="button"
                        onClick={() => setAiGenerateMenuOpen((v) => !v)}
                        className="flex items-center gap-1.5 transition-colors rounded-md"
                        style={{
                          height: 30,
                          paddingLeft: 8,
                          paddingRight: 10,
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#1644EB",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "rgba(22,68,235,0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Generar con IA
                      </button>
                      {aiGenerateMenuOpen &&
                        aiGenerateBtnRef.current &&
                        createPortal(
                          (() => {
                            const r =
                              aiGenerateBtnRef.current!.getBoundingClientRect();
                            return (
                              <>
                                <div
                                  className="fixed inset-0 z-[60]"
                                  onClick={() => setAiGenerateMenuOpen(false)}
                                />
                                <div
                                  className="fixed z-[61] bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[160px]"
                                  style={{
                                    left: r.left,
                                    top: r.top - 6,
                                    transform: "translateY(-100%)",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAiGenerateMenuOpen(false);
                                      setEditingClipId(null);
                                      setActivePanel("ai-scene");
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                  >
                                    <Film
                                      className="w-3.5 h-3.5"
                                      style={{ color: "#1644EB" }}
                                    />
                                    Video
                                  </button>
                                  <div className="h-px bg-black/[0.06]" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAiGenerateMenuOpen(false);
                                      setEditingClipId(null);
                                      setActivePanel("ai-audio");
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                                  >
                                    <Music
                                      className="w-3.5 h-3.5"
                                      style={{ color: "#10B981" }}
                                    />
                                    Audio
                                  </button>
                                </div>
                              </>
                            );
                          })(),
                          document.body,
                        )}
                    </div>
                  </div>

                  {/* ── Aspect ratio + Zoom controls (right-aligned) ────────── */}
                  <div className="flex items-center justify-end gap-1">
                    {/* Soporte — anclado al cluster de la derecha pero separado
                    por un divider y un margen extra para que no se sienta
                    pegado al selector de aspect ratio. */}
                    <button
                      type="button"
                      onClick={() => setSupportModalOpen(true)}
                      className="h-6 px-2 inline-flex items-center gap-1 rounded-md hover:bg-black/[0.04] transition-colors"
                      style={{
                        color: "rgba(2,6,43,0.55)",
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                      title="Contactar soporte"
                    >
                      <LifeBuoy className="w-3 h-3" />
                      Soporte
                    </button>
                    <div className="w-px h-4 bg-neutral-200 mx-2" />
                    {/* Aspect ratio selector */}
                    {(["16:9", "9:16", "4:3"] as AspectRatio[]).map((ar) => (
                      <button
                        key={ar}
                        onClick={() => setAspectRatio(ar)}
                        className="h-6 px-1.5 flex items-center justify-center rounded-md transition-colors"
                        style={{
                          fontSize: 10,
                          fontWeight: aspectRatio === ar ? 600 : 500,
                          color:
                            aspectRatio === ar ? "#1644EB" : "rgba(2,6,43,0.3)",
                          backgroundColor:
                            aspectRatio === ar
                              ? "rgba(22,68,235,0.08)"
                              : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        {ar}
                      </button>
                    ))}
                    <div className="w-px h-4 bg-neutral-200 mx-1" />
                    <button
                      onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                      className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-black/[0.04] transition-colors"
                      style={{ color: "rgba(2,6,43,0.3)" }}
                    >
                      <ZoomOut className="w-3 h-3" />
                    </button>
                    <span
                      className="tabular-nums text-center"
                      style={{
                        fontSize: 11,
                        color: "rgba(2,6,43,0.25)",
                        width: 32,
                      }}
                    >
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                      className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-black/[0.04] transition-colors"
                      style={{ color: "rgba(2,6,43,0.3)" }}
                    >
                      <ZoomIn className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Frame-from-video picker: blur overlay + clickable clip hitboxes + frame choice dropdown ── */}
          {frameFromVideoTarget && (
            <>
              <div
                className="fixed inset-0 z-[50] bg-black/15 backdrop-blur-[2px]"
                onClick={() => {
                  setFrameFromVideoTarget(null);
                  setFrameFromVideoClipMenu(null);
                }}
              >
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white border border-neutral-200 rounded-full shadow-lg px-4 py-2 flex items-center gap-2">
                  <Film className="w-4 h-4 text-brand-primary" />
                  <span className="text-[13px] font-medium text-brand-black/80">
                    Elegí un video de la timeline
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFrameFromVideoTarget(null);
                      setFrameFromVideoClipMenu(null);
                    }}
                    className="ml-1 w-6 h-6 inline-flex items-center justify-center rounded-full hover:bg-black/[0.06] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5 text-neutral-500 rotate-45" />
                  </button>
                </div>
              </div>
              {/* Clickable hitboxes over each media clip (above the blur overlay) — duplicate the clip visual so frames stay sharp */}
              {pickerClipRects.map((r) => {
                const clip = clips.find((c) => c.id === r.clipId);
                const thumbs = clipThumbnails[r.clipId];
                const isAi = clip?.type === "ai-generated";
                return (
                  <button
                    key={`picker-hit-${r.clipId}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFrameFromVideoClipMenu({
                        clipId: r.clipId,
                        top: r.top - 6,
                        left: r.left + r.width / 2,
                      });
                    }}
                    className="fixed z-[65] rounded-md ring-2 ring-brand-primary ring-offset-2 ring-offset-black/30 overflow-hidden cursor-pointer group"
                    style={{
                      top: r.top,
                      left: r.left,
                      width: r.width,
                      height: r.height,
                      backgroundColor: clip?.src
                        ? "#0a0a0a"
                        : clip?.color
                          ? clip.color + "20"
                          : "#0a0a0a",
                    }}
                  >
                    {thumbs && thumbs.length > 0 ? (
                      <div className="flex items-stretch w-full h-full">
                        {thumbs.map((t, i) => (
                          <div
                            key={i}
                            className="h-full shrink-0"
                            style={{
                              flex: "1 1 0",
                              backgroundImage: `url(${t})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                        ))}
                      </div>
                    ) : isAi ? (
                      <div className="flex items-center w-full h-full px-1 gap-1 min-w-0">
                        {clip?.aiFirstFrame && (
                          <img
                            src={clip.aiFirstFrame}
                            alt=""
                            className="h-full w-auto rounded-sm object-cover shrink-0"
                            style={{ maxWidth: 28 }}
                          />
                        )}
                        <span
                          className="text-[11px] font-medium truncate select-none flex-1 min-w-0 px-0.5"
                          style={{ color: clip?.color }}
                        >
                          {clip?.aiPrompt || clip?.name}
                        </span>
                        {clip?.aiLastFrame && (
                          <img
                            src={clip.aiLastFrame}
                            alt=""
                            className="h-full w-auto rounded-sm object-cover shrink-0"
                            style={{ maxWidth: 28 }}
                          />
                        )}
                      </div>
                    ) : null}
                    <div className="absolute inset-0 bg-brand-primary/0 group-hover:bg-brand-primary/10 transition-colors" />
                  </button>
                );
              })}
              {frameFromVideoClipMenu && (
                <div
                  className="fixed z-[70] bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[200px] -translate-x-1/2 -translate-y-full"
                  style={{
                    top: frameFromVideoClipMenu.top,
                    left: frameFromVideoClipMenu.left,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() =>
                      pickFrameFromClip(frameFromVideoClipMenu.clipId, "first")
                    }
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-brand-black/50" />
                    Utilizar primer frame
                  </button>
                  <div className="h-px bg-black/[0.06]" />
                  <button
                    type="button"
                    onClick={() =>
                      pickFrameFromClip(frameFromVideoClipMenu.clipId, "last")
                    }
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-brand-black/50" />
                    Utilizar último frame
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Export format selection modal (top-level so no transform
          ancestor clips it) ────────────────────────────────────────────── */}
          {exportMenuOpen &&
            (() => {
              // Three sources of "is this dialog busy" we have to combine:
              //   - `exporting`           → packaging the Premiere XML zip
              //   - exportState=rendering → an MP4 render is in flight
              // Both should block close + format-picker rendering.
              const xmlPackaging = !!exporting;
              const mp4Rendering = exportState.phase === "rendering";
              const isBusy = xmlPackaging || mp4Rendering;
              const closeDialog = () => {
                if (isBusy) return;
                setExportMenuOpen(false);
                setExportError(null);
              };
              const headerTitle = mp4Rendering
                ? "Exportando MP4"
                : exportState.phase === "done"
                  ? "Exportación lista"
                  : exportState.phase === "error"
                    ? "No se pudo exportar"
                    : xmlPackaging
                      ? "Empaquetando proyecto…"
                      : "Exportar proyecto";
              const headerSubtitle = mp4Rendering
                ? "Esto puede tardar unos minutos. No cierres esta ventana."
                : exportState.phase === "done"
                  ? "Tu video está listo para descargar."
                  : exportState.phase === "error"
                    ? "Revisá el detalle y volvé a intentar."
                    : xmlPackaging
                      ? `Descargando archivos (${exporting!.loaded}/${exporting!.total})`
                      : "Elegí el formato que mejor te sirva";
              return (
                <div
                  className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
                  onClick={closeDialog}
                >
                  <div
                    className="bg-surface-card rounded-lg shadow-xl border border-neutral-200 max-w-md w-full p-5 animate-in zoom-in-95 fade-in duration-150"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-[16px] font-semibold text-neutral-900">
                          {headerTitle}
                        </h3>
                        <p className="text-[13px] text-neutral-500 mt-0.5">
                          {headerSubtitle}
                        </p>
                      </div>
                      <button
                        onClick={closeDialog}
                        disabled={isBusy}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Cerrar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {/* XML packaging progress (Premiere XML zip download). Rendered
                inline because that flow re-uses the format-picker layout
                when finished — the bar here is just an upgrade. */}
                    {xmlPackaging && (
                      <div className="mb-4">
                        <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-primary transition-[width] duration-200"
                            style={{
                              width: `${exporting!.total === 0 ? 0 : Math.round((exporting!.loaded / exporting!.total) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {exportError &&
                      !xmlPackaging &&
                      exportState.phase === "idle" && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{exportError}</span>
                        </div>
                      )}

                    {/* MP4 render — rendering phase: progress bar + cancel. */}
                    {mp4Rendering && exportState.phase === "rendering" && (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-4 h-4 text-brand-primary animate-spin shrink-0" />
                          <span className="text-[13.5px] text-neutral-700 truncate">
                            {renderMessageFor(
                              exportState.progress,
                              renderMessageTick,
                            )}
                          </span>
                          <span className="ml-auto text-[12.5px] font-semibold tabular-nums text-neutral-600">
                            {Math.round(exportState.progress * 100)}%
                          </span>
                        </div>
                        <div className="h-2 w-full bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-primary transition-[width] duration-300 ease-out"
                            style={{
                              width: `${Math.round(exportState.progress * 100)}%`,
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            exportState.handle.cancel();
                            setExportMenuOpen(false);
                          }}
                          className="self-end text-[13px] font-medium text-neutral-500 hover:text-neutral-700 px-3 py-1.5 rounded-md hover:bg-neutral-100 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {/* MP4 render — done phase: download link. */}
                    {exportState.phase === "done" && (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start gap-3 p-3.5 rounded-lg bg-brand-primary/5 border border-brand-primary/20">
                          <Check className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
                          <div className="text-[13px] text-neutral-700 leading-relaxed">
                            Tu MP4 está listo. Podés descargarlo ahora o volver
                            a la edición.
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setExportState({ phase: "idle" })}
                            className="text-[13px] font-medium text-neutral-600 hover:text-neutral-800 px-3.5 py-2 rounded-md hover:bg-neutral-100 transition-colors"
                          >
                            Exportar otro formato
                          </button>
                          <a
                            href={exportState.outputUrl}
                            download
                            className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover px-4 py-2 rounded-md transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Descargar MP4
                          </a>
                        </div>
                      </div>
                    )}

                    {/* MP4 render — error phase: message + retry. */}
                    {exportState.phase === "error" && (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start gap-3 p-3.5 rounded-lg bg-red-50 border border-red-200">
                          <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                          <div className="text-[13px] text-red-800 leading-relaxed break-words">
                            {exportState.message}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExportState({ phase: "idle" })}
                          className="self-end text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover px-4 py-2 rounded-md transition-colors"
                        >
                          Volver
                        </button>
                      </div>
                    )}

                    {/* Idle phase: show both format buttons. Hidden during MP4 render
                or while we're packaging the Premiere XML zip — those flows
                replace the layout entirely above. */}
                    {!mp4Rendering && exportState.phase === "idle" && (
                      <div className="flex flex-col gap-2.5">
                        {/* Standard / MP4 — wired to Remotion render. */}
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={handleExportMp4}
                          className="group text-left rounded-lg border border-neutral-200 p-4 bg-white hover:bg-neutral-50 hover:border-brand-primary/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Video className="w-4 h-4 text-brand-primary shrink-0" />
                            <span className="text-[14px] font-semibold text-neutral-900">
                              Formato estándar
                            </span>
                            <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary">
                              .mp4
                            </span>
                          </div>
                          <p className="text-[12.5px] leading-relaxed text-neutral-600">
                            Exporta el video en formato MP4, perfecto para
                            compartir o subir a las redes.
                          </p>
                        </button>
                        {/* Professional / Premiere XML — enabled */}
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={async () => {
                            setExportError(null);
                            // Map runtime clips down to the export-only shape so the
                            // util doesn't see anything it can't serialize.
                            const exportClips: PremiereXmlExportClip[] =
                              clips.map((c) => ({
                                id: c.id,
                                type: c.type,
                                name: c.name,
                                startTime: c.startTime,
                                duration: c.duration,
                                src: c.src,
                                mediaOffset: c.mediaOffset,
                                sourceDuration: c.sourceDuration,
                                trackIndex: c.trackIndex,
                                text: c.text,
                                fontFamily: c.fontFamily,
                                fontSizePct: c.fontSizePct,
                                fontWeight: c.fontWeight,
                                fontStyle: c.fontStyle,
                                textColor: c.textColor,
                                textAlign: c.textAlign,
                                aiStatus: c.aiStatus,
                              }));
                            setExporting({ loaded: 0, total: 0 });
                            try {
                              const { filename, blob } =
                                await exportProjectAsPremiereXmlZip({
                                  clips: exportClips,
                                  aspectRatio:
                                    aspectRatio as PremiereXmlAspectRatio,
                                  projectName: projectName || "Atlas Project",
                                  onProgress: (loaded, total) =>
                                    setExporting({ loaded, total }),
                                });
                              downloadBlob(filename, blob);
                              setExporting(null);
                              setExportMenuOpen(false);
                            } catch (err) {
                              setExporting(null);
                              setExportError(
                                err instanceof Error
                                  ? err.message
                                  : "No pudimos exportar el proyecto.",
                              );
                            }
                          }}
                          className="group text-left rounded-lg border border-neutral-200 p-4 bg-white hover:bg-neutral-50 hover:border-brand-primary/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {xmlPackaging ? (
                              <Loader2 className="w-4 h-4 text-brand-primary shrink-0 animate-spin" />
                            ) : (
                              <Film className="w-4 h-4 text-brand-primary shrink-0" />
                            )}
                            <span className="text-[14px] font-semibold text-neutral-900">
                              Formato profesional
                            </span>
                            <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary">
                              .xml
                            </span>
                          </div>
                          <p className="text-[12.5px] leading-relaxed text-neutral-600">
                            Pensado para abrir el proyecto en Premiere Pro y
                            hacer retoques profesionales. En Premiere:{" "}
                            <span className="font-medium">File → Import</span> y
                            elegí el archivo{" "}
                            <span className="font-medium">.xml</span> de la
                            carpeta descargada.
                          </p>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {/* ── Subtítulos (transcripción + confirmación) ─────────────────────
          Top-level so it isn't clipped by any transformed ancestor. The
          backdrop click closes only when not loading — avoids yanking
          the modal while the webhook is still in flight. */}
          {subtitlesModalOpen && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
              onClick={() => {
                if (!subtitlesLoading) {
                  setSubtitlesModalOpen(false);
                  setSubtitlesResult(null);
                  setSubtitlesError(null);
                }
              }}
            >
              <div
                className="bg-surface-card rounded-lg shadow-xl border border-neutral-200 max-w-2xl w-full max-h-[85vh] flex flex-col animate-in zoom-in-95 fade-in duration-150"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between p-5 pb-3">
                  <div>
                    <h3 className="text-[16px] font-semibold text-neutral-900 flex items-center gap-2">
                      <Captions className="w-4 h-4 text-brand-primary" />
                      Generar subtítulos
                    </h3>
                    <p className="text-[13px] text-neutral-500 mt-0.5">
                      {subtitlesLoading
                        ? "Transcribiendo el audio del clip…"
                        : subtitlesError
                          ? "Ocurrió un problema."
                          : subtitlesResult
                            ? `Se detectaron ${subtitlesResult.segments.length} líneas de subtítulos.`
                            : "Listo para generar."}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!subtitlesLoading) {
                        setSubtitlesModalOpen(false);
                        setSubtitlesResult(null);
                        setSubtitlesError(null);
                      }
                    }}
                    disabled={subtitlesLoading}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-3">
                  {subtitlesLoading && (
                    <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
                      <Loader2 className="w-6 h-6 animate-spin text-brand-primary mb-3" />
                      <p className="text-[13px]">
                        Esto puede tomar hasta un minuto según la duración del
                        clip.
                      </p>
                    </div>
                  )}

                  {!subtitlesLoading && subtitlesError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-[13px] text-red-800 flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium mb-1">
                          No pudimos generar los subtítulos
                        </p>
                        <p className="text-red-700 leading-relaxed">
                          {subtitlesError}
                        </p>
                        {subtitlesSourceClipId && (
                          <button
                            type="button"
                            onClick={() =>
                              openSubtitlesModalForClip(subtitlesSourceClipId)
                            }
                            className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-red-800 hover:text-red-900 underline underline-offset-2"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Reintentar
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!subtitlesLoading && !subtitlesError && subtitlesResult && (
                    <div className="space-y-4">
                      {/* Full transcript preview — collapsible-feeling box */}
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">
                          Transcripción
                        </div>
                        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-[13px] leading-relaxed text-neutral-800 max-h-28 overflow-y-auto">
                          {subtitlesResult.transcript.text || "—"}
                        </div>
                      </div>

                      {/* Segment list — each row shows timing + text, so the
                      user can eyeball chunk quality before committing. */}
                      <div>
                        <div className="flex items-baseline justify-between mb-1.5">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                            Subtítulos ({subtitlesResult.segments.length})
                          </div>
                          {typeof subtitlesResult.transcript
                            .audio_duration_secs === "number" && (
                            <div className="text-[11px] text-neutral-400 tabular-nums">
                              {subtitlesResult.transcript.audio_duration_secs.toFixed(
                                1,
                              )}
                              s totales
                            </div>
                          )}
                        </div>
                        <div className="rounded-md border border-neutral-200 divide-y divide-neutral-100 bg-white max-h-72 overflow-y-auto">
                          {subtitlesResult.segments.length === 0 ? (
                            <p className="text-[12.5px] text-neutral-500 p-3">
                              No se pudieron generar segmentos a partir de esta
                              transcripción.
                            </p>
                          ) : (
                            subtitlesResult.segments.map((seg, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-3 px-3 py-2"
                              >
                                <span className="shrink-0 text-[11px] font-medium tabular-nums text-neutral-400 w-20 pt-0.5">
                                  {seg.start.toFixed(2)}s → {seg.end.toFixed(2)}
                                  s
                                </span>
                                <span className="flex-1 text-[13px] text-neutral-800 leading-snug">
                                  {seg.text}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 p-5 pt-3 border-t border-neutral-100">
                  <button
                    type="button"
                    onClick={() => {
                      if (!subtitlesLoading) {
                        setSubtitlesModalOpen(false);
                        setSubtitlesResult(null);
                        setSubtitlesError(null);
                      }
                    }}
                    disabled={subtitlesLoading}
                    className="h-9 px-3 rounded-md text-[13px] font-medium text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={applySubtitlesToTimeline}
                    disabled={
                      subtitlesLoading ||
                      !subtitlesResult ||
                      subtitlesResult.segments.length === 0
                    }
                    className="h-9 px-3.5 rounded-md text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agregar al timeline
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Insufficient-credits modal ────────────────────────────────────
          Shared with the rest of the app (image gen, etc) so the UX of
          "you ran out of credits" stays consistent. Triggered by the
          assertCredits() guard before any AI generation kicks off. */}
          <SubscriptionLimitModal
            isOpen={showSubscriptionLimitModal}
            type={subscriptionLimitType}
            onClose={() => setShowSubscriptionLimitModal(false)}
          />

          {/* ── Modal de error de subida ───────────────────────────────────
          Avisa cuando el archivo elegido supera el límite de tamaño (hoy
          100MB del Global file size limit de Supabase Storage). Bloquea la
          subida antes de intentarla, así no falla con un 502 sin feedback.
          Desde ADR-2026-08-11-los-rechazos-de-subida-se-muestran también
          muestra los rechazos por FORMATO (RAW/TIFF/GIF/SVG) y las fallas de
          conversión, que antes morían en un `console.error`. */}
          {uploadErrorMsg && (
            <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
              <div className="bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4">
                <div className="p-5">
                  <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">
                    No se pudo agregar el archivo
                  </h3>
                  {/* `whitespace-pre-line`: la subida del agente es múltiple y
                  junta los rechazos de la tanda separados por línea en blanco;
                  sin esto quedaban todos pegados en un párrafo. */}
                  <p className="text-[15px] text-brand-black/90 mb-5 whitespace-pre-line">
                    {uploadErrorMsg}
                  </p>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setUploadErrorMsg(null)}
                      className="inline-flex items-center justify-center h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                    >
                      Entendido
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Modal grande del pincel (ver el frame grande, diseño Atlas) ──── */}
          {eraseBrushFrame && eraseBrushClipId && companyId && (
            <EraseBrushPanel
              frameUrl={eraseBrushFrame.url}
              frameWidth={eraseBrushFrame.w}
              frameHeight={eraseBrushFrame.h}
              onRequestMask={requestBrushMask}
              onClose={() => {
                setEraseBrushClipId(null);
                setEraseBrushFrame(null);
              }}
              onConfirm={(marks) =>
                setEraseConfirm({ clipId: eraseBrushClipId, marks })
              }
            />
          )}

          {/* La edición de video con IA ahora vive como PANEL de la sidebar
          (activePanel === "edit-video-ia", arriba en el body), NO como modal. */}

          {/* ── Modal de confirmación del pincel (tiempo + créditos) ───────────
          Mismo patrón visual que la confirmación de traducción: card de
          Atlas, CreditBadge en el botón primario, sin emojis. */}
          {eraseConfirm &&
            (() => {
              const clip = clipsRef.current.find(
                (c) => c.id === eraseConfirm.clipId,
              );
              const cost = clip ? creditsForBrushErase(clip.duration) : 0;
              const cancel = () => setEraseConfirm(null);
              const confirm = () => {
                const ec = eraseConfirm;
                // Capturar el size del frame ANTES de cerrar el panel.
                const fr = eraseBrushFrame
                  ? { w: eraseBrushFrame.w, h: eraseBrushFrame.h }
                  : { w: 0, h: 0 };
                setEraseConfirm(null);
                setEraseBrushFrame(null);
                setEraseBrushClipId(null);
                if (clip && assertCredits(cost))
                  void runBrushErase(ec.clipId, ec.marks, fr);
              };
              return (
                <div
                  className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center"
                  onClick={cancel}
                >
                  <div
                    className="bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-5">
                      <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2">
                        Borrar objeto del clip
                      </h3>
                      <p className="text-[15px] text-brand-black/90 mb-3 leading-relaxed">
                        Vamos a borrar el objeto que marcaste y reconstruir el
                        fondo en todo el clip, manteniendo el resto intacto.
                      </p>
                      <div className="flex items-start gap-1.5 mb-6">
                        <Clock className="w-3.5 h-3.5 text-neutral-500 mt-0.5 shrink-0" />
                        <p className="text-[13px] text-neutral-500 leading-relaxed">
                          El borrado tarda unos minutos. Mientras tanto podés
                          seguir editando otros clips.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                        <button
                          onClick={cancel}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={confirm}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors gap-2"
                        >
                          Borrar objeto
                          <CreditBadge credits={cost} tone="on-primary" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ── Modal de confirmación de traducción ──────────────────────────
          Aparece cuando el usuario clickea "Traducir EN" del toolbar del
          clip. Le muestra cuánto le va a costar antes de gastar los
          créditos, para evitar sorpresas. No tiene "no me preguntes más" —
          decisión de producto: aparece SIEMPRE porque el costo varía con
          la duración del clip y la traducción tarda varios minutos. */}
          {translateConfirmClipId &&
            (() => {
              const clip = clips.find((c) => c.id === translateConfirmClipId);
              if (!clip) return null;
              const cost = creditsForVideoTranslate({
                durationSec: clip.duration,
              });
              const durSec = Math.round(clip.duration);
              const cancel = () => setTranslateConfirmClipId(null);
              const confirm = () => {
                const id = translateConfirmClipId;
                setTranslateConfirmClipId(null);
                if (id) void translateClipToEnglish(id);
              };
              return (
                <div
                  className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center"
                  onClick={cancel}
                >
                  <div
                    className="bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-5">
                      <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2">
                        Traducir clip al inglés
                      </h3>
                      <p className="text-[15px] text-brand-black/90 mb-3 leading-relaxed">
                        Vamos a doblar este clip de{" "}
                        <strong>{durSec}s</strong> al inglés, manteniendo la
                        voz del speaker original y sincronizando los labios.
                      </p>
                      <p className="text-[13px] text-neutral-500 mb-6 leading-relaxed">
                        La traducción tarda entre 5 y 15 minutos. Mientras
                        tanto podés seguir editando otros clips.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                        <button
                          onClick={cancel}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={confirm}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors gap-2"
                        >
                          Traducir
                          <CreditBadge credits={cost} tone="on-primary" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ── Soporte modal ────────────────────────────────────────────────
          Reusa el patrón del resto del app: QR para desktop (escanear
          desde el celu), botón directo a WhatsApp Web para abrir desde
          la PC misma. El mensaje viene pre-cargado para que cuando el
          usuario contacte sepamos que es por el editor de video. */}
          {supportModalOpen &&
            (() => {
              const supportMessage =
                "Buenas, tengo una duda sobre el estudio de video.";
              const supportWhatsappUrl = `https://wa.me/5491122635392?text=${encodeURIComponent(supportMessage)}`;
              const supportQRImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(supportWhatsappUrl)}`;
              return (
                <div
                  className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
                  onClick={() => setSupportModalOpen(false)}
                >
                  <div
                    className="bg-surface-card rounded-lg shadow-xl border border-neutral-200 max-w-sm w-full p-5 animate-in zoom-in-95 fade-in duration-150"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-[16px] font-semibold text-neutral-900">
                          Contactar soporte
                        </h3>
                        <p className="text-[13px] text-neutral-500 mt-0.5">
                          Estamos para ayudarte con cualquier duda
                        </p>
                      </div>
                      <button
                        onClick={() => setSupportModalOpen(false)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                        aria-label="Cerrar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex flex-col items-center mb-4">
                      <img
                        src={supportQRImageUrl}
                        alt="QR para abrir WhatsApp y contactar soporte"
                        className="w-44 h-44 rounded-md"
                      />
                      <p className="text-[12.5px] text-neutral-500 mt-3 text-center leading-relaxed">
                        Escaneá el código con la cámara de tu celular para abrir
                        WhatsApp.
                      </p>
                    </div>

                    <a
                      href={supportWhatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSupportModalOpen(false)}
                      className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium rounded-md shadow-sm transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Abrir WhatsApp Web
                    </a>
                  </div>
                </div>
              );
            })()}
        </BrandCustomFontsContext.Provider>
      </BrandFontsContext.Provider>
    </BrandColorsContext.Provider>
  );
};

export default VideoEditor;
